import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
         PieChart, Pie, Legend, Treemap, AreaChart, Area, RadialBarChart, RadialBar, ComposedChart,
         LineChart, Line, ReferenceLine, ScatterChart, Scatter, ZAxis, RadarChart, Radar,
         PolarGrid, PolarAngleAxis, PolarRadiusAxis, LabelList } from "recharts";

/* ── SheetJS with styling support ── */
import * as XLSX_MODULE from "xlsx-js-style";
var XLSX_LIB = XLSX_MODULE;
/* Max features (pivot levels) in one query — FilterPanel enforces the same cap on selection */
var MAX_PIVOT_DIMS = 8;
function getXLSX() {
  return Promise.resolve(XLSX_LIB);
}

/* ════════════════════════════════════════════════════════════════════════════
   AMS LOGO IN EXPORTED SHEETS
   xlsx-js-style (SheetJS) cannot embed images, so the workbook is written to a
   byte array first and the company logo (/logo.png, same file the app header
   uses) is injected at the ZIP level: the .xlsx archive is parsed (SheetJS
   writes every entry uncompressed), a spreadsheet-drawing part anchored to the
   top-left of the first sheet is added, and the archive is rebuilt with fresh
   CRCs. Fail-open by design — if the logo cannot be fetched or any patch step
   fails, the workbook downloads exactly as before, just without the logo.
   ════════════════════════════════════════════════════════════════════════════ */
var _amsCrcT = (function(){ var t=[],c,n,k; for(n=0;n<256;n++){ c=n; for(k=0;k<8;k++) c=(c&1)?(0xEDB88320^(c>>>1)):(c>>>1); t[n]=c; } return t; })();
function _amsCrc32(u8){ var c=-1,i; for(i=0;i<u8.length;i++) c=_amsCrcT[(c^u8[i])&255]^(c>>>8); return (c^-1)>>>0; }
function _amsU8ToStr(u8){
  if (typeof TextDecoder !== "undefined") return new TextDecoder("utf-8").decode(u8);
  var s=""; for (var i=0;i<u8.length;i++) s+=String.fromCharCode(u8[i]);
  try { return decodeURIComponent(escape(s)); } catch(e){ return s; }
}
function _amsStrToU8(str){
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(str);
  var s=unescape(encodeURIComponent(str)); var u=new Uint8Array(s.length);
  for (var i=0;i<s.length;i++) u[i]=s.charCodeAt(i); return u;
}
/* Parse a STORED-only zip (what SheetJS writes) into {name, data} entries.
   Returns null on anything unexpected (compressed entry, bad signature) so the
   caller can keep the original bytes. */
function _amsZipParse(u8){
  var n = u8.length, dv = new DataView(u8.buffer, u8.byteOffset, n);
  var e = -1, i, stop = Math.max(0, n - 22 - 65536);
  for (i = n - 22; i >= stop; i--) { if (dv.getUint32(i, true) === 0x06054b50) { e = i; break; } }
  if (e < 0) return null;
  var cnt = dv.getUint16(e + 10, true), cdOff = dv.getUint32(e + 16, true);
  var out = [], p = cdOff, j;
  for (j = 0; j < cnt; j++) {
    if (dv.getUint32(p, true) !== 0x02014b50) return null;
    var method = dv.getUint16(p + 10, true);
    var csz = dv.getUint32(p + 20, true);
    var nl = dv.getUint16(p + 28, true), el = dv.getUint16(p + 30, true), cl = dv.getUint16(p + 32, true);
    var lho = dv.getUint32(p + 42, true);
    if (method !== 0) return null;
    var nm = ""; for (i = 0; i < nl; i++) nm += String.fromCharCode(u8[p + 46 + i]);
    var lnl = dv.getUint16(lho + 26, true), lel = dv.getUint16(lho + 28, true);
    var ds = lho + 30 + lnl + lel;
    out.push({ name: nm, data: u8.subarray(ds, ds + csz) });
    p += 46 + nl + el + cl;
  }
  return out;
}
/* Rebuild a zip from {name, data} entries — everything STORED, CRCs recomputed. */
function _amsZipBuild(entries){
  var i, j, e, total = 22;
  for (i = 0; i < entries.length; i++) total += 30 + 46 + 2 * entries[i].name.length + entries[i].data.length;
  var out = new Uint8Array(total), dv = new DataView(out.buffer);
  var p = 0, offs = [], crcs = [];
  for (i = 0; i < entries.length; i++) {
    e = entries[i]; offs.push(p); crcs.push(_amsCrc32(e.data));
    dv.setUint32(p, 0x04034b50, true); dv.setUint16(p + 4, 20, true);
    dv.setUint16(p + 10, 0, true); dv.setUint16(p + 12, 0x2154, true); /* fixed DOS time/date */
    dv.setUint32(p + 14, crcs[i], true); dv.setUint32(p + 18, e.data.length, true); dv.setUint32(p + 22, e.data.length, true);
    dv.setUint16(p + 26, e.name.length, true);
    p += 30;
    for (j = 0; j < e.name.length; j++) out[p + j] = e.name.charCodeAt(j) & 255;
    p += e.name.length;
    out.set(e.data, p); p += e.data.length;
  }
  var cdStart = p;
  for (i = 0; i < entries.length; i++) {
    e = entries[i];
    dv.setUint32(p, 0x02014b50, true); dv.setUint16(p + 4, 20, true); dv.setUint16(p + 6, 20, true);
    dv.setUint16(p + 12, 0, true); dv.setUint16(p + 14, 0x2154, true);
    dv.setUint32(p + 16, crcs[i], true); dv.setUint32(p + 20, e.data.length, true); dv.setUint32(p + 24, e.data.length, true);
    dv.setUint16(p + 28, e.name.length, true);
    dv.setUint32(p + 42, offs[i], true);
    p += 46;
    for (j = 0; j < e.name.length; j++) out[p + j] = e.name.charCodeAt(j) & 255;
    p += e.name.length;
  }
  dv.setUint32(p, 0x06054b50, true);
  dv.setUint16(p + 8, entries.length, true); dv.setUint16(p + 10, entries.length, true);
  dv.setUint32(p + 12, p - cdStart, true); dv.setUint32(p + 16, cdStart, true);
  return out;
}
function _amsPngSize(u8){
  if (u8 && u8.length > 24 && u8[0] === 0x89 && u8[1] === 0x50)
    return { w: ((u8[16]<<24)|(u8[17]<<16)|(u8[18]<<8)|u8[19])>>>0, h: ((u8[20]<<24)|(u8[21]<<16)|(u8[22]<<8)|u8[23])>>>0 };
  return null;
}
var _amsLogoPngP = null;
function _amsFetchLogoPng(){
  if (_amsLogoPngP) return _amsLogoPngP;
  _amsLogoPngP = fetch("/logo.png")
    .then(function(r){ if (!r.ok) throw new Error("logo http " + r.status); return r.arrayBuffer(); })
    .then(function(ab){ return new Uint8Array(ab); })
    .catch(function(err){ _amsLogoPngP = null; console.warn("[export] logo fetch failed — exporting without logo:", err && err.message); return null; });
  return _amsLogoPngP;
}
/* r130: reseller partner (session.user.partner.logo) → the Excel logo band
   carries the Option A lockup "<partner logo>  POWERED BY  <AMS logo>" as ONE
   composed PNG (same single-picture pipeline as before). Fail-open: any load
   or canvas problem → the plain AMS logo bytes. Cached per partner logo path. */
var _brandLogoPngP = null, _brandLogoKey = "";
function _brandLoadImg9(src){
  return new Promise(function(resolve){
    var done = false, fin = function(v){ if (!done) { done = true; resolve(v); } };
    try { var im = new Image(); im.onload = function(){ fin(im.naturalWidth > 0 ? im : null); }; im.onerror = function(){ fin(null); }; setTimeout(function(){ fin(null); }, 3000); im.src = src; }
    catch (e) { fin(null); }
  });
}
function _brandFetchLogoPng(){
  var p = session && session.user && session.user.partner;
  var key = (p && (p.logoTrim || p.logo)) ? String(p.logoTrim || p.logo) : "";   /* r131: trimmed crop when available */
  if (!key) return _amsFetchLogoPng();
  if (_brandLogoPngP && _brandLogoKey === key) return _brandLogoPngP;
  _brandLogoKey = key;
  _brandLogoPngP = Promise.all([_brandLoadImg9(key), _brandLoadImg9("/logo.png")]).then(function(ims){
    var pi = ims[0], ai = ims[1];
    if (!pi || !ai) throw new Error("brand images unavailable");
    var H = 88, ph = H, pw = Math.round(ph * pi.naturalWidth / pi.naturalHeight);
    var ah = Math.round(H * 0.52), aw = Math.round(ah * ai.naturalWidth / ai.naturalHeight);
    var cv = document.createElement("canvas"), ctx = cv.getContext("2d");
    ctx.font = "bold 15px Arial, Helvetica, sans-serif";
    var lbl = "POWERED BY", lw = Math.round(ctx.measureText(lbl).width + 10 * 1.5);
    var gap = 22, sepW = 2;
    cv.width = pw + gap + sepW + gap + lw + 14 + aw; cv.height = H;
    ctx = cv.getContext("2d");
    ctx.drawImage(pi, 0, 0, pw, ph);
    var x = pw + gap;
    ctx.fillStyle = "#CBD5E1"; ctx.fillRect(x, Math.round(H * 0.1), sepW, Math.round(H * 0.8));
    x += sepW + gap;
    ctx.fillStyle = "#64748b"; ctx.font = "bold 15px Arial, Helvetica, sans-serif"; ctx.textBaseline = "middle";
    try { ctx.letterSpacing = "1.5px"; } catch (e) {}
    ctx.fillText(lbl, x, Math.round(H / 2));
    x += lw + 14;
    ctx.drawImage(ai, x, Math.round((H - ah) / 2), aw, ah);
    return new Promise(function(resolve, reject){
      try { cv.toBlob(function(b){ if (!b) return reject(new Error("toBlob failed")); b.arrayBuffer().then(function(ab){ resolve(new Uint8Array(ab)); }, reject); }, "image/png"); }
      catch (e) { reject(e); }
    });
  }).catch(function(err){
    console.warn("[export] partner lockup failed — using the AMS logo:", err && err.message);
    _brandLogoPngP = null; _brandLogoKey = "";
    return _amsFetchLogoPng();
  });
  return _brandLogoPngP;
}
/* Inject the logo into workbook bytes. Resolves with patched bytes, or the
   original bytes when the logo/patch is unavailable. Anchored to the blank
   band the export code leaves at the top of the sheet (rows 1-3). */
function _amsBrandXlsxBytes(wbBytes){
  return _brandFetchLogoPng().then(function(png){   /* r130: partner lockup when a reseller partner is on the session */
    if (!png) return wbBytes;
    try {
      var entries = _amsZipParse(wbBytes);
      if (!entries) return wbBytes;
      var map = {}, i;
      for (i = 0; i < entries.length; i++) map[entries[i].name] = entries[i];
      var shE = map["xl/worksheets/sheet1.xml"];
      var ctE = map["[Content_Types].xml"];
      if (!shE || !ctE || map["xl/drawings/drawing1.xml"]) return wbBytes;
      var sz = _amsPngSize(png) || { w: 400, h: 120 };
      var hPx = 44; /* fits inside the 3-row logo band */
      var wPx = Math.max(1, Math.round(hPx * sz.w / Math.max(1, sz.h)));
      var EMU = 9525, cx = wPx * EMU, cy = hPx * EMU;
      /* 1 · sheet → drawing relationship */
      var relsName = "xl/worksheets/_rels/sheet1.xml.rels";
      var relId = "rIdAmsLogo1";
      var relXml = '<Relationship Id="' + relId + '" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/>';
      if (map[relsName]) {
        var rs = _amsU8ToStr(map[relsName].data);
        if (rs.indexOf("relationships/drawing") >= 0) return wbBytes;
        map[relsName].data = _amsStrToU8(rs.replace("</Relationships>", relXml + "</Relationships>"));
      } else {
        entries.push({ name: relsName, data: _amsStrToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' + relXml + "</Relationships>") });
      }
      /* 2 · worksheet references the drawing (schema places <drawing> at the end) */
      var sx = _amsU8ToStr(shE.data);
      if (sx.indexOf("<drawing ") >= 0 || sx.indexOf("</worksheet>") < 0) return wbBytes;
      if (sx.indexOf("xmlns:r=") < 0) sx = sx.replace("<worksheet ", '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ');
      shE.data = _amsStrToU8(sx.replace("</worksheet>", '<drawing r:id="' + relId + '"/></worksheet>'));
      /* 3 · the drawing part itself: one picture, one-cell anchor at A1 */
      var drXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        '<xdr:oneCellAnchor>' +
        '<xdr:from><xdr:col>0</xdr:col><xdr:colOff>76200</xdr:colOff><xdr:row>0</xdr:row><xdr:rowOff>57150</xdr:rowOff></xdr:from>' +
        '<xdr:ext cx="' + cx + '" cy="' + cy + '"/>' +
        '<xdr:pic><xdr:nvPicPr><xdr:cNvPr id="2" name="AMS Analytix"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr>' +
        '<xdr:blipFill><a:blip r:embed="rId1"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill>' +
        '<xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="' + cx + '" cy="' + cy + '"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr>' +
        '</xdr:pic><xdr:clientData/></xdr:oneCellAnchor></xdr:wsDr>';
      entries.push({ name: "xl/drawings/drawing1.xml", data: _amsStrToU8(drXml) });
      entries.push({ name: "xl/drawings/_rels/drawing1.xml.rels", data: _amsStrToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/image1.png"/></Relationships>') });
      entries.push({ name: "xl/media/image1.png", data: png });
      /* 4 · content types */
      var cs = _amsU8ToStr(ctE.data);
      if (cs.indexOf('Extension="png"') < 0) cs = cs.replace("</Types>", '<Default Extension="png" ContentType="image/png"/></Types>');
      if (cs.indexOf("drawing1.xml") < 0) cs = cs.replace("</Types>", '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>');
      ctE.data = _amsStrToU8(cs);
      return _amsZipBuild(entries);
    } catch (err) {
      console.warn("[export] logo embed failed — exporting without logo:", err && err.message);
      return wbBytes;
    }
  });
}
/* r87: report-request helpers (admin banner + tab) */
var _REP_ORDER_LABELS9 = { market_overview: "Market Overview", manufacturer_overview: "Manufacturer Overview", agent_overview: "Agent Overview", therapeutic_overview: "Therapeutic Area · ATC2", therapeutic_atc3_overview: "Therapeutic Area · ATC3", molecule_overview: "Product Overview", sales_tracker_diagnostic: "Sell-In / Sell-Out Analysis" };
function _repOrderLabel9(k) { return _REP_ORDER_LABELS9[k] || String(k || "report"); }
/* r90: what the full report must be prepared for — period + market + deck options */
function _repOrderSel9(o) {
  var parts = [];
  if (o.Period) parts.push(o.Period); else if (o.PeriodType) parts.push(o.PeriodType === "mat" ? "MAT" : o.PeriodType === "quarter" ? "Quarter" : "Full year");
  if (o.Market) parts.push({ total: "Total market", private: "Private sector", public: "Public sector" }[String(o.Market).toLowerCase()] || o.Market);
  var det = null; try { det = o.Details ? JSON.parse(o.Details) : null; } catch (e) { det = null; }
  if (det && det.level) parts.push(String(det.level).toUpperCase() + " level");
  if (det && det.focusManufacturers && det.focusManufacturers.length) parts.push("focus: " + det.focusManufacturers.join(", "));
  if (det && det.atc3 && det.atc3.length) parts.push("ATC3: " + det.atc3.join(", "));
  return React.createElement("div", null,
    React.createElement("div", { style: { fontWeight: 700, color: "#005487" } }, parts.length ? parts.slice(0, 2).join(" \u00b7 ") : "\u2014"),
    parts.length > 2 ? React.createElement("div", { style: { fontSize: 9.5, color: "#64748b", marginTop: 2, whiteSpace: "normal", maxWidth: 260 } }, parts.slice(2).join(" \u00b7 ")) : null);
}
function _fmtWhen9(d) { if (!d) return "\u2014"; try { var dt = new Date(d); return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) + " " + dt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); } catch (e) { return String(d); } }
function _amsSaveXlsxBytes(bytes, fname){
  var blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  var a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  setTimeout(function(){ try { URL.revokeObjectURL(a.href); document.body.removeChild(a); } catch(e){} }, 2000);
}
/* Drop-in replacement for XLSX.writeFile that brands the workbook first. */
function _amsWriteXlsxWithLogo(XLSX, wb, fname){
  var bytes = null;
  try { bytes = new Uint8Array(XLSX.write(wb, { bookType: "xlsx", type: "array", compression: false })); }
  catch (e) { console.warn("[export] byte write failed — falling back to plain writeFile:", e && e.message); XLSX.writeFile(wb, fname); return; }
  _amsBrandXlsxBytes(bytes)
    .then(function(fin){ _amsSaveXlsxBytes(fin || bytes, fname); })
    .catch(function(){ _amsSaveXlsxBytes(bytes, fname); });
}


/* ── Extracted modules (Tier 1 + Tier 2 split) ── */
import { fmt, fmtPrice, fmtVal } from "./utils/format";
import {
  parseYear, parseQ, parseMonth,
  rowPeriodKey, periodLabel, realPrevPeriodKey,
  buildPeriodOpts, buildPeriodOptsFromDims,
  uniq, buildMatYtdLookup,
  periodKeyType, PERIOD_TYPE_META, realYoyPeriodKey
} from "./utils/period";
/* r108: the OPEN (partial) quarter of the active study — set from the dimensions payload
   (openQuarter) — is labelled with a star everywhere: "Q3 2026*", "MAT Q3 2026*". */
function _openQKey9() { try { return (window.__amsxOpenQ9 && window.__amsxOpenQ9.key) || ""; } catch (e) { return ""; } }
function _isOpenPk9(pk) {
  var k = _openQKey9(); if (!k || !pk) return false;
  var b = String(pk).split("|")[0];
  if (b === k) return true;
  var m = /^MAT-Q([1-4])-(\d{4})$/.exec(b); if (m && (m[2] + "-Q" + m[1]) === k) return true;
  var y = /^YTD-(\d{4})$/.exec(b); if (y && k.indexOf(y[1] + "-Q") === 0) return true;
  return false;
}
function periodLabel9(pk) { var l = periodLabel(pk); return _isOpenPk9(pk) && String(l).indexOf("*") < 0 ? l + "*" : l; }
import { autoMap } from "./utils/autoMap";
import { filterRows } from "./utils/filterRows";
import {
  MONTH_S, MONTH_N, ALL_MEASURES, FEAT_LBL, HIER, FIELD_DEFS, EMPTY_SEL,
  DASH_COLORS, DASH_COLORS2, AMSX_LOGO,
  SECTOR_TYPES, DIMENSION_KEYS
} from "./constants";
import AmsxLogo from "./components/AmsxLogo";
import FilterPanel, { MultiSelect, FilterSection, FilterFeatRow } from "./components/FilterPanel";


/* ── Tier 3 split: API helpers + dashboards module ── */
import {
  api, API_BASE, session,
  _b64ToBuf, _importEncKey, _decryptPayload, _maybeDecrypt,
  _SESSION_ID, logActivity,
  getOrCreateDeviceId, getDeviceName, storeDeviceToken, retrieveDeviceToken, clearDeviceToken
} from "./utils/api";
import { wmDecodeDots, wmDecodeZeroWidth } from "./utils/wm";

/* ── r83 report checker helpers (admin → Export Tracer → Check File) ──
   PPTX / PDF / PNG / JPG: metadata marker → zero-width text → invisible
   DOT-CODE on the images (survives screenshots). Libraries load on demand. */
function _wmLoadScript(src, test) {
  return new Promise(function (resolve, reject) {
    if (test()) return resolve(test());
    var sc = document.createElement("script"); sc.src = src;
    sc.onload = function () { if (test()) resolve(test()); else reject(new Error("library loaded but unavailable: " + src)); };
    sc.onerror = function () { reject(new Error("could not load " + src)); };
    document.head.appendChild(sc);
  });
}
function _wmImageData(src, maxW) {
  /* src: Blob | dataURL | HTMLImageElement → ImageData (downscaled to maxW px wide) */
  return new Promise(function (resolve, reject) {
    var done = function (im) {
      try {
        var sc = (maxW && im.naturalWidth > maxW) ? maxW / im.naturalWidth : 1;
        var cv = document.createElement("canvas"); cv.width = Math.max(1, Math.round(im.naturalWidth * sc)); cv.height = Math.max(1, Math.round(im.naturalHeight * sc));
        var ctx = cv.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, cv.width, cv.height); ctx.drawImage(im, 0, 0, cv.width, cv.height);
        resolve(ctx.getImageData(0, 0, cv.width, cv.height));
      } catch (e) { reject(e); }
    };
    if (src && src.naturalWidth) return done(src);
    var im = new Image();
    im.onload = function () { done(im); try { if (im._url) URL.revokeObjectURL(im._url); } catch (e) {} };
    im.onerror = function () { reject(new Error("image could not be decoded")); };
    if (typeof src === "string") im.src = src; else { im._url = URL.createObjectURL(src); im.src = im._url; }
  });
}
function _wmDecodeImages(list, maxW) {
  /* decode a list of image sources in order; stop at the first confident hit */
  var best = null, i = 0;
  var next = function () {
    if (i >= list.length) return Promise.resolve(best);
    var src = list[i++];
    return _wmImageData(src, maxW || 2800).then(function (d) { return wmDecodeDots(d); }).catch(function () { return null; }).then(function (r) {
      if (r && r.ok && (!best || !best.ok || r.confidence > best.confidence)) best = r; else if (r && !best) best = r;
      if (best && best.ok && best.confidence >= 0.5) return best;
      return next();
    });
  };
  return next();
}
function _wmXmlText(xml) {
  /* all <a:t>…</a:t> runs of a slide, entities decoded */
  var out = [], re = /<a:t[^>]*>([\s\S]*?)<\/a:t>/g, m;
  while ((m = re.exec(xml))) out.push(m[1]);
  return out.join(" ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#x([0-9A-Fa-f]+);/g, function (_, h) { return String.fromCharCode(parseInt(h, 16)); }).replace(/&#(\d+);/g, function (_, dd) { return String.fromCharCode(parseInt(dd, 10)); });
}
function _wmCheckReport(file) {
  var name = String(file.name || ""), ext = (name.split(".").pop() || "").toLowerCase();
  var res = { fileName: name, kind: ext, sources: [] };
  var idRe = /AMSX-[0-9A-F]{8,}/i;
  var pickId = function (txt, src) {
    var zw = wmDecodeZeroWidth(txt), m = idRe.exec(txt || "");
    if (zw && res.sources.indexOf("zero-width text") < 0) res.sources.push("zero-width text");
    if (m && res.sources.indexOf(src) < 0) res.sources.push(src);
    if (res.exportId) return;
    if (zw) { res.exportId = zw; res.source = src + " (zero-width text)"; }
    else if (m) { res.exportId = m[0].toUpperCase(); res.source = src; }
  };
  var finish = function () {
    if (res.dots && res.dots.ok) res.sources.push("dot-code");
    res.none = !res.exportId && !(res.dots && res.dots.ok);
    return res;
  };
  if (ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "webp" || ext === "bmp") {
    return _wmDecodeImages([file]).then(function (r) { res.dots = r; return finish(); });
  }
  if (ext === "pptx") {
    return _wmLoadScript("https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js", function () { return window.JSZip; }).then(function (JSZip) {
      return JSZip.loadAsync(file);
    }).then(function (zip) {
      var core = zip.file("docProps/core.xml"), app9 = zip.file("docProps/app.xml");
      var p0 = core ? core.async("string").then(function (x) { var m = /<dc:subject>([\s\S]*?)<\/dc:subject>/.exec(x); if (m) pickId(m[1], "document subject"); var a = /<dc:creator>([\s\S]*?)<\/dc:creator>/.exec(x); if (a) res.author = a[1]; }) : Promise.resolve();
      var slides = Object.keys(zip.files).filter(function (k) { return /^ppt\/slides\/slide\d+\.xml$/.test(k); }).sort(function (a, b) { return parseInt(a.replace(/\D/g, ""), 10) - parseInt(b.replace(/\D/g, ""), 10); });
      var p1 = Promise.all(slides.slice(0, 40).map(function (k) { return zip.file(k).async("string").then(function (x) { pickId(_wmXmlText(x), "slide text"); }); }));
      var media = Object.keys(zip.files).filter(function (k) { return /^ppt\/media\/.*\.(png|jpe?g)$/i.test(k); });
      return Promise.all([p0, p1, app9 ? app9.async("string").catch(function(){ return ""; }) : Promise.resolve("")]).then(function () {
        /* biggest 4 images first (slide captures), decode until a confident hit */
        return Promise.all(media.map(function (k) { return zip.file(k).async("blob").then(function (b) { return { k: k, b: b }; }); }));
      }).then(function (blobs) {
        blobs.sort(function (a, b) { return b.b.size - a.b.size; });
        return _wmDecodeImages(blobs.slice(0, 4).map(function (x) { return x.b; }));
      }).then(function (r) { res.dots = r; res.images = media.length; return finish(); });
    });
  }
  if (ext === "pdf") {
    return _wmLoadScript("https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.min.js", function () { return window.pdfjsLib; }).then(function (pdfjs) {
      try { if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js"; } catch (e) {}
      return new Promise(function (resolve, reject) { var fr = new FileReader(); fr.onload = function (e) { resolve(e.target.result); }; fr.onerror = reject; fr.readAsArrayBuffer(file); })
        .then(function (buf) { return pdfjs.getDocument({ data: buf }).promise; });
    }).then(function (doc) {
      res.pages = doc.numPages;
      var pMeta = doc.getMetadata().then(function (md) { var info = (md && md.info) || {}; if (info.Subject) pickId(String(info.Subject), "document subject"); if (info.Author) res.author = String(info.Author); }).catch(function () {});
      var nP = Math.min(doc.numPages, 3), chain = pMeta, imgs = [];
      var _one = function (pn) {
        return doc.getPage(pn).then(function (page) {
          return page.getTextContent().then(function (tc) { pickId((tc.items || []).map(function (it) { return it.str; }).join(" "), "page " + pn + " text"); })
            .then(function () {
              var vp = page.getViewport({ scale: 2 });
              var cv = document.createElement("canvas"); cv.width = Math.round(vp.width); cv.height = Math.round(vp.height);
              return page.render({ canvasContext: cv.getContext("2d"), viewport: vp }).promise.then(function () { imgs.push(cv.toDataURL("image/png")); });
            });
        });
      };
      for (var pn = 1; pn <= nP; pn++) (function (q) { chain = chain.then(function () { return _one(q); }); })(pn);
      return chain.then(function () { return _wmDecodeImages(imgs); }).then(function (r) { res.dots = r; return finish(); });
    });
  }
  return Promise.reject(new Error("unsupported file type ." + ext));
}
import DashboardsPanel, { ReportsPanel, SalesTrackerPanel, MarketHeaderControl } from "./dashboards";

/* ── DATA-side admin test ──
   role "admin" opens the admin panel, but only a FULL admin (no dataScoped flag)
   is unrestricted on the DATA side. Limited/team admins are logged in with their
   linked client account's permissions (server enforces the same): they pass
   their clientId on every data call and obey study/dimension/measure/statistic/
   dashboard grants, period caps and data scope exactly like that client. */
function _isDataAdmin(u) { return !!(u && u.role === "admin" && !u.dataScoped); }
/* r138: per-client, per-study TAB VISIBILITY (Admin → Client → study → "Tabs").
   ClientStudyAccess.HiddenTabs → studyAccess[i].hiddenTabs = ["market" | "dashboards" |
   "reports"]. Resolved for the active study from the user's resolved studyAccess (client
   entries carry the flags; a user-added study has none). Admins are never affected. */
function _hiddenTabs9(user, clientPerms, studyCode) {
  var out = {};
  if (!user || !studyCode || _isDataAdmin(user)) return out;
  var list = (Array.isArray(user.studyAccess) && user.studyAccess.length) ? user.studyAccess
           : ((clientPerms && Array.isArray(clientPerms.studyAccess)) ? clientPerms.studyAccess : []);
  for (var i = 0; i < list.length; i++) {
    if (list[i] && list[i].studyTypeCode === studyCode) {
      (Array.isArray(list[i].hiddenTabs) ? list[i].hiddenTabs : []).forEach(function (t) { out[String(t).toLowerCase()] = true; });
      break;
    }
  }
  return out;
}
var _TAB_KEY9 = { pivot: "market", dashboards: "dashboards", reports: "reports" };
/* r138b-guard */






/* ════════════════════════════════════════════════════════════
   BUILD PIVOT  — up to 8 hierarchical levels (MAX_PIVOT_DIMS)
   dims ordered by feature-selection order (sel.showCols order)
   Returns { grand, nodes, dims, dimLabels, periods, msrs,
             tree, parLabel, childLabel }
════════════════════════════════════════════════════════════ */
/* r126: default pivot sort key = first selected measure (selection order), mapped to the
   node-data key used by sortNodes. Price / ratio-only measures are skipped. */
var _DEF_SORT_KEY9 = { USD: "d", LC: "l", Unit: "u", UnitIU: "iu", Patients: "np", Prescriptions: "nrx",
                       Doctors: "ndr", DoctorsTotal: "ndrt", RxPerDoctor: "rxpd", Pharmacies: "nph", Gross: "gu", SellInValue: "sv", Profit: "pf" };
function _defaultSortKey(msrs) {
  msrs = msrs || [];
  for (var i = 0; i < msrs.length; i++) { var k = _DEF_SORT_KEY9[msrs[i]]; if (k) return k; }
  return null;
}
function buildPivot(rows, mp, sel, pOpts, opts) {
  var _opts = opts || {};
  const msrs   = sel.measures;
  const hasPer = sel.periodMode && sel.periodKeys.length > 0;
  /* ── Flow split (Sales Tracker): every selected base period × selected flow →
     compound "base~Flow" column bands ("2026-Q1~Sell-In" | "2026-Q1~Sell-Out").
     Works with EVERY period type (Yearly/Quarterly/Monthly/MAT/YTD) because the
     compound key keeps the base key's type, sort, label and growth semantics.
     Active only when the server returned the flow column in the row grain
     (mp.flow set on the RESULT mapping — guarantees rows actually carry it). */
  var _flowList = (mp && mp.flow && hasPer) ? (sel.periodFlows === undefined ? ["Sell-In", "Sell-Out"] : sel.periodFlows) : [];
  var isFlowSplit = _flowList.length > 0;
  var periods = hasPer ? sel.periodKeys : ["ALL"];
  if (isFlowSplit) {
    var _fpx = [];
    for (var _fe = 0; _fe < periods.length; _fe++) {
      for (var _ff = 0; _ff < _flowList.length; _ff++) _fpx.push(periods[_fe] + "~" + _flowList[_ff]);
    }
    periods = _fpx;
  }
  /* ── Per-key period TYPE (multi-period-type support) ──
     Every period key is self-typed by its format (2026 / 2026-Q1 / 2026-01 /
     MAT-Q1-2026 / MATM-1-2026 / YTD-2026 / YTDM-2026 / 2024_Awarded). One
     query may now mix several types; each key is attributed, sorted and grown
     WITHIN its own type family. */
  var keyTypes = {};
  var typeSeen = {};
  if (hasPer) {
    for (var _kt = 0; _kt < periods.length; _kt++) {
      var _ktv = periodKeyType(periods[_kt]);
      keyTypes[periods[_kt]] = _ktv;
      if (_ktv) typeSeen[_ktv] = 1;
    }
  }
  var isTenderMode = sel.periodMode === "TenderStatus" || !!typeSeen["TenderStatus"];
  /* Flow-compound keys carry their BASE type (periodKeyType strips "~Flow"), so
     multi-type banding works unchanged when flows are split. */
  var isMulti = !isTenderMode && Object.keys(typeSeen).length > 1;
  const isMatYtd = !!(typeSeen["MAT"] || typeSeen["MAT Monthly"] || typeSeen["YTD"] || typeSeen["YTD Monthly"]);

  /* ── Hidden fetch-only periods for Growth-vs-Previous ──
     For every selected period, its REAL calendar-previous period is accumulated
     too (the server widens its fetch accordingly) but is NEVER displayed as a
     column. This makes growth compare 2026-Q1 vs 2025-Q4 even when 2025-Q4 was
     not part of the user's selection. */
  var hiddenPrev = [];
  var accSet = {};
  for (var _ap = 0; _ap < periods.length; _ap++) accSet[periods[_ap]] = 1;
  if (hasPer) {
    for (var _hp = 0; _hp < periods.length; _hp++) {
      var _rpk0 = realPrevPeriodKey(periods[_hp]);
      if (_rpk0 && accSet[_rpk0] !== 1) { accSet[_rpk0] = 1; hiddenPrev.push(_rpk0); }
      /* Growth-vs-Similar base (same period last year, same type) — accumulated
         hidden too, so "Gth Similar" works even when last year isn't selected. */
      var _ryk0 = realYoyPeriodKey(periods[_hp]);
      if (_ryk0 && accSet[_ryk0] !== 1) { accSet[_ryk0] = 1; hiddenPrev.push(_ryk0); }
    }
  }
  var accPeriods = periods.concat(hiddenPrev);

  /* ── Σ Period-Total band (optional, sel.periodTotal) ──
     A synthetic period that SUMS the selected point periods (3 quarters,
     6 months, ...) into one extra column band, with the selected statistics
     computed on it. Growth base = the SAME periods one year earlier, summed
     the same way (accumulated hidden). Point-period selections only —
     summing MAT/YTD windows would double-count months. */
  var PT_KEY = "__PTOTAL__", PTY_KEY = "__PTOTALY__";
  var _ptOn = !!(sel && sel.periodTotal) && hasPer && !isTenderMode && !isFlowSplit && !isMulti && !isMatYtd && periods.length > 1;
  var _ptSel = {}, _ptYoy = {};
  if (_ptOn) {
    for (var _pt0 = 0; _pt0 < periods.length; _pt0++) {
      _ptSel[periods[_pt0]] = 1;
      var _ptY0 = realYoyPeriodKey(periods[_pt0]);
      if (_ptY0) _ptYoy[_ptY0] = 1;
    }
    accPeriods = accPeriods.concat([PT_KEY, PTY_KEY]);
  }

  /* Preserve selection order, cap at MAX_PIVOT_DIMS */
  const dims = sel.showCols.filter(function(k){ return mp[k]; }).slice(0, MAX_PIVOT_DIMS);
  if (!dims.length) return null;
  /* allDims: the full dimension list (may include dims not yet in data) */
  var allDims = _opts.allDims || dims;

  const pct = function(a, b){ return b ? ((a / b) * 100).toFixed(1) : "0.0"; };

  /* Build MAT/YTD lookup if needed. Flow split: the lookup only understands BASE
     keys (its regexes are format-anchored), so feed it the deduped bases of the
     accumulated compound periods; attribution re-appends the row's flow suffix. */
  var _lookupSrc = accPeriods;
  if (isFlowSplit) {
    var _bSeen = {}; _lookupSrc = [];
    for (var _lb = 0; _lb < accPeriods.length; _lb++) {
      var _lbk = String(accPeriods[_lb]);
      var _lbt = _lbk.indexOf("~");
      if (_lbt >= 0) _lbk = _lbk.slice(0, _lbt);
      if (!_bSeen[_lbk]) { _bSeen[_lbk] = 1; _lookupSrc.push(_lbk); }
    }
  }
  var matYtdLookup = isMatYtd ? buildMatYtdLookup(_lookupSrc, pOpts) : null;

  /* ── Accumulate into nested plain-object trie ── */
  var trie = {};
  var grandRaw = {};
  for (var pi = 0; pi < accPeriods.length; pi++) grandRaw[accPeriods[pi]] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};

  /* # Patients lookup: when provided by server, bypass row-level summing for np (distinct counts don't sum).
     The lookup is keyed as "dimVal1|dimVal2|...|periodKey" → correct COUNT(DISTINCT PatientID) at display period level. */
  var _patientsLookup = _opts.patientsLookup;
  var _useNpLookup = !!_patientsLookup && mp.patients;
  /* # Prescriptions lookup: same pattern as patients — server pre-aggregates COUNT(DISTINCT OrderNo)
     at display-period level since distinct counts don't sum across rows. */
  var _rxLookup = _opts.rxLookup;
  var _useNrxLookup = !!_rxLookup && mp.prescriptions;
  /* Rx per Doctor (r121): prescriptions ÷ distinct prescribing doctors of the row's speciality
     (whole panel when Speciality is not on the layout / above the speciality level). Stored on
     every node as rxpd — a ratio, never summed. */
  var _drLookup = _opts.doctorsLookup;
  var _useRxPd = !!_drLookup && !!mp.rxPerDoctor && _useNrxLookup;
  var _drSpecIdx = -1;
  for (var _dsi = 0; _dsi < dims.length; _dsi++) { if (String(dims[_dsi]).toLowerCase() === "docspec") { _drSpecIdx = _dsi; break; } }
  /* # Doctors (Total) (r122): the same speciality / panel doctor count shown as its own measure */
  var _useNdrt = !!_drLookup && !!mp.doctorsTotal;
  var _denOf = function (pathKey, pk) {
    if (!_drLookup) return null;
    var den = null;
    if (_drSpecIdx >= 0) { var sp = String(pathKey || "").split("|")[_drSpecIdx]; if (sp) { den = _drLookup["S|" + sp + "|" + pk]; if (den == null) return null; } }
    if (den == null) den = _drLookup["T|" + pk];
    return den == null ? null : Number(den) || 0;
  };
  var _rxPdOf = function (pathKey, pk, nrx) {
    if (!_useRxPd) return null;
    var den = _denOf(pathKey, pk);
    return (den > 0) ? (Number(nrx) || 0) / den : null;
  };
  var _ndrtOf = function (pathKey, pk) { return _useNdrt ? _denOf(pathKey, pk) : null; };
  /* # Doctors (r122): distinct prescribing doctors per row — server lookup, same shape as # Prescriptions */
  var _ndrLookup = _opts.doctorsDistinctLookup;
  var _useNdrLookup = !!_ndrLookup && mp.doctors;
  /* # Pharmacies lookup: same pattern as patients — server pre-aggregates
     COUNT(DISTINCT PharmacyID) at display-period level (distinct counts don't sum). */
  var _pharmaciesLookup = _opts.pharmaciesLookup;
  var _useNphLookup = !!_pharmaciesLookup && mp.pharmacies;

  for (var ri = 0; ri < rows.length; ri++) {
    var row = rows[ri];
    var pks;
    if (!hasPer) {
      pks = ["ALL"];
    } else if (isTenderMode) {
      var tPk = rowPeriodKey(row, mp, "TenderStatus");
      if (!tPk || accSet[tPk] !== 1) continue; /* selected OR hidden-prev periods */
      pks = [tPk];
    } else {
      /* ── Generalized multi-type attribution ──
         One row can belong to SEVERAL period keys when types are mixed
         (a March-2026 row feeds Mar 2026, Q1 2026, 2026, MAT Q1 2026 and
         YTD 2026 columns simultaneously). Candidates are generated only for
         the types actually selected, then gated by accSet (selected + hidden
         growth-base periods). Single-type queries behave exactly as before. */
      pks = [];
      var ry = parseYear(row[mp.yearCol]);
      var rq = (ry && mp.quarterCol) ? parseQ(row[mp.quarterCol]) : null;
      var rm = (ry && mp.monthCol) ? parseMonth(row[mp.monthCol]) : null;
      /* Flow split: the row's own flow value picks which compound band it feeds */
      var _rfSuf = "";
      if (isFlowSplit) {
        var _rfv = String(row[mp.flow] != null ? row[mp.flow] : "").trim();
        if (!_rfv) continue; /* row carries no flow → cannot belong to any band */
        _rfSuf = "~" + _rfv;
      }
      if (typeSeen["Yearly"] && ry && accSet[String(ry) + _rfSuf] === 1) pks.push(String(ry) + _rfSuf);
      if (typeSeen["Quarterly"] && rq) { var qk = ry + "-Q" + rq + _rfSuf; if (accSet[qk] === 1) pks.push(qk); }
      if (typeSeen["Monthly"] && rm) { var mk = ry + "-" + (rm < 10 ? "0" : "") + rm + _rfSuf; if (accSet[mk] === 1) pks.push(mk); }
      if (matYtdLookup) {
        /* Quarter-space keys (MAT-Q*, YTD-*) and month-space keys (MATM-*,
           YTDM-*, month-built MATs) live in DISJOINT lookup key spaces, so
           running both lookups never double-counts a period key. The lookup
           holds BASE keys; flow split re-appends the row's suffix and gates by
           accSet (the non-split path's gating stays implicit as before). */
        var _mConcat = function(baseKeys) {
          if (!isFlowSplit) return baseKeys;
          var _outC = [];
          for (var _bc = 0; _bc < baseKeys.length; _bc++) {
            var _ck = baseKeys[_bc] + _rfSuf;
            if (accSet[_ck] === 1) _outC.push(_ck);
          }
          return _outC;
        };
        if (rq) { var qk1 = ry + "-Q" + rq; if (matYtdLookup[qk1]) pks = pks.concat(_mConcat(matYtdLookup[qk1])); }
        if (rm) { var mmk1 = ry + "-" + rm; if (matYtdLookup[mmk1]) pks = pks.concat(_mConcat(matYtdLookup[mmk1])); }
      }
      /* Deduplicate */
      var seen = {};
      pks = pks.filter(function(p){ if (seen[p]) return false; seen[p]=1; return true; });
      if (!pks.length) continue;
      /* Σ Period-Total attribution: row belongs to the total when its point key
         is one of the SELECTED periods; to the total's growth base when it is
         one of their year-earlier counterparts. */
      if (_ptOn) {
        var _gotT9 = false, _gotY9 = false;
        for (var _pa9 = 0; _pa9 < pks.length; _pa9++) {
          if (_ptSel[pks[_pa9]]) _gotT9 = true;
          if (_ptYoy[pks[_pa9]]) _gotY9 = true;
        }
        if (_gotT9) pks.push(PT_KEY);
        if (_gotY9) pks.push(PTY_KEY);
      }
    }

    /* Always accumulate all measures (needed for TopN ranking by any measure) */
    var u = mp.units    ? (Number(row[mp.units])    || 0) : 0;
    var fu = mp.focUnit ? (Number(row[mp.focUnit])  || 0) : 0;
    var gu = mp.grossUnit ? (Number(row[mp.grossUnit]) || 0) : 0;
    var l = mp.valLC    ? (Number(row[mp.valLC])    || 0) : 0;
    var d = mp.valUSD   ? (Number(row[mp.valUSD])   || 0) : 0;
    /* Profit (Sales Tracker): Sell-Out rows carry Units × (sell-out price − sell-in price),
       summed server-side per group (ProfitUSD preferred, ProfitLC otherwise). */
    var pf9 = mp.profitUSD ? (Number(row[mp.profitUSD]) || 0) : 0; /* Profit USD */
    var pfl9 = mp.profitLC ? (Number(row[mp.profitLC]) || 0) : 0; /* Profit LC — the Profit STATISTIC shows the currency matching the scoped measure */
    /* Sell-Out Value (Sales Tracker, view v7): the RETAIL-priced sell-out
       value — sv = USD, svl = LC. Shown as the SELL-OUT VALUE column of the
       Profit statistic; the MAIN Value measure is at the sell-in price
       (apples-to-apples with the Sell-In flow — Noura 2026-09-23). Falls back
       to the legacy SellInValue mapping on a pre-v7 view. */
    var sv9 = mp.sellOutValUSD ? (Number(row[mp.sellOutValUSD]) || 0) : (mp.sellInValUSD ? (Number(row[mp.sellInValUSD]) || 0) : 0);
    var svl9 = mp.sellOutValLC ? (Number(row[mp.sellOutValLC]) || 0) : (mp.sellInValLC ? (Number(row[mp.sellInValLC]) || 0) : 0);
    /* Prices: server returns AVG per group; accumulate as-is for raw rows, or track count for re-averaging */
    var pl = mp.priceLC  ? (Number(row[mp.priceLC])  || 0) : 0;
    var pd = mp.priceUSD ? (Number(row[mp.priceUSD]) || 0) : 0;
    var hasPrice = (pl !== 0 || pd !== 0) ? 1 : 0;
    var npl = mp.netPriceLC  ? (Number(row[mp.netPriceLC])  || 0) : 0;
    var npd = mp.netPriceUSD ? (Number(row[mp.netPriceUSD]) || 0) : 0;
    var hasNetPrice = (npl !== 0 || npd !== 0) ? 1 : 0;
    /* GPD IU family: Units_ui summed; *_ui prices averaged like pl/pd */
    var iu9 = mp.unitsIU ? (Number(row[mp.unitsIU]) || 0) : 0;
    var pli9 = mp.priceLCIU ? (Number(row[mp.priceLCIU]) || 0) : 0;
    var pdi9 = mp.priceUSDIU ? (Number(row[mp.priceUSDIU]) || 0) : 0;
    var hasPriceIU = (pli9 !== 0 || pdi9 !== 0) ? 1 : 0;

    for (var pki = 0; pki < pks.length; pki++) {
      var pk = pks[pki];
      if (!grandRaw[pk]) continue;
      grandRaw[pk].u += u; grandRaw[pk].fu += fu; grandRaw[pk].gu += gu; grandRaw[pk].l += l; grandRaw[pk].d += d; grandRaw[pk].pf += pf9; grandRaw[pk].pfl += pfl9; grandRaw[pk].sv += sv9; grandRaw[pk].svl += svl9;
      grandRaw[pk].pl += pl; grandRaw[pk].pd += pd; grandRaw[pk].pc += hasPrice;
      grandRaw[pk].npl += npl; grandRaw[pk].npd += npd; grandRaw[pk].npc += hasNetPrice;
      grandRaw[pk].iu += iu9; grandRaw[pk].pli += pli9; grandRaw[pk].pdi += pdi9; grandRaw[pk].pci += hasPriceIU;

      var node = trie;
      for (var di = 0; di < dims.length; di++) {
        var val = String(row[mp[dims[di]]] != null ? row[mp[dims[di]]] : "?");
        if (!node[val]) node[val] = {};
        node = node[val];
      }
      if (!node["__DATA__"]) {
        node["__DATA__"] = {};
        for (var pi2 = 0; pi2 < accPeriods.length; pi2++) node["__DATA__"][accPeriods[pi2]] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      }
      node["__DATA__"][pk].u += u;
      node["__DATA__"][pk].fu += fu;
      node["__DATA__"][pk].gu += gu;
      node["__DATA__"][pk].l += l;
      node["__DATA__"][pk].d += d;
      node["__DATA__"][pk].pf += pf9;
      node["__DATA__"][pk].pfl += pfl9;
      node["__DATA__"][pk].sv += sv9;
      node["__DATA__"][pk].svl += svl9;
      node["__DATA__"][pk].pl += pl;
      node["__DATA__"][pk].pd += pd;
      node["__DATA__"][pk].pc += hasPrice;
      node["__DATA__"][pk].npl += npl;
      node["__DATA__"][pk].npd += npd;
      node["__DATA__"][pk].npc += hasNetPrice;
      node["__DATA__"][pk].iu += iu9;
      node["__DATA__"][pk].pli += pli9;
      node["__DATA__"][pk].pdi += pdi9;
      node["__DATA__"][pk].pci += hasPriceIU;
      /* np: server writes via patientsLookup below; skip row-level sum */
    }
  }

  /* ── Apply server's patient-lookup values directly to leaf nodes ──
     Server computed COUNT(DISTINCT PatientID) at the exact display period level (not quarter-summed).
     Each leaf gets its true distinct count without client-side aggregation. */
  if (_useNpLookup) {
    Object.keys(_patientsLookup).forEach(function(lkKey) {
      var parts = lkKey.split("|");
      if (parts.length < 2) return;
      var lkPk = parts[parts.length - 1];
      var lkDimVals = parts.slice(0, parts.length - 1);
      /* Skip if this period isn't displayed */
      if (periods.indexOf(lkPk) < 0) return;
      var npVal = Number(_patientsLookup[lkKey]) || 0;
      /* Determine depth: count leading non-blank dim parts.
         All-blank dim parts → grand total. Some blank → parent row at that depth.
         Note: server returns parent rolls with blanks for rolled-up dims (which will
         always be the trailing dims, since GROUPING SETS uses prefix combinations). */
      var depth = 0;
      for (var _di = 0; _di < lkDimVals.length; _di++) {
        if (lkDimVals[_di] !== "" && lkDimVals[_di] != null) depth = _di + 1;
        else break;
      }
      if (depth === 0) {
        /* Grand total — set directly on grandRaw (distinct counts don't sum) */
        if (grandRaw[lkPk]) grandRaw[lkPk].np = npVal;
        return;
      }
      /* Walk trie to the target node at `depth` levels deep */
      var node = trie;
      for (var lki = 0; lki < depth && lki < dims.length; lki++) {
        var lkv = lkDimVals[lki] || "?";
        if (!node[lkv]) node[lkv] = {};
        node = node[lkv];
      }
      if (!node["__DATA__"]) {
        node["__DATA__"] = {};
        for (var lkpi = 0; lkpi < accPeriods.length; lkpi++) node["__DATA__"][accPeriods[lkpi]] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      }
      if (!node["__DATA__"][lkPk]) node["__DATA__"][lkPk] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      node["__DATA__"][lkPk].np = npVal;
    });
  }

  /* ── Apply server's prescription-lookup values directly to leaf nodes ──
     Same pattern as patient lookup: distinct counts of OrderNo per (dimKey, period). */
  if (_useNrxLookup) {
    Object.keys(_rxLookup).forEach(function(lkKey) {
      var parts = lkKey.split("|");
      if (parts.length < 2) return;
      var lkPk = parts[parts.length - 1];
      var lkDimVals = parts.slice(0, parts.length - 1);
      if (periods.indexOf(lkPk) < 0) return;
      var nrxVal = Number(_rxLookup[lkKey]) || 0;
      var depth = 0;
      for (var _rdi = 0; _rdi < lkDimVals.length; _rdi++) {
        if (lkDimVals[_rdi] !== "" && lkDimVals[_rdi] != null) depth = _rdi + 1;
        else break;
      }
      if (depth === 0) {
        if (grandRaw[lkPk]) grandRaw[lkPk].nrx = nrxVal;
        return;
      }
      var node = trie;
      for (var lkri = 0; lkri < depth && lkri < dims.length; lkri++) {
        var lkrv = lkDimVals[lkri] || "?";
        if (!node[lkrv]) node[lkrv] = {};
        node = node[lkrv];
      }
      if (!node["__DATA__"]) {
        node["__DATA__"] = {};
        for (var lkrpi = 0; lkrpi < accPeriods.length; lkrpi++) node["__DATA__"][accPeriods[lkrpi]] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      }
      if (!node["__DATA__"][lkPk]) node["__DATA__"][lkPk] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      node["__DATA__"][lkPk].nrx = nrxVal;
    });
  }

  /* ── Apply server's distinct-doctors lookup (r122) — same pattern as prescriptions ── */
  if (_useNdrLookup) {
    Object.keys(_ndrLookup).forEach(function(lkKey) {
      var parts = lkKey.split("|");
      if (parts.length < 2) return;
      var lkPk = parts[parts.length - 1];
      var lkDimVals = parts.slice(0, parts.length - 1);
      if (periods.indexOf(lkPk) < 0) return;
      var ndrVal = Number(_ndrLookup[lkKey]) || 0;
      var depth = 0;
      for (var _ddi = 0; _ddi < lkDimVals.length; _ddi++) {
        if (lkDimVals[_ddi] !== "" && lkDimVals[_ddi] != null) depth = _ddi + 1;
        else break;
      }
      if (depth === 0) {
        if (grandRaw[lkPk]) grandRaw[lkPk].ndr = ndrVal;
        return;
      }
      var node = trie;
      for (var lkdi = 0; lkdi < depth && lkdi < dims.length; lkdi++) {
        var lkdv = lkDimVals[lkdi] || "?";
        if (!node[lkdv]) node[lkdv] = {};
        node = node[lkdv];
      }
      if (!node["__DATA__"]) {
        node["__DATA__"] = {};
        for (var lkdpi = 0; lkdpi < accPeriods.length; lkdpi++) node["__DATA__"][accPeriods[lkdpi]] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      }
      if (!node["__DATA__"][lkPk]) node["__DATA__"][lkPk] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      node["__DATA__"][lkPk].ndr = ndrVal;
    });
  }

  /* ── Apply server's pharmacy-lookup values directly to leaf nodes ──
     Same pattern as patients: COUNT(DISTINCT PharmacyID) per (dimKey, period). */
  if (_useNphLookup) {
    Object.keys(_pharmaciesLookup).forEach(function(lkKey) {
      var parts = lkKey.split("|");
      if (parts.length < 2) return;
      var lkPk = parts[parts.length - 1];
      var lkDimVals = parts.slice(0, parts.length - 1);
      if (periods.indexOf(lkPk) < 0) return;
      var nphVal = Number(_pharmaciesLookup[lkKey]) || 0;
      var depth = 0;
      for (var _hdi = 0; _hdi < lkDimVals.length; _hdi++) {
        if (lkDimVals[_hdi] !== "" && lkDimVals[_hdi] != null) depth = _hdi + 1;
        else break;
      }
      if (depth === 0) {
        if (grandRaw[lkPk]) grandRaw[lkPk].nph = nphVal;
        return;
      }
      var node = trie;
      for (var lkhi = 0; lkhi < depth && lkhi < dims.length; lkhi++) {
        var lkhv = lkDimVals[lkhi] || "?";
        if (!node[lkhv]) node[lkhv] = {};
        node = node[lkhv];
      }
      if (!node["__DATA__"]) {
        node["__DATA__"] = {};
        for (var lkhpi = 0; lkhpi < accPeriods.length; lkhpi++) node["__DATA__"][accPeriods[lkhpi]] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      }
      if (!node["__DATA__"][lkPk]) node["__DATA__"][lkPk] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      node["__DATA__"][lkPk].nph = nphVal;
    });
  }

  var grand = _opts.grandOverride || {};
  if (!_opts.grandOverride) {
  var _hasFOC = !!mp.focUnit;
  var _hasPhysicalNetPrice = !!(mp.netPriceUSD || mp.netPriceLC);
  var _computeNetPrice = _hasFOC && !_hasPhysicalNetPrice; /* Kimadia: compute Value/(Units+FOC) */
  /* Includes hidden growth-base periods (accPeriods) so Growth-vs-Previous /
     Growth-vs-Similar can read grand[prevKey] even when prevKey isn't displayed. */
  for (var pi3 = 0; pi3 < accPeriods.length; pi3++) {
    var p3 = accPeriods[pi3];
    var gpc = grandRaw[p3].pc || 1;
    var gnpc = grandRaw[p3].npc || 1;
    var _gU = grandRaw[p3].u, _gFU = grandRaw[p3].fu, _gL = grandRaw[p3].l, _gD = grandRaw[p3].d;
    var _gTotal = _gU + _gFU;
    grand[p3] = { u: _gU, fu: _gFU, gu: grandRaw[p3].gu || 0, l: _gL, d: _gD, pf: grandRaw[p3].pf || 0, pfl: grandRaw[p3].pfl || 0, sv: grandRaw[p3].sv || 0, svl: grandRaw[p3].svl || 0,
      pl: grandRaw[p3].pc > 0 ? grandRaw[p3].pl / gpc : 0,
      pd: grandRaw[p3].pc > 0 ? grandRaw[p3].pd / gpc : 0,
      npl: _computeNetPrice ? (_gTotal > 0 ? _gL / _gTotal : 0) : (grandRaw[p3].npc > 0 ? grandRaw[p3].npl / gnpc : 0),
      npd: _computeNetPrice ? (_gTotal > 0 ? _gD / _gTotal : 0) : (grandRaw[p3].npc > 0 ? grandRaw[p3].npd / gnpc : 0),
      iu: grandRaw[p3].iu || 0,
      pli: grandRaw[p3].pci > 0 ? grandRaw[p3].pli / grandRaw[p3].pci : 0,
      pdi: grandRaw[p3].pci > 0 ? grandRaw[p3].pdi / grandRaw[p3].pci : 0,
      np: grandRaw[p3].np || 0,
      nrx: grandRaw[p3].nrx || 0,
      rxpd: _rxPdOf("", p3, grandRaw[p3].nrx || 0),
      ndr: grandRaw[p3].ndr || 0,
      ndrt: _ndrtOf("", p3),
      nph: grandRaw[p3].nph || 0,
      pfs:"100.0", svs:"100.0", us:"100.0", fus:"100.0", gus:"100.0", ls:"100.0", ds:"100.0", pls:"100.0", pds:"100.0", npls:"100.0", npds:"100.0", nps:"100.0", nrxs:"100.0", nphs:"100.0", ius:"100.0" };
  }
  } /* end if !grandOverride */
  /* ── Sales Tracker (flow split): DROP empty period bands ──
     A month/quarter with neither Sell-In nor Sell-Out rows renders no column,
     and a flow band that is empty for a period disappears on its own — the
     table reads only the periods that actually carry data (Noura 2026-09-22).
     Growth bases keep accumulating hidden; only DISPLAYED columns filter. */
  if (isFlowSplit) {
    var _neKeep = periods.filter(function(_nek){
      var _neg = grand[_nek];
      if (!_neg) return false;
      return (_neg.u || 0) !== 0 || (_neg.fu || 0) !== 0 || (_neg.gu || 0) !== 0 || (_neg.l || 0) !== 0 || (_neg.d || 0) !== 0 || (_neg.pf || 0) !== 0 || (_neg.pfl || 0) !== 0 || (_neg.np || 0) !== 0 || (_neg.nrx || 0) !== 0 || (_neg.nph || 0) !== 0;
    });
    if (_neKeep.length > 0 && _neKeep.length < periods.length) periods = _neKeep;
  }
  var _hasFOC2 = !!mp.focUnit;
  var _hasPhysicalNetPrice2 = !!(mp.netPriceUSD || mp.netPriceLC);
  var _computeNetPrice = (_opts.grandOverride) ? (_hasFOC2 && !_hasPhysicalNetPrice2) : _computeNetPrice;

  var ATC_DIMS = ["atc1","atc2","atc3","atc4","molecule"];

  /* ── Convert trie sub-tree into node array ── */
  function trieToNodes(obj, depth, pathParts) {
    pathParts = pathParts || [];
    var keys = Object.keys(obj).filter(function(k){ return k !== "__DATA__"; });
    return keys.map(function(name) {
      var sub = obj[name];
      var nodePath = pathParts.concat([name]);
      var data = {};
      /* Aggregate sub-tree for this node */
      var accum = {};
      for (var pi4 = 0; pi4 < accPeriods.length; pi4++) accum[accPeriods[pi4]] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
      aggregateTrie(sub, accum);
      /* Build lookup-key prefix for direct lookup. Server stores keys padded to activeDims.length,
         which equals client `dims.length` (client filters showCols by what's in the server mapping).
         Padding nodePath (length = depth+1) up to dims.length with blanks reproduces the server's
         first-dim/parent-rollup key format exactly. This is the PRIMARY source for distinct counts —
         the trie-walk pre-population (lines ~675-746) is the secondary path. Belt-and-braces. */
      var _lkPathParts = nodePath.slice();
      while (_lkPathParts.length < dims.length) _lkPathParts.push("");
      var _basePathKey = _lkPathParts.join("|");
      for (var pi5 = 0; pi5 < accPeriods.length; pi5++) {
        var p5 = accPeriods[pi5]; var gt = grand[p5] || {u:1,fu:1,gu:1,l:1,d:1,pf:1,pfl:1,sv:1,svl:1,pl:1,pd:1,npl:1,npd:1,np:1,iu:1,pli:1,pdi:1}; var apc = accum[p5].pc || 1;
        var anpc = accum[p5].npc || 1;
        var avgPL = accum[p5].pc > 0 ? accum[p5].pl / apc : 0;
        var avgPD = accum[p5].pc > 0 ? accum[p5].pd / apc : 0;
        var _nU = accum[p5].u, _nFU = accum[p5].fu, _nL = accum[p5].l, _nD = accum[p5].d;
        var _nTotal = _nU + _nFU;
        var avgNPL = _computeNetPrice ? (_nTotal > 0 ? _nL / _nTotal : 0) : (accum[p5].npc > 0 ? accum[p5].npl / anpc : 0);
        var avgNPD = _computeNetPrice ? (_nTotal > 0 ? _nD / _nTotal : 0) : (accum[p5].npc > 0 ? accum[p5].npd / anpc : 0);
        var apci = accum[p5].pci || 1;
        var avgPLI = accum[p5].pci > 0 ? accum[p5].pli / apci : 0;
        var avgPDI = accum[p5].pci > 0 ? accum[p5].pdi / apci : 0;
        /* np: prefer DIRECT lookup (handles parent rollups regardless of trie-walk depth detection),
           fall back to trie data (set by walk at lines ~676-711), final fallback 0. */
        var _leafNp = 0;
        var _leafNrx = 0;
        var _leafNph = 0;
        var _lkKey = _basePathKey + "|" + p5;
        if (_useNpLookup && _patientsLookup && _patientsLookup[_lkKey] != null) {
          _leafNp = Number(_patientsLookup[_lkKey]) || 0;
        } else if (sub["__DATA__"] && sub["__DATA__"][p5]) {
          _leafNp = sub["__DATA__"][p5].np || 0;
        }
        if (_useNrxLookup && _rxLookup && _rxLookup[_lkKey] != null) {
          _leafNrx = Number(_rxLookup[_lkKey]) || 0;
        } else if (sub["__DATA__"] && sub["__DATA__"][p5]) {
          _leafNrx = sub["__DATA__"][p5].nrx || 0;
        }
        var _leafNdr = 0;   /* r122 */
        if (_useNdrLookup && _ndrLookup && _ndrLookup[_lkKey] != null) {
          _leafNdr = Number(_ndrLookup[_lkKey]) || 0;
        } else if (sub["__DATA__"] && sub["__DATA__"][p5]) {
          _leafNdr = sub["__DATA__"][p5].ndr || 0;
        }
        if (_useNphLookup && _pharmaciesLookup && _pharmaciesLookup[_lkKey] != null) {
          _leafNph = Number(_pharmaciesLookup[_lkKey]) || 0;
        } else if (sub["__DATA__"] && sub["__DATA__"][p5]) {
          _leafNph = sub["__DATA__"][p5].nph || 0;
        }
        data[p5] = { u:accum[p5].u, fu:accum[p5].fu, gu:accum[p5].gu||0, l:accum[p5].l, d:accum[p5].d, pf:accum[p5].pf||0, pfl:accum[p5].pfl||0, sv:accum[p5].sv||0, svl:accum[p5].svl||0, pl:avgPL, pd:avgPD, npl:avgNPL, npd:avgNPD, np:_leafNp, nrx:_leafNrx, rxpd:_rxPdOf(_basePathKey, p5, _leafNrx), ndr:_leafNdr, ndrt:_ndrtOf(_basePathKey, p5), nph:_leafNph, iu:accum[p5].iu, pli:avgPLI, pdi:avgPDI,
          pfs: pct(accum[p5].pf||0, gt.pf), svs: pct(accum[p5].sv||0, gt.sv), us: pct(accum[p5].u, gt.u), fus: pct(accum[p5].fu, gt.fu), gus: pct(accum[p5].gu||0, gt.gu||1), ls: pct(accum[p5].l, gt.l), ds: pct(accum[p5].d, gt.d),
          pls: pct(avgPL, gt.pl), pds: pct(avgPD, gt.pd), npls: pct(avgNPL, gt.npl), npds: pct(avgNPD, gt.npd), nps: pct(_leafNp, gt.np||1), nrxs: pct(_leafNrx, gt.nrx||1), nphs: pct(_leafNph, gt.nph||1), ius: pct(accum[p5].iu, gt.iu||1) };
      }
      var dim = dims[depth] || dims[dims.length-1];
      var isATC = ATC_DIMS.indexOf(dim) >= 0;
      var childNodes = trieToNodes(sub, depth + 1, nodePath);
      /* Mark as lazy if this is a leaf node but allDims has more levels */
      var isLazy = childNodes.length === 0 && depth + 1 < allDims.length;
      return { name: name, dim: dim, depth: depth, isATC: isATC,
               _lkPath: _basePathKey, /* server lookup key prefix (STOCK block) */
               data: data, children: childNodes, _lazy: isLazy };
    });
  }

  function aggregateTrie(obj, accum) {
    if (obj["__DATA__"]) {
      for (var pi6 = 0; pi6 < accPeriods.length; pi6++) {
        var p6 = accPeriods[pi6]; var src = obj["__DATA__"][p6] || {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,pc:0,npl:0,npd:0,npc:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,pci:0};
        accum[p6].u += src.u; accum[p6].fu += src.fu; accum[p6].gu += (src.gu||0); accum[p6].l += src.l; accum[p6].d += src.d; accum[p6].pf += (src.pf || 0); accum[p6].pfl += (src.pfl || 0); accum[p6].sv += (src.sv || 0); accum[p6].svl += (src.svl || 0);
        accum[p6].pl += src.pl; accum[p6].pd += src.pd; accum[p6].pc += (src.pc || 0);
        accum[p6].npl += src.npl; accum[p6].npd += src.npd; accum[p6].npc += (src.npc || 0);
        accum[p6].iu += (src.iu || 0); accum[p6].pli += (src.pli || 0); accum[p6].pdi += (src.pdi || 0); accum[p6].pci += (src.pci || 0);
        /* DO NOT sum np — distinct counts are non-additive; leaf np is read directly in trieToNodes */
      }
    }
    var ks = Object.keys(obj).filter(function(k){ return k !== "__DATA__"; });
    for (var ki = 0; ki < ks.length; ki++) aggregateTrie(obj[ks[ki]], accum);
  }

  var nodes = trieToNodes(trie, 0);
  var dimLabels = dims.map(function(d){ return FEAT_LBL[d] || d; });

  /* ── Period ordering helpers for growth calculations ── */
  var periodSortVal = function(pk) {
    /* Flow-compound "base~Flow": base order × 10 + flow order → each base
       period's Sell-In and Sell-Out columns sit adjacently, bases keep their
       own chronological/type ordering. */
    var _tz = String(pk).indexOf("~");
    if (_tz >= 0) {
      var _tfl = String(pk).slice(_tz + 1);
      var _tfo = _tfl === "Sell-In" ? 1 : _tfl === "Sell-Out" ? 2 : 5;
      return periodSortVal(String(pk).slice(0, _tz)) * 10 + _tfo;
    }
    if (/^\d{4}$/.test(pk)) return parseInt(pk) * 100;
    var q2 = /^(\d{4})-Q([1-4])$/.exec(pk); if (q2) return parseInt(q2[1]) * 100 + parseInt(q2[2]);
    var m2 = /^(\d{4})-(\d{2})$/.exec(pk);  if (m2) return parseInt(m2[1]) * 100 + parseInt(m2[2]);
    var mat2 = /^MAT-Q([1-4])-(\d{4})$/.exec(pk); if (mat2) return parseInt(mat2[2]) * 100 + parseInt(mat2[1]);
    var matm2 = /^MATM-(\d{1,2})-(\d{4})$/.exec(pk); if (matm2) return parseInt(matm2[2]) * 100 + parseInt(matm2[1]);
    var ytd2 = /^YTD-(\d{4})$/.exec(pk); if (ytd2) return parseInt(ytd2[1]) * 100 + 50;
    /* YTD Monthly (key = "YTDM-2026") — same sort weight as YTD, just different label format */
    var ytdm2 = /^YTDM-(\d{4})$/.exec(pk); if (ytdm2) return parseInt(ytdm2[1]) * 100 + 50;
    /* Compound: "2025_Awarded" → year*1000 + statusOrder */
    var tp2 = /^(\d{4})_(.+)$/.exec(pk); if (tp2) { var so = tp2[2]==="Awarded"?1:tp2[2]==="Delivered"?2:tp2[2]==="Pending"?3:5; return parseInt(tp2[1]) * 1000 + so; }
    return 0;
  };
  /* Multi-type: group into type bands ordered by the USER'S SELECTION — the
     first key selected of each type fixes that type's band position, so the
     table reads in the same order the periods were picked in the filter.
     Chronological within each band. Single-type: pure chronological
     (unchanged behavior). */
  var _typeSelOrder = {};
  var _typeSelNext = 0;
  if (hasPer) {
    for (var _tso = 0; _tso < periods.length; _tso++) {
      var _tsoT = keyTypes[periods[_tso]];
      if (_tsoT && _typeSelOrder[_tsoT] === undefined) _typeSelOrder[_tsoT] = _typeSelNext++;
    }
  }
  var _typeOrderOf = function(pk) {
    var _t = keyTypes[pk] || periodKeyType(pk);
    if (_t && _typeSelOrder[_t] !== undefined) return _typeSelOrder[_t];
    return (_t && PERIOD_TYPE_META[_t]) ? 90 + PERIOD_TYPE_META[_t].order : 99;
  };
  var sortedPeriods = periods.slice().sort(function(a,b){
    if (isMulti) { var ta = _typeOrderOf(a), tb = _typeOrderOf(b); if (ta !== tb) return ta - tb; }
    return periodSortVal(a) - periodSortVal(b);
  });
  /* Growth-vs-Previous base = the REAL calendar-previous period (2026-Q1 → 2025-Q4),
     NOT the previous key in the user's selection. Its data lives in the hidden
     accumulated periods when it wasn't selected; if the dataset simply has no rows
     for it, the growth cell falls back to "—" (grand/node data is zero/absent). */
  var prevPeriodOf = {};
  for (var spx = 0; spx < sortedPeriods.length; spx++) {
    var _spk = sortedPeriods[spx];
    var _spt = keyTypes[_spk] || periodKeyType(_spk);
    /* ROLLING / CUMULATIVE windows (MAT, MAT Monthly, YTD, YTD Monthly): the
       meaningful "previous similar period" is the SAME window one year earlier
       (MAT-Q2-2026 grows vs MAT-Q2-2025). The calendar-previous window
       (MAT-Q1-2026) overlaps 9 of 12 months and yields tiny misleading deltas.
       Point periods (year / quarter / month / tender) keep the true
       calendar-previous key as before. */
    var _rpv = (_spt === "MAT" || _spt === "MAT Monthly" || _spt === "YTD" || _spt === "YTD Monthly")
      ? realYoyPeriodKey(_spk)
      : realPrevPeriodKey(_spk);
    if (_rpv) prevPeriodOf[_spk] = _rpv;
  }
  if (_ptOn) {
    sortedPeriods = sortedPeriods.concat([PT_KEY]);
    keyTypes[PT_KEY] = keyTypes[periods[0]] || periodKeyType(periods[0]);
    prevPeriodOf[PT_KEY] = PTY_KEY; /* a summed band has no calendar-previous — both growth columns compare vs the same periods last year */
  }
  var yoyPeriodOf = {};
  for (var spy = 0; spy < sortedPeriods.length; spy++) {
    var spyk = sortedPeriods[spy];
    /* Same-type "similar period last year" via realYoyPeriodKey (2026-Q1 →
       2025-Q1, MAT-Q1-2026 → MAT-Q1-2025, ...). The base may be a selected
       period OR a hidden accumulated one — both are in accSet. */
    var yoyK = realYoyPeriodKey(spyk);
    if (yoyK && accSet[yoyK] === 1) yoyPeriodOf[spyk] = yoyK;
  }
  if (_ptOn) yoyPeriodOf[PT_KEY] = PTY_KEY;

  return {
    grand: grand, nodes: nodes, dims: dims, allDims: allDims, dimLabels: dimLabels,
    periods: _ptOn ? periods.concat([PT_KEY]) : periods, sortedPeriods: sortedPeriods,
    periodTypes: keyTypes, multiType: isMulti, flowSplit: isFlowSplit,
    /* STOCK: closing-stock lookup keyed "dimVals|baseKey" (server-computed
       cumulative Sell-In − Sell-Out; base keys — never flow-suffixed) */
    stockLookup: (_opts.stockLookup && mp.stock) ? _opts.stockLookup : null,
    /* COVERAGE %: denominator lookup keyed "T|<pk>" (whole panel) and
       "G|<gov>|<pk>" (per governorate). Numerators are the nph cell values. */
    coverageLookup: (_opts.coverageLookup && mp.pharmacies) ? _opts.coverageLookup : null,
    doctorsLookup: (_useRxPd || _useNdrt) ? _drLookup : null,
    doctorsDistinctLookup: _useNdrLookup ? _ndrLookup : null,
    matYtdLookup: matYtdLookup,
    prevPeriodOf: prevPeriodOf, yoyPeriodOf: yoyPeriodOf,
    msrs: msrs, tree: allDims.length > 1,
    parLabel: FEAT_LBL[dims[0]] || dims[0],
    childLabel: FEAT_LBL[allDims[allDims.length-1]] || allDims[allDims.length-1],
    computedNetPrice: _computeNetPrice,
  };
}

/* ════════════════════════════════════════════════════════════
   PIVOT TABLE  — multi-level collapse, recursive sort, stats, Excel export
════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   ShareQueryModal — pick visibility scope for a saved query:
     - Private (just me)        → sharedScope = null
     - Team (all my client)     → sharedScope = "team"
     - Specific Users           → sharedScope = "users", with userIds list
   Only teammates of the current user (same ClientId) can be picked.
   ══════════════════════════════════════════════════════════════ */
function ShareQueryModal({ query, currentUser, allUsers, onSave, onClose }) {
  var initialScope = query.sharedScope || null;
  var initialIds = Array.isArray(query.sharedUserIds) ? query.sharedUserIds.slice() : [];
  var _sc = useState(initialScope), scope = _sc[0], setScope = _sc[1];
  var _su = useState(initialIds), selUserIds = _su[0], setSelUserIds = _su[1];

  /* Restrict to teammates of the current user.
     IMPORTANT: data shape differs between the two sources:
       - `currentUser` (login response) has `userId` (number), `clientId` (number)
       - `allUsers` (from /api/users) have `id` (STRING via formatUserRow), `clientId` (STRING)
     So we coerce both sides to strings before comparing. Exclude the owner and any admins. */
  var _ownClient = String(currentUser.clientId == null ? "" : currentUser.clientId);
  var _ownUid    = String(currentUser.userId   == null ? "" : currentUser.userId);
  var _ownDuid   = currentUser.dataUserId != null ? String(currentUser.dataUserId) : null;
  var teammates = (allUsers || []).filter(function(u) {
    if (!u) return false;
    if (String(u.clientId) !== _ownClient) return false; /* not in my client */
    if (String(u.id) === _ownUid) return false;          /* it's me */
    if (_ownDuid != null && String(u.id) === _ownDuid) return false; /* my portal account (promoted admin) */
    if (u.role === "admin") return false;                /* skip admin (super-user, not a teammate) */
    if (u.status && u.status !== "active") return false; /* skip inactive/deleted users */
    return true;
  });

  /* selUserIds may have come from server as integers OR strings. Normalize the set we work
     with to numbers (the server expects numeric UserIds in SharedUserIds JSON). */
  var _normalizeId = function(x) { var n = parseInt(x, 10); return isNaN(n) ? null : n; };

  var toggleUser = function(uid) {
    /* uid here is u.id (string) — normalize to number for storage */
    var n = _normalizeId(uid);
    if (n == null) return;
    setSelUserIds(function(prev) {
      var idx = prev.map(_normalizeId).indexOf(n);
      if (idx >= 0) { var arr = prev.slice(); arr.splice(idx, 1); return arr; }
      return prev.concat([n]);
    });
  };

  var save = function() {
    /* Server stores SharedUserIds as integer array. selUserIds is already normalized. */
    var ids = scope === "users" ? selUserIds.map(_normalizeId).filter(function(n){ return n != null; }) : [];
    onSave(query, scope, ids);
  };

  return (
    <div onClick={onClose}
      style={{position:"fixed", inset:0, background:"rgba(15,23,42,.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999}}>
      <div onClick={function(e){e.stopPropagation();}}
        style={{background:"#fff", borderRadius:10, maxWidth:480, width:"92%", maxHeight:"85vh", display:"flex", flexDirection:"column", boxShadow:"0 25px 60px rgba(0,0,0,.4)"}}>
        <div style={{padding:"16px 20px", borderBottom:"1px solid #e2e8f0"}}>
          <div style={{fontSize:14, fontWeight:800, color:"#0f172a"}}>Share "{query.name}"</div>
          <div style={{fontSize:10.5, color:"#64748b", marginTop:3}}>Choose who can see and load this saved query.</div>
        </div>
        <div style={{padding:"14px 20px", overflowY:"auto", flex:1}}>
          {/* Scope options */}
          {[
            {k: null,    title: "Private",        desc: "Only you can see and load this query."},
            {k: "team",  title: "Whole team",     desc: "All users of your client (" + (currentUser.clientName || "your team") + ") can use this query."},
            {k: "users", title: "Specific users", desc: "Pick individual teammates below."}
          ].map(function(opt) {
            var active = scope === opt.k;
            return (
              <div key={String(opt.k)} onClick={function(){setScope(opt.k);}}
                style={{padding:"10px 12px", borderRadius:7, marginBottom:7, cursor:"pointer",
                        border:"1.5px solid " + (active ? "#2563eb" : "#e2e8f0"),
                        background: active ? "#eff6ff" : "#fff", transition:"all .15s"}}>
                <div style={{display:"flex", alignItems:"center", gap:8}}>
                  <div style={{width:16, height:16, borderRadius:8, border:"2px solid " + (active ? "#2563eb" : "#cbd5e1"), flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center"}}>
                    {active && <div style={{width:8, height:8, borderRadius:4, background:"#2563eb"}} />}
                  </div>
                  <div style={{fontSize:12, fontWeight:700, color: active ? "#1d4ed8" : "#0f172a"}}>{opt.title}</div>
                </div>
                <div style={{fontSize:10.5, color:"#64748b", marginTop:4, marginLeft:24, lineHeight:1.4}}>{opt.desc}</div>
              </div>
            );
          })}
          {/* Teammates picker — only when scope='users' */}
          {scope === "users" && (
            <div style={{marginTop:6, padding:"10px 12px", border:"1px solid #e2e8f0", borderRadius:7, background:"#fafafa"}}>
              <div style={{fontSize:10.5, fontWeight:700, color:"#475569", marginBottom:8}}>
                Teammates ({teammates.length})
                {selUserIds.length > 0 && <span style={{fontSize:9.5, color:"#2563eb", fontWeight:600, marginLeft:6}}>· {selUserIds.length} selected</span>}
              </div>
              {teammates.length === 0 ? (
                <div style={{fontSize:10.5, color:"#94a3b8", fontStyle:"italic", padding:"4px 0"}}>No teammates available for this client.</div>
              ) : (
                <div style={{display:"flex", flexDirection:"column", gap:4, maxHeight:240, overflowY:"auto"}}>
                  {teammates.map(function(u) {
                    var uIdNum = _normalizeId(u.id);
                    var checked = selUserIds.map(_normalizeId).indexOf(uIdNum) >= 0;
                    return (
                      <label key={u.id}
                        style={{display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:5,
                                background: checked ? "#dbeafe" : "transparent", cursor:"pointer", userSelect:"none"}}>
                        <input type="checkbox" checked={checked} onChange={function(){toggleUser(u.id);}} />
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{fontSize:11, fontWeight:600, color:"#0f172a"}}>{u.name || u.email}</div>
                          {u.email && u.name && <div style={{fontSize:9.5, color:"#94a3b8"}}>{u.email}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{padding:"12px 20px", borderTop:"1px solid #e2e8f0", display:"flex", justifyContent:"flex-end", gap:8}}>
          <button onClick={onClose}
            style={{padding:"7px 16px", border:"1px solid #cbd5e1", borderRadius:6, background:"#fff", color:"#475569", fontSize:11.5, fontWeight:600, cursor:"pointer"}}>
            Cancel
          </button>
          <button onClick={save}
            disabled={scope === "users" && selUserIds.length === 0}
            style={{padding:"7px 16px", border:"none", borderRadius:6, background: (scope === "users" && selUserIds.length === 0) ? "#94a3b8" : "#2563eb",
                    color:"#fff", fontSize:11.5, fontWeight:700, cursor:(scope === "users" && selUserIds.length === 0) ? "not-allowed" : "pointer",
                    boxShadow:"0 2px 6px rgba(37,99,235,.4)"}}>
            Save sharing
          </button>
        </div>
      </div>
    </div>
  );
}

function PivotTable({ data, stats, periodMode, user, allUsers, setAllUsers, mp, studyTypeConfig, onLoadChildren, onLoadLevel, onLoadAllLevels, onFetchExportRows, lazyChildren, lazyLoading, isLazyLoaded, isLazyLoading, cachedRows, cachedMapping, gridDims, gridMeasures, activeStudyType, onReorderDims, topNEnabled, onApplyTopN, selection }) {
  /* (retired) themed stage table — header family for the Sales Tracker study
     ONLY; every themed value falls back to the exact pre-existing literal. */
  var _ptv = activeStudyType === "sales_tracker"; /* Noura 2026-09-24: the teal Sales Tracker theme is RESTORED (table + shell) — it was AMS blue for one round */
  /* Medical & Pharma Intelligence: two colour families in the pivot — Pharma
     measures (Units/USD/LC/Price) on the kimadia BLUE band, Rx measures
     (# Prescriptions / # Patients) on the kimadia VIOLET band. */
  var _msaTv = activeStudyType === "jordan_msa";
  var _msaRxM = { np: 1, nrx: 1 };
  var grand        = data.grand;
  var rawNodes     = data.nodes;
  var dims         = data.dims;
  var allDims      = data.allDims || dims;
  var dimLabels    = data.dimLabels;
  /* msa merged market: spell the three region concepts out fully so a pivot
     header reads as an insight — which region concept the row split uses. */
  var _msaDimLbl = { region:"Sales Region", docRegion:"Doctor Region", docSpec:"Doctor Specialty", docCity:"Doctor City", phRegion:"Pharmacy Region", phCity:"Pharmacy City", docSalesRegion:"Doctor\u2013Sales Region", phSalesRegion:"Pharmacy\u2013Sales Region" };
  var allDimLabels = allDims.map(function(d){ return (_msaTv && _msaDimLbl[d]) || FEAT_LBL[d] || d; });
  var periods      = data.periods;
  var msrs         = data.msrs;
  var isTree       = data.tree;
  var childLabel   = data.childLabel;
  var prevPeriodOf = data.prevPeriodOf || {};
  var yoyPeriodOf  = data.yoyPeriodOf  || {};

  var statsInds  = (stats && stats.indicators) ? stats.indicators : [];
  var gMode      = (stats && stats.growthMode)  ? stats.growthMode  : "prev";
  /* Per-statistic measure-scope map. Shape: { "Market Share": ["Unit","LC"], "Rank": ["USD"] }
     When a stat is enabled but its scope is empty/undefined, NO measure shows that stat
     (the FilterPanel warns "no measures selected — this statistic won't appear"). */
  var statMeasures = (stats && stats.statMeasures) ? stats.statMeasures : {};
  var showShare  = statsInds.indexOf("Market Share") >= 0;
  var showRank   = statsInds.indexOf("Rank")         >= 0;
  var showGrPrev = statsInds.indexOf("Growth") >= 0 && (gMode === "prev" || gMode === "both");
  var showGrYoy  = statsInds.indexOf("Growth") >= 0 && (gMode === "yoy"  || gMode === "both");
  /* CAGR needs a homogeneous period series — disabled for mixed-type queries. */
  var showCAGR   = statsInds.indexOf("CAGR") >= 0 && !data.multiType && !data.flowSplit && (periodMode === "Yearly" || periodMode === "Year" || periodMode === "MAT" || periodMode === "TenderStatus") && periods.length >= 2;
  /* Evolution Index: (entity growth index ÷ market growth index) × 100 — i.e.
     how the row's share evolved vs its comparison period. 100 = grew exactly
     with the market; >100 = gained share; <100 = lost share.
     The comparison period has its OWN mode (stats.evoMode), like Growth:
       "prev" → vs previous period, "yoy" → vs similar period last year,
       "both" → two EI columns (EI + EI Similar).
     Back-compat: queries saved before evoMode existed follow the Growth mode
     (that was the old behavior). */
  var showEvo    = statsInds.indexOf("Evolution Index") >= 0;
  var eMode      = (stats && stats.evoMode) ? stats.evoMode : (gMode === "yoy" ? "yoy" : "prev");
  var showEvoPrev = showEvo && (eMode === "prev" || eMode === "both");
  var showEvoYoy  = showEvo && (eMode === "yoy"  || eMode === "both");

  /* Mapping from internal short measure key (used in activeMsrs.m) to the user-facing
     measure key stored in sel.measures and stats.statMeasures.
       "u"  ← "Unit"
       "d"  ← "USD"
       "l"  ← "LC"
       "pd" / "pl" / "npd" / "npl"  ← "Price" (all price variants map to Price)
       "fu" ← "Unit" (FOC Units, same family — but always noStats anyway)
       "np" ← "Patients"
       "nrx" ← "Prescriptions"
     Without this translation, statAppliesTo("Rank", "u") would compare against
     ["Unit"] in the scope and miss → no Rank column. */
  var _internalToUserMsrKey = function(internalKey) {
    if (internalKey === "u" || internalKey === "fu" || internalKey === "gu") return "Unit";
    if (internalKey === "d") return "USD";
    if (internalKey === "l") return "LC";
    if (internalKey === "pd" || internalKey === "pl" || internalKey === "npd" || internalKey === "npl") return "Price";
    if (internalKey === "iu") return "UnitIU";
    if (internalKey === "pdi" || internalKey === "pli") return "PriceIU";
    if (internalKey === "np") return "Patients";
    if (internalKey === "nph") return "Pharmacies";
    if (internalKey === "pf") return "Profit";
    if (internalKey === "sv") return "SellInValue";
    if (internalKey === "nrx") return "Prescriptions";
    if (internalKey === "rxpd") return "RxPerDoctor";
    if (internalKey === "ndr") return "Doctors";
    if (internalKey === "ndrt") return "DoctorsTotal";
    return internalKey; /* fallback — already a user key */
  };

  /* Helper: should `statName` (e.g. "Market Share") show a column for measure key `mKey`?
     - If the statistic is globally off → no
     - If statMeasures is entirely missing (legacy saved queries from before per-measure
       scoping existed), default to ALL summable measures (the old behavior)
     - If statMeasures has the stat but the scope is empty → user explicitly cleared it,
       respect that and show nothing
     - Otherwise → only when mKey is in the scope (after translating from internal key) */
  var _legacyMode = !stats || !stats.statMeasures || Object.keys(stats.statMeasures).length === 0;
  var statAppliesTo = function(statName, mKey) {
    if (statsInds.indexOf(statName) < 0) return false;
    if (_legacyMode) return true; /* legacy query: apply to all measures (old behavior) */
    var scope = statMeasures[statName];
    if (scope === undefined) return true; /* stat enabled before scoping existed: apply to all */
    if (!scope.length) return false;      /* user cleared all measures for this stat: show nothing */
    var userKey = _internalToUserMsrKey(mKey);
    return scope.indexOf(userKey) >= 0;
  };
  /* Convenience per-stat shortcuts for the inner render loops. */
  var shareFor   = function(mKey) { return showShare  && statAppliesTo("Market Share", mKey); };
  var rankFor    = function(mKey) { return showRank   && statAppliesTo("Rank",         mKey); };
  var grPrevFor  = function(mKey) { return showGrPrev && statAppliesTo("Growth",       mKey); };
  var grYoyFor   = function(mKey) { return showGrYoy  && statAppliesTo("Growth",       mKey); };
  var cagrFor    = function(mKey) { return showCAGR   && statAppliesTo("CAGR",         mKey); };
  var evoPrevFor = function(mKey) { return showEvoPrev && statAppliesTo("Evolution Index", mKey); };
  var evoYoyFor  = function(mKey) { return showEvoYoy  && statAppliesTo("Evolution Index", mKey); };
  /* ── Sales Tracker flow statistics (Noura 2026-09-23): Sell-Through % and
     Profit are STATISTICS scoped per measure, and their columns exist ONLY
     under Sell-Out flow bands — never under Sell-In. ── */
  var _soBandOf = function(pk) { return String(pk).slice(-9) === "~Sell-Out"; };
  var _siKeyOf = function(pk) { var s2 = String(pk); return s2.slice(0, s2.length - 9) + "~Sell-In"; };
  var showSellThr = statsInds.indexOf("Sell-Through %") >= 0 && data.flowSplit;
  var showProfitStat = statsInds.indexOf("Profit") >= 0 && data.flowSplit && mp && (mp.profitUSD || mp.profitLC);
  /* FAIL-OPEN scoping for the two flow stats (Noura 2026-09-23: pills were ON
     but no columns rendered). The generic statAppliesTo hides a stat whose
     saved scope no longer intersects the SELECTED measures (stale saved
     queries) — and Profit's scope could hold only measures profit can never
     attach to (e.g. Unit, the togStatInd first-measure default). Here the
     scope is pruned to measures that are BOTH currently selected AND
     applicable to the stat; when nothing usable remains, the stat applies to
     ALL applicable measures instead of silently disappearing. */
  var _flowStatApplicable = { "Sell-Through %": { Unit: 1, LC: 1, USD: 1 }, "Profit": { USD: 1, LC: 1 } };
  var _flowStatScopeOk = function(statName, mKey) {
    if (_legacyMode) return true;
    var scope = statMeasures[statName];
    if (scope === undefined || !scope.length) return true;      /* no scope saved → all */
    var selMs = gridMeasures || [];
    var app9 = _flowStatApplicable[statName] || {};
    var valid = scope.filter(function(k9){ return app9[k9] && (!selMs.length || selMs.indexOf(k9) >= 0); });
    if (!valid.length) return true;                             /* stale / inapplicable scope → all */
    return valid.indexOf(_internalToUserMsrKey(mKey)) >= 0;
  };
  var sthFor = function(mKey, pk) {
    if (!showSellThr || !_soBandOf(pk)) return false;
    if (mKey !== "u" && mKey !== "gu" && mKey !== "l" && mKey !== "d") return false;
    return _flowStatScopeOk("Sell-Through %", mKey);
  };
  var pfStatFor = function(mKey, pk) {
    if (!showProfitStat || !_soBandOf(pk)) return false;
    if (mKey === "d") return !!mp.profitUSD && _flowStatScopeOk("Profit", mKey);
    if (mKey === "l") return !!mp.profitLC && _flowStatScopeOk("Profit", mKey);
    return false;
  };

  var sU = msrs.includes("Unit");
  var sL = msrs.includes("LC");
  var sD = msrs.includes("USD");
  var sP = msrs.includes("Price");
  var sPt = msrs.includes("Patients"); /* medical-only COUNT(DISTINCT PatientID) */
  var sRx = msrs.includes("Prescriptions"); /* medical-only COUNT(DISTINCT OrderNo) */
  var sPh = msrs.includes("Pharmacies"); /* Sales Tracker COUNT(DISTINCT PharmacyID) — panel weight */

  /* Active measure descriptors */
  var isKimadia = studyTypeConfig && studyTypeConfig.code === "kimadia";
  var isGpdStudy = studyTypeConfig && studyTypeConfig.code === "gpd"; /* Jordan tender market */
  var lcLabel = isKimadia ? "IQD" : isGpdStudy ? "JD" : "LC";
  var hasFOC = mp && mp.focUnit;
  var hasNetPr = (mp && (mp.netPriceUSD || mp.netPriceLC)) || (data && data.computedNetPrice);
  var _computedNP = data && data.computedNetPrice; /* Kimadia: Value/(Units+FOC) */
  /* Active measure descriptors.
     Order: USD/LC main values come BEFORE Units when selected (display priority).
     Each measure carries a `tag` — a compact symbol shown next to stat sub-headers
     (% Share, Rnk, Gth, Gth YoY) so users can tell which measure each stat belongs to. */
  var activeMsrs = [];
  /* Main value measures FIRST: USD → LC → Units → FOC (the headline figures) */
  if (sD)  activeMsrs.push({m:"d",  ms:"ds",  label:"USD",           tag:"$",       hbg:"#d5e0ec", hco:"#1e3a5f", hbr:"#7b9ab8"});
  if (sL)  activeMsrs.push({m:"l",  ms:"ls",  label:lcLabel,         tag:lcLabel,   hbg:"#d0dbe7", hco:"#1a365d", hbr:"#7090a8"});
  if (sU)  activeMsrs.push({m:"u",  ms:"us",  label:"UNITS",         tag:"U",       hbg:"#e2e8f0", hco:"#1e3a5f", hbr:"#94a3b8"});
  if (sU && hasFOC) activeMsrs.push({m:"fu", ms:"fus", label:"FOC UNITS", tag:"U", hbg:"#dce5ef", hco:"#1a365d", hbr:"#8da3b8", noStats:true});
  /* GPD: UNITS IU — its own summable measure family (Units_ui column) */
  var sUI = msrs.includes("UnitIU");
  if (sUI && mp.unitsIU) activeMsrs.push({m:"iu", ms:"ius", label:"UNITS IU", tag:"IU", hbg:"#e3ece5", hco:"#14532d", hbr:"#93b8a1"});
  /* Sales Tracker: Gross Selling = paid Units + FOC units (computed in
     vw_SalesTracker). Its OWN measure checkbox ("Gross") — shown when ticked. */
  var sG = msrs.includes("Gross");
  if (sG && (mp && mp.grossUnit)) activeMsrs.push({m:"gu", ms:"gus", label:"GROSS SELLING", tag:"U", hbg:"#d8e4f0", hco:"#1a365d", hbr:"#88a0b8", noStats:true});
  /* FOC % — deal intensity: FOC units as % of PAID units (r178; was % of gross). DERIVED at
     render time from fu/gu already present in every cell, so it automatically
     respects any period selection (quarters, MAT, YTD, flow bands, ΣTotal).
     OPT-IN via the Statistics section ("FOC %") — not shown by default. */
  var showFocPct = statsInds.indexOf("FOC %") >= 0;
  if (showFocPct && sU && hasFOC && (mp && mp.grossUnit)) activeMsrs.push({m:"fp", label:"FOC %", tag:"%", hbg:"#e4ebf3", hco:"#1a365d", hbr:"#8da3b8", noStats:true, derived:"focPct"});
  /* COVERAGE % — panel reach: pharmacies carrying the row's selection ÷ total
     panel pharmacies (per governorate when Governorate is a row dimension,
     whole panel otherwise). DERIVED per cell from the # Pharmacies lookup (nph)
     and the server's coverage denominators — never summed or stored. OPT-IN
     via the Statistics section ("Coverage %"). */
  var showCoverage = statsInds.indexOf("Coverage %") >= 0;
  if (showCoverage && (mp && mp.pharmacies) && data.coverageLookup) activeMsrs.push({m:"cv", label:"COVERAGE %", tag:"%", hbg:"#ddefe6", hco:"#0F5132", hbr:"#4faf85", noStats:true, derived:"coverage"});
  /* Prices AFTER the main measures (SKU-only derived figures, not headline values) */
  if (sP && sD) activeMsrs.push({m:"pd", ms:"pds", label:"UNIT PRICE USD", tag:"$", hbg:"#edf2f7", hco:"#334155", hbr:"#cbd5e1", noStats:true, skuOnly:true});
  if (sP && sD && hasNetPr && (mp.netPriceUSD || _computedNP)) activeMsrs.push({m:"npd", ms:"npds", label:"NET PRICE USD", tag:"$", hbg:"#e8ecf1", hco:"#475569", hbr:"#b8c4ce", noStats:true, skuOnly:true});
  if (sP && sL) activeMsrs.push({m:"pl", ms:"pls", label:"UNIT PRICE " + lcLabel, tag:lcLabel, hbg:"#edf2f7", hco:"#334155", hbr:"#cbd5e1", noStats:true, skuOnly:true});
  if (sP && sL && hasNetPr && (mp.netPriceLC || _computedNP)) activeMsrs.push({m:"npl", ms:"npls", label:"NET PRICE " + lcLabel, tag:lcLabel, hbg:"#e8ecf1", hco:"#475569", hbr:"#b8c4ce", noStats:true, skuOnly:true});
  /* GPD: UNIT PRICE IU — *_ui price columns, averaged like pl/pd (SKU-level figures) */
  var sPI = msrs.includes("PriceIU");
  if (sPI && mp.priceUSDIU) activeMsrs.push({m:"pdi", label:"UNIT PRICE IU USD", tag:"$", hbg:"#edf2f7", hco:"#334155", hbr:"#cbd5e1", noStats:true, skuOnly:true});
  if (sPI && mp.priceLCIU) activeMsrs.push({m:"pli", label:"UNIT PRICE IU " + lcLabel, tag:lcLabel, hbg:"#edf2f7", hco:"#334155", hbr:"#cbd5e1", noStats:true, skuOnly:true});
  /* # Patients: distinct PatientID count. Server computes this at every dimension level
     (manufacturer, product, sku) — values DON'T sum from children to parent (a patient
     can use multiple products), but each parent has its own valid distinct count.
     Rank and Market Share are meaningful here:
       - Rank: order manufacturers by their distinct patient reach
       - Market Share: (mfr distinct patients / grand-total distinct patients) × 100
                       Shares won't add to 100% (overlap is normal) but each is valid
                       on its own. */
  if (sPt && mp.patients) activeMsrs.push({m:"np", ms:"nps", label:"# PATIENTS", tag:"P", hbg:"#fef3c7", hco:"#78350f", hbr:"#f59e0b"});
  /* # Prescriptions: distinct intOrder count. Same shape as Patients — server returns
     per-level distinct counts; supports Rank and Market Share. */
  if (sRx && mp.prescriptions) activeMsrs.push({m:"nrx", ms:"nrxs", label:"# PRESCRIPTIONS", tag:"R", hbg:"#fce7f3", hco:"#831843", hbr:"#ec4899"});
  /* Rx per Doctor (r121): prescriptions ÷ prescribing doctors of the row's speciality (whole
     panel when Speciality is not on the layout). A ratio stored per cell (rxpd) — no share /
     rank / growth statistics; 2 decimals; "—" when the speciality has no doctor count. */
  var sRxD = msrs.includes("RxPerDoctor");
  if (sRxD && mp.rxPerDoctor && data.doctorsLookup) activeMsrs.push({m:"rxpd", label:"RX PER DOCTOR", tag:"R/Dr", hbg:"#fde2ec", hco:"#831843", hbr:"#f472b6", noStats:true, ratio2:true});
  /* # Doctors (r122): distinct prescribing doctors of the row (server lookup, never summed);
     # Doctors (Total): the speciality's whole prescribing panel (same figure for every row of a
     speciality; whole panel when Speciality is not on the layout). Both without statistics. */
  var sNdr = msrs.includes("Doctors"), sNdrt = msrs.includes("DoctorsTotal");
  if (sNdr && mp.doctors && data.doctorsDistinctLookup) activeMsrs.push({m:"ndr", label:"# DOCTORS", tag:"Dr", hbg:"#e0f2fe", hco:"#0c4a6e", hbr:"#38bdf8", noStats:true});
  if (sNdrt && mp.doctorsTotal && data.doctorsLookup) activeMsrs.push({m:"ndrt", label:"# DOCTORS (TOTAL)", tag:"Dr", hbg:"#dbeafe", hco:"#1e3a8a", hbr:"#60a5fa", noStats:true, dashNull:true});
  /* # Pharmacies: distinct pharmacy count per cell (Sales Tracker panel weight —
     e.g. class A: 10 pharmacies, B: 20). Distinct counts don't sum: every level
     and period band gets its own true count from the server lookup. noStats —
     it's a panel-size figure, not a market measure. */
  if (sPh && mp.pharmacies) activeMsrs.push({m:"nph", ms:"nphs", label:"# PHARMACIES", tag:"Ph", hbg:"#d7ede3", hco:"#0F5132", hbr:"#4faf85", noStats:true});

  /* (retired, _ptv=false) unify the measure header band (Sales Tracker only) */
  if (_ptv) activeMsrs.forEach(function(_pm){ _pm.hbg = "#DBE7F1"; _pm.hco = "#0A2F42"; _pm.hbr = "#9FB9CE"; });
  /* msa: recolor the measure header band into the two families */
  if (_msaTv) activeMsrs.forEach(function(_mm){
    if (_msaRxM[_mm.m]) { _mm.hbg = "#e3eefa"; _mm.hco = "#1e3a5f"; _mm.hbr = "#93c5fd"; }
    else { _mm.hbg = "#e0f2e9"; _mm.hco = "#0F5132"; _mm.hbr = "#7fcfa9"; }
  });

  /* Measures that support statistics (summable: units, USD, LC) */
  var statMsrs = activeMsrs.filter(function(m){ return !m.noStats; });
  /* CAGR has a separate column block (not per-period). It uses statMsrs.length for colSpan
     — but should only include measures the user opted into via statMeasures["CAGR"]. */
  var cagrMsrs = statMsrs.filter(function(m){ return cagrFor(m.m); });

  /* Columns per period: each measure gets 1 (value), stat-eligible ones also get stat
     columns — and each statistic is gated by its own measure scope (statMeasures map). */
  var statColsFor = function(mKey) {
    return (shareFor(mKey)?1:0) + (rankFor(mKey)?1:0) + (grPrevFor(mKey)?1:0) + (grYoyFor(mKey)?1:0) + (evoPrevFor(mKey)?1:0) + (evoYoyFor(mKey)?1:0);
  };
  var span = 0;
  for (var _si=0; _si<activeMsrs.length; _si++) {
    if (activeMsrs[_si].noStats) { span += 1; continue; }
    span += 1 + statColsFor(activeMsrs[_si].m);
  }

  /* ── Extract limit helpers ── */
  var currentUser = useMemo(function() {
    if (!user || !allUsers) return null;
    var uid = String(user.userId || user.id || "");
    if (!uid) return null;
    for (var i = 0; i < allUsers.length; i++) { if (String(allUsers[i].id) === uid) return allUsers[i]; }
    return null;
  }, [user, allUsers]);
  var extractLimit = (currentUser && currentUser.extractLimit != null) ? currentUser.extractLimit : Infinity;
  var extractUsed  = (currentUser && currentUser.extractUsed  != null) ? currentUser.extractUsed  : 0;
  var extractRemaining = Math.max(0, extractLimit - extractUsed);
  var extractResetsOn = (currentUser && currentUser.extractResetsOn) || "";   /* r180: the allowance resets on the first day of the next quarter (server-stamped) */
  var rowExportLimit = (currentUser && currentUser.rowExportLimit != null && currentUser.rowExportLimit > 0) ? currentUser.rowExportLimit : Infinity;
  var isAdmin = _isDataAdmin(user); /* export limits / grid export follow the client for team admins */
  var isReadonly = !isAdmin && user && user.accessMode === "readonly";
  /* Per-client export permission. When user.allowExport === false, all export buttons
     should appear disabled (treated like a permanent limit-reached state). Admins always
     bypass — they may need to export for diagnostics regardless of client policy. */
  var exportBlocked = !isAdmin && user && user.allowExport === false;
  /* Grid export is gated independently. allowGridExport reflects the EFFECTIVE permission
     (client master AND'd with user override) as resolved on the server at login time. */
  var gridExportBlocked = !isAdmin && user && user.allowGridExport === false;
  var limitReached = !isAdmin && (exportBlocked || isReadonly || (extractLimit !== Infinity && extractUsed >= extractLimit));

  /* ── State ── */
  /* Initial collapsed state: ALL levels CLOSED by default. User sees just the
     manufacturer NAMES at L1 (no products under them, no SKUs). Clicking the ▶
     arrow on a manufacturer expands it to show products (L2). Clicking the ▶
     arrow on a product expands it to show SKUs (L3). Recursive walker sets every
     node's pathKey → true (collapsed). Since the full pivot data is loaded up
     front, every expand is instant — no server roundtrips. */
  const [collapsed,  setCollapsed]  = useState(function(){
    var s = {};
    var walk = function(nodes, parentPath) {
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        var pk = parentPath ? parentPath + "||" + n.name : n.name;
        s[pk] = true; /* all collapsed */
        if (n.children && n.children.length) walk(n.children, pk);
      }
    };
    walk(rawNodes, "");
    return s;
  });
  /* ── Expand-All confirmation modal state ──
     {mode: "togAll" | "togLevel", depth?: number, parents: number, heavyDims: string[]}
     null = no modal open. Used to gate heavy expand operations behind explicit user confirm. */
  const [expandPrompt, setExpandPrompt] = useState(null);
  const [expanding, setExpanding] = useState(false);  /* shows "this may take time" overlay */
  /* DOM-level pagination — caps how many L1 (top-level) nodes we render at a time.
     Since the full pivot data is loaded up front, we have everything in memory; this
     limit just controls how much we paint into the DOM at once. Initial 200 L1
     manufacturers; auto-grows by 200 when user scrolls near the bottom. */
  var INITIAL_ROW_LIMIT = 200;
  var ROW_LIMIT_STEP = 200;
  const [renderRowLimit, setRenderRowLimit] = useState(INITIAL_ROW_LIMIT);
  const [sortCfg,    setSortCfg]    = useState(null);
  const [exporting,  setExporting]  = useState(false);
  const [limitMsg,   setLimitMsg]   = useState("");
  const [periodAsc,  setPeriodAsc]  = useState(true); /* true=oldest first, false=newest first */

  /* ── Table ref for dynamic sticky measurement ── */
  var pivotTableRef = useRef(null);
  /* The scrolling container that wraps the table — we attach onScroll to grow the
     DOM row limit (renderRowLimit) when user scrolls near the bottom. */
  var scrollContainerRef = useRef(null);
  useEffect(function() {
    if (!pivotTableRef.current) return;
    var tbl = pivotTableRef.current;
    var rows = tbl.querySelectorAll("thead tr");
    var cumTop = 0;
    for (var ri = 0; ri < rows.length; ri++) {
      var cells = rows[ri].querySelectorAll("th, td");
      for (var ci = 0; ci < cells.length; ci++) {
        var cell = cells[ci];
        cell.style.position = "sticky";
        cell.style.top = cumTop + "px";
        cell.style.zIndex = String(20 - ri);
        /* Dimension column: also sticky left */
        if (ci === 0 && (cell.getAttribute("rowspan") || (ri === rows.length - 1 && ci === 0))) {
          cell.style.left = "0px";
          cell.style.zIndex = String(25 - ri);
        }
      }
      cumTop += rows[ri].getBoundingClientRect().height;
    }
  });

  useEffect(function(){
    var s={}; for(var i=0;i<rawNodes.length;i++){ s[rawNodes[i].name]=false; if(rawNodes[i].children) for(var j=0;j<rawNodes[i].children.length;j++) s[rawNodes[i].name+"||"+rawNodes[i].children[j].name]=true; }
    setCollapsed(s);
    /* Period direction default: kimadia tender data reads more naturally with the
       latest year FIRST (newest left → oldest right) — awarded/delivered/pending
       breakdowns are typically read "current year vs prior". Other studies stay
       ascending (oldest left → newest right). User can still flip via the
       period-header sort arrow. */
    /* Default order: DESCENDING by real period (newest column first) for all
       studies — click the period header to flip to oldest-first. */
    setPeriodAsc(false);
    /* Default sort: descending on the latest period by the FIRST SELECTED measure
       (r126, Noura: "sorted at least based on the first measure selection — unit,
       prescription …"). data.msrs keeps the user's selection order, so a medical
       query that picked Prescriptions first opens sorted by # Prescriptions, one that
       picked Unit first by Units; Patients / Doctors / Rx per Doctor / Pharmacies
       sort the same way. Price-type measures are skipped (not summable) and the
       next selected measure is used; nothing sortable → natural order. */
    var _sp = data.sortedPeriods || data.periods || [];
    var _lastPk = _sp.length > 0 ? _sp[_sp.length - 1] : null;
    var _ms = data.msrs || [];
    var _defM = _defaultSortKey(_ms);
    if (_lastPk && _defM) {
      setSortCfg({pk:_lastPk, m:_defM, type:"val", ms:null, dir:"desc"});
    } else {
      setSortCfg(null);
    }
  }, [data]); // eslint-disable-line

  /* ── Period ordering ──
     ALWAYS chronological by the REAL period (buildPivot's sortedPeriods), never
     the user's click/selection order. periodAsc toggles direction; default is
     descending (newest first). */
  var originalPeriods = periods;
  var sortedPeriods = useMemo(function() {
    if (!periods || !periods.length) return periods;
    var arr = (data.sortedPeriods && data.sortedPeriods.length === periods.length)
      ? data.sortedPeriods.slice()   /* chronological ASC, compound-aware */
      : periods.slice();
    /* Compound periods (kimadia "year_status"): direction flips the YEAR order
       ONLY — statuses stay alphabetical within each year, matching the
       year-grouped header exactly. Reversing the whole array used to flip the
       statuses inside each year too, which put Awarded values under the
       Delivered column (and vice-versa). */
    /* Flow-compound ("2026-Q1~Sell-In"): direction flips the BASE-period order
       only — flows keep their fixed order (Sell-In | Sell-Out) inside each
       base, matching the grouped header. Base segments are reversed WITHIN
       each period-type run so multi-type band order stays the user's. */
    var isFlowC = String(arr[0]).indexOf("~") > 0;
    if (isFlowC) {
      if (!periodAsc) {
        var _ptF = data.periodTypes || {};
        var _baseOfF = function(k){ var t = String(k).indexOf("~"); return t >= 0 ? String(k).slice(0, t) : String(k); };
        var _outF = [];
        var _iT = 0;
        while (_iT < arr.length) {
          /* type run */
          var _jT = _iT;
          while (_jT + 1 < arr.length && (_ptF[arr[_jT + 1]] || "") === (_ptF[arr[_iT]] || "")) _jT++;
          var _run = arr.slice(_iT, _jT + 1);
          /* base segments inside the run */
          var _segsF = []; var _iB = 0;
          while (_iB < _run.length) {
            var _jB = _iB;
            while (_jB + 1 < _run.length && _baseOfF(_run[_jB + 1]) === _baseOfF(_run[_iB])) _jB++;
            _segsF.push(_run.slice(_iB, _jB + 1));
            _iB = _jB + 1;
          }
          _segsF.reverse();
          for (var _sF = 0; _sF < _segsF.length; _sF++) {
            for (var _sF2 = 0; _sF2 < _segsF[_sF].length; _sF2++) _outF.push(_segsF[_sF][_sF2]);
          }
          _iT = _jT + 1;
        }
        arr = _outF;
      }
      return arr;
    }
    var isComp = String(arr[0]).indexOf("_") > 0;
    if (isComp) {
      arr.sort(function(a, b) {
        var pa = String(a).split("_"), pb = String(b).split("_");
        var ya = parseInt(pa[0]), yb = parseInt(pb[0]);
        if (ya !== yb) return periodAsc ? ya - yb : yb - ya;
        return pa[1] < pb[1] ? -1 : pa[1] > pb[1] ? 1 : 0; /* same comparator as the header */
      });
      return arr;
    }
    if (!periodAsc) {
      if (data.multiType) {
        /* Multi-type: band order is the USER'S SELECTION order — keep it fixed
           and reverse chronology only WITHIN each type band. A full reverse
           would flip the band sequence too. */
        var _pt9 = data.periodTypes || {};
        var _out9 = [];
        var _i9 = 0;
        while (_i9 < arr.length) {
          var _j9 = _i9;
          while (_j9 + 1 < arr.length && _pt9[arr[_j9 + 1]] === _pt9[arr[_i9]]) _j9++;
          var _seg9 = arr.slice(_i9, _j9 + 1); _seg9.reverse();
          for (var _s9 = 0; _s9 < _seg9.length; _s9++) _out9.push(_seg9[_s9]);
          _i9 = _j9 + 1;
        }
        arr = _out9;
      } else {
        arr.reverse();
      }
    }
    return arr;
  }, [periods, periodAsc]);
  periods = sortedPeriods; /* use sorted order everywhere in rendering */

  /* ── Level styles / colors ── */
  var LCOLORS = ["#1a4068","#1e5082","#2d6a8f","#3d7a9c","#4d8aa8","#64748b"];
  var LSTYLES = [
    {bg:"#eef4fb",co:"#1a4068",br:"#b8d0e8",fw:700,tbg:"#f4f8fc"},   /* L1 — soft blue-white */
    {bg:"#e8f0f8",co:"#1e5082",br:"#a8c4de",fw:700,tbg:"#f0f6fb"},   /* L2 — light steel */
    {bg:"#ecf1f7",co:"#2d6a8f",br:"#b0c8dc",fw:600,tbg:"#f3f7fb"},   /* L3 — misty blue */
    {bg:"#eff3f8",co:"#3d7a9c",br:"#bcd0e0",fw:600,tbg:"#f5f8fb"},   /* L4 — sky frost */
    {bg:"#f1f5f9",co:"#4d8aa8",br:"#c8d8e6",fw:600,tbg:"#f7f9fb"},   /* L5 — cloud grey */
    {bg:"#f5f8fa",co:"#64748b",br:"#d4dde6",fw:500,tbg:"#f9fafb"},   /* L6 — whisper */
    {bg:"#f8fafc",co:"#6b7a8f",br:"#dbe3ea",fw:500,tbg:"#fbfcfd"},   /* L7 — mist */
    {bg:"#fafbfc",co:"#7a8798",br:"#e2e8ee",fw:500,tbg:"#fcfdfe"},   /* L8 — haze */
  ];

  /* ── Collapse helpers ── */
  var _lzc = lazyChildren || {};
  var _hasKids = function(n, kk) { return n.children.length > 0 || n._lazy || (_lzc[kk] && _lzc[kk].length > 0); };
  var _getKids = function(n, kk) { return (_lzc[kk] && _lzc[kk].length > 0) ? _lzc[kk] : n.children; };
  var collectKeysAtDepth = function(nodes, targetDepth, parentPath) {
    var keys = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var kk = parentPath ? parentPath + "||" + n.name : n.name;
      if (n.depth === targetDepth && _hasKids(n, kk)) keys.push(kk);
      var kids = _getKids(n, kk);
      if (kids.length > 0) { var sub = collectKeysAtDepth(kids, targetDepth, kk); for (var j=0;j<sub.length;j++) keys.push(sub[j]); }
    }
    return keys;
  };
  var allAtDepthCollapsed = function(depth) {
    var keys = collectKeysAtDepth(rawNodes, depth, "");
    if (!keys.length) return true; /* No nodes at this depth yet = treat as collapsed (needs loading) */
    for (var i=0;i<keys.length;i++) { if (!collapsed[keys[i]]) return false; }
    return true;
  };
  var togLevel = function(depth, force) {
    var keys = collectKeysAtDepth(rawNodes, depth, "");
    var allCol = keys.length === 0 || allAtDepthCollapsed(depth);

    if (allCol) {
      /* Data is fully pre-loaded — no modal guard, no lazy fetch, no overlay needed.
         Just open the level (pure DOM toggle, instant). */
      var newCollapsed = Object.assign({}, collapsed);
      for (var d = 0; d <= depth; d++) {
        var dKeys = collectKeysAtDepth(rawNodes, d, "");
        /* If no keys at this depth yet, we need to expand+load all nodes at depth-1 first */
        if (dKeys.length === 0 && d > 0) {
          /* Trigger lazy load for all nodes at parent depth that are lazy */
          var parentKeys = collectKeysAtDepth(rawNodes, d - 1, "");
          if (onLoadChildren) {
            var _lzParentsA = [];
            for (var pk = 0; pk < parentKeys.length; pk++) {
              var pParts = parentKeys[pk].split("||");
              var pNode = null;
              var pSearch = rawNodes;
              for (var pp = 0; pp < pParts.length; pp++) {
                for (var ps = 0; ps < pSearch.length; ps++) {
                  if (pSearch[ps].name === pParts[pp]) { pNode = pSearch[ps]; pSearch = _getKids(pSearch[ps], pParts.slice(0, pp + 1).join("||")); break; }
                }
              }
              if (pNode && pNode._lazy && !_lzc[parentKeys[pk]]) {
                _lzParentsA.push({ key: parentKeys[pk], parts: pParts, depth: pNode.depth });
              }
            }
            /* Many lazy parents → ONE batch query for the whole level instead of a request storm */
            if (onLoadLevel && _lzParentsA.length > 5) { onLoadLevel(_lzParentsA[0].depth); }
            else { for (var _la = 0; _la < _lzParentsA.length; _la++) { onLoadChildren(_lzParentsA[_la].key, _lzParentsA[_la].parts, _lzParentsA[_la].depth); } }
          }
        }
        /* Expand all nodes at this depth */
        var _lzNodesB = [];
        for (var ki = 0; ki < dKeys.length; ki++) {
          delete newCollapsed[dKeys[ki]]; /* uncollapse */
          /* Trigger lazy load if needed */
          if (d === depth && onLoadChildren) {
            var lParts = dKeys[ki].split("||");
            var lNode = null;
            var lSearch = rawNodes;
            for (var lp = 0; lp < lParts.length; lp++) {
              for (var ls = 0; ls < lSearch.length; ls++) {
                if (lSearch[ls].name === lParts[lp]) { lNode = lSearch[ls]; lSearch = _getKids(lSearch[ls], lParts.slice(0, lp + 1).join("||")); break; }
              }
            }
            if (lNode && lNode._lazy && !_lzc[dKeys[ki]]) {
              _lzNodesB.push({ key: dKeys[ki], parts: lParts, depth: lNode.depth });
            }
          }
        }
        /* Many lazy nodes at this depth → ONE batch query instead of a request storm */
        if (_lzNodesB.length) {
          if (onLoadLevel && _lzNodesB.length > 5) { onLoadLevel(_lzNodesB[0].depth); }
          else { for (var _lb = 0; _lb < _lzNodesB.length; _lb++) { onLoadChildren(_lzNodesB[_lb].key, _lzNodesB[_lb].parts, _lzNodesB[_lb].depth); } }
        }
      }
      setCollapsed(newCollapsed);
    } else {
      /* COLLAPSING: collapse this depth and all deeper levels */
      var colKeys = {};
      for (var cd = depth; cd < allDims.length; cd++) {
        var cdKeys = collectKeysAtDepth(rawNodes, cd, "");
        for (var ci = 0; ci < cdKeys.length; ci++) colKeys[cdKeys[ci]] = true;
      }
      setCollapsed(function(p) { return Object.assign({}, p, colKeys); });
    }
  };
  var allCollapsed = rawNodes.length > 0 && rawNodes.every(function(n){ return collapsed[n.name]; });

  /* Heavy-expansion guard scope — applies ONLY to MEDICAL studies.
     Rationale: medical queries (with diagnosis, sku, product) fan out to enormous SQL
     loads because MedicalData has 9.5M rows and each parent expand fires its own query.
     Pharma studies (Iraq/Jordan/Libya/Kimadia) have smaller datasets and indexed lookups —
     a long Expand All is acceptable there. Both still show the "please be patient"
     overlay while the load is in progress. */
  var _isMedicalStudy = activeStudyType === "medical" || activeStudyType === "jordan_medical" || (activeStudyType && activeStudyType.indexOf("medical") >= 0);

  /* Heavy dims that explode the row count and SQL load when expanded across thousands of parents.
     The guard only fires on medical studies (see _isMedicalStudy above). */
  var HEAVY_DIMS = {medicine:1, sku:1, molecule:1, diagnosis:1, subProduct:1, productSource:1, orderNo:1};
  var EXPAND_ALL_GUARD_THRESHOLD = 50; /* parents at L1 — safe ceiling for unfiltered expand-all */

  /* Count how many lazy queries the expand-all would trigger and whether the deeper levels
     contain a heavy dim. The check is conservative — if either condition is met, prompt.
     For non-medical studies, ALWAYS returns null (no guard) so Expand All just proceeds. */
  var expandAllRisk = (function(){
    if (!_isMedicalStudy) return null;               /* pharma: no blocking, just patience overlay */
    if (allDims.length < 2) return null;             /* only one level, nothing to expand */
    if (topNEnabled) return null;                    /* user has Top N — they've scoped the result */
    /* Are any sub-levels (depth ≥ 1) heavy dims? */
    var hasHeavySub = false;
    for (var d = 1; d < allDims.length; d++) {
      if (HEAVY_DIMS[allDims[d]]) { hasHeavySub = true; break; }
    }
    if (!hasHeavySub) return null;                   /* deeper dims aren't heavy — fine to expand */
    if (rawNodes.length < EXPAND_ALL_GUARD_THRESHOLD) return null; /* small dataset */
    /* Build a friendly description of what's heavy */
    var heavyNames = [];
    for (var d2 = 1; d2 < allDims.length; d2++) {
      if (HEAVY_DIMS[allDims[d2]]) heavyNames.push(FEAT_LBL[allDims[d2]] || allDims[d2]);
    }
    return {
      parents: rawNodes.length,
      heavyDims: heavyNames
    };
  })();

  /* Scroll handler — grows the DOM row limit when user scrolls near the bottom.
     Throttled to 200ms to prevent excessive re-renders. */
  var _scrollThrottleRef = useRef(0);
  var _onPivotScroll = function(e) {
    var now = Date.now();
    if (now - _scrollThrottleRef.current < 200) return;
    _scrollThrottleRef.current = now;

    var container = e.currentTarget;
    var scrollTop = container.scrollTop;
    var clientHeight = container.clientHeight;
    var scrollHeight = container.scrollHeight;
    /* Trigger when within 400px of the bottom */
    if (scrollHeight - (scrollTop + clientHeight) < 400) {
      setRenderRowLimit(function(p){
        /* Don't grow past what's available */
        var maxL1 = (displayNodes || []).length || 0;
        if (p >= maxL1) return p;
        return Math.min(p + ROW_LIMIT_STEP, maxL1);
      });
    }
  };

  var runExpandAll = function() {
    /* With the always-full-fetch architecture, all children are already in memory
       at render time. Expand All just needs to clear the collapsed map — no lazy
       loads, no batching, no pagination. Pure DOM toggle, instant, no overlay
       needed (DOM pagination caps render at renderRowLimit rows). */
    setCollapsed({});
  };

  /* Collapse-state bookkeeping.
     NEW QUERY (nodes array identity changed): full reset — everything collapsed,
     pagination back to the first page. Same behavior as always.
     LAZY/BATCH REFRESH (same nodes array, only lazyChildren grew): PRESERVE the
     user's expand/collapse choices and only register the newly materialized child
     nodes as collapsed-by-default. Without this, every lazy load wiped the open
     tree and the level chips mis-reported un-rendered lazy nodes as expanded. */
  var _lastNodesRef = useRef(null);
  var _knownKeysRef = useRef({});
  useEffect(function(){
    var isNewQuery = _lastNodesRef.current !== rawNodes;
    _lastNodesRef.current = rawNodes;
    if (isNewQuery) {
      setRenderRowLimit(INITIAL_ROW_LIMIT);
      _knownKeysRef.current = {};
    }
    var known = _knownKeysRef.current;
    setCollapsed(function(prev){
      var out = isNewQuery ? {} : Object.assign({}, prev);
      var walk = function(nodes, parentPath) {
        for (var i = 0; i < nodes.length; i++) {
          var n = nodes[i];
          var pk = parentPath ? parentPath + "||" + n.name : n.name;
          if (!known[pk]) { known[pk] = 1; out[pk] = true; /* first sighting → collapsed */ }
          var kids = _getKids(n, pk);
          if (kids && kids.length) walk(kids, pk);
        }
      };
      walk(rawNodes, "");
      return out;
    });
  }, [data]);

  var togAll = function() {
    if (allCollapsed) {
      /* Data is fully pre-loaded (initialLevel=null fetches all dimensions in one
         server query). Expand All is now a pure DOM operation — no modal, no lazy
         loads. The renderRowLimit caps DOM size at INITIAL_ROW_LIMIT rows; more
         load on scroll. */
      runExpandAll();
    } else {
      /* Collapse all */
      var s = {};
      for (var i = 0; i < rawNodes.length; i++) s[rawNodes[i].name] = true;
      setCollapsed(s);
    }
  };
  var togNode = function(pathKey) {
    setCollapsed(function(p){ var n=Object.assign({},p); n[pathKey]=!n[pathKey]; return n; });
  };

  /* ── Recursive sort ── */
  var sortNodes = function(nodes) {
    if (!sortCfg || !nodes || !nodes.length) return nodes;
    var sc = sortCfg;
    /* Separate "Others" nodes — they must always stay at the bottom */
    var othersNodes = [];
    var regularNodes = [];
    for (var si = 0; si < nodes.length; si++) {
      if (nodes[si].name && nodes[si].name.indexOf("Others (") === 0) {
        othersNodes.push(nodes[si]);
      } else {
        regularNodes.push(nodes[si]);
      }
    }
    var sorted = regularNodes.slice().sort(function(a,b){
      var sign = sc.dir==="asc"?1:-1;
      var av = 0, bv = 0;
      var ad = a.data&&a.data[sc.pk] ? a.data[sc.pk] : null;
      var bd = b.data&&b.data[sc.pk] ? b.data[sc.pk] : null;
      if (sc.type === "share") {
        /* Share: use the share suffix key (us, ls, ds, pls, pds) */
        av = ad ? parseFloat(ad[sc.ms] || 0) : 0;
        bv = bd ? parseFloat(bd[sc.ms] || 0) : 0;
      } else if (sc.type === "rank") {
        /* Rank: lower rank is better, so sort by measure value */
        av = ad ? (ad[sc.m] || 0) : 0;
        bv = bd ? (bd[sc.m] || 0) : 0;
      } else if (sc.type === "grPrev" || sc.type === "grYoy") {
        /* Growth: compute growth % for sorting */
        var aPrevPk = sc.type === "grPrev" ? prevPeriodOf[sc.pk] : yoyPeriodOf[sc.pk];
        var bPrevPk = aPrevPk;
        var aCurr = ad ? (ad[sc.m] || 0) : 0;
        var aPrev = (aPrevPk && a.data && a.data[aPrevPk]) ? (a.data[aPrevPk][sc.m] || 0) : 0;
        var bCurr = bd ? (bd[sc.m] || 0) : 0;
        var bPrev = (bPrevPk && b.data && b.data[bPrevPk]) ? (b.data[bPrevPk][sc.m] || 0) : 0;
        av = aPrev !== 0 ? (aCurr - aPrev) / Math.abs(aPrev) * 100 : 0;
        bv = bPrev !== 0 ? (bCurr - bPrev) / Math.abs(bPrev) * 100 : 0;
      } else if (sc.type === "evo" || sc.type === "evoY") {
        /* Evolution Index: siblings share the same reference base, so ordering
           by the row's own growth ratio (cur ÷ base) equals ordering by EI.
           "evo" sorts the vs-previous column, "evoY" the vs-similar column. */
        var eBasePk = (sc.type === "evoY" ? yoyPeriodOf : prevPeriodOf)[sc.pk];
        var aeCur = ad ? (ad[sc.m] || 0) : 0;
        var aeBase = (eBasePk && a.data && a.data[eBasePk]) ? (a.data[eBasePk][sc.m] || 0) : 0;
        var beCur = bd ? (bd[sc.m] || 0) : 0;
        var beBase = (eBasePk && b.data && b.data[eBasePk]) ? (b.data[eBasePk][sc.m] || 0) : 0;
        av = aeBase > 0 ? aeCur / aeBase : 0;
        bv = beBase > 0 ? beCur / beBase : 0;
      } else if (sc.type === "cagr") {
        /* CAGR: compute from first to last period (or year for compound) */
        var aStart, aEnd, bStart, bEnd;
        if (isCompoundPeriod && cagrYears.length >= 2) {
          var _fy = cagrYears[0], _ly = cagrYears[cagrYears.length - 1];
          aStart = 0; aEnd = 0; bStart = 0; bEnd = 0;
          originalPeriods.forEach(function(pk) {
            var y = pk.split("_")[0];
            if (y === _fy) { aStart += (a.data && a.data[pk]) ? (a.data[pk][sc.m] || 0) : 0; bStart += (b.data && b.data[pk]) ? (b.data[pk][sc.m] || 0) : 0; }
            if (y === _ly) { aEnd   += (a.data && a.data[pk]) ? (a.data[pk][sc.m] || 0) : 0; bEnd   += (b.data && b.data[pk]) ? (b.data[pk][sc.m] || 0) : 0; }
          });
        } else {
          aStart = (cagrFirstPk && a.data && a.data[cagrFirstPk]) ? (a.data[cagrFirstPk][sc.m] || 0) : 0;
          aEnd   = (cagrLastPk  && a.data && a.data[cagrLastPk])  ? (a.data[cagrLastPk][sc.m]  || 0) : 0;
          bStart = (cagrFirstPk && b.data && b.data[cagrFirstPk]) ? (b.data[cagrFirstPk][sc.m] || 0) : 0;
          bEnd   = (cagrLastPk  && b.data && b.data[cagrLastPk])  ? (b.data[cagrLastPk][sc.m]  || 0) : 0;
        }
        av = (aStart > 0 && aEnd > 0 && cagrN > 1) ? (Math.pow(aEnd/aStart, 1/cagrIntv) - 1) * 100 : 0;
        bv = (bStart > 0 && bEnd > 0 && cagrN > 1) ? (Math.pow(bEnd/bStart, 1/cagrIntv) - 1) * 100 : 0;
      } else {
        /* Default: sort by raw measure value */
        av = ad ? (ad[sc.m] || 0) : 0;
        bv = bd ? (bd[sc.m] || 0) : 0;
      }
      return sign*(av-bv);
    });
    for (var i=0;i<sorted.length;i++) {
      if (sorted[i].children&&sorted[i].children.length) {
        sorted[i]=Object.assign({},sorted[i],{children:sortNodes(sorted[i].children)});
      }
    }
    /* Append Others at the end */
    return sorted.concat(othersNodes);
  };

  var handleSort = function(pk, m, type, ms) {
    type = type || "val";
    setSortCfg(function(c){
      if (c&&c.pk===pk&&c.m===m&&c.type===type) return c.dir==="desc"?{pk:pk,m:m,type:type,ms:ms,dir:"asc"}:null;
      return {pk:pk,m:m,type:type,ms:ms,dir:"desc"};
    });
  };
  var SA = function(pk, m, type) {
    type = type || "val";
    if (!sortCfg||sortCfg.pk!==pk||sortCfg.m!==m||sortCfg.type!==type) return " \u21c5";
    return sortCfg.dir==="desc"?" \u25bc":" \u25b2";
  };

  /* ── Rank: compute rank map for a sibling group ── */
  /* returns rankMap[periodKey][measureKey][nodeName] = rank# */
  var rankSiblings = function(siblings) {
    var rm = {};
    /* Filter out "Others" nodes from ranking */
    var rankable = siblings.filter(function(s){ return !(s.name && s.name.indexOf("Others (") === 0); });
    for (var ri=0;ri<periods.length;ri++) {
      var rpk=periods[ri]; rm[rpk]={};
      for (var mi=0;mi<statMsrs.length;mi++) {
        var mm=statMsrs[mi].m;
        var sorted2=rankable.slice().sort(function(a,b){
          var av=a.data&&a.data[rpk]?a.data[rpk][mm]:0;
          var bv=b.data&&b.data[rpk]?b.data[rpk][mm]:0;
          return bv-av;
        });
        var nr={};
        for (var rj=0;rj<sorted2.length;rj++) nr[sorted2[rj].name]=rj+1;
        /* "Always include" pinned rows: show the ACTUAL rank, not the
           position within the displayed subset. */
        for (var rj2=0;rj2<rankable.length;rj2++) {
          var rs9 = rankable[rj2];
          if (rs9._rnkOv && rs9._rnkOv[rpk] && rs9._rnkOv[rpk][mm] != null) nr[rs9.name] = rs9._rnkOv[rpk][mm];
          else if (rs9._rnkAll != null) nr[rs9.name] = rs9._rnkAll;
        }
        rm[rpk][mm]=nr;
      }
    }
    return rm;
  };

  /* ── Growth formatter ── */
  var fmtGr = function(curr, prev) {
    if (prev==null||prev===0) return null;
    var g=(curr-prev)/Math.abs(prev)*100;
    var arrow = g > 0 ? "▲" : g < 0 ? "▼" : "●";
    return {val:g, str:arrow+" "+(Math.abs(g)>999?">999":Math.abs(g).toFixed(1))+"%", pos:g>0, zero:Math.abs(g)<0.05};
  };

  /* ── Evolution Index ──
     EI = (entity current ÷ entity base) ÷ (reference current ÷ reference base) × 100.
     The reference is the row's SHARE base (its parent group; grand total at L1) —
     the same base Market Share uses — so EI reads as "how did my share evolve".
     TWO base-period maps, selected by stats.evoMode: previous period and/or
     similar period last year (both → two columns, like Growth's Both mode).
     null when the base period has no data for entity or reference (no fake ∞). */
  var fmtEvo = function(cur, base, refCur, refBase) {
    if (base == null || refBase == null || refCur == null) return null;
    if (!(base > 0) || !(refBase > 0) || !(refCur > 0)) return null;
    var ei = (((cur || 0) / base) / (refCur / refBase)) * 100;
    var str = ei >= 999.5 ? ">999" : ei.toFixed(0);
    return {val:ei, str:str, pos:ei > 100.5, zero:Math.abs(ei - 100) <= 0.5};
  };

  /* ── CAGR helper ── */
  /* CAGR = ((EndValue / StartValue) ^ (1/n)) - 1  where n = number of periods selected */
  /* Sort periods chronologically to find true start/end regardless of display order */
  var cagrSortedPeriods = originalPeriods.slice().sort();
  var cagrFirstPk = null, cagrLastPk = null, cagrN = originalPeriods.length;
  if (cagrSortedPeriods.length >= 2) {
    for (var _cfi = 0; _cfi < cagrSortedPeriods.length; _cfi++) {
      var _cpk = cagrSortedPeriods[_cfi];
      if (grand[_cpk] && (grand[_cpk].u > 0 || grand[_cpk].d > 0 || grand[_cpk].l > 0)) {
        if (!cagrFirstPk) cagrFirstPk = _cpk;
        cagrLastPk = _cpk;
      }
    }
  }

  /* For compound periods (e.g. "2025_Awarded"), CAGR should use unique year count */
  var isCompoundPeriod = cagrFirstPk && cagrFirstPk.indexOf("_") > 0;
  var cagrYears = [];
  if (isCompoundPeriod) {
    var _cagrYSeen = {};
    originalPeriods.forEach(function(pk) { var y = pk.split("_")[0]; if (!_cagrYSeen[y]) { cagrYears.push(y); _cagrYSeen[y] = 1; } });
    cagrYears.sort();
    cagrN = cagrYears.length;
  }
  /* Compounding INTERVALS between the endpoints = periods − 1 (textbook CAGR).
     2023→2025 selected as 3 yearly periods compounds over 2 intervals, so the
     exponent is 1/2 — matching the grid export's year-span exponent. cagrN
     stays the PERIOD count (labels + the ≥2-periods guard use it). */
  var cagrIntv = Math.max(1, cagrN - 1);

  var calcCAGR = function(nodeData, mKey) {
    if (!showCAGR || !cagrFirstPk || !cagrLastPk || cagrN < 2) return null;
    var startVal, endVal;

    if (isCompoundPeriod && cagrYears.length >= 2) {
      /* Sum measure across all statuses for first and last year */
      var firstYear = cagrYears[0];
      var lastYear  = cagrYears[cagrYears.length - 1];
      startVal = 0; endVal = 0;
      originalPeriods.forEach(function(pk) {
        var y = pk.split("_")[0];
        var val = nodeData && nodeData[pk] ? (nodeData[pk][mKey] || 0) : 0;
        if (y === firstYear) startVal += val;
        if (y === lastYear)  endVal   += val;
      });
    } else {
      startVal = nodeData && nodeData[cagrFirstPk] ? (nodeData[cagrFirstPk][mKey] || 0) : 0;
      endVal   = nodeData && nodeData[cagrLastPk]  ? (nodeData[cagrLastPk][mKey]  || 0) : 0;
    }

    if (startVal <= 0 || endVal <= 0) return null;
    var cagr = (Math.pow(endVal / startVal, 1 / cagrIntv) - 1) * 100;
    var arrow = cagr > 0 ? "▲" : cagr < 0 ? "▼" : "●";
    return {val:cagr, str:arrow+" "+Math.abs(cagr).toFixed(1)+"%", pos:cagr>0, zero:Math.abs(cagr)<0.05};
  };

  /* ── Sort nodes (now that cagrFirstPk/cagrLastPk/cagrN are defined) ── */
  var displayNodes = sortNodes(rawNodes);

  /* ── CURRENT STOCK block (Sales Tracker): ONE trailing column — cumulative
     (Sell-In + FOC) − (Sell-Out + FOC) from the start of the data through the
     end of the LATEST selected period ("final qty as current stock"). The
     server computes only that latest key; the lookup is keyed
     "dimVals|latestBaseKey" — flow-independent. Suppressed for multi-type
     queries (no clean latest-period definition across mixed types). ── */
  var _stockLk9 = data.stockLookup || null;
  var stockBases = null;
  if (_stockLk9 && !data.multiType) {
    /* Latest base = last entry of the chronological-ASC sortedPeriods */
    var _spSrc = (data.sortedPeriods && data.sortedPeriods.length) ? data.sortedPeriods : periods;
    if (_spSrc && _spSrc.length) {
      var _lastPk = String(_spSrc[_spSrc.length - 1]);
      var _lt = _lastPk.indexOf("~");
      if (_lt >= 0) _lastPk = _lastPk.slice(0, _lt);
      stockBases = [_lastPk];
    }
  }
  var stockOn = !!(_stockLk9 && stockBases && stockBases.length);
  var _stockBlankKey = (data.dims || []).map(function(){ return ""; }).join("|");
  /* COVERAGE %: denominator lookup + position of governorate in the row dims
     (gov rows and their children divide by that governorate's panel total) */
  var _covLk = data.coverageLookup || null;
  var _covGovIdx = (data.dims || []).indexOf("governorate");

  /* ── Grand total row ── */
  var gtCells = [];
  for (var gpi=0; gpi<periods.length; gpi++) {
    var gpk=periods[gpi]; var gv=grand[gpk]||{u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,npl:0,npd:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0};
    var gPrevPk=prevPeriodOf[gpk]; var gvPrev=gPrevPk?(grand[gPrevPk]||null):null;
    var gYoyPk=yoyPeriodOf[gpk];   var gvYoy=gYoyPk?(grand[gYoyPk]||null):null;
    for (var gmi=0; gmi<activeMsrs.length; gmi++) {
      var gm=activeMsrs[gmi];
      /* Value — skuOnly (prices) blanks in grand total. leafOnly was for distinct counts but
         now grand total has the true distinct count from server, so display it. */
      var _gCellRaw = gv[gm.m];
      /* FOC % is derived per cell: FOC ÷ paid units (Gross − FOC) × 100 (never summed or stored) */
      if (gm.derived === "focPct") _gCellRaw = ((gv.gu || 0) - (gv.fu || 0)) > 0 ? ((gv.fu || 0) / ((gv.gu || 0) - (gv.fu || 0))) * 100 : 0;   /* r178: ÷ paid units (gross − FOC) */
      /* Coverage %: grand = filtered panel reach ÷ whole panel for that period */
      if (gm.derived === "coverage") {
        var _gcd = _covLk ? _covLk["T|" + gpk] : null;
        _gCellRaw = (_gcd > 0) ? ((gv.nph || 0) / _gcd) * 100 : 0;
      }
      /* Sell-Through %: sell-out ÷ sell-in of the SAME base period (flow bands).
         Rendered under the Sell-Out band only — "—" elsewhere. */
      if (gm.derived === "sellThrough") {
        _gCellRaw = null;
        var _sgB = String(gpk); var _sgT = _sgB.indexOf("~");
        if (_sgT >= 0 && _sgB.slice(_sgT + 1) === "Sell-Out") {
          var _sgIn = grand[_sgB.slice(0, _sgT) + "~Sell-In"] || null;
          var _sgInU = _sgIn ? (((_sgIn.gu || 0) > 0) ? _sgIn.gu : (_sgIn.u || 0)) : 0;
          var _sgOutU = ((gv.gu || 0) > 0) ? gv.gu : (gv.u || 0);
          if (_sgInU > 0) _gCellRaw = (_sgOutU / _sgInU) * 100;
        }
      }
      if (gm.noStats && gm.derived !== "sellThrough" && !gm.ratio2 && !gm.dashNull && (_gCellRaw == null)) _gCellRaw = 0;
      var _gBg = _ptv ? "#0F5C66" : (_msaTv && !_msaRxM[gm.m] ? "#0F6E56" : "#2d6d9e");
      var _gBd = _ptv ? "#0E515A" : (_msaTv && !_msaRxM[gm.m] ? "#0d5f4b" : "#256090");
      gtCells.push(<td key={gpk+gm.m+"v"} style={{background:_gBg,color:"#fff",fontFamily:"'JetBrains Mono',monospace",fontWeight:600,textAlign:"right",padding:"10px 12px",border:"1px solid "+_gBd,fontSize:11.5}}>{gm.skuOnly ? "" : (gm.ratio2 ? (_gCellRaw == null ? "\u2014" : Number(_gCellRaw).toFixed(2)) : (gm.dashNull && _gCellRaw == null) ? "\u2014" : fmtVal(gm.m, _gCellRaw))}</td>);
      /* Share — only for summable measures */
      if (!gm.noStats && shareFor(gm.m)) gtCells.push(<td key={gpk+gm.m+"s"} style={{background:_gBg,color:(_ptv?"#CFE7EA":"#dbeafe"),textAlign:"center",padding:"10px 8px",fontSize:10,fontWeight:600,border:"1px solid "+_gBd}}>100%</td>);
      /* Rank */
      if (!gm.noStats && rankFor(gm.m)) gtCells.push(<td key={gpk+gm.m+"r"} style={{background:_gBg,color:(_ptv?"#8FD0D8":"#93c5fd"),textAlign:"center",padding:"10px 8px",fontSize:10,border:"1px solid "+_gBd}}>{"—"}</td>);
      /* Growth prev */
      if (!gm.noStats && grPrevFor(gm.m)) {
        var grGtP=gvPrev?fmtGr(gv[gm.m],gvPrev[gm.m]):null;
        var gPco=grGtP?(grGtP.pos?"#d1fae5":grGtP.zero?"#e2e8f0":"#fecaca"):(_ptv?"#0F5C66":"#2d6d9e");
        var gPtx=grGtP?(grGtP.pos?"#059669":grGtP.zero?"#94a3b8":"#e11d48"):"#475569";
        gtCells.push(<td key={gpk+gm.m+"gp"} style={{background:gPco,color:gPtx,textAlign:"center",padding:"9px 8px",fontSize:10,fontWeight:800,border:"1px solid "+(_ptv?"#0E515A":"#256090"),whiteSpace:"nowrap"}}>{grGtP?grGtP.str:"—"}</td>);
      }
      /* Growth YoY */
      if (!gm.noStats && grYoyFor(gm.m)) {
        var grGtY=gvYoy?fmtGr(gv[gm.m],gvYoy[gm.m]):null;
        var gYco=grGtY?(grGtY.pos?"#d1fae5":grGtY.zero?"#e2e8f0":"#fecaca"):(_ptv?"#0F5C66":"#2d6d9e");
        var gYtx=grGtY?(grGtY.pos?"#059669":grGtY.zero?"#94a3b8":"#e11d48"):"#475569";
        gtCells.push(<td key={gpk+gm.m+"gy"} style={{background:gYco,color:gYtx,textAlign:"center",padding:"9px 8px",fontSize:10,fontWeight:800,border:"1px solid "+(_ptv?"#0E515A":"#256090"),whiteSpace:"nowrap"}}>{grGtY?grGtY.str:"—"}</td>);
      }
      /* Evolution Index — the market vs itself is 100 by definition (shown only
         when the base period actually has data, else em-dash) */
      if (!gm.noStats && evoPrevFor(gm.m)) {
        var gtEvoP = (gvPrev && gvPrev[gm.m] > 0) ? "100" : "—";
        gtCells.push(<td key={gpk+gm.m+"ei"} style={{background:(_ptv?"#0F5C66":"#2d6d9e"),color:(_ptv?"#CFE7EA":"#dbeafe"),textAlign:"center",padding:"9px 8px",fontSize:10,fontWeight:800,border:"1px solid "+(_ptv?"#0E515A":"#256090"),whiteSpace:"nowrap"}}>{gtEvoP}</td>);
      }
      if (!gm.noStats && evoYoyFor(gm.m)) {
        var gtEvoY = (gvYoy && gvYoy[gm.m] > 0) ? "100" : "—";
        gtCells.push(<td key={gpk+gm.m+"eiy"} style={{background:(_ptv?"#0F5C66":"#2d6d9e"),color:(_ptv?"#CFE7EA":"#dbeafe"),textAlign:"center",padding:"9px 8px",fontSize:10,fontWeight:800,border:"1px solid "+(_ptv?"#0E515A":"#256090"),whiteSpace:"nowrap"}}>{gtEvoY}</td>);
      }
      /* Profit statistic (Sell-Out bands only): SELL-OUT VALUE (retail) then
         PROFIT — USD scope → sv/pf, LC scope → svl/pfl. The main Value column
         is at the sell-in price, so Value + Profit = Sell-Out Value. */
      if (pfStatFor(gm.m, gpk)) {
        var _gtCst = gm.m === "d" ? (gv.sv || 0) : (gv.svl || 0);
        var _gtPf = gm.m === "d" ? (gv.pf || 0) : (gv.pfl || 0);
        gtCells.push(<td key={gpk+gm.m+"csq"} style={{background:(_ptv?"#0F5C66":"#2d6d9e"),color:(_ptv?"#CFE7EA":"#dbeafe"),fontFamily:"'JetBrains Mono',monospace",fontWeight:600,textAlign:"right",padding:"9px 10px",fontSize:11,border:"1px solid "+(_ptv?"#0E515A":"#256090"),whiteSpace:"nowrap"}}>{fmt(_gtCst)}</td>);
        gtCells.push(<td key={gpk+gm.m+"pfq"} style={{background:(_ptv?"#0F5C66":"#2d6d9e"),color:_gtPf < 0 ? "#fecaca" : "#fff",fontFamily:"'JetBrains Mono',monospace",fontWeight:700,textAlign:"right",padding:"9px 10px",fontSize:11,border:"1px solid "+(_ptv?"#0E515A":"#256090"),whiteSpace:"nowrap"}}>{fmt(_gtPf)}</td>);
      }
      /* Sell-Through % (Sell-Out bands only): sell-out ÷ sell-in of this measure */
      if (sthFor(gm.m, gpk)) {
        var _gtSi = grand[_siKeyOf(gpk)] || null;
        var _gtSt = (_gtSi && (_gtSi[gm.m] || 0) > 0) ? ((gv[gm.m] || 0) / _gtSi[gm.m]) * 100 : null;
        gtCells.push(<td key={gpk+gm.m+"stq"} style={{background:(_ptv?"#0F5C66":"#2d6d9e"),color:(_ptv?"#CFE7EA":"#dbeafe"),textAlign:"center",padding:"9px 8px",fontSize:10,fontWeight:800,border:"1px solid "+(_ptv?"#0E515A":"#256090"),whiteSpace:"nowrap"}}>{_gtSt == null ? "—" : _gtSt.toFixed(1) + "%"}</td>);
      }
    }
  }
  /* ── CAGR columns for grand total (only summable measures) ── */
  if (showCAGR && cagrMsrs.length > 0) {
    for (var gcmi=0; gcmi<cagrMsrs.length; gcmi++) {
      var gcm = cagrMsrs[gcmi];
      var gcagr = calcCAGR(grand, gcm.m);
      var gcBg = gcagr ? (gcagr.pos?"#d1fae5":gcagr.zero?"#e2e8f0":"#fecaca") : (_ptv?"#0F5C66":"#2d6d9e");
      var gcTx = gcagr ? (gcagr.pos?"#059669":gcagr.zero?"#94a3b8":"#e11d48") : "#475569";
      gtCells.push(<td key={"cagr_gt_"+gcm.m} style={{background:gcBg,color:gcTx,textAlign:"center",padding:"9px 8px",fontSize:10,fontWeight:800,border:"1px solid "+(_ptv?"#0E515A":"#256090"),whiteSpace:"nowrap"}}>{gcagr?gcagr.str:"—"}</td>);
    }
  }
  /* ── STOCK columns for grand total (closing stock per base period) ── */
  if (stockOn) {
    for (var gsb = 0; gsb < stockBases.length; gsb++) {
      var _gsv = _stockLk9[_stockBlankKey + "|" + stockBases[gsb]];
      gtCells.push(<td key={"stk_gt_"+stockBases[gsb]} style={{background:(_ptv?"#0F5C66":"#2d6d9e"),color:"#fff",fontFamily:"'JetBrains Mono',monospace",fontWeight:600,textAlign:"right",padding:"10px 12px",border:"1px solid "+(_ptv?"#0E515A":"#256090"),fontSize:11.5,whiteSpace:"nowrap"}}>{_gsv != null ? fmt(_gsv) : "—"}</td>);
    }
  }

  /* ── DFS tree rows ── */
  var tableRows = [];
  /* GT row stored separately — will be appended to <thead> so it freezes with headers */
  var gtRow = (
    <tr key="grand" className="pivot-gt">
      <td style={{background:(_ptv?"#0F5C66":"#2d6d9e"),color:"#fff",fontWeight:700,fontSize:12.5,padding:"10px 16px",position:"sticky",left:0,zIndex:6,borderRight:"2px solid "+(_ptv?"#0E515A":"#256090"),border:"1px solid "+(_ptv?"#0F5C66":"#2d6d9e"),whiteSpace:"nowrap",letterSpacing:".02em",maxWidth:360,width:360,overflow:"hidden"}}>
        &#8721; Grand Total
      </td>
      {gtCells}
    </tr>
  );

  var rootRankMap = showRank ? rankSiblings(displayNodes) : null;
  /* DOM pagination: only render the first renderRowLimit L1 nodes. Their descendants
     (products / SKUs) all still render fully — this just caps how many top-level
     manufacturers appear in the DOM. User scrolls → setRenderRowLimit grows by step. */
  var visibleNodeCount = Math.min(displayNodes.length, renderRowLimit);
  var hasMoreRows = displayNodes.length > visibleNodeCount;
  var dfsStack = [];
  for (var si=visibleNodeCount-1; si>=0; si--) {
    dfsStack.push({node:displayNodes[si], pathKey:displayNodes[si].name, rankMap:rootRankMap, parentData:null});
  }

  while (dfsStack.length > 0) {
    var item    = dfsStack.pop();
    var node    = item.node;
    var pathKey = item.pathKey;
    var rankMap = item.rankMap;
    var parentData = item.parentData; /* null for L1 (uses grand total) */
    var _ll = lazyLoading || {};
    var isLeaf  = node.children.length === 0 && !node._lazy && !_lzc[pathKey];
    var isOpen  = !collapsed[pathKey];
    var lst     = LSTYLES[node.depth] || LSTYLES[LSTYLES.length-1];
    var indent  = node.depth * 18;
    var lvlNum  = node.depth + 1;
    /* Effective children: lazy-loaded children override empty node.children */
    var effChildren = (_lzc[pathKey] && _lzc[pathKey].length > 0) ? _lzc[pathKey] : node.children;

    var dCells = [];
    for (var dpi=0; dpi<periods.length; dpi++) {
      var dpk = periods[dpi];
      /* Kimadia tender columns: a very soft per-status tint so Awarded and
         Delivered read as distinct bands while the dark text stays readable.
         Blue / violet / amber only — red and green are reserved for growth. */
      var _stTint;
      if (dpk.indexOf("~") > 0) {
        /* Sales Tracker flow bands: same soft tint pair as Awarded/Delivered */
        var _flTName = dpk.slice(dpk.indexOf("~") + 1);
        _stTint = _ptv
          ? (_flTName === "Sell-In" ? "#EDF6F7" : _flTName === "Sell-Out" ? "#E7F4F5" : "#EFF4F4")
          : (_flTName === "Sell-In" ? "#e3eefa" : _flTName === "Sell-Out" ? "#ece5f7" : "#eef1f5");
      } else if (dpk.indexOf("_") > 0) {
        var _stName = dpk.split("_").slice(1).join("_");
        _stTint = _stName === "Awarded" ? "#e3eefa" : _stName === "Delivered" ? "#ece5f7" : _stName === "Pending" ? "#f9f0d9" : "#eef1f5";
      } else { _stTint = undefined; }
      var dv  = node.data&&node.data[dpk] ? node.data[dpk] : {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,npl:0,npd:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,us:"0.0",fus:"0.0",gus:"0.0",ls:"0.0",ds:"0.0",pls:"0.0",pds:"0.0",npls:"0.0",npds:"0.0",nps:"0.0",nrxs:"0.0",nphs:"0.0",ius:"0.0"};
      var dPrevPk = prevPeriodOf[dpk]; var dvPrev = dPrevPk&&node.data&&node.data[dPrevPk] ? node.data[dPrevPk] : null;
      var dYoyPk  = yoyPeriodOf[dpk];  var dvYoy  = dYoyPk&&node.data&&node.data[dYoyPk]   ? node.data[dYoyPk]  : null;
      for (var dmi=0; dmi<activeMsrs.length; dmi++) {
        var dm = activeMsrs[dmi];
        /* msa: kimadia band colours per measure FAMILY when no period-band tint applies */
        var _mTint = _stTint;
        if (_mTint === undefined && _msaTv) _mTint = _msaRxM[dm.m] ? "#e3eefa" : "#e7f5ee";
        /* Value — skuOnly (prices) blank on non-SKU; leafOnly only blanks Top-N Others bucket
           (where summing isn't meaningful). Real parent rows now have true distinct counts from server. */
        var _priceBlank = dm.skuOnly && node.dim !== "sku";
        var _othersRow = node.name && String(node.name).indexOf("Others (") === 0;
        var _leafBlank  = dm.leafOnly && _othersRow;
        var _blankCell = _priceBlank || _leafBlank;
        /* For noStats distinct-count measures (np, nrx), force undefined → 0 so missing data
           still renders "0" instead of a blank cell. Summable measures keep their natural fmt
           (where blank means "no data ever recorded" — distinct from a true 0). */
        var _cellRaw = dv[dm.m];
        /* FOC % is derived per cell: FOC ÷ paid units (Gross − FOC) × 100 (never summed or stored) */
        if (dm.derived === "focPct") _cellRaw = ((dv.gu || 0) - (dv.fu || 0)) > 0 ? ((dv.fu || 0) / ((dv.gu || 0) - (dv.fu || 0))) * 100 : 0;   /* r178: ÷ paid units (gross − FOC) */
        /* Coverage %: nph ÷ panel denominator — governorate-scoped when the
           row sits at/under a Governorate level, whole panel otherwise */
        if (dm.derived === "coverage") {
          var _cvDen = null;
          if (_covGovIdx >= 0 && node._lkPath) {
            var _cvGv = String(node._lkPath).split("|")[_covGovIdx];
            if (_cvGv) _cvDen = _covLk ? _covLk["G|" + _cvGv + "|" + dpk] : null;
          }
          if (_cvDen == null && _covLk) _cvDen = _covLk["T|" + dpk];
          _cellRaw = (_cvDen > 0) ? ((dv.nph || 0) / _cvDen) * 100 : 0;
        }
        /* Sell-Through %: this row's sell-out ÷ its own sell-in for the same base period */
        if (dm.derived === "sellThrough") {
          _cellRaw = null;
          var _sdB = String(dpk); var _sdT = _sdB.indexOf("~");
          if (_sdT >= 0 && _sdB.slice(_sdT + 1) === "Sell-Out") {
            var _sdIn = (node.data && node.data[_sdB.slice(0, _sdT) + "~Sell-In"]) || null;
            var _sdInU = _sdIn ? (((_sdIn.gu || 0) > 0) ? _sdIn.gu : (_sdIn.u || 0)) : 0;
            var _sdOutU = ((dv.gu || 0) > 0) ? dv.gu : (dv.u || 0);
            if (_sdInU > 0) _cellRaw = (_sdOutU / _sdInU) * 100;
          }
        }
        if (dm.noStats && dm.derived !== "sellThrough" && !dm.ratio2 && !dm.dashNull && (_cellRaw == null)) _cellRaw = 0;
        dCells.push(<td key={dpk+dm.m+"v"} style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:500,textAlign:"right",padding:"8px 12px",border:"1px solid #e8edf2",fontSize:11.5,background:_mTint,color:_blankCell?"#cbd5e1":undefined}}>{_blankCell ? "" : (dm.ratio2 ? (_cellRaw == null ? "\u2014" : Number(_cellRaw).toFixed(2)) : (dm.dashNull && _cellRaw == null) ? "\u2014" : fmtVal(dm.m, _cellRaw))}</td>);
        /* Share — relative to parent (L1=grand, L2=L1 parent, L3=L2 parent) */
        if (!dm.noStats && shareFor(dm.m)) {
          var _shareBase = parentData && parentData[dpk] ? parentData[dpk][dm.m] : (grand[dpk] ? grand[dpk][dm.m] : 0);
          var _shareVal = _shareBase > 0 ? ((dv[dm.m] || 0) / _shareBase * 100).toFixed(1) : "0.0";
          dCells.push(<td key={dpk+dm.m+"s"} style={{textAlign:"center",padding:"8px 8px",border:"1px solid #e8edf2",fontSize:10,color:"#475569",fontWeight:600,background:_mTint}}>{_shareVal}%</td>);
        }
        /* Rank */
        if (!dm.noStats && rankFor(dm.m)) {
          var rnkNum = rankMap&&rankMap[dpk]&&rankMap[dpk][dm.m] ? rankMap[dpk][dm.m][node.name] : null;
          dCells.push(
            <td key={dpk+dm.m+"r"} style={{textAlign:"center",padding:"8px 6px",border:"1px solid #e8edf2",background:_mTint}}>
              {rnkNum
                ? <span style={{display:"inline-block",padding:"2px 9px",borderRadius:12,fontSize:9.5,fontWeight:700,background:"#e0ecf7",color:"#1a4068",fontFamily:"'JetBrains Mono',monospace",border:"1px solid #b8d4ea",boxShadow:"0 1px 3px rgba(56,128,187,.12)"}}>{rnkNum}</span>
                : <span style={{color:"#cbd5e1",fontSize:9}}>{"—"}</span>}
            </td>
          );
        }
        /* Growth prev */
        if (!dm.noStats && grPrevFor(dm.m)) {
          var grP = dvPrev ? fmtGr(dv[dm.m], dvPrev[dm.m]) : null;
          var gPcell = grP ? (grP.pos?"#ecfdf5":grP.zero?"#f8fafc":"#fff5f5") : "transparent";
          var gPtext = grP ? (grP.pos?"#059669":grP.zero?"#94a3b8":"#e11d48") : "#d4dde6";
          dCells.push(
            <td key={dpk+dm.m+"gp"} style={{textAlign:"center",padding:"8px 6px",border:"1px solid #e8edf2",background:gPcell,whiteSpace:"nowrap"}}>
              <span style={{fontSize:9.5,fontWeight:600,color:gPtext}}>{grP?grP.str:"—"}</span>
            </td>
          );
        }
        /* Growth YoY */
        if (!dm.noStats && grYoyFor(dm.m)) {
          var grY = dvYoy ? fmtGr(dv[dm.m], dvYoy[dm.m]) : null;
          var gYcell = grY ? (grY.pos?"#ecfdf5":grY.zero?"#f8fafc":"#fff5f5") : "transparent";
          var gYtext = grY ? (grY.pos?"#059669":grY.zero?"#94a3b8":"#e11d48") : "#d4dde6";
          dCells.push(
            <td key={dpk+dm.m+"gy"} style={{textAlign:"center",padding:"8px 6px",border:"1px solid #e8edf2",background:gYcell,whiteSpace:"nowrap"}}>
              <span style={{fontSize:9.5,fontWeight:600,color:gYtext}}>{grY?grY.str:"—"}</span>
            </td>
          );
        }
        /* Evolution Index — row growth vs its SHARE-BASE growth (parent group,
           grand total at L1). >100 gained share, <100 lost share, 100 = with
           the market. One column per enabled base: previous and/or similar.
           Reference current + base MUST come from the SAME source (parent||grand). */
        if (!dm.noStats && (evoPrevFor(dm.m) || evoYoyFor(dm.m))) {
          var _evRefSrc = parentData || grand;
          var _evRefCur = _evRefSrc && _evRefSrc[dpk] ? _evRefSrc[dpk][dm.m] : null;
          var _evEmit = function(basePk, nodeBaseRow, keySfx, ttl) {
            var _evRefBase = (basePk && _evRefSrc && _evRefSrc[basePk]) ? _evRefSrc[basePk][dm.m] : null;
            var dEvo = fmtEvo(dv[dm.m], nodeBaseRow ? nodeBaseRow[dm.m] : null, _evRefCur, _evRefBase);
            var eCell = dEvo ? (dEvo.pos?"#ecfdf5":dEvo.zero?"#f8fafc":"#fff5f5") : "transparent";
            var eText = dEvo ? (dEvo.pos?"#059669":dEvo.zero?"#64748b":"#e11d48") : "#d4dde6";
            dCells.push(
              <td key={dpk+dm.m+keySfx} title={ttl} style={{textAlign:"center",padding:"8px 6px",border:"1px solid #e8edf2",background:eCell,whiteSpace:"nowrap"}}>
                <span style={{fontSize:9.5,fontWeight:700,color:eText,fontFamily:"'JetBrains Mono',monospace"}}>{dEvo?dEvo.str:"—"}</span>
              </td>
            );
          };
          if (evoPrevFor(dm.m)) _evEmit(dPrevPk, dvPrev, "ei",  "Evolution Index vs previous period: 100 = grew with the group total; above 100 = gained share");
          if (evoYoyFor(dm.m))  _evEmit(dYoyPk,  dvYoy,  "eiy", "Evolution Index vs similar period last year: 100 = grew with the group total; above 100 = gained share");
        }
        /* Profit statistic (Sell-Out bands only): SELL-OUT VALUE (retail) then PROFIT */
        if (pfStatFor(dm.m, dpk)) {
          var _rwCst = dm.m === "d" ? (dv.sv || 0) : (dv.svl || 0);
          var _rwPf = dm.m === "d" ? (dv.pf || 0) : (dv.pfl || 0);
          dCells.push(<td key={dpk+dm.m+"csq"} style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:500,textAlign:"right",padding:"8px 10px",border:"1px solid #e8edf2",fontSize:11,background:_mTint,color:"#64748b",whiteSpace:"nowrap"}}>{fmt(_rwCst)}</td>);
          dCells.push(<td key={dpk+dm.m+"pfq"} style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600,textAlign:"right",padding:"8px 10px",border:"1px solid #e8edf2",fontSize:11,background:_mTint,color:_rwPf < 0 ? "#e11d48" : undefined,whiteSpace:"nowrap"}}>{fmt(_rwPf)}</td>);
        }
        /* Sell-Through % (Sell-Out bands only): this row's sell-out ÷ its own sell-in */
        if (sthFor(dm.m, dpk)) {
          var _rwSi = (node.data && node.data[_siKeyOf(dpk)]) || null;
          var _rwSt = (_rwSi && (_rwSi[dm.m] || 0) > 0) ? ((dv[dm.m] || 0) / _rwSi[dm.m]) * 100 : null;
          dCells.push(<td key={dpk+dm.m+"stq"} style={{textAlign:"center",padding:"8px 6px",border:"1px solid #e8edf2",fontSize:9.5,fontWeight:700,color:_rwSt != null && _rwSt < 60 ? "#b45309" : "#475569",background:_mTint,whiteSpace:"nowrap"}}>{_rwSt == null ? "—" : _rwSt.toFixed(1) + "%"}</td>);
        }
      }
    }
    /* ── CAGR column for this node (only summable measures) ── */
    if (showCAGR && cagrMsrs.length > 0) {
      for (var dcmi=0; dcmi<cagrMsrs.length; dcmi++) {
        var dcm = cagrMsrs[dcmi];
        var dcagr = calcCAGR(node.data, dcm.m);
        var dcBg = dcagr ? (dcagr.pos?"#ecfdf5":dcagr.zero?"#f8fafc":"#fff5f5") : "transparent";
        var dcTx = dcagr ? (dcagr.pos?"#059669":dcagr.zero?"#94a3b8":"#e11d48") : "#d4dde6";
        dCells.push(
          <td key={"cagr_"+pathKey+"_"+dcm.m} style={{textAlign:"center",padding:"8px 6px",border:"1px solid #e8edf2",background:dcBg,whiteSpace:"nowrap"}}>
            <span style={{fontSize:9.5,fontWeight:600,color:dcTx}}>{dcagr?dcagr.str:"—"}</span>
          </td>
        );
      }
    }
    /* ── STOCK columns for this node (closing stock per base period) ──
       Direct lookup via the node's server key prefix; negative closing stock
       (more sold out than bought — a data signal) renders amber, not red
       (red/green stay reserved for growth). */
    if (stockOn) {
      for (var dsb = 0; dsb < stockBases.length; dsb++) {
        var _dsv = (node._lkPath != null) ? _stockLk9[node._lkPath + "|" + stockBases[dsb]] : null;
        dCells.push(
          <td key={"stk_"+pathKey+"_"+stockBases[dsb]} style={{textAlign:"right",padding:"8px 12px",border:"1px solid #e8edf2",background:"#f4f7fa",fontFamily:"'JetBrains Mono',monospace",fontWeight:600,fontSize:11.5,color:(_dsv != null && _dsv < 0) ? "#b45309" : "#1e3a5f",whiteSpace:"nowrap"}}>
            {_dsv != null ? fmt(_dsv) : ""}
          </td>
        );
      }
    }

    tableRows.push(
      <tr key={pathKey} style={{background:isLeaf?"#fff":lst.bg}}>
        <td
          style={{background:lst.tbg,color:lst.co,fontWeight:lst.fw,fontSize:isLeaf?11:11.5,
                  padding:"7px 12px 7px "+(14+indent)+"px",position:"sticky",left:0,zIndex:2,
                  borderRight:"2px solid "+lst.br,borderLeft:node.depth>0?"3px solid "+lst.br:"none",
                  border:"1px solid "+lst.br,
                  maxWidth:360,width:360,overflow:"hidden",
                  cursor:isLeaf?"default":"pointer",userSelect:"none"}}
          onClick={isLeaf?null:function(pk2,nd){return function(){
            /* Trigger lazy load if this is a lazy node that hasn't been loaded yet */
            if(nd._lazy && !_lzc[pk2] && !_ll[pk2] && onLoadChildren){
              /* Build parent names array from pathKey */
              var pNames = pk2.split("||");
              onLoadChildren(pk2, pNames, nd.depth);
            }
            togNode(pk2);
          };}(pathKey,node)}
        >
          <div style={{display:"flex",alignItems:"center",gap:6,maxWidth:320-indent,overflow:"hidden"}}>
            {!isLeaf&&<span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:18,height:18,borderRadius:5,border:"1.5px solid "+lst.br,background:isOpen?lst.co:"#fff",color:isOpen?"#fff":lst.co,fontSize:9,fontWeight:700,flexShrink:0,boxShadow:"0 1px 2px rgba(0,0,0,.05)",transition:"all .15s"}}>{isOpen?"▼":"▶"}</span>}
            {isLeaf&&node.depth>0&&<span style={{width:16,height:16,display:"inline-block",flexShrink:0}}/>}
            <span style={{whiteSpace:"normal",wordBreak:"break-word",lineHeight:1.35}}>{node.name}</span>
          </div>
        </td>
        {dCells}
      </tr>
    );

    if (!isLeaf && isOpen) {
      /* Show loading indicator if lazy children are being fetched */
      if (_ll[pathKey]) {
        tableRows.push(
          <tr key={pathKey+"__loading"} style={{background:"#f8fafc"}}>
            <td colSpan={1 + periods.length * span + (showCAGR ? cagrMsrs.length : 0) + (stockOn ? stockBases.length : 0)} style={{padding:"10px 12px 10px "+(32+indent)+"px",fontSize:10,color:"#64748b",fontStyle:"italic",border:"1px solid #e8edf2",position:"sticky",left:0,zIndex:1}}>
              <span style={{display:"inline-block",width:12,height:12,border:"2px solid #e2e8f0",borderTopColor:(_ptv?"#0F5C66":"#2563eb"),borderRadius:"50%",animation:"spin 0.8s linear infinite",marginRight:8,verticalAlign:"middle"}} />
              Loading…
            </td>
          </tr>
        );
      } else {
        var sortedChildren = sortNodes(effChildren);
        var childRankMap = showRank ? rankSiblings(sortedChildren) : null;
        for (var ci=sortedChildren.length-1; ci>=0; ci--) {
          dfsStack.push({node:sortedChildren[ci], pathKey:pathKey+"||"+sortedChildren[ci].name, rankMap:childRankMap, parentData:node.data});
        }
      }
    }
  }

  /* ── Build header row 1 (period colSpan) + row 2 (measure sub-headers) ── */
  /* Both rows use the same navy background for a unified header block */
  var _hdrBg = (_ptv?"#17707C":"#3880bb");
  var _hdrBr = (_ptv?"#0F5C66":"#2d6d9e");
  var hdr2Cells = [];
  var _cellsPerPeriod = 0;
  for (var hpi=0; hpi<periods.length; hpi++) {
    var hpk=periods[hpi];
    var _startLen = hdr2Cells.length;
    for (var hmi=0; hmi<activeMsrs.length; hmi++) {
      var hm=activeMsrs[hmi];
      hdr2Cells.push(<th key={hpk+hm.m+"v"} onClick={function(p2,m2){return function(){handleSort(p2,m2,"val");};}(hpk,hm.m)} style={{background:_hdrBg,color:"#fff",border:"1px solid "+_hdrBr,fontSize:9.5,fontWeight:700,textTransform:"uppercase",textAlign:"center",cursor:"pointer",padding:"8px 10px",minWidth:76,userSelect:"none",whiteSpace:"nowrap",letterSpacing:".05em"}}>{hm.label}{SA(hpk,hm.m,"val")}</th>);
      if (!hm.noStats && shareFor(hm.m))  hdr2Cells.push(<th key={hpk+hm.m+"s"} onClick={function(p2,m2,ms2){return function(){handleSort(p2,m2,"share",ms2);};}(hpk,hm.m,hm.ms)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>% Share <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span>{SA(hpk,hm.m,"share")}</th>);
      if (!hm.noStats && rankFor(hm.m))   hdr2Cells.push(<th key={hpk+hm.m+"r"} onClick={function(p2,m2){return function(){handleSort(p2,m2,"rank");};}(hpk,hm.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>Rnk <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span>{SA(hpk,hm.m,"rank")}</th>);
      if (!hm.noStats && grPrevFor(hm.m)) hdr2Cells.push(<th key={hpk+hm.m+"gp"} onClick={function(p2,m2){return function(){handleSort(p2,m2,"grPrev");};}(hpk,hm.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>Gth <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span>{SA(hpk,hm.m,"grPrev")}</th>);
      if (!hm.noStats && grYoyFor(hm.m))  hdr2Cells.push(<th key={hpk+hm.m+"gy"} onClick={function(p2,m2){return function(){handleSort(p2,m2,"grYoy");};}(hpk,hm.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>Gth Similar <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span>{SA(hpk,hm.m,"grYoy")}</th>);
      if (!hm.noStats && evoPrevFor(hm.m)) hdr2Cells.push(<th key={hpk+hm.m+"ei"} title="Evolution Index vs previous period — 100 = grew with the market" onClick={function(p2,m2){return function(){handleSort(p2,m2,"evo");};}(hpk,hm.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>EI <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span>{SA(hpk,hm.m,"evo")}</th>);
      if (!hm.noStats && evoYoyFor(hm.m))  hdr2Cells.push(<th key={hpk+hm.m+"eiy"} title="Evolution Index vs similar period last year — 100 = grew with the market" onClick={function(p2,m2){return function(){handleSort(p2,m2,"evoY");};}(hpk,hm.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>EI Similar <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span>{SA(hpk,hm.m,"evoY")}</th>);
      if (pfStatFor(hm.m, hpk)) {
        hdr2Cells.push(<th key={hpk+hm.m+"csq"} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",whiteSpace:"nowrap"}}>Sell-Out Value <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span></th>);
        hdr2Cells.push(<th key={hpk+hm.m+"pfq"} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",whiteSpace:"nowrap"}}>Profit <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span></th>);
      }
      if (sthFor(hm.m, hpk)) hdr2Cells.push(<th key={hpk+hm.m+"stq"} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",whiteSpace:"nowrap"}}>Sell-Through <span style={{opacity:.65,fontSize:7.5}}>{hm.tag||""}</span></th>);
    }
    if (hpi === 0) _cellsPerPeriod = hdr2Cells.length - _startLen;
  }
  span = _cellsPerPeriod;
  /* ── CAGR header columns ── */
  if (showCAGR && cagrMsrs.length > 0) {
    for (var chmi=0; chmi<cagrMsrs.length; chmi++) {
      var chm = cagrMsrs[chmi];
      hdr2Cells.push(<th key={"cagr_h_"+chm.m} onClick={function(m2){return function(){handleSort("__cagr__",m2,"cagr");};}(chm.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 8px",minWidth:70,cursor:"pointer",userSelect:"none",whiteSpace:"nowrap",letterSpacing:".03em"}}>CAGR {chm.label}{SA("__cagr__",chm.m,"cagr")}</th>);
    }
  }
  /* ── CURRENT STOCK header sub-column (classic 2-row header) ── */
  if (stockOn) {
    for (var shb = 0; shb < stockBases.length; shb++) {
      hdr2Cells.push(<th key={"stk_h_"+stockBases[shb]} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 8px",minWidth:76,whiteSpace:"nowrap",letterSpacing:".03em"}}>{stockBases[shb] === "ALL" ? "All data" : "as of " + periodLabel9(stockBases[shb])}</th>);
    }
  }

  /* ── Grid (raw rows) Excel export ──
     Uses the cached `cachedRows` already loaded into the browser by the last query,
     bypassing any server round-trip. The rows are alias-keyed (per cachedMapping)
     and have already been filtered/aggregated server-side; we just project the
     selected dims + measures into a flat .xlsx. Permission is gated by
     allowGridExport on the user object (resolved server-side at login). */
  const [gridExporting, setGridExporting] = useState(false);
  const [gridErrMsg, setGridErrMsg] = useState("");
  /* Ask the user to name an extracted report — the name goes into BOTH the file
     name and the Excel sheet tab (sanitized to Excel's sheet-name rules). */
  var _askReportName = function(defName) {
    var nm = "";
    try { nm = window.prompt("Name this report (used for the file name and the sheet tab):", defName) || ""; } catch (e) { nm = ""; }
    nm = String(nm).trim() || defName;
    var sheet = nm.replace(/[:\\\/?*\[\]]/g, "-").slice(0, 31).trim() || defName.slice(0, 31);
    var file = nm.replace(/[<>:"\/\\|?*]/g, "").trim().replace(/\s+/g, "_").slice(0, 80) || defName.replace(/\s+/g, "_");
    return { sheet: sheet, file: file };
  };

  /* ── Structural marker (Layer 2 / Suggestion 2) ──
     Embeds the server-issued marker into the WORKBOOK STRUCTURE:
     (1) custom document properties, (2) a very-hidden "_AMS_Metadata" sheet,
     (3) a defined name. Client identity is a one-way TOKEN, never the name.
     Strippable by a determined leaker — by design the Data Fingerprint (units
     deltas) remains the forensic backstop; this layer is fast, direct evidence
     on original files. No business values are touched. */
  var _embedAmsMarker = function(XLSX, wb, mk) {
    if (!mk || !mk.exportId) return;
    try {
      wb.Custprops = Object.assign({}, wb.Custprops || {}, {
        AMS_ExportID: String(mk.exportId || ""),
        AMS_ClientToken: String(mk.clientToken || ""),
        AMS_UserID: String(mk.userId || ""),
        AMS_StudyID: String(mk.studyId || ""),
        AMS_GeneratedAt: String(mk.generatedAt || ""),
        AMS_Version: String(mk.version || 1)
      });
      var mws = XLSX.utils.aoa_to_sheet([
        ["ExportID", String(mk.exportId || "")],
        ["ClientToken", String(mk.clientToken || "")],
        ["UserID", String(mk.userId || "")],
        ["StudyID", String(mk.studyId || "")],
        ["GeneratedAt", String(mk.generatedAt || "")],
        ["Version", String(mk.version || 1)]
      ]);
      XLSX.utils.book_append_sheet(wb, mws, "_AMS_Metadata");
      wb.Workbook = wb.Workbook || {};
      wb.Workbook.Sheets = wb.Workbook.Sheets || [];
      for (var _si = 0; _si < wb.SheetNames.length; _si++) {
        if (!wb.Workbook.Sheets[_si]) wb.Workbook.Sheets[_si] = { Hidden: 0 };
      }
      wb.Workbook.Sheets[wb.SheetNames.length - 1].Hidden = 2; /* veryHidden — not in the unhide list */
      wb.Workbook.Names = (wb.Workbook.Names || []).concat([{ Name: "AMS_EXP", Ref: "'_AMS_Metadata'!$B$1" }]);
    } catch (eM) { /* marker is best-effort — never break the export */ }
  };

  var doGridExport = function() {
    if (gridExportBlocked) {
      setGridErrMsg("Grid export is not enabled for your account. Contact your administrator.");
      return;
    }
    if (!cachedRows || !cachedRows.length) {
      setGridErrMsg("Run a query first — grid export uses the data already loaded for the pivot.");
      return;
    }
    if (!cachedMapping) {
      setGridErrMsg("Cached data shape is missing. Re-run your query and try again.");
      return;
    }
    if (!isAdmin && limitReached && !gridExportBlocked) {
      setGridErrMsg("Export limit reached. Contact your administrator to increase your allowance.");
      return;
    }
    setGridErrMsg("");
    /* Report name: user-chosen, lands in the file name AND the sheet tab */
    var _rpt = _askReportName("Grid " + (activeStudyType ? activeStudyType.charAt(0).toUpperCase() + activeStudyType.slice(1) : "Export"));
    setGridExporting(true);

    /* ── Layer 1 provenance: register the export BEFORE generating the file.
       Fail-closed for non-admin users; admins proceed on registration failure
       (console-warned) so a provenance outage can't lock out internal staff. ── */
    var _provMetaG = (typeof window !== "undefined" && window.__amsxPivotMeta) ? window.__amsxPivotMeta : null;
    var _regMarkerG = null; /* structural-marker payload from the server (Suggestion 2) */
    api.registerExport({
      exportType: "grid", exportFormat: "excel",
      studyType: activeStudyType || "",
      dashboardName: _rpt.file,
      queryPayload: {
        report: _rpt.file, dims: gridDims || [], measures: gridMeasures || [],
        filters: _provMetaG ? _provMetaG.filters : null,
        periodMode: _provMetaG ? _provMetaG.periodMode : null,
        periodKeys: _provMetaG ? _provMetaG.periodKeys : null,
        topN: _provMetaG ? _provMetaG.topNOpts : null
      }
    }).then(function(_reg){ _regMarkerG = (_reg && _reg.marker) || null; return (_reg && _reg.exportId) || null; })
      .catch(function(_regErr){
        if (isAdmin) { console.warn("[grid-export] provenance registration failed — admin bypass:", _regErr && _regErr.message); return null; }
        return "__REG_BLOCKED__";
      })
      .then(function(_exportId){
        if (_exportId === "__REG_BLOCKED__") {
          setGridExporting(false);
          setGridErrMsg("Export blocked — this export could not be registered with the server. Please check with your administrator.");
          return;
        }

    /* r132: progressive (medical) pivots — batch-load every remaining level so
       the tree walk below sees every leaf (not blank trailing dims for the rows
       that were never expanded); the raw-row path (no period selection)
       re-fetches the complete flat result instead of the level-0/1 cache. */
    var _fullG = null, _lvlOkG = true;
    var _hasPerSelG = !!(data && data.periods && data.periods.length && data.periods[0] !== "ALL");
    var _prepG = (onLoadAllLevels ? onLoadAllLevels().catch(function(){ return false; }) : Promise.resolve(true))
      .then(function(_ok){ if (_ok === false) _lvlOkG = false; return (onFetchExportRows && !_hasPerSelG) ? onFetchExportRows().catch(function(){ return null; }) : null; })
      .then(function(_f){ _fullG = _f || null; });
    /* Run the actual work asynchronously so the spinner state updates before the
       (potentially heavy) row projection blocks the UI thread. */
    _prepG.then(function(){ setTimeout(function() {
      try {
        if (!_lvlOkG) setGridErrMsg("⚠️ Some levels could not be loaded before the export — deeper rows may be missing. Please try again.");
        var _lkG = _fullG || _provMetaG || {};   /* r132: server distinct-count / stock lookups for the export */
        var _mapG = _fullG ? _fullG.mapping : cachedMapping;
        /* Determine the dims, measures, and period axes.
           Dims become row keys (one row per unique combination).
           Periods (year/quarter/month) become column groups across the top —
           matching the on-screen pivot layout instead of inflating row count. */
        var dimsArr = (gridDims && gridDims.length) ? gridDims : [];
        var msrsArr = (gridMeasures && gridMeasures.length) ? gridMeasures : [];

        if (!dimsArr.length) {
          setGridExporting(false);
          setGridErrMsg("No dimensions selected — pick at least one feature in the filter panel before exporting.");
          return;
        }

        /* Map measure-key → alias + display label. Only include measures whose alias
           actually exists in cachedMapping (server-side may have skipped a measure
           if its column wasn't available). */
        var measureCols = [];
        if (msrsArr.indexOf("Unit") >= 0 && cachedMapping.units)        measureCols.push({label:"UNITS",         alias:cachedMapping.units,       type:"num"});
        if (msrsArr.indexOf("Unit") >= 0 && cachedMapping.focUnit)      measureCols.push({label:"FOC UNITS",     alias:cachedMapping.focUnit,     type:"num"});
        if (msrsArr.indexOf("UnitIU") >= 0 && cachedMapping.unitsIU)    measureCols.push({label:"UNITS IU",      alias:cachedMapping.unitsIU,     type:"num"});
        if (msrsArr.indexOf("Gross") >= 0 && cachedMapping.grossUnit)   measureCols.push({label:"GROSS SELLING",  alias:cachedMapping.grossUnit,   type:"num"});
        if (msrsArr.indexOf("Profit") >= 0 && (cachedMapping.profitUSD || cachedMapping.profitLC)) measureCols.push({label:"PROFIT " + (cachedMapping.profitUSD ? "USD" : "LC"), alias:cachedMapping.profitUSD || cachedMapping.profitLC, type:"num"});
        if (msrsArr.indexOf("LC") >= 0 && cachedMapping.valLC)          measureCols.push({label:"VALUE LC",      alias:cachedMapping.valLC,       type:"num"});
        if (msrsArr.indexOf("USD") >= 0 && cachedMapping.valUSD)        measureCols.push({label:"VALUE USD",     alias:cachedMapping.valUSD,      type:"num"});
        if (msrsArr.indexOf("Price") >= 0 && cachedMapping.priceLC)     measureCols.push({label:"PRICE LC",      alias:cachedMapping.priceLC,     type:"decimal"});
        if (msrsArr.indexOf("Price") >= 0 && cachedMapping.priceUSD)    measureCols.push({label:"PRICE USD",     alias:cachedMapping.priceUSD,    type:"decimal"});
        if (msrsArr.indexOf("Price") >= 0 && cachedMapping.netPriceLC)  measureCols.push({label:"NET PRICE LC",  alias:cachedMapping.netPriceLC,  type:"decimal"});
        if (msrsArr.indexOf("Price") >= 0 && cachedMapping.netPriceUSD) measureCols.push({label:"NET PRICE USD", alias:cachedMapping.netPriceUSD, type:"decimal"});
        if (msrsArr.indexOf("PriceIU") >= 0 && cachedMapping.priceLCIU)  measureCols.push({label:"PRICE IU LC",   alias:cachedMapping.priceLCIU,   type:"decimal"});
        if (msrsArr.indexOf("PriceIU") >= 0 && cachedMapping.priceUSDIU) measureCols.push({label:"PRICE IU USD",  alias:cachedMapping.priceUSDIU,  type:"decimal"});
        /* r132: the medical and Sales Tracker measures were missing from the grid
           (the pivot showed them, the Excel did not). Distinct counts come from
           the server lookups (keyed dim path | period) — exact per period band,
           never summed across periods; the row column is the fallback. */
        if (msrsArr.indexOf("Patients") >= 0 && _mapG.patients)           measureCols.push({label:"# PATIENTS",        alias:_mapG.patients,      type:"num", lk:"patientsLookup", node:"np"});
        if (msrsArr.indexOf("Prescriptions") >= 0 && _mapG.prescriptions) measureCols.push({label:"# PRESCRIPTIONS",   alias:_mapG.prescriptions, type:"num", lk:"rxLookup", node:"nrx"});
        if (msrsArr.indexOf("Doctors") >= 0 && _mapG.doctors)             measureCols.push({label:"# DOCTORS",         alias:_mapG.doctors,       type:"num", lk:"doctorsDistinctLookup", node:"ndr"});
        if (msrsArr.indexOf("DoctorsTotal") >= 0 && _mapG.doctorsTotal)   measureCols.push({label:"# DOCTORS (TOTAL)", alias:_mapG.doctorsTotal,  type:"num", den:true, node:"ndrt"});
        if (msrsArr.indexOf("RxPerDoctor") >= 0 && _mapG.rxPerDoctor)     measureCols.push({label:"RX PER DOCTOR",     alias:_mapG.rxPerDoctor,   type:"decimal", rxpd:true, node:"rxpd"});
        if (msrsArr.indexOf("Pharmacies") >= 0 && _mapG.pharmacies)       measureCols.push({label:"# PHARMACIES",      alias:_mapG.pharmacies,    type:"num", lk:"pharmaciesLookup", node:"nph"});
        /* Sales Tracker closing stock: one figure per row at the latest base period */
        var _stkLkG = (msrsArr.indexOf("Stock") >= 0 && _mapG.stock && _lkG.stockLookup) ? _lkG.stockLookup : null;
        var _stkBaseG = null;
        if (_stkLkG && !(data && data.multiType)) {
          var _spG = (data && data.sortedPeriods && data.sortedPeriods.length) ? data.sortedPeriods : ((data && data.periods) || []);
          if (_spG.length) { _stkBaseG = String(_spG[_spG.length - 1]); var _tG = _stkBaseG.indexOf("~"); if (_tG >= 0) _stkBaseG = _stkBaseG.slice(0, _tG); if (_stkBaseG === "__PTOTAL__" || _stkBaseG === "ALL") _stkBaseG = null; }
        }
        /* r133: Sales Tracker statistics in the grid — FOC % (FOC ÷ paid units since r178), Sell-Through %
           (sell-out ÷ sell-in of the same base period, Sell-Out bands only) and the Profit
           statistic (Sell-Out Value + Profit under Sell-Out bands). The helper figures they
           need (gross, sell-out value, profit) ride as HIDDEN measure columns: accumulated
           like any measure, never written as columns of their own. */
        var _stFlowG = !!(data && data.flowSplit);
        var _stIndsG = (stats && stats.indicators) ? stats.indicators : [];   /* (statInds is declared further down) */
        var _wantFocG = _stIndsG.indexOf("FOC %") >= 0 && !!_mapG.focUnit && !!_mapG.grossUnit && msrsArr.indexOf("Unit") >= 0;
        var _wantSthG = _stIndsG.indexOf("Sell-Through %") >= 0 && _stFlowG;
        var _wantPfG  = _stIndsG.indexOf("Profit") >= 0 && _stFlowG && !!(_mapG.profitUSD || _mapG.profitLC);
        var _hasColG = function(l){ return measureCols.some(function(mc){ return mc.label === l; }); };
        if (_wantFocG && !_hasColG("GROSS SELLING")) measureCols.push({label:"GROSS SELLING", alias:_mapG.grossUnit, type:"num", hidden:true});
        if (_wantPfG && _mapG.valUSD && msrsArr.indexOf("USD") >= 0) { measureCols.push({label:"SELL-OUT VALUE USD", alias:_mapG.sellOutValUSD || "sod", type:"num", hidden:true}); measureCols.push({label:"PROFIT USD", alias:_mapG.profitUSD || "pfd", type:"num", hidden:true}); }
        if (_wantPfG && _mapG.valLC && msrsArr.indexOf("LC") >= 0)   { measureCols.push({label:"SELL-OUT VALUE LC", alias:_mapG.sellOutValLC || "sol", type:"num", hidden:true}); measureCols.push({label:"PROFIT LC", alias:_mapG.profitLC || "pfl", type:"num", hidden:true}); }
        var _soKeyG = function(pk){ return String(pk).slice(-9) === "~Sell-Out"; };
        var _siOfG = function(pk){ var s2 = String(pk); return s2.slice(0, s2.length - 9) + "~Sell-In"; };
        /* speciality position for the # Doctors (Total) / Rx per Doctor denominators (as the pivot) */
        var _specIdxG = -1;
        for (var _sgi = 0; _sgi < dimsArr.length; _sgi++) { if (String(dimsArr[_sgi]).toLowerCase() === "docspec") { _specIdxG = _sgi; break; } }
        var _denG = function(dimVals, pk){
          var dl = _lkG.doctorsLookup; if (!dl) return null;
          var den = null;
          if (_specIdxG >= 0) { var sp = dimVals[_specIdxG]; if (sp) { den = dl["S|" + sp + "|" + pk]; if (den == null) return null; } }
          if (den == null) den = dl["T|" + pk];
          return den == null ? null : (Number(den) || 0);
        };

        if (!measureCols.length) {
          setGridExporting(false);
          setGridErrMsg("No measures selected — pick at least one measure (Unit, USD, LC, Price) before exporting.");
          return;
        }

        /* Source-row memory guard (everyone): protects the browser from
           multi-hundred-thousand-row projections. The USER'S Row Export Limit is
           applied to the EXPORTED grid rows further down — capping source rows
           with it would silently compute wrong totals from partial data. */
        var rowsSrc = (_fullG && _fullG.rows && _fullG.rows.length) ? _fullG.rows : cachedRows;   /* r132 */
        var hardCap = 200000;
        var truncated = false;
        if (rowsSrc.length > hardCap) {
          rowsSrc = rowsSrc.slice(0, hardCap);
          truncated = true;
        }
        /* Per-user cap on EXPORTED grid rows (admins bypass) */
        var outCap = (!isAdmin && rowExportLimit !== Infinity) ? rowExportLimit : Infinity;

        /* Detect which period axis is in play. Granularity follows the data shape:
           Monthly > Quarterly > Yearly. */
        var sample = rowsSrc[0] || {};
        var yrAlias = cachedMapping.yearCol || "yr";
        var qrAlias = cachedMapping.quarterCol || "qr";
        var mnAlias = cachedMapping.monthCol || "mn";
        var hasYr = (yrAlias in sample);
        var hasQr = (qrAlias in sample);
        var hasMn = (mnAlias in sample);
        var MONTH_ABBR = MONTH_S; /* ["Jan",...,"Dec"] from constants */

        /* ── PIVOT-MIRROR PERIOD MODEL ──
           When a period selection exists, the grid's columns are EXACTLY the
           pivot's displayed periods (all types, same on-screen order) and growth
           uses the pivot's per-type prev/similar maps (Q1 2026 vs Q4 2025 /
           Q1 2025, MAT vs previous MAT, ...). Rows are attributed to EVERY
           selected period they belong to (multi-type: a March row feeds its
           month, quarter, year, MAT and YTD columns), plus the hidden
           growth-base periods so growth is computable without exporting them. */
        var _pivotPeriodModel = null;
        if (data && data.periods && data.periods.length && data.periods[0] !== "ALL") {
          var _pmSel = periods.slice(); /* displayed order — same as the on-screen pivot */
          var _pmPrev = data.prevPeriodOf || {};
          var _pmYoy  = data.yoyPeriodOf  || {};
          var _pmAcc = {};
          _pmSel.forEach(function(pk){ _pmAcc[pk] = 1; });
          _pmSel.forEach(function(pk){ if (_pmPrev[pk]) _pmAcc[_pmPrev[pk]] = 1; if (_pmYoy[pk]) _pmAcc[_pmYoy[pk]] = 1; });
          var _pmTypes = {};
          _pmSel.forEach(function(pk){ var _pt2 = periodKeyType(pk); if (_pt2) _pmTypes[_pt2] = 1; });
          var _pmFlow = false;
          _pmSel.forEach(function(pk){ if (String(pk).indexOf("~") >= 0) _pmFlow = true; });
          _pivotPeriodModel = { sel: _pmSel, acc: _pmAcc, types: _pmTypes, prev: _pmPrev, yoy: _pmYoy, matLk: data.matYtdLookup || {}, flowMode: _pmFlow };
        }

        /* periodMap: periodKey → {key,label,sortVal} so we can sort chronologically.
           Pivot-mirror mode: pre-filled from the selection; sortVal = on-screen index. */
        var periodMap = {};
        if (_pivotPeriodModel) {
          _pivotPeriodModel.sel.forEach(function(pk, _pi9){
            periodMap[pk] = {key: pk, label: periodLabel9(pk), sortVal: _pi9};
          });
        }
        /* Attribution: pivot-mirror mode returns ALL matching period keys for a
           row (selected + hidden growth bases); legacy mode returns one key by
           the data's finest granularity. */
        function periodKeysOfRow(row) {
          if (!_pivotPeriodModel) { var _k1 = periodKeyOf(row); return _k1 ? [_k1] : []; }
          var out = [];
          var _y = Number(row[yrAlias]);
          if (!_y || isNaN(_y)) return out;
          var _q = hasQr ? Number(row[qrAlias]) : null; if (_q == null || isNaN(_q) || !_q) _q = null;
          var _mo = hasMn ? Number(row[mnAlias]) : null; if (_mo == null || isNaN(_mo) || !_mo) _mo = null;
          var A = _pivotPeriodModel.acc, T = _pivotPeriodModel.types, L = _pivotPeriodModel.matLk;
          /* Flow split (Sales Tracker): row's flow value re-appends onto every
             base candidate key ("2026-Q1" → "2026-Q1~Sell-In") — mirrors the
             pivot's attribution exactly. */
          var _flSuf = "";
          if (_pivotPeriodModel.flowMode) {
            var _flAlias = cachedMapping.flow;
            var _flv = _flAlias ? row[_flAlias] : null;
            if (_flv == null || String(_flv).trim() === "") return out;
            _flSuf = "~" + String(_flv).trim();
          }
          if (T["Yearly"] && A[String(_y) + _flSuf] === 1) out.push(String(_y) + _flSuf);
          if (T["Quarterly"] && _q) { var _qk3 = _y + "-Q" + _q + _flSuf; if (A[_qk3] === 1) out.push(_qk3); }
          if (T["Monthly"] && _mo) { var _mk3 = _y + "-" + (_mo < 10 ? "0" : "") + _mo + _flSuf; if (A[_mk3] === 1) out.push(_mk3); }
          /* Kimadia tender: compound "year_status" keys */
          if (T["TenderStatus"]) {
            var _tsAlias = cachedMapping.tenderStatus || cachedMapping._statusColumn;
            var _tsv = _tsAlias ? row[_tsAlias] : null;
            if (_tsv != null && _tsv !== "") { var _tk3 = _y + "_" + String(_tsv); if (A[_tk3] === 1) out.push(_tk3); }
          }
          /* MAT/YTD membership from the pivot's own lookup (quarter-space and
             month-space keys are disjoint — no double counting). Lookup holds
             BASE keys; flow mode re-appends the suffix and gates by acc. */
          var _mCat = function(baseKeys) {
            if (!_pivotPeriodModel.flowMode) return baseKeys;
            var _o9 = [];
            for (var _b9 = 0; _b9 < baseKeys.length; _b9++) {
              var _c9 = baseKeys[_b9] + _flSuf;
              if (A[_c9] === 1) _o9.push(_c9);
            }
            return _o9;
          };
          if (_q && L[_y + "-Q" + _q]) out = out.concat(_mCat(L[_y + "-Q" + _q]));
          if (_mo && L[_y + "-" + _mo]) out = out.concat(_mCat(L[_y + "-" + _mo]));
          var _s9 = {};
          return out.filter(function(_p9){ if (_s9[_p9]) return false; _s9[_p9] = 1; return true; });
        }
        function periodKeyOf(row) {
          var y = Number(row[yrAlias]);
          if (hasMn) {
            var mo = Number(row[mnAlias]);
            if (!mo || isNaN(mo)) return null;
            var mk = y + "-" + (mo < 10 ? "0" : "") + mo;
            if (!periodMap[mk]) periodMap[mk] = {key:mk, label: MONTH_ABBR[mo-1] + " " + y, sortVal: y*100 + mo};
            return mk;
          }
          if (hasQr) {
            var q = Number(row[qrAlias]);
            if (!q || isNaN(q)) return null;
            var qk = y + "-Q" + q;
            if (!periodMap[qk]) periodMap[qk] = {key:qk, label: "Q" + q + " " + y, sortVal: y*10 + q};
            return qk;
          }
          if (hasYr && y) {
            var yk = String(y);
            if (!periodMap[yk]) periodMap[yk] = {key:yk, label: String(y), sortVal: y};
            return yk;
          }
          return "_all";
        }

        /* Resolve each dim's row-alias via cachedMapping. The server returns rows with
           alias-keyed columns (mapping.manufacturer = "mfr", mapping.medicine = "md", etc.),
           NOT with logical dim names. Build a parallel array so the inner loop is fast. */
        var dimAliases = dimsArr.map(function(d){ return _mapG[d] || cachedMapping[d] || d; });

        /* Group rows by dim-combo, accumulating measure values per period. */
        var groups = {};
        var dimKeySep = "\u0001";
        var _screenOrder = {};
        var _screenIdx = 0;
        var _gridDepth = dimsArr.length;
        var _treeLeafMode = false;
        /* ── PREFERRED: take the values straight from the PIVOT's aggregated tree ──
           When a period selection exists, every grid row is a full-depth leaf of
           the on-screen pivot and its per-period values (including the hidden
           growth-base periods) are copied verbatim from the pivot's own data —
           EXACT screen numbers by construction, no re-aggregation, and immune to
           the raw-row source cap (multi-period queries can fetch 3+ full years
           of monthly rows, far beyond the 200k projection guard that previously
           caused blank / undercounted Excel cells). Screen order is captured in
           the same walk. */
        if (_pivotPeriodModel && displayNodes && displayNodes.length) {
          try {
            var _msrField = {"UNITS":"u","FOC UNITS":"fu","GROSS SELLING":"gu","VALUE LC":"l","VALUE USD":"d","PRICE LC":"pl","PRICE USD":"pd","NET PRICE LC":"npl","NET PRICE USD":"npd",
              "UNITS IU":"iu","PRICE IU LC":"pli","PRICE IU USD":"pdi","# PATIENTS":"np","# PRESCRIPTIONS":"nrx","# DOCTORS":"ndr","# DOCTORS (TOTAL)":"ndrt","RX PER DOCTOR":"rxpd","# PHARMACIES":"nph","SELL-OUT VALUE USD":"sv","SELL-OUT VALUE LC":"svl","PROFIT USD":"pf","PROFIT LC":"pfl"};   /* r132: complete; r133: profit stat fields */
            (function _leafWalk(nodes, parentPath, pathVals) {
              var sortedN = sortNodes(nodes);
              for (var wi = 0; wi < sortedN.length; wi++) {
                var wn = sortedN[wi];
                var wPathKey = parentPath ? parentPath + "||" + wn.name : wn.name;
                var wVals = pathVals.concat([String(wn.name)]);
                var wKids = (_lzc[wPathKey] && _lzc[wPathKey].length > 0) ? _lzc[wPathKey] : (wn.children || []);
                if (wVals.length >= _gridDepth || !wKids.length) {
                  var _lv = wVals.slice(0, _gridDepth);
                  while (_lv.length < _gridDepth) _lv.push("");
                  /* buildPivot normalizes null dim values to "?" — grid uses "" */
                  var _dims2 = _lv.map(function(v){ return v === "?" ? "" : v; });
                  var _dkS = _dims2.join(dimKeySep);
                  if (!groups[_dkS]) {
                    var _byP = {};
                    var _ndAll = wn.data || {};
                    Object.keys(_ndAll).forEach(function(_npk) {
                      var _nd = _ndAll[_npk];
                      if (!_nd) return;
                      var _bk = {};
                      for (var _mi2 = 0; _mi2 < measureCols.length; _mi2++) {
                        var _fld = _msrField[measureCols[_mi2].label];
                        if (_fld && _nd[_fld] != null) _bk[measureCols[_mi2].label] = Number(_nd[_fld]) || 0;
                      }
                      _byP[_npk] = _bk;
                    });
                    groups[_dkS] = {dims: _dims2, byPeriod: _byP};
                    if (_stkLkG && _stkBaseG) { var _skv = (wn._lkPath != null) ? _stkLkG[wn._lkPath + "|" + _stkBaseG] : _stkLkG[_dims2.join("|") + "|" + _stkBaseG]; if (_skv != null) groups[_dkS].stock = Number(_skv) || 0; }
                    _screenOrder[_dkS] = _screenIdx++;
                  }
                }
                if (wVals.length < _gridDepth && wKids.length) _leafWalk(wKids, wPathKey, wVals);
              }
            })(displayNodes, "", []);
            _treeLeafMode = _screenIdx > 0;
          } catch (_tlErr) {
            console.error("[grid-export] tree-leaf projection failed, falling back to raw rows:", _tlErr);
            groups = {}; _screenOrder = {}; _screenIdx = 0; _treeLeafMode = false;
          }
        }
        if (_treeLeafMode) truncated = false; /* pivot tree values are complete — source cap irrelevant */
        if (!_treeLeafMode) for (var ri = 0; ri < rowsSrc.length; ri++) {
          var src = rowsSrc[ri];
          var dimVals = new Array(dimsArr.length);
          for (var di = 0; di < dimsArr.length; di++) {
            var dv = src[dimAliases[di]];
            dimVals[di] = (dv == null || dv === "") ? "" : String(dv);
          }
          var dimKey = dimVals.join(dimKeySep);
          var pKeysR = periodKeysOfRow(src);
          if (!pKeysR.length) continue;
          if (!groups[dimKey]) groups[dimKey] = {dims: dimVals, byPeriod: {}};
          for (var pki2 = 0; pki2 < pKeysR.length; pki2++) {
            var pKey = pKeysR[pki2];
            var bucket = groups[dimKey].byPeriod[pKey];
            if (!bucket) { bucket = {}; groups[dimKey].byPeriod[pKey] = bucket; }
            for (var mi = 0; mi < measureCols.length; mi++) {
              var mc = measureCols[mi];
              /* r132: distinct counts / doctor denominators from the server lookups (exact, never summed) */
              if (mc.lk && _lkG[mc.lk]) { var _lkv = _lkG[mc.lk][dimVals.join("|") + "|" + pKey]; if (_lkv != null) { bucket[mc.label] = Number(_lkv) || 0; continue; } }
              if (mc.den) { var _dn = _denG(dimVals, pKey); if (_dn != null) bucket[mc.label] = _dn; continue; }
              if (mc.rxpd) { var _dn2 = _denG(dimVals, pKey), _nrx2 = (_lkG.rxLookup && _lkG.rxLookup[dimVals.join("|") + "|" + pKey] != null) ? Number(_lkG.rxLookup[dimVals.join("|") + "|" + pKey]) : Number(src[_mapG.prescriptions || "nrx"]); if (_dn2 > 0 && !isNaN(_nrx2)) bucket[mc.label] = _nrx2 / _dn2; continue; }
              var rawV = src[mc.alias];
              if (rawV == null || rawV === "") continue;
              var nv = Number(rawV);
              if (isNaN(nv)) continue;
              /* Price/decimal: take the latest value (don't sum prices). Volume: sum. */
              if (mc.type === "decimal") bucket[mc.label] = nv;
              else                       bucket[mc.label] = (bucket[mc.label] || 0) + nv;
            }
          }
          if (_stkLkG && _stkBaseG && groups[dimKey].stock == null) { var _skr = _stkLkG[dimVals.join("|") + "|" + _stkBaseG]; if (_skr != null) groups[dimKey].stock = Number(_skr) || 0; }
        }

        /* ── Σ Period-Total band (mirrors the pivot) ──
           When the on-screen pivot carries the Σ Total column (data.periods
           includes "__PTOTAL__"), make sure every grid group has the summed
           bucket. Tree-leaf mode copied it from the pivot already; the raw-row
           fallback synthesizes it here by summing the selected point periods
           (and their year-earlier counterparts for growth). Volume measures are
           summed; price/decimal measures take the mean of the present values. */
        if (periodMap["__PTOTAL__"] && _pivotPeriodModel) {
          var _ptSelK = _pivotPeriodModel.sel.filter(function(pk){ return pk !== "__PTOTAL__"; });
          var _ptYoyK = _ptSelK.map(function(pk){ return _pivotPeriodModel.yoy[pk]; }).filter(Boolean);
          var _sumBand = function(bp, keys, dimsG, bandKey){
            var out = {}, cnt = {};
            keys.forEach(function(pk){
              var b = bp[pk]; if (!b) return;
              measureCols.forEach(function(m){
                if (m.lk || m.den || m.rxpd) return;   /* r132: distinct counts never sum across periods */
                var v = b[m.label]; if (typeof v !== "number") return;
                if (m.type === "decimal") { out[m.label] = (out[m.label] || 0) + v; cnt[m.label] = (cnt[m.label] || 0) + 1; }
                else out[m.label] = (out[m.label] || 0) + v;
              });
            });
            measureCols.forEach(function(m){ if (m.type === "decimal" && cnt[m.label]) out[m.label] = out[m.label] / cnt[m.label]; });
            /* r132: the band's own distinct count from the server lookup (dim path | band key) */
            if (dimsG && bandKey) measureCols.forEach(function(m){
              var k9 = dimsG.join("|") + "|" + bandKey;
              if (m.lk && _lkG[m.lk] && _lkG[m.lk][k9] != null) out[m.label] = Number(_lkG[m.lk][k9]) || 0;
              else if (m.den) { var d9 = _denG(dimsG, bandKey); if (d9 != null) out[m.label] = d9; }
              else if (m.rxpd) { var d8 = _denG(dimsG, bandKey), n8 = (_lkG.rxLookup && _lkG.rxLookup[k9] != null) ? Number(_lkG.rxLookup[k9]) : NaN; if (d8 > 0 && !isNaN(n8)) out[m.label] = n8 / d8; }
            });
            return out;
          };
          for (var _gk in groups) {
            var _bp = groups[_gk].byPeriod;
            if (!_bp["__PTOTAL__"]) _bp["__PTOTAL__"] = _sumBand(_bp, _ptSelK, groups[_gk].dims, "__PTOTAL__");
            if (!_bp["__PTOTALY__"] && _ptYoyK.length) _bp["__PTOTALY__"] = _sumBand(_bp, _ptYoyK, groups[_gk].dims, "__PTOTALY__");
          }
        }

        /* Sort period keys chronologically and dim groups alphabetically by joined dim string. */
        var periodKeys = Object.keys(periodMap).sort(function(a,b){
          return periodMap[a].sortVal - periodMap[b].sortVal;
        });
        /* The server fetch now includes each selected period's REAL previous
           period (growth support). Those are fetch-only: keep the export columns
           limited to the user's selection. Only applies when every selected key
           matches the export granularity (skip for MAT/YTD/mixed selections). */
        var _selPerRaw = (originalPeriods && originalPeriods.length && originalPeriods[0] !== "ALL") ? originalPeriods : null;
        if (_selPerRaw && !_pivotPeriodModel) {
          var _selGranOk = _selPerRaw.every(function(pk){
            return hasMn ? /^\d{4}-\d{1,2}$/.test(pk) : hasQr ? /^\d{4}-Q[1-4]$/.test(pk) : /^\d{4}$/.test(pk);
          });
          if (_selGranOk) {
            var _selSet = {};
            _selPerRaw.forEach(function(pk){
              var _mm3 = /^(\d{4})-(\d{1,2})$/.exec(pk);
              if (_mm3 && hasMn) { var _pm3 = parseInt(_mm3[2]); _selSet[_mm3[1] + "-" + (_pm3 < 10 ? "0" : "") + _pm3] = 1; }
              else _selSet[pk] = 1;
            });
            periodKeys = periodKeys.filter(function(pk){ return _selSet[pk] === 1; });
          }
        }
        /* ── Sort rows AS ON SCREEN ──
           Use the pivot's active sort: same measure, same period, same direction.
           Falls back to the first measure's all-period total (descending) when the
           screen sort isn't representable in the grid's flat shape. Ties break
           alphabetically so the order is stable. */
        var dimKeys = Object.keys(groups);
        var _sortMsrLabel = null, _sortPk = null, _sortDir = -1;
        var _mLbl = {d:"VALUE USD", l:"VALUE LC", u:"UNITS", fu:"FOC UNITS", gu:"GROSS SELLING", pd:"PRICE USD", pl:"PRICE LC", npd:"NET PRICE USD", npl:"NET PRICE LC", iu:"UNITS IU", pdi:"PRICE IU USD", pli:"PRICE IU LC"};
        if (sortCfg && sortCfg.m && _mLbl[sortCfg.m]) {
          var _cand = _mLbl[sortCfg.m];
          if (measureCols.some(function(mc){ return mc.label === _cand; })) _sortMsrLabel = _cand;
          if (sortCfg.pk && periodMap[sortCfg.pk]) _sortPk = sortCfg.pk;
          _sortDir = sortCfg.dir === "asc" ? 1 : -1;
        }
        if (!_sortMsrLabel && measureCols.length) _sortMsrLabel = measureCols[0].label;
        var _rowSortVal = function(dk) {
          var bp = groups[dk].byPeriod;
          if (_sortPk) { var b = bp[_sortPk]; return (b && typeof b[_sortMsrLabel] === "number") ? b[_sortMsrLabel] : 0; }
          var t = 0;
          for (var pk3 in bp) { var v3 = bp[pk3][_sortMsrLabel]; if (typeof v3 === "number") t += v3; }
          return t;
        };
        /* ── Exact ON-SCREEN row order ──
           Walk the pivot's sorted tree with the SAME traversal the pivot export
           uses (sortNodes per level, lazy children merged) and record each
           full-depth leaf path's position. Grid rows then follow that order
           exactly — same sort measure/period/direction, same Top-N "Others"
           placement, children grouped under their parents as displayed.
           Rows not found in the tree (edge cases: shallow lazy tree, null-value
           normalization) fall back to the value sort after the ordered block. */
        /* Tree-leaf mode captured screen order during the leaf walk above;
           the fallback (raw-row) path walks the tree here for ordering only. */
        if (!_treeLeafMode) try {
          (function _walkScreen(nodes, parentPath, pathVals) {
            var sortedN = sortNodes(nodes);
            for (var wi = 0; wi < sortedN.length; wi++) {
              var wn = sortedN[wi];
              var wPathKey = parentPath ? parentPath + "||" + wn.name : wn.name;
              var wVals = pathVals.concat([String(wn.name)]);
              var wKids = (_lzc[wPathKey] && _lzc[wPathKey].length > 0) ? _lzc[wPathKey] : (wn.children || []);
              if (wVals.length >= _gridDepth || !wKids.length) {
                var _lv = wVals.slice(0, _gridDepth);
                while (_lv.length < _gridDepth) _lv.push("");
                /* buildPivot normalizes null dim values to "?" — grid uses "" */
                var _dkS = _lv.map(function(v){ return v === "?" ? "" : v; }).join(dimKeySep);
                if (_screenOrder[_dkS] == null) _screenOrder[_dkS] = _screenIdx++;
              }
              if (wVals.length < _gridDepth && wKids.length) _walkScreen(wKids, wPathKey, wVals);
            }
          })(displayNodes, "", []);
        } catch (_soErr) { _screenOrder = {}; _screenIdx = 0; }
        dimKeys.sort(function(a, b) {
          var _ia = _screenOrder[a], _ib = _screenOrder[b];
          var _ha = _ia != null, _hb = _ib != null;
          if (_ha && _hb) return _ia - _ib;   /* both on screen → screen order */
          if (_ha) return -1;                 /* on-screen rows first */
          if (_hb) return 1;
          var dd = (_rowSortVal(a) - _rowSortVal(b)) * _sortDir;
          if (dd !== 0) return dd;
          return a < b ? -1 : a > b ? 1 : 0;
        });

        /* Statistics setup — read from `stats` prop (mirrors what the pivot displays).
              Market Share: value / total-for-period-measure × 100
              Rank        : per (period, measure), 1 = highest
              Growth      : period-over-period % change (mode prev / yoy / both)
              CAGR        : compound annual growth rate from first → last period of each measure
           If a stat needs cross-row context, we precompute it here so the row loop is fast. */
        var statInds   = (stats && stats.indicators) ? stats.indicators : [];
        var growthMode = (stats && stats.growthMode) ? stats.growthMode : "prev";
        var wantShare  = statInds.indexOf("Market Share") >= 0;
        var wantRank   = statInds.indexOf("Rank")         >= 0;
        var wantGrowth = statInds.indexOf("Growth")       >= 0;
        var wantCAGR   = statInds.indexOf("CAGR")         >= 0 && !(data && data.multiType); /* CAGR needs a homogeneous period series */
        var wantEvo    = statInds.indexOf("Evolution Index") >= 0;
        /* EI has its own comparison mode (falls back to the Growth mode for
           queries saved before evoMode existed — the old behavior). */
        var evoMode    = (stats && stats.evoMode) ? stats.evoMode : (growthMode === "yoy" ? "yoy" : "prev");
        var wantEvoPrev = wantEvo && (evoMode === "prev" || evoMode === "both");
        var wantEvoYoy  = wantEvo && (evoMode === "yoy"  || evoMode === "both");
        var wantPrev   = wantGrowth && (growthMode === "prev" || growthMode === "both");
        var wantYoY    = wantGrowth && (growthMode === "yoy"  || growthMode === "both");

        /* Granularity-aware YoY lookup: for each periodKey, find the periodKey whose
           sortVal corresponds to the same slot one year earlier.
             monthly  : sortVal = year*100 + month  → subtract 100
             quarterly: sortVal = year*10  + q      → subtract 10
             yearly   : sortVal = year              → subtract 1                       */
        var yoyOffset = hasMn ? 100 : (hasQr ? 10 : 1);
        var yoyOf = {}; /* periodKey → priorYearPeriodKey or null */
        if (wantYoY || wantEvoYoy) {
          if (_pivotPeriodModel) {
            /* Pivot-mirror: per-type similar period from the pivot's own map
               (2026-Q1 → 2025-Q1, MAT-Q1-2026 → MAT-Q1-2025, ...). The base may
               be a hidden accumulated period — its bucket exists in groups. */
            periodKeys.forEach(function(pk){ yoyOf[pk] = _pivotPeriodModel.yoy[pk] || null; });
          } else {
            var sortValIndex = {};
            periodKeys.forEach(function(pk){ sortValIndex[periodMap[pk].sortVal] = pk; });
            periodKeys.forEach(function(pk){
              var prior = sortValIndex[periodMap[pk].sortVal - yoyOffset];
              yoyOf[pk] = prior || null;
            });
          }
        }

        /* Per-period totals for Market Share */
        var periodTotals = {}; /* periodKey → measureLabel → sum */
        if (wantShare || wantEvo) {
          periodKeys.forEach(function(pk){
            periodTotals[pk] = {};
            measureCols.forEach(function(m){
              var sum = 0;
              for (var dki = 0; dki < dimKeys.length; dki++) {
                var v = (groups[dimKeys[dki]].byPeriod[pk] || {})[m.label];
                if (typeof v === "number") sum += v;
              }
              periodTotals[pk][m.label] = sum;
            });
          });
        }

        /* Ranks per (period, measure) — 1 = highest */
        var rankIdx = {}; /* periodKey → measureLabel → dimKey → rank */
        if (wantRank) {
          periodKeys.forEach(function(pk){
            rankIdx[pk] = {};
            measureCols.forEach(function(m){
              var sorted = dimKeys.slice().sort(function(a,b){
                var va = (groups[a].byPeriod[pk] || {})[m.label]; va = (typeof va === "number") ? va : -Infinity;
                var vb = (groups[b].byPeriod[pk] || {})[m.label]; vb = (typeof vb === "number") ? vb : -Infinity;
                return vb - va;
              });
              var byDim = {};
              for (var si = 0; si < sorted.length; si++) byDim[sorted[si]] = si + 1;
              rankIdx[pk][m.label] = byDim;
            });
          });
        }

        /* CAGR span: years between first and last period (for the % exponent).
           Pivot-mirror columns follow the ON-SCREEN order (often newest first),
           so CAGR endpoints come from the CHRONOLOGICAL series, never the
           column order. */
        var _chronoKeys = (_pivotPeriodModel && data.sortedPeriods && data.sortedPeriods.length)
          ? data.sortedPeriods.slice() : periodKeys.slice();
        var cagrFirstK = _chronoKeys.length ? _chronoKeys[0] : null;
        var cagrLastK  = _chronoKeys.length ? _chronoKeys[_chronoKeys.length - 1] : null;
        var cagrYears = 0;
        if (wantCAGR && periodKeys.length >= 2) {
          if (_pivotPeriodModel) {
            var _yrOfK = function(pk){ var _m4 = /(\d{4})/.exec(String(pk)); return _m4 ? parseInt(_m4[1]) : 0; };
            cagrYears = _yrOfK(cagrLastK) - _yrOfK(cagrFirstK);
            if (cagrYears <= 0) {
              var _ft = periodKeyType(cagrFirstK);
              var _slots = (_ft === "Monthly" || _ft === "MAT Monthly") ? 12 : (_ft === "Quarterly" || _ft === "MAT") ? 4 : 1;
              cagrYears = (periodKeys.length - 1) / _slots;
            }
          } else {
            var firstP = periodMap[periodKeys[0]];
            var lastP  = periodMap[periodKeys[periodKeys.length - 1]];
            var firstYrApprox = Math.floor(firstP.sortVal / yoyOffset);
            var lastYrApprox  = Math.floor(lastP.sortVal  / yoyOffset);
            cagrYears = lastYrApprox - firstYrApprox;
            /* If sub-year resolution and same year → fractional based on slot count */
            if (cagrYears <= 0) cagrYears = (periodKeys.length - 1) / (yoyOffset === 100 ? 12 : (yoyOffset === 10 ? 4 : 1));
          }
        }

        /* ── Per-statistic measure scope — SAME rules as the on-screen pivot ──
           A statistic column appears for a measure only when the user scoped it
           to that measure (stats.statMeasures), exactly like statAppliesTo in
           the pivot. Price / FOC measures never carry statistics (noStats on
           screen). Legacy saved queries without scoping keep the old
           all-measures behavior. */
        var _noStatsLbl = {"FOC UNITS":1, "PRICE LC":1, "PRICE USD":1, "NET PRICE LC":1, "NET PRICE USD":1};
        var _userMsrKeyOf = function(lbl){
          if (lbl === "UNITS" || lbl === "FOC UNITS") return "Unit";
          if (lbl === "VALUE LC") return "LC";
          if (lbl === "VALUE USD") return "USD";
          return "Price";
        };
        var _statMeasures = (stats && stats.statMeasures) ? stats.statMeasures : {};
        var _legacyScope = !stats || !stats.statMeasures || Object.keys(_statMeasures).length === 0;
        var _statOn = function(statName, mLabel){
          if (_noStatsLbl[mLabel]) return false;      /* prices & FOC: no stats, ever */
          if (_legacyScope) return true;              /* legacy query: all measures */
          var _scope = _statMeasures[statName];
          if (_scope === undefined) return true;      /* stat enabled pre-scoping: all */
          if (!_scope.length) return false;           /* user cleared all measures */
          return _scope.indexOf(_userMsrKeyOf(mLabel)) >= 0;
        };

        /* Build column descriptors: N dim cols, then for each period a block containing
           [value, share, rank, growth_prev, growth_yoy] for each measure (only enabled stats).
           CAGR is appended once per measure at the end (it's a span-level statistic). */
        var cols = [];
        dimsArr.forEach(function(d){
          cols.push({label:(FEAT_LBL[d]||d).toUpperCase(), type:"text"});
        });
        periodKeys.forEach(function(pk, pIdx){
          var pLabel = periodMap[pk].label;
          measureCols.forEach(function(m){
            if (m.hidden) return;   /* r133: helper figure, not a column */
            cols.push({label: m.label + " (" + pLabel + ")", type: m.type, kind:"value", periodKey:pk, measureLabel:m.label});
            if (wantShare && _statOn("Market Share", m.label)) {
              cols.push({label: "SHARE % " + m.label + " (" + pLabel + ")", type:"decimal", kind:"share", periodKey:pk, measureLabel:m.label});
            }
            if (wantRank && _statOn("Rank", m.label)) {
              cols.push({label: "RANK " + m.label + " (" + pLabel + ")", type:"int", kind:"rank", periodKey:pk, measureLabel:m.label});
            }
            /* Growth base: pivot-mirror uses the REAL same-type previous period
               (2026-Q1 → 2025-Q4, MAT → previous MAT, ...) whose bucket may be a
               hidden accumulated period; legacy keeps previous-column behavior. */
            var _gPrevK = _pivotPeriodModel ? (_pivotPeriodModel.prev[pk] || null) : (pIdx > 0 ? periodKeys[pIdx-1] : null);
            if (wantPrev && _gPrevK && _statOn("Growth", m.label)) {
              cols.push({label: "GROWTH % " + m.label + " (" + pLabel + ")", type:"decimal", kind:"growth_prev", periodKey:pk, prevPeriodKey:_gPrevK, measureLabel:m.label});
            }
            if (wantYoY && yoyOf[pk] && _statOn("Growth", m.label)) {
              cols.push({label: "SIMILAR % " + m.label + " (" + pLabel + ")", type:"decimal", kind:"growth_yoy", periodKey:pk, yoyPeriodKey:yoyOf[pk], measureLabel:m.label});
            }
            /* Evolution Index: one column per enabled base (prev and/or similar) */
            if (wantEvoPrev && _gPrevK && _statOn("Evolution Index", m.label)) {
              cols.push({label: "EI " + m.label + " (" + pLabel + ")", type:"decimal", kind:"evo", periodKey:pk, evoBaseKey:_gPrevK, measureLabel:m.label});
            }
            if (wantEvoYoy && yoyOf[pk] && _statOn("Evolution Index", m.label)) {
              cols.push({label: "EI SIMILAR " + m.label + " (" + pLabel + ")", type:"decimal", kind:"evo", evoSim:1, periodKey:pk, evoBaseKey:yoyOf[pk], measureLabel:m.label});
            }
            /* r133: FOC % right after FOC UNITS, as on screen */
            if (_wantFocG && m.label === "FOC UNITS") cols.push({label: "FOC % (" + pLabel + ")", type:"decimal", kind:"focpct", periodKey:pk, measureLabel:"FOC %"});
            /* r133: Sell-Out bands — Profit statistic (Sell-Out Value + Profit) and Sell-Through % per measure */
            if (_soKeyG(pk)) {
              if (_wantPfG && m.label === "VALUE USD" && _hasColG("PROFIT USD")) { cols.push({label: "SELL-OUT VALUE USD (" + pLabel + ")", type:"num", kind:"value", periodKey:pk, measureLabel:"SELL-OUT VALUE USD"}); cols.push({label: "PROFIT USD (" + pLabel + ")", type:"num", kind:"value", periodKey:pk, measureLabel:"PROFIT USD"}); }
              if (_wantPfG && m.label === "VALUE LC" && _hasColG("PROFIT LC"))   { cols.push({label: "SELL-OUT VALUE LC (" + pLabel + ")", type:"num", kind:"value", periodKey:pk, measureLabel:"SELL-OUT VALUE LC"}); cols.push({label: "PROFIT LC (" + pLabel + ")", type:"num", kind:"value", periodKey:pk, measureLabel:"PROFIT LC"}); }
              if (_wantSthG && (m.label === "UNITS" || m.label === "GROSS SELLING" || m.label === "VALUE LC" || m.label === "VALUE USD")) cols.push({label: "SELL-THROUGH % " + m.label + " (" + pLabel + ")", type:"decimal", kind:"sth", periodKey:pk, siKey:_siOfG(pk), measureLabel:m.label});
            }
          });
        });
        /* EI base periods may be HIDDEN accumulated buckets (pivot-mirror prev/yoy)
           that aren't in periodKeys — their market totals still must come from the
           FULL row set (this runs before the row-export-limit truncation). */
        if (wantEvo) {
          cols.forEach(function(c){
            if (c.kind !== "evo" || !c.evoBaseKey || periodTotals[c.evoBaseKey]) return;
            periodTotals[c.evoBaseKey] = {};
            measureCols.forEach(function(m){
              var sum = 0;
              for (var dki = 0; dki < dimKeys.length; dki++) {
                var v = (groups[dimKeys[dki]].byPeriod[c.evoBaseKey] || {})[m.label];
                if (typeof v === "number") sum += v;
              }
              periodTotals[c.evoBaseKey][m.label] = sum;
            });
          });
        }
        if (wantCAGR && cagrYears > 0 && periodKeys.length >= 2) {
          measureCols.forEach(function(m){
            if (m.hidden) return;
            if (!_statOn("CAGR", m.label)) return;
            cols.push({label: "CAGR % " + m.label, type:"decimal", kind:"cagr", measureLabel:m.label});
          });
        }
        /* Fallback: no period dimension at all → emit measures as plain columns. */
        if (!periodKeys.length) {
          measureCols.forEach(function(m){
            if (m.hidden) return;
            cols.push({label: m.label, type: m.type, kind:"value", periodKey:"_all", measureLabel:m.label});
            if (_wantFocG && m.label === "FOC UNITS") cols.push({label: "FOC %", type:"decimal", kind:"focpct", periodKey:"_all", measureLabel:"FOC %"});
          });
        }
        /* r132: Sales Tracker closing stock at the latest base period (as the pivot's CURRENT STOCK column) */
        if (_stkLkG && _stkBaseG) cols.push({label: "CURRENT STOCK (" + periodLabel9(_stkBaseG) + ")", type:"num", kind:"stock", measureLabel:"CURRENT STOCK", periodKey:_stkBaseG});

        /* ── Row Export Limit applies to the EXPORTED grid rows ──
           Keep the top rows by the first measure's total value. Share and Rank
           statistics were computed on the FULL set above, so exported shares and
           ranks remain true market figures even in a truncated file. */
        var outTruncated = false;
        if (dimKeys.length > outCap) {
          if (_screenIdx > 0) {
            /* Screen-ordered export: keep the FIRST rows exactly as displayed */
            dimKeys = dimKeys.slice(0, outCap);
          } else {
            var _m0 = measureCols[0].label;
            var _rowTot = {};
            dimKeys.forEach(function(dk2){ var t = 0; var bp = groups[dk2].byPeriod; for (var pk2 in bp) { var v2 = bp[pk2][_m0]; if (typeof v2 === "number") t += v2; } _rowTot[dk2] = t; });
            var _keep = dimKeys.slice().sort(function(a, b){ return _rowTot[b] - _rowTot[a]; }).slice(0, outCap);
            var _keepSet = {}; _keep.forEach(function(k2){ _keepSet[k2] = 1; });
            dimKeys = dimKeys.filter(function(k2){ return _keepSet[k2] === 1; });
          }
          outTruncated = true;
        }

        getXLSX().then(function(XLSX) {
          var bdr = {top:{style:"thin",color:{rgb:"B8C4CE"}},bottom:{style:"thin",color:{rgb:"B8C4CE"}},left:{style:"thin",color:{rgb:"B8C4CE"}},right:{style:"thin",color:{rgb:"B8C4CE"}}};
          var sHdr     = {fill:{fgColor:{rgb:"1E3A5F"}},font:{bold:true,color:{rgb:"FFFFFF"},sz:11},alignment:{horizontal:"center",vertical:"center"},border:bdr};
          var sCell    = {font:{sz:10,color:{rgb:"1E293B"}},alignment:{horizontal:"left",vertical:"center"},border:bdr};
          var sNum     = {font:{sz:10,color:{rgb:"1E293B"}},alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0"};
          var sNumDec  = {font:{sz:10,color:{rgb:"1E293B"}},alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0.00"};

          /* Build aoa: (optional NOTICE row) + header + one row per dim-combo.
             The truncation notice sits AT THE TOP of the Grid sheet — first thing
             the user sees — instead of a separate sheet nobody opens. */
          var _noticeTxt = null;
          if (truncated || outTruncated) {
            var _np = [];
            if (outTruncated) _np.push("Row export limit: showing the top " + outCap.toLocaleString() + " rows by " + measureCols[0].label + " (of " + Object.keys(groups).length.toLocaleString() + " total). Share % and Rank are computed on the full result set — refine filters or ask your administrator to raise the limit.");
            if (truncated) _np.push("Source data was capped at " + hardCap.toLocaleString() + " rows for processing.");
            _noticeTxt = "⚠ " + _np.join(" ");
          }
          var aoa = [];
          /* AMS logo band: blank rows at the top; the logo image is anchored over
             them at the zip level (see _amsBrandXlsxBytes). A1 gets an empty
             string so the sheet range still starts at A1. */
          var _amsLogoRowsG = 3;
          aoa.push([""]);
          for (var lgI = 1; lgI < _amsLogoRowsG; lgI++) aoa.push([]);
          if (_noticeTxt) aoa.push([_noticeTxt]);
          var _hdrRow = aoa.length; /* first header row (below logo band + optional notice) */
          var _hdrRow2 = _hdrRow + 1; /* second header row (sub-labels) */
          /* ── Two-row header, exactly like the PIVOT export ──
             Row 1: dim labels (merged vertically) + one merged cell per period
                    spanning its whole column block ("MAT Jun 2026", "Q1 2026", ...)
             Row 2: sub-labels per column (USD / Share% / Rank / ↕ Prev / ↕ Similar) */
          var _hdrMerges = [];
          var _h1 = [], _h2 = [];
          cols.forEach(function(c, ci){
            if (ci < dimsArr.length) {
              _h1.push(c.label);
              _h2.push("");
              _hdrMerges.push({s:{r:_hdrRow, c:ci}, e:{r:_hdrRow2, c:ci}}); /* vertical merge */
              return;
            }
            if (c.kind === "cagr") {
              _h1.push("CAGR " + (c.measureLabel || ""));
              _h2.push("CAGR");
              return;
            }
            if (c.kind === "stock") { _h1.push("CURRENT STOCK"); _h2.push(periodLabel9(c.periodKey)); return; }
            if (c.kind === "focpct") { _h1.push((c.periodKey !== "_all" && periodMap[c.periodKey]) ? periodMap[c.periodKey].label : "FOC %"); _h2.push("FOC %"); return; }
            if (c.kind === "sth") { _h1.push(periodMap[c.periodKey] ? periodMap[c.periodKey].label : ""); _h2.push("Sell-Through % (" + c.measureLabel + ")"); return; }
            var _pLbl = (c.periodKey && c.periodKey !== "_all" && periodMap[c.periodKey]) ? periodMap[c.periodKey].label : (c.measureLabel || c.label);
            _h1.push(_pLbl);
            _h2.push(c.kind === "share" ? "Share%"
                   : c.kind === "rank" ? "Rank"
                   : c.kind === "growth_prev" ? "↕ Prev"
                   : c.kind === "growth_yoy" ? "↕ Similar"
                   : c.kind === "evo" ? (c.evoSim ? "EI Similar" : "EI")
                   : (c.measureLabel || c.label));
          });
          /* NO horizontal merges on the period row (format request 2026-08-27):
             the period label REPEATS in every column of its block — row 1
             "Q2 2026 | Q2 2026 | Q2 2026", row 2 "UNITS | VALUE USD | PRICE USD"
             — so each column is self-describing and survives sorting, filtering,
             copy-paste and pivot-table use in Excel. _h1 already carries the
             period label per column; only the dim columns keep their vertical
             merge. */
          aoa.push(_h1);
          aoa.push(_h2);
          for (var gi = 0; gi < dimKeys.length; gi++) {
            var dk = dimKeys[gi];
            var grp = groups[dk];
            var arr = grp.dims.slice();
            for (var ci = dimsArr.length; ci < cols.length; ci++) {
              var col = cols[ci];
              var cv = "";
              switch (col.kind) {
                case "value": {
                  var vbk = grp.byPeriod[col.periodKey];
                  var vv = vbk ? vbk[col.measureLabel] : null;
                  cv = (vv != null) ? vv : "";
                  break;
                }
                case "stock": { cv = (grp.stock != null) ? grp.stock : ""; break; }
                case "focpct": {   /* r133: FOC ÷ Gross × 100 of this period's bucket */
                  var fbk = grp.byPeriod[col.periodKey] || {};
                  cv = (typeof fbk["GROSS SELLING"] === "number" && (fbk["GROSS SELLING"] - (fbk["FOC UNITS"] || 0)) > 0) ? ((fbk["FOC UNITS"] || 0) / (fbk["GROSS SELLING"] - (fbk["FOC UNITS"] || 0)) * 100) : "";   /* r178: ÷ paid units */
                  break;
                }
                case "sth": {   /* r133: sell-out ÷ sell-in of the same base period */
                  var obk = grp.byPeriod[col.periodKey] || {}, ibk = grp.byPeriod[col.siKey] || {};
                  var ov = obk[col.measureLabel], iv = ibk[col.measureLabel];
                  cv = (typeof ov === "number" && typeof iv === "number" && iv > 0) ? (ov / iv * 100) : "";
                  break;
                }
                case "share": {
                  var sbk = grp.byPeriod[col.periodKey];
                  var sv = sbk ? sbk[col.measureLabel] : null;
                  var st = (periodTotals[col.periodKey] || {})[col.measureLabel];
                  cv = (typeof sv === "number" && st > 0) ? (sv / st * 100) : "";
                  break;
                }
                case "rank": {
                  var rbk = (rankIdx[col.periodKey] || {})[col.measureLabel];
                  var rv = rbk ? rbk[dk] : null;
                  /* Suppress rank when the underlying value is missing — otherwise everyone
                     with no data shows as a tie at the bottom which is misleading. */
                  var rValSrc = grp.byPeriod[col.periodKey];
                  cv = (rv != null && rValSrc && typeof rValSrc[col.measureLabel] === "number") ? rv : "";
                  break;
                }
                case "growth_prev": {
                  var gbCur = grp.byPeriod[col.periodKey];
                  var gbPrev = grp.byPeriod[col.prevPeriodKey];
                  var curG  = gbCur  ? gbCur[col.measureLabel]  : null;
                  var prevG = gbPrev ? gbPrev[col.measureLabel] : null;
                  cv = (typeof curG === "number" && typeof prevG === "number" && prevG !== 0)
                    ? ((curG - prevG) / Math.abs(prevG) * 100) : "";
                  break;
                }
                case "growth_yoy": {
                  var ybCur = grp.byPeriod[col.periodKey];
                  var ybYoy = grp.byPeriod[col.yoyPeriodKey];
                  var curY  = ybCur ? ybCur[col.measureLabel] : null;
                  var prevY = ybYoy ? ybYoy[col.measureLabel] : null;
                  cv = (typeof curY === "number" && typeof prevY === "number" && prevY !== 0)
                    ? ((curY - prevY) / Math.abs(prevY) * 100) : "";
                  break;
                }
                case "evo": {
                  var ebCur  = grp.byPeriod[col.periodKey];
                  var ebBase = grp.byPeriod[col.evoBaseKey];
                  var curE  = ebCur  ? ebCur[col.measureLabel]  : null;
                  var baseE = ebBase ? ebBase[col.measureLabel] : null;
                  var totC = (periodTotals[col.periodKey]  || {})[col.measureLabel];
                  var totB = (periodTotals[col.evoBaseKey] || {})[col.measureLabel];
                  cv = (typeof curE === "number" && typeof baseE === "number" && baseE > 0 && totB > 0 && totC > 0)
                    ? ((curE / baseE) / (totC / totB) * 100) : "";
                  break;
                }
                case "cagr": {
                  var cFirst = grp.byPeriod[cagrFirstK];
                  var cLast  = grp.byPeriod[cagrLastK];
                  var fv = cFirst ? cFirst[col.measureLabel] : null;
                  var lv = cLast  ? cLast[col.measureLabel]  : null;
                  if (typeof fv === "number" && typeof lv === "number" && fv > 0 && lv > 0 && cagrYears > 0) {
                    cv = (Math.pow(lv / fv, 1 / cagrYears) - 1) * 100;
                  } else {
                    cv = "";
                  }
                  break;
                }
                default:
                  cv = "";
              }
              arr.push(cv);
            }
            aoa.push(arr);
          }

          /* ── Provenance footer: Export ID + WHO exported (Layer 1) ── */
          var _whoNameG = (user && (user.name || user.fullName || user.email)) || "";
          var _whoEmailG = (user && user.email) || "";
          var _whoClientG = (user && user.clientName) || "";
          var _provLineG = "Exported by: " + (_whoNameG || "unknown") + (_whoEmailG && _whoEmailG !== _whoNameG ? " <" + _whoEmailG + ">" : "")
            + (_whoClientG ? "  |  Client: " + _whoClientG : "")
            + "  |  " + new Date().toISOString().replace("T", " ").slice(0, 16) + " UTC";
          /* Filter context line — an exported sheet must state what the numbers
             represent (which market, which import channel, which period), not just
             who exported it. Built from the live selection, grouped per dimension. */
          var _ctxParts = [];
          try {
            var sel = selection || null;
            var _dimLbl = { country:"Country", sector:"Sector", manufacturer:"Manufacturer", subManufacturer:"Sub-manufacturer",
              medicine:"Product", productSource:"Product Source", subProduct:"SubProduct", sku:"SKU", agent:"Agent",
              region:"Region", governorate:"Governorate", continent:"Continent", subContinent:"Sub-continent",
              countryOfOrigin:"Country of origin", manufacturerType:"Manufacturer type", localImported:"Local / Imported" };
            Object.keys(_dimLbl).forEach(function (k) {
              var v = sel && sel[k];
              if (v && v.length) _ctxParts.push(_dimLbl[k] + ": " + (v.length <= 4 ? v.join(", ") : v.length + " selected"));
            });
            var _atcV = Object.values((sel && sel.atcFilters) || {}).flat();
            if (_atcV.length) _ctxParts.push("ATC: " + (_atcV.length <= 4 ? _atcV.join(", ") : _atcV.length + " selected"));
            var _nfcV = Object.values((sel && sel.nfcFilters) || {}).flat();
            if (_nfcV.length) _ctxParts.push("NFC: " + (_nfcV.length <= 4 ? _nfcV.join(", ") : _nfcV.length + " selected"));
            if (sel && sel.periodKeys && sel.periodKeys.length) _ctxParts.push("Period: " + sel.periodKeys.join(", "));
            if (sel && sel.measures && sel.measures.length) _ctxParts.push("Measures: " + sel.measures.join(", "));
          } catch (eCtx) { console.warn("[grid-export] selection line skipped:", eCtx && eCtx.message); _ctxParts = []; }
          var _ctxLineG = "Selection: " + (_ctxParts.length ? _ctxParts.join("  |  ") : "no filters — full market");
          aoa.push([]);
          aoa.push([_ctxLineG]);
          aoa.push([_provLineG]);
          var _provRows = 3;
          var ws = XLSX.utils.aoa_to_sheet(aoa);
          var range = XLSX.utils.decode_range(ws["!ref"]);

          /* ── Professional template ──
             Header: navy for dims/values, blue for SHARE, violet for RANK,
             slate for growth/CAGR. Body: zebra rows; SHARE columns highlighted
             soft blue, RANK soft violet (red/green stay reserved for growth);
             growth & CAGR cells colored green/red by their value. */
          var _kindOf = function(c){ return c && c.kind ? c.kind : "dim"; };
          var _hdrFillFor = function(k){
            if (k === "share") return "2E6DA4";
            if (k === "rank") return "6B5CA5";
            if (k === "growth_prev" || k === "growth_yoy" || k === "cagr" || k === "evo") return "44546A";
            return "1E3A5F";
          };
          /* Notice row: amber warning band merged across the full width */
          if (_noticeTxt) {
            var _nRef = XLSX.utils.encode_cell({c:0, r:_amsLogoRowsG});
            if (ws[_nRef]) ws[_nRef].s = {
              fill:{fgColor:{rgb:"FDEBC8"}},
              font:{bold:true,color:{rgb:"92400E"},sz:10},
              alignment:{horizontal:"left",vertical:"center",wrapText:false},
              border:{bottom:{style:"medium",color:{rgb:"E0A93E"}}}
            };
            ws["!merges"] = (ws["!merges"] || []).concat([{s:{c:0, r:_amsLogoRowsG}, e:{c:range.e.c, r:_amsLogoRowsG}}]);
          }
          /* Row 1: navy period band (dims left-aligned, periods centered) */
          for (var hC = 0; hC <= range.e.c; hC++) {
            var hRef = XLSX.utils.encode_cell({c:hC, r:_hdrRow});
            if (!ws[hRef]) ws[hRef] = {t:"s", v:""};
            ws[hRef].s = {
              fill:{fgColor:{rgb:"1E3A5F"}},
              font:{bold:true,color:{rgb:"FFFFFF"},sz:11},
              alignment:{horizontal:hC < dimsArr.length ? "left" : "center",vertical:"center",wrapText:true},
              border:bdr
            };
          }
          /* Row 2: sub-labels with kind-tinted fills (mirrors the pivot export) */
          var _h2StyleFor = function(k, isValCol){
            if (k === "share") return {fill:{fgColor:{rgb:"F1F5F9"}},font:{bold:true,color:{rgb:"64748B"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
            if (k === "rank")  return {fill:{fgColor:{rgb:"E2E8F0"}},font:{bold:true,color:{rgb:"334155"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
            if (k === "growth_prev" || k === "growth_yoy" || k === "evo") return {fill:{fgColor:{rgb:"EDF2F7"}},font:{bold:true,color:{rgb:"475569"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
            if (k === "cagr")  return {fill:{fgColor:{rgb:"D5E0EC"}},font:{bold:true,color:{rgb:"1E3A5F"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
            return {fill:{fgColor:{rgb:isValCol?"D5E0EC":"1E3A5F"}},font:{bold:true,color:{rgb:isValCol?"1E3A5F":"FFFFFF"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
          };
          for (var hC2 = 0; hC2 <= range.e.c; hC2++) {
            var hRef2 = XLSX.utils.encode_cell({c:hC2, r:_hdrRow2});
            if (!ws[hRef2]) ws[hRef2] = {t:"s", v:""};
            var _k9 = _kindOf(cols[hC2]);
            ws[hRef2].s = (hC2 < dimsArr.length)
              ? {fill:{fgColor:{rgb:"1E3A5F"}},font:{bold:true,color:{rgb:"FFFFFF"},sz:9},alignment:{horizontal:"left",vertical:"center"},border:bdr}
              : _h2StyleFor(_k9, _k9 === "value" || _k9 === "dim");
          }
          var _rowsHptG = []; for (var rhI = 0; rhI < _amsLogoRowsG; rhI++) _rowsHptG.push({hpt:16});
          ws["!rows"] = _rowsHptG.concat(_noticeTxt ? [{hpt:26},{hpt:26},{hpt:22}] : [{hpt:26},{hpt:22}]);

          for (var dR = _hdrRow2 + 1; dR <= range.e.r; dR++) {
            var _zebra = ((dR - _hdrRow2) % 2 === 0);
            for (var dC = 0; dC <= range.e.c; dC++) {
              var dRef = XLSX.utils.encode_cell({c:dC, r:dR});
              if (!ws[dRef]) continue;
              var col = cols[dC];
              var k = _kindOf(col);
              var cvv = aoa[dR] ? aoa[dR][dC] : null;
              var st;
              if (k === "growth_prev" || k === "growth_yoy" || k === "cagr") {
                /* Growth semantics: green up, red down — the ONLY red/green in the sheet */
                var _pos = (typeof cvv === "number" && cvv > 0);
                var _neg = (typeof cvv === "number" && cvv < 0);
                st = {
                  font:{sz:10,bold:true,color:{rgb:_pos?"166534":_neg?"B91C1C":"64748B"}},
                  fill:{fgColor:{rgb:_pos?"EFF8F2":_neg?"FDF3F4":(_zebra?"F6F8FA":"FFFFFF")}},
                  alignment:{horizontal:"right",vertical:"center"},border:bdr,
                  numFmt:'+0.0"%";-0.0"%";0.0"%"'
                };
              } else if (k === "evo") {
                /* Evolution Index: 100 = with the market — green above, red below */
                var _eiHi = (typeof cvv === "number" && cvv > 100.5);
                var _eiLo = (typeof cvv === "number" && cvv < 99.5);
                st = {
                  font:{sz:10,bold:true,color:{rgb:_eiHi?"166534":_eiLo?"B91C1C":"64748B"}},
                  fill:{fgColor:{rgb:_eiHi?"EFF8F2":_eiLo?"FDF3F4":(_zebra?"F6F8FA":"FFFFFF")}},
                  alignment:{horizontal:"right",vertical:"center"},border:bdr,
                  numFmt:"0"
                };
              } else if (k === "share") {
                st = {
                  font:{sz:10,bold:true,color:{rgb:"1F4E79"}},
                  fill:{fgColor:{rgb:"EAF2FA"}},
                  alignment:{horizontal:"right",vertical:"center"},border:bdr,
                  numFmt:'0.0"%"'
                };
              } else if (k === "rank") {
                st = {
                  font:{sz:10,bold:true,color:{rgb:"4A3F78"}},
                  fill:{fgColor:{rgb:"F0EDF8"}},
                  alignment:{horizontal:"center",vertical:"center"},border:bdr,
                  numFmt:"0"
                };
              } else if (col.type === "decimal") {
                st = {font:{sz:10,color:{rgb:"1E293B"}},fill:_zebra?{fgColor:{rgb:"F6F8FA"}}:undefined,alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0.00"};
              } else if (col.type === "num" || col.type === "int") {
                st = {font:{sz:10,color:{rgb:"1E293B"}},fill:_zebra?{fgColor:{rgb:"F6F8FA"}}:undefined,alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0"};
              } else {
                st = {font:{sz:10,color:{rgb:"1E293B"}},fill:_zebra?{fgColor:{rgb:"F6F8FA"}}:undefined,alignment:{horizontal:"left",vertical:"center"},border:bdr};
              }
              ws[dRef].s = st;
            }
          }
          ws["!cols"] = cols.map(function(c){
            var k2 = _kindOf(c);
            if (k2 === "share" || k2 === "rank") return {wch:12};
            if (k2 === "growth_prev" || k2 === "growth_yoy" || k2 === "cagr" || k2 === "evo") return {wch:13};
            if (c.type === "decimal") return {wch:14};
            if (c.type === "num")     return {wch:14};
            return {wch:Math.max(16, Math.min(32, (c.label || "").length + 2))};
          });
          /* Period band + dim merges (on top of any notice merge) */
          if (_hdrMerges.length) ws["!merges"] = (ws["!merges"] || []).concat(_hdrMerges);
          /* Freeze the dim columns + notice/header rows so periods scroll under fixed labels */
          ws["!freeze"] = {xSplit: dimsArr.length, ySplit: _hdrRow2 + 1};
          /* Autofilter anchored on the SUB-LABEL row — sort/filter ready out of the box
             (provenance footer rows excluded so Excel sorts never mix them into data) */
          ws["!autofilter"] = { ref: XLSX.utils.encode_range({s:{c:0,r:_hdrRow2}, e:{c:range.e.c, r:Math.max(_hdrRow2, range.e.r - _provRows)}}) };

          var wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, _rpt.sheet);
          /* Provenance in document properties — exporter lands in the VISIBLE fields
             (Author / Last Modified By / Company), Export ID in Subject + Keywords,
             and the full line in Comments. */
          wb.Props = {
            Title: _rpt.file,
            Subject: _exportId ? ("AMSX Export ID: " + _exportId) : "AMSX Export (unregistered)",
            Author: (_whoNameG || "AMSX user") + (_whoClientG ? " — " + _whoClientG : ""),
            LastAuthor: _whoNameG || "AMSX user",
            Company: _whoClientG || "AMS Analytix",
            Category: _whoClientG || "",
            /* Keywords intentionally NOT set — it surfaces as "Tags" in Excel's
               Info panel; the ID stays in Subject/Comments and the sheet footer. */
            Comments: _provLineG
          };
          _embedAmsMarker(XLSX, wb, _regMarkerG);

          /* (Truncation notice is now the amber band at the TOP of the Grid sheet) */

          var fname = _rpt.file + "_" + new Date().toISOString().slice(0,10) + ".xlsx";
          _amsWriteXlsxWithLogo(XLSX, wb, fname);
          /* Activity Timeline: grid exports are auditable like pivot exports.
             Details ride in queryPayload — the only free-form field the log stores. */
          try { logActivity("export", {dashboardName: "grid", exportFormat: "excel", rowsReturned: dimKeys.length, studyType: activeStudyType || "", queryPayload: {report: _rpt.file, dims: dimsArr, measures: msrsArr, periods: periodKeys.length || 1, truncated: !!(truncated || outTruncated)}}); } catch(e){}
          console.log("[grid-export] " + rowsSrc.length + " src rows → " + dimKeys.length + " pivoted rows × " + (periodKeys.length||1) + " period(s) → " + fname + (outTruncated ? " (ROW-LIMIT " + outCap + ")" : "") + (truncated ? " (SRC-TRUNCATED)" : ""));
          setGridExporting(false);
        }).catch(function(e){
          console.error("[grid-export] xlsx build failed:", e);
          setGridExporting(false);
          setGridErrMsg("Failed to generate Excel file. Please try again.");
        });
      } catch (e) {
        console.error("[grid-export] failed:", e);
        setGridExporting(false);
        setGridErrMsg(e.message || "Grid export failed. Please try again.");
      }
    }, 30); }); /* small delay so spinner renders before the heavy projection */
      }); /* end provenance-registration chain */
  };

  /* ── Themed Excel export with row grouping ── */
  var doExport = function() {
    /* ── Extract limit enforcement (admins bypass) ── */
    if (!isAdmin && limitReached) {
      if (exportBlocked) {
        setLimitMsg("Export is disabled for your organization. Contact your administrator.");
      } else if (isReadonly) {
        setLimitMsg("Read-only access — Excel export is disabled. Contact your administrator.");
      } else {
        setLimitMsg("Export limit reached for this quarter" + (extractResetsOn ? " — it resets on " + extractResetsOn : "") + ". Contact your administrator to increase your allowance.");
      }
      return;
    }
    setLimitMsg("");
    /* Report name: user-chosen, lands in the file name AND the sheet tab */
    var _rptP = _askReportName("Market " + (activeStudyType ? activeStudyType.charAt(0).toUpperCase() + activeStudyType.slice(1) : "Pivot"));
    setExporting(true);
    /* ── Layer 1 provenance: register the export with the server BEFORE the
       file is generated. Server records session identity + device + the exact
       slice and issues an ExportId that is embedded in the workbook.
       Fail-closed for non-admin users; admins proceed on registration failure
       (console-warned) so a provenance outage can't lock out internal staff. ── */
    var _provMeta = (typeof window !== "undefined" && window.__amsxPivotMeta) ? window.__amsxPivotMeta : null;
    var _regMarker = null; /* structural-marker payload from the server (Suggestion 2) */
    api.registerExport({
      exportType: "pivot", exportFormat: "excel",
      studyType: activeStudyType || "",
      dashboardName: _rptP.file,
      queryPayload: {
        report: _rptP.file,
        dims: allDims, measures: msrs, periods: periods,
        filters: _provMeta ? _provMeta.filters : null,
        periodMode: _provMeta ? _provMeta.periodMode : null,
        periodKeys: _provMeta ? _provMeta.periodKeys : null,
        topN: _provMeta ? _provMeta.topNOpts : null
      }
    }).then(function(_reg){ _regMarker = (_reg && _reg.marker) || null; return (_reg && _reg.exportId) || null; })
      .catch(function(_regErr){
        if (isAdmin) { console.warn("[export] provenance registration failed — admin bypass:", _regErr && _regErr.message); return null; }
        throw new Error("__REG_BLOCKED__");
      })
      .then(function(_exportId){
        /* r132: progressive (medical) pivots — batch-load every remaining level
           so the workbook carries the whole tree, not just the expanded rows */
        var _lvlP9 = onLoadAllLevels ? onLoadAllLevels().catch(function(){ return false; }) : Promise.resolve(true);
        return _lvlP9.then(function(_lvlOk9){ if (_lvlOk9 === false) setLimitMsg("⚠️ Some levels could not be loaded before the export — deeper rows may be missing. Please try again."); return getXLSX(); }).then(function(XLSX) {

      /* Style definitions */
      var bdr = {top:{style:"thin",color:{rgb:"B8C4CE"}},bottom:{style:"thin",color:{rgb:"B8C4CE"}},left:{style:"thin",color:{rgb:"B8C4CE"}},right:{style:"thin",color:{rgb:"B8C4CE"}}};
      var sHdr = {fill:{fgColor:{rgb:"1E3A5F"}},font:{bold:true,color:{rgb:"FFFFFF"},sz:11},alignment:{horizontal:"center",vertical:"center",wrapText:true},border:bdr};
      var sH2U = {fill:{fgColor:{rgb:"E2E8F0"}},font:{bold:true,color:{rgb:"1E3A5F"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sH2L = {fill:{fgColor:{rgb:"D0DBE7"}},font:{bold:true,color:{rgb:"1A365D"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sH2D = {fill:{fgColor:{rgb:"D5E0EC"}},font:{bold:true,color:{rgb:"1E3A5F"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sH2S = {fill:{fgColor:{rgb:"F1F5F9"}},font:{bold:true,color:{rgb:"64748B"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sH2R = {fill:{fgColor:{rgb:"E2E8F0"}},font:{bold:true,color:{rgb:"334155"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sH2GP= {fill:{fgColor:{rgb:"EDF2F7"}},font:{bold:true,color:{rgb:"475569"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sH2GY= {fill:{fgColor:{rgb:"EDF2F7"}},font:{bold:true,color:{rgb:"475569"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sH2PL= {fill:{fgColor:{rgb:"E8ECF1"}},font:{bold:true,color:{rgb:"334155"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sH2PD= {fill:{fgColor:{rgb:"EDF2F7"}},font:{bold:true,color:{rgb:"334155"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sGL  = {fill:{fgColor:{rgb:"1E3A5F"}},font:{bold:true,color:{rgb:"FFFFFF"},sz:11},alignment:{horizontal:"left",vertical:"center"},border:bdr};
      var sGV  = {fill:{fgColor:{rgb:"2C5282"}},font:{bold:true,color:{rgb:"E2E8F0"},sz:10},alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0"};
      var sGVP = {fill:{fgColor:{rgb:"2C5282"}},font:{bold:true,color:{rgb:"E2E8F0"},sz:10},alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0.00"};
      var sGP  = {fill:{fgColor:{rgb:"2C5282"}},font:{bold:true,color:{rgb:"CBD5E1"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var LF = ["DCE5EF","D5E0EC","DDE6EE","E0E7EE","E5EAEF","EDF1F5"];
      var LC = ["1E3A5F","2C5282","3B6D8F","4A7C96","5A7D8F","64748B"];
      var mkLL = function(d){ var i=Math.min(d,LF.length-1); return {fill:{fgColor:{rgb:LF[i]}},font:{bold:true,color:{rgb:LC[i]},sz:10},alignment:{horizontal:"left",vertical:"center",indent:d*2},border:bdr}; };
      var mkLV = function(d){ var i=Math.min(d,LF.length-1); return {fill:{fgColor:{rgb:LF[i]}},font:{color:{rgb:LC[i]},sz:10},alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0"}; };
      var mkLVP= function(d){ var i=Math.min(d,LF.length-1); return {fill:{fgColor:{rgb:LF[i]}},font:{color:{rgb:LC[i]},sz:10},alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0.00"}; };
      var sLL  = {fill:{fgColor:{rgb:"FFFFFF"}},font:{color:{rgb:"334155"},sz:10},alignment:{horizontal:"left",vertical:"center"},border:bdr};
      var sLV  = {fill:{fgColor:{rgb:"FFFFFF"}},font:{color:{rgb:"334155"},sz:10},alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0"};
      var sLVP = {fill:{fgColor:{rgb:"FFFFFF"}},font:{color:{rgb:"334155"},sz:10},alignment:{horizontal:"right",vertical:"center"},border:bdr,numFmt:"#,##0.00"};
      var sPct = function(d,leaf){ return {fill:{fgColor:{rgb:leaf?"FFFFFF":LF[Math.min(d,LF.length-1)]}},font:{color:{rgb:"64748B"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr}; };
      var sGrP = {fill:{fgColor:{rgb:"F0FDF4"}},font:{bold:true,color:{rgb:"15803D"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sGrN = {fill:{fgColor:{rgb:"FEF2F2"}},font:{bold:true,color:{rgb:"DC2626"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sGrZ = {fill:{fgColor:{rgb:"F8FAFC"}},font:{color:{rgb:"94A3B8"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sGrNA= {fill:{fgColor:{rgb:"F8FAFC"}},font:{color:{rgb:"CBD5E1"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var mH2  = function(m){ return m.m==="u"?sH2U:m.m==="l"?sH2L:m.m==="pl"?sH2PL:m.m==="pd"?sH2PD:sH2D; };
      var grS  = function(g){ if (!g) return sGrNA; if (g.pos) return sGrP; if (g.zero) return sGrZ; return sGrN; };
      var sCAGRH = {fill:{fgColor:{rgb:"1E3A5F"}},font:{bold:true,color:{rgb:"E2E8F0"},sz:10},alignment:{horizontal:"center",vertical:"center"},border:bdr};
      var sCAGRH2 = {fill:{fgColor:{rgb:"D5E0EC"}},font:{bold:true,color:{rgb:"1E3A5F"},sz:9},alignment:{horizontal:"center",vertical:"center"},border:bdr};

      /* ── msa (Medical & Pharma): mirror the on-screen family colours — Pharma
         (sales) columns light GREEN, Rx columns light BLUE, plus the Pharma/Rx
         band row above the measure headers, exactly like the live pivot. ── */
      var _msaX = activeStudyType === "jordan_msa";
      var _msaRxX = { np:1, nrx:1 };
      var _famX = function(mk){ return _msaX ? (_msaRxX[mk] ? "rx" : "ph") : null; };
      var _wfX = function(st, mk){
        var f = _famX(mk); if (!f) return st;
        return Object.assign({}, st, { fill: { fgColor: { rgb: (f === "rx" ? "E3EEFA" : "E7F5EE") } } });
      };
      var _hdX = function(st, mk){
        var f = _famX(mk); if (!f) return st;
        return Object.assign({}, st, f === "rx"
          ? { fill:{fgColor:{rgb:"E3EEFA"}}, font:Object.assign({}, st.font, {color:{rgb:"1E3A5F"}}) }
          : { fill:{fgColor:{rgb:"E0F2E9"}}, font:Object.assign({}, st.font, {color:{rgb:"0F5132"}}) });
      };
      var _gtX = function(st, mk){
        var f = _famX(mk); if (!f) return st;
        return f === "ph" ? Object.assign({}, st, { fill:{fgColor:{rgb:"0F6E56"}} }) : st;
      };

      var wsData = [];
      var rowOutline = []; /* outlineLevel per data row (0 = no grouping) */

      /* ── AMS logo band: blank rows at the very top; the logo image is
         anchored over them at the zip level (see _amsBrandXlsxBytes) ── */
      var _amsLogoRows = 3;
      for (var lbI = 0; lbI < _amsLogoRows; lbI++) { wsData.push([{v:"", s:undefined}]); rowOutline.push(0); }

      /* r133: DERIVED measures and the Sales Tracker flow statistics were not
         exported (FOC % came out as 0, Sell-Through % / Sell-Out Value / Profit
         columns were missing). _xDer9 mirrors the on-screen cell formulas:
         FOC % = FOC ÷ paid units (r178), Coverage % = pharmacies ÷ panel (governorate-scoped
         when the row sits under a Governorate), Sell-Through % = sell-out ÷
         sell-in of the same base period. Percentages are written as "7.3%". */
      var _pct1x = function(x){ return (Math.round(Number(x) * 10) / 10).toFixed(1) + "%"; };
      var _xDer9 = function(m, v, pk, rowData, lkPath){
        if (m.derived === "focPct") return _pct1x(((v.gu || 0) - (v.fu || 0)) > 0 ? ((v.fu || 0) / ((v.gu || 0) - (v.fu || 0))) * 100 : 0);   /* r181: ÷ paid units (gross − FOC) — the r178 change missed this export path */
        if (m.derived === "coverage") {
          var _cd = null;
          if (_covGovIdx >= 0 && lkPath) { var _cg = String(lkPath).split("|")[_covGovIdx]; if (_cg) _cd = _covLk ? _covLk["G|" + _cg + "|" + pk] : null; }
          if (_cd == null && _covLk) _cd = _covLk["T|" + pk];
          return _pct1x((_cd > 0) ? ((v.nph || 0) / _cd) * 100 : 0);
        }
        if (m.derived === "sellThrough") {
          var _b = String(pk), _t = _b.indexOf("~");
          if (_t >= 0 && _b.slice(_t + 1) === "Sell-Out") {
            var _in = (rowData && rowData[_b.slice(0, _t) + "~Sell-In"]) || null;
            var _inU = _in ? (((_in.gu || 0) > 0) ? _in.gu : (_in.u || 0)) : 0;
            var _outU = ((v.gu || 0) > 0) ? v.gu : (v.u || 0);
            if (_inU > 0) return _pct1x((_outU / _inU) * 100);
          }
          return "\u2014";
        }
        return undefined;
      };
      /* flow statistics (Sell-Out bands only): Sell-Out Value + Profit, Sell-Through % */
      var _xPf9 = function(m, pk){ return !!(typeof pfStatFor === "function" && pfStatFor(m.m, pk)); };
      var _xSth9 = function(m, pk){ return !!(typeof sthFor === "function" && sthFor(m.m, pk)); };
      var _xSthVal9 = function(m, v, pk, rowData){ var _si = (rowData && rowData[_siKeyOf(pk)]) || null; return (_si && (_si[m.m] || 0) > 0) ? _pct1x(((v[m.m] || 0) / _si[m.m]) * 100) : "\u2014"; };

      /* Header row 1: dimension label + period spans */
      var h1 = [{v:isTree?allDimLabels.join(" \u2192 "):(allDimLabels[0]||childLabel), s:sHdr}];
      for (var ehi=0; ehi<periods.length; ehi++) {
        var pl=periodLabel9(periods[ehi]);
        for (var ehmi=0; ehmi<activeMsrs.length; ehmi++) {
          var ehm=activeMsrs[ehmi];
          h1.push({v: ehmi === 0 ? pl : "", s:sHdr});
          if (!ehm.noStats && shareFor(ehm.m))  h1.push({v:"", s:sHdr});
          if (!ehm.noStats && rankFor(ehm.m))   h1.push({v:"", s:sHdr});
          if (!ehm.noStats && grPrevFor(ehm.m)) h1.push({v:"", s:sHdr});
          if (!ehm.noStats && grYoyFor(ehm.m))  h1.push({v:"", s:sHdr});
          if (!ehm.noStats && evoPrevFor(ehm.m)) h1.push({v:"", s:sHdr});
          if (!ehm.noStats && evoYoyFor(ehm.m))  h1.push({v:"", s:sHdr});
          if (_xPf9(ehm, periods[ehi])) { h1.push({v:"", s:sHdr}); h1.push({v:"", s:sHdr}); }
          if (_xSth9(ehm, periods[ehi])) h1.push({v:"", s:sHdr});
        }
      }
      /* CAGR header row 1 */
      if (showCAGR && cagrMsrs.length > 0) {
        for (var ech=0; ech<statMsrs.length; ech++) {
          h1.push({v:"CAGR "+statMsrs[ech].label, s:sCAGRH});
        }
      }
      /* r132: Sales Tracker CURRENT STOCK column (closing stock at the latest base period), as on screen */
      if (stockOn) { for (var esh=0; esh<stockBases.length; esh++) h1.push({v:"CURRENT STOCK", s:sCAGRH}); }
      wsData.push(h1); rowOutline.push(0);

      /* Pharma / Rx band row (mirrors the live pivot header) */
      var _msaColsX = function(isRx){ var _n8=0; for (var _bi2=0;_bi2<activeMsrs.length;_bi2++){ var _bm=activeMsrs[_bi2]; if (!!_msaRxX[_bm.m] !== isRx) continue; _n8 += 1; if (!_bm.noStats) _n8 += (shareFor(_bm.m)?1:0)+(rankFor(_bm.m)?1:0)+(grPrevFor(_bm.m)?1:0)+(grYoyFor(_bm.m)?1:0)+(evoPrevFor(_bm.m)?1:0)+(evoYoyFor(_bm.m)?1:0); } return _n8; };
      var _bPh = _msaX ? _msaColsX(false) : 0, _bRx = _msaX ? _msaColsX(true) : 0;
      var _msaBandX = _msaX && _bPh > 0 && _bRx > 0;
      var _bandMerges = [];
      if (_msaBandX) {
        var sBPh = {fill:{fgColor:{rgb:"E0F2E9"}},font:{bold:true,color:{rgb:"0F6E56"},sz:10},alignment:{horizontal:"center",vertical:"center"},border:bdr};
        var sBRx = {fill:{fgColor:{rgb:"E3EDF8"}},font:{bold:true,color:{rgb:"1E4E79"},sz:10},alignment:{horizontal:"center",vertical:"center"},border:bdr};
        var hB = [{v:"", s:sHdr}];
        var _bc = 1;
        for (var bpi=0; bpi<periods.length; bpi++) {
          hB.push({v:"Pharma", s:sBPh});
          for (var bfi=1; bfi<_bPh; bfi++) hB.push({v:"", s:sBPh});
          if (_bPh > 1) _bandMerges.push({s:_bc, e:_bc+_bPh-1});
          _bc += _bPh;
          hB.push({v:"Rx", s:sBRx});
          for (var bri=1; bri<_bRx; bri++) hB.push({v:"", s:sBRx});
          if (_bRx > 1) _bandMerges.push({s:_bc, e:_bc+_bRx-1});
          _bc += _bRx;
        }
        if (showCAGR && cagrMsrs.length > 0) { for (var bci=0; bci<statMsrs.length; bci++) hB.push({v:"", s:sH2S}); }
        wsData.push(hB); rowOutline.push(0);
      }

      /* Header row 2: sub-labels */
      var h2 = [{v:"", s:sHdr}];
      for (var eh2i=0; eh2i<periods.length; eh2i++) {
        for (var eh2mi=0; eh2mi<activeMsrs.length; eh2mi++) {
          var eh2m=activeMsrs[eh2mi];
          h2.push({v:eh2m.label, s:_hdX(mH2(eh2m), eh2m.m)});
          /* Statistic columns say WHAT they measure (review request):
             share of the market total, rank within the row's own group,
             growth vs which comparison period. */
          if (!eh2m.noStats && shareFor(eh2m.m))  h2.push({v:"Share % of Total (" + eh2m.label + ")", s:_wfX(sH2S, eh2m.m)});
          if (!eh2m.noStats && rankFor(eh2m.m))   h2.push({v:"Rank in Group (by " + eh2m.label + ")", s:_wfX(sH2R, eh2m.m)});
          if (!eh2m.noStats && grPrevFor(eh2m.m)) h2.push({v:"Gth % vs Prev Period", s:_wfX(sH2GP, eh2m.m)});
          if (!eh2m.noStats && grYoyFor(eh2m.m))  h2.push({v:"Gth % vs Similar Period (YoY)", s:_wfX(sH2GY, eh2m.m)});
          if (!eh2m.noStats && evoPrevFor(eh2m.m)) h2.push({v:"Evolution Index vs Prev (" + eh2m.label + ")", s:_wfX(sH2S, eh2m.m)});
          if (!eh2m.noStats && evoYoyFor(eh2m.m))  h2.push({v:"Evolution Index vs Similar (" + eh2m.label + ")", s:_wfX(sH2S, eh2m.m)});
          if (_xPf9(eh2m, periods[eh2i])) { h2.push({v:"Sell-Out Value " + (eh2m.m === "d" ? "USD" : lcLabel), s:sH2S}); h2.push({v:"Profit " + (eh2m.m === "d" ? "USD" : lcLabel), s:sH2S}); }
          if (_xSth9(eh2m, periods[eh2i])) h2.push({v:"Sell-Through % (" + eh2m.label + ")", s:sH2S});
        }
      }
      /* CAGR header row 2 */
      if (showCAGR && cagrMsrs.length > 0) {
        for (var ech2=0; ech2<statMsrs.length; ech2++) {
          h2.push({v:"CAGR", s:sCAGRH2});
        }
      }
      if (stockOn) { for (var esh2=0; esh2<stockBases.length; esh2++) h2.push({v:periodLabel9(stockBases[esh2]), s:sCAGRH2}); }
      wsData.push(h2); rowOutline.push(0);

      /* Grand total row */
      var gRow=[{v:"\u2211 Grand Total",s:sGL}];
      for (var egi=0; egi<periods.length; egi++) {
        var epk=periods[egi]; var egv=grand[epk]||{u:0,l:0,d:0};
        var ePrev=prevPeriodOf[epk]; var egvP=ePrev?(grand[ePrev]||null):null;
        var eYoy=yoyPeriodOf[epk];   var egvY=eYoy?(grand[eYoy]||null):null;
        for (var egmi=0; egmi<activeMsrs.length; egmi++) {
          var egm=activeMsrs[egmi];
          var isP = (egm.m==="pl"||egm.m==="pd"||egm.m==="npl"||egm.m==="npd");
          var _gDer = egm.derived ? _xDer9(egm, egv, epk, grand, null) : undefined;   /* r133 */
          gRow.push({v:egm.skuOnly?"":(_gDer !== undefined ? _gDer : (egm.ratio2 ? (egv[egm.m] == null ? "\u2014" : Math.round(Number(egv[egm.m]) * 100) / 100) : egv[egm.m])),s:_gtX(isP?sGVP:(_gDer !== undefined ? sGP : sGV), egm.m)});
          if (!egm.noStats && shareFor(egm.m))  gRow.push({v:"100%",s:_gtX(sGP, egm.m)});
          if (!egm.noStats && rankFor(egm.m))   gRow.push({v:"-",s:_gtX(sGP, egm.m)});
          if (!egm.noStats && grPrevFor(egm.m)) { var ggP=egvP?fmtGr(egv[egm.m],egvP[egm.m]):null; gRow.push({v:ggP?ggP.str:"-",s:grS(ggP)}); }
          if (!egm.noStats && grYoyFor(egm.m))  { var ggY=egvY?fmtGr(egv[egm.m],egvY[egm.m]):null; gRow.push({v:ggY?ggY.str:"-",s:grS(ggY)}); }
          if (!egm.noStats && evoPrevFor(egm.m)) { gRow.push({v:(egvP&&egvP[egm.m]>0)?"100":"-",s:_gtX(sGP, egm.m)}); }
          if (!egm.noStats && evoYoyFor(egm.m))  { gRow.push({v:(egvY&&egvY[egm.m]>0)?"100":"-",s:_gtX(sGP, egm.m)}); }
          if (_xPf9(egm, epk)) { gRow.push({v: egm.m === "d" ? (egv.sv || 0) : (egv.svl || 0), s:sGV}); gRow.push({v: egm.m === "d" ? (egv.pf || 0) : (egv.pfl || 0), s:sGV}); }   /* r133 */
          if (_xSth9(egm, epk)) gRow.push({v:_xSthVal9(egm, egv, epk, grand), s:sGP});
        }
      }
      /* CAGR grand total */
      if (showCAGR && cagrMsrs.length > 0) {
        for (var egc=0; egc<statMsrs.length; egc++) {
          var egcm = statMsrs[egc];
          var egcagr = calcCAGR(grand, egcm.m);
          gRow.push({v:egcagr?egcagr.str:"-", s:grS(egcagr)});
        }
      }
      if (stockOn) { for (var egs=0; egs<stockBases.length; egs++) { var _egsv = _stockLk9[_stockBlankKey + "|" + stockBases[egs]]; gRow.push({v:(_egsv != null) ? Number(_egsv) : "", s:sGV}); } }
      wsData.push(gRow); rowOutline.push(0);

      /* Node rows with outline levels for grouping */
      var _exportedRows = 0;
      var _rowLimit = isAdmin ? Infinity : rowExportLimit;
      var _totalAvailable = (function countNodes(nodes, parentPath){var c=0;for(var i=0;i<nodes.length;i++){c++;var pk=parentPath?parentPath+"||"+nodes[i].name:nodes[i].name;var kids=(_lzc[pk]&&_lzc[pk].length>0)?_lzc[pk]:nodes[i].children;if(kids&&kids.length)c+=countNodes(kids,pk);}return c;})(displayNodes, "");
      var expNodes = function(nodes, rnkM, parentPath, expParentData) {
        var sortedN = sortNodes(nodes);
        var rnk2 = rnkM || (showRank?rankSiblings(sortedN):null);
        for (var xi=0; xi<sortedN.length; xi++) {
          if (_exportedRows >= _rowLimit) break;
          var xn=sortedN[xi];
          var xPathKey = parentPath ? parentPath+"||"+xn.name : xn.name;
          /* Use effective children (lazy-loaded or built-in) */
          var xEffChildren = (_lzc[xPathKey] && _lzc[xPathKey].length > 0) ? _lzc[xPathKey] : (xn.children || []);
          var isLeaf = xEffChildren.length === 0 && !xn._lazy;
          var depth = xn.depth;
          var lblS = isLeaf ? Object.assign({}, sLL, {alignment:{horizontal:"left",vertical:"center",indent:depth*2}}) : mkLL(depth);
          var valS = isLeaf ? sLV : mkLV(depth);
          var valSP = isLeaf ? sLVP : mkLVP(depth);
          var xRow=[{v:xn.name, s:lblS}];
          for (var xp=0; xp<periods.length; xp++) {
            var xpk=periods[xp]; var xv=xn.data&&xn.data[xpk]?xn.data[xpk]:{u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,npl:0,npd:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,us:"0.0",fus:"0.0",gus:"0.0",ls:"0.0",ds:"0.0",pls:"0.0",pds:"0.0",npls:"0.0",npds:"0.0",nps:"0.0",nrxs:"0.0",nphs:"0.0",ius:"0.0"};
            var xPrevPk=prevPeriodOf[xpk]; var xvP=xPrevPk&&xn.data&&xn.data[xPrevPk]?xn.data[xPrevPk]:null;
            var xYoyPk=yoyPeriodOf[xpk];   var xvY=xYoyPk&&xn.data&&xn.data[xYoyPk]?xn.data[xYoyPk]:null;
            for (var xm=0; xm<activeMsrs.length; xm++) {
              var xms=activeMsrs[xm];
              var xIsP = (xms.m==="pl"||xms.m==="pd"||xms.m==="npl"||xms.m==="npd");
              var xSkuBlank = xms.skuOnly && xn.dim !== "sku";
              var xOthersRow = xn.name && String(xn.name).indexOf("Others (") === 0;
              var xLeafBlank = xms.leafOnly && xOthersRow;
              var xBlank = xSkuBlank || xLeafBlank;
              var _xCellRaw = xv[xms.m];
              var _xDerV = xms.derived ? _xDer9(xms, xv, xpk, xn.data, xn._lkPath) : undefined;   /* r133 */
              if (_xDerV !== undefined) _xCellRaw = _xDerV;
              else if (xms.ratio2) _xCellRaw = (_xCellRaw == null) ? "\u2014" : Math.round(Number(_xCellRaw) * 100) / 100;
              else if (xms.dashNull && _xCellRaw == null) _xCellRaw = "\u2014";
              else if (xms.noStats && (_xCellRaw == null)) _xCellRaw = 0;
              xRow.push({v:xBlank?"":_xCellRaw, s:(_xDerV !== undefined ? sPct(depth, isLeaf) : _wfX(xIsP?valSP:valS, xms.m))});
              if (!xms.noStats && shareFor(xms.m)) { var _xBase = expParentData && expParentData[xpk] ? expParentData[xpk][xms.m] : (grand[xpk] ? grand[xpk][xms.m] : 0); var _xSh = _xBase > 0 ? ((xv[xms.m] || 0) / _xBase * 100).toFixed(1) : "0.0"; xRow.push({v:_xSh+"%", s:_wfX(sPct(depth, isLeaf), xms.m)}); }
              if (!xms.noStats && rankFor(xms.m)) { var xr=rnk2&&rnk2[xpk]&&rnk2[xpk][xms.m]?rnk2[xpk][xms.m][xn.name]:null; xRow.push({v:xr?xr:"-", s:_wfX(sPct(depth, isLeaf), xms.m)}); }
              if (!xms.noStats && grPrevFor(xms.m)) { var xgP=xvP?fmtGr(xv[xms.m],xvP[xms.m]):null; xRow.push({v:xgP?xgP.str:"-", s:grS(xgP)}); }
              if (!xms.noStats && grYoyFor(xms.m))  { var xgY=xvY?fmtGr(xv[xms.m],xvY[xms.m]):null; xRow.push({v:xgY?xgY.str:"-", s:grS(xgY)}); }
              if (!xms.noStats && (evoPrevFor(xms.m) || evoYoyFor(xms.m))) {
                var _xRefSrc  = expParentData || grand;
                var _xRefCur  = _xRefSrc && _xRefSrc[xpk] ? _xRefSrc[xpk][xms.m] : null;
                var _xEvoCell = function(basePk, nodeBaseRow) {
                  var _xRefBase = (basePk && _xRefSrc && _xRefSrc[basePk]) ? _xRefSrc[basePk][xms.m] : null;
                  var _xEvo = fmtEvo(xv[xms.m], nodeBaseRow ? nodeBaseRow[xms.m] : null, _xRefCur, _xRefBase);
                  xRow.push({v:_xEvo?_xEvo.str:"-", s:grS(_xEvo)});
                };
                if (evoPrevFor(xms.m)) _xEvoCell(xPrevPk, xvP);
                if (evoYoyFor(xms.m))  _xEvoCell(xYoyPk,  xvY);
              }
              if (_xPf9(xms, xpk)) { xRow.push({v: xms.m === "d" ? (xv.sv || 0) : (xv.svl || 0), s:valS}); xRow.push({v: xms.m === "d" ? (xv.pf || 0) : (xv.pfl || 0), s:valS}); }   /* r133 */
              if (_xSth9(xms, xpk)) xRow.push({v:_xSthVal9(xms, xv, xpk, xn.data), s:sPct(depth, isLeaf)});
            }
          }
          /* CAGR data row */
          if (showCAGR && cagrMsrs.length > 0) {
            for (var xc=0; xc<statMsrs.length; xc++) {
              var xcm = statMsrs[xc];
              var xcagr = calcCAGR(xn.data, xcm.m);
              xRow.push({v:xcagr?xcagr.str:"-", s:grS(xcagr)});
            }
          }
          if (stockOn) { for (var xsb=0; xsb<stockBases.length; xsb++) { var _xsv = (xn._lkPath != null) ? _stockLk9[xn._lkPath + "|" + stockBases[xsb]] : null; xRow.push({v:(_xsv != null) ? Number(_xsv) : "", s:valS}); } }
          wsData.push(xRow);
          _exportedRows++;
          /* Outline level: depth > 0 means this row is grouped under its parent */
          rowOutline.push(depth);
          if (_exportedRows < _rowLimit && xEffChildren.length > 0) expNodes(xEffChildren, showRank?rankSiblings(xEffChildren):null, xPathKey, xn.data);
        }
      };
      expNodes(displayNodes, showRank?rankSiblings(displayNodes):null, "", null);

      /* ── Provenance footer: Export ID + WHO exported (Layer 1) ── */
      var _whoName = (user && (user.name || user.fullName || user.email)) || "";
      var _whoEmail = (user && user.email) || "";
      var _whoClient = (user && user.clientName) || "";
      var _provLine = "Exported by: " + (_whoName || "unknown") + (_whoEmail && _whoEmail !== _whoName ? " <" + _whoEmail + ">" : "")
        + (_whoClient ? "  |  Client: " + _whoClient : "")
        + "  |  " + new Date().toISOString().replace("T", " ").slice(0, 16) + " UTC";
      wsData.push([{v:"", s:undefined}]); rowOutline.push(0);
      wsData.push([{v:_provLine, s:{font:{sz:8,italic:true,color:{rgb:"94A3B8"}}}}]); rowOutline.push(0);

      /* Build sheet manually for styled cells (header row 1 sits below the logo band) */
      var numCols = wsData[_amsLogoRows] ? wsData[_amsLogoRows].length : wsData[0].length;
      var numRows = wsData.length;
      var ws = {};
      ws["!ref"] = XLSX.utils.encode_range({s:{c:0,r:0},e:{c:numCols-1,r:numRows-1}});
      for (var wr=0; wr<numRows; wr++) {
        for (var wc=0; wc<wsData[wr].length; wc++) {
          var cell = wsData[wr][wc];
          var val = typeof cell === "object" ? cell.v : cell;
          var sty = typeof cell === "object" ? cell.s : undefined;
          var cellRef = XLSX.utils.encode_cell({c:wc,r:wr});
          var cellObj = {};
          if (typeof val === "number") { cellObj.t = "n"; cellObj.v = val; }
          else { cellObj.t = "s"; cellObj.v = val != null ? String(val) : ""; }
          if (sty) cellObj.s = sty;
          ws[cellRef] = cellObj;
        }
      }

      /* Column widths */
      var colW = [];
      for (var cwi=0; cwi<numCols; cwi++) {
        var mx=8;
        for (var cwr=0; cwr<Math.min(numRows,100); cwr++) {
          var cv = wsData[cwr][cwi]; var cvv = typeof cv === "object" ? cv.v : cv;
          if (cvv!=null) mx=Math.max(mx,String(cvv).length);
        }
        colW.push({wch:Math.min(mx+3,45)});
      }
      ws["!cols"] = colW;

      /* Row outline / grouping */
      var rowProps = [];
      for (var ri2=0; ri2<numRows; ri2++) {
        var ol = rowOutline[ri2] || 0;
        rowProps.push(ol > 0 ? {outlineLevel: ol, hidden: false} : {});
      }
      ws["!rows"] = rowProps;

      /* Merge period header cells in the first header row (below the logo band) */
      var merges = [];
      var mergeStart = 1;
      for (var mpi=0; mpi<periods.length; mpi++) {
        if (span > 1) merges.push({s:{r:_amsLogoRows,c:mergeStart},e:{r:_amsLogoRows,c:mergeStart+span-1}});
        mergeStart += span;
      }
      /* msa: merge the Pharma / Rx band cells (row right under the period row) */
      if (_msaBandX) {
        for (var bmi=0; bmi<_bandMerges.length; bmi++) merges.push({s:{r:_amsLogoRows+1, c:_bandMerges[bmi].s}, e:{r:_amsLogoRows+1, c:_bandMerges[bmi].e}});
      }
      if (merges.length) ws["!merges"] = merges;

      /* Outline properties: summary rows above detail */
      if (!ws["!outline"]) ws["!outline"] = {};
      ws["!outline"].above = true;

      var wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,_rptP.sheet);
      /* Provenance in document properties — exporter lands in the VISIBLE fields
         (Author / Last Modified By / Company), Export ID in Subject + Keywords,
         and the full line in Comments. */
      wb.Props = {
        Title: _rptP.file,
        Subject: _exportId ? ("AMSX Export ID: " + _exportId) : "AMSX Export (unregistered)",
        Author: (_whoName || "AMSX user") + (_whoClient ? " — " + _whoClient : ""),
        LastAuthor: _whoName || "AMSX user",
        Company: _whoClient || "AMS Analytix",
        Category: _whoClient || "",
        /* Keywords intentionally NOT set — it surfaces as "Tags" in Excel's
           Info panel; the ID stays in Subject/Comments and the sheet footer. */
        Comments: _provLine
      };
      _embedAmsMarker(XLSX, wb, _regMarker);
      _amsWriteXlsxWithLogo(XLSX, wb, _rptP.file + "_" + new Date().toISOString().slice(0,10) + ".xlsx");
      try { logActivity("export", {dashboardName: "pivot", exportFormat: "excel", rowsReturned: _exportedRows}); } catch(e){}
      /* ── Row limit alert ── */
      if (_rowLimit !== Infinity && _exportedRows >= _rowLimit && _totalAvailable > _rowLimit) {
        setLimitMsg("⚠️ Partial export: " + _exportedRows + " of " + _totalAvailable + " rows exported (limit: " + _rowLimit + "). Contact admin to increase.");
      }
      /* ── Increment extract count for current user ── */
      if (!isAdmin && user && setAllUsers) {
        var uid = String(user.userId || user.id || "");
        if (uid) {
          /* Update in database */
          api.incrementExtract(uid).catch(function(e){ console.error(e); });
          /* Update local state */
          setAllUsers(function(prev) {
            return prev.map(function(u) {
              return String(u.id) === uid ? {...u, extractUsed: (u.extractUsed || 0) + 1} : u;
            });
          });
        }
      }
      setExporting(false);
        });
      })
      .catch(function(ex){
        if (ex && ex.message === "__REG_BLOCKED__") {
          setLimitMsg("Export blocked — this export could not be registered with the server. Please check with your administrator.");
        } else {
          console.error("Export error:", ex);
        }
        setExporting(false);
      });
  };

  /* ── Render ── */
  return (
    <div>
      {/* Toolbar */}
      <div style={{padding:"8px 14px",borderBottom:"1px solid #f1f5f9",display:"flex",flexDirection:"column",gap:6}}>

        {/* Row 1: stats badges + sort + export */}
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
          <span style={{fontSize:10,color:"#94a3b8",flex:1}}>
            {isTree
              ? (rawNodes.length+" "+allDimLabels[0]+" groups \u00b7 "+allDims.length+" levels")
              : (rawNodes.length+" "+childLabel+" rows")}
            {sortCfg && <span style={{marginLeft:6,color:(_ptv?"#0F5C66":"#2563eb"),fontWeight:700}}>sorted</span>}
          </span>
          {(showShare||showRank||showGrPrev||showGrYoy) && (
            <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
              {showShare  && <span style={{fontSize:8.5,background:"#dce5ef",color:"#1e3a5f",borderRadius:10,padding:"1px 7px",fontWeight:700}}>Share%</span>}
              {showRank   && <span style={{fontSize:8.5,background:"#e2e8f0",color:"#334155",borderRadius:10,padding:"1px 7px",fontWeight:700}}>Rank</span>}
              {showGrPrev && <span style={{fontSize:8.5,background:"#edf2f7",color:"#475569",borderRadius:10,padding:"1px 7px",fontWeight:700}}>↕ Prev</span>}
              {showGrYoy  && <span style={{fontSize:8.5,background:"#edf2f7",color:"#475569",borderRadius:10,padding:"1px 7px",fontWeight:700}}>↕ Similar</span>}
            </div>
          )}
          {sortCfg && (
            <button onClick={function(){setSortCfg(null);}} style={{padding:"3px 9px",border:"1.5px solid #fca5a5",borderRadius:5,background:"#fef2f2",fontSize:10,cursor:"pointer",fontFamily:"inherit",color:"#dc2626",fontWeight:700}}>
              {"✕"} Clear Sort
            </button>
          )}
          <button onClick={doExport} disabled={exporting || limitReached}
            title={exportBlocked ? "Export is disabled for your organization. Contact your administrator." : (limitReached ? "Export limit reached for this quarter. Contact your administrator." : (isAdmin ? "Admin — unlimited exports" : "Export available" + (rowExportLimit!==Infinity?" · Max rows per export limit applies":"")))}
            style={{padding:"4px 12px",border:"1.5px solid "+(limitReached?"#fca5a5":"#16a34a"),borderRadius:5,
                    background:limitReached?"#fef2f2":(exporting?"#f0fdf4":"#dcfce7"),fontSize:11,
                    cursor:(exporting||limitReached)?"not-allowed":"pointer",
                    fontFamily:"inherit",color:limitReached?"#dc2626":"#15803d",fontWeight:700,
                    opacity:limitReached?0.8:1}}>
            {exportBlocked ? "🔒 Export Disabled" : (limitReached ? "\uD83D\uDEAB Limit Reached" : (exporting?"⏳ Exporting…":"\u2193 Export Excel"))}
          </button>
          {/* Grid Export — raw rows (not pivoted). Only shown if user has AllowGridExport.
              Hidden entirely (not just disabled) when permission is off, to keep the toolbar clean. */}
          {(isAdmin || !gridExportBlocked) && (
            <button onClick={doGridExport} disabled={gridExporting || (!isAdmin && limitReached)}
              title={gridExportBlocked ? "Grid export is not enabled for your account." : "Export raw rows (not pivoted) — selected dims + measures × selected periods" + ((!isAdmin && rowExportLimit !== Infinity) ? " · max " + rowExportLimit.toLocaleString() + " rows" : "")}
              style={{padding:"4px 12px",border:"1.5px solid "+(_ptv?"#0F5C66":"#2563eb"),borderRadius:5,
                      background:gridExporting?(_ptv?"#EAF4F5":"#eff6ff"):(_ptv?"#CFE7EA":"#dbeafe"),fontSize:11,
                      cursor:gridExporting?"not-allowed":"pointer",
                      fontFamily:"inherit",color:(_ptv?"#0F5C66":"#1d4ed8"),fontWeight:700,
                      opacity:(gridExporting || (!isAdmin && limitReached))?0.7:1}}>
              {gridExporting ? "⏳ Exporting…" : "\u25A6 Grid Export"}
            </button>
          )}
          {/* Per-user remaining count + per-export row limit are intentionally not shown to end users —
              numeric quotas can feel pressuring; the disabled state is communicated via the button label/title. */}
        </div>
        {/* Grid export error message (rendered just below toolbar to avoid layout shift) */}
        {gridErrMsg && (
          <div style={{marginTop:6,padding:"6px 12px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:5,fontSize:10.5,color:"#991b1b",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
            <span>{gridErrMsg}</span>
            <button onClick={function(){setGridErrMsg("");}} style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:"#991b1b",lineHeight:1,padding:0}}>×</button>
          </div>
        )}

        {/* Row 2: per-level collapse buttons */}
        {isTree && (
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <span style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em"}}>Collapse:</span>
            <button onClick={togAll} style={{padding:"2px 8px",border:"1.5px solid #e2e8f0",borderRadius:4,background:allCollapsed?(_ptv?"#17707C":"#3880bb"):"#fff",fontSize:10,cursor:"pointer",fontFamily:"inherit",color:allCollapsed?"#fff":"#334155",fontWeight:700}}>
              {allCollapsed?"\u2295 All":"\u2296 All"}
            </button>
            {allDims.map(function(d,i){
              if (i===allDims.length-1) return null;
              var isAllCol=allAtDepthCollapsed(i);
              var isLazyLevel = i >= dims.length - 1; /* levels beyond loaded data */
              /* Button at index i reveals the children at depth i+1, so the label
                 should name THAT level (e.g., clicking "L2: Product Name" reveals
                 the product rows under each manufacturer). */
              var revealedLevel = i + 2; /* 1-indexed level that becomes visible */
              var revealedLabel = allDimLabels[i + 1];
              return (
                <button key={d} onClick={function(dep){return function(){togLevel(dep);};}(i)}
                  title={(isAllCol ? "Show " : "Hide ") + revealedLabel + " (level " + revealedLevel + ") for all rows"}
                  style={{padding:"2px 8px",border:"1.5px solid "+(LCOLORS[i]||"#e2e8f0"),borderRadius:4,
                          background:isAllCol?(LCOLORS[i]||"#64748b"):"#fff",fontSize:10,cursor:"pointer",
                          fontFamily:"inherit",color:isAllCol?"#fff":(LCOLORS[i]||"#334155"),fontWeight:700,
                          opacity:isLazyLevel?0.7:1}}>
                  {isAllCol?"\u2295":"\u2296"} L{revealedLevel}: {revealedLabel}
                  {isLazyLevel&&" ↓"}
                </button>
              );
            })}
          </div>
        )}

        {/* Row 3: level legend */}
        {allDims.length>1 && (
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <span style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginRight:2}}>Reorder:</span>
            {allDims.map(function(d,i){
              return (
                <span key={d}
                  draggable={!!onReorderDims}
                  onDragStart={function(e){
                    if (!onReorderDims) return;
                    try {
                      e.dataTransfer.setData("application/x-amsx-pivotdim", d);
                      e.dataTransfer.setData("text/plain", d);
                      e.dataTransfer.effectAllowed = "move";
                    } catch(err) {}
                    e.currentTarget.style.opacity = "0.45";
                  }}
                  onDragEnd={function(e){ e.currentTarget.style.opacity = "1"; }}
                  onDragOver={function(e){
                    if (!onReorderDims) return;
                    e.preventDefault(); e.dataTransfer.dropEffect="move";
                    e.currentTarget.style.background=(_ptv?"#CFE7EA":"#dbeafe");
                    e.currentTarget.style.borderColor=(_ptv?"#0F5C66":"#2563eb");
                  }}
                  onDragLeave={function(e){
                    e.currentTarget.style.background="#fff";
                    e.currentTarget.style.borderColor="#cbd5e1";
                  }}
                  onDrop={function(e){
                    if (!onReorderDims) return;
                    e.preventDefault();
                    e.currentTarget.style.background="#fff";
                    e.currentTarget.style.borderColor="#cbd5e1";
                    var dragKey = "";
                    try { dragKey = e.dataTransfer.getData("application/x-amsx-pivotdim") || e.dataTransfer.getData("text/plain") || ""; } catch(err) {}
                    if (dragKey && dragKey !== d) onReorderDims(dragKey, d);
                  }}
                  title={onReorderDims ? "Drag onto another dimension to reorder the pivot hierarchy" : ""}
                  style={{
                    display:"inline-flex",alignItems:"center",gap:4,
                    padding:"2px 7px",borderRadius:5,
                    background:"#fff", border:"1.5px solid #cbd5e1",
                    cursor: onReorderDims ? "grab" : "default",
                    userSelect:"none", transition:"background .12s, border-color .12s",
                  }}>
                  {onReorderDims && <span style={{color:"#94a3b8",fontSize:10,lineHeight:1}}>⋮⋮</span>}
                  <span style={{
                    background:(_ptv?"#0F5C66":"#2563eb"),color:"#fff",borderRadius:3,fontSize:8,fontWeight:800,
                    width:13,height:13,display:"flex",alignItems:"center",justifyContent:"center"
                  }}>{i+1}</span>
                  <span style={{fontSize:10,fontWeight:700,color:"#1e293b"}}>{FEAT_LBL[d]||d}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Extract limit warning banner */}
      {limitMsg && (
        <div style={{padding:"8px 14px",background:"#fef2f2",borderBottom:"1px solid #fecaca",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:16}}>🚫</span>
          <span style={{fontSize:11,color:"#991b1b",fontWeight:600,flex:1}}>{limitMsg}</span>
          <button onClick={function(){setLimitMsg("");}} style={{background:"none",border:"none",color:"#dc2626",cursor:"pointer",fontSize:14,fontFamily:"inherit",padding:"2px 6px"}}>✕</button>
        </div>
      )}
      {limitReached && !limitMsg && (
        <div style={{padding:"6px 14px",background:"#f8fafc",borderBottom:"1px solid #e2e8f0",display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:12}}>⚠️</span>
          <span style={{fontSize:10,color:"#475569",fontWeight:600}}>
            {"Your export limit for this quarter has been reached" + (extractResetsOn ? " — it resets on " + extractResetsOn : "") + ". Contact your administrator to increase your allowance."}
          </span>
        </div>
      )}

      {/* Table — single table, ref-based dynamic sticky offsets */}
      {(() => {
        /* Compound headers — TWO families share the same 3-row structure:
           · Kimadia tender: "2025_Awarded"    → Year band / Status sub-band / measures
           · Sales Tracker:  "2026-Q1~Sell-In" → Period band / Flow sub-band / measures */
        var _isFlowHdr = periods.length > 0 && String(periods[0]).indexOf("~") > 0;
        var isCompound = (periods.length > 0 && periods[0].indexOf("_") > 0) || _isFlowHdr;
        var _cSep = _isFlowHdr ? "~" : "_";

        var theadRows;
        if (isCompound) {
          /* Flow keys arrive already ordered (base chronology × flow) from the
             sortedPeriods memo — re-sorting only applies to tender year keys. */
          var sortedCompound = _isFlowHdr ? periods.slice() : periods.slice().sort(function(a, b) {
            var pa = a.split("_"), pb = b.split("_");
            var ya = parseInt(pa[0]), yb = parseInt(pb[0]);
            if (ya !== yb) return periodAsc ? ya - yb : yb - ya;
            return pa[1] < pb[1] ? -1 : pa[1] > pb[1] ? 1 : 0;
          });
          var yearGroups = [];
          var currentYear = null; var currentGroup = null;
          sortedCompound.forEach(function(pk) {
            var _sx = String(pk).indexOf(_cSep);
            var yr = _sx >= 0 ? String(pk).slice(0, _sx) : String(pk);
            var status = _sx >= 0 ? String(pk).slice(_sx + 1) : "";
            if (yr !== currentYear) { currentYear = yr; currentGroup = {year: yr, statuses: []}; yearGroups.push(currentGroup); }
            currentGroup.statuses.push({key: pk, status: status});
          });
          /* Soft, readable status colors: light tinted headers with dark text.
             The same family tints the body cells of each status column below.
             NO red/green here — those carry growth/decline meaning in this
             platform, so statuses use blue / violet / amber instead.
             Sell-In / Sell-Out reuse the Awarded / Delivered pair. */
          var statusColors = function(s) {
            if (_ptv && s === "Sell-In")  return {bg:"#E3EFF1", co:"#0F5C66", bd:"#C3DCE0"};
            if (_ptv && s === "Sell-Out") return {bg:"#DFF3F4", co:"#1E6E7A", bd:"#BFE3E6"};
            if (s === "Awarded" || s === "Sell-In") return {bg:"#e3edf8", co:(_ptv?"#0C4750":"#1e4e79"), bd:"#c3d8ee"};
            if (s === "Delivered" || s === "Sell-Out") return {bg:"#ece7f6", co:"#5b21b6", bd:"#d6cbec"};
            if (s === "Pending") return {bg:"#faf0d7", co:"#92400e", bd:"#ecd9a8"};
            return {bg:"#e8edf2", co:"#334155", bd:"#cbd5e1"};
          };
          var sortedHdr2 = [];
          var _compCellsPerStatus = 0; var _compFirst = true;
          var _compSpanOf = {}; /* per compound key — Sell-Out bands carry extra Profit / Sell-Through columns */
          sortedCompound.forEach(function(pk) {
            var _cStart = sortedHdr2.length;
            activeMsrs.forEach(function(mk) {
              sortedHdr2.push(<th key={pk+"_"+mk.m+"v"} onClick={function(p2,m2){return function(){handleSort(p2,m2,"val");};}(pk,mk.m)} style={{background:_hdrBg,color:"#fff",fontSize:9.5,fontWeight:700,textTransform:"uppercase",textAlign:"center",padding:"8px 6px",border:"1px solid "+_hdrBr,whiteSpace:"nowrap",cursor:"pointer",userSelect:"none",letterSpacing:".05em"}}>{mk.label}{SA(pk,mk.m,"val")}</th>);
              if (!mk.noStats && shareFor(mk.m))  sortedHdr2.push(<th key={pk+"_"+mk.m+"s"} onClick={function(p2,m2,ms2){return function(){handleSort(p2,m2,"share",ms2);};}(pk,mk.m,mk.ms)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>% Share <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span>{SA(pk,mk.m,"share")}</th>);
              if (!mk.noStats && rankFor(mk.m))   sortedHdr2.push(<th key={pk+"_"+mk.m+"r"} onClick={function(p2,m2){return function(){handleSort(p2,m2,"rank");};}(pk,mk.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>Rnk <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span>{SA(pk,mk.m,"rank")}</th>);
              if (!mk.noStats && grPrevFor(mk.m)) sortedHdr2.push(<th key={pk+"_"+mk.m+"gp"} onClick={function(p2,m2){return function(){handleSort(p2,m2,"grPrev");};}(pk,mk.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>Gth <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span>{SA(pk,mk.m,"grPrev")}</th>);
              if (!mk.noStats && grYoyFor(mk.m))  sortedHdr2.push(<th key={pk+"_"+mk.m+"gy"} onClick={function(p2,m2){return function(){handleSort(p2,m2,"grYoy");};}(pk,mk.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>Gth Similar <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span>{SA(pk,mk.m,"grYoy")}</th>);
              if (!mk.noStats && evoPrevFor(mk.m)) sortedHdr2.push(<th key={pk+"_"+mk.m+"ei"} title="Evolution Index vs previous period — 100 = grew with the market" onClick={function(p2,m2){return function(){handleSort(p2,m2,"evo");};}(pk,mk.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>EI <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span>{SA(pk,mk.m,"evo")}</th>);
              if (!mk.noStats && evoYoyFor(mk.m))  sortedHdr2.push(<th key={pk+"_"+mk.m+"eiy"} title="Evolution Index vs similar period last year — 100 = grew with the market" onClick={function(p2,m2){return function(){handleSort(p2,m2,"evoY");};}(pk,mk.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>EI Similar <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span>{SA(pk,mk.m,"evoY")}</th>);
              if (pfStatFor(mk.m, pk)) {
                sortedHdr2.push(<th key={pk+"_"+mk.m+"csq"} title="Sell-Out Value — the sold units at the SELL-OUT (retail) price; the Value column is at the sell-in price" style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",whiteSpace:"nowrap"}}>Sell-Out Value <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span></th>);
                sortedHdr2.push(<th key={pk+"_"+mk.m+"pfq"} title={"Profit — Sell-Out Value − Value (" + (mk.m === "d" ? "USD" : "LC") + ")"} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",whiteSpace:"nowrap"}}>Profit <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span></th>);
              }
              if (sthFor(mk.m, pk)) sortedHdr2.push(<th key={pk+"_"+mk.m+"stq"} title="Sell-Through % — sell-out ÷ sell-in of this measure for the same period" style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 5px",whiteSpace:"nowrap"}}>Sell-Through <span style={{opacity:.65,fontSize:7.5}}>{mk.tag||""}</span></th>);
            });
            _compSpanOf[pk] = sortedHdr2.length - _cStart;
            if (_compFirst) { _compCellsPerStatus = sortedHdr2.length - _cStart; _compFirst = false; }
          });
          var compSpan = _compCellsPerStatus;
          if (showCAGR && cagrMsrs.length > 0) {
            statMsrs.forEach(function(mk) {
              sortedHdr2.push(<th key={"cagr_"+mk.m} onClick={function(m2){return function(){handleSort("__cagr__",m2,"cagr");};}(mk.m)} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 6px",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap",letterSpacing:".03em"}}>CAGR {mk.label}{SA("__cagr__",mk.m,"cagr")}</th>);
            });
          }
          /* CURRENT STOCK sub-header (3-row compound header) */
          if (stockOn) {
            stockBases.forEach(function(sb) {
              sortedHdr2.push(<th key={"stk_h_"+sb} style={{background:_hdrBg,color:(_ptv?"#CFE7EA":"#dbeafe"),border:"1px solid "+_hdrBr,fontSize:8.5,fontWeight:700,textAlign:"center",padding:"8px 8px",minWidth:76,whiteSpace:"nowrap",letterSpacing:".03em"}}>{sb === "ALL" ? "All data" : "as of " + periodLabel9(sb)}</th>);
            });
          }
          theadRows = [
            <tr key="h1">
              <th rowSpan={3} style={{background:(_ptv?"#17707C":"#3880bb"),color:"#fff",fontSize:10,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase",textAlign:"left",padding:"8px 14px",maxWidth:360,width:360,overflow:"hidden",borderRight:"2px solid "+(_ptv?"#0F5C66":"#2d6d9e"),border:"1px solid "+(_ptv?"#17707C":"#3880bb"),whiteSpace:"nowrap"}}>
                {isTree?allDimLabels.join(" \u2192 "):(allDimLabels[0]||childLabel)}
              </th>
              {yearGroups.map(function(yg) {
                return <th key={yg.year} colSpan={yg.statuses.reduce(function(_a, _st){ return _a + (_compSpanOf[_st.key] || compSpan); }, 0)}
                  onClick={function(){ setPeriodAsc(function(p){ return !p; }); }}
                  style={{background:(_ptv?"#17707C":"#3880bb"),color:"#fff",fontSize:11,fontWeight:700,textAlign:"center",padding:"8px 10px",border:"1px solid "+(_ptv?"#0F5C66":"#2d6d9e"),whiteSpace:"nowrap",cursor:"pointer",userSelect:"none",letterSpacing:".04em"}}>
                  {_isFlowHdr ? periodLabel9(yg.year) : yg.year} {yg.year === yearGroups[0].year ? (periodAsc?"▲":"▼") : ""}
                </th>;
              })}
              {(showCAGR && cagrMsrs.length > 0) && <th rowSpan={2} colSpan={cagrMsrs.length} style={{background:(_ptv?"#17707C":"#3880bb"),color:"#e8f4ff",fontSize:10,fontWeight:700,textAlign:"center",padding:"8px 10px",border:"1px solid "+(_ptv?"#0F5C66":"#2d6d9e"),whiteSpace:"nowrap",letterSpacing:".04em"}}>CAGR ({cagrN})</th>}
              {stockOn && <th rowSpan={2} colSpan={stockBases.length} style={{background:(_ptv?"#0C4750":"#1e4e79"),color:"#e8f4ff",fontSize:10,fontWeight:800,textAlign:"center",padding:"8px 10px",border:"1px solid "+(_ptv?"#0F5C66":"#2d6d9e"),whiteSpace:"nowrap",letterSpacing:".06em"}}>CURRENT STOCK</th>}
            </tr>,
            <tr key="h2">
              {yearGroups.map(function(yg) {
                return yg.statuses.map(function(st) {
                  var sc = statusColors(st.status);
                  return <th key={st.key} colSpan={_compSpanOf[st.key] || compSpan} style={{background:sc.bg,color:sc.co,fontSize:9.5,fontWeight:700,textAlign:"center",padding:"3px 6px",border:"1px solid "+sc.bd,whiteSpace:"nowrap"}}>{st.status}</th>;
                });
              })}
            </tr>,
            <tr key="h3">{sortedHdr2}</tr>
          ];
        } else {
          /* ── Multi-type queries: an extra TOP band row groups the period columns
             by their type (Monthly / Quarterly / Full Year / MAT / YTD). Each
             band spans its periods' full column blocks; runs stay contiguous
             because sortedPeriods orders by type first and reversal preserves
             grouping. Single-type queries render the classic 2-row header. ── */
          var _pTypes = data.periodTypes || {};
          var _bandRuns = [];
          if (data.multiType) {
            for (var _bri = 0; _bri < periods.length; _bri++) {
              var _bt = _pTypes[periods[_bri]] || "";
              if (_bandRuns.length > 0 && _bandRuns[_bandRuns.length-1].t === _bt) _bandRuns[_bandRuns.length-1].n++;
              else _bandRuns.push({t:_bt, n:1});
            }
          }
          var _hasBands = _bandRuns.length > 1;
          var _bandColor = function(t) {
            if (_ptv) {
              /* (retired, _ptv=false) themed family per period type, amber for rolling windows */
              if (t === "Monthly") return "#2E8792";
              if (t === "Quarterly") return "#17707C";
              if (t === "Yearly") return "#0C4750";
              if (t === "MAT" || t === "MAT Monthly") return "#8A5A16";
              return "#33606B";
            }
            if (t === "Monthly") return "#0e7490";
            if (t === "Quarterly") return (_ptv?"#0F5C66":"#2563eb");
            if (t === "Yearly") return "#1e3a5f";
            if (t === "MAT") return "#9d174d";
            if (t === "MAT Monthly") return "#be185d";
            if (t === "YTD") return "#5b21b6";
            if (t === "YTD Monthly") return "#6d28d9";
            return "#475569";
          };
          /* msa: a source band row between the period header and the measure
             headers — "Pharma · sales data" spanning the pharma measure columns,
             "Rx · prescription data" spanning the Rx ones (kimadia Awarded /
             Delivered band styling). Only when BOTH families are visible. */
          var _msaColsOf = function(isRx){ var _n9=0; for (var _mi9=0;_mi9<activeMsrs.length;_mi9++){ var _m9=activeMsrs[_mi9]; if (!!_msaRxM[_m9.m] !== isRx) continue; _n9 += 1 + (_m9.noStats ? 0 : statColsFor(_m9.m)); } return _n9; };
          var _msaPhCols = _msaTv ? _msaColsOf(false) : 0;
          var _msaRxCols = _msaTv ? _msaColsOf(true) : 0;
          var _msaBand = _msaTv && _msaPhCols > 0 && _msaRxCols > 0;
          var _dimTh = (
            <th key="dimth" rowSpan={(_hasBands ? 3 : 2) + (_msaBand ? 1 : 0)} style={{background:(_ptv?"#17707C":"#3880bb"),color:"#fff",fontSize:10,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase",textAlign:"left",padding:"8px 14px",maxWidth:360,width:360,overflow:"hidden",borderRight:"2px solid "+(_ptv?"#0F5C66":"#2d6d9e"),border:"1px solid "+(_ptv?"#17707C":"#3880bb"),whiteSpace:"nowrap"}}>
              {isTree?allDimLabels.join(" \u2192 "):(allDimLabels[0]||childLabel)}
            </th>
          );
          theadRows = [];
          if (_hasBands) {
            theadRows.push(
              <tr key="h0">
                {_dimTh}
                {_bandRuns.map(function(run, _bi){
                  return <th key={"band"+_bi} colSpan={run.n * span}
                    style={{background:_bandColor(run.t),color:"#fff",fontSize:10,fontWeight:800,textAlign:"center",padding:"6px 10px",border:"1px solid rgba(255,255,255,.28)",whiteSpace:"nowrap",letterSpacing:".08em",textTransform:"uppercase"}}>
                    {(PERIOD_TYPE_META[run.t] && PERIOD_TYPE_META[run.t].label) || run.t || "Period"}
                  </th>;
                })}
              </tr>
            );
          }
          theadRows.push(
            <tr key="h1">
              {!_hasBands && _dimTh}
              {periods.map(function(pk, pi){
                return <th key={pk} colSpan={span} onClick={function(){ setPeriodAsc(function(p){ return !p; }); }}
                  style={{background:(_ptv?"#17707C":"#3880bb"),color:"#fff",fontSize:11,fontWeight:800,textAlign:"center",padding:"8px 14px",border:"1px solid "+(_ptv?"#0F5C66":"#2d6d9e"),whiteSpace:"nowrap",cursor:"pointer",userSelect:"none",letterSpacing:".04em"}}>
                  {periodLabel9(pk)}{pi===0 ? " "+(periodAsc?"▲":"▼") : ""}
                </th>;
              })}
              {(showCAGR && cagrMsrs.length > 0) && <th colSpan={cagrMsrs.length} style={{background:(_ptv?"#17707C":"#3880bb"),color:"#e8f4ff",fontSize:10.5,fontWeight:700,textAlign:"center",padding:"8px 14px",border:"1px solid "+(_ptv?"#0F5C66":"#2d6d9e"),whiteSpace:"nowrap",letterSpacing:".04em"}}>CAGR ({cagrN} periods)</th>}
              {stockOn && <th colSpan={stockBases.length} style={{background:(_ptv?"#0C4750":"#1e4e79"),color:"#e8f4ff",fontSize:10.5,fontWeight:800,textAlign:"center",padding:"8px 14px",border:"1px solid "+(_ptv?"#0F5C66":"#2d6d9e"),whiteSpace:"nowrap",letterSpacing:".06em"}}>CURRENT STOCK</th>}
            </tr>
          );
          if (_msaBand) theadRows.push(
            <tr key="hmsa">
              {periods.map(function(pk){ return [
                <th key={pk+"_bph"} colSpan={_msaPhCols} style={{background:"#e0f2e9",color:"#0F6E56",fontSize:9.5,fontWeight:800,textAlign:"center",padding:"3px 6px",border:"1px solid #bfe3d2",whiteSpace:"nowrap",letterSpacing:".05em"}}>{"Pharma"}</th>,
                <th key={pk+"_brx"} colSpan={_msaRxCols} style={{background:"#e3edf8",color:"#1e4e79",fontSize:9.5,fontWeight:800,textAlign:"center",padding:"3px 6px",border:"1px solid #c3d8ee",whiteSpace:"nowrap",letterSpacing:".05em"}}>{"Rx"}</th>
              ]; })}
              {(showCAGR && cagrMsrs.length > 0) && <th colSpan={cagrMsrs.length} style={{background:"#e8edf2",border:"1px solid #cbd5e1"}} />}
              {stockOn && <th colSpan={stockBases.length} style={{background:"#e8edf2",border:"1px solid #cbd5e1"}} />}
            </tr>
          );
          theadRows.push(<tr key="h2">{hdr2Cells}</tr>);
        }

        return (
          <div ref={scrollContainerRef}
               onScroll={_onPivotScroll}
               style={{overflow:"auto",maxHeight:"calc(100vh - 180px)",fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif"}}>
            <table ref={pivotTableRef} style={{borderCollapse:"separate",borderSpacing:0,fontSize:12}}>
              <thead>
                {theadRows}
                {gtRow}
              </thead>
              <tbody>{tableRows}</tbody>
            </table>
            {/* Pagination footer — shows when more L1 rows exist than currently rendered.
                Auto-loads on scroll; user can also click to expand the next batch. */}
            {hasMoreRows && (
              <div style={{padding:"12px 16px", textAlign:"center", borderTop:"1px solid #e2e8f0", background:"#f8fafc"}}>
                <span style={{fontSize:11, color:"#64748b"}}>
                  Showing {visibleNodeCount.toLocaleString()} of {displayNodes.length.toLocaleString()} {(FEAT_LBL[allDims[0]]||allDims[0]).toLowerCase()} rows
                </span>
                <button onClick={function(){
                  setRenderRowLimit(function(p){
                    var maxL1 = (displayNodes||[]).length || 0;
                    return Math.min(p + ROW_LIMIT_STEP, maxL1);
                  });
                }}
                  style={{marginLeft:14, padding:"4px 12px", border:"1px solid #cbd5e1", borderRadius:5, background:"#fff", color:(_ptv?"#0F5C66":"#2563eb"), fontSize:10.5, fontWeight:700, cursor:"pointer"}}>
                  Show next {Math.min(ROW_LIMIT_STEP, displayNodes.length - visibleNodeCount)} ↓
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Expand-All confirmation modal ──
         Triggered when togAll or togLevel detects a heavy expansion (many parents + heavy dim
         in deeper levels + no Top N). Three actions:
         - Apply Top 100 (recommended, default) — auto-sets Top N=100 + Show Others, re-runs query
         - Show All (slow) — full expand of every parent, shows the "please be patient" overlay
         - Cancel */}
      {expandPrompt && (
        <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999}}
             onClick={function(){ setExpandPrompt(null); }}>
          <div onClick={function(e){ e.stopPropagation(); }}
               style={{background:"#fff", borderRadius:10, maxWidth:540, width:"92%", padding:"22px 24px", boxShadow:"0 25px 60px rgba(0,0,0,.4)"}}>
            <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:14}}>
              <div style={{fontSize:28}}>📊</div>
              <div>
                <div style={{fontSize:15, fontWeight:800, color:"#0f172a"}}>Large expansion — pick a strategy</div>
                <div style={{fontSize:10.5, color:"#64748b", marginTop:2}}>
                  {expandPrompt.parents} {(FEAT_LBL[allDims[0]]||allDims[0])} groups × {expandPrompt.heavyDims.join(" / ")}
                </div>
              </div>
            </div>
            <div style={{fontSize:12, color:"#334155", lineHeight:1.55, marginBottom:16}}>
              Expanding every row across {expandPrompt.heavyDims.join(", ")} fires many simultaneous queries.
              How would you like to proceed?
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:8, marginBottom:18}}>
              <div style={{padding:"10px 12px", background:(_ptv?"#EAF4F5":"#eff6ff"), borderRadius:7, border:"1.5px solid "+(_ptv?"#0F5C66":"#2563eb")}}>
                <div style={{fontSize:11.5, fontWeight:800, color:(_ptv?"#0F5C66":"#1d4ed8"), marginBottom:3}}>✓ Recommended: Top 100 with "Others"</div>
                <div style={{fontSize:10.5, color:"#1e40af", lineHeight:1.5}}>
                  Show the top 100 {(FEAT_LBL[allDims[0]]||allDims[0]).toLowerCase()} groups (ranked by your sort) and a single
                  "Others" bucket aggregating the rest. Fast, complete, no waiting.
                </div>
              </div>
              <div style={{padding:"10px 12px", background:"#fef3c7", borderRadius:7, border:"1px solid #fde68a"}}>
                <div style={{fontSize:11.5, fontWeight:700, color:"#92400e", marginBottom:3}}>⏳ Show All ({expandPrompt.parents} rows)</div>
                <div style={{fontSize:10.5, color:"#78350f", lineHeight:1.5}}>
                  Load every row in full. <strong>This may take a minute or longer</strong> — please be patient while
                  the system processes many queries in parallel.
                </div>
              </div>
            </div>
            <div style={{display:"flex", gap:8, justifyContent:"flex-end", flexWrap:"wrap"}}>
              <button onClick={function(){ setExpandPrompt(null); }}
                style={{padding:"8px 14px", border:"1px solid #cbd5e1", borderRadius:6, background:"#fff", color:"#475569", fontSize:11.5, fontWeight:600, cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={function(){
                  /* "Show All" path — bypass guard and fire full expand */
                  var mode = expandPrompt.mode, dep = expandPrompt.depth;
                  setExpandPrompt(null);
                  if (mode === "togAll") runExpandAll();
                  else if (mode === "togLevel") togLevel(dep, true);
                }}
                style={{padding:"8px 14px", border:"none", borderRadius:6, background:"#d97706", color:"#fff", fontSize:11.5, fontWeight:700, cursor:"pointer"}}>
                Show All (slow)
              </button>
              <button onClick={function(){
                  /* "Top 100 + Others" path — call up to App-level to apply Top N and re-run. */
                  setExpandPrompt(null);
                  if (onApplyTopN) {
                    onApplyTopN({n:100, showOthers:true});
                  } else {
                    /* Defensive: parent didn't wire the callback. Tell the user how to do it manually. */
                    try { alert("Open the Top N Filter in the filter panel, set N=100 with Show Others, then click Run Query."); } catch(e){}
                  }
                }}
                style={{padding:"8px 14px", border:"none", borderRadius:6, background:(_ptv?"#0F5C66":"#2563eb"), color:"#fff", fontSize:11.5, fontWeight:800, cursor:"pointer", boxShadow:"0 2px 6px rgba(37,99,235,.4)"}}>
                ✓ Apply Top 100
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Expanding... please be patient" overlay — non-blocking but informative */}
      {expanding && (
        <div style={{position:"fixed", bottom:20, right:20, background:"#1e293b", color:"#fff",
                     padding:"12px 18px", borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,.3)",
                     display:"flex", alignItems:"center", gap:10, zIndex:9998, maxWidth:340}}>
          <div style={{width:18, height:18, border:"2.5px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin 0.8s linear infinite"}} />
          <div>
            <div style={{fontSize:12, fontWeight:700, marginBottom:2}}>Expanding all rows…</div>
            <div style={{fontSize:10, color:"#cbd5e1"}}>This may take a minute. Please be patient — data is loading in the background.</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   EXCEL UPLOADER
════════════════════════════════════════════════════════════ */
function Uploader({ onDone }) {
  const [step,  setStep]  = useState("drop");
  const [drag,  setDrag]  = useState(false);
  const [file,  setFile]  = useState(null);
  const [rawFile, setRawFile] = useState(null); /* keep original File object for API upload */
  const [cols,  setCols]  = useState([]);
  const [rows,  setRows]  = useState([]);
  const [map,   setMap]   = useState({});
  const [err,   setErr]   = useState("");
  const [busy,  setBusy]  = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  const parse = useCallback(function(f) {
    if (!f) return;
    setBusy(true); setErr("");
    setRawFile(f); /* save for DB upload */
    getXLSX().then(function(XLSX) {
      return f.arrayBuffer().then(function(buf) {
        var wb   = XLSX.read(new Uint8Array(buf), {type:"array"});
        var ws   = wb.Sheets[wb.SheetNames[0]];
        var data = XLSX.utils.sheet_to_json(ws, {defval:""});
        if (!data.length) { setErr("Sheet is empty."); setBusy(false); return; }
        var c = Object.keys(data[0]);
        setRows(data); setCols(c); setMap(autoMap(c));
        setFile({name:f.name, count:data.length}); setStep("map");
        setBusy(false);
      });
    }).catch(function(ex) {
      setErr("Could not read file. " + (ex.message || ""));
      setBusy(false);
    });
  }, []);

  const confirm = () => {
    if (!map.sector)                              { setErr("Map the Sector column.");         return; }
    if (!map.units && !map.valLC && !map.valUSD && !map.priceLC && !map.priceUSD)  { setErr("Map at least one measure column.");      return; }

    /* Upload to database via API */
    if (rawFile) {
      setBusy(true);
      setUploadMsg("Saving to database…");
      api.uploadPharmaData(rawFile, map)
        .then(function(r) {
          setUploadMsg("✅ Saved " + (r.rowsInserted || rows.length) + " rows to database.");
          setBusy(false);
          /* Also load locally for immediate use */
          onDone({rows, mapping:map, fileName:file.name});
        })
        .catch(function(e) {
          setUploadMsg("⚠️ DB upload failed (" + (e.message || "network error") + "). Using local data.");
          setBusy(false);
          /* Still allow local usage even if DB upload fails */
          onDone({rows, mapping:map, fileName:file.name});
        });
    } else {
      onDone({rows, mapping:map, fileName:file.name});
    }
  };

  const wrapSt = {minHeight:"100vh", background:"linear-gradient(135deg,#f0f5ff,#f8fafc 60%,#f0fdf4)", display:"flex", alignItems:"center", justifyContent:"center", padding:24};
  const cardSt = {background:"#fff", borderRadius:20, padding:"36px 44px", width:"100%", maxWidth:560, boxShadow:"0 8px 40px rgba(0,0,0,.09)"};

  if (step === "drop") return (
    <div style={wrapSt}>
      <div style={cardSt}>
        <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:28}}>
          <AmsxLogo height={28} />
        </div>
        <input id="fu" type="file" accept=".xlsx,.xls,.csv"
          style={{position:"absolute", opacity:0, width:1, height:1, pointerEvents:"none"}}
          onChange={function(e){ parse(e.target.files && e.target.files[0]); }} />
        <label htmlFor="fu"
          onDragOver={function(e){ e.preventDefault(); setDrag(true); }}
          onDragLeave={function(){ setDrag(false); }}
          onDrop={function(e){ e.preventDefault(); setDrag(false); parse(e.dataTransfer.files && e.dataTransfer.files[0]); }}
          style={{
            display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
            border:"2.5px dashed " + (drag ? "#3b82f6" : "#bfdbfe"), borderRadius:14,
            padding:"44px 24px", cursor:"pointer",
            background: drag ? "#eff6ff" : "#f8fbff", textAlign:"center", transition:"all .2s",
          }}>
          {busy
            ? <div style={{fontSize:14, color:"#64748b", fontWeight:600}}>Reading…</div>
            : <>
                <div style={{fontSize:44, marginBottom:12}}>📊</div>
                <div style={{fontSize:15, fontWeight:700, color:"#1e293b", marginBottom:6}}>Drop your Excel file here</div>
                <div style={{fontSize:12, color:"#64748b"}}>or <span style={{color:"#2563eb", fontWeight:700, textDecoration:"underline"}}>click to browse</span></div>
                <div style={{fontSize:10, color:"#94a3b8", marginTop:12, background:"#f1f5f9", borderRadius:5, padding:"3px 12px"}}>.xlsx · .xls · .csv</div>
              </>
          }
        </label>
        {err && <div style={{marginTop:12, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:7, padding:"9px 13px", fontSize:12, color:"#dc2626"}}>{err}</div>}
      </div>
    </div>
  );

  return (
    <div style={wrapSt}>
      <div style={{...cardSt, maxWidth:640}}>
        <div style={{fontSize:14, fontWeight:700, color:"#0f172a", marginBottom:4}}>Map columns — {file.name}</div>
        <div style={{fontSize:11, color:"#64748b", marginBottom:12}}>{file.count.toLocaleString()} rows detected</div>
        <div style={{display:"flex", flexWrap:"wrap", gap:3, marginBottom:14, padding:"8px 10px", background:"#f8fafc", borderRadius:7, border:"1px solid #e2e8f0"}}>
          <div style={{fontSize:9, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em", width:"100%", marginBottom:2}}>Columns found</div>
          {cols.map(c => <span key={c} style={{background:"#e2e8f0", color:"#475569", borderRadius:4, padding:"2px 6px", fontSize:10.5, fontWeight:600}}>{c}</span>)}
        </div>
        <div style={{maxHeight:340, overflowY:"auto", marginBottom:12}}>
          {FIELD_DEFS.map(({key, label, req, hint}) => (
            <div key={key} style={{display:"grid", gridTemplateColumns:"145px 1fr", alignItems:"center", gap:10, marginBottom:7}}>
              <div>
                <div style={{fontSize:11.5, fontWeight:700, color:"#334155"}}>{label}{req && <span style={{color:"#e11d48"}}> *</span>}</div>
                {hint && <div style={{fontSize:9.5, color:"#94a3b8"}}>{hint}</div>}
              </div>
              <select value={map[key] || ""} onChange={e => setMap(p => ({...p, [key]: e.target.value}))}
                style={{padding:"6px 9px", border:"1.5px solid #e2e8f0", borderRadius:6, fontSize:12, fontFamily:"inherit", outline:"none", color:"#334155", background:"#fff"}}>
                <option value="">— not mapped —</option>
                {cols.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          ))}
        </div>
        {err && <div style={{background:"#fef2f2", border:"1px solid #fecaca", borderRadius:7, padding:"9px 12px", fontSize:12, color:"#dc2626", marginBottom:10}}>{err}</div>}
        <div style={{display:"flex", gap:10}}>
          <button onClick={() => setStep("drop")}
            style={{padding:"10px 18px", border:"1.5px solid #e2e8f0", borderRadius:8, background:"#fff", color:"#334155", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>
            ← Back
          </button>
          <button onClick={confirm}
            style={{flex:1, padding:"10px", background:"linear-gradient(135deg,#1e40af,#3b82f6)", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>
            Confirm Mapping →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   PORTAL LAUNCH ANNOUNCEMENT
   Shown once per user after login (non-admins). Bump LAUNCH_MSG_VERSION
   to re-show the message to everyone (e.g. "v2" for a future update).
   Edit the wording in the LAUNCH_MSG_* constants below.
════════════════════════════════════════════════════════════ */
var LAUNCH_MSG_VERSION = "v1";
var LAUNCH_MSG_TITLE = "We are officially launching our new portal!";
var LAUNCH_MSG_LINES = [
  "To get your licence to login, kindly do the System Update in Pharma Index as in the email you received.",
  "Your licence then automatically moves to the new portal.",
  "Wishing you an exciting journey!"
];
var LAUNCH_MSG_SIGNOFF = "\u2014 The AMS-Analytix Team";

/* Post-login welcome modal (user already has access — no migration steps here).
   The LAUNCH_MSG_* constants above are shown on the LOGIN PAGE to paused
   accounts and keep the System Update instructions. */
var LAUNCH_WELCOME_TITLE = "Welcome to our new portal!";
var LAUNCH_WELCOME_LINES = [
  "We are officially launching our new portal, and we are excited to have you on board.",
  "Enjoy the new dashboards, reports and analysis tools built for you.",
  "Wishing you an exciting journey!"
];

function launchMsgKey(u) {
  return "amsx_launch_" + LAUNCH_MSG_VERSION + "_" + ((u && (u.userId || u.email)) || "anon");
}

function LaunchAnnouncementModal({ user, onClose }) {
  var firstName = String((user && user.name) || "").trim().split(/\s+/)[0] || "User";
  var close = function() {
    try { localStorage.setItem(launchMsgKey(user), "1"); } catch (e) {}
    onClose();
  };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",backdropFilter:"blur(3px)",zIndex:9500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:18,width:"100%",maxWidth:520,boxShadow:"0 24px 70px rgba(15,23,42,0.35)",overflow:"hidden"}}>
        <div style={{background:"linear-gradient(135deg, #1A3F6E, #2563eb)",padding:"26px 28px 22px",textAlign:"center"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,marginBottom:10}}>
            <img src="/logo.png" alt="" style={{height:38,filter:"brightness(0) invert(1)"}} onError={function(e){ e.target.style.display = "none"; }} />
            <span style={{fontSize:30}}>{"\uD83C\uDF89"}</span>
          </div>
          <div style={{color:"#fff",fontSize:19,fontWeight:800,letterSpacing:0.2,lineHeight:1.35}}>{LAUNCH_WELCOME_TITLE}</div>
        </div>
        <div style={{padding:"24px 30px 8px"}}>
          <div style={{fontSize:14.5,fontWeight:700,color:"#0f172a",marginBottom:12}}>{"Dear " + firstName + ","}</div>
          {LAUNCH_WELCOME_LINES.map(function(line, i) {
            return <div key={i} style={{fontSize:13.5,color:"#334155",lineHeight:1.65,marginBottom:10}}>{line}</div>;
          })}
          <div style={{fontSize:13,color:"#1A3F6E",fontWeight:700,marginTop:14,fontStyle:"italic"}}>{LAUNCH_MSG_SIGNOFF}</div>
        </div>
        <div style={{padding:"18px 30px 24px",textAlign:"center"}}>
          <button onClick={close}
            style={{background:"linear-gradient(135deg, #1A3F6E, #2563eb)",color:"#fff",border:"none",borderRadius:10,padding:"11px 34px",fontSize:14,fontWeight:800,cursor:"pointer",boxShadow:"0 6px 18px rgba(37,99,235,0.35)"}}>
            {"Let's start \uD83D\uDE80"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   LOGIN PAGE
════════════════════════════════════════════════════════════ */
/* r97: partner co-branding for the login page (by subdomain, e.g. iqvia.amsanalytix.com)
   r130: Option A lockup — the partner logo LEADS and AMS-Analytix follows as
   "— POWERED BY <logo> —" underneath (login) or beside (header). Without a
   partner every screen renders the AMS logo exactly as before. The partner logo
   is the Partners record's logo path (e.g. /iqvia-logo.png in client/public,
   served like /logo.png) or an uploaded file under /partners/. */
/* r131: partner logo files often come with a large transparent / white margin
   (IQVIA's is a 600×600 square around a 540×94 wordmark), which made the logo
   look tiny next to the AMS mark at the same CSS height. _trimLogo9 loads the
   file once, crops it to its visible pixels (alpha > 16 and not near-white,
   2 % padding) and returns a PNG data URL; cached per path, fail-open to the
   original URL (cross-origin, SVG without size, any error). The trimmed URLs
   are also written onto session.user.partner (logoTrim / logoDarkTrim) so the
   report decks, the licence page and the Excel band use the same crop. */
var _logoTrim9 = {};
function _trimLogo9(url) {
  if (!url) return Promise.resolve(null);
  if (_logoTrim9[url]) return _logoTrim9[url];
  _logoTrim9[url] = new Promise(function(resolve){
    var done = false, fin = function(v){ if (!done) { done = true; resolve(v || url); } };
    try {
      var im = new Image();
      if (/^https?:\/\//i.test(url) && url.indexOf(window.location.origin) !== 0) im.crossOrigin = "anonymous";
      im.onload = function(){
        try {
          var W = im.naturalWidth, H = im.naturalHeight; if (!W || !H) return fin(null);
          var sc = Math.min(1, 1400 / W, 1400 / H), w = Math.max(1, Math.round(W * sc)), h = Math.max(1, Math.round(H * sc));
          var cv = document.createElement("canvas"); cv.width = w; cv.height = h; var ctx = cv.getContext("2d"); ctx.drawImage(im, 0, 0, w, h);
          var d = ctx.getImageData(0, 0, w, h).data, x0 = w, y0 = h, x1 = -1, y1 = -1, i, x, y;
          for (y = 0; y < h; y++) for (x = 0; x < w; x++) { i = (y * w + x) * 4; if (d[i + 3] > 16 && !(d[i] > 245 && d[i + 1] > 245 && d[i + 2] > 245)) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; } }
          if (x1 < 0 || y1 < 0) return fin(null);
          var px = Math.round((x1 - x0 + 1) * 0.02), py = Math.round((y1 - y0 + 1) * 0.02);
          x0 = Math.max(0, x0 - px); y0 = Math.max(0, y0 - py); x1 = Math.min(w - 1, x1 + px); y1 = Math.min(h - 1, y1 + py);
          var cw = x1 - x0 + 1, ch = y1 - y0 + 1;
          if (cw >= w * 0.97 && ch >= h * 0.97) return fin(null);   /* nothing to trim → keep the file as is */
          var out = document.createElement("canvas"); out.width = cw; out.height = ch; out.getContext("2d").drawImage(cv, x0, y0, cw, ch, 0, 0, cw, ch);
          fin(out.toDataURL("image/png"));
        } catch (e) { fin(null); }
      };
      im.onerror = function(){ fin(null); };
      setTimeout(function(){ fin(null); }, 4000);
      im.src = url;
    } catch (e2) { fin(null); }
  });
  return _logoTrim9[url];
}
function _trimPartner9(p) {
  if (!p) return Promise.resolve(p);
  return Promise.all([_trimLogo9(p.logo), _trimLogo9(p.logoDark)]).then(function(r){ if (r[0]) p.logoTrim = r[0]; if (r[1]) p.logoDarkTrim = r[1]; return p; });
}
function PartnerLogoImg({ partner, height, maxWidth, dark }) {
  var _t = useState(0), _bump = _t[1];
  useEffect(function(){ if (!partner) return; var live = true; _trimPartner9(partner).then(function(){ if (live) _bump(function(n){ return n + 1; }); }); return function(){ live = false; }; }, [partner]);
  if (!partner) return null;
  var src = dark ? (partner.logoDarkTrim || partner.logoDark || partner.logoTrim || partner.logo) : (partner.logoTrim || partner.logo);
  return <span style={{display:"inline-flex", alignItems:"center"}}>
    {src ? <img src={src} alt={partner.name} title={"Partner logo: " + (dark && partner.logoDark ? partner.logoDark : partner.logo)} onError={function(e){ /* missing file → show the name, never a broken icon */ var el = e.currentTarget; el.style.display = "none"; var sp = el.nextSibling; if (sp) sp.style.display = "inline"; }} style={{height:height || 30, width:"auto", maxWidth:maxWidth || 160, display:"block"}} /> : null}
    <span style={{fontSize:Math.round((height || 30) * 0.5), fontWeight:900, color:dark ? "#F0F6FA" : "#005487", whiteSpace:"nowrap", display:src ? "none" : "inline"}}>{partner.name}</span>
  </span>;
}
function PartnerBrandRow({ partner, dark }) {
  if (!partner) return null;
  var rule = { width:38, height:1, background:dark ? "rgba(240,246,250,.35)" : "#C7D8EB", flexShrink:0 };
  return <div style={{display:"flex", flexDirection:"column", alignItems:"center", gap:11}}>
    <PartnerLogoImg partner={partner} height={30} maxWidth={170} dark={dark} />
    <div style={{display:"flex", alignItems:"center", justifyContent:"center", gap:10}}>
      <span style={rule} />
      <span style={{fontSize:9.5, color:dark ? "rgba(240,246,250,.7)" : "#64748b", fontWeight:800, letterSpacing:".16em", textTransform:"uppercase", whiteSpace:"nowrap"}}>powered by</span>
      {dark ? <img src="/logowhite.png" alt="AMS Analytix" style={{height:22, width:"auto", display:"block"}} /> : <AmsxLogo height={22} />}
      <span style={rule} />
    </div>
  </div>;
}
/* r130: header lockup "<partner logo> | POWERED BY <AMS logo>".
   r136 (Noura: "the header is very heavy"): a lighter, quieter lockup — partner
   logo 16 px, hairline, "powered by" in 7.5 px muted caps, AMS logo 20 px, no
   PARTNER ADMIN chip in the portal header (the role is in the tooltip; the
   admin panel header keeps its chip). */
function PartnerHeaderBrand({ partner, dark, admin, suffix, chip }) {
  if (!partner) return null;
  var tip = (admin ? "Partner admin \u2014 " + partner.name + ". " : "") + (partner.terms || "");
  return <div title={tip} style={{display:"flex", alignItems:"center", gap:9, marginRight:2}}>
    {dark
      ? (partner.logoDark
          ? <PartnerLogoImg partner={partner} height={16} maxWidth={96} dark />
          : <span style={{display:"inline-flex", alignItems:"center", background:"#fff", borderRadius:5, padding:"3px 7px", lineHeight:0}}><PartnerLogoImg partner={partner} height={14} maxWidth={86} /></span>)
      : <PartnerLogoImg partner={partner} height={16} maxWidth={96} />}
    <span style={{width:1, height:18, background:dark ? "rgba(240,246,250,.22)" : "#e2e8f0", flexShrink:0}} />
    <span style={{fontSize:7.5, fontWeight:700, letterSpacing:".12em", textTransform:"uppercase", color:dark ? "rgba(240,246,250,.5)" : "#b4c0cc", whiteSpace:"nowrap"}}>powered by</span>
    {dark ? <img src="/logowhite.png" alt="AMS Analytix" style={{height:22, width:"auto", display:"block"}} /> : <AmsxLogo height={20} />}
    {suffix ? <span style={{fontSize:15, fontWeight:800, color:"#F0F6FA", letterSpacing:"-.01em", whiteSpace:"nowrap", marginLeft:6}}>{suffix}</span> : null}
    {(admin && chip) ? <span style={{fontSize:8.5, fontWeight:800, letterSpacing:".06em", textTransform:"uppercase", color:dark ? "#0A2F42" : "#fff", background:dark ? "#9CC3F0" : "#64748b", borderRadius:4, padding:"2px 6px", whiteSpace:"nowrap"}}>partner admin</span> : null}
  </div>;
}
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState(null);   /* r97 */
  useEffect(function(){ api.getBranding().then(function(b){ setBrand(b && b.partner ? b.partner : null); }).catch(function(){}); }, []);
  const [pass,  setPass]  = useState("");
  const [err,   setErr]   = useState("");
  const [busy,  setBusy]  = useState(false);
  /* Portal-launch notice (paused accounts still on Pharma Index) */
  const [launchNote, setLaunchNote] = useState(false);

  /* ── Device pending state ── */
  const [devicePending, setDevicePending] = useState(false);
  const [deviceRevoked, setDeviceRevoked] = useState(false);
  const [pendingEmail, setPendingEmail]   = useState("");
  const [pendingPass,  setPendingPass]    = useState("");
  const [pollTimer, setPollTimer]         = useState(null);
  const [dots, setDots]                   = useState("");

  /* ── Animated dots for waiting screen ── */
  useEffect(function() {
    if (!devicePending) return;
    var iv = setInterval(function() {
      setDots(function(d) { return d.length >= 3 ? "" : d + "."; });
    }, 600);
    return function() { clearInterval(iv); };
  }, [devicePending]);

  /* ── Poll every 5 seconds when device is pending ── */
  useEffect(function() {
    if (!devicePending) return;
    var deviceId = getOrCreateDeviceId();
    var cancelled = false;
    var poll = function() {
      api.pollDevice(deviceId).then(function(res) {
        if (cancelled) return;
        if (res.status === "approved") {
          setDevicePending(false);
          // Re-attempt login automatically
          setBusy(true); setErr("");
          api.login(pendingEmail, pendingPass)
            .then(function(userData) { onLogin(userData); })
            .catch(function(e) { setErr(e.message); setBusy(false); });
        } else if (res.status === "revoked") {
          setDevicePending(false);
          setDeviceRevoked(true);
        }
      }).catch(function() { /* silent retry */ });
    };
    var iv = setInterval(poll, 5000);
    poll(); // immediate first check
    return function() { cancelled = true; clearInterval(iv); };
  }, [devicePending, pendingEmail, pendingPass]);

  const submit = () => {
    if (!email || !pass) { setErr("Please fill both fields."); return; }
    setBusy(true); setErr(""); setDeviceRevoked(false); setLaunchNote(false);
    api.login(email, pass)
      .then(function(userData) {
        console.log("[login] User data received:", JSON.stringify({
          name: userData.name, role: userData.role,
          studyTypes: (userData.studyTypes||[]).length,
          studyAccess: (userData.studyAccess||[]).map(function(sa){ return sa.studyTypeCode + ": ef=" + (sa.effectiveFeatures||[]).length + " ed=" + (sa.effectiveDashboards||[]).length; })
        }));
        onLogin(userData);
      })
      .catch(function(e) {
        if (e.deviceError === "device_pending") {
          setPendingEmail(email);
          setPendingPass(pass);
          setDevicePending(true);
          setBusy(false);
          return;
        }
        if (e.deviceError === "device_revoked") {
          setDeviceRevoked(true);
          setBusy(false);
          return;
        }
        if (e.code === "PORTAL_LAUNCH") { setLaunchNote(true); setBusy(false); return; }
        setErr(e.message || "Invalid credentials or account inactive.");
        setBusy(false);
      });
  };

  const inp = {width:"100%", padding:"11px 13px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", color:"#0f172a", background:"#fff"};

  /* ── Device Pending Waiting Screen ── */
  if (devicePending) {
    return (
      <div style={{minHeight:"100vh", background:"linear-gradient(135deg,#f0f8ff 0%,#f8fafc 40%,#e8f7fa 100%)", display:"flex", alignItems:"center", justifyContent:"center"}}>
        <div style={{background:"#fff", borderRadius:20, padding:"48px 44px", width:420, boxShadow:"0 8px 40px rgba(0,0,0,.09)", textAlign:"center"}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:14}}>{brand ? <PartnerBrandRow partner={brand} /> : <AmsxLogo height={46} />}</div>

          {/* Animated device icon */}
          <div style={{margin:"24px auto 20px", width:72, height:72, borderRadius:"50%", background:"linear-gradient(135deg,#fef3c7,#fde68a)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 20px rgba(217,119,6,.2)"}}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </div>

          <div style={{fontSize:18, fontWeight:800, color:"#0f172a", marginBottom:8}}>New Device Detected</div>
          <div style={{fontSize:13, color:"#64748b", lineHeight:1.6, marginBottom:24}}>
            This device hasn't been authorized yet. Your administrator has been notified and will review your request.
          </div>

          {/* Pulsing wait indicator */}
          <div style={{display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"14px 20px", background:"#fffbeb", borderRadius:12, border:"1px solid #fde68a", marginBottom:20}}>
            <div style={{width:10, height:10, borderRadius:"50%", background:"#f59e0b", animation:"pulse 1.5s infinite"}} />
            <span style={{fontSize:13, fontWeight:600, color:"#92400e"}}>Waiting for approval{dots}</span>
          </div>

          <div style={{fontSize:11, color:"#94a3b8", lineHeight:1.6}}>
            This page will automatically refresh once approved.<br/>
            Device ID: <span style={{fontFamily:"monospace", fontSize:10, color:"#64748b"}}>{getOrCreateDeviceId().substring(0,8)}…</span>
          </div>

          <button onClick={function(){setDevicePending(false);setPendingEmail("");setPendingPass("");}}
            style={{marginTop:20, padding:"8px 20px", background:"none", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:12, color:"#64748b", cursor:"pointer", fontFamily:"inherit"}}>
            ← Back to Login
          </button>

          {/* Pulse animation */}
          <style>{`@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.85)} }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh", background:"linear-gradient(135deg,#f0f8ff 0%,#f8fafc 40%,#e8f7fa 100%)", display:"flex", alignItems:"center", justifyContent:"center"}}>
      <div style={{background:"#fff", borderRadius:20, padding:"48px 44px", width:400, boxShadow:"0 8px 40px rgba(0,0,0,.09)"}}>
        <div style={{textAlign:"center", marginBottom:32}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:14}}>{brand ? <PartnerBrandRow partner={brand} /> : <AmsxLogo height={46} />}</div>
          <div style={{color:"#64748b", fontSize:12, marginTop:8}}>Pharma Intelligence Portal</div>
        </div>
        <div style={{marginBottom:14}}>
          <label style={{display:"block", fontSize:11.5, fontWeight:700, color:"#334155", marginBottom:5}}>Email</label>
          <input style={inp} type="email" placeholder="your@email.com" value={email}
            onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()} />
        </div>
        <div style={{marginBottom:20}}>
          <label style={{display:"block", fontSize:11.5, fontWeight:700, color:"#334155", marginBottom:5}}>Password</label>
          <input style={inp} type="password" placeholder="••••••••" value={pass}
            onChange={e => setPass(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()} />
        </div>
        {/* Device revoked error */}
        {deviceRevoked && <div style={{background:"#fef2f2", border:"1px solid #fecaca", borderRadius:7, padding:"12px 14px", fontSize:12, color:"#dc2626", marginBottom:14, lineHeight:1.5}}>
          <strong>Device Revoked</strong><br/>This device has been blocked by your administrator. Please contact support to request access.
        </div>}
        {launchNote && <div style={{background:"linear-gradient(135deg,#eef4fb,#dce8f5)", border:"1px solid #bfdbfe", borderRadius:9, padding:"13px 15px", fontSize:12, color:"#1e3a5f", marginBottom:14, lineHeight:1.65, textAlign:"left"}}>
          <div style={{fontWeight:800, fontSize:12.5, color:"#165372", marginBottom:6}}>{"\uD83C\uDF89 " + LAUNCH_MSG_TITLE}</div>
          {LAUNCH_MSG_LINES.map(function(l, i){ return <div key={i} style={{marginBottom:4}}>{l}</div>; })}
          <div style={{marginTop:6, fontWeight:700, fontStyle:"italic", color:"#165372"}}>{LAUNCH_MSG_SIGNOFF}</div>
        </div>}
        {err && <div style={{background:"#fef2f2", border:"1px solid #fecaca", borderRadius:7, padding:"9px 12px", fontSize:12, color:"#dc2626", marginBottom:14}}>{err}</div>}
        <button onClick={submit} disabled={busy}
          style={{width:"100%", padding:12, background:"linear-gradient(135deg,#165372,#48B7D3)", color:"#fff", border:"none", borderRadius:9, fontSize:14, fontWeight:700, cursor:busy?"not-allowed":"pointer", fontFamily:"inherit", opacity:busy?0.7:1, boxShadow:"0 4px 14px rgba(22,83,114,.3)"}}>
          {busy ? "Signing in…" : "Sign In →"}
        </button>
        <div style={{marginTop:16, padding:"9px 11px", background:"#f8fafc", borderRadius:7, fontSize:10.5, color:"#94a3b8", textAlign:"center"}}>
          Credentials are managed by your system administrator.
        </div>
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════════════
   ADMIN MODULE – Client & User Management
════════════════════════════════════════════════════════════ */
var STUDY_TYPES = ["IMS Monthly","IMS Quarterly","Retail Audit","Hospital Audit","Prescription Audit","Custom Study"];
var STUDY_TYPE_CODES = [
  {code:"iraq",name:"Iraq",cat:"private"},{code:"iraq_consumption",name:"Iraq Consumption",cat:"private"},{code:"jordan",name:"Jordan",cat:"private"},{code:"libya",name:"Libya",cat:"private"},
  {code:"jordan_medical",name:"Jordan Medical",cat:"private"},
  {code:"kimadia",name:"Kimadia",cat:"public"},{code:"gpd",name:"GPD",cat:"public"},{code:"libya_public",name:"Libya Public",cat:"public"},
  {code:"sales_tracker",name:"Sales Tracker",cat:"private"}
];
var ALL_DASHBOARD_KEYS = [
  {key:"market_overview",label:"Market Overview",icon:"📊"},
  {key:"regional_leading",label:"Regional Overview",icon:"🗺️"},
  {key:"leading_manufacturers",label:"Leading Manufacturers",icon:"🏭"},
  {key:"manufacturer_overview",label:"Manufacturer Overview (included with Leading Manufacturers)",icon:"🏭"},
  {key:"leading_products",label:"Leading Products",icon:"📦"},
  {key:"product_overview",label:"Product Overview (included with Leading Products)",icon:"💊"},
  {key:"competitive_analysis",label:"Competitive Analysis",icon:"⚔️"},
  {key:"price_sku_analysis",label:"Price / SKU Analysis",icon:"💰"},
  {key:"molecule_overview",label:"Product Overview (molecule analysis, product highlighted)",icon:"💊"},
  {key:"agents_overview",label:"Agents Overview",icon:"🏪"},
  {key:"agent_intelligence",label:"Agent Intelligence",icon:"🧠"},
  {key:"local_imported_analysis",label:"Local vs Imported",icon:"🌐"},
  {key:"new_launches",label:"New Launches",icon:"🚀"},
  {key:"voice_query",label:"AI Voice Query",icon:"🎙️"},
  {key:"bi_designer",label:"BI Dashboard Designer",icon:"🛠️"}
];
var KIMADIA_DASHBOARD_KEYS = [
  {key:"kimadia_competition",label:"Market Competition",icon:"⚔️"},
  {key:"kimadia_therapeutic",label:"Therapeutic Opportunities",icon:"🔬"},
  {key:"kimadia_pricing",label:"Pricing Intelligence",icon:"💲"},
  {key:"kimadia_foc_strategy",label:"FOC Strategy",icon:"🎁"},
  {key:"kimadia_agents",label:"Agent Performance",icon:"🏪"},
  {key:"kimadia_origin",label:"Supply Origin",icon:"🌍"},
  {key:"kimadia_leading_mfr",label:"Leading Manufacturers",icon:"🏭"},
  {key:"kimadia_product",label:"Product Analysis",icon:"💊"},
  {key:"kimadia_atc3",label:"ATC3 Analysis",icon:"🧬"},
  {key:"kimadia_foc",label:"FOC Analysis",icon:"📊"},
  {key:"voice_query",label:"AI Voice Query",icon:"🎙️"},
  {key:"bi_designer",label:"BI Dashboard Designer",icon:"🛠️"}
];
var MEDICAL_DASHBOARD_KEYS = [
  {key:"medical_market_overview",label:"Rx - Market Overview",icon:"📊"},
  {key:"medical_rx_overview",label:"Rx - Overview",icon:"📋"},
  {key:"medical_manufacturer_overview",label:"Rx - Manufacturer Overview",icon:"🏭"},
  {key:"medical_product_overview",label:"Rx - Product Overview",icon:"💊"},
  {key:"medical_molecule_overview",label:"Rx - Molecule Overview",icon:"🧬"},
  {key:"medical_competitive_analysis",label:"Rx - Competitive Analysis",icon:"⚔️"},
  {key:"medical_doctor_overview",label:"Rx - Doctor Overview",icon:"🩺"},
  {key:"medical_dynamics",label:"Rx - Patient Dynamics",icon:"🔀"},
  {key:"co_prescription",label:"Rx - Co-Prescription Intelligence",icon:"🔗"}
];
var CROSS_MAPPING_DASHBOARD_KEYS = [
  {key:"cross_regional_distribution",label:"Regional Distribution",icon:"🗺️"},
  {key:"cross_manufacturer",label:"Manufacturer Cross-Mapping",icon:"🏭"},
  {key:"cross_competition",label:"Cross Competition",icon:"⚔️"}
];
var SALES_TRACKER_DASHBOARD_KEYS = [
  {key:"st_scorecard",  label:"Executive Overview",               icon:"🎯"},
  {key:"st_market",     label:"Market Performance & Trends",      icon:"📊"},
  {key:"st_siso",       label:"Sell-In vs Sell-Out Intelligence", icon:"🔁"},
  {key:"st_arena",      label:"Competitive Intelligence",         icon:"⚔️"},
  {key:"st_regional",   label:"Regional Analysis",                icon:"🗺️"},
  {key:"st_channel",    label:"Pharmacy Segmentation & Channel",  icon:"🏪"},
  {key:"st_dist",       label:"Distribution & Opportunity",       icon:"🧭"},
  {key:"st_portfolio",  label:"Brand & SKU Economics",            icon:"📦"},
  {key:"st_price",      label:"Price, Volume & Mix",              icon:"💲"},
  {key:"st_origin",     label:"Origin & Segment Intelligence",    icon:"🌍"},
  {key:"st_foc",        label:"FOC Intelligence",                 icon:"🎁"},
  {key:"st_foc_effect", label:"FOC Effect on Share",              icon:"🎯"},
  {key:"st_price_effect", label:"Price Effect on Share",          icon:"⚖️"},
  {key:"voice_query",   label:"AI Voice Query",                   icon:"🎙️"},
  {key:"bi_designer",   label:"BI Dashboard Designer",            icon:"🛠️"}
];
function _getDashKeysForStudy(studyTypeCode) {
  if (studyTypeCode === "kimadia") return KIMADIA_DASHBOARD_KEYS;
  if (studyTypeCode === "sales_tracker") return SALES_TRACKER_DASHBOARD_KEYS;
  if (studyTypeCode === "jordan_medical") {
    /* medical dashboards (incl. Co-Prescription) are grantable for the medical study;
       generic keys stay available after them, as for jordan_msa */
    var mergedM = MEDICAL_DASHBOARD_KEYS.slice();
    var seenM = {};
    for (var mi = 0; mi < mergedM.length; mi++) seenM[mergedM[mi].key] = 1;
    for (var mj = 0; mj < ALL_DASHBOARD_KEYS.length; mj++) {
      if (!seenM[ALL_DASHBOARD_KEYS[mj].key]) mergedM.push(ALL_DASHBOARD_KEYS[mj]);
    }
    return mergedM;
  }
  if (studyTypeCode === "jordan_msa") {
    var merged = MEDICAL_DASHBOARD_KEYS.slice();
    var seen = {};
    for (var i = 0; i < merged.length; i++) seen[merged[i].key] = 1;
    for (var c = 0; c < CROSS_MAPPING_DASHBOARD_KEYS.length; c++) {
      if (!seen[CROSS_MAPPING_DASHBOARD_KEYS[c].key]) { merged.push(CROSS_MAPPING_DASHBOARD_KEYS[c]); seen[CROSS_MAPPING_DASHBOARD_KEYS[c].key] = 1; }
    }
    for (var j = 0; j < ALL_DASHBOARD_KEYS.length; j++) {
      if (!seen[ALL_DASHBOARD_KEYS[j].key]) merged.push(ALL_DASHBOARD_KEYS[j]);
    }
    return merged;
  }
  return ALL_DASHBOARD_KEYS;
}
var ALL_MEASURE_KEYS = [
  {key:"Unit",label:"Units",icon:"📦"},
  {key:"USD",label:"Value USD",icon:"💵"},
  {key:"LC",label:"Value LC",icon:"💴"},
  {key:"Price",label:"Price",icon:"💲"},
  {key:"Patients",label:"# Patients",icon:"🧑‍⚕️",medicalOnly:true},
  {key:"Prescriptions",label:"# Prescriptions",icon:"📝",medicalOnly:true},
  {key:"RxPerDoctor",label:"Rx per Doctor",icon:"🩺",medicalOnly:true},   /* r121: prescriptions ÷ prescribing doctors of the row's speciality */
  {key:"Doctors",label:"# Doctors",icon:"👨‍⚕️",medicalOnly:true},          /* r122: distinct prescribing doctors of the row */
  {key:"DoctorsTotal",label:"# Doctors (Total)",icon:"🏥",medicalOnly:true}, /* r122: the speciality's whole prescribing panel */
  /* Sales Tracker only */
  {key:"Gross",label:"Gross Selling",icon:"🧾",stOnly:true},
  {key:"Stock",label:"Current Stock",icon:"📦",stOnly:true},
  {key:"Pharmacies",label:"# Pharmacies",icon:"🏪",stOnly:true},
  /* GPD (Jordan tender market) only — the *_ui columns of GpdData */
  {key:"UnitIU",label:"Units IU",icon:"🧪",gpdOnly:true},
  {key:"PriceIU",label:"Unit Price IU",icon:"💲",gpdOnly:true}
];
/* Public (config-driven) studies — routed through the /api/study-data endpoints.
   The list is a belt-and-suspenders companion to StudyTypes.Category: even if the
   Category value is missing/stale in the DB, these codes ALWAYS take the public
   path (their filters/measures only exist as StudyTypeFeatures/Measures config). */
var PUBLIC_STUDY_CODES = ["kimadia", "gpd"];
function _isPublicStudy(cfg, code) {
  if (cfg && (cfg.category === "public" || PUBLIC_STUDY_CODES.indexOf(cfg.code || "") >= 0)) return true;
  return PUBLIC_STUDY_CODES.indexOf(code || "") >= 0;
}
/* Study-aware catalogs for the admin permission trees */
function _getMeasureKeysForStudy(studyTypeCode) {
  var _isMed = studyTypeCode === "jordan_medical" || studyTypeCode === "jordan_msa"; /* msa: merged market carries Rx measures too */
  var _isST = studyTypeCode === "sales_tracker";
  var _isGpd = studyTypeCode === "gpd";
  return ALL_MEASURE_KEYS.filter(function(m){ return (!m.medicalOnly || _isMed) && (!m.stOnly || _isST) && (!m.gpdOnly || _isGpd); });
}
function _getStatKeysForStudy(studyTypeCode) {
  var _isST = studyTypeCode === "sales_tracker";
  return ALL_STATISTIC_KEYS.filter(function(s){ return !s.stOnly || _isST; });
}
/* ── r42 (Noura 2026-10-11): per-study data access + access window helpers ──
   Mirror of the server's _parseStudyCap9 / _capEndIso9 / _addMonthsIso9 /
   _studyCloseIso9 — the client only PREVIEWS the derived close date; the
   server recomputes and stores it (ContractEnd) at save. */
function _pad2_9(n) { n = String(n); return n.length < 2 ? "0" + n : n; }
function _capEndIso9(mode, periodTo) {
  var md = String(mode || "").toLowerCase(), s = String(periodTo || "").trim(), m, y, mo;
  if (!s) return "";
  if (md === "quarter") { m = /Q([1-4])\s*(\d{4})/i.exec(s); if (!m) return ""; y = parseInt(m[2], 10); mo = parseInt(m[1], 10) * 3; }
  else if (md === "month") { m = /(\d{4})-(\d{1,2})/.exec(s); if (!m) return ""; y = parseInt(m[1], 10); mo = Math.max(1, Math.min(12, parseInt(m[2], 10))); }
  else return "";
  var last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return y + "-" + _pad2_9(mo) + "-" + _pad2_9(last);
}
function _addMonthsIso9(iso, n) {
  var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  var y = parseInt(m[1], 10), mo = parseInt(m[2], 10) + (parseInt(n, 10) || 0);
  y += Math.floor((mo - 1) / 12);
  mo = ((mo - 1) % 12 + 12) % 12 + 1;
  var last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  /* r43: a period END + months lands on the END of the target month (Q2 2026 + 6 → 2026-12-31) */
  var srcLast = new Date(Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10), 0)).getUTCDate();
  var d = (parseInt(m[3], 10) >= srcLast) ? last : Math.min(parseInt(m[3], 10), last);
  return y + "-" + _pad2_9(mo) + "-" + _pad2_9(d);
}
/* r43: is this study's close date DERIVED (Level 1 + Level 2 filled)? → the iso date or "" */
function _studyDerived9(entry) {
  if (!entry) return "";
  var n = parseInt(entry.accessMonthsAfter, 10);
  if (isNaN(n) || n < 0) return "";
  var ce = _capEndIso9(entry.periodMode, entry.periodTo);
  return ce ? _addMonthsIso9(ce, n) : "";
}
/* r43: every study of the client has a derived window (at least one study) */
function _allDerived9(studyAccess) {
  var arr = studyAccess || [];
  if (!arr.length) return false;
  for (var i = 0; i < arr.length; i++) { if (!_studyDerived9(arr[i])) return false; }
  return true;
}
/* effective close date of a study row: derived (cap end + months) else the manual date */
function _studyCloseIso9(entry) {
  if (!entry) return "";
  var n = parseInt(entry.accessMonthsAfter, 10);
  if (!isNaN(n) && n >= 0) { var ce = _capEndIso9(entry.periodMode, entry.periodTo); if (ce) return _addMonthsIso9(ce, n); }
  return entry.contractEnd || "";
}
/* latest close date across a client's studies ("" when none has one) */
function _portalUntil9(studyAccess) {
  var mx = "";
  (studyAccess || []).forEach(function (x) { var e9 = _studyCloseIso9(x); if (e9 && e9 > mx) mx = e9; });
  return mx;
}

/* Data-scope dimensions per study: which dimensions an admin can RESTRICT a
   client to (multi-select of values). Sales Tracker: ATC3 (per Noura). */
var STUDY_SCOPE_DIMS = {
  /* The admin can restrict a client by ANY of these dimensions — one, several,
     or none. Multiple scopes combine (rows must match every restricted
     dimension). Keys must exist in SCOPE_KEY_COLS on the server AND in the
     /api/pharma-data/dimensions payload (ScopeDimPicker loads its values from
     there). */
  "sales_tracker": [
    {key:"atc3",     label:"ATC3",     desc:"Client sees ONLY rows in the selected ATC3 classes (pivot, filters, dashboards, exports)"},
    {key:"molecule", label:"Molecule", desc:"Client sees ONLY rows for the selected molecules"},
    {key:"medicine", label:"Product",  desc:"Client sees ONLY rows for the selected products"}
  ]
};
/* Report catalog — mirrors the LIVE ReportsPanel catalog (Noura 2026-09-30:
   "read the current available reports"). Keys are the stored permission keys;
   the two Therapeutic Area keys render to the client as ONE "Therapeutic Area"
   card with an ATC2/ATC3 level toggle, so they are named as its two levels. */
var ALL_REPORT_KEYS = [
  {key:"market_overview",       label:"Market Overview",                icon:"🌐"},
  {key:"manufacturer_overview", label:"Manufacturer Overview",          icon:"🏭"},
  {key:"agent_overview",        label:"Agent Overview",                 icon:"🏪"},
  {key:"therapeutic_overview",  label:"Therapeutic Area · ATC2 level",  icon:"🧬"},
  {key:"therapeutic_atc3_overview", label:"Therapeutic Area · ATC3 level", icon:"🧬"},
  {key:"molecule_overview",     label:"Product Overview",               icon:"💊"},
  {key:"sales_tracker",         label:"Sales Tracker (tab)",            icon:"💹"},
  {key:"sales_tracker_diagnostic", label:"Sell-In / Sell-Out Analysis (Sales Tracker)", icon:"💹"}
];
var ALL_STATISTIC_KEYS = [
  {key:"Market Share",label:"Market Share",icon:"«%»"},
  {key:"Rank",label:"Rank",icon:"#"},
  {key:"Growth",label:"Growth",icon:"▲▼"},
  {key:"CAGR",label:"CAGR",icon:"μ"},
  {key:"Evolution Index",label:"Evolution Index",icon:"EI"},
  /* Sales Tracker only */
  {key:"FOC %",label:"FOC %",icon:"%",stOnly:true},
  {key:"Coverage %",label:"Coverage %",icon:"◔",stOnly:true},
  {key:"Sell-Through %",label:"Sell-Through %",icon:"⇄",stOnly:true},
  {key:"Profit",label:"Profit",icon:"$",stOnly:true}
];

/* ── Admin: data-scope multi-select (values of one dimension for one study) ──
   Loads the study's distinct values once (admin call, unrestricted), then a
   searchable checklist. Empty selection = unrestricted (no scope rows saved). */
var _scopeValsCache = {};
function ScopeDimPicker(props) {
  var studyType = props.studyType, dimKey = props.dimKey;
  var value = props.value || [];
  var _st = useState(_scopeValsCache[studyType + "|" + dimKey] || null), opts = _st[0], setOpts = _st[1];
  var _q = useState(""), q = _q[0], setQ = _q[1];
  var _o = useState(false), open = _o[0], setOpen = _o[1];
  useEffect(function() {
    if (opts) return;
    var ck = studyType + "|" + dimKey;
    api.getDimensions("", studyType).then(function(d) {
      var vals = (d && Array.isArray(d[dimKey])) ? d[dimKey].map(function(v){ return String(v); }) : [];
      _scopeValsCache[ck] = vals; setOpts(vals);
    }).catch(function() { setOpts([]); });
  }, [studyType, dimKey]);
  var list = opts || [];
  var shown = q ? list.filter(function(v){ return v.toLowerCase().indexOf(q.toLowerCase()) >= 0; }) : list;
  var tog = function(v) {
    var nx = value.indexOf(v) >= 0 ? value.filter(function(x){ return x !== v; }) : value.concat([v]);
    props.onChange(nx);
  };
  var active = value.length > 0;
  return (
    <div style={{marginTop:4, position:"relative"}}>
      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
        <span onClick={function(){ setOpen(!open); }}
          style={{padding:"3px 10px",borderRadius:5,fontSize:9,cursor:"pointer",userSelect:"none",fontWeight:700,
                  border:"1.5px solid "+(active?"#be185d":"#e2e8f0"),background:active?"#fdf2f8":"#fff",color:active?"#9d174d":"#64748b"}}>
          {active ? value.length + " " + props.label + " selected" : "All " + props.label + " (unrestricted)"} {open ? "▲" : "▼"}
        </span>
        {active && <span onClick={function(){ props.onChange([]); }} style={{fontSize:8.5,color:"#dc2626",cursor:"pointer",textDecoration:"underline"}}>Clear (unrestricted)</span>}
        {opts === null && <span style={{fontSize:8.5,color:"#94a3b8"}}>loading values…</span>}
      </div>
      {active && !open && (
        <div style={{marginTop:4,fontSize:8.5,color:"#9d174d",lineHeight:1.5}} title={value.join(", ")}>
          {value.slice(0, 6).join(" \u00b7 ") + (value.length > 6 ? "  (+" + (value.length - 6) + " more)" : "")}
        </div>
      )}
      {open && (
        <div style={{position:"absolute",top:"100%",left:0,zIndex:60,minWidth:340,maxWidth:470,marginTop:4,border:"1px solid #f9a8d4",borderRadius:8,background:"#fff",padding:8,boxShadow:"0 12px 32px rgba(0,0,0,.18)"}}>
          <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:5}}>
            <input value={q} onChange={function(e){ setQ(e.target.value); }} placeholder={"Search " + props.label + "…"}
              style={{flex:1,padding:"4px 8px",fontSize:9.5,border:"1px solid #e2e8f0",borderRadius:5,fontFamily:"inherit",outline:"none"}} />
            <span onClick={function(){ props.onChange(shown.slice()); }} style={{fontSize:8.5,color:"#be185d",cursor:"pointer",textDecoration:"underline"}}>Select shown</span>
            <span style={{fontSize:8.5,color:"#94a3b8"}}>{value.length}/{list.length}</span>
          </div>
          <div style={{maxHeight:230,overflowY:"auto",display:"flex",flexDirection:"column",border:"1px solid #f1f5f9",borderRadius:6}}>
            {shown.slice(0, 500).map(function(v){
              var on = value.indexOf(v) >= 0;
              return (
                <div key={v} onClick={function(){ tog(v); }}
                  onMouseEnter={function(e){ e.currentTarget.style.background = on ? "#fce7f3" : "#f8fafc"; }}
                  onMouseLeave={function(e){ e.currentTarget.style.background = on ? "#fdf2f8" : "#fff"; }}
                  style={{display:"flex",alignItems:"center",gap:7,padding:"4px 9px",fontSize:9.5,cursor:"pointer",userSelect:"none",
                          borderBottom:"1px solid #f8fafc",background:on?"#fdf2f8":"#fff",color:on?"#9d174d":"#334155",fontWeight:on?700:400}}>
                  <div style={{width:12,height:12,borderRadius:3,flexShrink:0,border:"1.5px solid "+(on?"#be185d":"#cbd5e1"),background:on?"#be185d":"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"#fff"}}>{on?"\u2713":""}</div>
                  <span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{v}</span>
                </div>
              );
            })}
            {shown.length === 0 && <span style={{fontSize:9,color:"#94a3b8",padding:4}}>{opts === null ? "Loading…" : "No values"}</span>}
          </div>
          <div style={{marginTop:6,display:"flex",justifyContent:"flex-end"}}>
            <span onClick={function(){ setOpen(false); }} style={{fontSize:9,fontWeight:800,color:"#fff",background:"#be185d",borderRadius:5,padding:"3px 14px",cursor:"pointer"}}>{"Done \u2713"}</span>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── PANEL DISCLOSURE (Sales Tracker) ──
   Professional disclaimer on the size and nature of the pharmacy panel behind
   the figures. Two renderings from the same facts:
     compact = one-line strip above the pivot (persistent while analysing)
     card    = fuller statement on the "Ready to query" landing state
   Facts come from /dimensions → panelInfo (server-computed, cached 1h). */
function PanelDisclosure(props) {
  var pi = props.info; if (!pi || !pi.pharmacies) return null;
  var n = Number(pi.pharmacies).toLocaleString();
  var cls = (pi.byClass || []).filter(function(c){ return c && c.cls; });
  var clsTxt = cls.length ? cls.map(function(c){ return "Class " + c.cls + ": " + Number(c.n).toLocaleString(); }).join(" \u00B7 ") : "";
  var per = (pi.periodFrom && pi.periodTo) ? (pi.periodFrom === pi.periodTo ? pi.periodFrom : pi.periodFrom + " \u2013 " + pi.periodTo) : "";
  var dispTxt = (pi.sellOutPharmacies != null && pi.sellOutPharmacies !== pi.pharmacies) ? (Number(pi.sellOutPharmacies).toLocaleString() + " reporting sell-out") : "";
  if (props.compact) {
    return (
      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",padding:"6px 12px",marginBottom:8,
                   background:"linear-gradient(90deg,#f0fdf4,#f8fafc)",border:"1px solid #bbf7d0",borderRadius:9,fontSize:10.5,color:"#14532d"}}>
        <span style={{fontSize:13}}>{"\uD83C\uDFEA"}</span>
        <span style={{fontWeight:800,letterSpacing:".02em"}}>PANEL OF {n} PHARMACIES</span>
        {clsTxt && <span style={{color:"#166534"}}>({clsTxt})</span>}
        {per && <span style={{color:"#166534"}}>{"\u00B7 " + per}</span>}
        <span style={{color:"#3f6212",fontStyle:"italic",marginLeft:"auto"}}>Figures reflect the actual purchasing &amp; dispensing behaviour of the panel pharmacies &mdash; not projected to the total market.</span>
      </div>
    );
  }
  return (
    <div style={{maxWidth:640,margin:"22px auto 0",textAlign:"left",background:"linear-gradient(135deg,#f0fdf4,#f8fafc)",border:"1px solid #bbf7d0",borderRadius:12,padding:"14px 18px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
        <div style={{width:26,height:26,borderRadius:7,background:"linear-gradient(135deg,#0F6E56,#1BA878)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13}}>{"\uD83C\uDFEA"}</div>
        <span style={{fontSize:10.5,fontWeight:800,color:"#0F6E56",textTransform:"uppercase",letterSpacing:".06em"}}>Panel disclosure</span>
      </div>
      <div style={{fontSize:12,color:"#1e293b",lineHeight:1.75}}>
        This analysis is based on a fixed panel of <strong>{n} pharmacies</strong>
        {clsTxt ? <span> ({clsTxt})</span> : null}
        {per ? <span> reporting their sell-in and sell-out transactions from <strong>{per}</strong></span> : <span> reporting their sell-in and sell-out transactions</span>}
        {dispTxt ? <span> ({dispTxt})</span> : null}.
        {" "}All figures reflect the <strong>actual purchasing and dispensing behaviour</strong> of the panel pharmacies. They are not projected, weighted or extrapolated to the total market; shares, growth and coverage are read within the panel.
      </div>
    </div>
  );
}

/* Feature groups per study type — only shows relevant features in Admin tree */
var STUDY_FEATURE_GROUPS = {
  "iraq": [
    {group:"🏷️ Market Features", dims:["manufacturer","medicine","sku","agent","LaunchDate"]},
    {group:"🧬 ATC / Molecule",  dims:["atc1","atc2","atc3","atc4","molecule"]},
    {group:"📋 NFC",             dims:["nfc","nfc2","nfc3"]},
    {group:"💊 Product Details", dims:["form","pack","strength"]},
    {group:"📍 Region / Governorate", dims:["region","governorate"]},
    {group:"🌍 Origin",          dims:["continent","subContinent","countryOfOrigin","manufacturerType","localImported"]}
  ],
  "jordan": [
    {group:"🏷️ Market Features", dims:["manufacturer","medicine","sku","agent"]},
    {group:"🧬 ATC / Molecule",  dims:["atc1","atc2","atc3","atc4","molecule"]},
    {group:"📋 NFC",             dims:["nfc","nfc2","nfc3"]},
    {group:"💊 Product Details", dims:["form","pack","strength"]},
    {group:"📍 Region / Governorate", dims:["region","governorate"]},
    {group:"🌍 Origin",          dims:["continent","subContinent","countryOfOrigin","manufacturerType","localImported"]}
  ],
  "libya": [
    {group:"🏷️ Market Features", dims:["manufacturer","subManufacturer","medicine","subProduct","productSource","sku","agent"]},
    {group:"🧬 ATC / Molecule",  dims:["atc1","atc2","atc3","atc4","molecule"]},
    {group:"📋 NFC",             dims:["nfc","nfc2","nfc3"]},
    {group:"💊 Product Details", dims:["form","pack","strength"]},
    {group:"📍 Region / Governorate", dims:["region","governorate"]},
    {group:"🌍 Origin",          dims:["continent","subContinent","countryOfOrigin","manufacturerType","localImported"]}
  ],
  "jordan_medical": [
    /* Market Features combines what was previously two separate groups (Market
       + Product Details). Agent is included since the column exists in
       MedicalData and the dimension is meaningful for medical analysis too. */
    {group:"🏷️ Market Features", dims:["manufacturer","medicine","sku","agent","form","pack","strength","aloneCombined"]},
    {group:"🧬 ATC / Molecule",  dims:["atc1","atc2","atc3","atc4","molecule"]},
    {group:"📋 NFC",             dims:["nfc","nfc2","nfc3"]},
    {group:"🩺 Doctor Details",   dims:["docRegion","docSpec","docCity"]},
    {group:"💊 Pharmacy Details", dims:["phRegion","phCity"]},
    {group:"👤 Patient Details",  dims:["patientAge","patientGender"]},
    {group:"🔬 Indication",      dims:["diagnosis"]}
  ],
  "jordan_msa": [
    /* Medical & Pharma Intelligence: the merged market. Shared dimensions appear
       ONCE (filled from whichever dataset the row comes from); dataset-specific
       dimensions keep their own groups — Sales Region (pharma) vs Doctor /
       Pharmacy regions (Rx) — and Source is the Pharma/Rx discriminator, like
       Flow on Sales Tracker. */
    {group:"🏷️ Market Features", dims:["source","manufacturer","medicine","sku","agent","form","pack","strength","aloneCombined"]},
    {group:"🧬 ATC / Molecule",  dims:["atc1","atc2","atc3","atc4","molecule"]},
    {group:"📋 NFC",             dims:["nfc","nfc2","nfc3"]},
    /* Region — TWO combined concepts, each aligned across the datasets:
       Doctor–Sales Region  = DoctorRegion on Rx rows, sales Region on Pharma rows
       Pharmacy–Sales Region = PharmacyRegion on Rx rows, sales Region on Pharma rows.
       The Rx detail dims (speciality, patient, indication) are intentionally NOT
       offered in this study. */
    {group:"📍 Region",          dims:["docSalesRegion","phSalesRegion"]},
    {group:"🌍 Origin",          dims:["continent","subContinent","countryOfOrigin","manufacturerType","localImported"]}
  ],
  "kimadia": [
    {group:"🏷️ Market Details",        dims:["manufacturer","medicine","sku","agent","orderNo","tenderStatus"]},
    {group:"🧬 ATC/Molecule",          dims:["atc1","atc2","atc3","atc4","molecule"]},
    {group:"📋 NFC",                    dims:["nfc","nfc2","nfc3"]},
    {group:"🌍 Manufacturers Origin",   dims:["continent","subContinent","countryOfOrigin","manufacturerType","localImported"]}
  ]
};
/* Sales Tracker (pharmacy sell-in / sell-out panel, Jordan) — every dimension the
   study exposes, so the admin can grant/deny each one. */
STUDY_FEATURE_GROUPS["sales_tracker"] = [
  {group:"🏷️ Market Features", dims:["manufacturer","medicine","sku","flow","sector"]},
  {group:"🧬 ATC / Molecule",  dims:["atc1","atc2","atc3","atc4","molecule"]},
  {group:"💊 Product Details", dims:["form"]},
  {group:"📍 Region / Governorate", dims:["region","governorate"]},
  {group:"🏪 Pharmacy Panel",  dims:["pharmacyClass"]},
  {group:"🌍 Origin",          dims:["countryOfOrigin","localImported"]}
];
/* GPD — Jordan public tender market (table GpdData). Groups mirror the legacy
   GPD desktop tool sidebar; these drive the admin permission trees (client +
   user feature grants), so every grantable tender dimension is listed. */
STUDY_FEATURE_GROUPS["gpd"] = [
  {group:"🏷️ Market Details",       dims:["manufacturer","medicine","sku","agent","orderNo","strength","institution","tender","tenderNo","bidder","registerStatus","reasonForAward","shelfLife","tenderState"]},
  {group:"🧬 Therapeutic Class",     dims:["atc1","atc2","atc3","atc4","molecule"]},
  {group:"📋 NFC",                   dims:["nfc","nfc2","nfc3"]},
  {group:"🌍 Manufacturers Origin",  dims:["continent","subContinent","countryOfOrigin","manufacturerType","localImported"]}
];
/* Molecule report advanced setup (focus manufacturers + therapeutic-class
   selectors): ADMIN-ONLY in the report UI; granting this key in the permission
   tree opens the selectors to that client/user ("molAdvanced"). */
["iraq", "jordan", "libya"].forEach(function(_stG){
  if (STUDY_FEATURE_GROUPS[_stG]) STUDY_FEATURE_GROUPS[_stG] = STUDY_FEATURE_GROUPS[_stG].concat([{group:"📑 Report Options", dims:["molAdvanced"]}]);
});
STUDY_FEATURE_GROUPS["libya_public"] = STUDY_FEATURE_GROUPS["libya"];
STUDY_FEATURE_GROUPS["egypt"] = STUDY_FEATURE_GROUPS["iraq"];
STUDY_FEATURE_GROUPS["saudi"] = STUDY_FEATURE_GROUPS["iraq"];
/* r88 (Noura 2026-10-12): "fullReports" — the FULL-REPORT permission. Without it
   a user gets SAMPLE MODE on every report (download a sample, order the full
   report); with it the report decks open in full with PPT/PDF export. Grantable
   per study at client level and per user, like any feature; NOT part of "All". */
var FULL_REPORT_FEATURE = "fullReports";
Object.keys(STUDY_FEATURE_GROUPS).forEach(function(_stK){
  var _gs = STUDY_FEATURE_GROUPS[_stK]; if (!Array.isArray(_gs)) return;
  var _ro = null; for (var _gi = 0; _gi < _gs.length; _gi++) if (_gs[_gi].group === "\uD83D\uDCD1 Report Options") { _ro = _gs[_gi]; break; }
  if (_ro) { if (_ro.dims.indexOf(FULL_REPORT_FEATURE) < 0) _ro.dims.push(FULL_REPORT_FEATURE); }
  else _gs.push({ group: "\uD83D\uDCD1 Report Options", dims: [FULL_REPORT_FEATURE] });
});
STUDY_FEATURE_GROUPS["uae"] = STUDY_FEATURE_GROUPS["iraq"];
STUDY_FEATURE_GROUPS["iraq_consumption"] = STUDY_FEATURE_GROUPS["iraq"];   /* r168: Iraq Consumption — the iraq permission tree (features, report options, full reports) */
STUDY_FEATURE_GROUPS["kuwait"] = STUDY_FEATURE_GROUPS["iraq"];
STUDY_FEATURE_GROUPS["other"] = STUDY_FEATURE_GROUPS["iraq"];
var COUNTRIES = ["Jordan","Iraq","Libya","Egypt","Saudi Arabia","UAE","Kuwait","Other"];
var COUNTRY_STUDY_MAP = {
  "Jordan":        [{code:"jordan",name:"Private",cat:"private"},{code:"jordan_medical",name:"Medical",cat:"private"},{code:"jordan_msa",name:"Medical & Pharma",cat:"private"},{code:"sales_tracker",name:"Sales Tracker",cat:"private"},{code:"gpd",name:"GPD",cat:"public"}],
  "Iraq":          [{code:"iraq",name:"Private",cat:"private"},{code:"iraq_consumption",name:"Consumption",cat:"private"},{code:"kimadia",name:"Public (Kimadia)",cat:"public"}],
  "Libya":         [{code:"libya",name:"Private",cat:"private"},{code:"libya_public",name:"Public",cat:"public"}],
  "Egypt":         [{code:"egypt",name:"Private",cat:"private"}],
  "Saudi Arabia":  [{code:"saudi",name:"Private",cat:"private"}],
  "UAE":           [{code:"uae",name:"Private",cat:"private"}],
  "Kuwait":        [{code:"kuwait",name:"Private",cat:"private"}],
  "Other":         [{code:"other",name:"Private",cat:"private"}]
};
var FEATURES = [
  {key:"pivot",      label:"Market"},
  {key:"excel_export",label:"Excel Export"},
  {key:"dash_market", label:"Dashboard: Market Overview"},
  {key:"dash_regional",label:"Dashboard: Regional Overview"},
  {key:"dash_mfr",     label:"Dashboard: Leading Manufacturers"},
  {key:"dash_3",      label:"Dashboard 3"},
  {key:"mat_ytd",     label:"MAT / YTD Periods"},
  {key:"growth_stats",label:"Growth & Statistics"},
  {key:"share_rank",  label:"Share% & Rank"},
];
var COUNTRY_PRESETS = ["Jordan","Egypt","Saudi Arabia","UAE","Iraq","Lebanon","Kuwait","Bahrain","Oman","Qatar","Libya","Tunisia","Morocco","Algeria","Sudan","Syria","Palestine","Yemen"];


var nextId = 100;
function genId(prefix) { nextId++; return prefix + nextId; }

/* ── Shared admin UI primitives ── */
var adminInp = {width:"100%", padding:"8px 11px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, fontFamily:"inherit", outline:"none", color:"#0f172a", background:"#fff"};
var adminBtn = function(bg, color, bdr) {
  return {padding:"6px 14px", border:"1.5px solid "+(bdr||bg), borderRadius:7, background:bg, color:color,
    fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap"};
};

function StatusBadge({ status }) {
  var s = status === "active" ? {bg:"#dcfce7",co:"#15803d",lb:"Active"} :
          status === "paused" ? {bg:"#fef3c7",co:"#92400e",lb:"Paused"} :
                                {bg:"#fee2e2",co:"#dc2626",lb:"Deleted"};
  return <span style={{fontSize:9.5, fontWeight:700, background:s.bg, color:s.co, borderRadius:20, padding:"2px 9px"}}>{s.lb}</span>;
}

/* ════════ Client Form Modal ════════ */
/* ── CONTRACT ANALYSIS (Noura 2026-10-11, r43): every client × study classified
   by its access window — Expired / Expiring soon (≤60 days) / Active / Long
   contract (>1 year left) / Open-ended. Admin Team section "contracts";
   study-scoped admins see only their studies; owner-group clients are already
   hidden server-side for team admins. Purely client-side over the loaded
   clients + users lists (both readable by every admin). ── */
var CA_SOON_DAYS = 60, CA_LONG_DAYS = 365;
var CA_STATUS = [
  {key:"expired", label:"Expired",                         bg:"#fee2e2", fg:"#b91c1c", bd:"#fecaca", icon:"\u26D4"},
  {key:"soon",    label:"Expiring soon (\u2264 60 days)",  bg:"#fef3c7", fg:"#92400e", bd:"#fcd34d", icon:"\u23F3"},
  {key:"active",  label:"Active",                          bg:"#dcfce7", fg:"#166534", bd:"#bbf7d0", icon:"\u2713"},
  {key:"long",    label:"Long contract (> 1 year left)",   bg:"#dbeafe", fg:"#1e40af", bd:"#bfdbfe", icon:"\uD83D\uDCC6"},
  {key:"open",    label:"Open-ended (no close date)",      bg:"#f1f5f9", fg:"#475569", bd:"#e2e8f0", icon:"\u221E"}
];
function _caClassify9(client, entry, today) {
  var close = _studyCloseIso9(entry) || "";
  var hard = client.contractEnd || "";                 /* client-wide hard cap wins when earlier */
  if (hard && (!close || hard < close)) close = hard;
  var start = entry.contractStart || client.contractStart || "";
  if (!close) return { key: "open", close: "", start: start, days: null };
  var d = Math.round((new Date(close + "T00:00:00Z") - new Date(today + "T00:00:00Z")) / 86400000);
  var key = d < 0 ? "expired" : d <= CA_SOON_DAYS ? "soon" : d > CA_LONG_DAYS ? "long" : "active";
  return { key: key, close: close, start: start, days: d };
}
function ContractAnalysisPanel(props) {
  var clients = props.clients || [], users = props.users || [], adminScope = props.adminScope || null;
  var _fs = useState("all"), fStatus = _fs[0], setFStatus = _fs[1];
  var _fst = useState("all"), fStudy = _fst[0], setFStudy = _fst[1];
  var _fq = useState(""), q = _fq[0], setQ = _fq[1];
  var today = new Date().toISOString().slice(0, 10);
  var uCount = {};
  users.forEach(function (u) { if (u.status === "deleted") return; var k = String(u.clientId); uCount[k] = (uCount[k] || 0) + 1; });
  var rows = [];
  clients.forEach(function (c) {
    if (c.status === "deleted") return;
    (c.studyAccess || []).forEach(function (e) {
      if (adminScope && adminScope.indexOf(e.studyTypeCode) < 0) return;
      rows.push({ client: c, entry: e, cls: _caClassify9(c, e, today), users: uCount[String(c.id)] || 0 });
    });
  });
  var studies = []; rows.forEach(function (r) { if (studies.indexOf(r.entry.studyTypeCode) < 0) studies.push(r.entry.studyTypeCode); }); studies.sort();
  var counts = {}, clientSets = {};
  rows.forEach(function (r) { counts[r.cls.key] = (counts[r.cls.key] || 0) + 1; (clientSets[r.cls.key] = clientSets[r.cls.key] || {})[String(r.client.id)] = 1; });
  var shown = rows.filter(function (r) {
    if (fStatus !== "all" && r.cls.key !== fStatus) return false;
    if (fStudy !== "all" && r.entry.studyTypeCode !== fStudy) return false;
    if (q && String(r.client.name || "").toLowerCase().indexOf(q.toLowerCase()) < 0) return false;
    return true;
  });
  shown.sort(function (a, b) { var da = a.cls.days == null ? 1e9 : a.cls.days, db = b.cls.days == null ? 1e9 : b.cls.days; return (da - db) || String(a.client.name).localeCompare(String(b.client.name)); });
  var stLbl = function (code) { return String(code || "").replace(/_/g, " ").toUpperCase(); };
  var latestData = function (e) { var pm = String(e.periodMode || "").toLowerCase(); return ((pm === "quarter" || pm === "month") && e.periodTo) ? e.periodTo : "\u2014"; };
  var stMeta = function (k) { for (var i = 0; i < CA_STATUS.length; i++) if (CA_STATUS[i].key === k) return CA_STATUS[i]; return CA_STATUS[4]; };
  var exportCsv = function () {
    var esc = function (v) { v = (v == null ? "" : String(v)); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
    var lines = [["Client", "Client status", "Study", "Country", "Latest data", "Start", "Close date", "Days left", "Status", "Users"].join(",")];
    shown.forEach(function (r) { lines.push([r.client.name, r.client.status, stLbl(r.entry.studyTypeCode), r.entry.country || "", latestData(r.entry), r.cls.start || "", r.cls.close || "", r.cls.days == null ? "" : r.cls.days, stMeta(r.cls.key).label, r.users].map(esc).join(",")); });
    var blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    var a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "AMSX_contracts_" + today + ".csv"; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  var thS9 = {padding:"8px 10px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:".05em", textAlign:"left", borderBottom:"1.5px solid #e2e8f0", background:"#f8fafc", whiteSpace:"nowrap"};
  var tdS9 = {padding:"8px 10px", fontSize:11.5, borderBottom:"1px solid #f1f5f9", verticalAlign:"middle"};
  var selS9 = {padding:"6px 10px", fontSize:11.5, border:"1.5px solid #e2e8f0", borderRadius:7, background:"#fff", fontFamily:"inherit", cursor:"pointer", color:"#0f172a"};
  return (
    <div>
      <div style={{display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:10, marginBottom:14}}>
        {CA_STATUS.map(function (s) {
          var on = fStatus === s.key, n = counts[s.key] || 0, nc = Object.keys(clientSets[s.key] || {}).length;
          return (
            <div key={s.key} onClick={function () { setFStatus(on ? "all" : s.key); }}
              style={{padding:"10px 12px", borderRadius:10, cursor:"pointer", background:s.bg, border:"2px solid " + (on ? s.fg : s.bd), boxShadow: on ? "0 4px 14px rgba(0,0,0,.08)" : "none"}}>
              <div style={{fontSize:9.5, fontWeight:800, color:s.fg, textTransform:"uppercase", letterSpacing:".04em"}}>{s.icon + " " + s.label}</div>
              <div style={{fontSize:22, fontWeight:800, color:s.fg, lineHeight:1.2, marginTop:4}}>{n}</div>
              <div style={{fontSize:9.5, color:s.fg, opacity:.8}}>{n === 1 ? "1 study row" : n + " study rows"}{" \u00b7 " + nc + " client" + (nc === 1 ? "" : "s")}</div>
            </div>
          );
        })}
      </div>
      <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10}}>
        <input value={q} onChange={function (e) { setQ(e.target.value); }} placeholder={"Search client\u2026"} style={Object.assign({}, selS9, {cursor:"text", width:220})} />
        <select style={selS9} value={fStudy} onChange={function (e) { setFStudy(e.target.value); }}>
          <option value="all">All studies</option>
          {studies.map(function (s) { return <option key={s} value={s}>{stLbl(s)}</option>; })}
        </select>
        <select style={selS9} value={fStatus} onChange={function (e) { setFStatus(e.target.value); }}>
          <option value="all">All statuses</option>
          {CA_STATUS.map(function (s) { return <option key={s.key} value={s.key}>{s.label}</option>; })}
        </select>
        <button onClick={exportCsv} style={{padding:"6px 14px", border:"none", borderRadius:7, background:"#2563eb", color:"#fff", fontSize:11.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>{"\u2B07 Export CSV (" + shown.length + ")"}</button>
        <span style={{fontSize:10, color:"#94a3b8", marginLeft:"auto"}}>{"Today " + today + " \u00b7 soon \u2264 " + CA_SOON_DAYS + " days \u00b7 long > " + CA_LONG_DAYS + " days left \u00b7 one row per client \u00d7 study" + (adminScope ? " \u00b7 your studies: " + adminScope.map(stLbl).join(", ") : "")}</span>
      </div>
      <div style={{background:"#fff", borderRadius:10, border:"1px solid #e2e8f0", overflow:"auto"}}>
        <table style={{width:"100%", borderCollapse:"collapse"}}>
          <thead><tr>
            <th style={thS9}>Client</th><th style={thS9}>Study</th><th style={thS9}>Country</th><th style={thS9}>Latest data</th>
            <th style={thS9}>Start</th><th style={thS9}>Close date</th><th style={Object.assign({}, thS9, {textAlign:"right"})}>Days left</th>
            <th style={thS9}>Status</th><th style={Object.assign({}, thS9, {textAlign:"right"})}>Users</th>
          </tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={9} style={Object.assign({}, tdS9, {textAlign:"center", color:"#94a3b8", padding:30})}>No study rows match.</td></tr>}
            {shown.map(function (r, i) {
              var m = stMeta(r.cls.key);
              return (
                <tr key={String(r.client.id) + "|" + r.entry.studyTypeCode + "|" + (r.entry.country || "") + "|" + i}>
                  <td style={Object.assign({}, tdS9, {fontWeight:700, color:"#0f172a"})}>
                    {r.client.name}
                    {r.client.status === "paused" ? <span style={{marginLeft:6, fontSize:8, fontWeight:800, background:"#fef3c7", color:"#92400e", border:"1px solid #fcd34d", borderRadius:3, padding:"1px 5px"}}>PAUSED</span> : null}
                    {r.client.contractEnd && r.cls.close === r.client.contractEnd ? <span title="client-wide hard cap decides this date" style={{marginLeft:6, fontSize:8, fontWeight:800, background:"#f3e8ff", color:"#7e22ce", border:"1px solid #d8b4fe", borderRadius:3, padding:"1px 5px"}}>HARD CAP</span> : null}
                  </td>
                  <td style={tdS9}>{stLbl(r.entry.studyTypeCode)}</td>
                  <td style={Object.assign({}, tdS9, {color:"#64748b"})}>{r.entry.country || "\u2014"}</td>
                  <td style={tdS9}>{latestData(r.entry)}</td>
                  <td style={Object.assign({}, tdS9, {color:"#64748b"})}>{r.cls.start || "\u2014"}</td>
                  <td style={Object.assign({}, tdS9, {fontWeight:600})}>{r.cls.close || "\u2014"}</td>
                  <td style={Object.assign({}, tdS9, {textAlign:"right", fontWeight:700, color:m.fg})}>{r.cls.days == null ? "\u2014" : r.cls.days}</td>
                  <td style={tdS9}><span style={{fontSize:9.5, fontWeight:800, background:m.bg, color:m.fg, border:"1px solid " + m.bd, borderRadius:10, padding:"2px 9px", whiteSpace:"nowrap"}}>{m.icon + " " + m.label}</span></td>
                  <td style={Object.assign({}, tdS9, {textAlign:"right"})}>{r.users}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* r72: Product Overview is included with Leading Products — ticking / unticking
   Leading Products carries Product Overview with it; Product Overview itself cannot
   be unticked while Leading Products is on (the login rule grants it anyway). Used by
   both the Client and the User modal. */
var _DASH_IMPLIES = { leading_products: "product_overview", leading_manufacturers: "manufacturer_overview" }; /* r73: parent → included dashboard */
function _dashParentOf(dashKey) { for (var k in _DASH_IMPLIES) { if (_DASH_IMPLIES[k] === dashKey) return k; } return null; }
function _togDashList(dashes, dashKey) {
  dashes = (dashes||[]).slice();
  var idx = dashes.indexOf(dashKey);
  var parent = _dashParentOf(dashKey);
  if (parent && dashes.indexOf(parent) >= 0) return dashes;          /* included with its parent — cannot be unticked alone */
  if (idx>=0) dashes.splice(idx,1); else dashes.push(dashKey);
  var child = _DASH_IMPLIES[dashKey];
  if (child) {
    var pi = dashes.indexOf(child);
    if (idx < 0 && pi < 0) dashes.push(child);
    if (idx >= 0 && pi >= 0) dashes.splice(pi, 1);
  }
  return dashes;
}
function _dashOn(dashes, dashKey) {
  dashes = dashes || [];
  if (dashes.indexOf(dashKey) >= 0) return true;
  var parent = _dashParentOf(dashKey);
  return !!(parent && dashes.indexOf(parent) >= 0);
}
function ClientModal({ client, onSave, onClose, adminScope, canReports, canOwner, partners }) {
  var isNew = !client;
  var defPerms = {clientType:"standard", allowedSectors:SECTOR_TYPES.slice(), allowedDimensions:DIMENSION_KEYS.slice(), periodAccess:{mode:"none",fromQ:"",toQ:"",fromDate:"",toDate:""}, periodPermission:"monthly", dashboards:[], clientDimensions:[], exportLimit:null, accessMode:"full", allowExport:true, allowGridExport:true, allowCustomRegions:false, allowCustomMarkets:false, reportQuota:null, reportOptions:null, ownerGroup:false, contractStart:"", contractEnd:""};
  var [f, setF] = useState(client ? {...defPerms, ...client} : {id:"", name:"", contact:"", email:"", status:"active", studies:[], features:[], studyTypes:[], studyAccess:[], ...defPerms, created:new Date().toISOString().slice(0,10)});
  /* Per-study report catalog (Noura 2026-10-02): each study's Reports group
     lists only the reports that actually exist for it. Unknown/still-loading
     studies fall back to the full list; a study the server reports as having
     NO reports shows a note instead of chips. */
  var _rcS = useState(null), repCatalog = _rcS[0], setRepCatalog = _rcS[1];
  useEffect(function(){ api.getReportsCatalog().then(function(r9){ setRepCatalog((r9 && r9.studies) || {}); }).catch(function(e9){}); }, []);
  var _repKeysFor = function(code9){
    if (!repCatalog || repCatalog[code9] === undefined) return ALL_REPORT_KEYS; /* loading / unknown study */
    var av9 = repCatalog[code9] || [];
    return ALL_REPORT_KEYS.filter(function(r9){ return av9.indexOf(r9.key) >= 0; });
  };
  var set = function(k,v) { setF(function(p) { var n={...p}; n[k]=v; return n; }); };
  var togArr = function(k, val) {
    setF(function(p) {
      var arr = (p[k]||[]).slice();
      var idx = arr.indexOf(val);
      if (idx >= 0) arr.splice(idx,1); else arr.push(val);
      var n = {...p}; n[k] = arr; return n;
    });
  };
  var setPeriod = function(k,v) { setF(function(p){ return {...p, periodAccess:{...(p.periodAccess||defPerms.periodAccess), [k]:v}}; }); };
  /* Client-level contract is authoritative: changing it syncs every per-study contract date
     so a per-study window can never silently drift behind the client window (which is what
     locked studies out even while the client contract was still active). Set a per-study date
     afterward only when a specific study needs a narrower window. */
  var setClientContract = function(field, v) {
    setF(function(p) {
      var n = {...p}; n[field] = v;
      n.studyAccess = (p.studyAccess||[]).map(function(x){
        /* r42: a study whose close date is DERIVED from its data cap + months keeps it */
        if (field === "contractEnd" && x.accessMonthsAfter != null && String(x.accessMonthsAfter) !== "") return x;
        var y={...x}; y[field]=v; return y;
      });
      return n;
    });
  };
  var save = function() {
    if (!f.name.trim()) return;
    if (f.clientType === "onetime") {
      var _pa = f.periodAccess || {};
      var _capped = (_pa.mode === "quarter" && _pa.toQ) || (_pa.mode === "date" && _pa.toDate);
      var _miss = [];
      if (!_capped) _miss.push("Data release cap (Period Access mode + \u201CTo\u201D bound)");
      if (!f.contractEnd) _miss.push("Access end date (Contract End Date)");
      if (_miss.length && !window.confirm("This ONE-TIME RELEASE client is missing:\n\n\u2022 " + _miss.join("\n\u2022 ") + "\n\nWithout these it behaves like a standard client.\nSave anyway?")) return;
    }
    if (f.clientType === "atc3_limited") {
      var _nA3 = 0;
      (f.studyAccess || []).forEach(function(x){ _nA3 += ((x.scope && x.scope.atc3) || []).length; });
      if (!_nA3 && !window.confirm("This ATC3-LIMITED client has NO ATC3 classes selected in ANY study.\n\nOpen each study row in Study Access and pick its classes in the ATC3 dropdown.\nWithout classes the client is NOT restricted (sees the full market).\nSave anyway?")) return;
    }
    var payload = {...f, id: f.id || genId("c")};
    /* r104: ONE-TIME → subscription: the release window and the fixed end date were the one-time
       limits; offer to drop them (and the per-study data caps) so the subscription really opens */
    if (f.clientType !== "onetime" && client && client.clientType === "onetime") {
      var _pa4 = payload.periodAccess || {};
      var _win4 = _pa4.mode === "quarter" ? !!(_pa4.fromQ || _pa4.toQ) : _pa4.mode === "date" ? !!(_pa4.fromDate || _pa4.toDate) : false;
      var _caps4 = (payload.studyAccess || []).filter(function(x){ return (x.periodMode === "quarter" || x.periodMode === "month") && x.periodTo; });
      var _lim4 = [];
      if (_win4) _lim4.push("the release data window (" + (_pa4.toQ || _pa4.toDate || "set") + ")");
      if (_caps4.length) _lim4.push(_caps4.length + " per-study data cap" + (_caps4.length > 1 ? "s" : "") + " with access window (" + _caps4.map(function(x){ return x.studyTypeCode + ": " + x.periodTo + (x.contractEnd ? " \u2192 closes " + x.contractEnd : ""); }).join(", ") + ")");
      if (payload.contractEnd && !_allDerived9(payload.studyAccess)) _lim4.push("the fixed access end date (" + payload.contractEnd + ")");
      if (_lim4.length) {
        if (window.confirm("This client was a ONE-TIME RELEASE. It still carries:\n\n\u2022 " + _lim4.join("\n\u2022 ") + "\n\nRemove these limits now so the subscription sees all data? (OK = remove, Cancel = keep them as they are)")) {
          payload.periodAccess = {mode:"none", fromQ:"", toQ:"", fromDate:"", toDate:""};
          payload.studyAccess = (payload.studyAccess || []).map(function(x){ return (x.periodMode === "quarter" || x.periodMode === "month") && x.periodTo ? {...x, periodMode:"all", periodTo:"", periodFrom:"", accessMonthsAfter:null, contractEnd:""} : x; });   /* the derived close date goes with the cap */
          if (payload.contractEnd && !_allDerived9(payload.studyAccess)) payload.contractEnd = "";
        }
      }
    }
    /* r43: every study has a derived window → the client-wide dates are cleared (no hidden hard cap) */
    if (_allDerived9(payload.studyAccess)) { payload.contractStart = ""; payload.contractEnd = ""; }
    if (payload.clientType !== "atc3_limited") {
      /* leaving the ATC3-limited type CLEARS the per-study ATC3 lists (except
         studies whose native scope UI offers ATC3, e.g. sales tracker) — no
         invisible filtering can survive a type switch */
      payload.studyAccess = (payload.studyAccess || []).map(function(x){
        var _nat9 = (STUDY_SCOPE_DIMS[x.studyTypeCode] || []).some(function(s9){ return s9.key === "atc3"; });
        if (_nat9 || !x.scope || !((x.scope.atc3 || []).length)) return x;
        var _sc9 = Object.assign({}, x.scope); delete _sc9.atc3;
        return {...x, scope: _sc9};
      });
    }
    delete payload._openNodes; delete payload._addCountry; delete payload._addStudy; onSave(payload);
  };

  var chkStyle = function(on, accent) {
    return {width:13,height:13,borderRadius:3,border:"1.5px solid "+(on?accent:"#cbd5e1"),
      background:on?accent:"#fff",display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:8,color:"#fff",flexShrink:0};
  };

  return (
    <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center"}}>
      <div style={{background:"#fff", borderRadius:16, padding:"28px 30px", width:740, maxHeight:"90vh", overflowY:"auto",
                   boxShadow:"0 20px 60px rgba(0,0,0,.15)"}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20}}>
          <div>
            <span style={{fontSize:16, fontWeight:800, color:"#0f172a"}}>{isNew ? "Add New Client" : "Edit Client"}</span>
            {/* audit line (Noura 2026-10-03): who created / last modified this client */}
            {!isNew ? (function(){
            var a9 = "";
            if (f.created) a9 += "Created " + f.created + (f.createdBy ? " by " + f.createdBy : "");
            if (f.updated && (f.updated !== f.created || f.updatedBy !== f.createdBy)) a9 += (a9 ? "   \u00b7   " : "") + "Last modified " + f.updated + (f.updatedBy ? " by " + f.updatedBy : "");
            return a9 ? <div style={{fontSize:9.5, fontWeight:600, color:"#94a3b8", marginTop:3}}>{"\ud83d\udd52 " + a9}</div> : null;
          })() : null}
          </div>
          <button onClick={onClose} style={{background:"none", border:"none", fontSize:18, cursor:"pointer", color:"#94a3b8"}}>✕</button>
        </div>

        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16}}>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Client Name *</label>
            <input style={adminInp} value={f.name} onChange={function(e){set("name",e.target.value);}} placeholder="Company name" />
          </div>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Contact Person</label>
            <input style={adminInp} value={f.contact} onChange={function(e){set("contact",e.target.value);}} placeholder="Full name" />
          </div>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Email</label>
            <input style={adminInp} value={f.email} onChange={function(e){set("email",e.target.value);}} placeholder="email@company.com" />
          </div>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Status</label>
            <select style={{...adminInp, cursor:"pointer"}} value={f.status} onChange={function(e){set("status",e.target.value);}}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>

        {/* ── Client Type ── */}
        <div style={{marginBottom:16, padding:"10px 14px", background:"#f8fafc", borderRadius:8, border:"1px solid #e2e8f0"}}>
          <label style={{display:"block", fontSize:11, fontWeight:800, color:"#334155", marginBottom:8}}>🏷 Client Type</label>
          <select style={{...adminInp, cursor:"pointer"}} value={f.clientType || "standard"} onChange={function(e){set("clientType", e.target.value);}}>
            <option value="standard">Standard subscription</option>
            <option value="onetime">One-time release — limited study data + fixed access end date</option>
            <option value="atc3_limited">ATC3-limited — private market: selected ATC3 classes only, set per study</option>
          </select>
          {/* r104: a release window left behind by a ONE-TIME RELEASE must never stay invisible —
              a standard / ATC3 client shows it with a one-click removal (Noura: "updated it to
              subscription but the access not updated") */}
          {(f.clientType !== "onetime") && (function(){
            var pa9 = f.periodAccess || {};
            var has9 = pa9.mode === "quarter" ? !!(pa9.fromQ || pa9.toQ) : pa9.mode === "date" ? !!(pa9.fromDate || pa9.toDate) : false;
            if (!has9) return null;
            var txt9 = pa9.mode === "quarter" ? ((pa9.fromQ || "\u2026") + " \u2192 " + (pa9.toQ || "\u2026")) : ((pa9.fromDate || "\u2026") + " \u2192 " + (pa9.toDate || "\u2026"));
            return (
              <div style={{marginTop:9, padding:"8px 11px", background:"#fff7ed", border:"1.5px solid #fdba74", borderRadius:6, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                <div style={{flex:1, minWidth:220}}>
                  <div style={{fontSize:9.5, color:"#9a3412", fontWeight:800, textTransform:"uppercase", letterSpacing:".04em"}}>{"\u26A0 Release data window still active: " + txt9}</div>
                  <div style={{fontSize:9.5, color:"#7c2d12", marginTop:2, lineHeight:1.45}}>{"Left over from the one-time release. While it stays, the client sees NO data newer than " + (pa9.toQ || pa9.toDate || "the window") + " in any study, whatever the study rows say. Remove it for a normal subscription."}</div>
                </div>
                <button type="button" onClick={function(){ setF(function(p){ return {...p, periodAccess: {mode:"none", fromQ:"", toQ:"", fromDate:"", toDate:""}}; }); }}
                  style={{padding:"6px 12px", border:"1.5px solid #ea580c", borderRadius:7, background:"#fff", color:"#c2410c", fontSize:10.5, fontWeight:800, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap"}}>{"\u2716 Remove window"}</button>
              </div>
            );
          })()}
          {(f.clientType === "onetime") && (() => {
            var pa = f.periodAccess || {};
            var capped = (pa.mode === "quarter" && pa.toQ) || (pa.mode === "date" && pa.toDate);
            var hasStudies = (f.studyAccess || []).length > 0;
            var today = new Date().toISOString().slice(0, 10);
            var endOk = !!f.contractEnd && f.contractEnd >= today;
            var it = function(ok, txt, key) {
              return <div key={key} style={{display:"flex", gap:6, alignItems:"baseline", fontSize:10, color: ok ? "#15803d" : "#b45309", fontWeight:600}}>
                <span>{ok ? "\u2713" : "\u25CB"}</span><span>{txt}</span>
              </div>;
            };
            return <div style={{marginTop:9, padding:"8px 11px", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, display:"flex", flexDirection:"column", gap:4}}>
              <div style={{fontSize:9.5, color:"#92400e", fontWeight:800, textTransform:"uppercase", letterSpacing:".04em"}}>One-time release checklist</div>
              {it(hasStudies, "Study access: add exactly the released studies (section below)", "k1")}
              {it(capped, "Data cap: set Period Access \u201CTo\u201D = last released period \u2014 the client never sees newer data, even after future loads", "k2")}
              {it(endOk, "Access end date: set Contract End Date \u2014 on that day logins AND live sessions are blocked automatically", "k3")}
              {(() => {
                /* Inline editor for the client-level period cap (Clients.PeriodFromQ/PeriodToQ,
                   server-enforced on every query). Format matches the server parser: "Q4 2025". */
                var qy = function(s){ var m = /Q([1-4])\s*(\d{4})/i.exec(String(s || "")); return m ? { q: m[1], y: m[2] } : { q: "", y: "" }; };
                var fromP = qy(pa.fromQ), toP = qy(pa.toQ);
                var years = []; var y0 = new Date().getFullYear() + 1;
                for (var yi = y0; yi >= y0 - 12; yi--) years.push(String(yi));
                var setQW = function(field, qv, yv) {
                  setF(function(p) {
                    var pp = {...(p.periodAccess || {mode:"none", fromQ:"", toQ:"", fromDate:"", toDate:""})};
                    pp[field] = (qv && yv) ? ("Q" + qv + " " + yv) : "";
                    pp.mode = (pp.toQ || pp.fromQ) ? "quarter" : "none";
                    return {...p, periodAccess: pp};
                  });
                };
                var selS = {padding:"3px 6px", fontSize:9.5, border:"1px solid #fcd34d", borderRadius:4, background:"#fff", color:"#78350f", fontFamily:"inherit", cursor:"pointer"};
                var qOpts = function(cur){ return [<option key="e" value="">\u2014</option>, <option key="1" value="1">Q1</option>, <option key="2" value="2">Q2</option>, <option key="3" value="3">Q3</option>, <option key="4" value="4">Q4</option>]; };
                return <div style={{marginTop:7, padding:"7px 10px", background:"#fff", border:"1px dashed #fcd34d", borderRadius:6}}>
                  <div style={{fontSize:9, fontWeight:800, color:"#92400e", marginBottom:5, textTransform:"uppercase", letterSpacing:".04em"}}>Release data window</div>
                  <div style={{display:"flex", gap:6, alignItems:"center", flexWrap:"wrap"}}>
                    <span style={{fontSize:9.5, color:"#78350f", fontWeight:700}}>From</span>
                    <select style={selS} value={fromP.q} onChange={function(e){ setQW("fromQ", e.target.value, e.target.value ? (fromP.y || String(y0 - 3)) : ""); }}>{qOpts()}</select>
                    <select style={selS} value={fromP.y} onChange={function(e){ setQW("fromQ", e.target.value ? (fromP.q || "1") : "", e.target.value); }}>
                      <option value="">\u2014</option>
                      {years.map(function(yv){ return <option key={yv} value={yv}>{yv}</option>; })}
                    </select>
                    <span style={{fontSize:9.5, color:"#78350f", fontWeight:700}}>\u2192 To (release cap)</span>
                    <select style={selS} value={toP.q} onChange={function(e){ setQW("toQ", e.target.value, e.target.value ? (toP.y || String(y0 - 1)) : ""); }}>{qOpts()}</select>
                    <select style={selS} value={toP.y} onChange={function(e){ setQW("toQ", e.target.value ? (toP.q || "4") : "", e.target.value); }}>
                      <option value="">\u2014</option>
                      {years.map(function(yv){ return <option key={yv} value={yv}>{yv}</option>; })}
                    </select>
                    {pa.toQ ? (
                      <span style={{fontSize:9, fontWeight:800, background:"#ecfdf5", color:"#065f46", border:"1px solid #a7f3d0", borderRadius:8, padding:"1px 8px"}}>{"Capped at " + pa.toQ}</span>
                    ) : (
                      <span style={{fontSize:9, fontWeight:700, background:"#fef2f2", color:"#b91c1c", borderRadius:8, padding:"1px 8px"}}>no cap yet</span>
                    )}
                  </div>
                  <div style={{fontSize:8.5, color:"#92400e", marginTop:4}}>
                    {"\uD83D\uDCCA From is optional (empty = from the beginning of the data). \u201CTo\u201D = the LATEST QUARTER OF DATA this client can see \u2014 every later period is hidden on every query, even after new data loads. It does NOT close the portal: logins stay open until the Contract End date above."}
                  </div>
                </div>;
              })()}
            </div>;
          })()}
        </div>

        {/* ── ATC3-limited client (Noura 2026-10-09, r39 BY STUDY): the classes
            are set PER STUDY in the Study Access rows below (each row gets an
            ATC3 dropdown). Report DECKS stay full-market — warned here. ── */}
        {(f.clientType === "atc3_limited") && (function(){
          var nSt9 = 0, nCls9 = 0, repN9 = 0;
          (f.studyAccess || []).forEach(function(x){
            var a9 = (x.scope && x.scope.atc3) || [];
            if (a9.length) { nSt9++; nCls9 += a9.length; }
            repN9 += (x.reports || []).length;
          });
          return (
            <div style={{marginBottom:16, padding:"10px 14px", background:"#eef2ff", borderRadius:8, border:"1px solid #c7d2fe"}}>
              <label style={{display:"block", fontSize:11, fontWeight:800, color:"#3730a3", marginBottom:4}}>{"\uD83E\uDDEC ATC3-limited client \u2014 set the classes PER STUDY"}</label>
              <div style={{fontSize:9, color:"#4338ca", lineHeight:1.5, marginBottom:6}}>
                {"\uD83D\uDD2C Open each study row in Study Access below \u2014 it has its own \u201CATC3\u201D dropdown. The client sees ONLY the selected classes in that study (query builder, dashboards, filters, exports, custom markets); values outside them disappear from the dropdowns too. A study left empty is NOT restricted."}
              </div>
              {nSt9 > 0 ? (
                <div style={{fontSize:9.5, color:"#166534", fontWeight:700}}>{"\u2713 " + nSt9 + " stud" + (nSt9 > 1 ? "ies" : "y") + " restricted \u00b7 " + nCls9 + " ATC3 selection" + (nCls9 > 1 ? "s" : "") + " in total."}</div>
              ) : (
                <div style={{fontSize:9.5, color:"#b45309", fontWeight:700}}>{"\u26a0 No ATC3 class selected in any study yet \u2014 the client is NOT restricted (sees the full market)."}</div>
              )}
              {repN9 > 0 && (
                <div style={{marginTop:6, padding:"6px 9px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, fontSize:9.5, color:"#b91c1c", fontWeight:700}}>
                  {"\u26a0 This client has " + repN9 + " report grant" + (repN9 > 1 ? "s" : "") + ". Report documents are FULL-MARKET \u2014 they are NOT filtered by the ATC3 limit. Keep reports granted only if seeing full-market numbers is acceptable."}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Contract Validity Window ──
            If today is OUTSIDE [start, end], login is blocked at the server with a friendly
            message. Both fields optional — empty = no bound on that side. */}
        <div style={Object.assign({marginBottom:16, padding:"10px 14px", background:"#fefce8", borderRadius:8, border:"1px solid #fde68a"}, _allDerived9(f.studyAccess) ? {opacity:.55} : null)}>
          <label style={{display:"block", fontSize:11, fontWeight:800, color:"#854d0e", marginBottom:4}}>{"\uD83D\uDCC5 Contract Validity (Access Window)" + (_allDerived9(f.studyAccess) ? "  \uD83D\uDD12 disabled \u2014 every study has its own window" : "")}</label>
          {_allDerived9(f.studyAccess) && (
            <div style={{fontSize:9.5, color:"#92400e", fontWeight:700, marginBottom:6}}>
              {"\uD83D\uDD12 All " + (f.studyAccess || []).length + " studies of this client have a data cap + access window, so portal access is decided by the studies (open until " + (_portalUntil9(f.studyAccess) || "\u2014") + "). These client-wide dates are disabled and cleared on save."}
            </div>
          )}
          {/* what this field controls (Noura 2026-10-06): the PORTAL closing date */}
          <div style={{fontSize:9, color:"#92400e", lineHeight:1.5, marginBottom:8}}>
            {"⏻ OPTIONAL HARD CAP. Portal access now follows the STUDIES: each study row below has its own data cap + access window, and the portal stays open while ANY of the client's studies is still open (login is refused only when all of them are closed). Fill these dates only to force an earlier cut-off for every study at once (e.g. an emergency stop) — on the End date logins AND live sessions are cut off. Leave blank in normal use."}
          </div>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:3}}>Start Date</label>
              <input type="date" style={adminInp} disabled={_allDerived9(f.studyAccess)} value={_allDerived9(f.studyAccess) ? "" : (f.contractStart||"")} onChange={function(e){setClientContract("contractStart", e.target.value);}} />
            </div>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:3}}>End Date</label>
              <input type="date" style={adminInp} disabled={_allDerived9(f.studyAccess)} value={_allDerived9(f.studyAccess) ? "" : (f.contractEnd||"")} onChange={function(e){setClientContract("contractEnd", e.target.value);}} />
            </div>
          </div>
          {f.contractStart && f.contractEnd && f.contractEnd < f.contractStart && (
            <div style={{marginTop:6, fontSize:10, color:"#b91c1c", fontWeight:600}}>⚠ End date is before start date — please correct.</div>
          )}
          {(() => {
            if (!f.contractStart && !f.contractEnd) {
              var _pu9 = _portalUntil9(f.studyAccess);
              var _tdy9 = new Date().toISOString().slice(0,10);
              if (!_pu9) return <div style={{marginTop:6, fontSize:9.5, color:"#854d0e", fontStyle:"italic"}}>{"No hard cap. No study has a close date yet \u2014 portal access is unrestricted until you set the studies' access windows below."}</div>;
              if (_pu9 < _tdy9) return <div style={{marginTop:6, fontSize:10, color:"#b91c1c", fontWeight:600}}>{"\u26a0 All studies are closed (latest close date " + _pu9 + ") \u2014 users of this client cannot log in."}</div>;
              return <div style={{marginTop:6, fontSize:10, color:"#15803d", fontWeight:600}}>{"\u2713 No hard cap \u2014 portal open until " + _pu9 + " (the latest-closing study below)."}</div>;
            }
            var today = new Date().toISOString().slice(0,10);
            if (f.contractStart && today < f.contractStart) return <div style={{marginTop:6, fontSize:10, color:"#1d4ed8", fontWeight:600}}>ℹ Contract starts on {f.contractStart} — users cannot log in until then.</div>;
            if (f.contractEnd && today > f.contractEnd) return <div style={{marginTop:6, fontSize:10, color:"#b91c1c", fontWeight:600}}>⚠ Contract has expired ({f.contractEnd}) — users are currently locked out.</div>;
            return <div style={{marginTop:6, fontSize:10, color:"#15803d", fontWeight:600}}>✓ Contract is currently active.</div>;
          })()}
        </div>

        {/* ── Study Access: Add country + study type ── */}
        {(() => {
          var selCountry = f._addCountry || COUNTRIES[0];
          var availStudies = COUNTRY_STUDY_MAP[selCountry] || [];
          var selStudy = f._addStudy || (availStudies.length ? availStudies[0].code : "");
          var addStudyAccess = function(country, stCode) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).slice();
              if (arr.some(function(x){return x.country===country && x.studyTypeCode===stCode;})) return p;
              arr.push({country:country, studyTypeCode:stCode, features:[], dashboards:[], periodMode:"all", periodFrom:"", periodTo:"", periodPermission:"monthly", contractStart:"", contractEnd:""});
              return {...p, studyAccess:arr, studyTypes:(p.studyTypes||[]).concat(stCode).filter(function(v,i,a){return a.indexOf(v)===i;})};
            });
          };
          return (
            <div style={{marginBottom:14, padding:"10px 14px", background:"#eff6ff", borderRadius:8, border:"1px solid #bfdbfe"}}>
              <label style={{display:"block", fontSize:11, fontWeight:800, color:"#1e40af", marginBottom:6}}>📋 Study Access</label>
              {adminScope && (
                <div style={{marginBottom:8,padding:"6px 10px",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:7,fontSize:9.5,color:"#92400e",fontWeight:600}}>
                  {"\uD83D\uDD12 Your study scope: " + adminScope.map(function(c9){ return String(c9).replace(/_/g, " ").toUpperCase(); }).join(", ") + " \u2014 changes to other studies are ignored (their access is preserved), and company-wide settings (status, contract, period caps, export flags) only save when ALL the client's studies are in your scope."}
                </div>
              )}
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
                <div style={{flex:"1 1 140px"}}>
                  <label style={{display:"block", fontSize:9, fontWeight:700, color:"#1e40af", marginBottom:3}}>Country</label>
                  <select value={selCountry} onChange={function(e){
                      var newC = e.target.value;
                      var newStudies = COUNTRY_STUDY_MAP[newC] || [];
                      setF(function(p){return{...p, _addCountry:newC, _addStudy:newStudies.length?newStudies[0].code:""};});
                    }}
                    style={{...adminInp,width:"100%",fontSize:10.5,cursor:"pointer"}}>
                    {COUNTRIES.map(function(c){return <option key={c} value={c}>{c}</option>;})}
                  </select>
                </div>
                <div style={{flex:"1 1 180px"}}>
                  <label style={{display:"block", fontSize:9, fontWeight:700, color:"#1e40af", marginBottom:3}}>Type</label>
                  <select value={selStudy} onChange={function(e){set("_addStudy",e.target.value);}}
                    style={{...adminInp,width:"100%",fontSize:10.5,cursor:"pointer"}}>
                    {availStudies.map(function(st){
                      return <option key={st.code} value={st.code}>{(st.cat==="private"?"🔒 ":"🌐 ")+st.name}</option>;
                    })}
                    {availStudies.length===0 && <option value="">No study types</option>}
                  </select>
                </div>
                <button onClick={function(){
                  if(selCountry && selStudy) addStudyAccess(selCountry, selStudy);
                }} style={{...adminBtn("#2563eb","#fff","#2563eb"),fontSize:10.5,padding:"7px 18px",fontWeight:700}}>+ Add</button>
              </div>
              {(f.studyAccess||[]).length > 0 && (
                <div style={{fontSize:8.5, color:"#3b82f6", marginTop:6}}>{(f.studyAccess||[]).length} study access entries configured — expand tree below to manage features & dashboards</div>
              )}
            </div>
          );
        })()}

        {/* ══════ TREE-SCOPED STUDY ACCESS ══════ */}
        {(() => {
          var sa = f.studyAccess || [];
          var openNodes = f._openNodes || {};
          var setOpen = function(k) { setF(function(p){ var n={...p}; var o={...(p._openNodes||{})}; o[k]=!o[k]; n._openNodes=o; return n; }); };

          var addStudyAccess = function(country, stCode) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).slice();
              /* Check if already exists */
              if (arr.some(function(x){return x.country===country && x.studyTypeCode===stCode;})) return p;
              arr.push({country:country, studyTypeCode:stCode, features:[], dashboards:[], periodMode:"all", periodFrom:"", periodTo:"", periodPermission:"monthly", contractStart:"", contractEnd:""});
              return {...p, studyAccess:arr, studyTypes:(p.studyTypes||[]).concat(stCode).filter(function(v,i,a){return a.indexOf(v)===i;})};
            });
          };
          var removeStudyAccess = function(country, stCode) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).filter(function(x){return !(x.country===country && x.studyTypeCode===stCode);});
              var remainingCodes = arr.map(function(x){return x.studyTypeCode;}).filter(function(v,i,a){return a.indexOf(v)===i;});
              return {...p, studyAccess:arr, studyTypes:remainingCodes};
            });
          };
          var togStudyFeature = function(country, stCode, dimKey) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var feats = (x.features||[]).slice();
                var idx = feats.indexOf(dimKey);
                if (idx>=0) feats.splice(idx,1); else feats.push(dimKey);
                return {...x, features:feats};
              });
              return {...p, studyAccess:arr};
            });
          };
          var togStudyDash = function(country, stCode, dashKey) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                return {...x, dashboards:_togDashList(x.dashboards, dashKey)};
              });
              return {...p, studyAccess:arr};
            });
          };
          var setAllFeatures = function(country, stCode) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                return {...x, features:DIMENSION_KEYS.slice()};
              });
              return {...p, studyAccess:arr};
            });
          };
          var setAllDash = function(country, stCode) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                return {...x, dashboards:_getDashKeysForStudy(stCode).map(function(d){return d.key;})};
              });
              return {...p, studyAccess:arr};
            });
          };
          var setStudyProp = function(country, stCode, key, val) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var n = {...x}; n[key] = val; return n;
              });
              return {...p, studyAccess:arr};
            });
          };
          var togStudyMeasure = function(country, stCode, mKey) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var ms = (x.measures||_getMeasureKeysForStudy(x.studyTypeCode).map(function(m){return m.key;})).slice();
                var idx = ms.indexOf(mKey);
                if (idx>=0) ms.splice(idx,1); else ms.push(mKey);
                return {...x, measures:ms};
              });
              return {...p, studyAccess:arr};
            });
          };
          var togStudyStatistic = function(country, stCode, sKey) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var ss = (x.statistics||_getStatKeysForStudy(x.studyTypeCode).map(function(s){return s.key;})).slice();
                var idx = ss.indexOf(sKey);
                if (idx>=0) ss.splice(idx,1); else ss.push(sKey);
                return {...x, statistics:ss};
              });
              return {...p, studyAccess:arr};
            });
          };
          var togStudyReport = function(country, stCode, rKey) {
            setF(function(p) {
              var arr = (p.studyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var rs = (x.reports||[]).slice();
                var idx = rs.indexOf(rKey);
                if (idx>=0) rs.splice(idx,1); else rs.push(rKey);
                return {...x, reports:rs};
              });
              return {...p, studyAccess:arr};
            });
          };

          /* Group studyAccess by country */
          var byCountry = {};
          sa.forEach(function(x) { var c = x.country || "(Global)"; if(!byCountry[c]) byCountry[c]=[]; byCountry[c].push(x); });
          var countryKeys = Object.keys(byCountry);

          return (
            <div style={{marginBottom:14, padding:"10px 14px", background:"#f8fafc", borderRadius:8, border:"1px solid #e2e8f0"}}>
              <label style={{display:"block", fontSize:11, fontWeight:800, color:"#0f172a", marginBottom:8}}>🌳 Study type access tree</label>
              <div style={{fontSize:9, color:"#64748b", marginBottom:8}}>Country → Study type → Features + Dashboards. Click to expand.</div>

              {countryKeys.map(function(ck) {
                var countryOpen = openNodes["c_"+ck];
                var entries = byCountry[ck];
                return (
                  <div key={ck} style={{marginBottom:6, border:"1px solid #e2e8f0", borderRadius:8, overflow:"hidden"}}>
                    {/* Country header */}
                    <div onClick={function(){setOpen("c_"+ck);}}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",cursor:"pointer",userSelect:"none",
                              background:countryOpen?"#dbeafe":"#eff6ff",borderBottom:countryOpen?"1px solid #bfdbfe":"none"}}>
                      <span style={{fontSize:10,color:"#1d4ed8"}}>{countryOpen?"▼":"▶"}</span>
                      <span style={{fontSize:11,fontWeight:700,color:"#1e40af",flex:1}}>🌍 {ck}</span>
                      <span style={{fontSize:9,color:"#64748b",background:"#fff",borderRadius:10,padding:"1px 6px",border:"1px solid #e2e8f0"}}>{entries.length} study types</span>
                    </div>

                    {countryOpen && entries.map(function(entry) {
                      var stKey = ck+"_"+entry.studyTypeCode;
                      /* Study-scoped admin: out-of-scope studies are LOCKED — visible but
                         not expandable, not editable, not deletable (server enforces too) */
                      var _lockedSt = adminScope && adminScope.indexOf(entry.studyTypeCode) < 0;
                      var stOpen = !_lockedSt && openNodes["st_"+stKey];
                      var stInfo = STUDY_TYPE_CODES.find(function(s){return s.code===entry.studyTypeCode;});
                      var csInfo = (COUNTRY_STUDY_MAP[ck]||[]).find(function(s){return s.code===entry.studyTypeCode;});
                      var stLabel = csInfo ? csInfo.name : (stInfo ? stInfo.name : entry.studyTypeCode);
                      var stCat = csInfo ? csInfo.cat : (stInfo ? stInfo.cat : "private");
                      var fCount = (entry.features||[]).length;
                      var dCount = (entry.dashboards||[]).length;

                      return (
                        <div key={stKey} style={{marginLeft:16, borderTop:"1px solid #f1f5f9"}}>
                          {/* Study type header */}
                          <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",cursor:_lockedSt?"not-allowed":"pointer",userSelect:"none",opacity:_lockedSt?0.55:1,background:_lockedSt?"#f8fafc":undefined}}
                               onClick={function(){ if (_lockedSt) return; setOpen("st_"+stKey); }}
                               title={_lockedSt ? "Outside your study scope — managed by other admins" : undefined}>
                            <span style={{fontSize:9,color:_lockedSt?"#94a3b8":"#7c3aed"}}>{_lockedSt?"\uD83D\uDD12":stOpen?"▼":"▶"}</span>
                            <span style={{fontSize:10.5,fontWeight:600,color:_lockedSt?"#64748b":"#4c1d95",flex:1}}>
                              {stCat==="private"?"🔒":"🌐"} {stLabel}
                            </span>
                            {_lockedSt && <span style={{fontSize:8,fontWeight:800,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:4,padding:"1px 6px"}}>LOCKED — OUTSIDE YOUR SCOPE</span>}
                            <span style={{fontSize:8,color:"#059669",background:"#ecfdf5",borderRadius:4,padding:"1px 5px"}}>{fCount} dims</span>
                            <span style={{fontSize:8,color:"#2563eb",background:"#eff6ff",borderRadius:4,padding:"1px 5px"}}>{dCount} dash</span>
                            {(function(){ var sc = entry.scope || {}; var n = 0; Object.keys(sc).forEach(function(k){ n += (sc[k]||[]).length; }); return n > 0 ? <span style={{fontSize:8,color:"#9d174d",background:"#fdf2f8",border:"1px solid #f9a8d4",borderRadius:4,padding:"1px 5px"}}>🔐 scoped ({n})</span> : null; })()}
                            {/* Delete-study button — properly sized + confirmation. Removing
                                a study at the client level cascades to every user of that
                                client, so the warning is more emphatic than the user-form one. */}
                            {!_lockedSt && <button onClick={function(e){
                                e.stopPropagation();
                                if (window.confirm("Remove study access \"" + stLabel + "\" from this CLIENT?\n\nThis cascades to every user of the client — they will all lose access to this study type. Continue?")) {
                                  removeStudyAccess(ck, entry.studyTypeCode);
                                }
                              }}
                              title={"Remove " + stLabel + " from client"}
                              onMouseOver={function(e){ e.currentTarget.style.background = "#fee2e2"; }}
                              onMouseOut={function(e){ e.currentTarget.style.background = "#fef2f2"; }}
                              style={{padding:"2px 7px",border:"1px solid #fecaca",borderRadius:4,background:"#fef2f2",
                                      color:"#dc2626",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                                      lineHeight:1.2}}>
                              ✕ Delete
                            </button>}
                          </div>

                          {stOpen && (
                            <div style={{padding:"6px 10px 10px 20px"}}>
                              {/* ── Period Permission ── */}
                              <div style={{marginBottom:8,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                <span style={{fontSize:9,fontWeight:700,color:"#7c3aed",textTransform:"uppercase"}}>📅 Period</span>
                                {[{k:"monthly",lb:"Full (Monthly)"},{k:"quarterly",lb:"Quarterly"},{k:"quarterly_open",lb:"Quarterly + open qtr*"},{k:"semesterly",lb:"Semesterly"},{k:"yearly",lb:"Yearly"}].map(function(opt){
                                  var on = (entry.periodPermission||"monthly")===opt.k;
                                  return (
                                    <span key={opt.k} onClick={function(){setStudyProp(ck,entry.studyTypeCode,"periodPermission",opt.k);}}
                                      style={{padding:"2px 8px",borderRadius:4,fontSize:8.5,cursor:"pointer",userSelect:"none",
                                              border:"1px solid "+(on?"#7c3aed":"#e2e8f0"),background:on?"#ede9fe":"#fff",
                                              color:on?"#4c1d95":"#94a3b8",fontWeight:on?600:400}}>{opt.lb}</span>
                                  );
                                })}
                              </div>
                              {/* ── r138: TAB VISIBILITY — which of Market / Dashboards / Reports this client's
                                  users see in this study. Ticked = shown. Dashboards / Reports also need
                                  their grants below; un-ticking here hides the tab regardless. */}
                              <div style={{marginBottom:8,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                <span style={{fontSize:9,fontWeight:700,color:"#0f766e",textTransform:"uppercase"}}>🗂 Tabs</span>
                                {[{k:"market",lb:"📊 Market"},{k:"dashboards",lb:"📈 Dashboards"},{k:"reports",lb:"📑 Reports"}].map(function(tb){
                                  var hidden = (entry.hiddenTabs||[]).indexOf(tb.k) >= 0;
                                  return (
                                    <span key={tb.k} title={hidden ? "Hidden for this client in this study — click to show" : "Shown — click to hide the tab for this client in this study"}
                                      onClick={function(){ var cur = (entry.hiddenTabs||[]).slice(); var ix = cur.indexOf(tb.k); if (ix >= 0) cur.splice(ix, 1); else cur.push(tb.k); setStudyProp(ck,entry.studyTypeCode,"hiddenTabs",cur); }}
                                      style={{padding:"2px 8px",borderRadius:4,fontSize:8.5,cursor:"pointer",userSelect:"none",
                                              border:"1px solid "+(hidden?"#e2e8f0":"#0f766e"),background:hidden?"#fff":"#ccfbf1",
                                              color:hidden?"#94a3b8":"#134e4a",fontWeight:hidden?400:600,textDecoration:hidden?"line-through":"none"}}>{(hidden ? "☐ " : "☑ ") + tb.lb}</span>
                                  );
                                })}
                                {(entry.hiddenTabs||[]).length ? <span style={{fontSize:8.5,color:"#b45309"}}>{"hidden: " + (entry.hiddenTabs||[]).join(", ")}</span> : null}
                              </div>
                              {/* ── Per-study DATA ACCESS + ACCESS WINDOW (Noura 2026-10-11, r42) ──
                                  Level 1: the latest period (quarter or month) the client may READ in
                                  this study — enforced on every query, hidden from the period pickers.
                                  Level 2: months the study stays OPEN after that period; the close date
                                  (end of the period + months) is derived and stored as this study's
                                  contract end — login hides the study after it. The portal itself stays
                                  open while ANY study is open (client-wide box = optional hard cap). */}
                              {(function(){
                                var pm = String(entry.periodMode || "all").toLowerCase(); if (pm !== "quarter" && pm !== "month") pm = "all";
                                var pt = entry.periodTo || "";
                                var qm = /Q([1-4])\s*(\d{4})/i.exec(pt), mm = /(\d{4})-(\d{1,2})/.exec(pt);
                                var capQ = (pm === "quarter" && qm) ? { q: qm[1], y: qm[2] } : { q: "", y: "" };
                                var capM = (pm === "month" && mm) ? { m: String(parseInt(mm[2], 10)), y: mm[1] } : { m: "", y: "" };
                                var yrs = []; var y0 = new Date().getFullYear() + 2; for (var yi = y0; yi >= y0 - 12; yi--) yrs.push(String(yi));
                                var nM = parseInt(entry.accessMonthsAfter, 10); var hasM = !isNaN(nM) && nM >= 0;
                                var capEnd = _capEndIso9(pm, pt);
                                var derivedEnd = (hasM && capEnd) ? _addMonthsIso9(capEnd, nM) : "";
                                var effEnd = derivedEnd || entry.contractEnd || "";
                                var setP = function(field, v){ setStudyProp(ck, entry.studyTypeCode, field, v); };
                                var selS = {padding:"3px 6px", fontSize:9.5, border:"1px solid #fde68a", borderRadius:4, background:"#fff", color:"#78350f", fontFamily:"inherit", cursor:"pointer"};
                                var inpS = {padding:"3px 6px", fontSize:9.5, border:"1px solid #fde68a", borderRadius:4, color:"#78350f", background:"#fff", fontFamily:"inherit"};
                                var lbl = {fontSize:8.5, fontWeight:700, color:"#854d0e", marginBottom:2};
                                var MONTHS9 = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                                var t = new Date().toISOString().slice(0,10);
                                var cs = entry.contractStart || "";
                                return (
                                  <div style={{marginBottom:8, padding:"6px 8px", background:"#fefce8", borderRadius:5, border:"1px solid #fde68a"}}>
                                    <div style={{fontSize:9, fontWeight:700, color:"#854d0e", textTransform:"uppercase", marginBottom:4}}>{"\uD83D\uDCC5 Data access & access window \u2014 this study"}</div>
                                    {/* Level 1 — latest data period */}
                                    <div style={{display:"flex", gap:6, alignItems:"center", flexWrap:"wrap"}}>
                                      <span style={{fontSize:9, color:"#78350f", fontWeight:700, minWidth:118}}>{"1 \u00b7 Latest data period"}</span>
                                      <select style={selS} value={pm} onChange={function(e){ var v = e.target.value; setP("periodMode", v); if (v !== pm) setP("periodTo", ""); }}>
                                        <option value="all">No cap (all data)</option>
                                        <option value="quarter">By quarter</option>
                                        <option value="month">By month</option>
                                      </select>
                                      {pm === "quarter" && (
                                        <span style={{display:"flex", gap:4}}>
                                          <select style={selS} value={capQ.q} onChange={function(e){ var q = e.target.value; setP("periodTo", (q && (capQ.y || String(y0 - 2))) ? ("Q" + q + " " + (capQ.y || String(y0 - 2))) : ""); }}>
                                            <option value="">{"Q\u2014"}</option><option value="1">Q1</option><option value="2">Q2</option><option value="3">Q3</option><option value="4">Q4</option>
                                          </select>
                                          <select style={selS} value={capQ.y} onChange={function(e){ var y = e.target.value; setP("periodTo", y ? ("Q" + (capQ.q || "4") + " " + y) : ""); }}>
                                            <option value="">{"\u2014"}</option>
                                            {yrs.map(function(yv){ return <option key={yv} value={yv}>{yv}</option>; })}
                                          </select>
                                        </span>
                                      )}
                                      {pm === "month" && (
                                        <span style={{display:"flex", gap:4}}>
                                          <select style={selS} value={capM.m} onChange={function(e){ var m = e.target.value; setP("periodTo", m ? ((capM.y || String(y0 - 2)) + "-" + _pad2_9(m)) : ""); }}>
                                            <option value="">{"\u2014"}</option>
                                            {MONTHS9.map(function(mn, i){ return <option key={mn} value={String(i + 1)}>{mn}</option>; })}
                                          </select>
                                          <select style={selS} value={capM.y} onChange={function(e){ var y = e.target.value; setP("periodTo", y ? (y + "-" + _pad2_9(capM.m || "12")) : ""); }}>
                                            <option value="">{"\u2014"}</option>
                                            {yrs.map(function(yv){ return <option key={yv} value={yv}>{yv}</option>; })}
                                          </select>
                                        </span>
                                      )}
                                      {pm !== "all" && (pt
                                        ? <span style={{fontSize:9, fontWeight:800, background:"#ecfdf5", color:"#065f46", border:"1px solid #a7f3d0", borderRadius:8, padding:"1px 8px"}}>{"Data up to " + pt}</span>
                                        : <span style={{fontSize:9, fontWeight:700, background:"#fef2f2", color:"#b91c1c", borderRadius:8, padding:"1px 8px"}}>pick the period</span>)}
                                    </div>
                                    {/* Level 2 — access window */}
                                    <div style={{display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", marginTop:5}}>
                                      <span style={{fontSize:9, color:"#78350f", fontWeight:700, minWidth:118}}>{"2 \u00b7 Access window"}</span>
                                      <input type="number" min="0" max="120" value={hasM ? String(nM) : ""} placeholder={"\u2014"}
                                        onChange={function(e){ setP("accessMonthsAfter", e.target.value === "" ? null : e.target.value); }}
                                        style={Object.assign({}, inpS, {width:54})} />
                                      <span style={{fontSize:9, color:"#78350f"}}>months after the latest data period</span>
                                      {derivedEnd
                                        ? <span style={{fontSize:9, fontWeight:800, background:"#eef2ff", color:"#3730a3", border:"1px solid #c7d2fe", borderRadius:8, padding:"1px 8px"}}>{"\u2192 study closes " + derivedEnd}</span>
                                        : (hasM && !capEnd ? <span style={{fontSize:9, fontWeight:700, background:"#fef2f2", color:"#b91c1c", borderRadius:8, padding:"1px 8px"}}>set the latest data period first</span> : null)}
                                    </div>
                                    {/* dates: start (manual) + close (derived or manual) */}
                                    <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginTop:5}}>
                                      <div>
                                        <div style={lbl}>{derivedEnd ? "Start date \u2014 not used (window derived)" : "Start date (optional)"}</div>
                                        <input type="date" value={cs} disabled={!!derivedEnd} onChange={function(e){ setP("contractStart", e.target.value); }}
                                          style={Object.assign({}, inpS, {width:"100%", boxSizing:"border-box", background: derivedEnd ? "#f1f5f9" : "#fff", color: derivedEnd ? "#94a3b8" : "#78350f"})} />
                                      </div>
                                      <div>
                                        <div style={lbl}>{derivedEnd ? "Close date \u2014 derived (period end + months)" : "Close date \u2014 manual (no months set)"}</div>
                                        <input type="date" value={effEnd} disabled={!!derivedEnd} onChange={function(e){ setP("contractEnd", e.target.value); }}
                                          style={Object.assign({}, inpS, {width:"100%", boxSizing:"border-box", background: derivedEnd ? "#fef3c7" : "#fff"})} />
                                      </div>
                                    </div>
                                    {(function(){
                                      if (!cs && !effEnd) return null;
                                      if (cs && effEnd && effEnd < cs) return <div style={{marginTop:4, fontSize:9, color:"#b91c1c", fontWeight:600}}>{"\u26a0 Close date is before the start date"}</div>;
                                      if (cs && t < cs) return <div style={{marginTop:4, fontSize:9, color:"#1d4ed8", fontWeight:600}}>{"\u2139 Starts " + cs}</div>;
                                      if (effEnd && t > effEnd) return <div style={{marginTop:4, fontSize:9, color:"#b91c1c", fontWeight:600}}>{"\u26a0 Closed " + effEnd + " \u2014 study hidden from this client's users"}</div>;
                                      return <div style={{marginTop:4, fontSize:9, color:"#15803d", fontWeight:600}}>{"\u2713 Open" + (effEnd ? " until " + effEnd : "")}</div>;
                                    })()}
                                    <div style={{fontSize:8, color:"#92400e", lineHeight:1.5, marginTop:4}}>
                                      {"\u2139 DATA: nothing newer than the latest data period is shown for this study (queries, dashboards, pickers). ACCESS: after the close date this study disappears from the client's study list; the portal stays open while ANY of their studies is still open \u2014 when all are closed, login is refused with a clear message."}
                                    </div>
                                  </div>
                                );
                              })()}
                              {/* ── Grouped Features tree ── */}
                              {(() => {
                                var groups = STUDY_FEATURE_GROUPS[entry.studyTypeCode] || STUDY_FEATURE_GROUPS["iraq"] || [];
                                var allGroupDims = [];
                                groups.forEach(function(g){g.dims.forEach(function(d){ if (d !== FULL_REPORT_FEATURE) allGroupDims.push(d); });});   /* r88: full reports never by "All" */
                                return (
                                  <div style={{marginBottom:8}}>
                                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                                      <span style={{fontSize:9,fontWeight:700,color:"#059669",textTransform:"uppercase"}}>Features / Dimensions</span>
                                      <span onClick={function(){setF(function(p){var arr=(p.studyAccess||[]).map(function(x){return x.country===ck&&x.studyTypeCode===entry.studyTypeCode?{...x,features:allGroupDims.slice()}:x;});return{...p,studyAccess:arr};});}}
                                        style={{fontSize:8,color:"#059669",cursor:"pointer",textDecoration:"underline"}}>All</span>
                                      <span onClick={function(){setF(function(p){var arr=(p.studyAccess||[]).map(function(x){return x.country===ck&&x.studyTypeCode===entry.studyTypeCode?{...x,features:[]}:x;});return{...p,studyAccess:arr};});}}
                                        style={{fontSize:8,color:"#dc2626",cursor:"pointer",textDecoration:"underline"}}>Clear</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>Empty = all allowed</span>
                                    </div>
                                    {groups.map(function(grp) {
                                      var grpKey = ck+"_"+entry.studyTypeCode+"_"+grp.group;
                                      var grpOpen = openNodes["fg_"+grpKey];
                                      var grpOnCount = grp.dims.filter(function(d){return (entry.features||[]).indexOf(d)>=0;}).length;
                                      var allOn = grpOnCount === grp.dims.length;
                                      var someOn = grpOnCount > 0 && !allOn;
                                      var togGrp = function(){
                                        setF(function(p){
                                          var arr=(p.studyAccess||[]).map(function(x){
                                            if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                            var feats=(x.features||[]).slice();
                                            if(allOn){ grp.dims.forEach(function(d){var i=feats.indexOf(d);if(i>=0)feats.splice(i,1);}); }
                                            else { grp.dims.forEach(function(d){if(feats.indexOf(d)<0)feats.push(d);}); }
                                            return{...x,features:feats};
                                          });
                                          return{...p,studyAccess:arr};
                                        });
                                      };
                                      return (
                                        <div key={grp.group} style={{marginBottom:3,border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden"}}>
                                          <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                        background:grpOpen?"#f0fdf4":"#fafafa"}}
                                               onClick={function(){setOpen("fg_"+grpKey);}}>
                                            <div onClick={function(e){e.stopPropagation();togGrp();}}
                                              style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(allOn||someOn?"#059669":"#cbd5e1"),
                                                      background:allOn?"#059669":someOn?"#bbf7d0":"#fff",display:"flex",alignItems:"center",
                                                      justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{allOn?"✓":someOn?"—":""}</div>
                                            <span style={{fontSize:9,color:grpOpen?"#166534":"#64748b",fontWeight:600,flex:1}}>{grp.group}</span>
                                            <span style={{fontSize:8,color:"#94a3b8"}}>{grpOnCount}/{grp.dims.length}</span>
                                            <span style={{fontSize:8,color:"#94a3b8"}}>{grpOpen?"▼":"▶"}</span>
                                          </div>
                                          {grpOpen && (
                                            <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                              {grp.dims.map(function(dk){
                                                var on=(entry.features||[]).indexOf(dk)>=0;
                                                return(
                                                  <span key={dk} onClick={function(){togStudyFeature(ck,entry.studyTypeCode,dk);}}
                                                    style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                            cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#059669":"#e2e8f0"),
                                                            background:on?"#dcfce7":"#fff",color:on?"#166534":"#94a3b8",fontWeight:on?600:400}}>
                                                    <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(on?"#059669":"#cbd5e1"),
                                                                  background:on?"#059669":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                                  fontSize:6,color:"#fff"}}>{on?"✓":""}</div>
                                                    {FEAT_LBL[dk]||dk}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                              {/* ── Data scope (value-level restriction, e.g. ATC3) ── */}
                              {(function(){
                                var _sds9 = (STUDY_SCOPE_DIMS[entry.studyTypeCode] || []).slice();
                                /* ATC3-limited client type (Noura 2026-10-09 r39, BY STUDY): every
                                   study row gets its own ATC3 dropdown; empty = study unrestricted */
                                if (f.clientType === "atc3_limited" && !_sds9.some(function(s9){ return s9.key === "atc3"; }))
                                  _sds9.unshift({key:"atc3", label:"ATC3", desc:"ATC3-limited client: this study shows ONLY the selected ATC3 classes (empty = this study is NOT restricted)"});
                                return _sds9;
                              })().map(function(sd){
                                var curVals = (entry.scope && entry.scope[sd.key]) || [];
                                var setVals = function(nx){
                                  setF(function(p){
                                    var arr=(p.studyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      var sc = Object.assign({}, x.scope || {}); sc[sd.key] = nx;
                                      return {...x, scope: sc};
                                    });
                                    return {...p, studyAccess:arr};
                                  });
                                };
                                return (
                                  <div key={sd.key} style={{border:"1px solid #f9a8d4",borderRadius:5,padding:"5px 8px",marginTop:3,marginBottom:3,background:"#fff7fb"}}>
                                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                                      <span style={{fontSize:9,color:"#9d174d",fontWeight:700}}>🔐 Data scope — {sd.label}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{sd.desc}</span>
                                    </div>
                                    <ScopeDimPicker studyType={entry.studyTypeCode} dimKey={sd.key} label={sd.label} value={curVals} onChange={setVals} />
                                  </div>
                                );
                              })}
                              {/* ── Dashboards group ── */}
                              {(() => {
                                var _dashKeys = _getDashKeysForStudy(entry.studyTypeCode);
                                var dGrpKey = ck+"_"+entry.studyTypeCode+"_dash";
                                var dGrpOpen = openNodes["fg_"+dGrpKey];
                                var dOnCount = _dashKeys.filter(function(d){return (entry.dashboards||[]).indexOf(d.key)>=0;}).length;
                                var dAllOn = dOnCount === _dashKeys.length;
                                var dSomeOn = dOnCount > 0 && !dAllOn;
                                var togAllDash = function(){
                                  setF(function(p){
                                    var arr=(p.studyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      return{...x,dashboards:dAllOn?[]:_dashKeys.map(function(d){return d.key;})};
                                    });
                                    return{...p,studyAccess:arr};
                                  });
                                };
                                return (
                                  <div style={{border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden"}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                  background:dGrpOpen?"#eff6ff":"#fafafa"}}
                                         onClick={function(){setOpen("fg_"+dGrpKey);}}>
                                      <div onClick={function(e){e.stopPropagation();togAllDash();}}
                                        style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(dAllOn||dSomeOn?"#2563eb":"#cbd5e1"),
                                                background:dAllOn?"#2563eb":dSomeOn?"#bfdbfe":"#fff",display:"flex",alignItems:"center",
                                                justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{dAllOn?"✓":dSomeOn?"—":""}</div>
                                      <span style={{fontSize:9,color:dGrpOpen?"#1e40af":"#64748b",fontWeight:600,flex:1}}>📈 Dashboards</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{dOnCount}/{_dashKeys.length}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{dGrpOpen?"▼":"▶"}</span>
                                    </div>
                                    {dGrpOpen && (
                                      <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                        {_dashKeys.map(function(db){
                                          var on=_dashOn(entry.dashboards, db.key);
                                          return(
                                            <span key={db.key} onClick={function(){togStudyDash(ck,entry.studyTypeCode,db.key);}} title={_dashParentOf(db.key) && on && (entry.dashboards||[]).indexOf(_dashParentOf(db.key))>=0 ? "Included with " + (db.key==="product_overview" ? "Leading Products" : "Leading Manufacturers") : ""}
                                              style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                      cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#2563eb":"#e2e8f0"),
                                                      background:on?"#dbeafe":"#fff",color:on?"#1e40af":"#94a3b8",fontWeight:on?600:400}}>
                                              <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(on?"#2563eb":"#cbd5e1"),
                                                            background:on?"#2563eb":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                            fontSize:6,color:"#fff"}}>{on?"✓":""}</div>
                                              {db.icon} {db.label}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* ── Reports group ── */}
                              {(() => {
                                var rGrpKey = ck+"_"+entry.studyTypeCode+"_rep";
                                var _keys9 = _repKeysFor(entry.studyTypeCode); /* only reports this study actually has */
                                var rGrpOpen = openNodes["fg_"+rGrpKey];
                                var entryReports = entry.reports || [];
                                var rOnCount = _keys9.filter(function(r){return entryReports.indexOf(r.key)>=0;}).length;
                                var rAllOn = _keys9.length > 0 && rOnCount === _keys9.length;
                                var rSomeOn = rOnCount > 0 && !rAllOn;
                                var _repLock = canReports === false; /* team admin without the Report Permissions grant */
                                var togAllRep = function(){
                                  if (_repLock) return;
                                  setF(function(p){
                                    var arr=(p.studyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      return{...x,reports:rAllOn?[]:_keys9.map(function(r){return r.key;}),reportsHidden:[]};
                                    });
                                    return{...p,studyAccess:arr};
                                  });
                                };
                                return (
                                  <div style={_repLock ? {border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3,opacity:.6} : {border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                  background:rGrpOpen?"#f5f3ff":"#fafafa"}}
                                         onClick={function(){setOpen("fg_"+rGrpKey);}}>
                                      <div onClick={function(e){e.stopPropagation();togAllRep();}}
                                        style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(rAllOn||rSomeOn?"#7c3aed":"#cbd5e1"),
                                                background:rAllOn?"#7c3aed":rSomeOn?"#ddd6fe":"#fff",display:"flex",alignItems:"center",
                                                justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{rAllOn?"✓":rSomeOn?"—":""}</div>
                                      <span style={{fontSize:9,color:rGrpOpen?"#5b21b6":"#64748b",fontWeight:600,flex:1}}>{"📑 Reports" + (_repLock ? "  🔒 full admin only" : "")}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{rOnCount}/{_keys9.length}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{rGrpOpen?"▼":"▶"}</span>
                                    </div>
                                    {rGrpOpen && (
                                      <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                        {/* tri-state (Noura 2026-09-28): Active (usable) → Inactive (visible, locked) → Hidden (not shown) */}
                                        {_keys9.length === 0 ? <span style={{fontSize:8.5,color:"#94a3b8",fontStyle:"italic",padding:"2px 0"}}>No reports exist for this study.</span> : null}
                                        {_keys9.map(function(rp){
                                          var on=entryReports.indexOf(rp.key)>=0;
                                          var hid=(entry.reportsHidden||[]).indexOf(rp.key)>=0;
                                          var st9 = on ? "active" : (hid ? "hidden" : "inactive");
                                          var cyc = function(){
                                            if (_repLock) return;
                                            setF(function(p){
                                              var arr=(p.studyAccess||[]).map(function(x){
                                                if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                                var rs=(x.reports||[]).slice(), hs=(x.reportsHidden||[]).slice();
                                                var ri=rs.indexOf(rp.key), hi=hs.indexOf(rp.key);
                                                if (ri>=0) { rs.splice(ri,1); }               /* active → inactive */
                                                else if (hi<0) { hs.push(rp.key); }           /* inactive → hidden */
                                                else { hs.splice(hi,1); rs.push(rp.key); }    /* hidden → active */
                                                return {...x, reports:rs, reportsHidden:hs};
                                              });
                                              return {...p, studyAccess:arr};
                                            });
                                          };
                                          return(
                                            <span key={rp.key} onClick={cyc}
                                              title={"Click to cycle: Active (usable) → Inactive (visible but locked) → Hidden (not shown at all)"}
                                              style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                      cursor:"pointer",userSelect:"none",
                                                      border:"1px solid "+(st9==="active"?"#7c3aed":st9==="hidden"?"#fca5a5":"#e2e8f0"),
                                                      background:st9==="active"?"#ede9fe":st9==="hidden"?"#fef2f2":"#fff",
                                                      color:st9==="active"?"#5b21b6":st9==="hidden"?"#b91c1c":"#94a3b8",
                                                      fontWeight:st9==="active"?600:400,
                                                      textDecoration:st9==="hidden"?"line-through":"none"}}>
                                              <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(st9==="active"?"#7c3aed":st9==="hidden"?"#f87171":"#cbd5e1"),
                                                            background:st9==="active"?"#7c3aed":st9==="hidden"?"#f87171":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                            fontSize:6,color:"#fff"}}>{st9==="active"?"✓":st9==="hidden"?"✕":""}</div>
                                              {rp.icon} {rp.label}
                                              <span style={{fontSize:6.5,fontWeight:700,letterSpacing:".05em",color:st9==="active"?"#7c3aed":st9==="hidden"?"#ef4444":"#b6bfcc"}}>{st9==="active"?"ACTIVE":st9==="hidden"?"HIDDEN":"LOCKED"}</span>
                                            </span>
                                          );
                                        })}
                                        {_keys9.some(function(k9){ return k9.key.indexOf("therapeutic") === 0; }) ? <div style={{flexBasis:"100%",fontSize:8,color:"#94a3b8",lineHeight:1.5,marginTop:2}}>
                                          {"ATC2 & ATC3 appear to the client as ONE “Therapeutic Area” card with a level toggle. Counts & reset (the report license) are in the 📑 Report subject limits box further down this form."}
                                        </div> : null}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* ── Measures group ── */}
                              {(() => {
                                var mGrpKey = ck+"_"+entry.studyTypeCode+"_msr";
                                var mGrpOpen = openNodes["fg_"+mGrpKey];
                                /* Study-specific measure list: Patients only for medical studies */
                                var _studyMeasures = _getMeasureKeysForStudy(entry.studyTypeCode);
                                var entryMeasures = entry.measures || _studyMeasures.map(function(m){return m.key;});
                                var mOnCount = _studyMeasures.filter(function(m){return entryMeasures.indexOf(m.key)>=0;}).length;
                                var mAllOn = mOnCount === _studyMeasures.length;
                                var mSomeOn = mOnCount > 0 && !mAllOn;
                                var togAllMsr = function(){
                                  setF(function(p){
                                    var arr=(p.studyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      return{...x,measures:mAllOn?[]:_studyMeasures.map(function(m){return m.key;})};
                                    });
                                    return{...p,studyAccess:arr};
                                  });
                                };
                                return (
                                  <div style={{border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                  background:mGrpOpen?"#fefce8":"#fafafa"}}
                                         onClick={function(){setOpen("fg_"+mGrpKey);}}>
                                      <div onClick={function(e){e.stopPropagation();togAllMsr();}}
                                        style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(mAllOn||mSomeOn?"#ca8a04":"#cbd5e1"),
                                                background:mAllOn?"#ca8a04":mSomeOn?"#fef9c3":"#fff",display:"flex",alignItems:"center",
                                                justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{mAllOn?"✓":mSomeOn?"—":""}</div>
                                      <span style={{fontSize:9,color:mGrpOpen?"#854d0e":"#64748b",fontWeight:600,flex:1}}>📊 Measures</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{mOnCount}/{_studyMeasures.length}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{mGrpOpen?"▼":"▶"}</span>
                                    </div>
                                    {mGrpOpen && (
                                      <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                        {_studyMeasures.map(function(ms){
                                          var on=entryMeasures.indexOf(ms.key)>=0;
                                          return(
                                            <span key={ms.key} onClick={function(){togStudyMeasure(ck,entry.studyTypeCode,ms.key);}}
                                              style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                      cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#ca8a04":"#e2e8f0"),
                                                      background:on?"#fef9c3":"#fff",color:on?"#854d0e":"#94a3b8",fontWeight:on?600:400}}>
                                              <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(on?"#ca8a04":"#cbd5e1"),
                                                            background:on?"#ca8a04":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                            fontSize:6,color:"#fff"}}>{on?"✓":""}</div>
                                              {ms.icon} {ms.label}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* ── Statistics group ── */}
                              {(() => {
                                var sGrpKey = ck+"_"+entry.studyTypeCode+"_stat";
                                var sGrpOpen = openNodes["fg_"+sGrpKey];
                                var _studyStats = _getStatKeysForStudy(entry.studyTypeCode);
                                var entryStats = entry.statistics || _studyStats.map(function(s){return s.key;});
                                var sOnCount = _studyStats.filter(function(s){return entryStats.indexOf(s.key)>=0;}).length;
                                var sAllOn = sOnCount === _studyStats.length;
                                var sSomeOn = sOnCount > 0 && !sAllOn;
                                var togAllStat = function(){
                                  setF(function(p){
                                    var arr=(p.studyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      return{...x,statistics:sAllOn?[]:_studyStats.map(function(s){return s.key;})};
                                    });
                                    return{...p,studyAccess:arr};
                                  });
                                };
                                return (
                                  <div style={{border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                  background:sGrpOpen?"#f0fdfa":"#fafafa"}}
                                         onClick={function(){setOpen("fg_"+sGrpKey);}}>
                                      <div onClick={function(e){e.stopPropagation();togAllStat();}}
                                        style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(sAllOn||sSomeOn?"#0891b2":"#cbd5e1"),
                                                background:sAllOn?"#0891b2":sSomeOn?"#ccfbf1":"#fff",display:"flex",alignItems:"center",
                                                justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{sAllOn?"✓":sSomeOn?"—":""}</div>
                                      <span style={{fontSize:9,color:sGrpOpen?"#0e7490":"#64748b",fontWeight:600,flex:1}}>📈 Statistics</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{sOnCount}/{_studyStats.length}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{sGrpOpen?"▼":"▶"}</span>
                                    </div>
                                    {sGrpOpen && (
                                      <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                        {_studyStats.map(function(st){
                                          var on=entryStats.indexOf(st.key)>=0;
                                          return(
                                            <span key={st.key} onClick={function(){togStudyStatistic(ck,entry.studyTypeCode,st.key);}}
                                              style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                      cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#0891b2":"#e2e8f0"),
                                                      background:on?"#ccfbf1":"#fff",color:on?"#0e7490":"#94a3b8",fontWeight:on?600:400}}>
                                              <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(on?"#0891b2":"#cbd5e1"),
                                                            background:on?"#0891b2":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                            fontSize:6,color:"#fff"}}>{on?"✓":""}</div>
                                              {st.icon} {st.label}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* ── Period Permission ── */}
                              {(() => {
                                var PERIOD_OPTS = [
                                  {key:"monthly",label:"Monthly (all periods)",icon:"📅"},
                                  {key:"quarterly",label:"Quarterly (no monthly) \u2014 complete quarters only",icon:"📊"},
                                  {key:"quarterly_open",label:"Quarterly incl. the open quarter (shown as Q3 2026*)",icon:"📊"},
                                  {key:"semesterly",label:"Semesterly (S1: Q1–Q2 · S2: full year)",icon:"⏳"},
                                  {key:"yearly",label:"Yearly only",icon:"📆"}
                                ];
                                var curPP = entry.periodPermission || "monthly";
                                return (
                                  <div style={{border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",background:"#faf5ff"}}>
                                      <span style={{fontSize:9,color:"#7c3aed",fontWeight:600,flex:1}}>⏱️ Period Permission</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{curPP}</span>
                                    </div>
                                    <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                      {PERIOD_OPTS.map(function(po){
                                        var on = curPP === po.key;
                                        return(
                                          <span key={po.key} onClick={function(){
                                            setF(function(p){
                                              var arr=(p.studyAccess||[]).map(function(x){
                                                if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                                return{...x,periodPermission:po.key};
                                              });
                                              return{...p,studyAccess:arr};
                                            });
                                          }}
                                            style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                    cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#7c3aed":"#e2e8f0"),
                                                    background:on?"#ede9fe":"#fff",color:on?"#5b21b6":"#94a3b8",fontWeight:on?600:400}}>
                                            <div style={{width:10,height:10,borderRadius:"50%",border:"1px solid "+(on?"#7c3aed":"#cbd5e1"),
                                                          background:on?"#7c3aed":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                          fontSize:6,color:"#fff"}}>{on?"\u25CF":""}</div>
                                            {po.icon} {po.label}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}

                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* ── Export Limit & Access Mode ── */}
        <div style={{marginBottom:14, padding:"10px 14px", background:"#fef9c3", borderRadius:8, border:"1px solid #fde047"}}>
          <label style={{display:"block", fontSize:11, fontWeight:800, color:"#854d0e", marginBottom:6}}>🔐 Company Settings</label>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:3}}>Export Limit (per quarter)</label>
              <input style={adminInp} type="number" value={f.exportLimit||""} onChange={function(e){set("exportLimit",e.target.value?parseInt(e.target.value):null);}} placeholder="Unlimited" />
              <div style={{fontSize:8, color:"#a16207", marginTop:2}}>Leave empty = unlimited</div>
            </div>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:3}}>Access Mode</label>
              <select style={{...adminInp, cursor:"pointer"}} value={f.accessMode||"full"} onChange={function(e){set("accessMode",e.target.value);}}>
                <option value="full">Full Access</option>
                <option value="readonly">Read Only</option>
              </select>
            </div>
          </div>
          {/* Allow Export toggle — when off, all users belonging to this client see export buttons disabled (Excel/PNG/PPT). */}
          <div style={{marginTop:10, padding:"8px 12px", background:"#fff7ed", borderRadius:6, border:"1px solid "+(f.allowExport === false ? "#fca5a5" : "#fcd34d")}}>
            <label style={{display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none"}}>
              <div onClick={function(){set("allowExport", f.allowExport === false ? true : false);}}
                style={{width:34, height:18, borderRadius:10, background:f.allowExport===false?"#dc2626":"#16a34a",
                        position:"relative", transition:"background .2s", flexShrink:0, cursor:"pointer"}}>
                <div style={{width:14, height:14, borderRadius:"50%", background:"#fff",
                             position:"absolute", top:2, left:f.allowExport===false?2:18, transition:"left .2s",
                             boxShadow:"0 1px 3px rgba(0,0,0,.2)"}} />
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:10.5, fontWeight:700, color:f.allowExport===false?"#991b1b":"#854d0e"}}>
                  {f.allowExport === false ? "🚫 Export Disabled" : "✓ Export Enabled"}
                </div>
                <div style={{fontSize:8.5, color:"#a16207", marginTop:1}}>
                  Company default for exports (Excel, PNG, PPT, copy). Users on "Inherit" follow it; an explicit per-user Enabled/Disabled overrides it.
                </div>
              </div>
            </label>
          </div>
          {/* Allow Grid Export — separate permission for the raw-row grid export feature.
              Independent of AllowExport: a client can have pivot export but not grid export, or vice-versa. */}
          <div style={{marginTop:10, padding:"8px 12px", background:"#eff6ff", borderRadius:6, border:"1px solid "+(f.allowGridExport === false ? "#fca5a5" : "#bfdbfe")}}>
            <label style={{display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none"}}>
              <div onClick={function(){set("allowGridExport", f.allowGridExport === false ? true : false);}}
                style={{width:34, height:18, borderRadius:10, background:f.allowGridExport===false?"#dc2626":"#2563eb",
                        position:"relative", transition:"background .2s", flexShrink:0, cursor:"pointer"}}>
                <div style={{width:14, height:14, borderRadius:"50%", background:"#fff",
                             position:"absolute", top:2, left:f.allowGridExport===false?2:18, transition:"left .2s",
                             boxShadow:"0 1px 3px rgba(0,0,0,.2)"}} />
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:10.5, fontWeight:700, color:f.allowGridExport===false?"#991b1b":"#1d4ed8"}}>
                  {f.allowGridExport === false ? "🚫 Grid Export Disabled" : "▦ Grid Export Enabled"}
                </div>
                <div style={{fontSize:8.5, color:"#1e40af", marginTop:1}}>
                  Company default for raw-row grid export. Users on "Inherit" follow it; an explicit per-user Allowed/Blocked overrides it.
                </div>
              </div>
            </label>
          </div>

          {/* Custom Regions permission — fail-closed: users only see the Regions builder when granted here. */}
          <div style={{marginTop:10, padding:"8px 12px", background:"#f0fdf4", borderRadius:6, border:"1px solid "+(f.allowCustomRegions === true ? "#86efac" : "#e2e8f0")}}>
            <label style={{display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none"}}>
              <div onClick={function(){set("allowCustomRegions", f.allowCustomRegions === true ? false : true);}}
                style={{width:34, height:18, borderRadius:10, background:f.allowCustomRegions===true?"#16a34a":"#94a3b8",
                        position:"relative", transition:"background .2s", flexShrink:0, cursor:"pointer"}}>
                <div style={{width:14, height:14, borderRadius:"50%", background:"#fff",
                             position:"absolute", top:2, left:f.allowCustomRegions===true?18:2, transition:"left .2s",
                             boxShadow:"0 1px 3px rgba(0,0,0,.2)"}} />
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:10.5, fontWeight:700, color:f.allowCustomRegions===true?"#166534":"#64748b"}}>
                  {f.allowCustomRegions === true ? "🗺️ Custom Regions Enabled" : "🗺️ Custom Regions Disabled"}
                </div>
              </div>
            </label>
          </div>

          {/* Custom Markets permission — fail-closed: hidden from the client until the
             admin grants it here (same rule as Custom Regions). */}
          <div style={{marginTop:10, padding:"8px 12px", background:"#eff6ff", borderRadius:6, border:"1px solid "+(f.allowCustomMarkets === true ? "#93c5fd" : "#e2e8f0")}}>
            <label style={{display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none"}}>
              <div onClick={function(){set("allowCustomMarkets", f.allowCustomMarkets === true ? false : true);}}
                style={{width:34, height:18, borderRadius:10, background:f.allowCustomMarkets===true?"#2563eb":"#94a3b8",
                        position:"relative", transition:"background .2s", flexShrink:0, cursor:"pointer"}}>
                <div style={{width:14, height:14, borderRadius:"50%", background:"#fff",
                             position:"absolute", top:2, left:f.allowCustomMarkets===true?18:2, transition:"left .2s",
                             boxShadow:"0 1px 3px rgba(0,0,0,.2)"}} />
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:10.5, fontWeight:700, color:f.allowCustomMarkets===true?"#1d4ed8":"#64748b"}}>
                  {f.allowCustomMarkets === true ? "🌐 Global Definition Market Enabled" : "🌐 Global Definition Market Disabled"}
                </div>
              </div>
            </label>
          </div>

          {/* r97: reseller partner — full admins assign a client to a partner (IQVIA); the partner's
             admins then manage it, its users see the partner's co-branding and licence line */}
          {canOwner && partners && partners.length > 0 ? (
            <div style={{marginTop:10, padding:"8px 12px", background:"#f0fdfa", borderRadius:6, border:"1px solid " + (f.partnerId ? "#5eead4" : "#e2e8f0"), display:"flex", alignItems:"center", gap:10}}>
              <div style={{fontSize:10.5, fontWeight:700, color:"#0f766e", whiteSpace:"nowrap"}}>{"\uD83E\uDD1D Reseller partner"}</div>
              <select value={f.partnerId || ""} onChange={function(e){ set("partnerId", e.target.value ? Number(e.target.value) : null); }}
                style={{flex:1, padding:"6px 9px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11.5, fontFamily:"inherit", background:"#fff"}}>
                <option value="">— direct AMS Analytix client —</option>
                {partners.map(function(pr){ return <option key={pr.id} value={pr.id}>{pr.name + " (" + pr.code + ")"}</option>; })}
              </select>
            </div>
          ) : null}
          {/* ── Owner company group (Noura 2026-10-01): only the FULL admin sees
             the switch; a marked client (and its users) can only be managed by
             full admin accounts — the server refuses every team-admin write. ── */}
          {canOwner ? (
            <div style={{marginTop:10, padding:"8px 12px", background:"#fdf4ff", borderRadius:6, border:"1px solid "+(f.ownerGroup === true ? "#d8b4fe" : "#e2e8f0")}}>
              <label style={{display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none"}}>
                <div onClick={function(){set("ownerGroup", f.ownerGroup === true ? false : true);}}
                  style={{width:34, height:18, borderRadius:10, background:f.ownerGroup===true?"#9333ea":"#94a3b8", position:"relative", transition:"background .2s", flexShrink:0, cursor:"pointer"}}>
                  <div style={{width:14, height:14, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:f.ownerGroup===true?18:2, transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,.2)"}} />
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:10.5, fontWeight:700, color:f.ownerGroup===true?"#7e22ce":"#64748b"}}>
                    {f.ownerGroup === true ? "🏢 Owner Company Group — full admin only" : "🏢 Owner Company Group — off"}
                  </div>
                  <div style={{fontSize:8.5, color:"#94a3b8"}}>When on, this client and all its users can only be managed by the full admin account — team admins see them but cannot edit, pause or delete them.</div>
                </div>
              </label>
            </div>
          ) : (f.ownerGroup === true ? (
            <div style={{marginTop:10, padding:"8px 12px", background:"#fdf4ff", borderRadius:6, border:"1px solid #d8b4fe", fontSize:10, fontWeight:700, color:"#7e22ce"}}>
              🏢 Owner Company Group — this client is managed by the full admin account only
            </div>
          ) : null)}

          {/* ── Report access rules (Noura 2026-09-24): how many DIFFERENT subjects
             (agents / molecules / classes…) the client can generate each report
             deck for, shared across all its users, with a configurable reset.
             Blank = unlimited · 0 = blocked. WHICH reports the client sees stays
             in each study's Reports grants — this only adds the HOW MANY. ── */}
          <div style={Object.assign({marginTop:10, padding:"10px 12px", background:"#fffbeb", borderRadius:6, border:"1px solid "+((f.reportQuota && f.reportQuota.limits) ? "#fcd34d" : "#e2e8f0")}, canReports === false ? {opacity:.6, pointerEvents:"none"} : null)}>
            <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:5}}>
              <div style={{fontSize:10.5, fontWeight:700, color:"#92400e", flex:1}}>{"📑 Report subject limits — the client's report license" + (canReports === false ? "  🔒 full admin only" : "")}</div>
              <select value={(f.reportQuota && f.reportQuota.reset) || "quarter"}
                onChange={function(e){ var v = e.target.value; setF(function(p){ var rq = Object.assign({ reset: "quarter", limits: {} }, p.reportQuota || {}); var _o8 = { reset: v, limits: rq.limits || {} }; if (rq.sampleLimit != null) _o8.sampleLimit = rq.sampleLimit; return {...p, reportQuota: _o8}; }); }}
                style={{fontSize:9.5, fontWeight:700, padding:"3px 6px", borderRadius:6, border:"1px solid #fcd34d", background:"#fff", color:"#92400e", fontFamily:"inherit", outline:"none"}}>
                <option value="month">Resets monthly</option>
                <option value="quarter">Resets quarterly</option>
                <option value="year">Resets yearly</option>
                <option value="never">No reset (contract total)</option>
              </select>
            </div>
            <div style={{fontSize:8.5, color:"#a16207", marginBottom:8, lineHeight:1.5}}>
              How many DIFFERENT subjects the client can generate each report for (all its users share the pool). Re-opening or re-exporting an already-used subject is free. Blank = unlimited · 0 = fully blocked.
            </div>
            {/* r87: SAMPLE reports for LOCKED reports — per USER, per QUARTER */}
            <div style={{display:"flex", alignItems:"center", gap:8, padding:"5px 8px", marginBottom:6, borderRadius:6, background:"#eff9fb", border:"1px solid #9fd8e8"}}>
              <div style={{flex:1, fontSize:9.5, fontWeight:700, color:"#0e7490"}}>{"\uD83E\uDDFE Sample reports — for reports the client does NOT have (locked): samples per USER per QUARTER"}</div>
              <input type="number" min="0" value={(f.reportQuota && f.reportQuota.sampleLimit != null) ? f.reportQuota.sampleLimit : ""} placeholder="2"
                onChange={function(e){
                  var v = e.target.value;
                  setF(function(p){
                    var rq = Object.assign({ reset: "quarter", limits: {} }, p.reportQuota || {});
                    var n9 = (v === "" || v == null) ? null : parseInt(v, 10);
                    if (n9 != null && (isNaN(n9) || n9 < 0)) n9 = null;
                    var out = { reset: rq.reset || "quarter", limits: rq.limits || {} };
                    if (n9 != null) out.sampleLimit = n9;
                    return {...p, reportQuota: (Object.keys(out.limits).length || n9 != null) ? out : null};
                  });
                }}
                style={{width:64, fontSize:10, fontWeight:700, padding:"3px 7px", borderRadius:6, border:"1px solid #49B8D3", background:"#fff", color:"#0e7490", fontFamily:"inherit", outline:"none"}} />
              <div style={{width:78, fontSize:8, color:"#0e7490"}}>{(f.reportQuota && f.reportQuota.sampleLimit != null) ? (f.reportQuota.sampleLimit === 0 ? "samples off" : "per user / qtr") : "default 2 / qtr"}</div>
            </div>
            {[["market_overview", "🌐 Market Overview", "periods"],
              ["manufacturer_overview", "🏭 Manufacturer Overview", "manufacturers"],
              ["agent_overview", "🏪 Agent Overview", "agents"],
              ["therapeutic_overview", "🧬 Therapeutic Area · ATC2 level", "classes"],
              ["therapeutic_atc3_overview", "🧬 Therapeutic Area · ATC3 level", "classes"],
              ["molecule_overview", "⚗️ Molecule Overview", "molecules"],
              ["sales_tracker_diagnostic", "💹 Sell-In / Sell-Out Analysis", "analyses"]].map(function(rr){
              var rqv = (f.reportQuota && f.reportQuota.limits && f.reportQuota.limits[rr[0]] != null) ? f.reportQuota.limits[rr[0]] : "";
              return (
                <div key={rr[0]} style={{display:"flex", alignItems:"center", gap:8, padding:"3px 0", borderBottom:"1px solid #fef3c7"}}>
                  <div style={{flex:1, fontSize:9.5, fontWeight:600, color:"#78350f"}}>{rr[1]}</div>
                  <input type="number" min="0" value={rqv} placeholder="∞"
                    onChange={function(e){
                      var v = e.target.value;
                      setF(function(p){
                        var rq = Object.assign({ reset: "quarter", limits: {} }, p.reportQuota || {});
                        var lims = Object.assign({}, rq.limits || {});
                        if (v === "" || v == null) delete lims[rr[0]];
                        else { var n9 = parseInt(v, 10); if (isNaN(n9) || n9 < 0) delete lims[rr[0]]; else lims[rr[0]] = n9; }
                        var _o9 = { reset: rq.reset || "quarter", limits: lims }; if (rq.sampleLimit != null) _o9.sampleLimit = rq.sampleLimit;   /* r87: keep the sample limit */
                        return {...p, reportQuota: (Object.keys(lims).length || rq.sampleLimit != null) ? _o9 : null};
                      });
                    }}
                    style={{width:64, fontSize:10, fontWeight:700, padding:"3px 7px", borderRadius:6, border:"1px solid "+(rqv !== "" ? "#f59e0b" : "#e2e8f0"), background:"#fff", color:"#78350f", fontFamily:"inherit", outline:"none", textAlign:"right"}} />
                  <div style={{width:78, fontSize:8, color:"#b7862b"}}>{rqv === "" ? "unlimited" : (rqv === 0 ? "blocked" : "different " + rr[2])}</div>
                </div>
              );
            })}
          </div>

          {/* ── Report options (Noura 2026-10-01): per-client unlock of the
             Quarter/MAT report bases and the Market Overview sector decks.
             Fail-closed: every option shows locked (🔒) to the client's users
             until granted here. Same report_perms rule as the quotas. ── */}
          <div style={Object.assign({marginTop:10, padding:"10px 12px", background:"#eff9fb", borderRadius:6, border:"1px solid "+((f.reportOptions && (f.reportOptions.qtr || f.reportOptions.mat || f.reportOptions.moPrivate || f.reportOptions.moPublic)) ? "#7dd3e8" : "#e2e8f0")}, canReports === false ? {opacity:.6, pointerEvents:"none"} : null)}>
            <div style={{fontSize:10.5, fontWeight:700, color:"#0e7490", marginBottom:4}}>{"🔓 Report options — unlock locked report features" + (canReports === false ? "  🔒 full admin only" : "")}</div>
            <div style={{fontSize:8.5, color:"#155e75", marginBottom:8, lineHeight:1.5}}>
              These options show as locked (🔒) in the report setup bars until granted here. Applies to all this client's users. Admin accounts always see everything.
            </div>
            {[["qtr", "📅 Latest Quarter basis", "generate any report on the latest-quarter basis"],
              ["mat", "📅 MAT basis", "generate any report on the moving-annual-total basis"],
              ["moPrivate", "🏛️ Market Overview — Private Sector", "the Private Sector option in the Market Overview report"],
              ["moPublic", "🏛️ Market Overview — Public Sector", "the Public Sector (tender) option in the Market Overview report"]].map(function(ro){
              var onv = !!(f.reportOptions && f.reportOptions[ro[0]]);
              return (
                <div key={ro[0]} style={{display:"flex", alignItems:"center", gap:8, padding:"4px 0", borderBottom:"1px solid #e0f2f7", userSelect:"none"}}>
                  <div onClick={function(){ setF(function(p){ var o9 = Object.assign({}, p.reportOptions || {}); if (o9[ro[0]]) delete o9[ro[0]]; else o9[ro[0]] = true; return {...p, reportOptions: Object.keys(o9).length ? o9 : null}; }); }}
                    style={{width:30, height:16, borderRadius:9, background:onv?"#0891b2":"#94a3b8", position:"relative", flexShrink:0, cursor:"pointer", transition:"background .2s"}}>
                    <div style={{width:12, height:12, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:onv?16:2, transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,.2)"}} />
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:9.5, fontWeight:700, color:onv?"#0e7490":"#64748b"}}>{ro[1] + (onv ? "  — unlocked" : "  — locked")}</div>
                    <div style={{fontSize:8, color:"#94a3b8"}}>{ro[2]}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{display:"flex", justifyContent:"flex-end", gap:8}}>
          <button onClick={onClose} style={adminBtn("#fff","#64748b","#e2e8f0")}>Cancel</button>
          <button onClick={save} style={adminBtn("#2563eb","#fff","#2563eb")}>{isNew ? "Create Client" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}

/* ════════ User Form Modal ════════ */
function UserModal({ usr, clients, onSave, onClose, adminScope, canReports }) {
  var isNew = !usr;
  var [f, setF] = useState(usr ? {...usr, password: usr.password||"", userStudyAccess: usr.userStudyAccess||[], accessMode: usr.accessMode||null, reportAccess: usr.reportAccess||null} : {id:"", reportAccess:null, clientId:clients.length?clients[0].id:"", name:"", email:"", password:"", role:"analyst", status:"active",
    extractLimit:50, rowExportLimit:0, extractUsed:0, userStudyAccess:[], accessMode:null, allowExport:null, allowGridExport:null, cleanExport:false, created:new Date().toISOString().slice(0,10)});
  var [showPw, setShowPw] = useState(false);
  var [pwMsg, setPwMsg] = useState("");
  /* Per-study report catalog (Noura 2026-10-02): each study's Reports group
     lists only the reports that actually exist for it. Unknown/still-loading
     studies fall back to the full list; a study the server reports as having
     NO reports shows a note instead of chips. */
  var _rcS = useState(null), repCatalog = _rcS[0], setRepCatalog = _rcS[1];
  useEffect(function(){ api.getReportsCatalog().then(function(r9){ setRepCatalog((r9 && r9.studies) || {}); }).catch(function(e9){}); }, []);
  var _repKeysFor = function(code9){
    if (!repCatalog || repCatalog[code9] === undefined) return ALL_REPORT_KEYS; /* loading / unknown study */
    var av9 = repCatalog[code9] || [];
    return ALL_REPORT_KEYS.filter(function(r9){ return av9.indexOf(r9.key) >= 0; });
  };
  var set = function(k,v) { setF(function(p) { var n={...p}; n[k]=v; return n; }); };
  var togArr = function(k, val) {
    setF(function(p) {
      var arr = (p[k]||[]).slice();
      var idx = arr.indexOf(val);
      if (idx >= 0) arr.splice(idx,1); else arr.push(val);
      var n = {...p}; n[k] = arr; return n;
    });
  };
  var save = function() {
    if (!f.name.trim() || !f.clientId) return;
    if (isNew && !f.password.trim()) { setPwMsg("Password is required for new users."); return; }
    var payload = {...f, id:f.id||genId("u"), extractLimit:Number(f.extractLimit)||0, rowExportLimit:Number(f.rowExportLimit)||0};
    delete payload._openNodes; delete payload._addCountry; delete payload._addStudy;
    console.log("[saveUser] Saving user:", payload.name, "studies:", (payload.userStudyAccess||[]).length,
      "details:", (payload.userStudyAccess||[]).map(function(sa){ return sa.country+"/"+sa.studyTypeCode+" f="+((sa.features||[]).length)+" d="+((sa.dashboards||[]).length); }));
    onSave(payload);
  };
  var genPassword = function() {
    var chars = "abcdefghijkmnpqrstuvwxyz23456789";
    var pw = "";
    for (var i = 0; i < 10; i++) pw += chars[Math.floor(Math.random()*chars.length)];
    set("password", pw);
    setShowPw(true);
    setPwMsg("Generated: " + pw);
  };
  var chkStyle = function(on, accent) {
    return {width:13,height:13,borderRadius:3,border:"1.5px solid "+(on?accent:"#cbd5e1"),
      background:on?accent:"#fff",display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:8,color:"#fff",flexShrink:0};
  };
  /* Find the selected client to show inherited permissions */
  var selClient = clients.find(function(c){return c.id===f.clientId;}) || null;

  return (
    <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center"}}>
      <div style={{background:"#fff", borderRadius:16, padding:"28px 30px", width:740, maxHeight:"90vh", overflowY:"auto",
                   boxShadow:"0 20px 60px rgba(0,0,0,.15)"}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20}}>
          <div>
            <span style={{fontSize:16, fontWeight:800, color:"#0f172a"}}>{isNew ? "Add New User" : "Edit User"}</span>
            {/* audit line (Noura 2026-10-03): who created / last modified this user */}
            {!isNew ? (function(){
            var a9 = "";
            if (f.created) a9 += "Created " + f.created + (f.createdBy ? " by " + f.createdBy : "");
            if (f.updated && (f.updated !== f.created || f.updatedBy !== f.createdBy)) a9 += (a9 ? "   \u00b7   " : "") + "Last modified " + f.updated + (f.updatedBy ? " by " + f.updatedBy : "");
            return a9 ? <div style={{fontSize:9.5, fontWeight:600, color:"#94a3b8", marginTop:3}}>{"\ud83d\udd52 " + a9}</div> : null;
          })() : null}
          </div>
          <button onClick={onClose} style={{background:"none", border:"none", fontSize:18, cursor:"pointer", color:"#94a3b8"}}>✕</button>
        </div>

        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16}}>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Full Name *</label>
            <input style={adminInp} value={f.name} onChange={function(e){set("name",e.target.value);}} placeholder="Full name" />
          </div>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Email *</label>
            <input style={adminInp} value={f.email} onChange={function(e){set("email",e.target.value);}} placeholder="user@company.com" />
          </div>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Client *</label>
            <select style={{...adminInp, cursor:"pointer"}} value={f.clientId} onChange={function(e){
              var newCid = e.target.value;
              var newClient = clients.find(function(c){return c.id===newCid;}) || null;
              setF(function(p){
                var inherited = newClient && newClient.studyAccess ? newClient.studyAccess.map(function(sa){
                  return {country:sa.country, studyTypeCode:sa.studyTypeCode, features:(sa.features||[]).slice(), dashboards:(sa.dashboards||[]).slice(), reports:(sa.reports||[]).slice(), reportsHidden:(sa.reportsHidden||[]).slice(), measures:(sa.measures||["Unit","USD","LC","Price"]).slice(), statistics:(sa.statistics||["Market Share","Rank","Growth","CAGR","Evolution Index"]).slice(), periodMode:sa.periodMode||"all", periodFrom:sa.periodFrom||"", periodTo:sa.periodTo||"", periodPermission:sa.periodPermission||"monthly", contractStart:sa.contractStart||"", contractEnd:sa.contractEnd||""};
                }) : [];
                return {...p, clientId:newCid, userStudyAccess:inherited};
              });
            }}>
              {clients.filter(function(c){return c.status!=="deleted";}).map(function(c) {
                return <option key={c.id} value={c.id}>{c.name}</option>;
              })}
            </select>
          </div>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Role</label>
            <select style={{...adminInp, cursor:"pointer"}} value={f.role} onChange={function(e){set("role",e.target.value);}}>
              <option value="manager">Manager</option>
              <option value="analyst">Analyst</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div>
            <label style={{display:"block", fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4}}>Status</label>
            <select style={{...adminInp, cursor:"pointer"}} value={f.status} onChange={function(e){set("status",e.target.value);}}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>

        {/* ── Study Access: Auto-inherit from client ── */}
        {(() => {
          /* Auto-populate from client if empty */
          if (selClient && selClient.studyAccess && selClient.studyAccess.length > 0 && (f.userStudyAccess||[]).length === 0) {
            setTimeout(function(){
              setF(function(p) {
                if ((p.userStudyAccess||[]).length > 0) return p;
                return {...p, userStudyAccess: selClient.studyAccess.map(function(sa) {
                  return {country:sa.country, studyTypeCode:sa.studyTypeCode, features:(sa.features||[]).slice(), dashboards:(sa.dashboards||[]).slice(), reports:(sa.reports||[]).slice(), reportsHidden:(sa.reportsHidden||[]).slice(), measures:(sa.measures||["Unit","USD","LC","Price"]).slice(), statistics:(sa.statistics||["Market Share","Rank","Growth","CAGR","Evolution Index"]).slice(), periodMode:sa.periodMode||"all", periodFrom:sa.periodFrom||"", periodTo:sa.periodTo||"", periodPermission:sa.periodPermission||"monthly", contractStart:sa.contractStart||"", contractEnd:sa.contractEnd||""};
                })};
              });
            }, 0);
          }
          var nEntries = (f.userStudyAccess||[]).length;
          var clientEntries = selClient && selClient.studyAccess ? selClient.studyAccess.length : 0;
          return (
            <div style={{marginBottom:14, padding:"10px 14px", background:"#eff6ff", borderRadius:8, border:"1px solid #bfdbfe"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <label style={{fontSize:11, fontWeight:800, color:"#1e40af"}}>📋 Study Access</label>
                <span style={{fontSize:9,color:"#64748b"}}>{nEntries} entries (inherited from {selClient?selClient.name:"client"})</span>
              </div>
              <div style={{fontSize:9,color:"#64748b",marginTop:4}}>Studies are inherited from the client. Expand the tree below to customize dimensions, dashboards, and period per study. <strong>Empty = no access</strong> (select specific items to grant access).</div>
              {adminScope && (
                <div style={{marginTop:6,padding:"6px 10px",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:7,fontSize:9.5,color:"#92400e",fontWeight:600}}>
                  {"\uD83D\uDD12 Your study scope: " + adminScope.map(function(c){ return String(c).replace(/_/g, " ").toUpperCase(); }).join(", ") + " \u2014 changes you make to other studies are ignored and their current access is preserved."}
                </div>
              )}
              {nEntries === 0 && clientEntries === 0 && (
                <div style={{fontSize:9,color:"#d97706",marginTop:4,fontWeight:600}}>⚠ No studies configured on the client. Add study access to the client first.</div>
              )}
              {clientEntries > 0 && (
                <div style={{marginTop:6,display:"flex",gap:6}}>
                  <button onClick={function(){
                    if(!selClient||!selClient.studyAccess) return;
                    setF(function(p){
                      return {...p, userStudyAccess: selClient.studyAccess.map(function(sa){
                        return {country:sa.country, studyTypeCode:sa.studyTypeCode, features:(sa.features||[]).slice(), dashboards:(sa.dashboards||[]).slice(), reports:(sa.reports||[]).slice(), reportsHidden:(sa.reportsHidden||[]).slice(), measures:(sa.measures||["Unit","USD","LC","Price"]).slice(), statistics:(sa.statistics||["Market Share","Rank","Growth","CAGR","Evolution Index"]).slice(), periodMode:sa.periodMode||"all", periodFrom:sa.periodFrom||"", periodTo:sa.periodTo||"", periodPermission:sa.periodPermission||"monthly", contractStart:sa.contractStart||"", contractEnd:sa.contractEnd||""};
                      })};
                    });
                  }} style={{fontSize:9,fontWeight:700,padding:"4px 10px",borderRadius:5,border:"1.5px solid #2563eb",background:"#dbeafe",color:"#1e40af",cursor:"pointer",fontFamily:"inherit"}}>
                    🔄 Inherit All from Client
                  </button>
                  <button onClick={function(){
                    if(!selClient||!selClient.studyAccess) return;
                    setF(function(p){
                      var existing = p.userStudyAccess || [];
                      var merged = selClient.studyAccess.map(function(csa){
                        var found = existing.find(function(u){ return u.country===csa.country && u.studyTypeCode===csa.studyTypeCode; });
                        if (found) return found;
                        return {country:csa.country, studyTypeCode:csa.studyTypeCode, features:(csa.features||[]).slice(), dashboards:(csa.dashboards||[]).slice(), reports:(csa.reports||[]).slice(), reportsHidden:(csa.reportsHidden||[]).slice(), measures:(csa.measures||["Unit","USD","LC","Price"]).slice(), statistics:(csa.statistics||["Market Share","Rank","Growth","CAGR","Evolution Index"]).slice(), periodMode:csa.periodMode||"all", periodFrom:csa.periodFrom||"", periodTo:csa.periodTo||"", periodPermission:csa.periodPermission||"monthly", contractStart:csa.contractStart||"", contractEnd:csa.contractEnd||""};
                      });
                      return {...p, userStudyAccess: merged};
                    });
                  }} style={{fontSize:9,fontWeight:700,padding:"4px 10px",borderRadius:5,border:"1.5px solid #059669",background:"#dcfce7",color:"#166534",cursor:"pointer",fontFamily:"inherit"}}>
                    ➕ Add Missing Studies from Client
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── User Study Access Tree ── */}
        {(() => {
          var sa = f.userStudyAccess || [];
          var openNodes = f._openNodes || {};
          var setOpen = function(k) { setF(function(p){ var n={...p}; var o={...(p._openNodes||{})}; o[k]=!o[k]; n._openNodes=o; return n; }); };

          var removeUserStudy = function(country, stCode) {
            setF(function(p) {
              return {...p, userStudyAccess:(p.userStudyAccess||[]).filter(function(x){return !(x.country===country && x.studyTypeCode===stCode);})};
            });
          };
          var togUserFeat = function(country, stCode, dimKey) {
            setF(function(p) {
              var arr = (p.userStudyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var feats = (x.features||[]).slice();
                var idx = feats.indexOf(dimKey); if (idx>=0) feats.splice(idx,1); else feats.push(dimKey);
                return {...x, features:feats};
              });
              return {...p, userStudyAccess:arr};
            });
          };
          var togUserDash = function(country, stCode, dashKey) {
            setF(function(p) {
              var arr = (p.userStudyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                return {...x, dashboards:_togDashList(x.dashboards, dashKey)};
              });
              return {...p, userStudyAccess:arr};
            });
          };
          var setUserStudyProp = function(country, stCode, key, val) {
            setF(function(p) {
              var arr = (p.userStudyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var n = {...x}; n[key] = val; return n;
              });
              return {...p, userStudyAccess:arr};
            });
          };
          var togUserMeasure = function(country, stCode, mKey) {
            setF(function(p) {
              var arr = (p.userStudyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var ms = (x.measures||_getMeasureKeysForStudy(x.studyTypeCode).map(function(m){return m.key;})).slice();
                var idx = ms.indexOf(mKey); if (idx>=0) ms.splice(idx,1); else ms.push(mKey);
                return {...x, measures:ms};
              });
              return {...p, userStudyAccess:arr};
            });
          };
          var togUserStatistic = function(country, stCode, sKey) {
            setF(function(p) {
              var arr = (p.userStudyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var ss = (x.statistics||_getStatKeysForStudy(x.studyTypeCode).map(function(s){return s.key;})).slice();
                var idx = ss.indexOf(sKey); if (idx>=0) ss.splice(idx,1); else ss.push(sKey);
                return {...x, statistics:ss};
              });
              return {...p, userStudyAccess:arr};
            });
          };
          var togUserReport = function(country, stCode, rKey) {
            setF(function(p) {
              var arr = (p.userStudyAccess||[]).map(function(x) {
                if (x.country!==country || x.studyTypeCode!==stCode) return x;
                var rs = (x.reports||[]).slice();
                var idx = rs.indexOf(rKey); if (idx>=0) rs.splice(idx,1); else rs.push(rKey);
                return {...x, reports:rs};
              });
              return {...p, userStudyAccess:arr};
            });
          };

          /* Group by country */
          var byCountry = {};
          sa.forEach(function(x) { var c = x.country || "(Global)"; if(!byCountry[c]) byCountry[c]=[]; byCountry[c].push(x); });
          var countryKeys = Object.keys(byCountry);

          return (
            <div style={{marginBottom:14, padding:"10px 14px", background:"#f0f9ff", borderRadius:8, border:"1px solid #bae6fd"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <label style={{fontSize:11, fontWeight:800, color:"#0369a1", flex:1}}>🌳 Study type access tree</label>
                {sa.length > 0 && (
                  <span style={{fontSize:8,color:"#0369a1"}}>{sa.length} entries</span>
                )}
              </div>
              <div style={{fontSize:9, color:"#0369a1", marginBottom:8}}>Click to expand and configure features, dashboards, period. <strong>Empty = no access</strong> — select specific items to grant.</div>

              {countryKeys.map(function(ck) {
                var countryOpen = openNodes["uc_"+ck];
                var entries = byCountry[ck];
                return (
                  <div key={ck} style={{marginBottom:6, border:"1px solid #e2e8f0", borderRadius:8, overflow:"hidden"}}>
                    <div onClick={function(){setOpen("uc_"+ck);}}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",cursor:"pointer",userSelect:"none",
                              background:countryOpen?"#dbeafe":"#eff6ff",borderBottom:countryOpen?"1px solid #bfdbfe":"none"}}>
                      <span style={{fontSize:10,color:"#1d4ed8"}}>{countryOpen?"▼":"▶"}</span>
                      <span style={{fontSize:11,fontWeight:700,color:"#1e40af",flex:1}}>🌍 {ck}</span>
                      <span style={{fontSize:9,color:"#64748b",background:"#fff",borderRadius:10,padding:"1px 6px",border:"1px solid #e2e8f0"}}>{entries.length} study types</span>
                    </div>

                    {countryOpen && entries.map(function(entry) {
                      var stKey = ck+"_"+entry.studyTypeCode;
                      var _lockedSt = adminScope && adminScope.indexOf(entry.studyTypeCode) < 0;
                      var stOpen = !_lockedSt && openNodes["ust_"+stKey];
                      var csInfo = (COUNTRY_STUDY_MAP[ck]||[]).find(function(s){return s.code===entry.studyTypeCode;});
                      var stLabel = csInfo ? csInfo.name : entry.studyTypeCode;
                      var stCat = csInfo ? csInfo.cat : "private";
                      var fCount = (entry.features||[]).length;
                      var dCount = (entry.dashboards||[]).length;

                      return (
                        <div key={stKey} style={{marginLeft:16, borderTop:"1px solid #f1f5f9"}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",cursor:_lockedSt?"not-allowed":"pointer",userSelect:"none",opacity:_lockedSt?0.55:1,background:_lockedSt?"#f8fafc":undefined}}
                               onClick={function(){ if (_lockedSt) return; setOpen("ust_"+stKey); }}
                               title={_lockedSt ? "Outside your study scope — managed by other admins" : undefined}>
                            <span style={{fontSize:9,color:_lockedSt?"#94a3b8":"#7c3aed"}}>{_lockedSt?"\uD83D\uDD12":stOpen?"▼":"▶"}</span>
                            <span style={{fontSize:10.5,fontWeight:600,color:_lockedSt?"#64748b":"#4c1d95",flex:1}}>
                              {stCat==="private"?"🔒":"🌐"} {stLabel}
                            </span>
                            {_lockedSt && <span style={{fontSize:8,fontWeight:800,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:4,padding:"1px 6px"}}>LOCKED — OUTSIDE YOUR SCOPE</span>}
                            <span style={{fontSize:8,color:"#059669",background:"#ecfdf5",borderRadius:4,padding:"1px 5px"}}>{fCount} dims</span>
                            <span style={{fontSize:8,color:"#2563eb",background:"#eff6ff",borderRadius:4,padding:"1px 5px"}}>{dCount} dash</span>
                            {/* Delete-study button — properly sized + confirmation. Was a tiny
                                colored ✕ that was easy to misclick or miss; now a real
                                button with hover state and a window.confirm guard. */}
                            {!_lockedSt && <button onClick={function(e){
                                e.stopPropagation();
                                if (window.confirm("Remove study access \"" + stLabel + "\" from this user?\n\nThe user will lose all features, dashboards, and period permissions configured for this study. This affects only this user; the client-level access remains.")) {
                                  removeUserStudy(ck, entry.studyTypeCode);
                                }
                              }}
                              title={"Remove " + stLabel + " from user"}
                              onMouseOver={function(e){ e.currentTarget.style.background = "#fee2e2"; }}
                              onMouseOut={function(e){ e.currentTarget.style.background = "#fef2f2"; }}
                              style={{padding:"2px 7px",border:"1px solid #fecaca",borderRadius:4,background:"#fef2f2",
                                      color:"#dc2626",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                                      lineHeight:1.2}}>
                              ✕ Delete
                            </button>}
                          </div>

                          {stOpen && (
                            <div style={{padding:"6px 10px 10px 20px"}}>
                              {/* Period */}
                              <div style={{marginBottom:8,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                <span style={{fontSize:9,fontWeight:700,color:"#7c3aed",textTransform:"uppercase"}}>📅 Period</span>
                                {[{k:"monthly",lb:"Full (Monthly)"},{k:"quarterly",lb:"Quarterly"},{k:"quarterly_open",lb:"Quarterly + open qtr*"},{k:"semesterly",lb:"Semesterly"},{k:"yearly",lb:"Yearly"}].map(function(opt){
                                  var on = (entry.periodPermission||"monthly")===opt.k;
                                  return (
                                    <span key={opt.k} onClick={function(){setUserStudyProp(ck,entry.studyTypeCode,"periodPermission",opt.k);}}
                                      style={{padding:"2px 8px",borderRadius:4,fontSize:8.5,cursor:"pointer",userSelect:"none",
                                              border:"1px solid "+(on?"#7c3aed":"#e2e8f0"),background:on?"#ede9fe":"#fff",
                                              color:on?"#4c1d95":"#94a3b8",fontWeight:on?600:400}}>{opt.lb}</span>
                                  );
                                })}
                              </div>

                              {/* Features — grouped */}
                              {(() => {
                                var groups = STUDY_FEATURE_GROUPS[entry.studyTypeCode] || STUDY_FEATURE_GROUPS["iraq"] || [];
                                var allGroupDims = [];
                                groups.forEach(function(g){g.dims.forEach(function(d){ if (d !== FULL_REPORT_FEATURE) allGroupDims.push(d); });});   /* r88: full reports never by "All" */
                                return (
                                  <div style={{marginBottom:8}}>
                                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                                      <span style={{fontSize:9,fontWeight:700,color:"#059669",textTransform:"uppercase"}}>Features</span>
                                      <span onClick={function(){setUserStudyProp(ck,entry.studyTypeCode,"features",allGroupDims.slice());}}
                                        style={{fontSize:8,color:"#059669",cursor:"pointer",textDecoration:"underline"}}>All</span>
                                      <span onClick={function(){setUserStudyProp(ck,entry.studyTypeCode,"features",[]);}}
                                        style={{fontSize:8,color:"#dc2626",cursor:"pointer",textDecoration:"underline"}}>Clear</span>
                                      <span style={{fontSize:8,color:"#dc2626"}}>Empty = no access</span>
                                    </div>
                                    {groups.map(function(grp) {
                                      var grpKey = "ufg_"+ck+"_"+entry.studyTypeCode+"_"+grp.group;
                                      var grpOpen = openNodes[grpKey];
                                      var grpOnCount = grp.dims.filter(function(d){return (entry.features||[]).indexOf(d)>=0;}).length;
                                      var allOn = grpOnCount === grp.dims.length;
                                      var someOn = grpOnCount > 0 && !allOn;
                                      var togGrp = function(){
                                        setF(function(p){
                                          var arr=(p.userStudyAccess||[]).map(function(x){
                                            if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                            var feats=(x.features||[]).slice();
                                            if(allOn){ grp.dims.forEach(function(d){var i=feats.indexOf(d);if(i>=0)feats.splice(i,1);}); }
                                            else { grp.dims.forEach(function(d){if(feats.indexOf(d)<0)feats.push(d);}); }
                                            return{...x,features:feats};
                                          });
                                          return{...p,userStudyAccess:arr};
                                        });
                                      };
                                      return (
                                        <div key={grp.group} style={{marginBottom:3,border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden"}}>
                                          <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                        background:grpOpen?"#f0fdf4":"#fafafa"}}
                                               onClick={function(){setOpen(grpKey);}}>
                                            <div onClick={function(e){e.stopPropagation();togGrp();}}
                                              style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(allOn||someOn?"#059669":"#cbd5e1"),
                                                      background:allOn?"#059669":someOn?"#bbf7d0":"#fff",display:"flex",alignItems:"center",
                                                      justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{allOn?"✓":someOn?"—":""}</div>
                                            <span style={{fontSize:9,color:grpOpen?"#166534":"#64748b",fontWeight:600,flex:1}}>{grp.group}</span>
                                            <span style={{fontSize:8,color:"#94a3b8"}}>{grpOnCount}/{grp.dims.length}</span>
                                            <span style={{fontSize:8,color:"#94a3b8"}}>{grpOpen?"▼":"▶"}</span>
                                          </div>
                                          {grpOpen && (
                                            <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                              {grp.dims.map(function(dk){
                                                var on=(entry.features||[]).indexOf(dk)>=0;
                                                return(
                                                  <span key={dk} onClick={function(){togUserFeat(ck,entry.studyTypeCode,dk);}}
                                                    style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                            cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#059669":"#e2e8f0"),
                                                            background:on?"#dcfce7":"#fff",color:on?"#166534":"#94a3b8",fontWeight:on?600:400}}>
                                                    <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(on?"#059669":"#cbd5e1"),
                                                                  background:on?"#059669":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                                  fontSize:6,color:"#fff"}}>{on?"✓":""}</div>
                                                    {FEAT_LBL[dk]||dk}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}

                              {/* Dashboards */}
                              {(() => {
                                var _dashKeys2 = _getDashKeysForStudy(entry.studyTypeCode);
                                var dGrpKey = "udg_"+ck+"_"+entry.studyTypeCode;
                                var dGrpOpen = openNodes[dGrpKey];
                                var dOnCount = _dashKeys2.filter(function(d){return (entry.dashboards||[]).indexOf(d.key)>=0;}).length;
                                var dAllOn = dOnCount === _dashKeys2.length;
                                var dSomeOn = dOnCount > 0 && !dAllOn;
                                var togAllDash = function(){
                                  setF(function(p){
                                    var arr=(p.userStudyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      return{...x,dashboards:dAllOn?[]:_dashKeys2.map(function(d){return d.key;})};
                                    });
                                    return{...p,userStudyAccess:arr};
                                  });
                                };
                                return (
                                  <div style={{border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden"}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                  background:dGrpOpen?"#eff6ff":"#fafafa"}}
                                         onClick={function(){setOpen(dGrpKey);}}>
                                      <div onClick={function(e){e.stopPropagation();togAllDash();}}
                                        style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(dAllOn||dSomeOn?"#2563eb":"#cbd5e1"),
                                                background:dAllOn?"#2563eb":dSomeOn?"#bfdbfe":"#fff",display:"flex",alignItems:"center",
                                                justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{dAllOn?"✓":dSomeOn?"—":""}</div>
                                      <span style={{fontSize:9,color:dGrpOpen?"#1e40af":"#64748b",fontWeight:600,flex:1}}>📈 Dashboards</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{dOnCount}/{_dashKeys2.length}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{dGrpOpen?"▼":"▶"}</span>
                                    </div>
                                    {dGrpOpen && (
                                      <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                        {_dashKeys2.map(function(db){
                                          var on=_dashOn(entry.dashboards, db.key);
                                          return(
                                            <span key={db.key} onClick={function(){togUserDash(ck,entry.studyTypeCode,db.key);}} title={_dashParentOf(db.key) && on && (entry.dashboards||[]).indexOf(_dashParentOf(db.key))>=0 ? "Included with " + (db.key==="product_overview" ? "Leading Products" : "Leading Manufacturers") : ""}
                                              style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                      cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#2563eb":"#e2e8f0"),
                                                      background:on?"#dbeafe":"#fff",color:on?"#1e40af":"#94a3b8",fontWeight:on?600:400}}>
                                              <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(on?"#2563eb":"#cbd5e1"),
                                                            background:on?"#2563eb":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                            fontSize:6,color:"#fff"}}>{on?"✓":""}</div>
                                              {db.icon} {db.label}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* ── Reports group (user-level override) ── */}
                              {(() => {
                                var rGrpKey = ck+"_"+entry.studyTypeCode+"_rep";
                                var _keys9 = _repKeysFor(entry.studyTypeCode); /* only reports this study actually has */
                                var rGrpOpen = openNodes["fg_"+rGrpKey];
                                var entryReports = entry.reports || [];
                                var rOnCount = _keys9.filter(function(r){return entryReports.indexOf(r.key)>=0;}).length;
                                var rAllOn = _keys9.length > 0 && rOnCount === _keys9.length;
                                var rSomeOn = rOnCount > 0 && !rAllOn;
                                var _repLock = canReports === false; /* team admin without the Report Permissions grant */
                                var togAllRep = function(){
                                  if (_repLock) return;
                                  setF(function(p){
                                    var arr=(p.userStudyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      return{...x,reports:rAllOn?[]:_keys9.map(function(r){return r.key;}),reportsHidden:[]};
                                    });
                                    return{...p,userStudyAccess:arr};
                                  });
                                };
                                return (
                                  <div style={_repLock ? {border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3,opacity:.6} : {border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                  background:rGrpOpen?"#f5f3ff":"#fafafa"}}
                                         onClick={function(){setOpen("fg_"+rGrpKey);}}>
                                      <div onClick={function(e){e.stopPropagation();togAllRep();}}
                                        style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(rAllOn||rSomeOn?"#7c3aed":"#cbd5e1"),
                                                background:rAllOn?"#7c3aed":rSomeOn?"#ddd6fe":"#fff",display:"flex",alignItems:"center",
                                                justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{rAllOn?"✓":rSomeOn?"—":""}</div>
                                      <span style={{fontSize:9,color:rGrpOpen?"#5b21b6":"#64748b",fontWeight:600,flex:1}}>{"📑 Reports" + (_repLock ? "  🔒 full admin only" : "")}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{rOnCount}/{_keys9.length}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{rGrpOpen?"▼":"▶"}</span>
                                    </div>
                                    {rGrpOpen && (
                                      <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                        {_keys9.length === 0 ? <span style={{fontSize:8.5,color:"#94a3b8",fontStyle:"italic",padding:"2px 0"}}>No reports exist for this study.</span> : null}
                                        {/* tri-state (Noura 2026-10-02): Active (usable) → Locked (visible, locked) → Hidden (not shown to this user) */}
                                        {_keys9.map(function(rp){
                                          var on=entryReports.indexOf(rp.key)>=0;
                                          var hid=(entry.reportsHidden||[]).indexOf(rp.key)>=0;
                                          var st9 = on ? "active" : (hid ? "hidden" : "inactive");
                                          var cyc = function(){
                                            if (_repLock) return;
                                            setF(function(p){
                                              var arr=(p.userStudyAccess||[]).map(function(x){
                                                if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                                var rs=(x.reports||[]).slice(), hs=(x.reportsHidden||[]).slice();
                                                var ri=rs.indexOf(rp.key), hi=hs.indexOf(rp.key);
                                                if (ri>=0) { rs.splice(ri,1); }               /* active → locked */
                                                else if (hi<0) { hs.push(rp.key); }           /* locked → hidden */
                                                else { hs.splice(hi,1); rs.push(rp.key); }    /* hidden → active */
                                                return {...x, reports:rs, reportsHidden:hs};
                                              });
                                              return {...p, userStudyAccess:arr};
                                            });
                                          };
                                          return(
                                            <span key={rp.key} onClick={cyc}
                                              title={"Click to cycle: Active (usable) → Locked (visible but locked) → Hidden (not shown to this user at all)"}
                                              style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                      cursor:"pointer",userSelect:"none",
                                                      border:"1px solid "+(st9==="active"?"#7c3aed":st9==="hidden"?"#fca5a5":"#e2e8f0"),
                                                      background:st9==="active"?"#ede9fe":st9==="hidden"?"#fef2f2":"#fff",
                                                      color:st9==="active"?"#5b21b6":st9==="hidden"?"#b91c1c":"#94a3b8",
                                                      fontWeight:st9==="active"?600:400,
                                                      textDecoration:st9==="hidden"?"line-through":"none"}}>
                                              <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(st9==="active"?"#7c3aed":st9==="hidden"?"#f87171":"#cbd5e1"),
                                                            background:st9==="active"?"#7c3aed":st9==="hidden"?"#f87171":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                            fontSize:6,color:"#fff"}}>{st9==="active"?"✓":st9==="hidden"?"✕":""}</div>
                                              {rp.icon} {rp.label}
                                              <span style={{fontSize:6.5,fontWeight:700,letterSpacing:".05em",color:st9==="active"?"#7c3aed":st9==="hidden"?"#ef4444":"#b6bfcc"}}>{st9==="active"?"ACTIVE":st9==="hidden"?"HIDDEN":"LOCKED"}</span>
                                            </span>
                                          );
                                        })}
                                        {_keys9.some(function(k9){ return k9.key.indexOf("therapeutic") === 0; }) ? <div style={{flexBasis:"100%",fontSize:8,color:"#94a3b8",lineHeight:1.5,marginTop:2}}>
                                          {"ATC2 & ATC3 appear to the user as ONE “Therapeutic Area” card with a level toggle."}
                                        </div> : null}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* ── Measures group ── */}
                              {(() => {
                                var mGrpKey = ck+"_"+entry.studyTypeCode+"_msr";
                                var mGrpOpen = openNodes["fg_"+mGrpKey];
                                /* Study-specific measure list: Patients only for medical studies */
                                var _studyMeasures = _getMeasureKeysForStudy(entry.studyTypeCode);
                                var entryMeasures = entry.measures || _studyMeasures.map(function(m){return m.key;});
                                var mOnCount = _studyMeasures.filter(function(m){return entryMeasures.indexOf(m.key)>=0;}).length;
                                var mAllOn = mOnCount === _studyMeasures.length;
                                var mSomeOn = mOnCount > 0 && !mAllOn;
                                var togAllMsr = function(){
                                  setF(function(p){
                                    var arr=(p.userStudyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      return{...x,measures:mAllOn?[]:_studyMeasures.map(function(m){return m.key;})};
                                    });
                                    return{...p,userStudyAccess:arr};
                                  });
                                };
                                return (
                                  <div style={{border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                  background:mGrpOpen?"#fefce8":"#fafafa"}}
                                         onClick={function(){setOpen("fg_"+mGrpKey);}}>
                                      <div onClick={function(e){e.stopPropagation();togAllMsr();}}
                                        style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(mAllOn||mSomeOn?"#ca8a04":"#cbd5e1"),
                                                background:mAllOn?"#ca8a04":mSomeOn?"#fef9c3":"#fff",display:"flex",alignItems:"center",
                                                justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{mAllOn?"✓":mSomeOn?"—":""}</div>
                                      <span style={{fontSize:9,color:mGrpOpen?"#854d0e":"#64748b",fontWeight:600,flex:1}}>📊 Measures</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{mOnCount}/{_studyMeasures.length}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{mGrpOpen?"▼":"▶"}</span>
                                    </div>
                                    {mGrpOpen && (
                                      <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                        {_studyMeasures.map(function(ms){
                                          var on=entryMeasures.indexOf(ms.key)>=0;
                                          return(
                                            <span key={ms.key} onClick={function(){togUserMeasure(ck,entry.studyTypeCode,ms.key);}}
                                              style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                      cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#ca8a04":"#e2e8f0"),
                                                      background:on?"#fef9c3":"#fff",color:on?"#854d0e":"#94a3b8",fontWeight:on?600:400}}>
                                              <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(on?"#ca8a04":"#cbd5e1"),
                                                            background:on?"#ca8a04":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                            fontSize:6,color:"#fff"}}>{on?"✓":""}</div>
                                              {ms.icon} {ms.label}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* ── Statistics group ── */}
                              {(() => {
                                var sGrpKey = ck+"_"+entry.studyTypeCode+"_stat";
                                var sGrpOpen = openNodes["fg_"+sGrpKey];
                                var _studyStats = _getStatKeysForStudy(entry.studyTypeCode);
                                var entryStats = entry.statistics || _studyStats.map(function(s){return s.key;});
                                var sOnCount = _studyStats.filter(function(s){return entryStats.indexOf(s.key)>=0;}).length;
                                var sAllOn = sOnCount === _studyStats.length;
                                var sSomeOn = sOnCount > 0 && !sAllOn;
                                var togAllStat = function(){
                                  setF(function(p){
                                    var arr=(p.userStudyAccess||[]).map(function(x){
                                      if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                      return{...x,statistics:sAllOn?[]:_studyStats.map(function(s){return s.key;})};
                                    });
                                    return{...p,userStudyAccess:arr};
                                  });
                                };
                                return (
                                  <div style={{border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",cursor:"pointer",userSelect:"none",
                                                  background:sGrpOpen?"#f0fdfa":"#fafafa"}}
                                         onClick={function(){setOpen("fg_"+sGrpKey);}}>
                                      <div onClick={function(e){e.stopPropagation();togAllStat();}}
                                        style={{width:12,height:12,borderRadius:2,border:"1.5px solid "+(sAllOn||sSomeOn?"#0891b2":"#cbd5e1"),
                                                background:sAllOn?"#0891b2":sSomeOn?"#ccfbf1":"#fff",display:"flex",alignItems:"center",
                                                justifyContent:"center",fontSize:7,color:"#fff",flexShrink:0}}>{sAllOn?"✓":sSomeOn?"—":""}</div>
                                      <span style={{fontSize:9,color:sGrpOpen?"#0e7490":"#64748b",fontWeight:600,flex:1}}>📈 Statistics</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{sOnCount}/{_studyStats.length}</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{sGrpOpen?"▼":"▶"}</span>
                                    </div>
                                    {sGrpOpen && (
                                      <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                        {_studyStats.map(function(st){
                                          var on=entryStats.indexOf(st.key)>=0;
                                          return(
                                            <span key={st.key} onClick={function(){togUserStatistic(ck,entry.studyTypeCode,st.key);}}
                                              style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                      cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#0891b2":"#e2e8f0"),
                                                      background:on?"#ccfbf1":"#fff",color:on?"#0e7490":"#94a3b8",fontWeight:on?600:400}}>
                                              <div style={{width:10,height:10,borderRadius:2,border:"1px solid "+(on?"#0891b2":"#cbd5e1"),
                                                            background:on?"#0891b2":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                            fontSize:6,color:"#fff"}}>{on?"✓":""}</div>
                                              {st.icon} {st.label}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* ── Period Permission ── */}
                              {(() => {
                                var PERIOD_OPTS = [
                                  {key:"monthly",label:"Monthly (all periods)",icon:"📅"},
                                  {key:"quarterly",label:"Quarterly (no monthly) \u2014 complete quarters only",icon:"📊"},
                                  {key:"quarterly_open",label:"Quarterly incl. the open quarter (shown as Q3 2026*)",icon:"📊"},
                                  {key:"semesterly",label:"Semesterly (S1: Q1–Q2 · S2: full year)",icon:"⏳"},
                                  {key:"yearly",label:"Yearly only",icon:"📆"}
                                ];
                                var curPP = entry.periodPermission || "monthly";
                                return (
                                  <div style={{border:"1px solid #f1f5f9",borderRadius:5,overflow:"hidden",marginTop:3}}>
                                    <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 8px",background:"#faf5ff"}}>
                                      <span style={{fontSize:9,color:"#7c3aed",fontWeight:600,flex:1}}>⏱️ Period Permission</span>
                                      <span style={{fontSize:8,color:"#94a3b8"}}>{curPP}</span>
                                    </div>
                                    <div style={{padding:"3px 8px 5px 24px",display:"flex",flexWrap:"wrap",gap:3}}>
                                      {PERIOD_OPTS.map(function(po){
                                        var on = curPP === po.key;
                                        return(
                                          <span key={po.key} onClick={function(){
                                            setF(function(p){
                                              var arr=(p.studyAccess||[]).map(function(x){
                                                if(x.country!==ck||x.studyTypeCode!==entry.studyTypeCode) return x;
                                                return{...x,periodPermission:po.key};
                                              });
                                              return{...p,studyAccess:arr};
                                            });
                                          }}
                                            style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:4,fontSize:8.5,
                                                    cursor:"pointer",userSelect:"none",border:"1px solid "+(on?"#7c3aed":"#e2e8f0"),
                                                    background:on?"#ede9fe":"#fff",color:on?"#5b21b6":"#94a3b8",fontWeight:on?600:400}}>
                                            <div style={{width:10,height:10,borderRadius:"50%",border:"1px solid "+(on?"#7c3aed":"#cbd5e1"),
                                                          background:on?"#7c3aed":"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                                                          fontSize:6,color:"#fff"}}>{on?"\u25CF":""}</div>
                                            {po.icon} {po.label}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}

                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* ── User Settings ── */}
        <div style={{marginBottom:14, padding:"10px 14px", background:"#fef9c3", borderRadius:8, border:"1px solid #fde047"}}>
          <label style={{display:"block", fontSize:11, fontWeight:800, color:"#854d0e", marginBottom:6}}>🔐 User settings</label>
          {/* r112 (Noura 2026-10-14): give a portal user FULL report decks (or samples only) without
              touching the per-study feature tree — full admins only, like every report permission */}
          <div style={{marginBottom:12, padding:"8px 12px", background:canReports === false ? "#f8fafc" : (f.reportAccess === "full" ? "#ecfdf5" : f.reportAccess === "sample" ? "#eff9fb" : "#fff"), border:"1px solid " + (f.reportAccess === "full" ? "#a7f3d0" : f.reportAccess === "sample" ? "#9fd8e8" : "#e2e8f0"), borderRadius:8, opacity:canReports === false ? .6 : 1}}>
            <label style={{display:"block", fontSize:9.5, fontWeight:800, color:"#334155", marginBottom:5}}>{"\uD83D\uDCD1 Reports \u2014 full decks or samples" + (canReports === false ? "  \uD83D\uDD12 full admin only" : "")}</label>
            <select disabled={canReports === false} style={{...adminInp, cursor:canReports === false ? "not-allowed" : "pointer", width:"100%"}} value={f.reportAccess || ""} onChange={function(e){ set("reportAccess", e.target.value || null); }}>
              <option value="">{"Inherit \u2014 per study: \u201cFULL reports\u201d feature (Report Options) \u2192 full decks, otherwise samples"}</option>
              <option value="full">{"Full reports \u2014 complete decks on every report this user can open (PPT / PDF export)"}</option>
              <option value="sample">{"Sample reports only \u2014 Full Year sample + \u201cOrder the full report\u201d, even if the company has the feature"}</option>
            </select>
            <div style={{fontSize:8.5, color:"#64748b", marginTop:3}}>{f.reportAccess === "full" ? "Applies within a minute, no re-login. Which reports are open (Active / Locked / Hidden) is still set per study above." : f.reportAccess === "sample" ? "The user keeps the quarterly sample allowance and can order full reports." : "Choose Full reports to give this user the complete decks without editing the study feature tree."}</div>
          </div>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:3}}>Access mode</label>
              <select style={{...adminInp, cursor:"pointer"}} value={f.accessMode||""} onChange={function(e){set("accessMode",e.target.value||null);}}>
                <option value="">Inherit from company{selClient ? " ("+(selClient.accessMode||"full")+")" : ""}</option>
                <option value="full">Full access</option>
                <option value="readonly">Read only</option>
              </select>
            </div>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:4}}>Export Limit (per quarter)</label>
              <input style={adminInp} type="number" min="0" value={f.extractLimit} onChange={function(e){set("extractLimit",e.target.value);}} placeholder="0 = inherit company limit" />
              <div style={{fontSize:8,color:"#a16207",marginTop:2}}>0 = inherit company limit{selClient ? " ("+(selClient.exportLimit||"unlimited")+")" : ""}</div>
            </div>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:4}}>Export (Excel, PNG, PPT, copy)</label>
              <select style={{...adminInp, cursor:"pointer"}}
                value={f.allowExport == null ? "" : (f.allowExport ? "yes" : "no")}
                onChange={function(e){
                  var v = e.target.value;
                  set("allowExport", v === "" ? null : (v === "yes"));
                }}>
                <option value="">Inherit from company{selClient ? " ("+(selClient.allowExport === false ? "off" : "on")+")" : ""}</option>
                <option value="yes">Enabled</option>
                <option value="no">Disabled</option>
              </select>
              <div style={{fontSize:8,color:"#a16207",marginTop:2}}>Explicit choice overrides the company setting; Inherit follows it</div>
            </div>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:4}}>Row Export Limit</label>
              <input style={adminInp} type="number" min="0" value={f.rowExportLimit||""} onChange={function(e){set("rowExportLimit",e.target.value);}} placeholder="0 = unlimited" />
              <div style={{fontSize:8,color:"#a16207",marginTop:2}}>Max rows per export (0 = unlimited)</div>
            </div>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:4}}>Grid Export</label>
              <select style={{...adminInp, cursor:"pointer"}}
                value={f.allowGridExport == null ? "" : (f.allowGridExport ? "yes" : "no")}
                onChange={function(e){
                  var v = e.target.value;
                  set("allowGridExport", v === "" ? null : (v === "yes"));
                }}>
                <option value="">Inherit from company{selClient ? " ("+(selClient.allowGridExport === false ? "off" : "on")+")" : ""}</option>
                <option value="yes">Allowed</option>
                <option value="no">Blocked</option>
              </select>
              <div style={{fontSize:8,color:"#a16207",marginTop:2}}>Explicit choice overrides the company setting; Inherit follows it</div>
            </div>
            <div>
              {/* r146 (Noura): per-user right to export reports WITHOUT the visible watermark */}
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:4}}>Report watermark</label>
              <select style={{...adminInp, cursor:"pointer"}}
                value={f.cleanExport ? "clean" : "always"}
                onChange={function(e){ set("cleanExport", e.target.value === "clean"); }}>
                <option value="always">Always watermarked (default)</option>
                <option value="clean">User may export without watermark</option>
              </select>
              <div style={{fontSize:8,color:"#a16207",marginTop:2}}>With the right, a Watermark On / Off switch appears on the report page; the licence page and the hidden traceability mark always stay</div>
            </div>
            <div>
              <label style={{display:"block", fontSize:9, fontWeight:700, color:"#854d0e", marginBottom:4}}>Global Definition Market</label>
              <select style={{...adminInp, cursor:"pointer"}}
                value={f.allowCustomMarkets == null ? "" : (f.allowCustomMarkets ? "yes" : "no")}
                onChange={function(e){
                  var v = e.target.value;
                  set("allowCustomMarkets", v === "" ? null : (v === "yes"));
                }}>
                <option value="">Inherit from company{selClient ? " ("+(selClient.allowCustomMarkets === true ? "on" : "off")+")" : ""}</option>
                <option value="yes">Enabled</option>
                <option value="no">Disabled</option>
              </select>
              <div style={{fontSize:8,color:"#a16207",marginTop:2}}>The 🌐 Global Definition Market selector in the top bar; Inherit follows the company toggle</div>
            </div>
          </div>
        </div>

        {/* ── Password Management ── */}
        <div style={{marginBottom:16, padding:"10px 14px", background:"#fef2f2", borderRadius:8, border:"1px solid #fecaca"}}>
          <label style={{display:"block", fontSize:11, fontWeight:800, color:"#991b1b", marginBottom:6}}>🔒 {isNew ? "Set Password *" : "Password Management"}</label>
          <div style={{display:"flex", gap:6, alignItems:"center"}}>
            <div style={{flex:1, position:"relative"}}>
              <input style={{...adminInp, paddingRight:36}} type={showPw?"text":"password"} value={f.password}
                onChange={function(e){set("password",e.target.value); setPwMsg("");}}
                placeholder={isNew ? "Enter password" : "Enter new password (leave blank to keep current)"} />
              <span onClick={function(){setShowPw(!showPw);}}
                style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",cursor:"pointer",fontSize:14,userSelect:"none"}}>
                {showPw ? "🙈" : "👁"}
              </span>
            </div>
            <button onClick={genPassword} style={{...adminBtn("#f0fdf4","#15803d","#bbf7d0"), whiteSpace:"nowrap", fontSize:10}}>🎲 Generate</button>
          </div>
          {pwMsg && <div style={{fontSize:9, marginTop:4, color:"#15803d", fontWeight:600}}>{pwMsg}</div>}
          {!isNew && <div style={{fontSize:9, marginTop:4, color:"#94a3b8"}}>Leave blank to keep existing password</div>}
        </div>

        <div style={{display:"flex", justifyContent:"flex-end", gap:8}}>
          <button onClick={onClose} style={adminBtn("#fff","#64748b","#e2e8f0")}>Cancel</button>
          <button onClick={save} style={adminBtn("#2563eb","#fff","#2563eb")}>{isNew ? "Create User" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Dashboard Add/Edit Modal ── */
function DashboardModal({ dashboard, isNew, availableKeys, onSave, onClose }) {
  var [d, setD] = useState({...dashboard});
  var set = function(k,v) { setD(function(p){ return {...p, [k]:v}; }); };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:14,padding:"28px 30px",width:480,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.15)"}} onClick={function(e){e.stopPropagation();}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <span style={{fontSize:16,fontWeight:800,color:"#0f172a"}}>{isNew ? "Add Dashboard" : "Edit Dashboard"}</span>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#94a3b8"}}>✕</button>
        </div>
        <div style={{marginBottom:14}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Dashboard Key (Component) *</label>
          <select style={{...adminInp,cursor:"pointer"}} value={d.key} onChange={function(e){set("key",e.target.value);}}>
            <option value="">— Select —</option>
            {availableKeys.map(function(k){ return <option key={k} value={k}>{k}</option>; })}
          </select>
          <div style={{fontSize:9,color:"#94a3b8",marginTop:3}}>Maps to a built-in React dashboard component</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:12,marginBottom:14}}>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Display Label *</label>
            <input style={adminInp} value={d.label} onChange={function(e){set("label",e.target.value);}} placeholder="e.g. Market Overview" />
          </div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Icon</label>
            <input style={{...adminInp,width:50,textAlign:"center",fontSize:18}} value={d.icon} onChange={function(e){set("icon",e.target.value);}} />
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:12,marginBottom:14}}>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Description</label>
            <input style={adminInp} value={d.desc} onChange={function(e){set("desc",e.target.value);}} placeholder="Short description" />
          </div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Sort</label>
            <input style={{...adminInp,width:60}} type="number" min="0" value={d.sortOrder} onChange={function(e){set("sortOrder",Number(e.target.value));}} />
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onClose} style={adminBtn("#fff","#64748b","#e2e8f0")}>Cancel</button>
          <button onClick={function(){if(!d.key||!d.label.trim()){window.alert("Key and Label required");return;} onSave(d);}}
            style={adminBtn("#2563eb","#fff","#2563eb")}>{isNew ? "Add Dashboard" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}

/* ════════ Admin Page ════════ */
/* ── Feature Add/Edit Modal ── */
function FeatureModal({ feature, isNew, onSave, onClose, existingGroups }) {
  var [f, setF] = useState({...feature});
  var [customGrp, setCustomGrp] = useState("");
  var [showCustom, setShowCustom] = useState(false);
  var set = function(k,v) { setF(function(p){ return {...p, [k]:v}; }); };
  var DEFAULT_GROUPS = ["Market Details","Therapeutic Class","Region / Governorate","Origin","Manufacturers Origin","ATC/Molecule"];
  /* Merge defaults + existing groups from data */
  var allGroups = DEFAULT_GROUPS.slice();
  (existingGroups || []).forEach(function(g){ if(allGroups.indexOf(g)<0) allGroups.push(g); });
  if(f.group && allGroups.indexOf(f.group)<0) allGroups.push(f.group);
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:14,padding:"28px 30px",width:480,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.15)"}} onClick={function(e){e.stopPropagation();}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <span style={{fontSize:16,fontWeight:800,color:"#0f172a"}}>{isNew ? "Add Feature" : "Edit Feature"}</span>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#94a3b8"}}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Feature Key *</label>
            <input style={adminInp} value={f.key} onChange={function(e){set("key",e.target.value);}} placeholder="e.g. subManufacturer" />
          </div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Display Label *</label>
            <input style={adminInp} value={f.label} onChange={function(e){set("label",e.target.value);}} placeholder="e.g. Sub-Manufacturer" />
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>DB Column Name *</label>
            <input style={adminInp} value={f.dbColumn} onChange={function(e){set("dbColumn",e.target.value);}} placeholder="e.g. SubManufacturer" />
          </div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Sort Order</label>
            <input style={adminInp} type="number" min="0" value={f.sortOrder} onChange={function(e){set("sortOrder",Number(e.target.value));}} />
          </div>
        </div>
        <div style={{marginBottom:20}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748b",marginBottom:4}}>Feature Group</label>
          {!showCustom ? (
            <div style={{display:"flex",gap:6}}>
              <select style={{...adminInp,cursor:"pointer",flex:1}} value={f.group} onChange={function(e){set("group",e.target.value);}}>
                {allGroups.map(function(g){ return <option key={g} value={g}>{g}</option>; })}
              </select>
              <button onClick={function(){setShowCustom(true);setCustomGrp("");}}
                style={{padding:"6px 12px",border:"1.5px solid #7c3aed",borderRadius:7,background:"#f5f3ff",color:"#7c3aed",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                + New Group
              </button>
            </div>
          ) : (
            <div style={{display:"flex",gap:6}}>
              <input style={{...adminInp,flex:1}} value={customGrp} onChange={function(e){setCustomGrp(e.target.value);}} placeholder="Type new group name…" autoFocus />
              <button onClick={function(){if(customGrp.trim()){set("group",customGrp.trim());setShowCustom(false);}}}
                style={{padding:"6px 12px",border:"1.5px solid #15803d",borderRadius:7,background:"#f0fdf4",color:"#15803d",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                ✓ Use
              </button>
              <button onClick={function(){setShowCustom(false);}}
                style={{padding:"6px 12px",border:"1.5px solid #e2e8f0",borderRadius:7,background:"#fff",color:"#64748b",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                ✕
              </button>
            </div>
          )}
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onClose} style={adminBtn("#fff","#64748b","#e2e8f0")}>Cancel</button>
          <button onClick={function(){if(!f.key.trim()||!f.label.trim()||!f.dbColumn.trim()){alert("Key, Label and DB Column are required");return;} onSave(f);}}
            style={adminBtn("#2563eb","#fff","#2563eb")}>{isNew ? "Add Feature" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   BI DASHBOARD DESIGNER — ADMIN MODULE
   Matches the exact visual template of existing dashboards:
   MarketOverview, LeadingManufacturers, RegionalLeading, etc.
   Same cards, charts, KPIs, colors, fonts, gradients.
════════════════════════════════════════════════════════════ */

/* ── Exact same color palette as existing dashboards ── */
var _BIA_COLORS = ["#0B2447","#19376D","#1B4F72","#1A6FA0","#2196CE","#48A9D4","#6BBCE0","#154360","#2980B9","#3498DB","#1E3A5F","#5DADE2"];
/* KPI accent gradients — blue palette */
var _BIA_KPI_GRADS = [
  "linear-gradient(135deg,#1A3F6E,#2D6BA8)","linear-gradient(135deg,#23548E,#0F6E56)",
  "linear-gradient(135deg,#154360,#2980B9)","linear-gradient(135deg,#1A6FA0,#48A9D4)",
  "linear-gradient(135deg,#19376D,#3498DB)","linear-gradient(135deg,#2196CE,#6BBCE0)"
];
var _BIA_KPI_ICONS = ["\uD83D\uDCB0","\uD83D\uDCE6","\uD83C\uDFEA","\uD83C\uDFED","\uD83D\uDC8A","\uD83D\uDCC8"];

/* Card + ChartPanel styles — EXACT match to existing dashboards */
var _biaCard = {background:"#fff",borderRadius:18,border:"1px solid rgba(0,0,0,.06)",boxShadow:"0 1px 3px rgba(0,0,0,.04), 0 8px 30px rgba(0,0,0,.04)",position:"relative",overflow:"hidden"};
var _biaChartPanel = {background:"#fff",borderRadius:12,border:"1.5px solid #e2e8f0",padding:"14px 16px",boxShadow:"0 1px 4px rgba(0,0,0,.03)",position:"relative",overflow:"hidden"};
var _biaAccent = function(gr){return{position:"absolute",top:0,left:0,right:0,height:3,background:gr,borderRadius:"18px 18px 0 0"};};

var _BIA_CHART_TYPES = [
  {type:"kpi",label:"KPI Card",icon:"\u2B21",cat:"stat"},{type:"number",label:"Big Number",icon:"\u266F",cat:"stat"},
  {type:"bar",label:"Bar Chart",icon:"\u2593",cat:"compare"},{type:"hbar",label:"Horizontal Bar",icon:"\u2586",cat:"compare"},
  {type:"stackbar",label:"Stacked Bar",icon:"\u2591",cat:"compare"},
  {type:"pie",label:"Pie / Donut",icon:"\u25CF",cat:"part"},{type:"donut",label:"Donut Chart",icon:"\u25CE",cat:"part"},
  {type:"line",label:"Line Chart",icon:"\u2571",cat:"trend"},{type:"area",label:"Area Chart",icon:"\u25E3",cat:"trend"},
  {type:"composed",label:"Bar + Line",icon:"\u2295",cat:"trend"},
  {type:"scatter",label:"Scatter Plot",icon:"\u2022",cat:"correl"},{type:"radar",label:"Radar",icon:"\u2B22",cat:"compare"},
  {type:"radialbar",label:"Radial Gauge",icon:"\u25D4",cat:"stat"},{type:"treemap",label:"Treemap",icon:"\u229E",cat:"part"},
  {type:"funnel",label:"Funnel",icon:"\u25BD",cat:"part"},{type:"waterfall",label:"Waterfall",icon:"\u2502",cat:"trend"},
  {type:"table",label:"Data Table",icon:"\u2630",cat:"detail"},
];
var _BIA_AGG_FNS = [
  {key:"sum",label:"Sum",icon:"\u03A3"},{key:"avg",label:"Average",icon:"\u03BC"},{key:"count",label:"Count",icon:"#"},
  {key:"countDistinct",label:"Distinct",icon:"D#"},{key:"min",label:"Min",icon:"\u2193"},{key:"max",label:"Max",icon:"\u2191"},
  {key:"mod",label:"Mode",icon:"Mo"},{key:"growth",label:"Growth %",icon:"\u0394%"},
  {key:"rankDesc",label:"Rank \u2193",icon:"R\u2193"},{key:"rankAsc",label:"Rank \u2191",icon:"R\u2191"},
  {key:"custom",label:"Custom Eq.",icon:"f(x)"},
];

function _biaUID(){return"bw"+Date.now().toString(36)+Math.random().toString(36).slice(2,6);}
function _biaFmt(v){if(v==null||isNaN(v))return"\u2014";var a=Math.abs(v);if(a>=1e9)return(v/1e9).toFixed(1)+"B";if(a>=1e6)return(v/1e6).toFixed(1)+"M";if(a>=1e3)return(v/1e3).toFixed(1)+"K";if(a<1&&a>0)return v.toFixed(2);return Math.round(v).toLocaleString();}
function _biaFmtFull(v){if(v==null||isNaN(v))return"\u2014";return Number(v).toLocaleString(undefined,{maximumFractionDigits:2});}

function _biaBuildSchema(mp){
  var KM={valUSD:1,valLC:1,units:1,priceUSD:1,priceLC:1,unitsFOC:1,netPriceUSD:1,netPriceLC:1,focUnit:1,grossUnit:1};
  var KP={yearCol:1,quarterCol:1,monthCol:1};
  var FR={country:"Country",sector:"Sector",manufacturer:"Manufacturer",medicine:"Product Name",sku:"SKU",agent:"Agent",region:"Region",governorate:"Governorate",atc1:"ATC1",atc2:"ATC2",atc3:"ATC3",atc4:"ATC4",molecule:"Molecule",continent:"Continent",subContinent:"Sub-Continent",countryOfOrigin:"Country of Origin",manufacturerType:"Mfr Type",localImported:"Local/Imported",subManufacturer:"Sub-Mfr",subProduct:"Sub-Product",productSource:"Product Source",nfc:"NFC",nfc2:"NFC2",nfc3:"NFC3",
    form:"Form",pack:"Pack Size",strength:"Strength",aloneCombined:"Alone/Combined",
    source:"Dataset",docSalesRegion:"Doctor\u2013Sales Region",phSalesRegion:"Pharmacy\u2013Sales Region",docRegion:"Doctor Region",docSpec:"Doctor Specialty",docCity:"Doctor City",
    phRegion:"Pharmacy Region",phCity:"Pharmacy City",
    patientAge:"Patient Age",patientGender:"Patient Gender",diagnosis:"Diagnosis",
    valUSD:"Value USD",valLC:"Value LC",units:"Units",priceUSD:"Price USD",priceLC:"Price LC",unitsFOC:"Units FOC",netPriceUSD:"Net Price USD",netPriceLC:"Net Price LC",focUnit:"FOC Units",grossUnit:"Gross Selling",yearCol:"Year",quarterCol:"Quarter",monthCol:"Month",tenderStatus:"Tender Status",orderNo:"Order No"};
  var dims=[],measures=[],dimMap={},measureMap={},periodCols={};
  Object.keys(mp).forEach(function(k){if(!mp[k]||k.charAt(0)==="_")return;var f=FR[k]||k;var c=mp[k];if(KM[k]){measures.push(f);measureMap[f]=c;}else if(KP[k]){periodCols[k]={friendly:f,col:c};dims.push(f);dimMap[f]=c;}else{dims.push(f);dimMap[f]=c;}});
  return{dimensions:dims,measures:measures,dimMap:dimMap,measureMap:measureMap,periodCols:periodCols};
}

function _biaAggregate(rows,dimCol,measureCol,aggFn,topN,customEq,allMC){
  if(!dimCol)return[];var bk={};
  for(var i=0;i<rows.length;i++){var key=String(rows[i][dimCol]!=null?rows[i][dimCol]:"").trim()||"(blank)";var v=Number(rows[i][measureCol])||0;if(!bk[key])bk[key]={v:[],a:[]};bk[key].v.push(v);bk[key].a.push(rows[i]);}
  var result=Object.keys(bk).map(function(k){var b=bk[k],val,vs=b.v;
    switch(aggFn){case"sum":val=vs.reduce(function(a,c){return a+c;},0);break;case"avg":val=vs.length?vs.reduce(function(a,c){return a+c;},0)/vs.length:0;break;case"count":val=vs.length;break;case"countDistinct":var d={};vs.forEach(function(v2){d[v2]=1;});val=Object.keys(d).length;break;case"min":val=vs.length?Math.min.apply(null,vs):0;break;case"max":val=vs.length?Math.max.apply(null,vs):0;break;case"mod":var fr={};vs.forEach(function(v2){fr[Math.round(v2)]=(fr[Math.round(v2)]||0)+1;});var mx=0;val=0;Object.keys(fr).forEach(function(fk){if(fr[fk]>mx){mx=fr[fk];val=Number(fk);}});break;case"growth":var h=Math.floor(vs.length/2);var f1=0,f2=0;for(var gi=0;gi<h;gi++)f1+=vs[gi];for(var gj=h;gj<vs.length;gj++)f2+=vs[gj];val=f1>0?((f2-f1)/Math.abs(f1))*100:0;break;case"custom":if(customEq&&allMC){try{var sc={};Object.keys(allMC).forEach(function(mk){var mc=allMC[mk];var t=0;b.a.forEach(function(r){t+=Number(r[mc])||0;});sc[mk.replace(/[^a-zA-Z0-9_]/g,"_")]=t;});var ex=customEq;Object.keys(sc).forEach(function(sk){ex=ex.replace(new RegExp("\\b"+sk+"\\b","g"),sc[sk]);});val=Function("return "+ex)();}catch(e){val=0;}}else val=vs.reduce(function(a,c){return a+c;},0);break;default:val=vs.reduce(function(a,c){return a+c;},0);}
    return{name:k,value:isNaN(val)?0:val};});
  result.sort(function(a,b){return aggFn==="rankAsc"?a.value-b.value:b.value-a.value;});
  if(aggFn==="rankDesc"||aggFn==="rankAsc")result.forEach(function(r,i){r.rank=i+1;});
  if(topN&&topN>0)result=result.slice(0,topN);return result;
}
function _biaTotalAgg(rows,mc,aggFn){var vs=[];for(var i=0;i<rows.length;i++)vs.push(Number(rows[i][mc])||0);switch(aggFn){case"avg":return vs.length?vs.reduce(function(a,c){return a+c;},0)/vs.length:0;case"count":return vs.length;case"min":return vs.length?Math.min.apply(null,vs):0;case"max":return vs.length?Math.max.apply(null,vs):0;default:return vs.reduce(function(a,c){return a+c;},0);}}

/* ── Tooltip — exact match to existing dashboards ── */
function _BIATooltip(props){
  if(!props.active||!props.payload||!props.payload.length)return null;
  return React.createElement("div",{style:{borderRadius:8,border:"1px solid #e2e8f0",fontSize:10,boxShadow:"0 4px 12px rgba(0,0,0,.08)",background:"#fff",padding:"8px 12px"}},
    React.createElement("div",{style:{fontWeight:700,color:"#0f172a",marginBottom:3}},props.label),
    props.payload.map(function(p,i){return React.createElement("div",{key:i,style:{color:p.color||"#334155",fontWeight:600}},p.name+": "+_biaFmtFull(p.value));}));
}

/* ══════════════════════════════════════════════════════════════
   WIDGET RENDERER — exact visual match to existing dashboards
══════════════════════════════════════════════════════════════ */
function _BIAWidgetRender(props){
  var config=props.config,data=props.data,schema=props.schema;
  var type=config.type,dimF=config.dimension,measF=config.measure,meas2F=config.measure2,aggFn=config.aggFn||"sum",topN=config.topN;
  var dimCol=schema.dimMap[dimF]||"",measureCol=schema.measureMap[measF]||"",m2Col=schema.measureMap[meas2F]||"";
  var colors=_BIA_COLORS;
  var cd=useMemo(function(){if(!data.length||!dimCol||!measureCol)return[];return _biaAggregate(data,dimCol,measureCol,aggFn,topN||10,config.customEq,schema.measureMap);},[data,dimCol,measureCol,aggFn,topN,config.customEq]);
  var tv=useMemo(function(){if(!data.length||!measureCol)return 0;return _biaTotalAgg(data,measureCol,aggFn);},[data,measureCol,aggFn]);
  var cd2=useMemo(function(){if(!data.length||!dimCol||!m2Col)return[];return _biaAggregate(data,dimCol,m2Col,aggFn,topN||10);},[data,dimCol,m2Col,aggFn,topN]);
  var comp=useMemo(function(){return cd.map(function(d){var m=null;for(var i=0;i<cd2.length;i++){if(cd2[i].name===d.name){m=cd2[i];break;}}return{name:d.name,value:d.value,value2:m?m.value:0};});},[cd,cd2]);
  var gid="bia_"+config.id;
  if(!dimF||!measF)return React.createElement("div",{style:{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"#94a3b8",fontSize:11}},"Click \u270E to configure");
  /* ── Exact chart axis/grid styles from existing dashboards ── */
  var axTick={fontSize:8.5,fill:"#94a3b8"};var axLine={stroke:"#e2e8f0"};var gridS={strokeDasharray:"3 3",stroke:"#f1f5f9"};
  /* ── KPI (exact match to existing KPI row) ── */
  if(type==="kpi"||type==="number"){var isGr=aggFn==="growth";var up=tv>=0;return React.createElement("div",{style:{display:"flex",flexDirection:"column",justifyContent:"center",height:"100%",padding:"0 6px"}},React.createElement("div",{style:{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8}},React.createElement("span",{style:{fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".06em",lineHeight:1.4}},config.title||measF),React.createElement("span",{style:{fontSize:20}},_BIA_KPI_ICONS[0])),React.createElement("div",{style:{fontSize:type==="number"?32:26,fontWeight:800,color:"#0f172a",marginBottom:8,lineHeight:1,letterSpacing:"-.03em"}},isGr?(tv>=0?"+":"")+tv.toFixed(1)+"%":_biaFmt(tv)),isGr&&React.createElement("div",{style:{display:"inline-flex",alignItems:"center",gap:2,fontSize:9.5,fontWeight:700,color:up?"#059669":"#dc2626",background:up?"#ecfdf5":"#fef2f2",padding:"2px 8px",borderRadius:20,width:"fit-content"}},(up?"\u25B2":"\u25BC")+" "+Math.abs(tv).toFixed(1)+"%"));}
  /* ── BAR — gradient bars, exact chart styling ── */
  if(type==="bar")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(BarChart,{data:cd,margin:{top:8,right:16,left:4,bottom:4}},React.createElement("defs",null,cd.map(function(_,i){return React.createElement("linearGradient",{key:i,id:gid+"b"+i,x1:"0",y1:"0",x2:"0",y2:"1"},React.createElement("stop",{offset:"5%",stopColor:colors[i%colors.length],stopOpacity:.9}),React.createElement("stop",{offset:"95%",stopColor:colors[i%colors.length],stopOpacity:.5}));})),React.createElement(CartesianGrid,gridS),React.createElement(XAxis,{dataKey:"name",tick:axTick,axisLine:axLine,tickLine:false,interval:0,angle:-40,textAnchor:"end",height:55}),React.createElement(YAxis,{tickFormatter:_biaFmt,tick:axTick,axisLine:false,tickLine:false,width:50}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Bar,{dataKey:"value",radius:[4,4,0,0],name:measF,isAnimationActive:false},cd.map(function(_,i){return React.createElement(Cell,{key:i,fill:"url(#"+gid+"b"+i+")"});}))));
  /* ── HBAR ── */
  if(type==="hbar")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(BarChart,{data:cd,layout:"vertical",margin:{top:4,right:16,left:4,bottom:4}},React.createElement(CartesianGrid,gridS),React.createElement(XAxis,{type:"number",tick:axTick,axisLine:axLine,tickFormatter:_biaFmt,tickLine:false}),React.createElement(YAxis,{type:"category",dataKey:"name",tick:{fontSize:9,fill:"#334155",fontWeight:600},axisLine:false,tickLine:false,width:90}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Bar,{dataKey:"value",radius:[0,4,4,0],name:measF,isAnimationActive:false},cd.map(function(_,i){return React.createElement(Cell,{key:i,fill:colors[i%colors.length]});}))));
  /* ── STACKBAR ── */
  if(type==="stackbar")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(BarChart,{data:comp,margin:{top:8,right:16,left:4,bottom:4}},React.createElement(CartesianGrid,gridS),React.createElement(XAxis,{dataKey:"name",tick:axTick,axisLine:axLine,tickLine:false,interval:0,angle:-40,textAnchor:"end",height:55}),React.createElement(YAxis,{tickFormatter:_biaFmt,tick:axTick,axisLine:false,tickLine:false,width:50}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Bar,{dataKey:"value",stackId:"a",fill:colors[0],name:measF,isAnimationActive:false}),meas2F&&React.createElement(Bar,{dataKey:"value2",stackId:"a",fill:colors[1],radius:[4,4,0,0],name:meas2F,isAnimationActive:false}),React.createElement(Legend,{wrapperStyle:{fontSize:9,paddingTop:4}})));
  /* ── PIE / DONUT — exact match: innerRadius 45%, label with pct ── */
  if(type==="pie"||type==="donut")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(PieChart,null,React.createElement(Pie,{data:cd,cx:"50%",cy:"45%",innerRadius:type==="donut"?"45%":"0%",outerRadius:"75%",paddingAngle:type==="donut"?3:1,dataKey:"value",nameKey:"name",stroke:"#fff",strokeWidth:2,isAnimationActive:false,label:function(p){return(p.name||"").slice(0,12)+" "+Math.round(p.percent*100)+"%";},labelLine:{stroke:"#94a3b8",strokeWidth:1}},cd.map(function(_,i){return React.createElement(Cell,{key:i,fill:colors[i%colors.length]});})),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Legend,{wrapperStyle:{fontSize:9}})));
  /* ── LINE ── */
  if(type==="line")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(LineChart,{data:cd,margin:{top:8,right:16,left:4,bottom:4}},React.createElement(CartesianGrid,gridS),React.createElement(XAxis,{dataKey:"name",tick:axTick,axisLine:axLine,tickLine:false,interval:0,angle:-40,textAnchor:"end",height:55}),React.createElement(YAxis,{tickFormatter:_biaFmt,tick:axTick,axisLine:false,tickLine:false,width:50}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Line,{type:"monotone",dataKey:"value",stroke:"#1E3A5F",strokeWidth:2.5,dot:{r:3,fill:"#1A3F6E",strokeWidth:0},activeDot:{r:5},isAnimationActive:false,name:measF})));
  /* ── AREA — exact gradient match ── */
  if(type==="area")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(AreaChart,{data:cd,margin:{top:8,right:16,left:4,bottom:4}},React.createElement("defs",null,React.createElement("linearGradient",{id:gid+"ar",x1:"0",y1:"0",x2:"0",y2:"1"},React.createElement("stop",{offset:"5%",stopColor:"#1E3A5F",stopOpacity:.25}),React.createElement("stop",{offset:"95%",stopColor:"#1E3A5F",stopOpacity:.02}))),React.createElement(CartesianGrid,gridS),React.createElement(XAxis,{dataKey:"name",tick:axTick,axisLine:axLine,tickLine:false,interval:0,angle:-40,textAnchor:"end",height:55}),React.createElement(YAxis,{tickFormatter:_biaFmt,tick:axTick,axisLine:false,tickLine:false,width:50}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Area,{type:"monotone",dataKey:"value",stroke:"#1E3A5F",strokeWidth:2.5,fill:"url(#"+gid+"ar)",dot:{r:3,fill:"#1A3F6E",strokeWidth:0},activeDot:{r:5},isAnimationActive:false,name:measF})));
  /* ── COMPOSED ── */
  if(type==="composed")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(ComposedChart,{data:comp,margin:{top:8,right:16,left:4,bottom:4}},React.createElement(CartesianGrid,gridS),React.createElement(XAxis,{dataKey:"name",tick:axTick,axisLine:axLine,tickLine:false,interval:0,angle:-40,textAnchor:"end",height:55}),React.createElement(YAxis,{tickFormatter:_biaFmt,tick:axTick,axisLine:false,tickLine:false,width:50}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Bar,{dataKey:"value",fill:colors[0],radius:[4,4,0,0],name:measF,isAnimationActive:false,opacity:.8}),meas2F&&React.createElement(Line,{type:"monotone",dataKey:"value2",stroke:colors[1],strokeWidth:2.5,dot:{r:3,fill:colors[1],strokeWidth:0},isAnimationActive:false,name:meas2F}),React.createElement(Legend,{wrapperStyle:{fontSize:9,paddingTop:4}})));
  /* ── SCATTER ── */
  if(type==="scatter"&&meas2F)return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(ScatterChart,{margin:{top:8,right:16,left:4,bottom:4}},React.createElement(CartesianGrid,gridS),React.createElement(XAxis,{dataKey:"value",name:measF,tick:axTick,axisLine:axLine,tickFormatter:_biaFmt,tickLine:false}),React.createElement(YAxis,{dataKey:"value2",name:meas2F,tick:axTick,axisLine:false,tickLine:false,tickFormatter:_biaFmt,width:50}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Scatter,{data:comp,isAnimationActive:false},comp.map(function(_,i){return React.createElement(Cell,{key:i,fill:colors[i%colors.length]});}))));
  /* ── RADAR ── */
  if(type==="radar")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(RadarChart,{data:cd.slice(0,8),cx:"50%",cy:"50%",outerRadius:"70%"},React.createElement(PolarGrid,{stroke:"#e2e8f0"}),React.createElement(PolarAngleAxis,{dataKey:"name",tick:{fill:"#334155",fontSize:9}}),React.createElement(PolarRadiusAxis,{tick:{fill:"#94a3b8",fontSize:8}}),React.createElement(Radar,{dataKey:"value",stroke:colors[0],fill:colors[0],fillOpacity:.2,strokeWidth:2,isAnimationActive:false,name:measF})));
  /* ── RADIALBAR ── */
  if(type==="radialbar"){var rb=cd.slice(0,6).map(function(d,i){return{name:d.name,value:d.value,fill:colors[i%colors.length]};});return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(RadialBarChart,{data:rb,cx:"50%",cy:"50%",innerRadius:"20%",outerRadius:"90%",startAngle:180,endAngle:0},React.createElement(RadialBar,{dataKey:"value",background:{fill:"#f1f5f9"},label:{fill:"#0f172a",fontSize:9,position:"insideStart"}}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Legend,{wrapperStyle:{fontSize:9}})));}
  /* ── TREEMAP ── */
  if(type==="treemap")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(Treemap,{data:cd.map(function(d,i){return{name:d.name,value:d.value,fill:colors[i%colors.length]};}),dataKey:"value",nameKey:"name",stroke:"#fff",strokeWidth:2,isAnimationActive:false,content:function(p){if(p.width<35||p.height<22)return React.createElement("rect",{x:p.x,y:p.y,width:p.width,height:p.height,fill:p.fill||colors[0],stroke:"#fff",strokeWidth:2,rx:6});return React.createElement("g",null,React.createElement("rect",{x:p.x,y:p.y,width:p.width,height:p.height,fill:p.fill||colors[0],stroke:"#fff",strokeWidth:2,rx:6}),React.createElement("text",{x:p.x+p.width/2,y:p.y+p.height/2-7,textAnchor:"middle",fill:"#fff",fontSize:9,fontWeight:700},(p.name||"").slice(0,14)),React.createElement("text",{x:p.x+p.width/2,y:p.y+p.height/2+8,textAnchor:"middle",fill:"rgba(255,255,255,.7)",fontSize:8},_biaFmt(p.value)));}}));
  /* ── FUNNEL ── */
  if(type==="funnel")return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(BarChart,{data:cd.slice(0,8),layout:"vertical",margin:{top:4,right:12,bottom:4,left:4}},React.createElement(XAxis,{type:"number",hide:true}),React.createElement(YAxis,{type:"category",dataKey:"name",tick:{fill:"#334155",fontSize:10,fontWeight:700},axisLine:false,tickLine:false,width:90}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Bar,{dataKey:"value",radius:[0,12,12,0],name:measF,isAnimationActive:false},cd.slice(0,8).map(function(_,i){return React.createElement(Cell,{key:i,fill:colors[i%colors.length]});}))));
  /* ── WATERFALL ── */
  if(type==="waterfall"){var wf=[],run=0;cd.forEach(function(d){wf.push({name:d.name,base:run,delta:d.value,fill:d.value>=0?colors[2]:colors[1]});run+=d.value;});wf.push({name:"Total",base:0,delta:run,fill:colors[0]});return React.createElement(ResponsiveContainer,{width:"100%",height:"100%"},React.createElement(BarChart,{data:wf,margin:{top:8,right:16,left:4,bottom:4}},React.createElement(CartesianGrid,gridS),React.createElement(XAxis,{dataKey:"name",tick:axTick,axisLine:axLine,tickLine:false,interval:0,angle:-40,textAnchor:"end",height:55}),React.createElement(YAxis,{tickFormatter:_biaFmt,tick:axTick,axisLine:false,tickLine:false,width:50}),React.createElement(Tooltip,{content:React.createElement(_BIATooltip)}),React.createElement(Bar,{dataKey:"base",stackId:"a",fill:"transparent",isAnimationActive:false}),React.createElement(Bar,{dataKey:"delta",stackId:"a",radius:[4,4,0,0],name:measF,isAnimationActive:false},wf.map(function(d,i){return React.createElement(Cell,{key:i,fill:d.fill});}))));}
  /* ── TABLE ── */
  if(type==="table"){var th={padding:"8px 10px",textAlign:"left",color:"#64748b",borderBottom:"1px solid #e2e8f0",fontWeight:700,fontSize:9.5,position:"sticky",top:0,background:"#fff"};var td={padding:"7px 10px",color:"#0f172a",borderBottom:"1px solid #f1f5f9",fontSize:10.5};return React.createElement("div",{style:{width:"100%",height:"100%",overflow:"auto"}},React.createElement("table",{style:{width:"100%",borderCollapse:"collapse"}},React.createElement("thead",null,React.createElement("tr",null,React.createElement("th",{style:th},"#"),React.createElement("th",{style:th},dimF),React.createElement("th",{style:{...th,textAlign:"right"}},measF),React.createElement("th",{style:{...th,textAlign:"right"}},"Share"))),React.createElement("tbody",null,cd.map(function(d,i){var tot=0;for(var t=0;t<cd.length;t++)tot+=cd[t].value;return React.createElement("tr",{key:i,style:{background:i%2?"#f8fafc":"#fff"}},React.createElement("td",{style:{...td,color:"#94a3b8",fontWeight:700,width:30}},d.rank||i+1),React.createElement("td",{style:{...td,fontWeight:600}},d.name),React.createElement("td",{style:{...td,textAlign:"right",fontWeight:700,fontFamily:"system-ui"}},_biaFmtFull(d.value)),React.createElement("td",{style:{...td,textAlign:"right",color:"#4f46e5",fontWeight:600}},tot>0?((d.value/tot)*100).toFixed(1)+"%":"\u2014"));}))));}
  return React.createElement("div",{style:{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"#94a3b8"}},"Unsupported: "+type);
}

/* ══════════════════════════════════════════════════════════════
   FILTER PANEL — Period filters + dynamic Add Filter
══════════════════════════════════════════════════════════════ */
function _BIAFilterPanel(props){
  var schema=props.schema,data=props.data,filters=props.filters,setFilters=props.setFilters,pf=props.periodFilters,setPf=props.setPeriodFilters,afd=props.activeFilterDims,setAfd=props.setActiveFilterDims;
  var _od=useState(null),openDim=_od[0],setOpenDim=_od[1];var _af=useState(false),showAdd=_af[0],setShowAdd=_af[1];
  var pc=schema.periodCols;
  var pOpts=useMemo(function(){var y={},q={},m={};for(var i=0;i<data.length;i++){if(pc.yearCol){var yv=data[i][pc.yearCol.col];if(yv!=null&&yv!=="")y[yv]=1;}if(pc.quarterCol){var qv=data[i][pc.quarterCol.col];if(qv!=null&&qv!=="")q[qv]=1;}if(pc.monthCol){var mv=data[i][pc.monthCol.col];if(mv!=null&&mv!=="")m[mv]=1;}}return{y:Object.keys(y).sort().reverse(),q:Object.keys(q).sort(),m:Object.keys(m).sort()};},[data,pc]);
  var chip=function(on){return{padding:"4px 12px",borderRadius:5,border:"none",fontSize:10.5,fontWeight:on?700:500,background:on?"#fff":"transparent",color:on?"#1d4ed8":"#64748b",cursor:"pointer",fontFamily:"inherit",boxShadow:on?"0 1px 2px rgba(0,0,0,.06)":"none"};};
  var getVals=function(dc){var v={};for(var i=0;i<Math.min(data.length,15000);i++){var val=data[i][dc];if(val!=null&&val!=="")v[String(val)]=1;}return Object.keys(v).sort().slice(0,200);};
  var hasAny=Object.keys(filters).some(function(k){return filters[k]&&filters[k].length>0;})||Object.keys(pf).some(function(k){return pf[k]&&pf[k].length>0;});
  return React.createElement("div",{style:{padding:"10px 20px",display:"flex",flexDirection:"column",gap:8}},
    /* Period row */
    React.createElement("div",{style:{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}},
      React.createElement("span",{style:{fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".04em"}},"Period"),
      pc.yearCol&&pOpts.y.length>0&&React.createElement("div",{style:{display:"flex",alignItems:"center",gap:2,background:"#f1f5f9",borderRadius:7,padding:2}},pOpts.y.map(function(y){var on=(pf.year||[]).indexOf(y)>=0;return React.createElement("button",{key:y,onClick:function(){setPf(function(p){var c=(p.year||[]).slice();if(on)c=c.filter(function(x){return x!==y;});else c.push(y);return Object.assign({},p,{year:c});});},style:chip(on)},y);})),
      pc.quarterCol&&pOpts.q.length>0&&React.createElement("div",{style:{display:"flex",alignItems:"center",gap:2,background:"#f1f5f9",borderRadius:7,padding:2}},pOpts.q.map(function(q){var on=(pf.quarter||[]).indexOf(q)>=0;return React.createElement("button",{key:q,onClick:function(){setPf(function(p){var c=(p.quarter||[]).slice();if(on)c=c.filter(function(x){return x!==q;});else c.push(q);return Object.assign({},p,{quarter:c});});},style:chip(on)},q);})),
      pc.monthCol&&pOpts.m.length>0&&React.createElement("select",{value:"",onChange:function(e){if(!e.target.value)return;setPf(function(p){var c=(p.month||[]).slice();if(c.indexOf(e.target.value)<0)c.push(e.target.value);return Object.assign({},p,{month:c});});},style:{padding:"4px 8px",borderRadius:6,border:"1.5px solid #e2e8f0",fontSize:10,color:"#64748b",cursor:"pointer",background:"#fff"}},React.createElement("option",{value:""},"+ Month"),(pOpts.m||[]).map(function(m){return React.createElement("option",{key:m,value:m},m);})),
      (pf.month||[]).map(function(m){return React.createElement("span",{key:m,style:{padding:"3px 8px",borderRadius:5,background:"#eff6ff",color:"#2563eb",fontSize:10,fontWeight:600,display:"inline-flex",alignItems:"center",gap:4,border:"1px solid #bfdbfe"}},m,React.createElement("span",{onClick:function(){setPf(function(p){return Object.assign({},p,{month:(p.month||[]).filter(function(x){return x!==m;})});});},style:{cursor:"pointer",fontWeight:900}},"\u00D7"));})
    ),
    /* Dimension filters row */
    React.createElement("div",{style:{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}},
      React.createElement("span",{style:{fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".04em"}},"Filters"),
      afd.map(function(dim){var active=filters[dim]&&filters[dim].length>0;var dc=schema.dimMap[dim];return React.createElement("div",{key:dim,style:{position:"relative"}},
        React.createElement("button",{onClick:function(){setOpenDim(openDim===dim?null:dim);},style:{padding:"4px 12px",borderRadius:6,fontSize:10,fontWeight:active?700:500,cursor:"pointer",border:"1.5px solid "+(active?"#2563eb":"#e2e8f0"),background:active?"#eff6ff":"#fff",color:active?"#2563eb":"#64748b",fontFamily:"inherit"}},dim+(active?" ("+filters[dim].length+")":"")),
        openDim===dim&&React.createElement("div",{style:{position:"absolute",top:"100%",left:0,zIndex:200,marginTop:4,background:"#fff",border:"1.5px solid #e2e8f0",borderRadius:12,padding:10,width:220,maxHeight:250,overflow:"auto",boxShadow:"0 8px 30px rgba(0,0,0,.1)"}},
          React.createElement("div",{style:{display:"flex",justifyContent:"space-between",marginBottom:6}},React.createElement("span",{style:{fontSize:10,fontWeight:800,color:"#0f172a"}},dim),React.createElement("div",{style:{display:"flex",gap:6}},React.createElement("button",{onClick:function(){setFilters(function(f){var n=Object.assign({},f);n[dim]=[];return n;});},style:{background:"none",border:"none",cursor:"pointer",fontSize:8,color:"#dc2626",fontWeight:700}},"Clear"),React.createElement("button",{onClick:function(){setAfd(function(d){return d.filter(function(x){return x!==dim;});});setFilters(function(f){var n=Object.assign({},f);delete n[dim];return n;});setOpenDim(null);},style:{background:"none",border:"none",cursor:"pointer",fontSize:8,color:"#94a3b8",fontWeight:700}},"Remove"))),
          dc&&getVals(dc).map(function(v){var sel=(filters[dim]||[]).indexOf(v)>=0;return React.createElement("div",{key:v,onClick:function(){setFilters(function(f){var c=(f[dim]||[]).slice();if(sel)c=c.filter(function(x){return x!==v;});else c.push(v);var n=Object.assign({},f);n[dim]=c;return n;});},style:{padding:"3px 8px",borderRadius:4,cursor:"pointer",fontSize:10,color:sel?"#2563eb":"#334155",background:sel?"#eff6ff":"transparent",fontWeight:sel?600:400}},(sel?"\u2611":"\u2610")+" "+v);}))
      );}),
      React.createElement("div",{style:{position:"relative"}},React.createElement("button",{onClick:function(){setShowAdd(!showAdd);},style:{padding:"4px 12px",borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer",border:"1.5px dashed #93c5fd",background:"#f0f7ff",color:"#2563eb",fontFamily:"inherit"}},"\u002B Add Filter"),
        showAdd&&React.createElement("div",{style:{position:"absolute",top:"100%",left:0,zIndex:200,marginTop:4,background:"#fff",border:"1.5px solid #e2e8f0",borderRadius:12,padding:8,width:200,maxHeight:260,overflow:"auto",boxShadow:"0 8px 30px rgba(0,0,0,.1)"}},React.createElement("div",{style:{fontSize:10,fontWeight:800,color:"#0f172a",marginBottom:6}},"Add dimension filter"),schema.dimensions.filter(function(d){return afd.indexOf(d)<0;}).map(function(d){return React.createElement("div",{key:d,onClick:function(){setAfd(function(p){return p.concat([d]);});setShowAdd(false);},style:{padding:"5px 8px",borderRadius:6,cursor:"pointer",fontSize:10,color:"#0f172a"}},"\u25B8 "+d);}))),
      hasAny&&React.createElement("button",{onClick:function(){setFilters({});setPf({});},style:{padding:"4px 10px",borderRadius:6,fontSize:10,fontWeight:600,cursor:"pointer",border:"1.5px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontFamily:"inherit"}},"\u2715 Clear All")
    )
  );
}

/* ══════════════════════════════════════════════════════════════
   RefreshDashQButton — admin tool to manually rebuild DashQ summary tables
   Used in the Dashboard Configuration tab. Auto-refresh after upload + nightly
   schedule cover most cases; this button is for ad-hoc rebuilds when admins
   suspect drift (e.g. after direct DB edits).
══════════════════════════════════════════════════════════════ */
function RefreshDashQButton() {
  var _b = useState(false), busy = _b[0], setBusy = _b[1];
  var _r = useState(null), result = _r[0], setResult = _r[1];

  var doRefresh = function() {
    if (busy) return;
    if (!window.confirm("Rebuild DashQ summary tables for Iraq, Jordan, and Libya?\n\nThis takes 10–60 seconds. Dashboards will briefly show a loading state.")) return;
    setBusy(true); setResult(null);
    /* Route through api._fetch — endpoint is now behind requireSession +
       requireAdmin (Layer 2), which means the response is encrypted. Raw
       fetch + r.json() would read the encrypted envelope and miss data.success,
       producing the misleading "Refresh failed" badge while the server-side
       operation actually succeeded. */
    api._fetch("/api/admin/refresh-dashq?role=admin", {
      method: "POST",
      body: JSON.stringify({ role: "admin" })
    })
    .then(function(data) {
      if (data.success) {
        setResult({ ok: true, ms: data.elapsedMs, results: data.results || [] });
      } else {
        setResult({ ok: false, error: data.error || "Refresh failed" });
      }
    })
    .catch(function(e){ setResult({ ok: false, error: e.message }); })
    .finally(function(){ setBusy(false); });
  };

  return (
    <div style={{display:"flex",alignItems:"center",gap:10}}>
      {result && (
        <div style={{
          fontSize:10, fontWeight:600,
          color: result.ok ? "#047857" : "#b91c1c",
          background: result.ok ? "#ecfdf5" : "#fef2f2",
          padding: "4px 10px", borderRadius: 8,
          border: "1px solid " + (result.ok ? "#a7f3d0" : "#fecaca")
        }}>
          {result.ok
            ? <span>✓ Refreshed in {(result.ms/1000).toFixed(1)}s · {result.results.filter(function(r){return r.status==="ok";}).length}/{result.results.length} tables</span>
            : <span>✕ {result.error}</span>}
        </div>
      )}
      <button onClick={doRefresh} disabled={busy}
        title="Rebuild the pre-aggregated tables that power dashboards. Use after direct DB edits or if dashboards seem stale."
        style={{
          padding:"7px 14px",
          background: busy ? "#94a3b8" : "linear-gradient(180deg, #f97316 0%, #ea580c 100%)",
          color:"#fff", border:"none", borderRadius:7,
          fontSize:11, fontWeight:700, letterSpacing:".02em",
          cursor: busy ? "wait" : "pointer",
          boxShadow: busy ? "none" : "0 1px 3px rgba(234,88,12,.3)",
          display:"flex", alignItems:"center", gap:6,
          transition:"all .15s"
        }}>
        {busy ? <><span className="spin" style={{display:"inline-block", animation:"spin 1s linear infinite"}}>⟳</span> Refreshing…</> : <>🔄 Refresh DashQ</>}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   UserAnalyticsPanel — admin reporting on user activity
══════════════════════════════════════════════════════════════ */
/* r45 (Noura 2026-10-11): User Analytics is granted PER TAB to team admins —
   permission "analytics" = all tabs, "analytics:<key>" = that tab only. */
var ANALYTICS_TABS = [
  {k:"users",       l:"Users"},
  {k:"clients",     l:"Clients"},
  {k:"dashboards",  l:"Dashboards"},
  {k:"queries",     l:"Popular Queries"},
  {k:"filters",     l:"Filter Usage"},
  {k:"grouping",    l:"Grouping Patterns"},
  {k:"exports",     l:"Export Tracer"},
  {k:"fingerprint", l:"Data Fingerprint"},
  {k:"activation",  l:"Activation"},
  {k:"audit",       l:"Audit Log"}
];
/* granted analytics tabs from a permissions array: null = all, [] = none */
function _analyticsTabsOf9(perms, isFull) {
  if (isFull) return null;
  var p = perms || [];
  if (p.indexOf("analytics") >= 0) return null;
  var out = [];
  p.forEach(function (k) { k = String(k || ""); if (k.indexOf("analytics:") === 0) out.push(k.slice(10)); });
  return out;
}
function UserAnalyticsPanel(props) {
  var allowedTabs = (props && props.allowedTabs !== undefined) ? props.allowedTabs : null; /* r45: null = every tab */
  var _tabOk = function (k) { return !allowedTabs || allowedTabs.indexOf(k) >= 0; };
  var _d = useState(30), days = _d[0], setDays = _d[1];
  var _tab = useState(!allowedTabs ? "overview" : (allowedTabs[0] || "overview")), subTab = _tab[0], setSubTab = _tab[1]; /* r45: restricted grant → first allowed tab */
  var _ld = useState(false), loading = _ld[0], setLoading = _ld[1];
  var _ov = useState(null), overview = _ov[0], setOverview = _ov[1];
  var _users = useState([]), usersList = _users[0], setUsersList = _users[1];
  var _uSrt = useState({col:"LastSeen", dir:"desc"}), uSort = _uSrt[0], setUSort = _uSrt[1];
  var _uFlt = useState(""), uFilter = _uFlt[0], setUFilter = _uFlt[1];
  /* r46 (Noura 2026-10-11): Users tab dropdown filters — by user, by client, by study */
  var _uSU = useState(""), uSelUser = _uSU[0], setUSelUser = _uSU[1];
  var _uSC = useState(""), uSelClient = _uSC[0], setUSelClient = _uSC[1];
  var _uSS = useState(""), uSelStudy = _uSS[0], setUSelStudy = _uSS[1];
  var _clients = useState([]), clientsList = _clients[0], setClientsList = _clients[1];
  var _dash = useState([]), dashList = _dash[0], setDashList = _dash[1];
  var _q = useState([]), queriesList = _q[0], setQueriesList = _q[1];
  var _f = useState(null), filtersAnalysis = _f[0], setFiltersAnalysis = _f[1];
  var _rec = useState([]), recommendations = _rec[0], setRecommendations = _rec[1];
  var _drill = useState(null), drillUser = _drill[0], setDrillUser = _drill[1];
  var _drillEv = useState([]), drillEvents = _drillEv[0], setDrillEvents = _drillEv[1];
  /* Scoping: restrict the "Popular Queries", "Filter Usage", and "Grouping
     Patterns" tabs to a specific client or specific user. Empty = global view. */
  var _sc = useState(""), scopeClient = _sc[0], setScopeClient = _sc[1];
  var _su = useState(""), scopeUser   = _su[0], setScopeUser   = _su[1];
  /* Dim-combo (grouping patterns) data */
  var _dc = useState([]), dimCombos = _dc[0], setDimCombos = _dc[1];
  /* Export provenance tracer (Layer 1 — ExportLog) */
  var _xl = useState([]), exportsList = _xl[0], setExportsList = _xl[1];
  var _xq = useState(""), exportSearch = _xq[0], setExportSearch = _xq[1];
  var _xe = useState(null), expandedExport = _xe[0], setExpandedExport = _xe[1];
  var _xt = useState(false), traceBusy = _xt[0], setTraceBusy = _xt[1];
  /* Data fingerprint (Layer 2) — client toggles + verification tool */
  var _fpc = useState([]), fpClients = _fpc[0], setFpClients = _fpc[1];
  var _fps = useState(""), fpStudy = _fps[0], setFpStudy = _fps[1];
  var _fpr = useState(""), fpRowsText = _fpr[0], setFpRowsText = _fpr[1];
  var _fpres = useState(null), fpResult = _fpres[0], setFpResult = _fpres[1];
  var _fpb = useState(false), fpBusy = _fpb[0], setFpBusy = _fpb[1];
  /* Excel Marker Checker (Suggestion 2) + admin audit log */
  var _fm = useState(null), fileMarker = _fm[0], setFileMarker = _fm[1];
  var _au = useState([]), auditList = _au[0], setAuditList = _au[1];
  /* Activation report (all-time, independent of the period selector) */
  var _adp = useState(null), adoption = _adp[0], setAdoption = _adp[1];
  var _adpS = useState(""), adpStudy = _adpS[0], setAdpStudy = _adpS[1];
  var _adpC = useState(""), adpClient = _adpC[0], setAdpClient = _adpC[1];
  var _adpA = useState(""), adpStatus = _adpA[0], setAdpStatus = _adpA[1]; /* "" | "activated" | "never" */
  var _adpQ = useState(""), adpQ = _adpQ[0], setAdpQ = _adpQ[1];
  useEffect(function() { api.getActivityAdoption().then(setAdoption).catch(function(e){ console.error(e); }); }, []);
  var _auf = useState(""), auditAdminFilter = _auf[0], setAuditAdminFilter = _auf[1];
  /* Fingerprint user-list filter */
  var _fpf = useState(""), fpUserFilter = _fpf[0], setFpUserFilter = _fpf[1];
  var _fpo = useState(false), fpOnlyOn = _fpo[0], setFpOnlyOn = _fpo[1];
  /* Candidate-SKU helper (which SKUs to sample from suspect data) */
  var _fpcand = useState(null), fpCandidates = _fpcand[0], setFpCandidates = _fpcand[1];
  /* Structured row builder for the Fingerprint Check */
  var _fprows = useState([]), fpRows = _fprows[0], setFpRows = _fprows[1];
  var _fph = useState([]), fpHistory = _fph[0], setFpHistory = _fph[1];
  var _fphs = useState(null), fpHistSel = _fphs[0], setFpHistSel = _fphs[1];
  var _fpu = useState(""), fpUpMsg = _fpu[0], setFpUpMsg = _fpu[1];
  var _fpupt = useState("all"), fpUpPType = _fpupt[0], setFpUpPType = _fpupt[1];
  var _fpro = useState(true), fpRowsOpen = _fpro[0], setFpRowsOpen = _fpro[1];
  var _fpent = useState({sku:"", mf:"", product:"", ptype:"quarter", year:String(new Date().getFullYear()), quarter:"1", month:"1", units:""}), fpEntry = _fpent[0], setFpEntry = _fpent[1];
  var _fpbulk = useState(false), fpBulkOpen = _fpbulk[0], setFpBulkOpen = _fpbulk[1];
  var _fpmk = useState(null), fpMarkedView = _fpmk[0], setFpMarkedView = _fpmk[1];
  /* Live presence (Active Now card) — refreshed every 60s, independent of period */
  var _anlv = useState(null), anLive = _anlv[0], setAnLive = _anlv[1];
  var _anlo = useState(false), anLiveOpen = _anlo[0], setAnLiveOpen = _anlo[1];
  useEffect(function() {
    var _lvF = function() { api.getDeviceLiveStats().then(setAnLive).catch(function(e){}); };
    _lvF();
    var _lvIv = setInterval(_lvF, 60000);
    return function() { clearInterval(_lvIv); };
  }, []);

  useEffect(function() {
    setLoading(true);
    /* Scope object passed to scoping-aware endpoints; falsy values are dropped by api._scopeQs */
    var scope = { clientId: scopeClient || null, userId: scopeUser || null };
    Promise.all([
      api.getActivityOverview(days).catch(function(){return {};}),
      api.getActivityUsers(days).catch(function(){return {users:[]};}),
      api.getActivityClients(days).catch(function(){return {clients:[]};}),
      api.getActivityDashboards(days).catch(function(){return {dashboards:[]};}),
      api.getActivityQueries(days, scope).catch(function(){return {queries:[]};}),
      api.getActivityFilters(days, scope).catch(function(){return null;}),
      api.getActivityRecommendations(days).catch(function(){return {recommendations:[]};}),
      api.getActivityDimCombos(days, scope).catch(function(){return {combos:[]};}),
      api.getExportLog({days: days, clientId: scopeClient || "", userId: scopeUser || "", limit: 500}).catch(function(){return {exports:[]};}),
      api.getFingerprintUsers().catch(function(){return {users:[]};}),
      api.getAdminAudit(days).catch(function(){return {audit:[]};})
    ]).then(function(results) {
      setOverview(results[0] || {});
      setUsersList((results[1] && results[1].users) || []);
      setClientsList((results[2] && results[2].clients) || []);
      setDashList((results[3] && results[3].dashboards) || []);
      setQueriesList((results[4] && results[4].queries) || []);
      setFiltersAnalysis(results[5] || null);
      setRecommendations((results[6] && results[6].recommendations) || []);
      setDimCombos((results[7] && results[7].combos) || []);
      setExportsList((results[8] && results[8].exports) || []);
      setFpClients((results[9] && results[9].users) || []);
      setAuditList((results[10] && results[10].audit) || []);
      setLoading(false);
    }).catch(function(){ setLoading(false); });
  }, [days, scopeClient, scopeUser]);

  var fmtNum = function(n) { n = n || 0; if(n >= 1e9) return (n/1e9).toFixed(1)+"B"; if(n >= 1e6) return (n/1e6).toFixed(1)+"M"; if(n >= 1e3) return (n/1e3).toFixed(1)+"K"; return String(Math.round(n)); };
  var fmtDate = function(d) { if(!d) return "—"; try { var dt = new Date(d); return dt.toLocaleDateString() + " " + dt.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}); } catch(e){return "—";} };
  var fmtPretty = function(d) { if(!d) return "—"; try { var dt = new Date(d); var ms = Date.now() - dt.getTime(); var mins = Math.floor(ms/60000); if(mins < 1) return "just now"; if(mins < 60) return mins+"m ago"; var hrs = Math.floor(mins/60); if(hrs < 24) return hrs+"h ago"; var days2 = Math.floor(hrs/24); if(days2 < 30) return days2+"d ago"; return dt.toLocaleDateString(); } catch(e){return "—";} };
  /* Absolute timestamp in Amman local time (Asia/Amman), 24h. */
  var fmtAmman = function(d) { if(!d) return "—"; try { return new Date(d).toLocaleString("en-GB",{timeZone:"Asia/Amman",day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",hour12:false}); } catch(e){ return "—"; } };

  var loadUserTimeline = function(u) {
    setDrillUser(u);
    api.getActivityUser(u.UserId, days).then(function(r){ setDrillEvents(r.events || []); }).catch(function(){ setDrillEvents([]); });
  };

  var recBg = {info:"#eff6ff", warning:"#fef3c7", opportunity:"#ecfdf5"};
  var recBorder = {info:"#60a5fa", warning:"#f59e0b", opportunity:"#10b981"};
  var recIcon = {info:"ℹ️", warning:"⚠️", opportunity:"💡"};

  var kpiCard = function(title, value, sub, color) {
    return React.createElement("div",{key:title, style:{background:"#fff",borderRadius:12,padding:"16px 20px",border:"1px solid #e2e8f0",boxShadow:"0 1px 3px rgba(0,0,0,.04)",flex:1,minWidth:160}},
      React.createElement("div",{style:{fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}},title),
      React.createElement("div",{style:{fontSize:24,fontWeight:800,color:color||"#0f172a",letterSpacing:"-.02em"}},value),
      sub ? React.createElement("div",{style:{fontSize:10,color:"#94a3b8",marginTop:4}},sub) : null
    );
  };

  if (loading && !overview) return <div style={{padding:40,textAlign:"center",color:"#64748b"}}><span style={{display:"inline-block",width:32,height:32,border:"3px solid #e2e8f0",borderTopColor:"#2563eb",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/><div style={{marginTop:12,fontSize:11}}>Loading analytics…</div></div>;

  return (
    <div style={{padding:"0 0 40px 0"}}>
      {/* Period selector */}
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:18,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:"#64748b",fontWeight:600,marginRight:6}}>PERIOD:</span>
        {[{v:0,l:"Today"},{v:7,l:"Last 7 days"},{v:30,l:"Last 30 days"},{v:90,l:"Last 90 days"},{v:180,l:"Last 180 days"}].map(function(opt){
          var on = days === opt.v;
          return <button key={opt.v} onClick={function(){setDays(opt.v);}} style={{padding:"5px 12px",border:on?"1.5px solid #2563eb":"1.5px solid #e2e8f0",borderRadius:16,background:on?"#eff6ff":"#fff",color:on?"#2563eb":"#64748b",fontSize:10,fontWeight:700,cursor:"pointer"}}>{opt.l}</button>;
        })}

        {/* Scope filters — apply to Popular Queries, Filter Usage, and Grouping Patterns
            tabs. Empty selection = no filter (global view across all clients/users). */}
        <span style={{fontSize:11,color:"#64748b",fontWeight:600,marginLeft:16,marginRight:6}}>SCOPE:</span>
        <select value={scopeClient} onChange={function(e){ setScopeClient(e.target.value); setScopeUser(""); /* changing client resets user — user list scopes to client */ }}
          style={{padding:"5px 10px",border:"1.5px solid "+(scopeClient?"#2563eb":"#e2e8f0"),borderRadius:6,background:scopeClient?"#eff6ff":"#fff",color:scopeClient?"#2563eb":"#64748b",fontSize:10,fontWeight:600,fontFamily:"inherit",outline:"none",cursor:"pointer",minWidth:160}}>
          <option value="">All clients</option>
          {clientsList.map(function(c){ return <option key={c.ClientId} value={c.ClientId}>{c.ClientName || ("Client #" + c.ClientId)}</option>; })}
        </select>
        <select value={scopeUser} onChange={function(e){ setScopeUser(e.target.value); }}
          style={{padding:"5px 10px",border:"1.5px solid "+(scopeUser?"#2563eb":"#e2e8f0"),borderRadius:6,background:scopeUser?"#eff6ff":"#fff",color:scopeUser?"#2563eb":"#64748b",fontSize:10,fontWeight:600,fontFamily:"inherit",outline:"none",cursor:"pointer",minWidth:180}}>
          <option value="">All users</option>
          {(scopeClient ? usersList.filter(function(u){ return String(u.ClientId) === String(scopeClient); }) : usersList).map(function(u){
            return <option key={u.UserId} value={u.UserId}>{u.UserName || u.Email || ("User #" + u.UserId)}</option>;
          })}
        </select>
        {(scopeClient || scopeUser) && (
          <button onClick={function(){ setScopeClient(""); setScopeUser(""); }}
            title="Clear scope filters"
            style={{padding:"5px 10px",border:"1px solid #e2e8f0",borderRadius:6,background:"#f8fafc",color:"#475569",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* KPI cards */}
      {overview && <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <div onClick={function(){ setAnLiveOpen(!anLiveOpen); }}
          title={anLive && anLive.activeNow ? "Click to see who is online and which study they have open" : "No users on the portal right now"}
          style={{background:anLiveOpen?"#ecfdf5":"#fff",borderRadius:12,padding:"16px 20px",border:"1.5px solid #6ee7b7",boxShadow:"0 1px 3px rgba(16,185,129,.15)",flex:1,minWidth:160,cursor:"pointer"}}>
          <div style={{fontSize:9.5,fontWeight:700,color:"#059669",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:"#10b981",display:"inline-block",boxShadow:"0 0 0 3px rgba(16,185,129,.25)"}} />
            Active Now
          </div>
          <div style={{fontSize:24,fontWeight:800,color:"#059669",letterSpacing:"-.02em"}}>{anLive ? anLive.activeNow : "\u2026"}</div>
          <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>{"on the portal right now " + (anLiveOpen ? "\u25B4" : "\u25BE")}</div>
        </div>
        {kpiCard("Active Users", fmtNum(overview.ActiveUsers), "unique users in period", "#2563eb")}
        {kpiCard("Active Clients", fmtNum(overview.ActiveClients), "organizations", "#7c3aed")}
        {kpiCard("Logins", fmtNum(overview.TotalLogins), "session starts", "#0891b2")}
        {kpiCard("Queries", fmtNum(overview.TotalQueries), "pivot executions", "#0891b2")}
        {kpiCard("Dashboard Views", fmtNum(overview.TotalDashViews), "dashboard opens", "#6366f1")}
        {kpiCard("Exports", fmtNum(overview.TotalExports), "files downloaded", "#059669")}
        {kpiCard("Rows Processed", fmtNum(overview.TotalRowsProcessed), "data volume", "#475569")}
        {kpiCard("Data Volume", fmtNum(overview.TotalDataKB) + " KB", "aggregated payload", "#475569")}
      </div>}

      {/* ═══ WHO'S ONLINE — expands from the Active Now card ═══ */}
      {anLiveOpen && (
        <div style={{background:"#fff",borderRadius:12,border:"1.5px solid #6ee7b7",boxShadow:"0 2px 10px rgba(16,185,129,.12)",marginBottom:20,overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"12px 18px",background:"linear-gradient(135deg,#ecfdf5,#d1fae5)",borderBottom:"1px solid #a7f3d0"}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:"#10b981",display:"inline-block",boxShadow:"0 0 0 3px rgba(16,185,129,.25)"}} />
            <span style={{fontSize:12.5,fontWeight:800,color:"#065f46"}}>{"Online now (" + ((anLive && anLive.activeNow) || 0) + ") \u00B7 sessions active in the last 5 minutes"}</span>
            <div style={{flex:1}} />
            <span style={{fontSize:9.5,color:"#059669"}}>auto-refreshes every 60s</span>
          </div>
          {(!anLive || !anLive.activeUsers || anLive.activeUsers.length === 0) ? (
            <div style={{padding:"18px 20px",fontSize:11.5,color:"#64748b"}}>No users are on the portal right now.</div>
          ) : (
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11.5}}>
              <thead><tr style={{background:"#f8fafc"}}>
                <th style={{padding:"8px 18px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em"}}>User</th>
                <th style={{padding:"8px 12px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em"}}>Client</th>
                <th style={{padding:"8px 12px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em"}}>Study Open</th>
                <th style={{padding:"8px 12px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em"}}>Last Action</th>
                <th style={{padding:"8px 12px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em"}}>Last Seen</th>
                <th style={{padding:"8px 18px",textAlign:"right",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em"}}></th>
              </tr></thead>
              <tbody>
                {anLive.activeUsers.map(function(u9, i9){
                  var _stLbl = u9.study ? String(u9.study).replace(/_/g, " ").toUpperCase() : "";
                  var _actLbl = u9.dashboard ? u9.dashboard : (u9.activity ? String(u9.activity).replace(/_/g, " ") : "\u2014");
                  return (
                    <tr key={i9} style={{borderTop:"1px solid #f1f5f9"}}>
                      <td style={{padding:"9px 18px"}}>
                        <span style={{width:7,height:7,borderRadius:"50%",background:"#10b981",display:"inline-block",marginRight:8}} />
                        <span style={{fontWeight:700,color:"#0f172a"}}>{u9.name}</span>
                      </td>
                      <td style={{padding:"9px 12px",color:"#475569"}}>{u9.client || "\u2014"}</td>
                      <td style={{padding:"9px 12px"}}>
                        {_stLbl ? <span style={{background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:10,padding:"2px 10px",fontSize:10,fontWeight:700}}>{_stLbl}</span>
                                : <span style={{color:"#94a3b8"}}>{"\u2014"}</span>}
                      </td>
                      <td style={{padding:"9px 12px",color:"#475569",textTransform:"capitalize"}}>{_actLbl}</td>
                      <td style={{padding:"9px 12px",color:"#64748b"}}>{fmtPretty(u9.lastSeen)}</td>
                      <td style={{padding:"9px 18px",textAlign:"right"}}>
                        {u9.userId ? <button onClick={function(){ setScopeClient(""); setScopeUser(String(u9.userId)); }}
                          title="Filter all analytics below to this user"
                          style={{padding:"4px 12px",background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer"}}>{"Filter \u2192"}</button> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && <div style={{background:"#fff",borderRadius:12,padding:"16px 20px",border:"1px solid #e2e8f0",marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:800,color:"#0f172a",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:18}}>🎯</span> Actionable Recommendations
        </div>
        {recommendations.map(function(r, i){
          return <div key={i} style={{background:recBg[r.severity]||"#f8fafc",borderLeft:"3px solid "+(recBorder[r.severity]||"#94a3b8"),padding:"10px 14px",borderRadius:6,marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:"#0f172a",marginBottom:3}}>{recIcon[r.severity]||"•"} {r.title}</div>
            <div style={{fontSize:10.5,color:"#475569",lineHeight:1.5}}>{r.text}</div>
          </div>;
        })}
      </div>}

      {/* Sub-tab switcher */}
      <div style={{display:"flex",gap:0,marginBottom:14,borderBottom:"1.5px solid #e2e8f0",flexWrap:"wrap"}}>
        {[{k:"users",l:"Users",c:usersList.length},
          {k:"clients",l:"Clients",c:clientsList.length},
          {k:"dashboards",l:"Dashboards",c:dashList.length},
          {k:"queries",l:"Popular Queries",c:queriesList.length},
          {k:"filters",l:"Filter Usage",c:filtersAnalysis?filtersAnalysis.totalQueries:0},
          {k:"grouping",l:"Grouping Patterns",c:dimCombos.length},
          {k:"exports",l:"Export Tracer",c:exportsList.length},
          {k:"fingerprint",l:"Data Fingerprint",c:fpClients.filter(function(c){return c.FingerprintEnabled;}).length},
          {k:"activation",l:"Activation",c:adoption?adoption.users.length:0},
          {k:"audit",l:"Audit Log",c:auditList.length}].filter(function(t){ return _tabOk(t.k); }).map(function(t){
          var on = subTab === t.k;
          return <button key={t.k} onClick={function(){setSubTab(t.k);}} style={{padding:"9px 16px",border:"none",borderBottom:on?"2.5px solid #2563eb":"2.5px solid transparent",background:"transparent",color:on?"#2563eb":"#64748b",fontSize:11,fontWeight:on?700:500,cursor:"pointer",fontFamily:"inherit",marginBottom:-1.5}}>{t.l} <span style={{background:on?"#dbeafe":"#f1f5f9",color:on?"#1d4ed8":"#94a3b8",padding:"1px 7px",borderRadius:10,fontSize:9,marginLeft:4,fontWeight:700}}>{t.c}</span></button>;
        })}
      </div>

      {/* Users tab */}
      {subTab === "users" && (function(){
        var _uCols = [
          {k:"UserName",   l:"USER",       al:"left",  t:"s"},
          {k:"ClientName", l:"CLIENT",     al:"left",  t:"s"},
          {k:"Logins",     l:"LOGINS",     al:"right", t:"n"},
          {k:"Queries",    l:"QUERIES",    al:"right", t:"n"},
          {k:"DashViews",  l:"DASHBOARDS", al:"right", t:"n"},
          {k:"Exports",    l:"EXPORTS",    al:"right", t:"n"},
          {k:"RowsTotal",  l:"ROWS",       al:"right", t:"n"},
          {k:"LastSeen",   l:"LAST SEEN",  al:"left",  t:"d"}
        ];
        var _ct = {}; for (var _ci=0;_ci<_uCols.length;_ci++) _ct[_uCols[_ci].k]=_uCols[_ci].t;
        var _q = uFilter.trim().toLowerCase();
        /* r46: dropdown options from the loaded rows */
        var _optU = [], _optC = [], _optS = [];
        usersList.forEach(function(u){
          var un = u.UserName || ("User #" + u.UserId); if (_optU.indexOf(un) < 0) _optU.push(un);
          var cn = u.ClientName || ""; if (cn && _optC.indexOf(cn) < 0) _optC.push(cn);
          (u.Studies || []).forEach(function(s9){ if (_optS.indexOf(s9) < 0) _optS.push(s9); });
        });
        _optU.sort(); _optC.sort(); _optS.sort();
        var _stLbl9 = function(code){ return String(code || "").replace(/_/g, " ").toUpperCase(); };
        var _view = usersList.filter(function(u){
          var un = u.UserName || ("User #" + u.UserId);
          if (uSelUser && un !== uSelUser) return false;
          if (uSelClient && (u.ClientName || "") !== uSelClient) return false;
          if (uSelStudy && (u.Studies || []).indexOf(uSelStudy) < 0) return false;
          if(!_q) return true;
          return String(u.UserName||"").toLowerCase().indexOf(_q)>=0 || String(u.ClientName||"").toLowerCase().indexOf(_q)>=0;
        });
        var _anyF9 = !!(uFilter || uSelUser || uSelClient || uSelStudy);
        var _selS9 = {padding:"6px 10px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:11,fontFamily:"inherit",outline:"none",color:"#0f172a",background:"#fff",cursor:"pointer",maxWidth:220};
        var _dir = uSort.dir==="asc"?1:-1, _col = uSort.col, _t = _ct[_col]||"s";
        _view = _view.slice().sort(function(a,b){
          var av=a[_col], bv=b[_col];
          if(_t==="n") return ((Number(av)||0)-(Number(bv)||0))*_dir;
          if(_t==="d") return (((av?new Date(av).getTime():0))-((bv?new Date(bv).getTime():0)))*_dir;
          return String(av==null?"":av).localeCompare(String(bv==null?"":bv))*_dir;
        });
        return <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
            <input value={uFilter} onChange={function(e){setUFilter(e.target.value);}} placeholder="Search user or client…"
              style={{flex:"0 0 220px",padding:"7px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:11,fontFamily:"inherit",outline:"none",color:"#0f172a"}} />
            <select value={uSelUser} onChange={function(e){setUSelUser(e.target.value);}} style={_selS9}>
              <option value="">All users</option>
              {_optU.map(function(v){ return <option key={v} value={v}>{v}</option>; })}
            </select>
            <select value={uSelClient} onChange={function(e){setUSelClient(e.target.value);}} style={_selS9}>
              <option value="">All clients</option>
              {_optC.map(function(v){ return <option key={v} value={v}>{v}</option>; })}
            </select>
            <select value={uSelStudy} onChange={function(e){setUSelStudy(e.target.value);}} style={_selS9}>
              <option value="">All studies</option>
              {_optS.map(function(v){ return <option key={v} value={v}>{_stLbl9(v)}</option>; })}
            </select>
            {_anyF9 && <button onClick={function(){setUFilter(""); setUSelUser(""); setUSelClient(""); setUSelStudy("");}} style={{padding:"6px 12px",border:"1px solid #e2e8f0",borderRadius:8,background:"#fff",color:"#64748b",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Clear</button>}
            <span style={{fontSize:10,color:"#94a3b8"}}>{_view.length} of {usersList.length}</span>
            <span style={{fontSize:10,color:"#94a3b8",marginLeft:"auto"}}>Times in Amman (GMT+3)</span>
          </div>
          <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead>
                <tr style={{background:"#f8fafc",borderBottom:"1.5px solid #e2e8f0"}}>
                  {_uCols.map(function(c){
                    var on = uSort.col===c.k;
                    return <th key={c.k} onClick={function(){ setUSort({col:c.k, dir:(on && uSort.dir==="desc")?"asc":"desc"}); }}
                      style={{padding:"10px 14px",textAlign:c.al,fontWeight:700,color:on?"#2563eb":"#475569",fontSize:10,cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>
                      {c.l}{on ? (uSort.dir==="desc"?" ▼":" ▲") : ""}
                    </th>;
                  })}
                  <th style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:"#475569",fontSize:10}}></th>
                </tr>
              </thead>
              <tbody>
                {_view.map(function(u, i){
                  return <tr key={i} style={{borderBottom:"1px solid #f1f5f9"}}>
                    <td style={{padding:"9px 14px",fontWeight:700,color:"#0f172a"}}>{u.UserName || "User #"+u.UserId}</td>
                    <td style={{padding:"9px 14px",color:"#475569"}}>{u.ClientName || "—"}
                      {(u.Studies || []).length ? <div style={{marginTop:2,display:"flex",flexWrap:"wrap",gap:3}}>{u.Studies.map(function(s9){ return <span key={s9} onClick={function(){ setUSelStudy(s9); }} title="filter by this study" style={{fontSize:8,fontWeight:700,background:uSelStudy===s9?"#dbeafe":"#f1f5f9",color:uSelStudy===s9?"#1d4ed8":"#64748b",border:"1px solid "+(uSelStudy===s9?"#bfdbfe":"#e2e8f0"),borderRadius:3,padding:"0 5px",cursor:"pointer"}}>{_stLbl9(s9)}</span>; })}</div> : null}
                    </td>
                    <td style={{padding:"9px 14px",textAlign:"right",color:"#0891b2",fontFamily:"'JetBrains Mono',monospace"}}>{u.Logins}</td>
                    <td style={{padding:"9px 14px",textAlign:"right",color:"#0891b2",fontFamily:"'JetBrains Mono',monospace"}}>{u.Queries}</td>
                    <td style={{padding:"9px 14px",textAlign:"right",color:"#6366f1",fontFamily:"'JetBrains Mono',monospace"}}>{u.DashViews}</td>
                    <td style={{padding:"9px 14px",textAlign:"right",color:"#059669",fontFamily:"'JetBrains Mono',monospace"}}>{u.Exports}</td>
                    <td style={{padding:"9px 14px",textAlign:"right",color:"#475569",fontFamily:"'JetBrains Mono',monospace"}}>{fmtNum(u.RowsTotal)}</td>
                    <td style={{padding:"9px 14px",color:"#64748b",fontSize:10,whiteSpace:"nowrap"}} title={fmtPretty(u.LastSeen)}>{fmtAmman(u.LastSeen)}</td>
                    <td style={{padding:"9px 14px",textAlign:"center"}}><button onClick={function(){loadUserTimeline(u);}} style={{padding:"3px 8px",border:"1px solid #2563eb",borderRadius:6,background:"#fff",color:"#2563eb",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Timeline</button></td>
                  </tr>;
                })}
                {_view.length === 0 && <tr><td colSpan={9} style={{padding:40,textAlign:"center",color:"#94a3b8"}}>{usersList.length===0?"No user activity in this period.":"No users match the filter."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>;
      })()}

      {/* Clients tab */}
      {subTab === "clients" && <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
          <thead>
            <tr style={{background:"#f8fafc",borderBottom:"1.5px solid #e2e8f0"}}>
              <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>CLIENT</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>USERS</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>EVENTS</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>QUERIES</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>DASHBOARDS</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>EXPORTS</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>ROWS PROCESSED</th>
              <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>LAST ACTIVITY</th>
            </tr>
          </thead>
          <tbody>
            {clientsList.map(function(c, i){
              return <tr key={i} style={{borderBottom:"1px solid #f1f5f9"}}>
                <td style={{padding:"9px 14px",fontWeight:700,color:"#0f172a"}}>{c.ClientName || "Client #"+c.ClientId}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#7c3aed",fontFamily:"'JetBrains Mono',monospace"}}>{c.UniqueUsers}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#475569",fontFamily:"'JetBrains Mono',monospace"}}>{c.EventCount}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#0891b2",fontFamily:"'JetBrains Mono',monospace"}}>{c.Queries}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#6366f1",fontFamily:"'JetBrains Mono',monospace"}}>{c.DashViews}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#059669",fontFamily:"'JetBrains Mono',monospace"}}>{c.Exports}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#475569",fontFamily:"'JetBrains Mono',monospace"}}>{fmtNum(c.RowsTotal)}</td>
                <td style={{padding:"9px 14px",color:"#64748b",fontSize:10}}>{fmtPretty(c.LastActivity)}</td>
              </tr>;
            })}
            {clientsList.length === 0 && <tr><td colSpan={8} style={{padding:40,textAlign:"center",color:"#94a3b8"}}>No client activity in this period.</td></tr>}
          </tbody>
        </table>
      </div>}

      {/* Dashboards tab */}
      {subTab === "dashboards" && <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",padding:20}}>
        <div style={{fontSize:12,fontWeight:700,color:"#0f172a",marginBottom:14}}>Dashboard Popularity — views per dashboard</div>
        {(function(){
          var maxV = 1;
          dashList.forEach(function(d){ if(d.Views > maxV) maxV = d.Views; });
          return dashList.map(function(d, i){
            var pct = (d.Views / maxV) * 100;
            return <div key={i} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:11}}>
                <span style={{fontWeight:700,color:"#0f172a"}}>{d.DashboardName} <span style={{color:"#94a3b8",fontWeight:400,fontSize:9,marginLeft:6}}>{d.StudyType || "—"}</span></span>
                <span style={{color:"#64748b",fontFamily:"'JetBrains Mono',monospace"}}>{d.Views} views · {d.UniqueUsers} users · {d.UniqueClients} clients</span>
              </div>
              <div style={{height:8,background:"#f1f5f9",borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:pct+"%",background:"linear-gradient(90deg,#2563eb,#60a5fa)",borderRadius:4}}/>
              </div>
            </div>;
          });
        })()}
        {dashList.length === 0 && <div style={{padding:40,textAlign:"center",color:"#94a3b8"}}>No dashboard views in this period.</div>}
      </div>}

      {/* Queries tab */}
      {subTab === "queries" && <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
        <div style={{padding:"12px 14px",background:"#f8fafc",borderBottom:"1px solid #e2e8f0",fontSize:10.5,color:"#64748b"}}>Query patterns sorted by frequency. Patterns run by multiple users suggest opportunities for ready-made dashboards.</div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
          <thead>
            <tr style={{background:"#f8fafc",borderBottom:"1.5px solid #e2e8f0"}}>
              <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>HASH</th>
              <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>DASHBOARD</th>
              <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>STUDY</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>RUNS</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>USERS</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>CLIENTS</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>AVG ROWS</th>
              <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>AVG MS</th>
              <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>LAST RUN</th>
            </tr>
          </thead>
          <tbody>
            {queriesList.map(function(q, i){
              var isPopular = q.UniqueUsers >= 2 && q.RunCount >= 5;
              return <tr key={i} style={{borderBottom:"1px solid #f1f5f9",background:isPopular?"#f0fdf4":undefined}}>
                <td style={{padding:"9px 14px",color:"#64748b",fontFamily:"'JetBrains Mono',monospace",fontSize:9.5}}>{(q.QueryHash||"").substring(0,10)}…</td>
                <td style={{padding:"9px 14px",color:"#0f172a",fontWeight:600}}>{q.DashboardName || "pivot"}</td>
                <td style={{padding:"9px 14px",color:"#64748b"}}>{q.StudyType || "—"}</td>
                <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:isPopular?"#059669":"#0891b2",fontFamily:"'JetBrains Mono',monospace"}}>{q.RunCount}{isPopular?" ⭐":""}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#2563eb",fontFamily:"'JetBrains Mono',monospace"}}>{q.UniqueUsers}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#7c3aed",fontFamily:"'JetBrains Mono',monospace"}}>{q.UniqueClients}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:"#475569",fontFamily:"'JetBrains Mono',monospace"}}>{fmtNum(q.AvgRows)}</td>
                <td style={{padding:"9px 14px",textAlign:"right",color:q.AvgDurationMs > 5000 ? "#dc2626" : "#475569",fontFamily:"'JetBrains Mono',monospace"}}>{q.AvgDurationMs ? Math.round(q.AvgDurationMs) : "—"}</td>
                <td style={{padding:"9px 14px",color:"#64748b",fontSize:10}}>{fmtPretty(q.LastRun)}</td>
              </tr>;
            })}
            {queriesList.length === 0 && <tr><td colSpan={9} style={{padding:40,textAlign:"center",color:"#94a3b8"}}>No query patterns recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>}

      {/* Filters tab */}
      {subTab === "filters" && filtersAnalysis && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",padding:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#0f172a",marginBottom:10}}>Most Used Filter Dimensions</div>
          <div style={{fontSize:9.5,color:"#94a3b8",marginBottom:12}}>Out of {filtersAnalysis.totalQueries} queries · Top N used {filtersAnalysis.topNUsage} times ({((filtersAnalysis.topNUsage/Math.max(filtersAnalysis.totalQueries,1))*100).toFixed(0)}%)</div>
          {filtersAnalysis.filterDims.map(function(d, i){
            var pct = (d.count / Math.max(filtersAnalysis.totalQueries,1)) * 100;
            return <div key={i} style={{marginBottom:7}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:10,marginBottom:2}}><span style={{fontWeight:600,color:"#475569"}}>{d.name}</span><span style={{color:"#94a3b8",fontFamily:"'JetBrains Mono',monospace"}}>{d.count} ({pct.toFixed(0)}%)</span></div>
              <div style={{height:6,background:"#f1f5f9",borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:pct+"%",background:"#0891b2"}}/></div>
            </div>;
          })}
          {filtersAnalysis.filterDims.length === 0 && <div style={{padding:20,textAlign:"center",color:"#94a3b8",fontSize:10}}>No filter usage data yet.</div>}
        </div>
        <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",padding:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#0f172a",marginBottom:10}}>Most Used Hierarchy Dimensions</div>
          <div style={{fontSize:9.5,color:"#94a3b8",marginBottom:12}}>Dimensions users add to pivot rows</div>
          {filtersAnalysis.hierarchyDims.map(function(d, i){
            var pct = (d.count / Math.max(filtersAnalysis.totalQueries,1)) * 100;
            return <div key={i} style={{marginBottom:7}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:10,marginBottom:2}}><span style={{fontWeight:600,color:"#475569"}}>{d.name}</span><span style={{color:"#94a3b8",fontFamily:"'JetBrains Mono',monospace"}}>{d.count} ({pct.toFixed(0)}%)</span></div>
              <div style={{height:6,background:"#f1f5f9",borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:pct+"%",background:"#6366f1"}}/></div>
            </div>;
          })}
          <div style={{fontSize:11,fontWeight:700,color:"#0f172a",marginTop:18,marginBottom:8}}>Measures</div>
          {filtersAnalysis.measures.map(function(m, i){
            var pct = (m.count / Math.max(filtersAnalysis.totalQueries,1)) * 100;
            return <div key={i} style={{marginBottom:6}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:10,marginBottom:2}}><span style={{fontWeight:600,color:"#475569"}}>{m.name}</span><span style={{color:"#94a3b8",fontFamily:"'JetBrains Mono',monospace"}}>{m.count} ({pct.toFixed(0)}%)</span></div>
              <div style={{height:5,background:"#f1f5f9",borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:pct+"%",background:"#059669"}}/></div>
            </div>;
          })}
        </div>
      </div>}

      {/* ── Grouping Patterns tab — shows the dimension combinations users group
            by, e.g. "manufacturer × medicine × sku" with run counts. Helps
            admins see which drill paths users actually use, identify candidates
            for pre-aggregation, and notice if a particular user is exploring
            unusually deep combinations. Respects scope filters. ── */}
      {subTab === "grouping" && <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid #e2e8f0",background:"#f8fafc",fontSize:11,color:"#475569"}}>
          {dimCombos.length > 0
            ? <span><b>{dimCombos.length}</b> unique grouping combinations
                {(scopeClient||scopeUser) ? <span style={{color:"#2563eb"}}> · scoped</span> : <span> · across all users</span>}
                {" "}in the last <b>{days}</b> days</span>
            : <span style={{color:"#94a3b8"}}>No grouping patterns recorded for the current scope.</span>}
        </div>
        {dimCombos.length > 0 && (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <thead>
              <tr style={{background:"#f8fafc",borderBottom:"1.5px solid #e2e8f0"}}>
                <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>GROUPING COMBINATION</th>
                <th style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:"#475569",fontSize:10}}>DEPTH</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>RUNS</th>
                <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>USERS</th>
                <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>CLIENTS</th>
                <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>TOP MEASURE</th>
                <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>TOP STUDY</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>AVG ROWS</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>AVG MS</th>
              </tr>
            </thead>
            <tbody>
              {dimCombos.map(function(c, i){
                /* Color the depth chip — deeper = more orange (signals heavy queries) */
                var depthColor = c.depth >= 5 ? "#dc2626" : c.depth >= 3 ? "#d97706" : "#059669";
                /* Render top-N name list with overflow indicator: "Alice, Bob, +3 more". */
                var renderNameList = function(arr, totalCount, color) {
                  if (!arr || !arr.length) return <span style={{color:"#94a3b8"}}>—</span>;
                  var shown = arr.slice(0, 2); /* names visible in row */
                  var extra = totalCount - shown.length;
                  /* Tooltip: full list with run counts */
                  var fullList = arr.map(function(x){ return x.name + " (" + x.runs + ")"; }).join("\n");
                  return <span title={fullList} style={{cursor: arr.length > 2 ? "help" : "default"}}>
                    {shown.map(function(x, ix){
                      return <span key={ix}>
                        {ix > 0 && <span style={{color:"#cbd5e1"}}>, </span>}
                        <span style={{color:color,fontWeight:600}}>{x.name}</span>
                      </span>;
                    })}
                    {extra > 0 && <span style={{color:"#94a3b8",fontSize:9.5,marginLeft:4}}>+{extra}</span>}
                  </span>;
                };
                return <tr key={i} style={{borderBottom:"1px solid #f1f5f9"}}>
                  <td style={{padding:"10px 14px",color:"#0f172a",fontWeight:600,fontFamily:"'JetBrains Mono', monospace",fontSize:10.5}}>
                    {c.dims.map(function(d, j){
                      return <span key={j}>
                        {j > 0 && <span style={{color:"#cbd5e1",margin:"0 5px"}}>×</span>}
                        <span style={{background:"#eff6ff",color:"#1d4ed8",padding:"2px 7px",borderRadius:4,fontWeight:700}}>{d}</span>
                      </span>;
                    })}
                  </td>
                  <td style={{padding:"10px 14px",textAlign:"center"}}>
                    <span style={{background:depthColor+"15",color:depthColor,padding:"2px 8px",borderRadius:10,fontSize:9.5,fontWeight:800}}>L{c.depth}</span>
                  </td>
                  <td style={{padding:"10px 14px",textAlign:"right",fontWeight:800,color:"#0f172a",fontFamily:"'JetBrains Mono',monospace"}}>{fmtNum(c.runCount)}</td>
                  <td style={{padding:"10px 14px",fontSize:10.5}}>{renderNameList(c.topUsers, c.uniqueUsers, "#475569")}</td>
                  <td style={{padding:"10px 14px",fontSize:10.5}}>{renderNameList(c.topClients, c.uniqueClients, "#7c3aed")}</td>
                  <td style={{padding:"10px 14px",color:"#475569",fontWeight:600}}>{c.topMeasure || "—"}</td>
                  <td style={{padding:"10px 14px",color:"#475569",fontWeight:600}}>{c.topStudy || "—"}</td>
                  <td style={{padding:"10px 14px",textAlign:"right",color:"#94a3b8",fontFamily:"'JetBrains Mono',monospace"}}>{fmtNum(c.avgRows)}</td>
                  <td style={{padding:"10px 14px",textAlign:"right",color:c.avgDurationMs > 5000 ? "#dc2626" : "#94a3b8",fontFamily:"'JetBrains Mono',monospace",fontWeight:c.avgDurationMs > 5000 ? 700 : 400}}>{fmtNum(c.avgDurationMs)}</td>
                </tr>;
              })}
            </tbody>
          </table>
        )}
      </div>}

      {/* ══════ Export Tracer tab — Layer 1 provenance (ExportLog) ══════
         Every registered export: who, which client, which device, which slice,
         when — plus the server-issued Export ID embedded in the file itself.
         "Trace ID" runs an exact server-side lookup across ALL time, so an
         ID read out of a surfaced file resolves even outside the period window. */}
      {subTab === "exports" && (function(){
        var q = (exportSearch || "").trim().toLowerCase();
        var list = q ? exportsList.filter(function(e){
          return String(e.ExportId||"").toLowerCase().indexOf(q) >= 0 ||
                 String(e.UserName||"").toLowerCase().indexOf(q) >= 0 ||
                 String(e.UserEmail||"").toLowerCase().indexOf(q) >= 0 ||
                 String(e.ClientName||"").toLowerCase().indexOf(q) >= 0 ||
                 String(e.DashboardName||"").toLowerCase().indexOf(q) >= 0 ||
                 String(e.ExportType||"").toLowerCase().indexOf(q) >= 0 ||
                 String(e.DeviceId||"").toLowerCase().indexOf(q) >= 0 ||
                 String(e.IpAddress||"").toLowerCase().indexOf(q) >= 0;
        }) : exportsList;
        var typeChip = function(t){
          var m = { pivot:{bg:"#eff6ff",co:"#1d4ed8"}, grid:{bg:"#ecfeff",co:"#0e7490"}, report:{bg:"#f5f3ff",co:"#6d28d9"}, dashboard:{bg:"#f1f5f9",co:"#475569"}, chart:{bg:"#f1f5f9",co:"#64748b"} };
          var c = m[t] || {bg:"#f1f5f9",co:"#64748b"};
          return <span style={{background:c.bg,color:c.co,padding:"2px 8px",borderRadius:10,fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".04em"}}>{t || "?"}</span>;
        };
        var fmtPayload = function(p){ if (!p) return "—"; try { return JSON.stringify(JSON.parse(p), null, 2); } catch(e){ return String(p); } };
        var traceExact = function(){
          var id = (exportSearch || "").trim();
          if (!id) return;
          setTraceBusy(true);
          api.getExportLog({ exportId: id, days: 3650, limit: 50 })
            .then(function(r){ setExportsList((r && r.exports) || []); setExpandedExport(null); setTraceBusy(false); })
            .catch(function(){ setTraceBusy(false); });
        };
        var copyId = function(id){ try { navigator.clipboard.writeText(id); } catch(e){} };
        /* ── Excel Marker Checker: upload a suspect .xlsx, extract the structural
           marker (custom props → hidden _AMS_Metadata sheet → doc subject) and
           auto-trace its ExportId against the ExportLog. ── */
        var checkFileMarker = function(ev){
          var f = ev.target.files && ev.target.files[0];
          try { ev.target.value = ""; } catch(e2){}
          if (!f) return;
          var _ext9 = (String(f.name || "").split(".").pop() || "").toLowerCase();
          if (_ext9 !== "xlsx" && _ext9 !== "xlsm") {
            /* r83: reports, PDFs and images — metadata / zero-width text / dot-code */
            setFileMarker({ busy: true, fileName: f.name });
            _wmCheckReport(f).then(function(r){
              setFileMarker(r);
              var q = null;
              if (r.exportId) q = { exportId: r.exportId, days: 3650, limit: 50 };
              else if (r.dots && r.dots.ok && r.dots.kind === "seq") q = { seq: r.dots.value, days: 3650, limit: 50 };
              else if (r.dots && r.dots.ok && r.dots.kind === "user") q = { userId: r.dots.value, days: 3650, limit: 30 };
              if (q) {
                if (q.exportId) setExportSearch(q.exportId);
                setTraceBusy(true);
                api.getExportLog(q).then(function(r2){ setExportsList((r2 && r2.exports) || []); setExpandedExport(null); setTraceBusy(false); }).catch(function(){ setTraceBusy(false); });
              }
            }).catch(function(e9){ setFileMarker({ none:true, fileName:f.name, error:(e9 && e9.message) || "Could not read file" }); });
            return;
          }
          getXLSX().then(function(XLSX){
            var reader = new FileReader();
            reader.onload = function(e3){
              try {
                var wb = XLSX.read(new Uint8Array(e3.target.result), {type:"array"});
                var mk = null;
                var cp = wb.Custprops || {};
                if (cp.AMS_ExportID) mk = { exportId:String(cp.AMS_ExportID), clientToken:String(cp.AMS_ClientToken||""), userId:String(cp.AMS_UserID||""), studyId:String(cp.AMS_StudyID||""), generatedAt:String(cp.AMS_GeneratedAt||""), version:String(cp.AMS_Version||""), source:"custom properties" };
                if (!mk && wb.Sheets && wb.Sheets["_AMS_Metadata"]) {
                  var ms = wb.Sheets["_AMS_Metadata"];
                  var gv = function(ref){ return ms[ref] != null && ms[ref].v != null ? String(ms[ref].v) : ""; };
                  if (gv("B1")) mk = { exportId:gv("B1"), clientToken:gv("B2"), userId:gv("B3"), studyId:gv("B4"), generatedAt:gv("B5"), version:gv("B6"), source:"hidden metadata sheet" };
                }
                if (!mk && wb.Props && wb.Props.Subject) {
                  var m9 = /AMSX-[0-9A-F]+/i.exec(String(wb.Props.Subject));
                  if (m9) mk = { exportId:m9[0], source:"document subject" };
                }
                setFileMarker(mk ? Object.assign({fileName:f.name}, mk) : { none:true, fileName:f.name });
                if (mk && mk.exportId) {
                  setExportSearch(mk.exportId);
                  setTraceBusy(true);
                  api.getExportLog({ exportId: mk.exportId, days: 3650, limit: 50 })
                    .then(function(r2){ setExportsList((r2 && r2.exports) || []); setExpandedExport(null); setTraceBusy(false); })
                    .catch(function(){ setTraceBusy(false); });
                }
              } catch(e4){ setFileMarker({ none:true, fileName:f.name, error:(e4 && e4.message) || "Could not read file" }); }
            };
            reader.readAsArrayBuffer(f);
          }).catch(function(){ setFileMarker({ none:true, fileName:f.name, error:"Excel library failed to load" }); });
        };
        return <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:"1px solid #e2e8f0",background:"#f8fafc",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontSize:11,color:"#475569"}}>
              <b>{list.length}</b> registered exports
              {(scopeClient||scopeUser) ? <span style={{color:"#2563eb"}}> · scoped</span> : null}
              {" "}in the last <b>{days}</b> days · each file carries its Export ID in the footer &amp; document properties
            </span>
            <div style={{flex:1}} />
            <input value={exportSearch} onChange={function(e){ setExportSearch(e.target.value); }}
              placeholder="Filter — or paste an AMSX-… Export ID from a file"
              style={{padding:"6px 12px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10.5,fontFamily:"'JetBrains Mono',monospace",minWidth:300,outline:"none"}} />
            <button onClick={traceExact} disabled={traceBusy || !(exportSearch||"").trim()}
              title="Exact server-side lookup by Export ID across ALL time (ignores the period window)"
              style={{padding:"6px 14px",border:"none",borderRadius:6,background:traceBusy?"#94a3b8":"#2563eb",color:"#fff",fontSize:10.5,fontWeight:800,cursor:traceBusy?"wait":"pointer",fontFamily:"inherit"}}>
              {traceBusy ? "Tracing…" : "🔎 Trace ID"}
            </button>
            <label title="Upload a suspect file — Excel: structural marker · PPTX / PDF / PNG / JPG (even a screenshot): Export ID from metadata or hidden text, or the invisible dot-code on the image"
              style={{padding:"6px 14px",border:"1.5px solid #7c3aed",borderRadius:6,background:"#f5f3ff",color:"#6d28d9",fontSize:10.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
              🧾 Check File
              <input type="file" accept=".xlsx,.xlsm,.pptx,.pdf,.png,.jpg,.jpeg,.webp" style={{display:"none"}} onChange={checkFileMarker} />
            </label>
          </div>
          {fileMarker && (
            <div style={{padding:"10px 16px",borderBottom:"1px solid #e2e8f0",background:fileMarker.busy?"#eff6ff":fileMarker.none?"#fffbeb":"#ecfdf5"}}>
              {fileMarker.busy ? (
                <div style={{fontSize:10.5,color:"#1e40af"}}><b>⏳ Checking “{fileMarker.fileName}”</b> — reading metadata, hidden text and the dot-code on the images…</div>
              ) : fileMarker.dots !== undefined ? (
                <div style={{fontSize:10.5,color:fileMarker.none?"#92400e":"#065f46"}}>
                  <b>{fileMarker.none ? "⚠ No AMSX mark recovered from" : "✓ Mark recovered from"} “{fileMarker.fileName}”</b>
                  {fileMarker.exportId ? <span> — Export ID <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>{fileMarker.exportId}</span> <span style={{color:"#64748b"}}>(via {fileMarker.source})</span></span> : null}
                  {fileMarker.dots && fileMarker.dots.ok ? <span> · dot-code <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{fileMarker.dots.hex}</span> = {fileMarker.dots.kind === "seq" ? "export record #" + fileMarker.dots.value : "user #" + fileMarker.dots.value} · confidence {Math.round(fileMarker.dots.confidence * 100)}% · {fileMarker.dots.tiles} tiles · pitch {fileMarker.dots.pitch.toFixed(1)} px</span>
                    : <span style={{color:"#64748b"}}> · dot-code: {(fileMarker.dots && fileMarker.dots.reason) || "not found"}{fileMarker.images != null ? " (" + fileMarker.images + " images)" : ""}{fileMarker.pages != null ? " (" + fileMarker.pages + " pages)" : ""}</span>}
                  {fileMarker.author ? <span> · author “{fileMarker.author}”</span> : null}
                  {fileMarker.none ? <span> — the marks may have been cropped out or heavily re-compressed; try a larger / original screenshot, or another page.</span> : <span style={{color:"#64748b"}}> — resolved against the Export Log below.</span>}
                </div>
              ) : fileMarker.none ? (
                <div style={{fontSize:10.5,color:"#92400e"}}>
                  <b>⚠ No structural marker found in “{fileMarker.fileName}”</b>{fileMarker.error ? " — " + fileMarker.error : ""}.
                  {" "}The marker may have been stripped — check the file's unit values with the <b>Data Fingerprint</b> tool (Layer 2), which survives copy-paste.
                </div>
              ) : (
                <div style={{fontSize:10.5,color:"#065f46"}}>
                  <b>✓ Marker found in “{fileMarker.fileName}”</b> <span style={{color:"#64748b"}}>(via {fileMarker.source})</span> —
                  {" "}<span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>{fileMarker.exportId}</span>
                  {fileMarker.clientToken ? <span> · ClientToken <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{fileMarker.clientToken}</span></span> : null}
                  {fileMarker.userId ? <span> · User #{fileMarker.userId}</span> : null}
                  {fileMarker.studyId ? <span> · {fileMarker.studyId}</span> : null}
                  {fileMarker.generatedAt ? <span> · {fileMarker.generatedAt}</span> : null}
                  <span style={{color:"#64748b"}}> — resolved against the Export Log below.</span>
                </div>
              )}
            </div>
          )}
          {list.length === 0 ? (
            <div style={{padding:"28px 20px",textAlign:"center",color:"#94a3b8",fontSize:11}}>
              No registered exports {q ? "match this filter" : "for the current scope/period"}.
              {q && /^amsx-/.test(q) ? <span> Use <b>Trace ID</b> to search the full log beyond the period window.</span> : null}
            </div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:1100}}>
                <thead>
                  <tr style={{background:"#f8fafc",borderBottom:"1.5px solid #e2e8f0"}}>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>TIME</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>EXPORT ID</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>CLIENT</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>USER</th>
                    <th style={{padding:"10px 12px",textAlign:"center",fontWeight:700,color:"#475569",fontSize:10}}>TYPE</th>
                    <th style={{padding:"10px 12px",textAlign:"center",fontWeight:700,color:"#475569",fontSize:10}}>FORMAT</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>REPORT / DASHBOARD</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>STUDY</th>
                    <th style={{padding:"10px 12px",textAlign:"right",fontWeight:700,color:"#475569",fontSize:10}}>ROWS</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>DEVICE</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(function(e){
                    var rid = e.ExportRecordId;
                    var open = expandedExport === rid;
                    var devShort = e.DeviceId ? (String(e.DeviceId).length > 14 ? String(e.DeviceId).slice(0, 14) + "…" : e.DeviceId) : "—";
                    var rows = [
                      <tr key={rid} onClick={function(){ setExpandedExport(open ? null : rid); }}
                        style={{borderBottom:"1px solid #f1f5f9",cursor:"pointer",background:open?"#f8fafc":undefined}}>
                        <td style={{padding:"9px 12px",whiteSpace:"nowrap",color:"#475569"}} title={fmtDate(e.CreatedAt)}>{fmtAmman(e.CreatedAt)}</td>
                        <td style={{padding:"9px 12px",fontFamily:"'JetBrains Mono',monospace",fontSize:9.5,color:"#1d4ed8",fontWeight:700,whiteSpace:"nowrap"}} title="Click row for details · double-click to copy"
                          onDoubleClick={function(ev){ ev.stopPropagation(); copyId(e.ExportId); }}>{e.ExportId}</td>
                        <td style={{padding:"9px 12px",color:"#7c3aed",fontWeight:700,whiteSpace:"nowrap"}}>{e.ClientName || (e.ClientId != null ? ("Client #"+e.ClientId) : "—")}</td>
                        <td style={{padding:"9px 12px",color:"#0f172a",fontWeight:600,whiteSpace:"nowrap"}} title={e.UserEmail || ""}>{e.UserName || e.UserEmail || ("User #"+e.UserId)}</td>
                        <td style={{padding:"9px 12px",textAlign:"center"}}>{typeChip(e.ExportType)}</td>
                        <td style={{padding:"9px 12px",textAlign:"center",color:"#475569",fontWeight:700,fontSize:9.5,textTransform:"uppercase"}}>{e.ExportFormat || "—"}</td>
                        <td style={{padding:"9px 12px",color:"#475569",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={e.DashboardName || ""}>{e.DashboardName || "—"}</td>
                        <td style={{padding:"9px 12px",color:"#475569",whiteSpace:"nowrap"}}>{e.StudyType || "—"}</td>
                        <td style={{padding:"9px 12px",textAlign:"right",fontFamily:"'JetBrains Mono',monospace",color:"#94a3b8"}}>{e.RowsExported != null ? fmtNum(e.RowsExported) : "—"}</td>
                        <td style={{padding:"9px 12px",fontFamily:"'JetBrains Mono',monospace",fontSize:9.5,color:"#64748b",whiteSpace:"nowrap"}} title={e.DeviceId || ""}>{devShort}</td>
                        <td style={{padding:"9px 12px",fontFamily:"'JetBrains Mono',monospace",fontSize:9.5,color:"#64748b",whiteSpace:"nowrap"}}>{e.IpAddress || "—"}</td>
                      </tr>
                    ];
                    if (open) rows.push(
                      <tr key={rid + "_d"} style={{borderBottom:"1px solid #e2e8f0",background:"#f8fafc"}}>
                        <td colSpan={11} style={{padding:"12px 16px"}}>
                          <div style={{display:"flex",gap:24,flexWrap:"wrap",marginBottom:10,fontSize:10}}>
                            <span><b style={{color:"#475569"}}>Export ID:</b> <span style={{fontFamily:"'JetBrains Mono',monospace",color:"#1d4ed8",fontWeight:700}}>{e.ExportId}</span>
                              <button onClick={function(){ copyId(e.ExportId); }} style={{marginLeft:6,padding:"1px 8px",border:"1px solid #cbd5e1",borderRadius:4,background:"#fff",color:"#475569",fontSize:9,cursor:"pointer",fontFamily:"inherit"}}>Copy</button></span>
                            <span><b style={{color:"#475569"}}>Session:</b> <span style={{fontFamily:"'JetBrains Mono',monospace",color:"#64748b"}}>{e.SessionSid ? String(e.SessionSid).slice(0,12) + "…" : "—"}</span></span>
                            <span><b style={{color:"#475569"}}>Device:</b> <span style={{fontFamily:"'JetBrains Mono',monospace",color:"#64748b"}}>{e.DeviceId || "—"}</span></span>
                            <span><b style={{color:"#475569"}}>Query hash:</b> <span style={{fontFamily:"'JetBrains Mono',monospace",color:"#64748b"}}>{e.QueryHash ? String(e.QueryHash).slice(0,16) + "…" : "—"}</span></span>
                          </div>
                          <div style={{fontSize:9.5,fontWeight:800,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}}>Exact exported slice</div>
                          <pre style={{margin:0,padding:"10px 12px",background:"#0f172a",color:"#e2e8f0",borderRadius:8,fontSize:9.5,fontFamily:"'JetBrains Mono',monospace",maxHeight:260,overflow:"auto",whiteSpace:"pre-wrap"}}>{fmtPayload(e.QueryPayload)}</pre>
                          {e.UserAgent ? <div style={{marginTop:8,fontSize:9,color:"#94a3b8"}}><b>Browser:</b> {e.UserAgent}</div> : null}
                        </td>
                      </tr>
                    );
                    return rows;
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>;
      })()}

      {/* ══════ Data Fingerprint tab — Layer 2 (per-client units-delta) ══════ */}
      {subTab === "fingerprint" && (function(){
        var toggleFp = function(c){
          var next = !c.FingerprintEnabled;
          var _who = (c.FullName || c.Email || ("User #" + c.UserId)) + (c.ClientName ? " — " + c.ClientName : "");
          if (next && !window.confirm("Enable data fingerprint for \"" + _who + "\"?\n\nThis USER's responses get a rare ±1-unit delta on one low-priced SKU per manufacturer (roughly one month in six; unit price exactly unchanged, market totals exactly original). The database itself is never modified.")) return;
          api.setUserFingerprint(c.UserId, next).then(function(){
            setFpClients(fpClients.map(function(x){ return x.UserId === c.UserId ? Object.assign({}, x, {FingerprintEnabled: next ? 1 : 0, HasSeed: 1}) : x; }));
          }).catch(function(e){ try { alert("Failed: " + (e && e.message)); } catch(_e){} });
        };
        var fpResBlock = function(res){
          return <div>
                      <div style={{padding:"10px 14px",borderRadius:8,marginBottom:10,
                        background: res.verdict ? "#ecfdf5" : "#f8fafc",
                        border: "1.5px solid " + (res.verdict ? "#10b981" : "#e2e8f0")}}>
                        <div style={{fontSize:12,fontWeight:800,color:res.verdict?"#065f46":"#475569"}}>
                          {res.verdict ? ("\u26A0 Match: data fingerprint points to \u201C" + res.verdict + "\u201D") : "No conclusive fingerprint match in the provided rows"}
                        </div>
                        <div style={{fontSize:9.5,color:"#64748b",marginTop:2}}>
                          {res.rowsChecked} rows checked against {(res.results||[]).length} seeded user(s)
                          {!res.verdict ? " \u2014 try more rows from the candidate list (step 2), across more manufacturers and periods" : ""}
                        </div>
                      </div>
                      {(function(){
                        /* Rank: fingerprint matches first, then evidence rows, then clean matches.
                           With a huge user list only the top 10 show; the rest collapse. */
                        var _all = (res.results||[]).slice().sort(function(a,b){
                          return (b.exactMatches - a.exactMatches) || (b.markedRowsSeen - a.markedRowsSeen) || (b.cleanMatches - a.cleanMatches);
                        });
                        var _top = _all.length > 10 ? _all.slice(0, 10) : _all;
                        var _rest = _all.length > 10 ? _all.slice(10) : [];
                        var _mkRow = function(rr){
                          var strong = res.verdict && rr.clientName === res.verdict;
                          return <tr key={rr.clientId} style={{borderBottom:"1px solid #f1f5f9",background:strong?"#ecfdf5":undefined}}>
                            <td style={{padding:"7px 10px",fontWeight:strong?800:600,color:strong?"#065f46":"#0f172a"}}>{rr.clientName}{rr.enabled ? "" : " (disabled)"}</td>
                            <td style={{padding:"7px 10px",textAlign:"center",color:"#475569",width:110}}>{rr.markedRowsSeen}</td>
                            <td style={{padding:"7px 10px",textAlign:"center",fontWeight:800,color:rr.exactMatches>0?"#059669":"#94a3b8",width:150}}>{rr.exactMatches}</td>
                            <td style={{padding:"7px 10px",textAlign:"center",color:"#94a3b8",width:150}}>{rr.cleanMatches}</td>
                          </tr>;
                        };
                        return <div>
                          <table style={{width:"100%",borderCollapse:"collapse",fontSize:10.5}}>
                            <thead><tr style={{background:"#f8fafc",borderBottom:"1.5px solid #e2e8f0"}}>
                              <th style={{padding:"7px 10px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:9.5}}>USER (CLIENT)</th>
                              <th style={{padding:"7px 10px",textAlign:"center",fontWeight:700,color:"#475569",fontSize:9.5,width:110}}>EVIDENCE ROWS</th>
                              <th style={{padding:"7px 10px",textAlign:"center",fontWeight:700,color:"#475569",fontSize:9.5,width:150}}>FINGERPRINT MATCHES</th>
                              <th style={{padding:"7px 10px",textAlign:"center",fontWeight:700,color:"#475569",fontSize:9.5,width:150}}>CLEAN-VALUE MATCHES</th>
                            </tr></thead>
                            <tbody>{_top.map(_mkRow)}</tbody>
                          </table>
                          {_rest.length > 0 && (
                            <details style={{marginTop:4}}>
                              <summary style={{cursor:"pointer",fontSize:9.5,color:"#64748b",fontWeight:700,padding:"4px 2px"}}>
                                {"show " + _rest.length + " more user(s) with less evidence"}
                              </summary>
                              <table style={{width:"100%",borderCollapse:"collapse",fontSize:10.5}}>
                                <tbody>{_rest.map(_mkRow)}</tbody>
                              </table>
                            </details>
                          )}
                        </div>;
                      })()}
                      {/* Row-by-row diagnostics: WHY each row did / didn't count, per user */}
                      {res.detail && res.detail.length > 0 && (
                        <div style={{marginTop:12}}>
                          <div style={{fontSize:10,fontWeight:800,color:"#475569",textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>Row diagnostics</div>
                          {res.detail.map(function(dt, di){
                            return <div key={di} style={{border:"1px solid #e2e8f0",borderRadius:8,marginBottom:8,overflow:"hidden"}}>
                              <div style={{padding:"6px 12px",background:"#f8fafc",borderBottom:"1px solid #e2e8f0",fontSize:9.5,display:"flex",gap:10,flexWrap:"wrap",alignItems:"baseline"}}>
                                <span style={{color:"#7c3aed",fontWeight:800}}>{dt.mf}{dt.product ? " \u203A " + dt.product : ""}</span>
                                <span style={{fontFamily:"'JetBrains Mono',monospace",color:"#0f172a"}}>{dt.sku}</span>
                                <span style={{color:"#2563eb",fontWeight:700}}>{dt.period}</span>
                                <span style={{color:"#475569"}}>suspect: <b style={{fontFamily:"'JetBrains Mono',monospace"}}>{Number(dt.units).toLocaleString()}</b></span>
                                <span style={{color:dt.trueUnits>0?"#475569":"#dc2626"}}>DB clean: <b style={{fontFamily:"'JetBrains Mono',monospace"}}>{Number(dt.trueUnits).toLocaleString()}</b></span>
                              </div>
                              <div style={{padding:"6px 12px",display:"flex",flexDirection:"column",gap:3}}>
                                {(function(){
                                  var _chip = function(st, key){
                                    var isHit = st.indexOf("FINGERPRINT") === 0;
                                    var isClean = st.indexOf("clean-value") === 0;
                                    var isBad = st.indexOf("no-db-row") === 0 || st.indexOf("mismatch") === 0;
                                    var co = isHit ? "#059669" : isClean ? "#2563eb" : isBad ? "#dc2626" : "#94a3b8";
                                    var bg = isHit ? "#ecfdf5" : isClean ? "#eff6ff" : isBad ? "#fef2f2" : "#f8fafc";
                                    return <span key={key} style={{background:bg,color:co,padding:"1px 8px",borderRadius:8,fontWeight:700}}>{st}</span>;
                                  };
                                  var _line = function(name, st, key){
                                    return <div key={key} style={{display:"flex",gap:8,alignItems:"baseline",fontSize:9.5}}>
                                      <span style={{fontWeight:700,color:"#0f172a",minWidth:150}}>{name}</span>
                                      {_chip(st)}
                                    </div>;
                                  };
                                  /* Important statuses stay one line per user; repeated quiet
                                     statuses (not-marked, no-delta, ...) collapse into one group. */
                                  var sig = [], quietFam = {}, famOrder = [];
                                  (dt.perUser||[]).forEach(function(pu){
                                    var st = String(pu.status||"");
                                    var important = st.indexOf("FINGERPRINT") === 0 || st.indexOf("clean-value") === 0 ||
                                                    st.indexOf("mismatch") === 0 || st.indexOf("no-db-row") === 0 || st.indexOf("capped") === 0;
                                    if (important) { sig.push({name: pu.name, st: st}); return; }
                                    var fam = st.split(" (")[0];
                                    if (!quietFam[fam]) { quietFam[fam] = []; famOrder.push(fam); }
                                    quietFam[fam].push({name: pu.name, st: st});
                                  });
                                  var out = sig.map(function(x, i){ return _line(x.name, x.st, "s" + i); });
                                  famOrder.forEach(function(fam, fi){
                                    var lst = quietFam[fam];
                                    if (lst.length === 1) { out.push(_line(lst[0].name, lst[0].st, "f" + fi)); return; }
                                    var same = lst.every(function(x){ return x.st === lst[0].st; });
                                    out.push(
                                      <details key={"f" + fi} style={{fontSize:9.5}}>
                                        <summary style={{cursor:"pointer",display:"flex",gap:8,alignItems:"baseline"}}>
                                          <span style={{fontWeight:700,color:"#64748b",minWidth:150}}>{lst.length + " user(s) \u25BE"}</span>
                                          {_chip(same ? lst[0].st : fam + " \u2014 open for per-user detail")}
                                        </summary>
                                        <div style={{padding:"3px 0 3px 14px",display:"flex",flexDirection:"column",gap:2}}>
                                          {lst.map(function(x, xi){ return _line(x.name, x.st, "f" + fi + "_" + xi); })}
                                        </div>
                                      </details>
                                    );
                                  });
                                  return out;
                                })()}
                              </div>
                            </div>;
                          })}
                        </div>
                      )}
          </div>;
        };
        var fpTemplateDl = function(){
          getXLSX().then(function(XLSX){
            var y0 = new Date().getFullYear();
            var hdr = ["Manufacturer", "Product", "SKU", String(y0), String(y0 - 1), String(y0 - 2), String(y0 - 3)];
            var ws = XLSX.utils.aoa_to_sheet([hdr]);
            ws["!cols"] = [{wch:24},{wch:22},{wch:28},{wch:12},{wch:12},{wch:12},{wch:12}];
            var hint = XLSX.utils.aoa_to_sheet([
              ["How to fill the AMSX fingerprint-check template"],
              [""],
              ["Same layout as an exported grid: one row per SKU, one column per period."],
              ["Manufacturer + Product + SKU: copy them exactly as they appear in the suspect data."],
              ["Period columns: the header tells the period type \u2014"],
              ["   \u2022 \u201C2026\u201D = full year"],
              ["   \u2022 \u201CQ1 2026\u201D or \u201C2026-Q1\u201D = quarter"],
              ["   \u2022 \u201CJan 2026\u201D or \u201C2026-01\u201D = month"],
              ["Add / remove / rename period columns freely \u2014 each recognized column is checked."],
              ["Cells: the units values from the suspect file (decimals and thousands separators allowed; empty cells are ignored)."],
              [""],
              ["The classic long layout (columns SKU, Year, Quarter, Month, Units) is also accepted."],
              ["Best evidence: rows for the candidate SKUs from step 2, across several manufacturers."],
            ]);
            hint["!cols"] = [{wch:100}];
            var wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Data");
            XLSX.utils.book_append_sheet(wb, hint, "How to fill");
            XLSX.writeFile(wb, "AMSX_fingerprint_check_template.xlsx");
          });
        };
        /* Parse a period column header: "2026" | "Q1 2026" | "2026-Q1" | "Jan 2026" | "2026-01" */
        var fpParsePeriodHdr = function(h){
          var t = String(h == null ? "" : h).trim();
          if (!t) return null;
          if (/^\d{4}$/.test(t)) return { year: t, quarter: null, month: null };
          var m = t.match(/^[Qq]\s*([1-4])[\s\-\/]*(\d{4})$/) || t.match(/^(\d{4})[\s\-\/]*[Qq]\s*([1-4])$/);
          if (m) {
            return m[1].length === 4 ? { year: m[1], quarter: m[2], month: null } : { year: m[2], quarter: m[1], month: null };
          }
          var low = t.toLowerCase();
          var MN = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
          m = low.match(/^([a-z]{3,9})[\s\-\/]*(\d{4})$/) || low.match(/^(\d{4})[\s\-\/]*([a-z]{3,9})$/);
          if (m) {
            var name = /^\d{4}$/.test(m[1]) ? m[2] : m[1];
            var yy = /^\d{4}$/.test(m[1]) ? m[1] : m[2];
            for (var mi = 0; mi < 12; mi++) { if (name.indexOf(MN[mi]) === 0) return { year: yy, quarter: null, month: String(mi + 1) }; }
            return null;
          }
          m = t.match(/^(\d{4})[\s\-\/](\d{1,2})$/) || t.match(/^(\d{1,2})[\s\-\/](\d{4})$/);
          if (m) {
            var y2 = m[1].length === 4 ? m[1] : m[2];
            var mo = parseInt(m[1].length === 4 ? m[2] : m[1], 10);
            if (mo >= 1 && mo <= 12) return { year: y2, quarter: null, month: String(mo) };
          }
          return null;
        };
        var fpFileUpload = function(ev){
          var f = ev.target && ev.target.files && ev.target.files[0];
          if (ev.target) ev.target.value = "";
          if (!f) return;
          setFpUpMsg("Reading \u201C" + f.name + "\u201D\u2026");
          var rd = new FileReader();
          rd.onerror = function(){ setFpUpMsg("\u26A0 Could not read the file."); };
          rd.onload = function(e2){
            getXLSX().then(function(XLSX){
              try {
                var wb = XLSX.read(e2.target.result, {type:"array"});
                var ws = wb.Sheets[wb.SheetNames[0]];
                if (!ws) { setFpUpMsg("\u26A0 The file has no sheets."); return; }
                var aoa = XLSX.utils.sheet_to_json(ws, {header:1, raw:false, defval:""});
                /* find the header row: needs a SKU column plus either a Units column
                   (long layout) or at least one period column like 2026 / Q1 2026 (wide layout) */
                var hIdx = -1, cols = null, periodCols = null;
                for (var ri = 0; ri < Math.min(aoa.length, 15); ri++) {
                  var rawRow = aoa[ri] || [];
                  var low = rawRow.map(function(c){ return String(c == null ? "" : c).trim().toLowerCase(); });
                  var skuAt = -1;
                  low.forEach(function(c, ci){ if (skuAt === -1 && c.indexOf("sku") !== -1) skuAt = ci; });
                  if (skuAt === -1) continue;
                  var c9 = { mf:-1, product:-1, sku:skuAt, year:-1, quarter:-1, month:-1, units:-1 };
                  var pc9 = [];
                  low.forEach(function(c, ci){
                    if (ci === skuAt) return;
                    if (c9.mf === -1 && c.indexOf("manufact") !== -1) { c9.mf = ci; return; }
                    if (c9.product === -1 && (c.indexOf("product") !== -1 || c.indexOf("medicine") !== -1 || c.indexOf("brand") !== -1)) { c9.product = ci; return; }
                    if (c9.year === -1 && c === "year") { c9.year = ci; return; }
                    if (c9.quarter === -1 && (c === "quarter" || c === "qtr")) { c9.quarter = ci; return; }
                    if (c9.month === -1 && c === "month") { c9.month = ci; return; }
                    if (c9.units === -1 && c.indexOf("unit") !== -1) { c9.units = ci; return; }
                    var p9 = fpParsePeriodHdr(rawRow[ci]);
                    if (p9) pc9.push({ ci: ci, p: p9 });
                  });
                  if (c9.units !== -1 && c9.year !== -1) { hIdx = ri; cols = c9; periodCols = null; break; }
                  if (pc9.length > 0) { hIdx = ri; cols = c9; periodCols = pc9; break; }
                }
                if (hIdx === -1) {
                  setFpUpMsg("\u26A0 Header row not found \u2014 the first sheet needs a SKU column plus period columns (2026, Q1 2026, Jan 2026\u2026) or Year+Units columns. Use \u201CDownload template\u201D."); return;
                }
                var _tOk = function(pp){
                  if (fpUpPType === "year") return !pp.quarter && !pp.month;
                  if (fpUpPType === "quarter") return !!pp.quarter;
                  if (fpUpPType === "month") return !!pp.month;
                  return true;
                };
                if (periodCols) {
                  var pcAll = periodCols.length;
                  periodCols = periodCols.filter(function(pc){ return _tOk(pc.p); });
                  if (!periodCols.length) { setFpUpMsg("\u26A0 The file has " + pcAll + " period column(s), but none match the selected period type \u2014 set the type filter to \u201CAll period types\u201D or adjust the headers."); return; }
                }
                var typeSkipped = 0;
                var _s = function(v){ return String(v == null ? "" : v).trim(); };
                var _dig = function(v){ return _s(v).replace(/[^0-9]/g, ""); };
                var _num = function(v){
                  var t9 = _s(v);
                  if (!/[0-9]/.test(t9)) return null;
                  var n9 = Number(t9.replace(/[^0-9.\-]/g, ""));
                  return isNaN(n9) ? null : n9;
                };
                var parsed = [], skipped = 0;
                for (var r2 = hIdx + 1; r2 < aoa.length; r2++) {
                  var row = aoa[r2] || [];
                  var sku = _s(row[cols.sku]);
                  var mf9 = cols.mf !== -1 ? (_s(row[cols.mf]) || null) : null;
                  var pd9 = cols.product !== -1 ? (_s(row[cols.product]) || null) : null;
                  if (periodCols) {
                    if (!sku) continue;
                    periodCols.forEach(function(pc){
                      var u9 = _num(row[pc.ci]);
                      if (u9 == null) return;   /* empty / non-numeric cell: ignored */
                      parsed.push({ sku: sku, manufacturer: mf9, product: pd9,
                                    year: pc.p.year, quarter: pc.p.quarter, month: pc.p.month, units: u9 });
                    });
                  } else {
                    var yr = _dig(row[cols.year]);
                    var u8 = _num(row[cols.units]);
                    if (!sku && !yr && u8 == null) continue;
                    if (!sku || !/^\d{4}$/.test(yr) || u8 == null) { skipped++; continue; }
                    var q8 = cols.quarter !== -1 ? _dig(row[cols.quarter]) : "";
                    var m8 = cols.month !== -1 ? _dig(row[cols.month]) : "";
                    if (!_tOk({ quarter: q8 || null, month: m8 || null })) { typeSkipped++; continue; }
                    parsed.push({ sku: sku, manufacturer: mf9, product: pd9,
                                  year: yr, quarter: q8 || null, month: m8 || null, units: u8 });
                  }
                }
                if (!parsed.length) { setFpUpMsg("\u26A0 No valid data rows found in \u201C" + f.name + "\u201D" + (skipped ? " (" + skipped + " line(s) skipped)" : "") + (typeSkipped ? " \u2014 " + typeSkipped + " row(s) excluded by the period-type filter" : "") + "."); return; }
                /* Candidate-SKU rows are the only ones that can carry evidence \u2014
                   put them first so the 300-row cap never cuts them off */
                if (fpCandidates && fpCandidates.length) {
                  var candSet = {};
                  fpCandidates.forEach(function(cd){
                    (cd.skus || []).forEach(function(s9){
                      candSet[String(s9.sku || "").replace(/\s+/g, " ").trim().toLowerCase()] = 1;
                    });
                  });
                  var hot = [], cold = [];
                  parsed.forEach(function(p8){
                    if (candSet[String(p8.sku).replace(/\s+/g, " ").trim().toLowerCase()]) hot.push(p8); else cold.push(p8);
                  });
                  parsed = hot.concat(cold);
                }
                var room = 300 - fpRows.length;
                var take = room > 0 ? parsed.slice(0, room) : [];
                setFpRows(fpRows.concat(take));
                setFpRowsOpen(false);   /* keep the list collapsed after an upload */
                setFpUpMsg("\u2713 Loaded " + take.length + " row(s) from \u201C" + f.name + "\u201D" +
                  (skipped ? " \u2014 " + skipped + " line(s) skipped" : "") +
                  (typeSkipped ? " \u2014 " + typeSkipped + " row(s) outside the selected period type ignored" : "") +
                  (parsed.length > take.length ? " \u2014 capped at 300 rows (candidate SKUs kept first)" : "") +
                  " \u2014 press Run Check (expand the list above to review).");
              } catch (err) { setFpUpMsg("\u26A0 Could not parse the file: " + ((err && err.message) || err)); }
            });
          };
          rd.readAsArrayBuffer(f);
        };
        var bulkFp = function(ids, enable){
          if (!ids || !ids.length) return;
          var msg = enable
            ? ("Enable data fingerprint for ALL " + ids.length + " listed user(s)?\n\nEach user gets (or keeps) a permanent personal seed. Rare \u00B11-unit deltas, totals exactly original, admins always clean, database never modified.")
            : ("Disable data fingerprint for " + ids.length + " listed user(s)?\n\nSeeds are kept, so re-enabling later restores the exact same fingerprints.");
          if (!window.confirm(msg)) return;
          api.bulkFingerprint(enable, ids).then(function(r){
            setFpClients(fpClients.map(function(x){
              return ids.indexOf(x.UserId) >= 0
                ? Object.assign({}, x, { FingerprintEnabled: enable ? 1 : 0, HasSeed: enable ? 1 : x.HasSeed })
                : x;
            }));
          }).catch(function(e){ try { alert("Bulk update failed: " + (e && e.message)); } catch(_e){} });
        };
        var runCheck = function(){
          var st = (fpStudy || "").trim().toLowerCase();
          if (!st) { setFpResult({error:"Enter a study code (e.g. iraq, jordan, libya)"}); return; }
          /* Structured rows from the builder come first; bulk-paste lines are added on top */
          var rows = (fpRows || []).map(function(r9){
            return { sku: r9.sku, manufacturer: r9.manufacturer || null, product: r9.product || null,
                     year: r9.year, quarter: r9.quarter || null, month: r9.month || null, units: Number(r9.units) };
          });
          (fpRowsText || "").split("\n").forEach(function(line){
            var t = line.trim(); if (!t) return;
            var parts = t.split(",");
            if (parts.length < 5) return;
            /* last 4 fields = Year, Quarter, Month, Units; everything before = SKU (may contain commas) */
            var units = parts[parts.length-1].trim();
            var month = parts[parts.length-2].trim();
            var quarter = parts[parts.length-3].trim();
            var year = parts[parts.length-4].trim();
            var sku = parts.slice(0, parts.length-4).join(",").trim();
            if (!sku || !/^\d{4}$/.test(year)) return;        /* skips pasted header lines */
            var uNum = Number(units.replace(/[^0-9.\-]/g,""));
            if (!/[0-9]/.test(units) || isNaN(uNum)) return;  /* units must be numeric */
            /* "MFR:" prefix = a MANUFACTURER TOTAL for the period (no SKU needed) —
               leaked manufacturer rankings are traceable because the mfr total
               carries the user's pair delta. (Full-MARKET totals are NOT — they
               equal the original exactly for every user, by design.) */
            if (/^mfr\s*:/i.test(sku)) {
              var mfName = sku.replace(/^mfr\s*:/i, "").trim();
              if (mfName) rows.push({ sku: null, manufacturer: mfName, year: year, quarter: quarter || null, month: month || null, units: uNum });
              return;
            }
            rows.push({ sku: sku, year: year, quarter: quarter || null, month: month || null, units: uNum });
          });
          if (!rows.length) { setFpResult({error:"No rows to check yet — in step 3, pick a SKU, choose the period type and period, enter the units value and click “+ Add”."}); return; }
          setFpBusy(true); setFpResult(null); setFpHistSel(null);
          api.checkFingerprint(st, rows)
            .then(function(r){
              setFpResult(r); setFpBusy(false);
              if (r && !r.error) {
                var _d0 = new Date();
                var _p2 = function(n){ return (n < 10 ? "0" : "") + n; };
                var _ts0 = _p2(_d0.getDate()) + "/" + _p2(_d0.getMonth() + 1) + " " + _p2(_d0.getHours()) + ":" + _p2(_d0.getMinutes());
                var _he = { ts: _ts0, study: st, rowCount: rows.length, verdict: r.verdict || null, res: r };
                setFpHistory(function(prev){ return [_he].concat(prev || []).slice(0, 15); });
              }
            })
            .catch(function(e){ setFpResult({error: (e && e.message) || "Check failed"}); setFpBusy(false); });
        };
        return <div style={{display:"grid",gridTemplateColumns:"minmax(300px,1fr) minmax(400px,1.4fr)",gap:14,alignItems:"start"}}>
          {/* Left: per-client toggles */}
          <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
            <div style={{padding:"12px 16px",borderBottom:"1px solid #e2e8f0",background:"#f8fafc"}}>
              <div style={{fontSize:12,fontWeight:800,color:"#0f172a"}}>Per-USER fingerprint</div>
              <div style={{fontSize:9.5,color:"#64748b",marginTop:3,lineHeight:1.5}}>
                Default OFF. When ON, that user's market data carries a rare
                deterministic ±1-unit delta (one low-priced SKU per manufacturer, ~1 month in 6,
                unit price exactly unchanged, market totals exactly original) — consistent at SKU,
                product AND manufacturer levels. A leak traces back to the specific USER, not just the company.
                Admins always see clean data. The database is never modified.
              </div>
              {/* Filter row: free-text over user / email / client + ON-only switch */}
              <div style={{display:"flex",gap:8,alignItems:"center",marginTop:9}}>
                <input value={fpUserFilter} onChange={function(e){ setFpUserFilter(e.target.value); }}
                  placeholder="🔍 Filter by user, email or client…"
                  style={{flex:1,padding:"6px 11px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10.5,fontFamily:"inherit",outline:"none",background:"#fff"}} />
                <button onClick={function(){ setFpOnlyOn(!fpOnlyOn); }}
                  title="Show only users with fingerprint enabled"
                  style={{padding:"6px 11px",border:"1.5px solid "+(fpOnlyOn?"#059669":"#e2e8f0"),borderRadius:6,background:fpOnlyOn?"#ecfdf5":"#fff",color:fpOnlyOn?"#059669":"#64748b",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                  ON only
                </button>
                {(fpUserFilter || fpOnlyOn) && (
                  <button onClick={function(){ setFpUserFilter(""); setFpOnlyOn(false); }}
                    style={{padding:"6px 9px",border:"1px solid #e2e8f0",borderRadius:6,background:"#f8fafc",color:"#475569",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                )}
              </div>
            </div>
            {fpMarkedView && (
              <div style={{borderBottom:"1px solid #e9d5ff",background:"#faf5ff"}}>
                <div style={{display:"flex",alignItems:"center",padding:"7px 12px",gap:8}}>
                  <span style={{fontSize:10,fontWeight:800,color:"#6d28d9",flex:1}}>
                    {fpMarkedView.error ? "\u26A0 " + fpMarkedView.error
                      : fpMarkedView.loading ? "Loading marked SKUs\u2026"
                      : ("\uD83D\uDD0E Marked SKUs \u2014 " + (fpMarkedView.userName || "") + (fpMarkedView.enabled ? "" : " (fingerprint currently OFF)"))}
                  </span>
                  <button onClick={function(){ setFpMarkedView(null); }}
                    style={{border:"none",background:"none",color:"#7c3aed",cursor:"pointer",fontSize:11,fontWeight:800}}>\u2715</button>
                </div>
                {fpMarkedView.note && <div style={{padding:"0 12px 8px",fontSize:9.5,color:"#92400e"}}>{fpMarkedView.note}</div>}
                {fpMarkedView.marked && fpMarkedView.marked.length > 0 && (
                  <div style={{maxHeight:170,overflowY:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:9.5}}>
                      <thead><tr style={{background:"#f5f0fb"}}>
                        <th style={{padding:"3px 12px",textAlign:"left",fontWeight:800,color:"#4c1d95",fontSize:8.5}}>MANUFACTURER</th>
                        <th style={{padding:"3px 8px",textAlign:"left",fontWeight:800,color:"#4c1d95",fontSize:8.5}}>PRODUCT</th>
                        <th style={{padding:"3px 8px",textAlign:"left",fontWeight:800,color:"#4c1d95",fontSize:8.5}}>MARKED SKU</th>
                        <th style={{padding:"3px 8px",textAlign:"center",fontWeight:800,color:"#4c1d95",fontSize:8.5}}>SIDE</th>
                        <th style={{padding:"3px 8px",textAlign:"left",fontWeight:800,color:"#4c1d95",fontSize:8.5}}>ACTIVE PERIODS (last 2 yrs) — where the \u00B11 appears</th>
                      </tr></thead>
                      <tbody>
                        {fpMarkedView.marked.map(function(mk9, i9){
                          return <tr key={i9} style={{borderBottom:"1px solid #f3e8ff"}}>
                            <td style={{padding:"3px 12px",fontWeight:700,color:"#4c1d95",whiteSpace:"nowrap"}}>{mk9.manufacturer}</td>
                            <td style={{padding:"3px 8px",color:"#7c3aed",whiteSpace:"nowrap"}}>{mk9.product || "\u2014"}</td>
                            <td style={{padding:"3px 8px",color:"#6d28d9",fontFamily:"'JetBrains Mono',monospace",fontSize:9}}>{mk9.sku}</td>
                            <td style={{padding:"3px 8px",textAlign:"center"}}>
                              {mk9.active
                                ? <span style={{background:mk9.sign>0?"#ecfdf5":"#fef2f2",color:mk9.sign>0?"#059669":"#dc2626",padding:"1px 7px",borderRadius:8,fontSize:8.5,fontWeight:800}}>{mk9.sign>0?"+ side":"\u2212 side"}</span>
                                : <span style={{background:"#f1f5f9",color:"#94a3b8",padding:"1px 7px",borderRadius:8,fontSize:8.5,fontWeight:800}}>none</span>}
                            </td>
                            <td style={{padding:"3px 8px",color:"#0f172a",fontSize:9,lineHeight:1.6}}>
                              {(mk9.deltas && mk9.deltas.length)
                                ? mk9.deltas.map(function(dl9, di9){
                                    return <span key={di9} style={{display:"inline-block",background:dl9.d>0?"#ecfdf5":"#fef2f2",color:dl9.d>0?"#059669":"#dc2626",padding:"0 6px",borderRadius:6,fontWeight:800,marginRight:4}}>
                                      {MONTH_S[dl9.m-1] + " " + String(dl9.y).slice(2) + " " + (dl9.d>0?"+":"") + dl9.d}
                                    </span>;
                                  })
                                : <span style={{color:"#94a3b8"}}>quiet in this window</span>}
                            </td>
                          </tr>;
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {(function(){
              var fq = (fpUserFilter || "").trim().toLowerCase();
              var flist = fpClients.filter(function(c){
                if (fpOnlyOn && !c.FingerprintEnabled) return false;
                if (!fq) return true;
                return String(c.FullName||"").toLowerCase().indexOf(fq) >= 0 ||
                       String(c.Email||"").toLowerCase().indexOf(fq) >= 0 ||
                       String(c.ClientName||"").toLowerCase().indexOf(fq) >= 0;
              });
              return <div style={{maxHeight:420,overflowY:"auto"}}>
              <div style={{padding:"6px 16px",fontSize:9.5,color:"#94a3b8",borderBottom:"1px solid #f1f5f9",background:"#fcfdfe",display:"flex",alignItems:"center",gap:8}}>
                <span style={{flex:1}}>{flist.length} of {fpClients.length} users{fq ? " matching \u201C" + fpUserFilter.trim() + "\u201D" : ""}{fpOnlyOn ? " \u00B7 ON only" : ""}</span>
                {flist.some(function(x){ return !x.FingerprintEnabled; }) && (
                  <button onClick={function(){ bulkFp(flist.filter(function(x){ return !x.FingerprintEnabled; }).map(function(x){ return x.UserId; }), true); }}
                    title="Turn fingerprint ON for every user currently listed (respects the filter)"
                    style={{padding:"3px 10px",border:"none",borderRadius:5,background:"#059669",color:"#fff",fontSize:9,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                    {"\u26A1 Enable all (" + flist.filter(function(x){ return !x.FingerprintEnabled; }).length + ")"}
                  </button>
                )}
                {flist.some(function(x){ return !!x.FingerprintEnabled; }) && (
                  <button onClick={function(){ bulkFp(flist.filter(function(x){ return !!x.FingerprintEnabled; }).map(function(x){ return x.UserId; }), false); }}
                    title="Turn fingerprint OFF for every listed user that is currently ON (seeds are kept)"
                    style={{padding:"3px 10px",border:"1px solid #e2e8f0",borderRadius:5,background:"#fff",color:"#64748b",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                    Disable all
                  </button>
                )}
              </div>
              {flist.length === 0 && <div style={{padding:"20px",textAlign:"center",color:"#94a3b8",fontSize:11}}>No users match this filter.</div>}
              {flist.map(function(c){
                var on = !!c.FingerprintEnabled;
                return <div key={c.UserId} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 16px",borderBottom:"1px solid #f1f5f9"}}>
                  <span style={{flex:1,fontSize:11.5,fontWeight:600,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={c.Email || ""}>
                    {c.FullName || c.Email || ("User #" + c.UserId)}
                    {c.ClientName ? <span style={{color:"#7c3aed",fontWeight:700,fontSize:10}}> · {c.ClientName}</span> : null}
                  </span>
                  {!!c.HasSeed && <span title="Secret seed generated — fingerprints from past sessions remain verifiable" style={{fontSize:8.5,color:"#64748b",background:"#f1f5f9",padding:"1px 6px",borderRadius:8,fontWeight:700}}>seeded</span>}
                  {!!c.HasSeed && (
                    <button onClick={function(){
                        var st = (fpStudy || "").trim().toLowerCase();
                        if (!st) { setFpMarkedView({error: "Enter the study code in the Fingerprint Check panel first (e.g. iraq)."}); return; }
                        setFpMarkedView({loading: true});
                        api.getUserMarkedSkus(c.UserId, st)
                          .then(function(r){ setFpMarkedView({ userName: (r && r.userName) || (c.FullName || c.Email), marked: (r && r.marked) || [], enabled: r && r.enabled, note: r && r.note }); })
                          .catch(function(e){ setFpMarkedView({error: (e && e.message) || "Failed to load marked SKUs"}); });
                      }}
                      title="Reveal this user's actual marked SKUs (admin-only, audited) — use for testing and investigations"
                      style={{padding:"2px 8px",border:"1px solid #7c3aed",borderRadius:5,background:"#f5f3ff",color:"#6d28d9",fontSize:9,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                      🔎 SKUs
                    </button>
                  )}
                  <div onClick={function(){ toggleFp(c); }}
                    style={{width:38,height:20,borderRadius:12,cursor:"pointer",position:"relative",transition:"background .15s",background:on?"#059669":"#cbd5e1"}}>
                    <div style={{position:"absolute",top:2,left:on?20:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left .15s",boxShadow:"0 1px 3px rgba(0,0,0,.3)"}} />
                  </div>
                  <span style={{fontSize:9,fontWeight:800,color:on?"#059669":"#94a3b8",width:26}}>{on?"ON":"OFF"}</span>
                </div>;
              })}
            </div>;
            })()}
          </div>
          {/* Right: verification tool — clear 4-step pipeline */}
          <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
            <div style={{padding:"12px 16px",borderBottom:"1px solid #e2e8f0",background:"#f8fafc"}}>
              <div style={{fontSize:12,fontWeight:800,color:"#0f172a"}}>🔬 Fingerprint Check — who leaked this data?</div>
              <div style={{fontSize:9.5,color:"#64748b",marginTop:3,lineHeight:1.5}}>
                You never need to know the user in advance — follow the 4 steps and the numbers identify them.
                (Holding the original Excel file? Use <b>Export Tracer → 🧾 Check File</b> instead — it's one step.)
              </div>
            </div>
            <div style={{padding:"12px 16px"}}>
              {(function(){
                var stepHdr = function(n, title, sub){
                  return <div style={{display:"flex",alignItems:"baseline",gap:8,margin:"2px 0 6px"}}>
                    <span style={{width:18,height:18,borderRadius:"50%",background:"#2563eb",color:"#fff",fontSize:10,fontWeight:800,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{n}</span>
                    <span style={{fontSize:11,fontWeight:800,color:"#0f172a"}}>{title}</span>
                    {sub ? <span style={{fontSize:9,color:"#94a3b8"}}>{sub}</span> : null}
                  </div>;
                };
                return <div>
                  {/* STEP 1 — study */}
                  {stepHdr(1, "Choose the study", "which market the suspect data belongs to")}
                  <input value={fpStudy} onChange={function(e){ setFpStudy(e.target.value); }} placeholder="iraq / jordan / libya"
                    style={{padding:"7px 12px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:11,fontFamily:"inherit",outline:"none",width:230,marginBottom:12,display:"block"}} />

                  {/* STEP 2 — candidate SKUs */}
                  {stepHdr(2, "See which SKUs can carry a fingerprint", "Manufacturer → Product → SKU")}
                  <div style={{fontSize:9.5,color:"#64748b",lineHeight:1.5,marginBottom:6}}>
                    Each manufacturer has <b>3 candidate SKUs</b> (its cheapest eligible ones). For any given user,
                    the fingerprint changes <b style={{color:"#6d28d9"}}>exactly ONE of the 3</b> — never all of them;
                    the other two always stay clean. Which one it is depends on the user's secret seed.
                  </div>
                  <button onClick={function(){
                      var st = (fpStudy || "").trim().toLowerCase();
                      if (!st) { setFpResult({error:"Step 1 first — enter a study code (e.g. iraq)."}); return; }
                      if (fpCandidates) { setFpCandidates(null); return; }
                      api.getFingerprintCandidates(st)
                        .then(function(r){ setFpCandidates((r && r.candidates) || []); })
                        .catch(function(e){ setFpResult({error:(e && e.message) || "Failed to load candidates"}); });
                    }}
                    style={{padding:"6px 14px",border:"1.5px solid #7c3aed",borderRadius:6,background:fpCandidates?"#6d28d9":"#f5f3ff",color:fpCandidates?"#fff":"#6d28d9",fontSize:10.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginBottom:8}}>
                    {fpCandidates ? "Hide candidate SKUs" : "📋 Show candidate SKUs"}
                  </button>
                  {fpCandidates && (
                    <div style={{marginBottom:12,border:"1px solid #e9d5ff",borderRadius:8,background:"#faf5ff",maxHeight:200,overflowY:"auto"}}>
                      <div style={{padding:"7px 12px",fontSize:9.5,color:"#6d28d9",fontWeight:700,borderBottom:"1px solid #e9d5ff",position:"sticky",top:0,background:"#faf5ff"}}>
                        {fpCandidates.length} manufacturers · 3 candidates each · only ONE per manufacturer changes for a given user
                      </div>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:9.5}}>
                        <thead><tr style={{background:"#f5f0fb"}}>
                          <th style={{padding:"4px 12px",textAlign:"left",fontWeight:800,color:"#4c1d95",fontSize:9}}>MANUFACTURER</th>
                          <th style={{padding:"4px 8px",textAlign:"left",fontWeight:800,color:"#4c1d95",fontSize:9}}>PRODUCT</th>
                          <th style={{padding:"4px 8px",textAlign:"left",fontWeight:800,color:"#4c1d95",fontSize:9}}>CANDIDATE SKU</th>
                        </tr></thead>
                        <tbody>
                          {fpCandidates.map(function(cd){
                            return (cd.skus || []).map(function(s9, i9){
                              var _sk = (typeof s9 === "string") ? {sku:s9, product:""} : s9;
                              return <tr key={cd.manufacturer + "_" + i9} style={{borderBottom:"1px solid #f3e8ff"}}>
                                <td style={{padding:"3px 12px",fontWeight:i9===0?800:400,color:i9===0?"#4c1d95":"#c4b5fd",whiteSpace:"nowrap"}}>{i9===0 ? cd.manufacturer : "\u2033"}</td>
                                <td style={{padding:"3px 8px",color:"#7c3aed",whiteSpace:"nowrap"}}>{_sk.product || "\u2014"}</td>
                                <td style={{padding:"3px 8px",color:"#6d28d9",fontFamily:"'JetBrains Mono',monospace",fontSize:9}}>{_sk.sku}</td>
                              </tr>;
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* STEP 3 — structured row builder (+ optional bulk paste) */}
                  {stepHdr(3, "Add the suspect figures", "pick SKU + period type + which period + units")}
                  <div style={{border:"1px solid #e2e8f0",borderRadius:8,padding:"10px 12px",background:"#fcfdfe",marginBottom:8}}>
                    {/* SKU picker: dropdown from candidates + manual override */}
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:9,fontWeight:800,color:"#64748b",textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>SKU</div>
                      {fpCandidates ? (
                        <select value={fpEntry.mf ? (fpEntry.mf + "|" + fpEntry.product + "|" + fpEntry.sku) : ""}
                          onChange={function(e){
                            var _pp = String(e.target.value || "").split("|");
                            if (_pp.length === 3) setFpEntry(Object.assign({}, fpEntry, {mf: _pp[0], product: _pp[1], sku: _pp[2]}));
                            else setFpEntry(Object.assign({}, fpEntry, {mf: "", product: "", sku: ""}));
                          }}
                          style={{width:"100%",padding:"6px 10px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10.5,fontFamily:"inherit",outline:"none",background:"#fff"}}>
                          <option value="">— choose a candidate SKU —</option>
                          {fpCandidates.map(function(cd){
                            return <optgroup key={cd.manufacturer} label={cd.manufacturer}>
                              {(cd.skus||[]).map(function(s9,i9){
                                var _sk = (typeof s9 === "string") ? {sku:s9, product:""} : s9;
                                return <option key={i9} value={cd.manufacturer + "|" + (_sk.product||"") + "|" + _sk.sku}>{(_sk.product ? _sk.product + " \u2014 " : "") + _sk.sku}</option>;
                              })}
                            </optgroup>;
                          })}
                        </select>
                      ) : (
                        <div style={{fontSize:9.5,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:6,padding:"6px 10px",marginBottom:4}}>
                          Load the candidate list in step 2 to pick SKUs from a dropdown — or type the SKU name below.
                        </div>
                      )}
                      <input value={fpEntry.sku} onChange={function(e){ setFpEntry(Object.assign({}, fpEntry, {sku: e.target.value, mf: "", product: ""})); }}
                        placeholder="…or type / edit the SKU name manually (identity strongest when picked from the dropdown)"
                        style={{width:"100%",padding:"6px 10px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10,fontFamily:"'JetBrains Mono',monospace",outline:"none",marginTop:5,boxSizing:"border-box"}} />
                    </div>
                    {/* Period type + which period + units */}
                    <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
                      <div>
                        <div style={{fontSize:9,fontWeight:800,color:"#64748b",textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>Period type</div>
                        <div style={{display:"flex",border:"1.5px solid #2563eb",borderRadius:6,overflow:"hidden"}}>
                          {[["year","Full Year"],["quarter","Quarter"],["month","Month"]].map(function(pt){
                            var on = fpEntry.ptype === pt[0];
                            return <button key={pt[0]} onClick={function(){ setFpEntry(Object.assign({}, fpEntry, {ptype: pt[0]})); }}
                              style={{padding:"6px 12px",border:"none",borderRight:pt[0]!=="month"?"1px solid #bfdbfe":"none",background:on?"#2563eb":"#fff",color:on?"#fff":"#1d4ed8",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{pt[1]}</button>;
                          })}
                        </div>
                      </div>
                      <div>
                        <div style={{fontSize:9,fontWeight:800,color:"#64748b",textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>Year</div>
                        <select value={fpEntry.year} onChange={function(e){ setFpEntry(Object.assign({}, fpEntry, {year: e.target.value})); }}
                          style={{padding:"6px 10px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10.5,fontFamily:"inherit",outline:"none",background:"#fff"}}>
                          {(function(){ var ys=[]; var cy=new Date().getFullYear()+1; for (var yy=cy; yy>=cy-9; yy--) ys.push(String(yy)); return ys; })().map(function(yy){
                            return <option key={yy} value={yy}>{yy}</option>;
                          })}
                        </select>
                      </div>
                      {fpEntry.ptype === "quarter" && (
                        <div>
                          <div style={{fontSize:9,fontWeight:800,color:"#64748b",textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>Quarter</div>
                          <select value={fpEntry.quarter} onChange={function(e){ setFpEntry(Object.assign({}, fpEntry, {quarter: e.target.value})); }}
                            style={{padding:"6px 10px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10.5,fontFamily:"inherit",outline:"none",background:"#fff"}}>
                            {["1","2","3","4"].map(function(qq){ return <option key={qq} value={qq}>{"Q"+qq}</option>; })}
                          </select>
                        </div>
                      )}
                      {fpEntry.ptype === "month" && (
                        <div>
                          <div style={{fontSize:9,fontWeight:800,color:"#64748b",textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>Month</div>
                          <select value={fpEntry.month} onChange={function(e){ setFpEntry(Object.assign({}, fpEntry, {month: e.target.value})); }}
                            style={{padding:"6px 10px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10.5,fontFamily:"inherit",outline:"none",background:"#fff"}}>
                            {MONTH_S.map(function(mn9,i9){ return <option key={i9} value={String(i9+1)}>{mn9}</option>; })}
                          </select>
                        </div>
                      )}
                      <div>
                        <div style={{fontSize:9,fontWeight:800,color:"#64748b",textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>Units (from suspect data)</div>
                        <input value={fpEntry.units} onChange={function(e){ setFpEntry(Object.assign({}, fpEntry, {units: e.target.value})); }}
                          placeholder="e.g. 166,740"
                          style={{padding:"6px 10px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10.5,fontFamily:"'JetBrains Mono',monospace",outline:"none",width:120}} />
                      </div>
                      <button onClick={function(){
                          var skuV = (fpEntry.sku || "").trim();
                          var uV = Number(String(fpEntry.units).replace(/[^0-9.\-]/g,""));
                          if (!skuV) { setFpResult({error:"Choose or type a SKU first."}); return; }
                          if (!uV && uV !== 0) { setFpResult({error:"Enter the units value from the suspect data."}); return; }
                          setFpResult(null);
                          setFpRows(fpRows.concat([{
                            sku: skuV, manufacturer: fpEntry.mf || null, product: fpEntry.product || null,
                            year: fpEntry.year,
                            quarter: fpEntry.ptype === "quarter" ? fpEntry.quarter : null,
                            month: fpEntry.ptype === "month" ? fpEntry.month : null,
                            units: uV
                          }]));
                          setFpRowsOpen(true);
                          setFpEntry(Object.assign({}, fpEntry, {units: ""}));
                        }}
                        style={{padding:"7px 16px",border:"none",borderRadius:6,background:"#059669",color:"#fff",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                        + Add
                      </button>
                    </div>
                  </div>
                  {/* Added rows \u2014 collapsible list with summary header */}
                  {fpRows.length > 0 && (
                    <div style={{border:"1px solid #e2e8f0",borderRadius:8,overflow:"hidden",marginBottom:8}}>
                      <div onClick={function(){ setFpRowsOpen(!fpRowsOpen); }}
                        style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",background:"#f8fafc",borderBottom:fpRowsOpen?"1px solid #e2e8f0":"none",cursor:"pointer",userSelect:"none"}}>
                        <span style={{fontSize:10.5,fontWeight:800,color:"#0f172a"}}>{(fpRowsOpen ? "\u25BE " : "\u25B8 ") + fpRows.length + " row(s) ready"}</span>
                        <span style={{flex:1,fontSize:9,color:"#94a3b8"}}>{fpRowsOpen ? "click to collapse" : "click to review the rows \u2014 10\u201330 across several manufacturers give a decisive answer"}</span>
                        <span onClick={function(ev){ ev.stopPropagation(); setFpRows([]); setFpRowsOpen(true); }}
                          style={{color:"#dc2626",cursor:"pointer",fontWeight:700,fontSize:9.5}}>clear all</span>
                      </div>
                      {fpRowsOpen && (
                        <div style={{maxHeight:280,overflowY:"auto"}}>
                          <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                        <thead><tr style={{background:"#f8fafc",borderBottom:"1px solid #e2e8f0"}}>
                          <th style={{padding:"5px 10px",textAlign:"left",fontWeight:800,color:"#475569",fontSize:9}}>SKU</th>
                          <th style={{padding:"5px 10px",textAlign:"left",fontWeight:800,color:"#475569",fontSize:9}}>PERIOD</th>
                          <th style={{padding:"5px 10px",textAlign:"right",fontWeight:800,color:"#475569",fontSize:9}}>UNITS</th>
                          <th style={{width:30}}></th>
                        </tr></thead>
                        <tbody>
                          {fpRows.map(function(r9, i9){
                            var perLbl = r9.month ? (MONTH_S[parseInt(r9.month)-1] + " " + r9.year) : r9.quarter ? ("Q" + r9.quarter + " " + r9.year) : ("Full Year " + r9.year);
                            return <tr key={i9} style={{borderBottom:"1px solid #f1f5f9"}}>
                              <td style={{padding:"4px 10px",fontSize:9.5,color:"#0f172a"}}>
                                {(r9.manufacturer || r9.product) ? <span style={{color:"#7c3aed",fontWeight:700}}>{(r9.manufacturer||"?") + (r9.product ? " \u203A " + r9.product : "") + " \u203A "}</span> : null}
                                <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{r9.sku}</span>
                              </td>
                              <td style={{padding:"4px 10px",color:"#2563eb",fontWeight:700,whiteSpace:"nowrap"}}>{perLbl}</td>
                              <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'JetBrains Mono',monospace"}}>{Number(r9.units).toLocaleString()}</td>
                              <td style={{padding:"4px 8px",textAlign:"center"}}>
                                <button onClick={function(){ setFpRows(fpRows.filter(function(_x, _xi){ return _xi !== i9; })); }}
                                  style={{border:"none",background:"none",color:"#dc2626",cursor:"pointer",fontSize:11,fontWeight:800}}>✕</button>
                              </td>
                            </tr>;
                          })}
                        </tbody>
                      </table>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Upload a filled template (or any sheet with SKU/Year/Units columns) */}
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                    <select value={fpUpPType} onChange={function(e){ setFpUpPType(e.target.value); }}
                      title="Which period columns/rows to take from the uploaded file"
                      style={{padding:"6px 8px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10,fontFamily:"inherit",background:"#fff",color:"#475569",fontWeight:700,outline:"none"}}>
                      <option value="all">All period types</option>
                      <option value="year">Full years only</option>
                      <option value="quarter">Quarters only</option>
                      <option value="month">Months only</option>
                    </select>
                    <label style={{padding:"6px 14px",border:"1.5px dashed #2563eb",borderRadius:6,background:"#eff6ff",color:"#1d4ed8",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                      {"\uD83D\uDCC4 Upload filled template"}
                      <input type="file" accept=".xlsx,.xls,.csv" style={{display:"none"}} onChange={fpFileUpload} />
                    </label>
                    <button onClick={fpTemplateDl}
                      style={{padding:"6px 14px",border:"1px solid #e2e8f0",borderRadius:6,background:"#fff",color:"#475569",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                      {"\u2B07 Download template"}
                    </button>
                    <span style={{fontSize:9,color:"#94a3b8"}}>{"Pick a period type (optional), then upload \u2014 grid-style Excel/CSV: Manufacturer, Product, SKU + one column per period (2026, Q1 2026, Jan 2026\u2026)"}</span>
                  </div>
                  {fpUpMsg ? (
                    <div style={{marginBottom:8,padding:"6px 11px",borderRadius:6,fontSize:9.5,fontWeight:700,
                      background: fpUpMsg.indexOf("\u26A0") === 0 ? "#fef2f2" : "#ecfdf5",
                      border: "1px solid " + (fpUpMsg.indexOf("\u26A0") === 0 ? "#fecaca" : "#a7f3d0"),
                      color: fpUpMsg.indexOf("\u26A0") === 0 ? "#b91c1c" : "#065f46"}}>{fpUpMsg}</div>
                  ) : null}
                  {/* Optional bulk paste */}
                  <div style={{marginBottom:12}}>
                    <span onClick={function(){ setFpBulkOpen(!fpBulkOpen); }}
                      style={{fontSize:9.5,color:"#2563eb",cursor:"pointer",fontWeight:700,userSelect:"none"}}>
                      {fpBulkOpen ? "▾" : "▸"} Bulk paste (advanced) — SKU, Year, Quarter, Month, Units · or MFR:Name for a manufacturer total
                    </span>
                    {fpBulkOpen && (
                      <textarea value={fpRowsText} onChange={function(e){ setFpRowsText(e.target.value); }}
                        placeholder={"AMOCLAN TAB 625MG 20`S, 2026, 1, , 46120\nFERINJECT AMP 500MG, 2026, , 3, 1204\nMFR:NOVO NORDISK, 2026, 2, , 1204553   ← manufacturer TOTAL for Q2 2026 (no SKU needed)"}
                        style={{width:"100%",minHeight:90,padding:"9px 12px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10,fontFamily:"'JetBrains Mono',monospace",outline:"none",resize:"vertical",boxSizing:"border-box",marginTop:6}} />
                    )}
                  </div>

                  {/* STEP 4 — run + verdict */}
                  {stepHdr(4, "Run the check", "tests every seeded user at once")}
                  <button onClick={runCheck} disabled={fpBusy}
                    style={{padding:"8px 20px",border:"none",borderRadius:6,background:fpBusy?"#94a3b8":"#2563eb",color:"#fff",fontSize:11.5,fontWeight:800,cursor:fpBusy?"wait":"pointer",fontFamily:"inherit"}}>
                    {fpBusy ? "Checking\u2026" : "\u25B6 Run Check"}
                  </button>
                  {fpResult && fpResult.error && (
                    <div style={{marginTop:10,padding:"9px 12px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:6,fontSize:10.5,color:"#b91c1c",fontWeight:600}}>{fpResult.error}</div>
                  )}
                  {fpResult && !fpResult.error && (
                    <div style={{marginTop:10}}>
                      <div style={{fontSize:9,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".07em",marginBottom:6}}>Current run</div>
                      {fpResBlock(fpResult)}
                    </div>
                  )}
                  {/* ---- Search history: every run kept here; click a run to review it ---- */}
                  {fpHistory.length > 0 && (
                    <div style={{marginTop:16,border:"1px solid #e2e8f0",borderRadius:8,overflow:"hidden"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"#f8fafc",borderBottom:"1px solid #e2e8f0"}}>
                        <span style={{fontSize:10.5,fontWeight:800,color:"#475569"}}>{"\uD83D\uDD52 Search history (" + fpHistory.length + ")"}</span>
                        <span style={{flex:1,fontSize:9,color:"#94a3b8"}}>{"runs from this session, newest first \u2014 click a run to review its full result"}</span>
                        <button onClick={function(){ setFpHistory([]); setFpHistSel(null); }}
                          style={{padding:"3px 10px",border:"1px solid #e2e8f0",borderRadius:6,background:"#fff",color:"#64748b",fontSize:9.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>clear</button>
                      </div>
                      {fpHistory.map(function(h, hi){
                        var sel = fpHistSel === hi;
                        return <div key={hi} style={{borderBottom:"1px solid #f1f5f9"}}>
                          <div onClick={function(){ setFpHistSel(sel ? null : hi); }}
                            style={{display:"flex",alignItems:"center",gap:10,padding:"7px 12px",cursor:"pointer",background:sel?"#eff6ff":"#fff",fontSize:10}}>
                            <span style={{fontFamily:"'JetBrains Mono',monospace",color:"#64748b",whiteSpace:"nowrap"}}>{h.ts}</span>
                            <span style={{fontWeight:700,color:"#0f172a",textTransform:"uppercase"}}>{h.study}</span>
                            <span style={{color:"#64748b"}}>{h.rowCount + " row(s)"}</span>
                            {hi === 0 && <span style={{background:"#eff6ff",color:"#2563eb",padding:"1px 8px",borderRadius:10,fontSize:8.5,fontWeight:800}}>LATEST</span>}
                            <span style={{flex:1}} />
                            {h.verdict
                              ? <span style={{background:"#ecfdf5",color:"#065f46",padding:"1px 9px",borderRadius:10,fontSize:9,fontWeight:800}}>{"\u26A0 " + h.verdict}</span>
                              : <span style={{background:"#f1f5f9",color:"#64748b",padding:"1px 9px",borderRadius:10,fontSize:9,fontWeight:700}}>no match</span>}
                            <span style={{color:"#2563eb",fontSize:9.5,fontWeight:700,whiteSpace:"nowrap"}}>{sel ? "hide \u25B4" : "view \u25BE"}</span>
                          </div>
                          {sel && (
                            <div style={{padding:"10px 12px",background:"#fbfdff",borderTop:"1px dashed #dbeafe"}}>
                              {fpResBlock(h.res)}
                            </div>
                          )}
                        </div>;
                      })}
                    </div>
                  )}
                </div>;
              })()}
            </div>
          </div>
        </div>;
      })()}

      {/* ══════ Audit Log tab — who used the traceability controls ══════ */}
      {/* ═══ ACTIVATION — who activated / logged in, filter by study, export ═══ */}
      {subTab === "activation" && (function(){
        var _all = (adoption && adoption.users) || [];
        var _list = _all.filter(function(u){
          if (adpStudy && u.studies.indexOf(adpStudy) < 0) return false;
          if (adpClient && u.client !== adpClient) return false;
          if (adpStatus === "activated" && !u.activated) return false;
          if (adpStatus === "never" && u.activated) return false;
          if (adpQ.trim()) {
            var q = adpQ.toLowerCase();
            if ((u.name||"").toLowerCase().indexOf(q) < 0 && (u.email||"").toLowerCase().indexOf(q) < 0 && (u.client||"").toLowerCase().indexOf(q) < 0) return false;
          }
          return true;
        });
        var _act = _list.filter(function(u){ return u.activated; }).length;
        var _cliOpts = (function(){ var s = {}; _all.forEach(function(u){ if (u.client) s[u.client] = 1; }); return Object.keys(s).sort(); })();
        var _stLbl = function(c){ return String(c).replace(/_/g, " ").toUpperCase(); };
        var _expAdp = function(){
          var hdr = ["Client", "Studies", "User", "Email", "Account Status", "Activated (has logged in)", "First Login", "Last Login", "Total Logins"];
          var aoa = [hdr];
          _list.forEach(function(u){
            aoa.push([u.client, u.studies.map(_stLbl).join(", "), u.name, u.email, u.status,
                      u.activated ? "YES" : "NO",
                      u.firstLogin ? fmtAmman(u.firstLogin) : "", u.lastLogin ? fmtAmman(u.lastLogin) : "", u.logins]);
          });
          getXLSX().then(function(XLSX) {
            var ws = XLSX.utils.aoa_to_sheet(aoa);
            ws["!cols"] = hdr.map(function(h, i){ return { wch: i <= 3 ? 26 : 16 }; });
            var wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Activation");
            XLSX.writeFile(wb, "AMSX_User_Activation_" + new Date().toISOString().slice(0,10) + ".xlsx");
          }).catch(function(e){ alert("Export failed: " + e.message); });
        };
        return (
          <div>
            {/* KPI strip */}
            <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
              {[{l:"Users (filtered)",v:_list.length,co:"#2563eb",bg:"#eff6ff"},
                {l:"Activated \u2014 logged in",v:_act,co:"#059669",bg:"#ecfdf5"},
                {l:"Never logged in",v:_list.length-_act,co:"#dc2626",bg:"#fef2f2"},
                {l:"Activation rate",v:(_list.length?Math.round(_act/_list.length*100):0)+"%",co:"#7c3aed",bg:"#f5f3ff"}].map(function(k9){
                return <div key={k9.l} style={{flex:1,minWidth:140,background:k9.bg,borderRadius:10,padding:"12px 16px",border:"1px solid #e2e8f0"}}>
                  <div style={{fontSize:9,fontWeight:700,color:"#64748b",textTransform:"uppercase",marginBottom:4}}>{k9.l}</div>
                  <div style={{fontSize:22,fontWeight:800,color:k9.co}}>{k9.v}</div>
                </div>;
              })}
            </div>
            {/* Filters + export */}
            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10,flexWrap:"wrap"}}>
              <select value={adpStudy} onChange={function(e){ setAdpStudy(e.target.value); }}
                style={{padding:"5px 10px",border:"1.5px solid "+(adpStudy?"#2563eb":"#e2e8f0"),borderRadius:6,background:adpStudy?"#eff6ff":"#fff",color:adpStudy?"#2563eb":"#64748b",fontSize:10,fontWeight:600,fontFamily:"inherit",outline:"none",cursor:"pointer"}}>
                <option value="">All studies</option>
                {((adoption && adoption.studies) || []).map(function(st9){ return <option key={st9} value={st9}>{_stLbl(st9)}</option>; })}
              </select>
              <select value={adpClient} onChange={function(e){ setAdpClient(e.target.value); }}
                style={{padding:"5px 10px",border:"1.5px solid "+(adpClient?"#2563eb":"#e2e8f0"),borderRadius:6,background:adpClient?"#eff6ff":"#fff",color:adpClient?"#2563eb":"#64748b",fontSize:10,fontWeight:600,fontFamily:"inherit",outline:"none",cursor:"pointer",maxWidth:200}}>
                <option value="">All clients</option>
                {_cliOpts.map(function(c9){ return <option key={c9} value={c9}>{c9}</option>; })}
              </select>
              <select value={adpStatus} onChange={function(e){ setAdpStatus(e.target.value); }}
                style={{padding:"5px 10px",border:"1.5px solid "+(adpStatus?"#2563eb":"#e2e8f0"),borderRadius:6,background:adpStatus?"#eff6ff":"#fff",color:adpStatus?"#2563eb":"#64748b",fontSize:10,fontWeight:600,fontFamily:"inherit",outline:"none",cursor:"pointer"}}>
                <option value="">All users</option>
                <option value="activated">{"\u2713 Activated \u2014 has logged in"}</option>
                <option value="never">{"\u2715 Never logged in"}</option>
              </select>
              <input value={adpQ} onChange={function(e){ setAdpQ(e.target.value); }} placeholder={"Search name / email / client\u2026"}
                style={{padding:"5px 10px",border:"1.5px solid #e2e8f0",borderRadius:6,fontSize:10,fontFamily:"inherit",outline:"none",width:190}} />
              <div style={{flex:1}} />
              <button onClick={_expAdp} disabled={!_list.length}
                style={{padding:"6px 16px",background:"#059669",color:"#fff",border:"none",borderRadius:7,fontSize:10.5,fontWeight:700,cursor:"pointer",opacity:_list.length?1:0.5}}>
                {"\uD83D\uDCE5 Export (" + _list.length + ")"}
              </button>
            </div>
            {/* Table */}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
              <div style={{maxHeight:520,overflowY:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:10.5}}>
                  <thead><tr style={{background:"#f8fafc",position:"sticky",top:0}}>
                    {["USER","EMAIL","CLIENT","STUDIES","STATUS","ACTIVATED","FIRST LOGIN","LAST LOGIN","LOGINS"].map(function(h9){
                      return <th key={h9} style={{padding:"8px 10px",textAlign:"left",fontSize:8.5,fontWeight:700,color:"#64748b"}}>{h9}</th>;
                    })}
                  </tr></thead>
                  <tbody>
                    {_list.map(function(u){
                      return (
                        <tr key={u.userId} style={{borderTop:"1px solid #f1f5f9"}}>
                          <td style={{padding:"7px 10px",fontWeight:700,color:"#0f172a",whiteSpace:"nowrap"}}>{u.name}</td>
                          <td style={{padding:"7px 10px",color:"#64748b"}}>{u.email}</td>
                          <td style={{padding:"7px 10px",color:"#475569",whiteSpace:"nowrap"}}>{u.client || "\u2014"}</td>
                          <td style={{padding:"7px 10px"}}>{u.studies.map(function(st9){ return <span key={st9} style={{display:"inline-block",background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:8,padding:"0px 7px",fontSize:8.5,fontWeight:700,margin:"1px 2px 1px 0"}}>{_stLbl(st9)}</span>; })}</td>
                          <td style={{padding:"7px 10px"}}><span style={{background:u.status==="active"?"#ecfdf5":"#fef3c7",color:u.status==="active"?"#059669":"#92400e",borderRadius:8,padding:"1px 8px",fontSize:8.5,fontWeight:700}}>{u.status}</span></td>
                          <td style={{padding:"7px 10px"}}>{u.activated
                            ? <span style={{color:"#059669",fontWeight:800,fontSize:10}}>{"\u2713 YES"}</span>
                            : <span style={{color:"#dc2626",fontWeight:800,fontSize:10}}>{"\u2715 NEVER"}</span>}</td>
                          <td style={{padding:"7px 10px",color:"#64748b",whiteSpace:"nowrap"}}>{u.firstLogin ? fmtAmman(u.firstLogin) : "\u2014"}</td>
                          <td style={{padding:"7px 10px",color:"#64748b",whiteSpace:"nowrap"}}>{u.lastLogin ? fmtAmman(u.lastLogin) : "\u2014"}</td>
                          <td style={{padding:"7px 10px",fontWeight:700,color:"#0f172a"}}>{u.logins}</td>
                        </tr>
                      );
                    })}
                    {_list.length === 0 && <tr><td colSpan={9} style={{padding:"26px 10px",textAlign:"center",color:"#94a3b8"}}>No users match the current filters.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {subTab === "audit" && (
        <div style={{background:"#fff",borderRadius:10,border:"1px solid #e2e8f0",overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:"1px solid #e2e8f0",background:"#f8fafc",fontSize:11,color:"#475569"}}>
            <b>{auditList.length}</b> audit entries in the last <b>{days}</b> days — every admin action (users, clients, devices, features, uploads, logins, fingerprint tools) recorded with admin identity and IP
            <select value={auditAdminFilter} onChange={function(e){ setAuditAdminFilter(e.target.value); }}
              style={{marginLeft:12,padding:"4px 9px",border:"1.5px solid "+(auditAdminFilter?"#2563eb":"#e2e8f0"),borderRadius:6,background:auditAdminFilter?"#eff6ff":"#fff",color:auditAdminFilter?"#2563eb":"#64748b",fontSize:10,fontWeight:600,fontFamily:"inherit",outline:"none",cursor:"pointer"}}>
              <option value="">All admins</option>
              {(function(){ var s9 = {}; auditList.forEach(function(a){ if (a.AdminEmail) s9[a.AdminEmail] = 1; }); return Object.keys(s9).sort(); })().map(function(em){
                return <option key={em} value={em}>{em}</option>;
              })}
            </select>
          </div>
          {auditList.length === 0 ? (
            <div style={{padding:"24px",textAlign:"center",color:"#94a3b8",fontSize:11}}>No audit entries for this period.</div>
          ) : (
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead>
                <tr style={{background:"#f8fafc",borderBottom:"1.5px solid #e2e8f0"}}>
                  <th style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>TIME</th>
                  <th style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>ADMIN</th>
                  <th style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>ACTION</th>
                  <th style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>TARGET</th>
                  <th style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>DETAILS</th>
                  <th style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:"#475569",fontSize:10}}>IP</th>
                </tr>
              </thead>
              <tbody>
                {auditList.filter(function(a){ return !auditAdminFilter || a.AdminEmail === auditAdminFilter; }).map(function(a){
                  var actionChip = function(ac){
                    var m = { fingerprint_toggle:{bg:"#ecfdf5",co:"#065f46",l:"FP Toggle"}, fingerprint_bulk:{bg:"#ecfdf5",co:"#065f46",l:"FP Bulk"}, fingerprint_check:{bg:"#f5f3ff",co:"#6d28d9",l:"FP Check"}, fingerprint_reveal:{bg:"#faf5ff",co:"#7c3aed",l:"FP Reveal"}, feature_toggle:{bg:"#fff7ed",co:"#c2410c",l:"Feature"}, export_trace:{bg:"#eff6ff",co:"#1d4ed8",l:"Trace"}, team_create:{bg:"#fefce8",co:"#a16207",l:"Team +"}, team_update:{bg:"#fefce8",co:"#a16207",l:"Team \u270E"}, team_delete:{bg:"#fef2f2",co:"#b91c1c",l:"Team \u2212"}, team_promote:{bg:"#fefce8",co:"#a16207",l:"Promote"},
                      admin_login:{bg:"#f0f9ff",co:"#0369a1",l:"Login"},
                      user_create:{bg:"#eff6ff",co:"#1d4ed8",l:"User +"}, user_update:{bg:"#eff6ff",co:"#1d4ed8",l:"User \u270E"}, user_toggle:{bg:"#eff6ff",co:"#1d4ed8",l:"User \u23EF"}, user_delete:{bg:"#fef2f2",co:"#b91c1c",l:"User \u2212"},
                      client_create:{bg:"#f5f3ff",co:"#6d28d9",l:"Client +"}, client_update:{bg:"#f5f3ff",co:"#6d28d9",l:"Client \u270E"}, client_toggle:{bg:"#f5f3ff",co:"#6d28d9",l:"Client \u23EF"}, client_delete:{bg:"#fef2f2",co:"#b91c1c",l:"Client \u2212"},
                      device_approve:{bg:"#ecfdf5",co:"#065f46",l:"Device \u2713"}, device_revoke:{bg:"#fef2f2",co:"#b91c1c",l:"Device \u2715"}, device_action:{bg:"#f1f5f9",co:"#475569",l:"Device"},
                      feature_edit:{bg:"#fff7ed",co:"#c2410c",l:"Feature \u270E"}, dashboard_config:{bg:"#eef2ff",co:"#4338ca",l:"Dash Config"}, cache_refresh:{bg:"#f1f5f9",co:"#475569",l:"Cache"}, data_upload:{bg:"#fefce8",co:"#a16207",l:"Upload"}, admin_action:{bg:"#f1f5f9",co:"#475569",l:"Admin"} };
                    var c = m[ac] || {bg:"#f1f5f9",co:"#475569",l:ac};
                    return <span style={{background:c.bg,color:c.co,padding:"2px 9px",borderRadius:10,fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".04em"}}>{c.l}</span>;
                  };
                  return <tr key={a.AuditId} style={{borderBottom:"1px solid #f1f5f9"}}>
                    <td style={{padding:"8px 12px",whiteSpace:"nowrap",color:"#475569"}} title={fmtDate(a.CreatedAt)}>{fmtAmman(a.CreatedAt)}</td>
                    <td style={{padding:"8px 12px",color:"#0f172a",fontWeight:600,whiteSpace:"nowrap"}}>{a.AdminEmail || (a.AdminUserId != null ? ("User #"+a.AdminUserId) : "—")}</td>
                    <td style={{padding:"8px 12px"}}>{actionChip(a.Action)}</td>
                    <td style={{padding:"8px 12px",fontFamily:"'JetBrains Mono',monospace",fontSize:9.5,color:"#64748b",whiteSpace:"nowrap"}}>{(a.TargetType || "") + (a.TargetId != null ? (" " + a.TargetId) : "")}</td>
                    <td style={{padding:"8px 12px",color:"#475569",fontSize:10,maxWidth:340}} title={a.Details || ""}>
                      {(function(){
                        /* value-level change log (Noura 2026-10-03): edits carry details.changed
                           = ["Status: active → paused", ...] — shown as amber chips */
                        var d9 = null; try { d9 = a.Details ? JSON.parse(a.Details) : null; } catch(e9) { d9 = null; }
                        if (d9 && d9.changed && d9.changed.length) {
                          var rows9 = d9.changed.slice(0, 6).map(function(c9, i9){
                            return <span key={i9} style={{background:"#fffbeb",border:"1px solid #fde68a",color:"#92400e",padding:"1px 7px",borderRadius:5,fontSize:9,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:326,alignSelf:"flex-start"}}>{c9}</span>;
                          });
                          if (d9.changed.length > 6) rows9.push(<span key="m9" style={{fontSize:9,color:"#94a3b8",fontWeight:700}}>{"+" + (d9.changed.length - 6) + " more — hover for all"}</span>);
                          return <div style={{display:"flex",flexDirection:"column",gap:2}}>{rows9}</div>;
                        }
                        return <span style={{display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.Details || "—"}</span>;
                      })()}
                    </td>
                    <td style={{padding:"8px 12px",fontFamily:"'JetBrains Mono',monospace",fontSize:9.5,color:"#64748b"}}>{a.IpAddress || "—"}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* User Timeline Drill-Down Modal */}
      {drillUser && <div onClick={function(){setDrillUser(null);setDrillEvents([]);}} style={{position:"fixed",inset:0,background:"rgba(15,23,42,.55)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div onClick={function(e){e.stopPropagation();}} style={{background:"#fff",borderRadius:14,maxWidth:900,width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,.25)"}}>
          <div style={{padding:"16px 20px",borderBottom:"1px solid #e2e8f0",display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1}}>
              <div style={{fontSize:15,fontWeight:800,color:"#0f172a"}}>{drillUser.UserName || "User #"+drillUser.UserId} — Activity Timeline</div>
              <div style={{fontSize:10.5,color:"#64748b",marginTop:2}}>{drillUser.ClientName || "—"} · Last {days} days · {drillEvents.length} events</div>
            </div>
            <button onClick={function(){setDrillUser(null);setDrillEvents([]);}} style={{padding:"6px 12px",border:"1px solid #e2e8f0",borderRadius:8,background:"#fff",color:"#64748b",cursor:"pointer",fontSize:11}}>✕ Close</button>
          </div>
          <div style={{flex:1,overflow:"auto",padding:"12px 20px"}}>
            {drillEvents.map(function(ev, i){
              var typColor = {login:"#0891b2",logout:"#94a3b8",query_execute:"#2563eb",export:"#059669",dashboard_view:"#6366f1",dashboard_filter:"#8b5cf6",report_view:"#7c3aed"}[ev.ActivityType] || "#64748b";
              var typIcon = {login:"🔓",logout:"🔒",query_execute:"🔍",export:"📥",dashboard_view:"📊",dashboard_filter:"🎛️",report_view:"📑"}[ev.ActivityType] || "•";
              /* Filter selections: render COLLAPSED by default. Long value lists
                 (e.g. 100+ manufacturers) made the timeline unreadable — now the
                 chip shows compact counts ("manufacturer: 132 selected") and the
                 admin clicks it to expand the full, readable selection. */
              var filterParts = (function(){ try { var qp = ev.QueryPayload ? JSON.parse(ev.QueryPayload) : null; if (!qp || typeof qp !== "object") return null;
                var ks = Object.keys(qp).filter(function(k){ var v = qp[k]; return v != null && v !== "" && !(Array.isArray(v) && !v.length); });
                if (!ks.length) return null;
                return ks.map(function(k){ var v = qp[k]; var arr = Array.isArray(v) ? v : null;
                  return { k: k, n: arr ? arr.length : null, txt: arr ? arr.join(", ") : (typeof v === "object" ? JSON.stringify(v) : String(v)) };
                }); } catch(e) { return null; } })();
              var filterShort = filterParts ? filterParts.map(function(p){
                if (p.n != null && p.n > 3) return p.k + ": " + p.n + " selected";
                return p.k + ": " + (p.txt.length > 45 ? p.txt.slice(0, 45) + "…" : p.txt);
              }).join("  ·  ") : null;
              var filterNeedsExpand = filterParts ? filterParts.some(function(p){ return (p.n != null && p.n > 3) || p.txt.length > 45; }) : false;
              return <div key={i} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:"1px solid #f1f5f9"}}>
                <div style={{fontSize:14,width:24,textAlign:"center"}}>{typIcon}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:700,color:typColor,textTransform:"uppercase",letterSpacing:".04em"}}>{ev.ActivityType}{ev.DashboardName?" · "+ev.DashboardName:""}{ev.ExportFormat?" · "+ev.ExportFormat:""}</div>
                  <div style={{fontSize:10,color:"#64748b",marginTop:2}}>{fmtDate(ev.Timestamp)} {ev.StudyType?" · "+ev.StudyType:""} {ev.RowsReturned?" · "+fmtNum(ev.RowsReturned)+" rows":""} {ev.DurationMs?" · "+ev.DurationMs+"ms":""}</div>
                  {filterShort && !filterNeedsExpand && <div style={{fontSize:10.5,color:"#334155",marginTop:3,background:"#f1f5f9",display:"inline-block",padding:"3px 10px",borderRadius:6,fontWeight:600}}>🔎 {filterShort}</div>}
                  {filterShort && filterNeedsExpand && <details style={{marginTop:3}}>
                    <summary style={{fontSize:10.5,color:"#334155",cursor:"pointer",background:"#f1f5f9",display:"inline-block",padding:"3px 10px",borderRadius:6,fontWeight:600}}>
                      🔎 {filterShort} <span style={{color:"#2563eb",fontWeight:800,fontSize:9.5,marginLeft:4}}>click to view all</span>
                    </summary>
                    <div style={{marginTop:5,background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:6,padding:"8px 12px",maxHeight:220,overflow:"auto"}}>
                      {filterParts.map(function(p, pi){
                        return <div key={pi} style={{marginBottom:7}}>
                          <div style={{fontSize:9,fontWeight:800,color:"#0f172a",textTransform:"uppercase",letterSpacing:".05em"}}>{p.k}{p.n != null ? " (" + p.n + ")" : ""}</div>
                          <div style={{fontSize:10,color:"#475569",lineHeight:1.55,marginTop:2,wordBreak:"break-word"}}>{p.txt}</div>
                        </div>;
                      })}
                    </div>
                  </details>}
                  {ev.QueryPayload && <details style={{marginTop:4}}><summary style={{fontSize:9.5,color:"#94a3b8",cursor:"pointer"}}>Query details</summary><pre style={{fontSize:9,color:"#475569",background:"#f8fafc",padding:"6px 10px",borderRadius:4,marginTop:4,overflow:"auto",maxHeight:180}}>{(function(){try{return JSON.stringify(JSON.parse(ev.QueryPayload),null,2);}catch(e){return ev.QueryPayload;}})()}</pre></details>}
                </div>
              </div>;
            })}
            {drillEvents.length === 0 && <div style={{padding:40,textAlign:"center",color:"#94a3b8"}}>No events.</div>}
          </div>
        </div>
      </div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   BIDesignerAdmin — MAIN (template-matching)
══════════════════════════════════════════════════════════════ */
function BIDesignerAdmin(){
  var _st=useState(null),selStudy=_st[0],setSelStudy=_st[1];var _sts=useState([]),st2=_sts[0],setSt2=_sts[1];
  var _rows=useState([]),rows=_rows[0],setRows=_rows[1];var _mp=useState({}),mp=_mp[0],setMp=_mp[1];
  var _ld=useState(false),loading=_ld[0],setLoading=_ld[1];var _err=useState(""),error=_err[0],setError=_err[1];
  var _w=useState([]),widgets=_w[0],setWidgets=_w[1];var _e=useState(null),editWid=_e[0],setEditWid=_e[1];
  var _g=useState(4),gridCols=_g[0],setGridCols=_g[1];
  var _f=useState({}),filters=_f[0],setFilters=_f[1];var _pf=useState({}),pf=_pf[0],setPf=_pf[1];
  var _af=useState([]),afd=_af[0],setAfd=_af[1];
  var _sn=useState(""),saveName=_sn[0],setSaveName=_sn[1];var _sm=useState(""),saveMsg=_sm[0],setSaveMsg=_sm[1];var _sl=useState([]),savedList=_sl[0],setSavedList=_sl[1];
  var _drag=useState(null),dragIdx=_drag[0],setDragIdx=_drag[1];
  useEffect(function(){api._fetch("/api/study-types").then(setSt2).catch(function(){});},[]); /* session-gated now — use api._fetch for x-enc-sid + decrypt */
  useEffect(function(){try{var k2=[];for(var i=0;i<localStorage.length;i++){var k=localStorage.key(i);if(k.indexOf("amsx_bi_admin:")===0)k2.push(k.replace("amsx_bi_admin:",""));}setSavedList(k2);}catch(e){}},[saveMsg]);
  /* BI Designer data fetch — uses api._fetch (not raw fetch) so the x-enc-sid
     header is attached and the response is decrypted. Raw fetch would 401
     against the Layer 1 gate AND fail to read the encrypted response body. */
  useEffect(function(){if(!selStudy)return;setLoading(true);setError("");api._fetch("/api/pharma-data/dashboard?studyType="+selStudy+"&dashKey=bi_designer").then(function(d){setRows(d.rows||[]);setMp(d.mapping||{});setLoading(false);}).catch(function(e){setError(e.message);setLoading(false);});},[selStudy]);
  var schema=useMemo(function(){return _biaBuildSchema(mp);},[mp]);
  var filteredData=useMemo(function(){var r=rows;var pc=schema.periodCols;if(pf.year&&pf.year.length>0&&pc.yearCol)r=r.filter(function(row){return pf.year.indexOf(String(row[pc.yearCol.col]))>=0;});if(pf.quarter&&pf.quarter.length>0&&pc.quarterCol)r=r.filter(function(row){return pf.quarter.indexOf(String(row[pc.quarterCol.col]))>=0;});if(pf.month&&pf.month.length>0&&pc.monthCol)r=r.filter(function(row){return pf.month.indexOf(String(row[pc.monthCol.col]))>=0;});var fK=Object.keys(filters);for(var i=0;i<fK.length;i++){var dim=fK[i];var vs=filters[dim];if(vs&&vs.length>0){var dc=schema.dimMap[dim];if(dc)r=r.filter(function(row){return vs.indexOf(String(row[dc]||""))>=0;});}}return r;},[rows,filters,pf,schema]);
  var addWidget=function(type){var w={id:_biaUID(),type:type,title:"",dimension:"",measure:"",measure2:"",aggFn:"sum",topN:10,customEq:"",colSpan:(type==="kpi"||type==="number")?1:2,rowSpan:(type==="kpi"||type==="number")?1:2};setWidgets(function(p){return p.concat([w]);});setEditWid(w);};
  var updateWidget=function(cfg){setWidgets(function(p){return p.map(function(w){return w.id===cfg.id?cfg:w;});});setEditWid(null);};
  var deleteWidget=function(id){setWidgets(function(p){return p.filter(function(w){return w.id!==id;});});};
  var duplicateWidget=function(w){setWidgets(function(p){return p.concat([Object.assign({},w,{id:_biaUID()})]);});};
  var handleSave=function(){if(!saveName.trim())return;try{localStorage.setItem("amsx_bi_admin:"+saveName.trim(),JSON.stringify({w:widgets,f:afd}));setSaveMsg("Saved!");setTimeout(function(){setSaveMsg("");},2000);}catch(e){}};
  var handleLoad=function(n){try{var d=localStorage.getItem("amsx_bi_admin:"+n);if(d){var p=JSON.parse(d);if(Array.isArray(p))setWidgets(p);else{setWidgets(p.w||[]);setAfd(p.f||[]);}setSaveMsg("Loaded!");setTimeout(function(){setSaveMsg("");},2000);}}catch(e){}};

  /* ── Dashboard background — exact match to existing ── */
  return React.createElement("div",{style:{display:"flex",flexDirection:"column",height:"100%",background:"linear-gradient(160deg,#f0f9ff 0%,#e0f2fe 30%,#f0fdfa 60%,#f0f9ff 100%)",borderRadius:14,overflow:"hidden"}},
    /* HEADER — matches existing dashboard header style */
    React.createElement("div",{style:{display:"flex",alignItems:"center",gap:14,padding:"16px 20px",flexWrap:"wrap"}},
      React.createElement("div",{style:{width:44,height:44,borderRadius:14,background:"linear-gradient(135deg,#1A3F6E,#2D6BA8)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 16px rgba(37,99,235,.3)"}},React.createElement("span",{style:{fontSize:20,color:"#fff"}},"\uD83D\uDEE0\uFE0F")),
      React.createElement("div",null,
        React.createElement("div",{style:{fontSize:16,fontWeight:800,color:"#0f172a"}},"BI Dashboard Designer"),
        React.createElement("div",{style:{fontSize:9.5,color:"#94a3b8"}},selStudy?filteredData.length.toLocaleString()+" rows \u00B7 "+schema.dimensions.length+" dimensions \u00B7 "+schema.measures.length+" measures":"Select a study type to begin")
      ),
      React.createElement("div",{style:{flex:1}}),
      /* Study selector */
      React.createElement("select",{value:selStudy||"",onChange:function(e){setSelStudy(e.target.value||null);setWidgets([]);setFilters({});setPf({});setAfd([]);},style:{padding:"6px 14px",borderRadius:8,border:"1.5px solid #e2e8f0",background:"#fff",color:"#0f172a",fontSize:11,outline:"none",cursor:"pointer",minWidth:180,fontFamily:"inherit"}},React.createElement("option",{value:""},"\u2014 Select Study Type \u2014"),st2.map(function(s){return React.createElement("option",{key:s.id||s.code,value:s.code},(s.category==="private"?"\uD83D\uDD12":"\uD83C\uDF10")+" "+s.name);})),
      React.createElement("div",{style:{width:1,height:24,background:"#e2e8f0"}}),
      /* Grid selector — same style as measure selector in existing dashboards */
      React.createElement("div",{style:{display:"flex",alignItems:"center",gap:3,background:"#f1f5f9",borderRadius:7,padding:2}},
        [2,3,4,6].map(function(n){var on=gridCols===n;return React.createElement("button",{key:n,onClick:function(){setGridCols(n);},style:{padding:"4px 12px",borderRadius:5,border:"none",fontSize:10.5,fontWeight:on?700:500,background:on?"#fff":"transparent",color:on?"#1d4ed8":"#64748b",cursor:"pointer",fontFamily:"inherit",boxShadow:on?"0 1px 2px rgba(0,0,0,.06)":"none"}},n+"col");})
      ),
      React.createElement("div",{style:{width:1,height:24,background:"#e2e8f0"}}),
      React.createElement("input",{value:saveName,onChange:function(e){setSaveName(e.target.value);},placeholder:"Dashboard name...",style:{padding:"5px 12px",borderRadius:7,border:"1.5px solid #e2e8f0",fontSize:10,width:130,outline:"none",fontFamily:"inherit"}}),
      React.createElement("button",{onClick:handleSave,style:{padding:"5px 12px",borderRadius:6,border:"1.5px solid #2563eb",background:"#eff6ff",color:"#2563eb",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}},"\uD83D\uDCBE Save"),
      savedList.length>0&&React.createElement("select",{onChange:function(e){if(e.target.value)handleLoad(e.target.value);e.target.value="";},style:{padding:"5px 10px",borderRadius:6,border:"1.5px solid #e2e8f0",fontSize:10,outline:"none",cursor:"pointer",fontFamily:"inherit"}},React.createElement("option",{value:""},"\uD83D\uDCC2 Load..."),savedList.map(function(n){return React.createElement("option",{key:n,value:n},n);})),
      saveMsg&&saveMsg!=="d"&&React.createElement("span",{style:{fontSize:10,color:"#059669",fontWeight:700}},saveMsg)
    ),
    /* FILTERS */
    rows.length>0&&React.createElement(_BIAFilterPanel,{schema:schema,data:rows,filters:filters,setFilters:setFilters,periodFilters:pf,setPeriodFilters:setPf,activeFilterDims:afd,setActiveFilterDims:setAfd}),
    /* MAIN AREA */
    !selStudy?React.createElement("div",{style:{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}},React.createElement("div",{style:{textAlign:"center",background:"#fff",borderRadius:12,border:"1.5px dashed #e2e8f0",padding:"50px 40px"}},React.createElement("div",{style:{fontSize:32,marginBottom:8}},"\uD83D\uDCC9"),React.createElement("div",{style:{fontSize:13,fontWeight:700,color:"#64748b"}},"Select a study type above"),React.createElement("div",{style:{fontSize:10,color:"#94a3b8",marginTop:4}},"Your data will load automatically from the server"))):
    loading?React.createElement("div",{style:{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}},React.createElement("div",{style:{textAlign:"center"}},React.createElement("div",{style:{width:40,height:40,border:"3px solid #e2e8f0",borderTopColor:"#2563eb",borderRadius:"50%",animation:"spin .8s linear infinite",margin:"0 auto 14px"}}),React.createElement("div",{style:{fontSize:12,fontWeight:700,color:"#64748b"}},"Loading..."),React.createElement("style",null,"@keyframes spin{to{transform:rotate(360deg)}}"))):
    error?React.createElement("div",{style:{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"#dc2626"}},React.createElement("div",null,"\u26A0\uFE0F "+error)):
    React.createElement("div",{style:{flex:1,display:"flex",overflow:"hidden"}},
      /* PALETTE SIDEBAR */
      React.createElement("div",{style:{width:170,background:"#fff",borderRight:"1.5px solid #e2e8f0",padding:10,overflowY:"auto",flexShrink:0,boxShadow:"1px 0 4px rgba(0,0,0,.02)"}},
        React.createElement("div",{style:{fontSize:9,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}},"DRAG TO ADD"),
        ["stat","compare","part","trend","correl","detail"].map(function(cat){var items=_BIA_CHART_TYPES.filter(function(w){return w.cat===cat;});var L={stat:"Statistics",compare:"Compare",part:"Composition",trend:"Trends",correl:"Correlation",detail:"Detail"};return React.createElement("div",{key:cat,style:{marginBottom:8}},React.createElement("div",{style:{fontSize:8,fontWeight:700,color:"#4338ca",textTransform:"uppercase",letterSpacing:".06em",marginBottom:3,paddingLeft:4}},L[cat]),items.map(function(w){return React.createElement("button",{key:w.type,draggable:true,onDragStart:function(e){e.dataTransfer.setData("widgetType",w.type);},onClick:function(){addWidget(w.type);},style:{display:"flex",alignItems:"center",gap:6,width:"100%",padding:"6px 8px",marginBottom:2,borderRadius:8,border:"1px solid #f1f5f9",background:"#fff",cursor:"grab",textAlign:"left",fontFamily:"inherit"}},React.createElement("span",{style:{fontSize:14,width:18,textAlign:"center",opacity:.5}},w.icon),React.createElement("span",{style:{fontSize:10,fontWeight:600,color:"#334155"}},w.label));}));}),
        React.createElement("div",{style:{marginTop:12,borderTop:"1px solid #e2e8f0",paddingTop:8}},
          React.createElement("div",{style:{fontSize:8,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",marginBottom:4}},"DIMENSIONS"),
          schema.dimensions.map(function(d){return React.createElement("div",{key:d,style:{fontSize:8.5,color:"#0284c7",padding:"1px 0"}},"\u25B8 "+d);}),
          React.createElement("div",{style:{fontSize:8,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",marginTop:8,marginBottom:4}},"MEASURES"),
          schema.measures.map(function(m){return React.createElement("div",{key:m,style:{fontSize:8.5,color:"#059669",padding:"1px 0"}},"\u266F "+m);})
        )
      ),
      /* CANVAS */
      React.createElement("div",{style:{flex:1,padding:20,overflow:"auto"},onDragOver:function(e){e.preventDefault();},onDrop:function(e){e.preventDefault();var wt=e.dataTransfer.getData("widgetType");if(wt)addWidget(wt);}},
        widgets.length===0?React.createElement("div",{style:{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",minHeight:300}},React.createElement("div",{style:{textAlign:"center",background:"#fff",borderRadius:12,border:"1.5px dashed #e2e8f0",padding:"50px 40px"}},React.createElement("div",{style:{fontSize:32,marginBottom:8}},"\u229E"),React.createElement("div",{style:{fontSize:13,fontWeight:700,color:"#64748b"}},"Drag widgets here to build your dashboard"),React.createElement("div",{style:{fontSize:10,color:"#94a3b8",marginTop:4}},"Charts will look identical to Market Overview, Leading Manufacturers, etc."))):
        React.createElement("div",{style:{display:"grid",gridTemplateColumns:"repeat("+gridCols+", 1fr)",gap:14,gridAutoRows:180}},
          widgets.map(function(w,idx){
            var isKpi=w.type==="kpi"||w.type==="number";
            var panelStyle=isKpi?_biaCard:_biaChartPanel;
            var accentGrad=isKpi?_BIA_KPI_GRADS[idx%_BIA_KPI_GRADS.length]:"linear-gradient(90deg,"+_BIA_COLORS[idx%_BIA_COLORS.length]+","+_BIA_COLORS[(idx+2)%_BIA_COLORS.length]+")";
            return React.createElement("div",{key:w.id,draggable:true,onDragStart:function(){setDragIdx(idx);},onDragOver:function(e){e.preventDefault();},onDrop:function(e){e.preventDefault();if(dragIdx!==null&&dragIdx!==idx){setWidgets(function(prev){var arr=prev.slice();var item=arr.splice(dragIdx,1)[0];arr.splice(idx,0,item);return arr;});setDragIdx(null);}},onDragEnd:function(){setDragIdx(null);},
              style:{gridColumn:"span "+Math.min(w.colSpan||1,gridCols),gridRow:"span "+(w.rowSpan||1),opacity:dragIdx===idx?.4:1,cursor:"grab",...panelStyle,padding:isKpi?"18px 20px":"14px 16px",display:"flex",flexDirection:"column"}},
              /* Accent bar */
              React.createElement("div",{style:_biaAccent(accentGrad)}),
              /* Header — matches ChartPanel header */
              React.createElement("div",{style:{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:2,marginTop:isKpi?0:4}},
                React.createElement("div",null,
                  React.createElement("div",{style:{fontSize:12,fontWeight:800,color:"#0f172a"}},w.title||"Untitled"),
                  (w.dimension||w.measure)&&React.createElement("div",{style:{fontSize:9,color:"#94a3b8",marginTop:1}},(w.dimension||"")+(w.measure?" \u00B7 "+w.measure:"")+(w.aggFn?" ("+w.aggFn+")":""))
                ),
                React.createElement("div",{style:{display:"flex",gap:2}},
                  React.createElement("button",{onClick:function(){setEditWid(w);},style:{padding:"3px 8px",borderRadius:5,border:"1px solid #e2e8f0",background:"#f8fafc",fontSize:9,color:"#165372",cursor:"pointer",fontFamily:"inherit",fontWeight:700}},"\u270E"),
                  React.createElement("button",{onClick:function(){duplicateWidget(w);},style:{padding:"3px 8px",borderRadius:5,border:"1px solid #e2e8f0",background:"#f8fafc",fontSize:9,color:"#0ea5e9",cursor:"pointer",fontFamily:"inherit",fontWeight:700}},"\u2295"),
                  React.createElement("button",{onClick:function(){deleteWidget(w.id);},style:{padding:"3px 8px",borderRadius:5,border:"1px solid #e2e8f0",background:"#f8fafc",fontSize:9,color:"#dc2626",cursor:"pointer",fontFamily:"inherit",fontWeight:700}},"\u2715")
                )
              ),
              /* Chart content */
              React.createElement("div",{style:{flex:1,minHeight:0}},React.createElement(_BIAWidgetRender,{config:w,data:filteredData,schema:schema}))
            );
          })
        )
      )
    ),
    editWid&&React.createElement(_BIAConfigModal,{config:editWid,schema:schema,onSave:updateWidget,onCancel:function(){setEditWid(null);}})
  );
}

/* ── Config Modal (matching admin style) ── */
function _BIAConfigModal(props){
  var config=props.config,schema=props.schema,onSave=props.onSave,onCancel=props.onCancel;
  var _c=useState(Object.assign({},config)),cfg=_c[0],setCfg=_c[1];
  var upd=function(k,v){setCfg(function(p){var n=Object.assign({},p);n[k]=v;return n;});};
  var lbl={fontSize:9,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".06em",marginBottom:5,display:"block"};
  var sel={width:"100%",padding:"8px 11px",border:"1.5px solid #e2e8f0",borderRadius:7,fontSize:12,fontFamily:"inherit",outline:"none",color:"#0f172a",background:"#fff"};
  var inp=Object.assign({},sel);var sec={marginBottom:14};
  var chipOn=function(on){return{padding:"5px 12px",borderRadius:6,fontSize:10,fontWeight:on?700:500,cursor:"pointer",border:"1.5px solid "+(on?"#2563eb":"#e2e8f0"),background:on?"#eff6ff":"#fff",color:on?"#2563eb":"#64748b",fontFamily:"inherit"};};
  return React.createElement("div",{style:{position:"fixed",inset:0,zIndex:99999,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.3)",backdropFilter:"blur(4px)"},onClick:onCancel},
    React.createElement("div",{style:{background:"#fff",border:"1.5px solid #e2e8f0",borderRadius:14,width:500,maxHeight:"85vh",overflow:"auto",padding:24,boxShadow:"0 24px 60px rgba(0,0,0,.12)"},onClick:function(e){e.stopPropagation();}},
      React.createElement("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18}},React.createElement("div",{style:{fontSize:16,fontWeight:800,color:"#0f172a"}},"Configure Widget"),React.createElement("button",{onClick:onCancel,style:{background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#94a3b8"}},"\u2715")),
      React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Title"),React.createElement("input",{style:inp,value:cfg.title||"",onChange:function(e){upd("title",e.target.value);},placeholder:"Widget title..."})),
      React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Chart Type"),React.createElement("div",{style:{display:"flex",flexWrap:"wrap",gap:5}},_BIA_CHART_TYPES.map(function(w){return React.createElement("button",{key:w.type,onClick:function(){upd("type",w.type);},style:chipOn(cfg.type===w.type)},w.icon+" "+w.label);}))),
      React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Dimension"),React.createElement("select",{style:sel,value:cfg.dimension||"",onChange:function(e){upd("dimension",e.target.value);}},React.createElement("option",{value:""},"\u2014 Select \u2014"),schema.dimensions.map(function(d){return React.createElement("option",{key:d,value:d},d);}))),
      React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Measure"),React.createElement("select",{style:sel,value:cfg.measure||"",onChange:function(e){upd("measure",e.target.value);}},React.createElement("option",{value:""},"\u2014 Select \u2014"),schema.measures.map(function(m){return React.createElement("option",{key:m,value:m},m);}))),
      (cfg.type==="composed"||cfg.type==="scatter"||cfg.type==="stackbar")&&React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Second Measure"),React.createElement("select",{style:sel,value:cfg.measure2||"",onChange:function(e){upd("measure2",e.target.value);}},React.createElement("option",{value:""},"\u2014 Select \u2014"),schema.measures.map(function(m){return React.createElement("option",{key:m,value:m},m);}))),
      React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Aggregation"),React.createElement("div",{style:{display:"flex",flexWrap:"wrap",gap:4}},_BIA_AGG_FNS.map(function(a){return React.createElement("button",{key:a.key,onClick:function(){upd("aggFn",a.key);},style:{...chipOn(cfg.aggFn===a.key),display:"flex",alignItems:"center",gap:3}},React.createElement("span",{style:{fontWeight:800}},a.icon),a.label);}))),
      cfg.aggFn==="custom"&&React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Custom Equation"),React.createElement("input",{style:inp,value:cfg.customEq||"",onChange:function(e){upd("customEq",e.target.value);},placeholder:"e.g. Value_USD / Units"}),React.createElement("div",{style:{fontSize:8,color:"#94a3b8",marginTop:4}},"Variables: "+schema.measures.map(function(m){return m.replace(/[^a-zA-Z0-9_]/g,"_");}).join(", "))),
      cfg.type!=="kpi"&&cfg.type!=="number"&&React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Top N"),React.createElement("input",{type:"number",style:{...inp,width:80},value:cfg.topN||"",onChange:function(e){upd("topN",parseInt(e.target.value)||null);},placeholder:"10"})),
      React.createElement("div",{style:sec},React.createElement("label",{style:lbl},"Size"),React.createElement("div",{style:{display:"flex",gap:5}},
        [{w:1,h:1,l:"1\u00D71"},{w:2,h:1,l:"2\u00D71"},{w:2,h:2,l:"2\u00D72"},{w:3,h:2,l:"3\u00D72"},{w:4,h:2,l:"4\u00D72"},{w:1,h:2,l:"1\u00D72"},{w:3,h:1,l:"3\u00D71"},{w:4,h:3,l:"4\u00D73"}].map(function(s){return React.createElement("button",{key:s.l,onClick:function(){upd("colSpan",s.w);upd("rowSpan",s.h);},style:chipOn(cfg.colSpan===s.w&&cfg.rowSpan===s.h)},s.l);}))),
      React.createElement("div",{style:{display:"flex",gap:8,justifyContent:"flex-end",marginTop:20}},
        React.createElement("button",{onClick:onCancel,style:{padding:"8px 18px",borderRadius:7,border:"1.5px solid #e2e8f0",background:"#fff",color:"#64748b",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}},"Cancel"),
        React.createElement("button",{onClick:function(){onSave(cfg);},style:{padding:"8px 18px",borderRadius:7,border:"none",background:"#2563eb",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}},"Save Widget"))
    ));
}


function AdminPage({ onGoPortal, onSignOut, clients, setClients, users, setUsers, user }) {
  /* ── Delegated admin permissions ──
     user.adminPerms == null  → FULL admin (everything incl. Admin Team screen)
     user.adminPerms == array → limited to those admin sections (server enforces too) */
  var _adminPerms = (user && user.adminPerms) || null;
  var _isFullAdmin = !_adminPerms;
  var _canSec = function(k) {
    if (_isFullAdmin || _adminPerms.indexOf(k) >= 0) return true;
    /* r45: any per-tab analytics grant opens the User Analytics section */
    if (k === "analytics") { for (var i9 = 0; i9 < _adminPerms.length; i9++) { if (String(_adminPerms[i9]).indexOf("analytics:") === 0) return true; } }
    /* r96: "report_requests" is its own grant (checked above) — no longer implied by analytics / clients */
    return false;
  };
  var _anTabs9 = _analyticsTabsOf9(_adminPerms, _isFullAdmin); /* r45: granted analytics tabs (null = all) */
  /* Report permissions are FULL-ADMIN-ONLY (Noura 2026-10-06) — the old
     "report_perms" team grant is retired; stored grants are ignored */
  var _canRep = _isFullAdmin;
  /* Study scope: null = all studies; array = this admin only manages users of these studies */
  var _adminScope = (!_isFullAdmin && user && user.adminScope && user.adminScope.length) ? user.adminScope : null;
  var ADMIN_SECTIONS = [
    {key:"clients", label:"Client Management", icon:"\uD83C\uDFE2"},
    {key:"users", label:"User Management", icon:"\uD83D\uDC64"},
    {key:"devices", label:"Device Authorization", icon:"\uD83D\uDCBB"},
    {key:"features", label:"Feature Management", icon:"\u2699"},
    {key:"dashboards", label:"Dashboard Config", icon:"\uD83D\uDCCA"},
    {key:"bi_designer", label:"BI Dashboard Designer", icon:"\uD83D\uDEE0\uFE0F"},
    {key:"analytics", label:"User Analytics", icon:"\uD83D\uDCC8"},
    {key:"contracts", label:"Contract Analysis", icon:"\uD83D\uDCD1"},
    {key:"report_requests", label:"Report Requests", icon:"\uD83D\uDCEC"}   /* r96: answer full-report requests of the admin's studies + receive their e-mail notifications */
  ];
  var [tab, setTab] = useState("clients");
  var [clientModal, setClientModal] = useState(null);
  var [userModal, setUserModal] = useState(null);
  var [search, setSearch] = useState("");
  /* Per-column filters for the Users table — each key maps to a text/select filter
     that AND's together with the global `search` and with each other. Empty string
     means "no filter on this column". */
  var [userColFilter, setUserColFilter] = useState({
    user:"", client:"", role:"", status:"", study:""
  });
  var [userAdvFilter, setUserAdvFilter] = useState({region:"", gov:"", dash:"", reports:"", exp:"", contract:""});
  var [confirmDel, setConfirmDel] = useState(null);
  var [pwResetId, setPwResetId] = useState(null);
  var [pwResetVal, setPwResetVal] = useState("");
  var [pwResetMsg, setPwResetMsg] = useState("");

  /* ── Feature Management State ── */
  var [studyTypes, setStudyTypes] = useState([]);
  var [allFeatures, setAllFeatures] = useState([]);
  var [allDashboards, setAllDashboards] = useState([]);
  var [featStudy, setFeatStudy] = useState(null);
  var [featModal, setFeatModal] = useState(null);
  var [copyFrom, setCopyFrom] = useState("");
  var [dashStudy, setDashStudy] = useState(null);
  var [dashModal, setDashModal] = useState(null);

  /* ── Device Management State ── */
  var [devices, setDevices] = useState([]);
  var [devAn, setDevAn] = useState(null); /* device login-analysis panel: {id, loading, data, error} */
  var [deviceNotifs, setDeviceNotifs] = useState([]);
  /* r87: full-report requests from sample mode (ReportOrders) — badge + banner */
  var [repOrders, setRepOrders] = useState([]);
  var [repOrdersAll, setRepOrdersAll] = useState(null);     /* the tab's list (new + handled, 90 days) */
  var [repOrdFilter, setRepOrdFilter] = useState("new");
  var reloadRepOrders = function() {
    api.getReportOrders("new", 365).then(function(d){ setRepOrders((d && d.orders) || []); }).catch(function(){});
  };
  var reloadRepOrdersAll = function(st) {
    api.getReportOrders(st || repOrdFilter, 365).then(function(d){ setRepOrdersAll((d && d.orders) || []); }).catch(function(){ setRepOrdersAll([]); });
  };
  var handleRepOrder = function(id, st) {
    api.setReportOrderStatus(id, st).then(function(){ reloadRepOrders(); reloadRepOrdersAll(); }).catch(function(e){ alert(e.message); });
  };
  /* r95: conversation + e-mail answer per request; server mailer status */
  var [repConv, setRepConv] = useState(null);      /* { id, loading, data:{order,messages,mail,template}, to, subject, body, markHandled, busy, err, result } */
  var [mailStatus, setMailStatus] = useState(null);
  var openRepConv = function(id) {
    setRepConv({ id: id, loading: true });
    api.getReportOrderMessages(id).then(function(d){
      var hasReply = ((d && d.messages) || []).some(function(m){ return m.Kind === "reply"; });
      setRepConv({ id: id, loading: false, data: d, to: (d.order && d.order.UserEmail) || "", subject: (d.template && d.template.subject) || "", body: (d.template && d.template.body) || "",
                   markHandled: !hasReply, busy: false, err: "", result: null });
    }).catch(function(e){ setRepConv({ id: id, loading: false, err: (e && e.message) || "Could not load the conversation" }); });
  };
  var sendRepReply = function(manual) {
    var c = repConv; if (!c || c.busy || !c.data) return;
    if (!String(c.to || "").trim() || !String(c.subject || "").trim() || !String(c.body || "").trim()) { setRepConv(Object.assign({}, c, { err: "Recipient, subject and message are required" })); return; }
    setRepConv(Object.assign({}, c, { busy: true, err: "" }));
    api.replyReportOrder(c.id, { to: c.to, subject: c.subject, body: c.body, markHandled: !!c.markHandled, manual: !!manual }).then(function(r){
      reloadRepOrders(); reloadRepOrdersAll();
      api.getReportOrderMessages(c.id).then(function(d){ setRepConv(Object.assign({}, c, { busy: false, data: d, result: r, err: (r && !r.sent && !manual) ? ("Not sent: " + (r.error || "mailer error") + " \u2014 the answer is recorded; you can open it in your e-mail app.") : "" })); })
        .catch(function(){ setRepConv(Object.assign({}, c, { busy: false, result: r })); });
    }).catch(function(e){ setRepConv(Object.assign({}, c, { busy: false, err: (e && e.message) || "Could not send" })); });
  };
  var _mailtoOf9 = function(c) { return "mailto:" + encodeURIComponent(String(c.to || "")) + "?subject=" + encodeURIComponent(String(c.subject || "")) + "&body=" + encodeURIComponent(String(c.body || "")); };
  var _kindLbl9 = { ack: ["AUTO · ACKNOWLEDGEMENT", "#0e7490", "#ecfeff"], notify: ["AUTO · ADMIN NOTICE", "#6d28d9", "#f5f3ff"], reply: ["ANSWER", "#005487", "#eff6ff"], status: ["STATUS", "#475569", "#f8fafc"] };
  var [devFilter, setDevFilter] = useState("all"); /* all | pending | approved | revoked */
  var [devLive, setDevLive] = useState(null); /* {activeNow, activeUsers, today, todayUsers} */
  var [teamList, setTeamList] = useState([]);
  /* r97: partners (reseller tenants) */
  var [partners, setPartners] = useState([]);
  var [partnerModal, setPartnerModal] = useState(null);   /* null | { id, name, code, contactEmail, terms, usageMailDay, status, busy, err, logoFile } */
  var [pu, setPu] = useState({ partnerId: "", from: "", to: "", data: null, loading: false, err: "", sent: null });
  var _isPartnerAdmin = !!(user && user.partnerId);
  var reloadPartners = function() { api.getPartners().then(function(l){ setPartners(Array.isArray(l) ? l : []); }).catch(function(){ setPartners([]); }); };
  var _partnerName9 = function(id){ var p = partners.filter(function(x){ return String(x.id) === String(id); })[0]; return p ? p.name : (id ? "partner #" + id : ""); };
  var loadPartnerUsage = function(pid, from, to) {
    if (!pid && !_isPartnerAdmin) return;
    setPu(function(prev){ return Object.assign({}, prev, { partnerId: pid, from: from, to: to, loading: true, err: "", sent: null }); });
    api.getPartnerUsage(pid, from, to).then(function(d){ setPu(function(prev){ return Object.assign({}, prev, { data: d, loading: false, from: d.from, to: d.to }); }); })
      .catch(function(e){ setPu(function(prev){ return Object.assign({}, prev, { loading: false, err: (e && e.message) || "Could not load" }); }); });
  };
  var [teamModal, setTeamModal] = useState(null); /* null | working object */
  var [teamPicker, setTeamPicker] = useState(null); /* null | {q:""} — pick a portal user to promote */
  /* Master export tab state */
  var [meUserQ, setMeUserQ] = useState("");
  var [meUser, setMeUser] = useState(null);   /* selected user object */
  var [meStudy, setMeStudy] = useState("");
  var [meGeo, setMeGeo] = useState("country");
  var [meLayout, setMeLayout] = useState("wide"); /* wide = quarters as columns (fewer rows) */
  var [mePeriod, setMePeriod] = useState({ptype:"Quarterly", fromY:"", fromS:"", toY:"", toS:"", sels:[]}); /* master export period type + range; sels = explicit MAT/YTD period picks ["YYYY-Q"] */
  var [meStruct, setMeStruct] = useState(null);
  var [meBusy, setMeBusy] = useState(false);
  var [meMsg, setMeMsg] = useState("");
  var [meSelDims, setMeSelDims] = useState(null); /* array of selected dim keys (null = all) */
  var [meFiles, setMeFiles] = useState(null); /* generation result: {pairId, rows, marks, files:[{token,name,done}]} */
  var meFetchStruct = function(dimsSel, perOv) {
    setMeBusy(true); setMeMsg("");
    api.getMasterStructure(meUser.id, meStudy, meGeo, dimsSel, meLayout, perOv || mePeriod)
      .then(function(s){ setMeStruct(s); setMeSelDims((s.dims || []).filter(function(d){ return d.selected; }).map(function(d){ return d.key; })); setMeBusy(false); })
      .catch(function(e){ setMeMsg("Error: " + e.message); setMeBusy(false); });
  };
  var reloadTeam = function() { api.getAdminTeam().then(setTeamList).catch(function(e){ console.error(e); }); };
  /* Limited admin landing: jump to the first section they can access */
  useEffect(function() {
    if (tab !== "team" && !_canSec(tab)) {
      var _ord = ["clients","users","devices","features","dashboards","bi_designer","analytics","contracts"];
      for (var oi = 0; oi < _ord.length; oi++) { if (_canSec(_ord[oi])) { setTab(_ord[oi]); break; } }
    }
  }, []);

  /* Load study types and features when tab switches */
  useEffect(function() {
    if (tab === "features" || tab === "dashboards") {
      api.getStudyTypes().then(setStudyTypes).catch(function(e){ console.error(e); });
      api.getStudyFeatures().then(setAllFeatures).catch(function(e){ console.error(e); });
      api.getStudyDashboards().then(setAllDashboards).catch(function(e){ console.error(e); });
    }
    if (tab === "devices") {
      reloadDevices();
    }
    if (tab === "team") {
      reloadTeam();
      if (!studyTypes.length) api.getStudyTypes().then(setStudyTypes).catch(function(e){ console.error(e); });
    }
    if (tab === "partners" || tab === "partner_usage" || tab === "team" || tab === "clients") { if (_isFullAdmin || _isPartnerAdmin) reloadPartners(); }   /* r97 */
    if (tab === "partner_usage") { var _d9 = new Date(); _d9.setUTCDate(1); _d9.setUTCMonth(_d9.getUTCMonth() - 1); var _m9 = _d9.toISOString().slice(0, 7); loadPartnerUsage(_isPartnerAdmin ? "" : (pu.partnerId || ""), pu.from || _m9, pu.to || _m9); }
  }, [tab]);
  var reloadFeatures = function() { api.getStudyFeatures().then(setAllFeatures).catch(function(e){ console.error(e); }); };
  var reloadDashboards = function() { api.getStudyDashboards().then(setAllDashboards).catch(function(e){ console.error(e); }); };
  var reloadDevices = function() {
    api.getDevices().then(setDevices).catch(function(e){ console.error(e); });
    api.getDeviceNotifications().then(setDeviceNotifs).catch(function(e){ console.error(e); });
    api.getDeviceLiveStats().then(setDevLive).catch(function(e){ console.error(e); });
  };
  /* Pending-device alert must reach the admin WITHOUT opening the Devices tab:
     load on mount and refresh every 60s while the admin panel is open. */
  useEffect(function() {
    if (!_canSec("devices")) return; /* limited admin without Device Authorization */
    reloadDevices();
    var _dnIv = setInterval(function() {
      api.getDeviceNotifications().then(setDeviceNotifs).catch(function(e){});
      api.getDeviceLiveStats().then(setDevLive).catch(function(e){});
    }, 60000);
    return function() { clearInterval(_dnIv); };
  }, []);
  useEffect(function() {
    if (!_canSec("report_requests")) return;
    reloadRepOrders();
    var _roIv = setInterval(reloadRepOrders, 60000);
    return function() { clearInterval(_roIv); };
  }, []);
  useEffect(function() { if (tab === "report_requests") reloadRepOrdersAll(repOrdFilter); }, [tab, repOrdFilter]);
  useEffect(function() { if (tab === "report_requests" || tab === "partner_usage") api.getMailStatus().then(setMailStatus).catch(function(){ setMailStatus(null); }); }, [tab]);

  /* ── Reload helpers ── */
  var reloadClients = function() { api.getClients().then(setClients).catch(function(e){ console.error(e); }); };
  var reloadUsers = function() { api.getUsers().then(setUsers).catch(function(e){ console.error(e); }); };

  /* ── Multi-admin freshness (Noura 2026-09-30) ──
     Clients/users were fetched ONCE at login, so a client or user added by
     ANOTHER team admin never appeared for admins who were already signed in
     (until they logged out and back in). Refetch whenever the admin panel
     opens or the Clients/Users tab is entered, and whenever the browser
     window regains focus while one of those tabs is showing — so switching
     back from a colleague's message straight to the panel shows their work. */
  useEffect(function() {
    if (tab === "clients" || tab === "users" || tab === "master_export") { reloadClients(); reloadUsers(); }
  }, [tab]);
  useEffect(function() {
    var _adFresh = function() {
      try {
        if (document.visibilityState === "hidden") return;
        if (tab === "clients" || tab === "users" || tab === "master_export") { reloadClients(); reloadUsers(); }
      } catch (e9) {}
    };
    window.addEventListener("focus", _adFresh);
    document.addEventListener("visibilitychange", _adFresh);
    return function() {
      window.removeEventListener("focus", _adFresh);
      document.removeEventListener("visibilitychange", _adFresh);
    };
  }, [tab]);

  /* ── Client CRUD (API-backed) ── */
  var saveClient = function(c) {
    api.saveClient(c).then(function(r) {
      reloadClients();
      setClientModal(null);
    }).catch(function(e){ alert("Save failed: " + e.message); });
  };
  var toggleClientStatus = function(id) {
    api.toggleClient(id).then(reloadClients).catch(function(e){ console.error(e); alert(e.message); });
  };
  var deleteClient = function(id) {
    api.deleteClient(id).then(function() {
      reloadClients();
      reloadUsers();
      setConfirmDel(null);
    }).catch(function(e){ console.error(e); setConfirmDel(null); alert(e.message); });
  };

  /* ── User CRUD (API-backed) ── */
  var saveUser = function(u) {
    api.saveUser(u).then(function(r) {
      reloadUsers();
      setUserModal(null);
    }).catch(function(e){ alert("Save failed: " + e.message); });
  };
  var toggleUserStatus = function(id) {
    api.toggleUser(id).then(reloadUsers).catch(function(e){ console.error(e); alert(e.message); });
  };
  var deleteUser = function(id) {
    api.deleteUser(id).then(function() {
      reloadUsers();
      setConfirmDel(null);
    }).catch(function(e){ console.error(e); setConfirmDel(null); alert(e.message); });
  };
  var resetExtract = function(id) {
    /* Reset extract count via direct update */
    var usr = null;
    for (var i = 0; i < users.length; i++) { if (users[i].id === id) { usr = users[i]; break; } }
    if (usr) { api.saveUser({...usr, extractUsed: 0}).then(reloadUsers); }
  };
  var resetPassword = function(id) {
    if (!pwResetVal.trim()) { setPwResetMsg("Password cannot be empty."); return; }
    var usr = null;
    for (var i = 0; i < users.length; i++) { if (users[i].id === id) { usr = users[i]; break; } }
    if (usr) {
      api.saveUser({...usr, password: pwResetVal.trim()}).then(function() {
        setPwResetMsg("Password reset successfully!");
        setTimeout(function(){ setPwResetId(null); setPwResetVal(""); setPwResetMsg(""); }, 1500);
      }).catch(function(e){ setPwResetMsg("Failed: " + e.message); });
    }
  };

  /* ── Helpers ── */
  var clientName = function(cid) {
    for (var i = 0; i < clients.length; i++) { if (clients[i].id === cid) return clients[i].name; }
    return "—";
  };
  var clientUsers = function(cid) {
    return users.filter(function(u) { return u.clientId === cid && u.status !== "deleted"; }).length;
  };

  /* ── Filter ── */
  var q = search.toLowerCase();
  var filtClients = clients.filter(function(c) {
    if (c.status === "deleted") return false;
    /* owner company group is INVISIBLE to every admin except the FULL admin
       (Noura 2026-10-04) — the server already omits it; this is the backstop */
    if (!_isFullAdmin && c.ownerGroup) return false;
    if (!(c.name.toLowerCase().indexOf(q) >= 0 || c.email.toLowerCase().indexOf(q) >= 0)) return false;
    /* Study-scoped admins SEE every client (Noura 2026-09-30: "if the client or
       user is added, all admin panels should see them") — the scope only limits
       what they can EDIT: out-of-scope study rows are locked in the modal and
       the server preserves them verbatim; pause/delete of a client with
       out-of-scope studies is refused server-side. */
    return true;
  });
  /* User table filtering: global search (q) + per-column filters (userColFilter).
     All filters are AND'd. Each filter is a case-insensitive substring match,
     except status which is an exact-equal dropdown when set. */
  var _ucf = userColFilter;
  var _ucfUser   = (_ucf.user   || "").toLowerCase().trim();
  var _ucfClient = (_ucf.client || "").toLowerCase().trim();
  var _ucfRole   = (_ucf.role   || "").toLowerCase().trim();
  var _ucfStatus = (_ucf.status || "").toLowerCase().trim();
  var _ucfStudy  = _ucf.study || "";
  var _uaf = userAdvFilter;
  /* Effective per-study access for a user: the client's studyAccess narrowed by the
     user's per-study overrides (same semantics as the login merge — an override with
     an empty features list means NONE; client features empty with no override = ALL). */
  var _clientById = function(cid) { for (var ci = 0; ci < clients.length; ci++) { if (clients[ci].id === cid) return clients[ci]; } return null; };
  /* Owner company group (Noura 2026-10-01, tightened 2026-10-04): full-admin-only
     clients — INVISIBLE to team admins (lists are filtered server-side AND here);
     the action guards below remain as the write-side backstop. */
  var _ownerLocked = function(cid) { if (_isFullAdmin) return false; var c9 = _clientById(cid); return !!(c9 && c9.ownerGroup); };
  var _ownerAlert = function() { alert("This client is the owner company group — it is managed by the full admin account only."); };
  /* Contract status from an effective end date: active / expiring (<=30d) / expired */
  var _ctStatus = function(ce) {
    if (!ce) return "active";
    var _t = new Date().toISOString().slice(0, 10);
    if (_t > ce) return "expired";
    var _d = Math.round((new Date(ce + "T00:00:00Z") - new Date(_t + "T00:00:00Z")) / 86400000);
    return _d <= 30 ? "expiring" : "active";
  };
  var userEffAccess = function(u) {
    var c = _clientById(u.clientId);
    var base = (c && c.studyAccess) || [];
    var ovs = u.userStudyAccess || [];
    /* Mirror the LOGIN rule (user-side study restriction): when the user has ANY
       per-user study entries, that list is AUTHORITATIVE — client studies without a
       matching user entry are dropped (code-level match, same as the server). A user
       with NO per-user entries inherits the full client list. */
    if (ovs.length) {
      var _uc = {};
      ovs.forEach(function(o){ _uc[o.studyTypeCode] = true; });
      base = base.filter(function(sa){ return !!_uc[sa.studyTypeCode]; });
    }
    return base.map(function(sa) {
      var ov = null;
      for (var oi = 0; oi < ovs.length; oi++) {
        if (ovs[oi].country === sa.country && ovs[oi].studyTypeCode === sa.studyTypeCode) { ov = ovs[oi]; break; }
      }
      var feats = ov ? (ov.features || []) : (sa.features || []);
      var featsAll = !ov && !(sa.features || []).length;   /* empty client list = all dims allowed */
      var dash = ov ? (ov.dashboards || []) : (sa.dashboards || []);
      var reps = ov ? (ov.reports || []) : (sa.reports || []);
      /* Effective contract window for this study: override > per-study > client-wide */
      var cs = (ov && ov.contractStart) || sa.contractStart || (c && c.contractStart) || "";
      var ce = (ov && ov.contractEnd) || sa.contractEnd || (c && c.contractEnd) || "";
      return {
        key: sa.country + "/" + sa.studyTypeCode,
        country: sa.country, code: sa.studyTypeCode,
        region: featsAll || feats.indexOf("region") >= 0,
        gov: featsAll || feats.indexOf("governorate") >= 0,
        dash: dash.length, reps: reps.length, ov: !!ov,
        featList: featsAll ? null : feats, dashList: dash, repList: reps,
        cs: cs, ce: ce
      };
    });
  };
  /* Distinct study options across all clients — feeds the Study Access filter */
  var userStudyOptions = (function() {
    var seen = {}, out = [];
    clients.forEach(function(c) {
      (c.studyAccess || []).forEach(function(sa) {
        var k = sa.country + "/" + sa.studyTypeCode;
        if (!seen[k]) { seen[k] = 1; out.push(k); }
      });
    });
    out.sort();
    return out;
  })();
  var filtUsers = users.filter(function(u) {
    if (u.status === "deleted") return false;
    /* owner company group users are INVISIBLE to non-full admins (Noura 2026-10-04) */
    if (!_isFullAdmin && _ownerLocked(u.clientId)) return false;
    /* Global search box (legacy) */
    if (q && !(u.name.toLowerCase().indexOf(q) >= 0 || u.email.toLowerCase().indexOf(q) >= 0 || clientName(u.clientId).toLowerCase().indexOf(q) >= 0)) return false;
    /* Per-column filters */
    if (_ucfUser) {
      var nameMatch  = (u.name  || "").toLowerCase().indexOf(_ucfUser) >= 0;
      var emailMatch = (u.email || "").toLowerCase().indexOf(_ucfUser) >= 0;
      if (!nameMatch && !emailMatch) return false;
    }
    if (_ucfClient && clientName(u.clientId).toLowerCase().indexOf(_ucfClient) < 0) return false;
    if (_ucfRole   && (u.role || "").toLowerCase() !== _ucfRole) return false;
    if (_ucfStatus && (u.status || "").toLowerCase() !== _ucfStatus) return false;
    /* Users whose client/overrides grant NO study at all are hidden from the list
       (they cannot use the platform anyway). Admin accounts stay visible. */
    if ((u.role || "").toLowerCase() !== "admin" && userEffAccess(u).length === 0) return false;
    /* Study-scoped admins SEE every user (Noura 2026-09-30) — the scope only
       limits what they can EDIT: out-of-scope study rows are locked in the
       modal and preserved server-side; pause/delete of a user with
       out-of-scope studies is refused server-side. */
    if (_ucfStudy) {
      var _acc9 = userEffAccess(u);
      var _hasSt = false;
      for (var _ai = 0; _ai < _acc9.length; _ai++) { if (_acc9[_ai].key === _ucfStudy) { _hasSt = true; break; } }
      if (!_hasSt) return false;
    }
    /* Advanced access filters. Scoped to the selected study when one is chosen,
       otherwise "yes" means: in ANY of the user's studies. */
    if (_uaf.region || _uaf.gov || _uaf.dash || _uaf.reports || _uaf.contract) {
      var _accA = userEffAccess(u);
      var _scope = _ucfStudy ? _accA.filter(function(x){ return x.key === _ucfStudy; }) : _accA;
      var _some = function(fn){ for (var _i2 = 0; _i2 < _scope.length; _i2++) { if (fn(_scope[_i2])) return true; } return false; };
      var _yn = function(val, has){ if (!val) return true; return val === "yes" ? has : !has; };
      if (!_yn(_uaf.region,  _some(function(x){ return x.region; }))) return false;
      if (!_yn(_uaf.gov,     _some(function(x){ return x.gov; }))) return false;
      if (!_yn(_uaf.dash,    _some(function(x){ return x.dash > 0; }))) return false;
      if (!_yn(_uaf.reports, _some(function(x){ return x.reps > 0; }))) return false;
      if (_uaf.contract) {
        var _sts;
        if (_scope.length) { _sts = _scope.map(function(x){ return _ctStatus(x.ce); }); }
        else { var _c9 = _clientById(u.clientId); _sts = [_ctStatus((_c9 && _c9.contractEnd) || "")]; }
        if (_sts.indexOf(_uaf.contract) < 0) return false;
      }
    }
    if (_uaf.exp) {
      var _c8 = _clientById(u.clientId);
      var _effExp = u.allowExport != null ? u.allowExport : !(_c8 && _c8.allowExport === false);
      if (_uaf.exp === "yes" ? !_effExp : _effExp) return false;
    }
    return true;
  });
  /* Excel export of the FILTERED user list, one row per user per study,
     with access flags, export permissions, limits and contract windows. */
  var exportUsersList = function() {
    var _pw = "";
    try { _pw = window.prompt("Protect the Excel file with a password?\n\nEnter a password, or leave EMPTY for a normal file:", "") ; } catch (e9) { _pw = ""; }
    if (_pw === null) return;   /* Cancel pressed - no export */
    _pw = String(_pw || "");
    getXLSX().then(function(XLSX) {
      var today = new Date().toISOString().slice(0, 10);
      var hdr = ["User", "Email", "Client", "Role", "Status", "Country", "Study",
                 "Region", "Governorate", "Dashboards", "Dashboard List", "Reports", "Report List",
                 "Features (dims)", "User Override", "Export Allowed", "Grid Export",
                 "Extracts Used", "Extract Limit", "Row Export Limit",
                 "Contract Start", "Contract End", "Contract Status"];
      var aoa = [hdr];
      filtUsers.forEach(function(u) {
        var c = _clientById(u.clientId);
        var effExp = u.allowExport != null ? u.allowExport : !(c && c.allowExport === false);
        var effGrid = u.allowGridExport != null ? u.allowGridExport : !(c && c.allowGridExport === false);
        var acc = userEffAccess(u);
        var scope = _ucfStudy ? acc.filter(function(x){ return x.key === _ucfStudy; }) : acc;
        var mkRow = function(a) {
          return [u.name, u.email, (c && c.name) || "", u.role, u.status,
            a ? a.country : "", a ? a.code : "",
            a ? (a.region ? "Yes" : "No") : "", a ? (a.gov ? "Yes" : "No") : "",
            a ? a.dash : "", a ? (a.dashList || []).join(", ") : "",
            a ? a.reps : "", a ? (a.repList || []).join(", ") : "",
            a ? (a.featList == null ? "ALL" : (a.featList.join(", ") || "NONE")) : "",
            a ? (a.ov ? "Yes" : "No") : "",
            effExp ? "Yes" : "No", effGrid ? "Yes" : "No",
            u.extractUsed, u.extractLimit, u.rowExportLimit || 0,
            a ? (a.cs || "") : ((c && c.contractStart) || ""),
            a ? (a.ce || "") : ((c && c.contractEnd) || ""),
            _ctStatus(a ? a.ce : ((c && c.contractEnd) || ""))];
        };
        if (scope.length) { scope.forEach(function(a){ aoa.push(mkRow(a)); }); }
        else { aoa.push(mkRow(null)); }
      });
      var ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = hdr.map(function(h){ return { wch: Math.max(10, Math.min(32, String(h).length + 8)) }; });
      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      var fname = "AMSX_Users_Access_" + today + ".xlsx";
      if (!_pw) { XLSX.writeFile(wb, fname); return; }
      /* Password-protected: re-encode through xlsx-populate (standard Office encryption —
         Excel asks for the password on open). Loaded at runtime as a STATIC file from the
         public folder (/xlsx-populate.min.js) — no npm dependency, build never breaks. */
      var _buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      var _loadXP = function(){
        return new Promise(function(resolve, reject){
          if (window.XlsxPopulate) return resolve(window.XlsxPopulate);
          var s9 = document.createElement("script");
          s9.src = "/xlsx-populate.min.js";
          s9.onload = function(){ if (window.XlsxPopulate) resolve(window.XlsxPopulate); else reject(new Error("xlsx-populate loaded but global missing")); };
          s9.onerror = function(){ reject(new Error("could not load /xlsx-populate.min.js — copy it into the client public folder")); };
          document.head.appendChild(s9);
        });
      };
      _loadXP()
        .then(function(XP){ return XP.fromDataAsync(_buf).then(function(wb2){ return wb2.outputAsync({ password: _pw }); }); })
        .then(function(blob){
          var a9 = document.createElement("a");
          a9.href = URL.createObjectURL(blob);
          a9.download = fname;
          document.body.appendChild(a9); a9.click();
          setTimeout(function(){ try { URL.revokeObjectURL(a9.href); document.body.removeChild(a9); } catch (_e) {} }, 2000);
        })
        .catch(function(err){
          try { alert("Password protection unavailable (" + ((err && err.message) || err) + ").\nExported WITHOUT a password instead.\n\nTo enable: copy xlsx-populate.min.js into the client public folder (next to logo.png)."); } catch (_e) {}
          XLSX.writeFile(wb, fname);
        });
    });
  };

  var thS = {padding:"8px 12px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:".05em",
             textAlign:"left", background:"#f8fafc", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap", position:"sticky", top:0, zIndex:2};
  var tdS = {padding:"10px 12px", fontSize:11.5, borderBottom:"1px solid #f1f5f9", verticalAlign:"middle"};
  var tdBtnS = function(bg, co, bdr) {
    return {padding:"3px 8px", border:"1px solid "+(bdr||bg), borderRadius:5, background:bg, color:co,
            fontSize:9.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap"};
  };

  return (
    <div style={{display:"flex", flexDirection:"column", height:"100vh", overflow:"hidden",
                 fontFamily:"'Plus Jakarta Sans',system-ui,-apple-system,sans-serif", fontSize:13, color:"#1e293b", background:"#f0f4f8"}}>

      {/* ── Admin Header ── */}
      <header style={{height:50, background:"#fff", borderBottom:"1.5px solid #e2e8f0",
                      display:"flex", alignItems:"center", padding:"0 18px", gap:12, flexShrink:0, zIndex:20, boxShadow:"0 1px 4px rgba(0,0,0,.04)"}}>
        <div style={{display:"flex", alignItems:"center", gap:8}}>
          {(user && user.partner) ? <PartnerHeaderBrand partner={user.partner} admin={!!user.partnerId} chip /> : <AmsxLogo height={28} />}
          <span style={{fontSize:9, background:"#2563eb", color:"#fff", borderRadius:4, padding:"2px 6px", fontWeight:700, marginLeft:4}}>ADMIN</span>
        </div>
        <div style={{flex:1}} />
        <div style={{fontSize:11, color:"#64748b", marginRight:8}}>{clients.filter(function(c){return c.status!=="deleted";}).length} Clients · {users.filter(function(u){return u.status!=="deleted";}).length} Users</div>
        {onGoPortal && (
          <button onClick={onGoPortal}
            style={{padding:"5px 12px", border:"1.5px solid #2563eb", borderRadius:6, background:"#eff6ff",
                    color:"#2563eb", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>
            📊 Go to Portal
          </button>
        )}
        {onSignOut && (
          <button onClick={onSignOut}
            style={{padding:"5px 12px", border:"1.5px solid #e2e8f0", borderRadius:6, background:"#f8fafc",
                    color:"#64748b", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>
            Sign Out
          </button>
        )}
      </header>

      <div style={{display:"flex", flex:1, overflow:"hidden"}}>
        {/* ── Left Sidebar ── */}
        <div style={{width:220, background:"linear-gradient(180deg,#eef4fb 0%,#dce8f5 100%)", borderRight:"1.5px solid #c9daea", padding:"16px 0", flexShrink:0, display:"flex", flexDirection:"column"}}>
          {[{key:"clients", label:"Client Management", icon:"🏢", cnt:clients.filter(function(c){return c.status!=="deleted";}).length},
            {key:"users",   label:"User Management",   icon:"👤", cnt:users.filter(function(u){return u.status!=="deleted";}).length},
            {key:"devices", label:"Device Authorization", icon:"💻", cnt:deviceNotifs.length || devices.filter(function(d){return d.Status==="pending";}).length},
            {key:"features",label:"Feature Management", icon:"⚙", cnt:allFeatures.length},
            {key:"dashboards",label:"Dashboard Config", icon:"📊", cnt:allDashboards.length},
            {key:"bi_designer",label:"BI Dashboard Designer", icon:"🛠️", cnt:0},
            {key:"analytics", label:"User Analytics", icon:"📈", cnt:0},
            {key:"report_requests", label:"Report Requests", icon:"📬", cnt:repOrders.length},
            {key:"contracts", label:"Contract Analysis", icon:"📑", cnt:(function(){ var n9 = 0, t9 = new Date().toISOString().slice(0,10); clients.forEach(function(c9){ if (c9.status === "deleted") return; (c9.studyAccess||[]).forEach(function(e9){ if (_adminScope && _adminScope.indexOf(e9.studyTypeCode) < 0) return; var k9 = _caClassify9(c9, e9, t9).key; if (k9 === "expired" || k9 === "soon") n9++; }); }); return n9; })()},
            {key:"partners", label:"Partners", icon:"🤝", cnt:partners.length},
            {key:"partner_usage", label:"Partner Usage", icon:"📊", cnt:0},
            {key:"team", label:"Admin Team", icon:"🛡️", cnt:teamList.length},
            {key:"master_export", label:"Master Export", icon:"🗄️", cnt:0}
          ].filter(function(t) {
            if (t.key === "partners") return _isFullAdmin;                               /* r97 */
            if (t.key === "partner_usage") return _isFullAdmin || _isPartnerAdmin;        /* r97 */
            return (t.key === "team" || t.key === "master_export") ? _isFullAdmin : _canSec(t.key);
          }).map(function(t) {
            var on = tab === t.key;
            return (
              <button key={t.key} onClick={function(){setTab(t.key); setSearch("");}}
                style={{display:"flex", alignItems:"center", gap:10, padding:"11px 18px", border:"none", textAlign:"left",
                        background:on?"rgba(37,99,235,.1)":"transparent", color:on?"#1d4ed8":"#3b5a80",
                        fontSize:12, fontWeight:on?700:500, cursor:"pointer", fontFamily:"inherit",
                        borderLeft:"3px solid "+(on?"#2563eb":"transparent"), transition:"all .15s"}}>
                <span style={{fontSize:16}}>{t.icon}</span>
                <span style={{flex:1}}>{t.label}</span>
                <span style={{fontSize:10, background:on?"#dbeafe":"rgba(255,255,255,.6)", color:on?"#1d4ed8":"#6b8db5",
                              borderRadius:10, padding:"1px 7px", fontWeight:700}}>{t.cnt}</span>
              </button>
            );
          })}
          <div style={{flex:1}} />
          <div style={{padding:"12px 18px", borderTop:"1px solid #c9daea"}}>
            <div style={{fontSize:9, color:"#6b8db5", marginBottom:4}}>QUICK STATS</div>
            <div style={{fontSize:11, color:"#3b5a80", lineHeight:1.7}}>
              Active: {clients.filter(function(c){return c.status==="active";}).length} clients<br/>
              Paused: {clients.filter(function(c){return c.status==="paused";}).length} clients<br/>
              Total extracts: {users.reduce(function(s,u){return s+u.extractUsed;},0)}
            </div>
          </div>
        </div>

        {/* ── Main Content ── */}
        <div style={{flex:1, overflow:"auto", padding:20, background:"#f0f4f8"}}>
          {/* Toolbar */}
          <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:16, flexWrap:"wrap"}}>
            <div style={{fontSize:18, fontWeight:800, color:"#0f172a"}}>{tab === "clients" ? "🏢 Clients" : tab === "users" ? "👤 Users" : tab === "devices" ? "💻 Device Authorization" : tab === "features" ? "⚙ Feature Management" : tab === "bi_designer" ? "🛠️ BI Dashboard Designer" : tab === "analytics" ? "📈 User Analytics" : tab === "report_requests" ? "📬 Full-report requests" : tab === "contracts" ? "📑 Contract Analysis — by study" : tab === "team" ? "🛡️ Admin Team" : tab === "master_export" ? "🗄️ Master Export" : tab === "partners" ? "🤝 Partners — reseller tenants" : tab === "partner_usage" ? "📊 Partner usage — per end customer" : "📊 Dashboard Configuration"}</div>
            <div style={{flex:1}} />
            {tab !== "features" && tab !== "dashboards" && tab !== "devices" && tab !== "bi_designer" && tab !== "master_export" && <input style={{...adminInp, width:220}} placeholder={"Search " + tab + "…"} value={search}
              onChange={function(e){setSearch(e.target.value);}} />}
            {(tab === "clients" || tab === "users") && <button onClick={function(){ tab==="clients" ? setClientModal("new") : setUserModal("new"); }}
              style={adminBtn("#2563eb","#fff","#2563eb")}>
              + Add {tab === "clients" ? "Client" : "User"}
            </button>}
          </div>

          {/* ═══ PENDING DEVICE ALERT — visible on every tab, direct actions ═══ */}
          {tab !== "devices" && _canSec("devices") && deviceNotifs.length > 0 && (
            <div style={{marginBottom:16,padding:"13px 16px",background:"linear-gradient(135deg,#fffbeb,#fef3c7)",borderRadius:12,border:"1.5px solid #f59e0b",boxShadow:"0 3px 14px rgba(217,119,6,.18)"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{fontSize:17}}>🔔</span>
                <span style={{fontSize:13,fontWeight:800,color:"#92400e"}}>
                  {deviceNotifs.length === 1 ? "1 device is waiting for your authorization" : deviceNotifs.length + " devices are waiting for your authorization"}
                </span>
                <div style={{flex:1}} />
                <button onClick={function(){setTab("devices");}}
                  style={{padding:"4px 12px",background:"#fff",color:"#92400e",border:"1px solid #f59e0b",borderRadius:6,fontSize:10.5,fontWeight:700,cursor:"pointer"}}>Open Device Authorization →</button>
              </div>
              {deviceNotifs.slice(0,3).map(function(n){
                return (
                  <div key={n.NotificationId} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"#fff",borderRadius:8,marginBottom:5,border:"1px solid #fde68a"}}>
                    <div style={{width:30,height:30,borderRadius:"50%",background:"#fef3c7",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>💻</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11.5,fontWeight:700,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.UserName || n.UserEmail}</div>
                      <div style={{fontSize:9.5,color:"#64748b",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.DeviceName} — {(function(d){ if(!d) return "\u2014"; try { var dt=new Date(d); return dt.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})+" "+dt.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}); } catch(e){ return "\u2014"; } })(n.CreatedAt)}</div>
                    </div>
                    <button onClick={function(){api.approveDevice(n.DeviceRecordId,"admin").then(reloadDevices).catch(function(e){alert(e.message);});}}
                      style={{padding:"5px 14px",background:"#059669",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>Approve</button>
                    <button onClick={function(){api.revokeDevice(n.DeviceRecordId).then(reloadDevices).catch(function(e){alert(e.message);});}}
                      style={{padding:"5px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>Deny</button>
                  </div>
                );
              })}
              {deviceNotifs.length > 3 && (
                <div style={{fontSize:10,color:"#92400e",fontWeight:700,marginTop:2}}>{"+ " + (deviceNotifs.length - 3) + " more — open Device Authorization to see all"}</div>
              )}
            </div>
          )}

          {/* ═══ r87: NEW FULL-REPORT REQUESTS (from sample mode) — visible on every tab ═══ */}
          {tab !== "report_requests" && _canSec("report_requests") && repOrders.length > 0 && (
            <div style={{marginBottom:16,padding:"13px 16px",background:"linear-gradient(135deg,#eff9fb,#e0f2f8)",borderRadius:12,border:"1.5px solid #49B8D3",boxShadow:"0 3px 14px rgba(73,184,211,.18)"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{fontSize:17}}>📬</span>
                <span style={{fontSize:13,fontWeight:800,color:"#005487"}}>
                  {repOrders.length === 1 ? "1 user has requested a full report" : repOrders.length + " users have requested full reports"}
                </span>
                <div style={{flex:1}} />
                <button onClick={function(){setTab("report_requests");}}
                  style={{padding:"4px 12px",background:"#fff",color:"#005487",border:"1px solid #49B8D3",borderRadius:6,fontSize:10.5,fontWeight:700,cursor:"pointer"}}>Open Report Requests →</button>
              </div>
              {repOrders.slice(0,3).map(function(o){
                return (
                  <div key={o.OrderID} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"#fff",borderRadius:8,marginBottom:5,border:"1px solid #cfe3f1"}}>
                    <div style={{width:30,height:30,borderRadius:"50%",background:"#eff9fb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>📑</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11.5,fontWeight:700,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(o.UserName || o.UserEmail || ("user #" + o.UserID)) + (o.ClientName ? " — " + o.ClientName : "")}</div>
                      <div style={{fontSize:9.5,color:"#64748b",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{_repOrderLabel9(o.ReportKey) + " · " + (o.StudyType || "") + (o.Subject ? " · " + o.Subject : "") + (o.Period ? " · " + o.Period : "") + (o.Market ? " · " + o.Market : "") + " · " + _fmtWhen9(o.CreatedAt)}</div>
                    </div>
                    <button onClick={function(){ handleRepOrder(o.OrderID, "handled"); }}
                      style={{padding:"5px 14px",background:"#005487",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>Mark handled</button>
                  </div>
                );
              })}
              {repOrders.length > 3 && (
                <div style={{fontSize:10,color:"#005487",fontWeight:700,marginTop:2}}>{"+ " + (repOrders.length - 3) + " more — open Report Requests to see all"}</div>
              )}
            </div>
          )}

          {/* ═══ r87: REPORT REQUESTS TAB ═══ */}
          {/* ═══ r97 PARTNERS — reseller tenants (full admin) ═══ */}
          {tab === "partners" && _isFullAdmin && (
            <div style={{background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,.04)"}}>
              <div style={{padding:"12px 16px", borderBottom:"1px solid #e2e8f0", background:"#f8fafc", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                <span style={{fontSize:11, color:"#475569"}}>A partner (e.g. IQVIA) resells AMSX to its own end customers: assign clients to it in the client editor, make its account managers <b>partner admins</b> in Admin Team, and it gets co-branding, scoped management and a monthly usage report.</span>
                <div style={{flex:1}} />
                <button onClick={function(){ setPartnerModal({ id: null, name: "", code: "", contactEmail: "", terms: "", usageMailDay: 1, status: "active", logoUrl: "", logoDarkUrl: "", busy: false, err: "" }); }}
                  style={{padding:"6px 14px", background:"#005487", color:"#fff", border:"none", borderRadius:8, fontSize:11, fontWeight:800, cursor:"pointer"}}>{"+ Add partner"}</button>
                <button onClick={reloadPartners} style={{padding:"5px 12px", border:"1.5px solid #e2e8f0", borderRadius:8, background:"#fff", color:"#475569", fontSize:10.5, fontWeight:700, cursor:"pointer"}}>{"↻ Refresh"}</button>
              </div>
              {partners.length === 0 ? <div style={{padding:28, textAlign:"center", color:"#94a3b8", fontSize:11}}>No partners yet.</div> : (
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:11}}>
                  <thead><tr style={{background:"#f8fafc", borderBottom:"1.5px solid #e2e8f0"}}>
                    {["PARTNER","CODE / SUBDOMAIN","END CUSTOMERS","PARTNER ADMINS","CONTACT (notified · usage report)","USAGE MAIL","STATUS",""].map(function(h){ return <th key={h} style={{padding:"10px 12px", textAlign:"left", fontWeight:700, color:"#475569", fontSize:10}}>{h}</th>; })}
                  </tr></thead>
                  <tbody>
                    {partners.map(function(pr){
                      return <tr key={pr.id} style={{borderBottom:"1px solid #f1f5f9"}}>
                        <td style={{padding:"9px 12px"}}><div style={{display:"flex", alignItems:"center", gap:10}}>{pr.logo ? <img src={pr.logo} alt="" style={{height:26, width:"auto", maxWidth:90}} /> : <span style={{width:26, height:26, borderRadius:7, background:"#ccfbf1", display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:13}}>{"🤝"}</span>}<div><div style={{fontWeight:800, color:"#0f172a"}}>{pr.name}</div><div style={{fontSize:9.5, color:"#64748b"}}>{pr.terms}</div></div></div></td>
                        <td style={{padding:"9px 12px", color:"#334155"}}><div style={{fontWeight:700}}>{pr.code}</div><div style={{fontSize:9.5, color:"#64748b"}}>{pr.code + ".amsanalytix.com"}</div></td>
                        <td style={{padding:"9px 12px", color:"#334155"}}>{pr.clients + (pr.activeClients !== pr.clients ? " (" + pr.activeClients + " active)" : "")}</td>
                        <td style={{padding:"9px 12px", color:"#334155"}}>{pr.admins}</td>
                        <td style={{padding:"9px 12px", color:"#334155", maxWidth:260}}>{pr.contactEmail || <span style={{color:"#b45309"}}>{"⚠ none — add one for notifications and the usage mail"}</span>}</td>
                        <td style={{padding:"9px 12px", color:"#475569", whiteSpace:"nowrap"}}>{"day " + pr.usageMailDay + (pr.lastUsageMailMonth ? " · last " + pr.lastUsageMailMonth : " · not sent yet")}</td>
                        <td style={{padding:"9px 12px"}}><span style={{fontSize:9.5, fontWeight:800, padding:"3px 9px", borderRadius:10, background:pr.status==="active"?"#ecfdf5":"#fef3c7", color:pr.status==="active"?"#065f46":"#92400e"}}>{String(pr.status || "active").toUpperCase()}</span></td>
                        <td style={{padding:"9px 12px", whiteSpace:"nowrap"}}>
                          <button onClick={function(){ setPartnerModal({ id: pr.id, name: pr.name, code: pr.code, contactEmail: pr.contactEmail || "", terms: pr.terms || "", usageMailDay: pr.usageMailDay || 1, status: pr.status || "active", logoUrl: (pr.logoFile && /^(\/|https?:\/\/)/i.test(pr.logoFile) ? pr.logoFile : ""), logoDarkUrl: pr.logoDark || "", logo: pr.logo, busy: false, err: "" }); }} style={{padding:"5px 12px", background:"#fff", color:"#005487", border:"1px solid #005487", borderRadius:6, fontSize:10.5, fontWeight:700, cursor:"pointer", marginRight:6}}>{"✏️ Edit"}</button>
                          <button onClick={function(){ setTab("partner_usage"); setTimeout(function(){ loadPartnerUsage(String(pr.id), pu.from, pu.to); }, 50); }} style={{padding:"5px 12px", background:"#005487", color:"#fff", border:"none", borderRadius:6, fontSize:10.5, fontWeight:700, cursor:"pointer"}}>{"📊 Usage"}</button>
                        </td>
                      </tr>;
                    })}
                  </tbody>
                </table>
              )}
              {partnerModal ? (
                <div onClick={function(e){ if (e.target === e.currentTarget && !partnerModal.busy) setPartnerModal(null); }} style={{position:"fixed", inset:0, zIndex:9000, background:"rgba(15,23,42,.45)", display:"flex", alignItems:"center", justifyContent:"center", padding:20}}>
                  <div style={{width:"min(560px, 100%)", background:"#fff", borderRadius:14, boxShadow:"0 24px 64px rgba(2,6,23,.35)", overflow:"hidden"}}>
                    <div style={{padding:"14px 20px", background:"linear-gradient(135deg,#005487,#49B8D3)", color:"#fff", fontSize:14, fontWeight:800}}>{partnerModal.id ? "Edit partner" : "New partner"}</div>
                    <div style={{padding:"16px 20px", display:"grid", gridTemplateColumns:"130px 1fr", gap:"8px 12px", alignItems:"center", fontSize:11.5}}>
                      <label style={{fontWeight:700, color:"#475569"}}>Name</label>
                      <input value={partnerModal.name} onChange={function(e){ setPartnerModal(Object.assign({}, partnerModal, { name: e.target.value, code: partnerModal.id ? partnerModal.code : e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })); }} placeholder="IQVIA" style={{padding:"7px 10px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:12, fontFamily:"inherit", fontWeight:700}} />
                      <label style={{fontWeight:700, color:"#475569"}}>Code / subdomain</label>
                      <div><input value={partnerModal.code} onChange={function(e){ setPartnerModal(Object.assign({}, partnerModal, { code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })); }} placeholder="iqvia" style={{width:140, padding:"7px 10px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:12, fontFamily:"inherit"}} /><span style={{fontSize:10.5, color:"#64748b", marginLeft:8}}>{"→ " + (partnerModal.code || "code") + ".amsanalytix.com (DNS + IIS binding)"}</span></div>
                      <label style={{fontWeight:700, color:"#475569"}}>Contact e-mail(s)</label>
                      <input value={partnerModal.contactEmail} onChange={function(e){ setPartnerModal(Object.assign({}, partnerModal, { contactEmail: e.target.value })); }} placeholder="account.manager@iqvia.com, partner.ops@iqvia.com" style={{padding:"7px 10px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:12, fontFamily:"inherit"}} />
                      <label style={{fontWeight:700, color:"#475569"}}>Licence line</label>
                      <input value={partnerModal.terms} onChange={function(e){ setPartnerModal(Object.assign({}, partnerModal, { terms: e.target.value })); }} placeholder={"Distributed by " + (partnerModal.name || "the partner") + " under licence from AMS Analytix"} style={{padding:"7px 10px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:12, fontFamily:"inherit"}} />
                      <label style={{fontWeight:700, color:"#475569"}}>Usage mail day</label>
                      <div><input type="number" min="1" max="28" value={partnerModal.usageMailDay} onChange={function(e){ setPartnerModal(Object.assign({}, partnerModal, { usageMailDay: e.target.value })); }} style={{width:70, padding:"7px 10px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:12, fontFamily:"inherit"}} /><span style={{fontSize:10.5, color:"#64748b", marginLeft:8}}>of each month, the previous month's report goes to the contact e-mail(s)</span></div>
                      <label style={{fontWeight:700, color:"#475569"}}>Logo path</label>
                      <div>
                        <input value={partnerModal.logoUrl || ""} onChange={function(e){ setPartnerModal(Object.assign({}, partnerModal, { logoUrl: e.target.value })); }} placeholder="/iqvia-logo.png" style={{width:220, padding:"7px 10px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:12, fontFamily:"inherit"}} />
                        {partnerModal.logoUrl ? <img src={(/^(\/|https?:\/\/)/i.test(partnerModal.logoUrl) ? "" : "/") + partnerModal.logoUrl} alt="" onError={function(e){ e.currentTarget.style.outline = "2px solid #ef4444"; }} style={{height:26, width:"auto", maxWidth:110, marginLeft:10, verticalAlign:"middle"}} /> : null}
                        <div style={{fontSize:10.5, color:"#64748b", marginTop:3}}>{"A file in client/public (e.g. iqvia-logo.png → /iqvia-logo.png) or a full URL \u2014 shown on the login page, in the header and on the licence slide. Leave empty to use the uploaded logo below."}</div>
                      </div>
                      <label style={{fontWeight:700, color:"#475569"}}>Dark logo path</label>
                      <div>
                        <input value={partnerModal.logoDarkUrl || ""} onChange={function(e){ setPartnerModal(Object.assign({}, partnerModal, { logoDarkUrl: e.target.value })); }} placeholder="/iqvia-logo-white.png" style={{width:220, padding:"7px 10px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:12, fontFamily:"inherit"}} />
                        {partnerModal.logoDarkUrl ? <span style={{display:"inline-block", background:"#0A2F42", borderRadius:6, padding:"4px 10px", marginLeft:10, verticalAlign:"middle"}}><img src={(/^(\/|https?:\/\/)/i.test(partnerModal.logoDarkUrl) ? "" : "/") + partnerModal.logoDarkUrl} alt="" onError={function(e){ e.currentTarget.style.outline = "2px solid #ef4444"; }} style={{height:22, width:"auto", maxWidth:110, display:"block"}} /></span> : null}
                        <div style={{fontSize:10.5, color:"#64748b", marginTop:3}}>{"r131: the partner's white / reversed logo for navy surfaces (Sales Tracker header, report covers, closing and licence pages). Leave empty and the normal logo is shown in a white chip there instead."}</div>
                      </div>
                      {partnerModal.id ? <label style={{fontWeight:700, color:"#475569"}}>Logo upload</label> : null}
                      {partnerModal.id ? <div style={{display:"flex", alignItems:"center", gap:10}}>{partnerModal.logo ? <img src={partnerModal.logo} alt="" style={{height:30, width:"auto", maxWidth:120}} /> : <span style={{fontSize:10.5, color:"#94a3b8"}}>none</span>}<input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={function(e){ var f = e.target.files && e.target.files[0]; if (!f) return; setPartnerModal(Object.assign({}, partnerModal, { busy: true })); api.uploadPartnerLogo(partnerModal.id, f).then(function(r){ setPartnerModal(Object.assign({}, partnerModal, { busy: false, logo: r.logo })); reloadPartners(); }).catch(function(err){ setPartnerModal(Object.assign({}, partnerModal, { busy: false, err: err.message })); }); }} style={{fontSize:11}} /></div> : null}
                      {partnerModal.id ? <label style={{fontWeight:700, color:"#475569"}}>Status</label> : null}
                      {partnerModal.id ? <select value={partnerModal.status} onChange={function(e){ setPartnerModal(Object.assign({}, partnerModal, { status: e.target.value })); }} style={{width:140, padding:"7px 10px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:12, fontFamily:"inherit"}}><option value="active">active</option><option value="paused">paused</option></select> : null}
                    </div>
                    {partnerModal.err ? <div style={{padding:"0 20px", fontSize:11, color:"#b91c1c"}}>{partnerModal.err}</div> : null}
                    <div style={{padding:"12px 20px 18px", display:"flex", gap:8, justifyContent:"flex-end"}}>
                      <button onClick={function(){ setPartnerModal(null); }} disabled={partnerModal.busy} style={{padding:"8px 16px", border:"1.5px solid #e2e8f0", borderRadius:8, background:"#fff", color:"#64748b", fontSize:11, fontWeight:700, cursor:"pointer"}}>Cancel</button>
                      <button disabled={partnerModal.busy} onClick={function(){
                        var m = partnerModal; if (!m.name.trim() || !m.code.trim()) { setPartnerModal(Object.assign({}, m, { err: "Name and code are required" })); return; }
                        setPartnerModal(Object.assign({}, m, { busy: true, err: "" }));
                        var body = { name: m.name.trim(), code: m.code.trim(), contactEmail: m.contactEmail, terms: m.terms, usageMailDay: m.usageMailDay, status: m.status, logoUrl: String(m.logoUrl || "").trim(), logoDarkUrl: String(m.logoDarkUrl || "").trim() };   /* r101: static logo path; r131: dark logo path */
                        (m.id ? api.updatePartner(m.id, body) : api.createPartner(body)).then(function(r){ reloadPartners(); if (!m.id && r && r.id) { setPartnerModal(Object.assign({}, m, { id: r.id, busy: false, err: "" })); } else setPartnerModal(null); })
                          .catch(function(e){ setPartnerModal(Object.assign({}, m, { busy: false, err: (e && e.message) || "Could not save" })); });
                      }} style={{padding:"8px 20px", border:"none", borderRadius:8, background:"#005487", color:"#fff", fontSize:11, fontWeight:800, cursor:"pointer"}}>{partnerModal.busy ? "Saving…" : (partnerModal.id ? "Save" : "Create (then add the logo)")}</button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
          {/* ═══ r97 PARTNER USAGE — per end customer, per month (full admins pick a partner; partner admins see their own) ═══ */}
          {tab === "partner_usage" && (_isFullAdmin || _isPartnerAdmin) && (
            <div style={{background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,.04)"}}>
              <div style={{padding:"12px 16px", borderBottom:"1px solid #e2e8f0", background:"#f8fafc", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                {_isFullAdmin ? <select value={pu.partnerId || ""} onChange={function(e){ loadPartnerUsage(e.target.value, pu.from, pu.to); }} style={{padding:"6px 9px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11.5, fontFamily:"inherit", background:"#fff"}}>
                  <option value="">— choose a partner —</option>{partners.map(function(pr){ return <option key={pr.id} value={pr.id}>{pr.name}</option>; })}</select>
                  : <span style={{fontSize:12, fontWeight:800, color:"#0f172a"}}>{(user.partner && user.partner.name) || "Your partner"}</span>}
                <span style={{fontSize:10.5, color:"#64748b"}}>from</span>
                <input type="month" value={pu.from} onChange={function(e){ loadPartnerUsage(pu.partnerId, e.target.value, pu.to < e.target.value ? e.target.value : pu.to); }} style={{padding:"5px 8px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11, fontFamily:"inherit"}} />
                <span style={{fontSize:10.5, color:"#64748b"}}>to</span>
                <input type="month" value={pu.to} onChange={function(e){ loadPartnerUsage(pu.partnerId, pu.from > e.target.value ? e.target.value : pu.from, e.target.value); }} style={{padding:"5px 8px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11, fontFamily:"inherit"}} />
                <div style={{flex:1}} />
                {pu.data ? <a href={api.partnerUsageCsvUrl(pu.partnerId, pu.from, pu.to)} style={{padding:"6px 12px", border:"1.5px solid #005487", borderRadius:8, background:"#fff", color:"#005487", fontSize:10.5, fontWeight:800, textDecoration:"none"}}>{"⭳ Download CSV"}</a> : null}
                {pu.data && mailStatus && mailStatus.configured ? <button onClick={function(){ setPu(Object.assign({}, pu, { sent: { busy: true } })); api.sendPartnerUsage(pu.partnerId, pu.from).then(function(r){ setPu(function(prev){ return Object.assign({}, prev, { sent: r }); }); reloadPartners(); }).catch(function(e){ setPu(function(prev){ return Object.assign({}, prev, { sent: { ok: false, error: e.message } }); }); }); }} style={{padding:"6px 12px", border:"none", borderRadius:8, background:"#005487", color:"#fff", fontSize:10.5, fontWeight:800, cursor:"pointer"}}>{"✉ E-mail " + pu.from + " report now"}</button> : null}
              </div>
              {pu.sent ? <div style={{padding:"8px 16px", fontSize:11, color:pu.sent.busy ? "#64748b" : (pu.sent.ok ? "#065f46" : "#b91c1c"), background:pu.sent.busy ? "#f8fafc" : (pu.sent.ok ? "#ecfdf5" : "#fef2f2"), borderBottom:"1px solid #e2e8f0"}}>{pu.sent.busy ? "Sending…" : (pu.sent.ok ? "✅ Sent to " + (pu.sent.to || []).join(", ") : "⚠ " + (pu.sent.error || "not sent"))}</div> : null}
              {pu.loading ? <div style={{padding:28, textAlign:"center", color:"#94a3b8", fontSize:11}}>Loading…</div>
              : pu.err ? <div style={{padding:28, textAlign:"center", color:"#b91c1c", fontSize:11}}>{pu.err}</div>
              : !pu.data ? <div style={{padding:28, textAlign:"center", color:"#94a3b8", fontSize:11}}>{_isFullAdmin ? "Choose a partner." : "No data."}</div>
              : (
                <div style={{overflowX:"auto"}}>
                  <div style={{padding:"10px 16px 0", fontSize:11, color:"#475569"}}>{pu.data.partner.name + " · " + (pu.from === pu.to ? pu.from : pu.from + " to " + pu.to) + " · " + pu.data.rows.length + " end customer" + (pu.data.rows.length === 1 ? "" : "s") + " · counts from the activity log, export log, report quota, sample and request tables"}</div>
                  <table style={{width:"100%", borderCollapse:"collapse", fontSize:11, minWidth:1000, marginTop:8}}>
                    <thead><tr style={{background:"#f8fafc", borderBottom:"1.5px solid #e2e8f0"}}>
                      {["END CUSTOMER","STUDIES","ACTIVE USERS","LOGGED IN","LOGINS","DASHBOARDS","QUERIES","FILES EXPORTED","SAMPLES","FULL REPORTS","REQUESTS","HANDLED"].map(function(h, i){ return <th key={h} style={{padding:"9px 10px", textAlign:i < 2 ? "left" : "right", fontWeight:700, color:"#475569", fontSize:10, whiteSpace:"nowrap"}}>{h}</th>; })}
                    </tr></thead>
                    <tbody>
                      {pu.data.rows.concat([pu.data.total]).map(function(r, i){
                        var isTot = r.client === "TOTAL";
                        var num = function(v){ return <td style={{padding:"8px 10px", textAlign:"right", fontWeight:isTot?800:500, color:"#0f172a"}}>{Number(v || 0).toLocaleString()}</td>; };
                        return <tr key={isTot ? "tot" : r.clientId} style={{borderBottom:"1px solid #f1f5f9", background:isTot ? "#f1f7fb" : "#fff"}}>
                          <td style={{padding:"8px 10px", fontWeight:isTot?800:700, color:"#0f172a"}}>{r.client}{!isTot && r.status !== "active" ? <span style={{fontSize:9, color:"#92400e", marginLeft:6}}>{r.status}</span> : null}</td>
                          <td style={{padding:"8px 10px", color:"#64748b", fontSize:10}}>{isTot ? "" : (r.studies || "")}</td>
                          {num(r.activeUsers)}{num(r.usersLoggedIn)}{num(r.logins)}{num(r.dashboardViews)}{num(r.queries)}{num(r.filesExported)}{num(r.samples)}{num(r.fullReports)}{num(r.requests)}{num(r.requestsHandled)}
                        </tr>;
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {tab === "report_requests" && (
            <div style={{background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,.04)"}}>
              <div style={{padding:"12px 16px", borderBottom:"1px solid #e2e8f0", background:"#f8fafc", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                <span style={{fontSize:11, color:"#475569"}}>Users who opened a report in <b>sample mode</b> and asked for the full report{_adminScope ? " — your studies: " + _adminScope.join(", ") : ""}. Answer them by e-mail from here (every message is recorded), then mark the request handled. New requests are e-mailed to the admins holding this permission for the request's study.</span>
                {mailStatus ? <span title={mailStatus.configured ? ("SMTP " + mailStatus.host + " · from " + mailStatus.from + " · new requests notified to: " + (mailStatus.notifyRule || "") + (mailStatus.autoAck ? " · automatic acknowledgement ON" : " · automatic acknowledgement OFF")) : ("E-mail sending is not configured: " + mailStatus.reason + ". Set SMTP_HOST / SMTP_PORT / SMTP_USER / SMTP_PASS / SMTP_FROM / REPORT_ADMIN_EMAIL on the server and run npm install nodemailer. Until then answers are recorded here and opened in your e-mail app.")}
                  style={{fontSize:10, fontWeight:800, padding:"4px 10px", borderRadius:10, background:mailStatus.configured?"#ecfdf5":"#fef3c7", color:mailStatus.configured?"#065f46":"#92400e", whiteSpace:"nowrap"}}>{mailStatus.configured ? "\u2709 E-mail: automatic (" + mailStatus.host + ")" : "\u2709 E-mail: not configured \u2014 manual"}</span> : null}
                <div style={{flex:1}} />
                {["new","handled","all"].map(function(k){ var on = repOrdFilter === k; return <button key={k} onClick={function(){ setRepOrdFilter(k); }}
                  style={{padding:"5px 12px", border:"1.5px solid "+(on?"#005487":"#e2e8f0"), borderRadius:8, background:on?"#005487":"#fff", color:on?"#fff":"#475569", fontSize:10.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>{k === "new" ? "New" : k === "handled" ? "Handled" : "All"}</button>; })}
                <button onClick={function(){ reloadRepOrdersAll(); reloadRepOrders(); }} style={{padding:"5px 12px", border:"1.5px solid #e2e8f0", borderRadius:8, background:"#fff", color:"#475569", fontSize:10.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>↻ Refresh</button>
              </div>
              {!repOrdersAll ? <div style={{padding:28, textAlign:"center", color:"#94a3b8", fontSize:11}}>Loading…</div>
              : repOrdersAll.length === 0 ? <div style={{padding:28, textAlign:"center", color:"#94a3b8", fontSize:11}}>No {repOrdFilter === "all" ? "" : repOrdFilter + " "}requests.</div>
              : (
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%", borderCollapse:"collapse", fontSize:11, minWidth:900}}>
                    <thead><tr style={{background:"#f8fafc", borderBottom:"1.5px solid #e2e8f0"}}>
                      {["WHEN","USER","CLIENT","STUDY","REPORT","SUBJECT / NOTE","PREPARE FOR","ANSWERS","STATUS",""].map(function(h){ return <th key={h} style={{padding:"10px 12px", textAlign:"left", fontWeight:700, color:"#475569", fontSize:10}}>{h}</th>; })}
                    </tr></thead>
                    <tbody>
                      {repOrdersAll.map(function(o){
                        var isNew = o.Status === "new";
                        return <React.Fragment key={o.OrderID}><tr style={{borderBottom:"1px solid #f1f5f9", background:isNew?"#f8fdff":"#fff"}}>
                          <td style={{padding:"9px 12px", whiteSpace:"nowrap", color:"#64748b"}}>{_fmtWhen9(o.CreatedAt)}</td>
                          <td style={{padding:"9px 12px"}}><div style={{fontWeight:700, color:"#0f172a"}}>{o.UserName || ("user #" + o.UserID)}</div><div style={{fontSize:9.5, color:"#64748b"}}>{o.UserEmail || ""}</div></td>
                          <td style={{padding:"9px 12px", fontWeight:600, color:"#1e293b"}}>{o.ClientName || "—"}</td>
                          <td style={{padding:"9px 12px", color:"#475569"}}>{o.StudyType || "—"}</td>
                          <td style={{padding:"9px 12px", fontWeight:700, color:"#005487"}}>{_repOrderLabel9(o.ReportKey)}</td>
                          <td style={{padding:"9px 12px", color:"#334155", maxWidth:360}}><div>{o.Subject || ""}</div>{o.Note ? <div style={{fontSize:10, color:"#64748b", fontStyle:"italic", marginTop:2}}>{"“" + o.Note + "”"}</div> : null}</td>
                          <td style={{padding:"9px 12px", whiteSpace:"nowrap"}}>{_repOrderSel9(o)}</td>
                          <td style={{padding:"9px 12px", whiteSpace:"nowrap"}}>
                            <div style={{fontWeight:700, color:(Number(o.RepliesSent) > 0) ? "#065f46" : "#94a3b8"}}>{(Number(o.RepliesSent) > 0) ? ("\u2709 " + o.RepliesSent + " sent") : "no answer yet"}</div>
                            {o.LastReplyAt ? <div style={{fontSize:9.5, color:"#64748b"}}>{"last " + _fmtWhen9(o.LastReplyAt)}</div> : null}
                            {Number(o.MailFailed) > 0 ? <div style={{fontSize:9.5, color:"#b91c1c", fontWeight:700}}>{"\u26a0 " + o.MailFailed + " e-mail" + (Number(o.MailFailed) > 1 ? "s" : "") + " not sent"}</div> : null}
                          </td>
                          <td style={{padding:"9px 12px"}}><span style={{fontSize:9.5, fontWeight:800, padding:"3px 9px", borderRadius:10, background:isNew?"#fef3c7":"#ecfdf5", color:isNew?"#92400e":"#065f46"}}>{isNew ? "NEW" : "HANDLED" + (o.HandledBy ? " · " + o.HandledBy : "")}</span></td>
                          <td style={{padding:"9px 12px", whiteSpace:"nowrap"}}>
                            <button onClick={function(){ if (repConv && repConv.id === o.OrderID) setRepConv(null); else openRepConv(o.OrderID); }} style={{padding:"5px 12px", background:(repConv && repConv.id === o.OrderID) ? "#0e7490" : "#005487", color:"#fff", border:"none", borderRadius:6, fontSize:10.5, fontWeight:700, cursor:"pointer", marginRight:6}}>{(repConv && repConv.id === o.OrderID) ? "Close" : "\u2709 Answer"}</button>
                            {isNew ? <button onClick={function(){ handleRepOrder(o.OrderID, "handled"); }} style={{padding:"5px 12px", background:"#fff", color:"#005487", border:"1px solid #005487", borderRadius:6, fontSize:10.5, fontWeight:700, cursor:"pointer"}}>Mark handled</button>
                                   : <button onClick={function(){ handleRepOrder(o.OrderID, "new"); }} style={{padding:"5px 12px", background:"#fff", color:"#475569", border:"1px solid #e2e8f0", borderRadius:6, fontSize:10.5, fontWeight:700, cursor:"pointer"}}>Reopen</button>}
                          </td>
                        </tr>
                        {(repConv && repConv.id === o.OrderID) ? <tr key={"conv" + o.OrderID}><td colSpan={10} style={{padding:0, background:"#f8fafc", borderBottom:"2px solid #cfe3f1"}}>
                          {repConv.loading ? <div style={{padding:18, fontSize:11, color:"#64748b"}}>Loading the conversation…</div>
                          : (!repConv.data) ? <div style={{padding:18, fontSize:11, color:"#b91c1c"}}>{repConv.err || "Could not load"}</div>
                          : (function(){
                            var c = repConv, d = c.data, msgs = d.messages || [], mail = d.mail || {};
                            return <div style={{display:"grid", gridTemplateColumns:"minmax(320px, 1fr) minmax(380px, 1.1fr)", gap:0}}>
                              <div style={{padding:"14px 18px", borderRight:"1px solid #e2e8f0"}}>
                                <div style={{fontSize:10, fontWeight:800, letterSpacing:".12em", color:"#64748b", marginBottom:8}}>{"CONVERSATION · REQUEST #" + c.id + " · " + (d.order && d.order.UserEmail ? d.order.UserEmail : "no e-mail on file")}</div>
                                {msgs.length === 0 ? <div style={{fontSize:11, color:"#94a3b8", padding:"10px 0"}}>No e-mail has been sent for this request yet.</div> : null}
                                {msgs.map(function(m){
                                  var k = _kindLbl9[m.Kind] || _kindLbl9.status, failed = m.Status === "failed";
                                  return <div key={m.MessageID} style={{border:"1px solid " + (failed ? "#fecaca" : "#e2e8f0"), borderLeft:"4px solid " + (failed ? "#dc2626" : k[1]), borderRadius:8, background:"#fff", padding:"9px 12px", marginBottom:8}}>
                                    <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:4}}>
                                      <span style={{fontSize:9, fontWeight:800, letterSpacing:".08em", padding:"2px 7px", borderRadius:6, background:k[2], color:k[1]}}>{k[0]}</span>
                                      <span style={{fontSize:9.5, fontWeight:800, padding:"2px 7px", borderRadius:6, background:failed?"#fef2f2":(m.Status==="manual"?"#fefce8":"#ecfdf5"), color:failed?"#b91c1c":(m.Status==="manual"?"#854d0e":"#065f46")}}>{failed ? "NOT SENT" : (m.Status === "manual" ? "SENT FROM MAIL APP" : "SENT")}</span>
                                      <span style={{fontSize:9.5, color:"#64748b"}}>{_fmtWhen9(m.SentAt) + " · by " + (m.SentBy || "system") + " · to " + (m.ToEmail || "")}</span>
                                    </div>
                                    <div style={{fontSize:11, fontWeight:700, color:"#0f172a"}}>{m.Subject}</div>
                                    <div style={{fontSize:10.5, color:"#334155", whiteSpace:"pre-wrap", marginTop:4, maxHeight:160, overflowY:"auto"}}>{m.Body}</div>
                                    {failed ? <div style={{fontSize:10, color:"#b91c1c", marginTop:4}}>{"\u26a0 " + (m.Error || "mailer error")}</div> : null}
                                  </div>;
                                })}
                              </div>
                              <div style={{padding:"14px 18px"}}>
                                <div style={{fontSize:10, fontWeight:800, letterSpacing:".12em", color:"#64748b", marginBottom:8}}>{"ANSWER BY E-MAIL"}</div>
                                {!mail.configured ? <div style={{fontSize:10.5, color:"#92400e", background:"#fef3c7", border:"1px solid #fde68a", borderRadius:8, padding:"7px 10px", marginBottom:8}}>{"Automatic sending is off (" + (mail.reason || "not configured") + "). Write the answer here, press \u201cOpen in my e-mail app\u201d to send it from Outlook \u2014 it is recorded in the conversation either way."}</div> : null}
                                <div style={{display:"grid", gridTemplateColumns:"70px 1fr", gap:"6px 10px", alignItems:"center", fontSize:11}}>
                                  <label style={{fontWeight:700, color:"#475569"}}>To</label>
                                  <input value={c.to || ""} onChange={function(e){ setRepConv(Object.assign({}, c, { to: e.target.value })); }} style={{padding:"6px 9px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11, fontFamily:"inherit"}} />
                                  <label style={{fontWeight:700, color:"#475569"}}>Subject</label>
                                  <input value={c.subject || ""} onChange={function(e){ setRepConv(Object.assign({}, c, { subject: e.target.value })); }} style={{padding:"6px 9px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11, fontFamily:"inherit", fontWeight:700}} />
                                </div>
                                <textarea value={c.body || ""} onChange={function(e){ setRepConv(Object.assign({}, c, { body: e.target.value })); }} rows={9}
                                  style={{width:"100%", boxSizing:"border-box", marginTop:8, padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11, fontFamily:"inherit", lineHeight:1.5, resize:"vertical"}} />
                                <label style={{display:"flex", alignItems:"center", gap:6, fontSize:10.5, color:"#334155", marginTop:6, cursor:"pointer"}}>
                                  <input type="checkbox" checked={!!c.markHandled} onChange={function(e){ setRepConv(Object.assign({}, c, { markHandled: e.target.checked })); }} />{"Mark the request handled after sending"}</label>
                                {c.err ? <div style={{fontSize:10.5, color:"#b91c1c", marginTop:6}}>{c.err}</div> : null}
                                {c.result && c.result.sent && !c.err ? <div style={{fontSize:10.5, color:"#065f46", fontWeight:700, marginTop:6}}>{"\u2705 Answer sent and recorded."}</div> : null}
                                <div style={{display:"flex", gap:8, marginTop:10, flexWrap:"wrap"}}>
                                  <button onClick={function(){ sendRepReply(false); }} disabled={c.busy || !mail.configured} title={mail.configured ? "Send through the server mailer" : "Configure SMTP on the server to send automatically"}
                                    style={{padding:"7px 16px", background:mail.configured?"#005487":"#cbd5e1", color:"#fff", border:"none", borderRadius:7, fontSize:11, fontWeight:800, cursor:(c.busy||!mail.configured)?"not-allowed":"pointer"}}>{c.busy ? "Sending\u2026" : "\u2709 Send e-mail"}</button>
                                  <a href={_mailtoOf9(c)} onClick={function(){ sendRepReply(true); }} style={{padding:"7px 16px", background:"#fff", color:"#005487", border:"1.5px solid #005487", borderRadius:7, fontSize:11, fontWeight:800, textDecoration:"none"}}>{"\u2197 Open in my e-mail app (records the answer)"}</a>
                                  <button onClick={function(){ navigator.clipboard && navigator.clipboard.writeText(String(c.body || "")); }} style={{padding:"7px 12px", background:"#fff", color:"#475569", border:"1px solid #e2e8f0", borderRadius:7, fontSize:11, fontWeight:700, cursor:"pointer"}}>{"Copy text"}</button>
                                </div>
                              </div>
                            </div>;
                          })()}
                        </td></tr> : null}</React.Fragment>;
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ═══ CLIENTS TABLE ═══ */}
          {tab === "clients" && (
            <div style={{background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,.04)"}}>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%", borderCollapse:"collapse"}}>
                  <thead>
                    <tr>
                      <th style={thS}>Client</th><th style={thS}>Contact</th><th style={thS}>Studies</th>
                      <th style={thS}>Features</th><th style={thS}>Permissions</th><th style={thS}>Users</th><th style={thS}>Status</th>
                      <th style={thS}>Created</th><th style={{...thS, textAlign:"center"}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtClients.length === 0 && (
                      <tr><td colSpan={9} style={{...tdS, textAlign:"center", color:"#94a3b8", padding:30}}>No clients found</td></tr>
                    )}
                    {filtClients.map(function(c) {
                      return (
                        <tr key={c.id} style={{transition:"background .1s"}}
                          onMouseEnter={function(e){e.currentTarget.style.background="#f8fafc";}}
                          onMouseLeave={function(e){e.currentTarget.style.background="transparent";}}>
                          <td style={{...tdS, fontWeight:700, color:"#0f172a"}}>
                            <div>{c.name}{c.ownerGroup ? <span style={{marginLeft:6, fontSize:8, fontWeight:800, background:"#f3e8ff", color:"#7e22ce", border:"1px solid #d8b4fe", borderRadius:3, padding:"1px 5px", verticalAlign:"middle"}}>🏢 OWNER GROUP</span> : null}</div>
                            <div style={{fontSize:9, color:"#94a3b8", fontWeight:400}}>{c.email}</div>
                            {c.clientType === "onetime" && (
                              <div style={{marginTop:3, marginRight:4, display:"inline-block", fontSize:8.5, fontWeight:800, background:"#fef3c7", color:"#92400e", border:"1px solid #fcd34d", borderRadius:3, padding:"1px 5px"}}>ONE-TIME RELEASE</div>
                            )}
                            {c.clientType === "atc3_limited" && (function(){
                              var nS9 = 0, nC9 = 0;
                              ((c.studyAccess) || []).forEach(function(x){ var a9 = (x.scope && x.scope.atc3) || []; if (a9.length) { nS9++; nC9 += a9.length; } });
                              return (
                                <div style={{marginTop:3, marginRight:4, display:"inline-block", fontSize:8.5, fontWeight:800, background:"#e0e7ff", color:"#3730a3", border:"1px solid #a5b4fc", borderRadius:3, padding:"1px 5px"}}>{"\uD83E\uDDEC ATC3-LIMITED" + (nS9 ? " \u00b7 " + nS9 + " stud" + (nS9 > 1 ? "ies" : "y") + " \u00d7" + nC9 : " \u2014 NO CLASSES SET!")}</div>
                              );
                            })()}
                            {(() => {
                              /* Contract status badge — only render when at least one bound is set. */
                              if (!c.contractStart && !c.contractEnd) {
                                /* r42: no hard cap → show when the LATEST-closing study closes the portal */
                                var _pu9 = "";
                                (c.studyAccess || []).forEach(function(x){ var e9 = x.contractEnd || ""; if (e9 && e9 > _pu9) _pu9 = e9; });
                                if (!_pu9) return null;
                                var _td9 = new Date().toISOString().slice(0,10);
                                var _closed9 = _pu9 < _td9;
                                return <div style={{marginTop:3, display:"inline-block", fontSize:8.5, fontWeight:700, background:_closed9 ? "#fee2e2" : "#f1f5f9", color:_closed9 ? "#b91c1c" : "#475569", border:"1px solid " + (_closed9 ? "#fecaca" : "#e2e8f0"), borderRadius:3, padding:"1px 5px"}}>{_closed9 ? "All studies closed " + _pu9 : "Portal until " + _pu9}</div>;
                              }
                              var today = new Date().toISOString().slice(0,10);
                              var label, bg, fg;
                              if (c.contractStart && today < c.contractStart) {
                                label = "Future · starts " + c.contractStart;
                                bg = "#dbeafe"; fg = "#1d4ed8";
                              } else if (c.contractEnd && today > c.contractEnd) {
                                label = "Expired " + c.contractEnd;
                                bg = "#fee2e2"; fg = "#b91c1c";
                              } else {
                                /* Currently active. Show end-date hint if it's within 30 days. */
                                if (c.contractEnd) {
                                  var endD = new Date(c.contractEnd + "T00:00:00Z");
                                  var todayD = new Date(today + "T00:00:00Z");
                                  var daysLeft = Math.round((endD - todayD) / 86400000);
                                  if (daysLeft <= 30) {
                                    label = "Expires in " + daysLeft + "d (" + c.contractEnd + ")";
                                    bg = "#fef3c7"; fg = "#92400e";
                                  } else {
                                    label = "Until " + c.contractEnd;
                                    bg = "#dcfce7"; fg = "#15803d";
                                  }
                                } else {
                                  label = "From " + c.contractStart + " · no end";
                                  bg = "#dcfce7"; fg = "#15803d";
                                }
                              }
                              return <div style={{marginTop:3, display:"inline-block", fontSize:8.5, fontWeight:700, background:bg, color:fg, borderRadius:3, padding:"1px 5px"}}>{label}</div>;
                            })()}
                          </td>
                          <td style={tdS}>{c.contact || "—"}</td>
                          <td style={tdS}>
                            <div style={{display:"flex", flexWrap:"wrap", gap:3}}>
                              {(c.studies||[]).map(function(s){ return <span key={s} style={{fontSize:8.5, background:"#eff6ff", color:"#1d4ed8", borderRadius:3, padding:"1px 5px", fontWeight:600}}>{s}</span>; })}
                              {(!c.studies||!c.studies.length) && <span style={{fontSize:9, color:"#94a3b8"}}>None</span>}
                            </div>
                          </td>
                          <td style={tdS}>
                            <div style={{display:"flex", flexWrap:"wrap", gap:3}}>
                              {(c.features||[]).slice(0,3).map(function(fk){ var fl=FEATURES.find(function(x){return x.key===fk;}); return <span key={fk} style={{fontSize:8.5, background:"#ede9fe", color:"#6d28d9", borderRadius:3, padding:"1px 5px", fontWeight:600}}>{fl?fl.label:fk}</span>; })}
                              {(c.features||[]).length > 3 && <span style={{fontSize:8.5, background:"#f1f5f9", color:"#64748b", borderRadius:3, padding:"1px 5px", fontWeight:700}}>+{c.features.length-3}</span>}
                            </div>
                          </td>
                          <td style={tdS}>
                            <div style={{display:"flex", flexDirection:"column", gap:2}}>
                              <div style={{display:"flex", gap:2, flexWrap:"wrap"}}>
                                {(c.allowedSectors||SECTOR_TYPES).map(function(s){ return <span key={s} style={{fontSize:7.5, background:"#fef9c3", color:"#854d0e", borderRadius:3, padding:"1px 4px", fontWeight:700}}>{s}</span>; })}
                              </div>
                              <span style={{fontSize:8, color:"#0369a1", fontWeight:600}}>{(c.allowedDimensions||DIMENSION_KEYS).length}/{DIMENSION_KEYS.length} dims</span>
                              <span style={{fontSize:8, color:"#86198f", fontWeight:600}}>
                                {(!c.periodAccess || c.periodAccess.mode === "none") ? "No period limit" :
                                 c.periodAccess.mode === "quarter" ? "Q: " + (c.periodAccess.fromQ||"?") + " → " + (c.periodAccess.toQ||"?") :
                                 "Date: " + (c.periodAccess.fromDate||"?") + " → " + (c.periodAccess.toDate||"?")}
                              </span>
                              {(c.studyTypes||[]).length > 0 && (
                                <div style={{display:"flex", gap:2, flexWrap:"wrap", marginTop:1}}>
                                  {(c.studyTypes||[]).map(function(st){ var isPvt = ["iraq","iraq_consumption","jordan","libya","jordan_medical","jordan_msa"].indexOf(st) >= 0; return <span key={st} style={{fontSize:7.5, background:isPvt?"#dbeafe":"#ecfeff", color:isPvt?"#1d4ed8":"#0e7490", borderRadius:3, padding:"1px 4px", fontWeight:700}}>{st}</span>; })}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{...tdS, textAlign:"center"}}>
                            <span style={{fontSize:12, fontWeight:800, color:"#334155"}}>{clientUsers(c.id)}</span>
                          </td>
                          <td style={tdS}><StatusBadge status={c.status} /></td>
                          <td style={{...tdS, fontSize:10, color:"#64748b"}}>{c.created}</td>
                          <td style={{...tdS, textAlign:"center"}}>
                            <div style={{display:"flex", gap:4, justifyContent:"center", flexWrap:"wrap"}}>
                              <button onClick={function(){ if (_ownerLocked(c.id)) return _ownerAlert(); setClientModal(c); }} style={tdBtnS("#eff6ff","#1d4ed8","#bfdbfe")}>Edit</button>
                              <button onClick={function(){ if (_ownerLocked(c.id)) return _ownerAlert(); toggleClientStatus(c.id); }}
                                style={tdBtnS(c.status==="active"?"#fef3c7":"#dcfce7", c.status==="active"?"#92400e":"#15803d", c.status==="active"?"#fde68a":"#bbf7d0")}>
                                {c.status === "active" ? "Pause" : "Activate"}
                              </button>
                              <button onClick={function(){ if (_ownerLocked(c.id)) return _ownerAlert(); setConfirmDel({type:"client",id:c.id,name:c.name}); }}
                                style={tdBtnS("#fef2f2","#dc2626","#fecaca")}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ USERS TABLE ═══ */}
          {tab === "users" && (
            <div style={{background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,.04)"}}>
              {(function(){
                var selS = {padding:"4px 8px", border:"1px solid #e2e8f0", borderRadius:5, fontSize:10, fontFamily:"inherit", outline:"none", color:"#0f172a", background:"#fff", cursor:"pointer"};
                var setA = function(k){ return function(e){ var v = e.target.value; setUserAdvFilter(function(p){ return Object.assign({}, p, (function(){ var o = {}; o[k] = v; return o; })()); }); }; };
                var ynSel = function(k, label){
                  return <select key={k} value={userAdvFilter[k]} onChange={setA(k)} style={selS}>
                    <option value="">{label + ": any"}</option>
                    <option value="yes">{label + ": yes"}</option>
                    <option value="no">{label + ": no"}</option>
                  </select>;
                };
                var anyAdv = userAdvFilter.region || userAdvFilter.gov || userAdvFilter.dash || userAdvFilter.reports || userAdvFilter.exp || userAdvFilter.contract;
                return <div style={{display:"flex", alignItems:"center", gap:6, padding:"9px 14px", borderBottom:"1px solid #e2e8f0", background:"#fbfdff", flexWrap:"wrap"}}>
                  <span style={{fontSize:9.5, fontWeight:800, color:"#475569", textTransform:"uppercase", letterSpacing:".05em"}}>Access filters</span>
                  {ynSel("region", "Region")}
                  {ynSel("gov", "Governorate")}
                  {ynSel("dash", "Dashboards")}
                  {ynSel("reports", "Reports")}
                  <select value={userAdvFilter.exp} onChange={setA("exp")} style={selS}>
                    <option value="">Export: any</option>
                    <option value="yes">Export: allowed</option>
                    <option value="no">Export: blocked</option>
                  </select>
                  <select value={userAdvFilter.contract} onChange={setA("contract")} style={selS}>
                    <option value="">Contract: any</option>
                    <option value="active">Contract: active</option>
                    <option value="expiring">{"Contract: expiring \u226430d"}</option>
                    <option value="expired">Contract: expired</option>
                  </select>
                  {anyAdv && (
                    <button onClick={function(){ setUserAdvFilter({region:"", gov:"", dash:"", reports:"", exp:"", contract:""}); }}
                      style={{padding:"4px 9px", border:"1px solid #e2e8f0", borderRadius:5, background:"#f8fafc", color:"#475569", fontSize:9.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>{"\u2715 Clear"}</button>
                  )}
                  <span style={{flex:1}} />
                  <span style={{fontSize:10, fontWeight:700, color:"#64748b"}}>{filtUsers.length + " user(s)"}</span>
                  <button onClick={exportUsersList}
                    title="Export the filtered users with studies, region/governorate access, dashboards, reports, export permissions, limits and contract windows"
                    style={{padding:"5px 14px", border:"none", borderRadius:6, background:"#15803d", color:"#fff", fontSize:10, fontWeight:800, cursor:"pointer", fontFamily:"inherit"}}>{"\u2B07 Export Users"}</button>
                </div>;
              })()}
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%", borderCollapse:"collapse"}}>
                  <thead>
                    <tr>
                      <th style={thS}>User</th><th style={thS}>Client</th>
                      <th style={thS}>Study Access</th><th style={thS}>Role</th>
                      <th style={thS}>Extracts</th><th style={thS}>Status</th>
                      <th style={thS}>Created</th><th style={{...thS, textAlign:"center"}}>Actions</th>
                    </tr>
                    {/* Per-column filter row — text input for name/email, text input for
                        client substring, dropdowns for role and status (closed sets are
                        easier than typing). Empty value = no filter on that column.
                        Filters AND together with the global search box above the table. */}
                    <tr>
                      <th style={{...thS, padding:"6px 12px", background:"#fff"}}>
                        <input value={userColFilter.user}
                          onChange={function(e){ var v=e.target.value; setUserColFilter(function(p){return Object.assign({},p,{user:v});}); }}
                          placeholder="Filter name or email…"
                          style={{width:"100%", padding:"4px 7px", border:"1px solid #e2e8f0", borderRadius:5,
                                  fontSize:10, fontFamily:"inherit", outline:"none", color:"#0f172a", background:"#fff"}} />
                      </th>
                      <th style={{...thS, padding:"6px 12px", background:"#fff"}}>
                        <input value={userColFilter.client}
                          onChange={function(e){ var v=e.target.value; setUserColFilter(function(p){return Object.assign({},p,{client:v});}); }}
                          placeholder="Filter client…"
                          style={{width:"100%", padding:"4px 7px", border:"1px solid #e2e8f0", borderRadius:5,
                                  fontSize:10, fontFamily:"inherit", outline:"none", color:"#0f172a", background:"#fff"}} />
                      </th>
                      <th style={{...thS, padding:"6px 12px", background:"#fff"}}>
                        <select value={userColFilter.study}
                          onChange={function(e){ var v=e.target.value; setUserColFilter(function(p){return Object.assign({},p,{study:v});}); }}
                          style={{width:"100%", padding:"4px 7px", border:"1px solid #e2e8f0", borderRadius:5,
                                  fontSize:10, fontFamily:"inherit", outline:"none", color:"#0f172a", background:"#fff"}}>
                          <option value="">All studies</option>
                          {userStudyOptions.map(function(k){ return <option key={k} value={k}>{k.replace("/", " · ")}</option>; })}
                        </select>
                      </th>
                      <th style={{...thS, padding:"6px 12px", background:"#fff"}}>
                        <select value={userColFilter.role}
                          onChange={function(e){ var v=e.target.value; setUserColFilter(function(p){return Object.assign({},p,{role:v});}); }}
                          style={{width:"100%", padding:"4px 7px", border:"1px solid #e2e8f0", borderRadius:5,
                                  fontSize:10, fontFamily:"inherit", outline:"none", color:"#0f172a", background:"#fff"}}>
                          <option value="">All roles</option>
                          <option value="manager">Manager</option>
                          <option value="analyst">Analyst</option>
                          <option value="viewer">Viewer</option>
                          <option value="admin">Admin</option>
                        </select>
                      </th>
                      <th style={{...thS, padding:"6px 12px", background:"#fff"}}></th>
                      <th style={{...thS, padding:"6px 12px", background:"#fff"}}>
                        <select value={userColFilter.status}
                          onChange={function(e){ var v=e.target.value; setUserColFilter(function(p){return Object.assign({},p,{status:v});}); }}
                          style={{width:"100%", padding:"4px 7px", border:"1px solid #e2e8f0", borderRadius:5,
                                  fontSize:10, fontFamily:"inherit", outline:"none", color:"#0f172a", background:"#fff"}}>
                          <option value="">All</option>
                          <option value="active">Active</option>
                          <option value="paused">Paused</option>
                        </select>
                      </th>
                      <th style={{...thS, padding:"6px 12px", background:"#fff"}}></th>
                      <th style={{...thS, padding:"6px 12px", background:"#fff", textAlign:"center"}}>
                        {(userColFilter.user || userColFilter.client || userColFilter.role || userColFilter.status || userColFilter.study) && (
                          <button onClick={function(){ setUserColFilter({user:"",client:"",role:"",status:"",study:""}); }}
                            title="Clear all column filters"
                            style={{padding:"3px 8px", border:"1px solid #e2e8f0", borderRadius:5,
                                    background:"#f8fafc", color:"#475569", fontSize:9.5, fontWeight:700,
                                    cursor:"pointer", fontFamily:"inherit"}}>
                            ✕ Clear
                          </button>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtUsers.length === 0 && (
                      <tr><td colSpan={8} style={{...tdS, textAlign:"center", color:"#94a3b8", padding:30}}>No users found</td></tr>
                    )}
                    {filtUsers.map(function(u) {
                      var pct = u.extractLimit > 0 ? Math.round((u.extractUsed / u.extractLimit) * 100) : 0;
                      var barColor = pct >= 100 ? "#dc2626" : pct >= 75 ? "#ea580c" : "#15803d";
                      return (
                        <tr key={u.id}
                          onMouseEnter={function(e){e.currentTarget.style.background="#f8fafc";}}
                          onMouseLeave={function(e){e.currentTarget.style.background="transparent";}}>
                          <td style={{...tdS, fontWeight:700, color:"#0f172a"}}>
                            <div>{u.name}</div>
                            <div style={{fontSize:9, color:"#94a3b8", fontWeight:400}}>{u.email}</div>
                          </td>
                          <td style={tdS}>
                            <span style={{fontSize:10, fontWeight:600, color:"#334155"}}>{clientName(u.clientId)}</span>
                          </td>
                          <td style={tdS}>
                            {(function(){
                              var acc = userEffAccess(u);
                              if (!acc.length) return <span style={{fontSize:9, color:"#94a3b8"}}>None</span>;
                              var mk = function(on, label, key, tip) {
                                return <span key={key} title={tip + ": " + (on ? "yes" : "no")}
                                  style={{fontSize:8, fontWeight:800, borderRadius:3, padding:"0 4px",
                                          background:on?"#dcfce7":"#f1f5f9", color:on?"#15803d":"#b6bfca"}}>{label}</span>;
                              };
                              return <div style={{display:"flex", flexDirection:"column", gap:3}}>
                                {acc.map(function(a){
                                  return <div key={a.key} style={{display:"flex", alignItems:"center", gap:4, whiteSpace:"nowrap"}}>
                                    <span title={a.ov ? "User-level override applied" : "Inherited from client"}
                                      style={{fontSize:9, fontWeight:700, color:a.ov?"#7c3aed":"#334155",
                                              background:a.ov?"#f5f3ff":"#eff6ff", borderRadius:3, padding:"1px 5px"}}>
                                      {a.country + " · " + a.code + (a.ov ? " *" : "")}
                                    </span>
                                    {mk(a.region, "REG", "r", "Region")}
                                    {mk(a.gov, "GOV", "g", "Governorate")}
                                    {mk(a.dash > 0, "DASH " + a.dash, "d", "Dashboards")}
                                    {mk(a.reps > 0, "RPT " + a.reps, "p", "Reports")}
                                  </div>;
                                })}
                              </div>;
                            })()}
                          </td>
                          <td style={tdS}>
                            <span style={{fontSize:9.5, fontWeight:700, textTransform:"capitalize",
                              background:u.role==="manager"?"#dbeafe":u.role==="analyst"?"#f0fdf4":"#f8fafc",
                              color:u.role==="manager"?"#1d4ed8":u.role==="analyst"?"#15803d":"#64748b",
                              borderRadius:4, padding:"2px 8px"}}>{u.role}</span>
                            {u.reportAccess ? <span title={u.reportAccess === "full" ? "Full report decks (user override)" : "Sample reports only (user override)"} style={{display:"inline-block", marginLeft:4, fontSize:8.5, fontWeight:800, borderRadius:8, padding:"1px 6px", background:u.reportAccess === "full" ? "#ecfdf5" : "#eff9fb", color:u.reportAccess === "full" ? "#047857" : "#0e7490", border:"1px solid " + (u.reportAccess === "full" ? "#a7f3d0" : "#9fd8e8")}}>{u.reportAccess === "full" ? "\uD83D\uDCD1 full" : "\uD83E\uDDFE sample"}</span> : null}
                          </td>
                          <td style={tdS}>
                            <div style={{display:"flex", alignItems:"center", gap:8, minWidth:140}}>
                              <div style={{flex:1, height:6, background:"#f1f5f9", borderRadius:3, overflow:"hidden"}}>
                                <div style={{width:Math.min(pct,100)+"%", height:"100%", background:barColor, borderRadius:3, transition:"width .3s"}} />
                              </div>
                              <span style={{fontSize:10, fontWeight:700, color:barColor, whiteSpace:"nowrap"}} title="Exports used this quarter — the counter resets on the first day of the next quarter">{u.extractUsed}/{u.extractLimit}</span>
                            </div>
                          </td>
                          <td style={tdS}><StatusBadge status={u.status} /></td>
                          <td style={{...tdS, fontSize:10, color:"#64748b"}}>{u.created}</td>
                          <td style={{...tdS, textAlign:"center"}}>
                            <div style={{display:"flex", gap:4, justifyContent:"center", flexWrap:"wrap"}}>
                              <button onClick={function(){ if (_ownerLocked(u.clientId)) return _ownerAlert(); setUserModal(u); }} style={tdBtnS("#eff6ff","#1d4ed8","#bfdbfe")}>Edit</button>
                              <button onClick={function(){ if (_ownerLocked(u.clientId)) return _ownerAlert(); toggleUserStatus(u.id); }}
                                style={tdBtnS(u.status==="active"?"#fef3c7":"#dcfce7", u.status==="active"?"#92400e":"#15803d", u.status==="active"?"#fde68a":"#bbf7d0")}>
                                {u.status === "active" ? "Pause" : "Activate"}
                              </button>
                              <button onClick={function(){ if (_ownerLocked(u.clientId)) return _ownerAlert(); resetExtract(u.id); }} style={tdBtnS("#f0fdf4","#15803d","#bbf7d0")}>Reset Ext</button>
                              <button onClick={function(){ if (_ownerLocked(u.clientId)) return _ownerAlert(); setPwResetId(u.id); setPwResetVal(""); setPwResetMsg(""); }}
                                style={tdBtnS("#fef3c7","#92400e","#fde68a")}>🔒 Pwd</button>
                              <button onClick={function(){ if (_ownerLocked(u.clientId)) return _ownerAlert(); setConfirmDel({type:"user",id:u.id,name:u.name}); }}
                                style={tdBtnS("#fef2f2","#dc2626","#fecaca")}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ FEATURES TAB ═══ */}
          {/* ═══ ADMIN TEAM ═══ */}
          {tab === "team" && _isFullAdmin && (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                <div style={{fontSize:11.5,color:"#64748b",lineHeight:1.6,flex:1}}>
                  Team accounts sign in like admins but only see the sections you grant them — e.g. let a colleague add users and approve devices without touching clients or data tools. Changes take effect immediately (their open sessions are signed out).
                </div>
                <button onClick={function(){ setTeamPicker({q:""}); }}
                  style={adminBtn("#fefce8","#a16207","#fde68a")}>{"\uD83D\uDEE1 Promote Existing User"}</button>
                <button onClick={function(){ setTeamModal({adminId:null, name:"", email:"", password:"", full:false, permissions:["users","devices"], scopeAll:true, studyScope:[]}); }}
                  style={adminBtn("#2563eb","#fff","#2563eb")}>+ Add Team Member</button>
              </div>
              <div style={{background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,.04)"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11.5}}>
                  <thead><tr style={{background:"#f8fafc"}}>
                    <th style={{padding:"9px 16px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Name</th>
                    <th style={{padding:"9px 12px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Email</th>
                    <th style={{padding:"9px 12px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Can Manage</th>
                    <th style={{padding:"9px 12px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Status</th>
                    <th style={{padding:"9px 12px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Last Login</th>
                    <th style={{padding:"9px 16px",textAlign:"center",fontSize:9.5,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {teamList.filter(function(m){
                      if (!search.trim()) return true;
                      var q = search.toLowerCase();
                      return (m.name || "").toLowerCase().indexOf(q) >= 0 || (m.email || "").toLowerCase().indexOf(q) >= 0;
                    }).map(function(m){
                      var _secLbl = {};
                      ADMIN_SECTIONS.forEach(function(s){ _secLbl[s.key] = s.label; });
                      ANALYTICS_TABS.forEach(function(t9){ _secLbl["analytics:" + t9.k] = "Analytics \u00b7 " + t9.l; }); /* r45 */
                      return (
                        <tr key={m.adminId} style={{borderTop:"1px solid #f1f5f9", opacity:m.status==="active"?1:0.6}}>
                          <td style={{padding:"10px 16px",fontWeight:700,color:"#0f172a"}}>{m.name}{m.isSelf && <span style={{marginLeft:7,fontSize:8.5,fontWeight:800,background:"#eff6ff",color:"#2563eb",borderRadius:8,padding:"1px 7px"}}>YOU</span>}</td>
                          <td style={{padding:"10px 12px",color:"#475569"}}>{m.email}</td>
                          <td style={{padding:"10px 12px"}}>
                            {m.isFull
                              ? <span style={{background:"#fef3c7",color:"#92400e",border:"1px solid #fde68a",borderRadius:10,padding:"2px 10px",fontSize:9.5,fontWeight:800}}>FULL ADMIN</span>
                              : (m.permissions || []).map(function(pk){ return <span key={pk} style={{display:"inline-block",background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:9,padding:"1px 8px",fontSize:9,fontWeight:700,margin:"1px 3px 1px 0"}}>{_secLbl[pk] || pk}</span>; })}
                            {!m.isFull && m.partnerId ? <span style={{display:"inline-block", fontSize:9.5, fontWeight:800, padding:"2px 8px", borderRadius:8, background:"#ccfbf1", color:"#0f766e", marginRight:4}}>{"\uD83E\uDD1D " + _partnerName9(m.partnerId)}</span> : null}
                            {!m.isFull && m.reportAccess ? <span title={m.reportAccess === "full" ? "Opens the FULL report decks regardless of the client's FULL reports feature" : "Reports open as SAMPLES only, even if the client has the FULL reports feature"} style={{display:"inline-block", fontSize:9, fontWeight:800, padding:"1px 8px", borderRadius:8, background:m.reportAccess === "full" ? "#ecfdf5" : "#eff9fb", color:m.reportAccess === "full" ? "#047857" : "#0e7490", border:"1px solid " + (m.reportAccess === "full" ? "#a7f3d0" : "#9fd8e8"), margin:"1px 3px 1px 0"}}>{m.reportAccess === "full" ? "\uD83D\uDCD1 full reports" : "\uD83E\uDDFE sample reports"}</span> : null}
                            {!m.isFull ? (m.dataClientId
                              ? <span title={"Portal data access: Market, Dashboards and Reports as a user of " + (m.dataClientName || "this client")} style={{display:"inline-block", fontSize:9, fontWeight:800, padding:"1px 8px", borderRadius:8, background:"#eef2ff", color:"#3730a3", border:"1px solid #c7d2fe", margin:"1px 3px 1px 0"}}>{"\uD83D\uDCCA data: " + (m.dataClientName || ("client " + m.dataClientId))}</span>
                              : <span title="No portal account with this e-mail — Market, Dashboards and Reports stay empty for this admin. Edit → Portal data access." style={{display:"inline-block", fontSize:9, fontWeight:800, padding:"1px 8px", borderRadius:8, background:"#fff7ed", color:"#9a3412", border:"1px solid #fed7aa", margin:"1px 3px 1px 0"}}>{"\uD83D\uDCCA no portal data"}</span>) : null}
                            {!m.isFull && m.studyScope && m.studyScope.length > 0 && m.studyScope.map(function(sc){ return <span key={"sc"+sc} style={{display:"inline-block",background:"#fffbeb",color:"#92400e",border:"1px solid #fde68a",borderRadius:9,padding:"1px 8px",fontSize:9,fontWeight:800,margin:"1px 3px 1px 0"}}>{String(sc).replace(/_/g," ").toUpperCase() + " ONLY"}</span>; })}
                          </td>
                          <td style={{padding:"10px 12px"}}>
                            <span style={{background:m.status==="active"?"#ecfdf5":"#fef2f2",color:m.status==="active"?"#059669":"#dc2626",borderRadius:10,padding:"2px 10px",fontSize:9.5,fontWeight:700}}>{m.status}</span>
                          </td>
                          <td style={{padding:"10px 12px",color:"#64748b",fontSize:10.5}}>{m.lastLoginAt ? new Date(m.lastLoginAt).toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "\u2014"}</td>
                          <td style={{padding:"10px 16px",textAlign:"center",whiteSpace:"nowrap"}}>
                            <button title="Edit" onClick={function(){ setTeamModal({adminId:m.adminId, name:m.name, email:m.email, password:"", full:!!m.isFull, permissions:(m.permissions||[]).slice(), isSelf:m.isSelf, scopeAll:!(m.studyScope&&m.studyScope.length), studyScope:(m.studyScope||[]).slice(), partnerId:m.partnerId||"", dataClientId:m.dataClientId||"", dataClientName:m.dataClientName||"", reportAccess:m.reportAccess||""}); }}
                              style={{background:"none",border:"none",cursor:"pointer",fontSize:13,marginRight:4}}>{"\u270F\uFE0F"}</button>
                            {!m.isSelf && <button title={m.status==="active"?"Suspend":"Activate"} onClick={function(){
                              api.updateTeamMember(m.adminId, {status: m.status==="active"?"paused":"active"}).then(reloadTeam).catch(function(e){ alert("Error: " + e.message); });
                            }} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,marginRight:4}}>{m.status==="active"?"\u23F8":"\u25B6"}</button>}
                            {!m.isSelf && <button title="Delete" onClick={function(){
                              if (!window.confirm("Delete team account '" + m.name + "'? They will be signed out immediately.")) return;
                              api.deleteTeamMember(m.adminId).then(reloadTeam).catch(function(e){ alert("Error: " + e.message); });
                            }} style={{background:"none",border:"none",cursor:"pointer",fontSize:13}}>{"\uD83D\uDDD1"}</button>}
                          </td>
                        </tr>
                      );
                    })}
                    {teamList.length === 0 && <tr><td colSpan={6} style={{padding:"24px 16px",textAlign:"center",color:"#94a3b8",fontSize:11}}>Loading team accounts\u2026</td></tr>}
                  </tbody>
                </table>
              </div>

            </div>
          )}

              {/* ── Promote picker: choose a portal user from the user list ── */}
              {_isFullAdmin && teamPicker && (
                <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,.5)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
                  <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:520,padding:"22px 26px",boxShadow:"0 20px 60px rgba(15,23,42,.3)",maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
                    <div style={{fontSize:15,fontWeight:800,color:"#0f172a",marginBottom:6}}>{"\uD83D\uDEE1 Promote a User to Admin"}</div>
                    <div style={{fontSize:10.5,color:"#64748b",marginBottom:12}}>Pick a user from the user list — next you choose which admin sections they can manage. They keep their current password.</div>
                    <input autoFocus style={{...adminInp,width:"100%",boxSizing:"border-box",marginBottom:10}} placeholder={"Search users by name, email or company\u2026"} value={teamPicker.q}
                      onChange={function(e){ setTeamPicker({q: e.target.value}); }} />
                    <div style={{flex:1,overflowY:"auto",border:"1px solid #f1f5f9",borderRadius:9}}>
                      {(function(){
                        var _adminEmails = {};
                        teamList.forEach(function(m){ _adminEmails[(m.email || "").toLowerCase()] = 1; });
                        var _cById = {};
                        (clients || []).forEach(function(c){ _cById[c.id] = c.name; });
                        var q = (teamPicker.q || "").toLowerCase();
                        var list = (users || []).filter(function(u){
                          if (u.status !== "active") return false;
                          if (_adminEmails[(u.email || "").toLowerCase()]) return false;
                          if (!q) return true;
                          var cn = _cById[u.clientId] || "";
                          return (u.name || "").toLowerCase().indexOf(q) >= 0 || (u.email || "").toLowerCase().indexOf(q) >= 0 || cn.toLowerCase().indexOf(q) >= 0;
                        }).slice(0, 50);
                        if (!list.length) return <div style={{padding:"18px 16px",fontSize:11,color:"#94a3b8",textAlign:"center"}}>No matching active users (users who are already admins are hidden).</div>;
                        return list.map(function(u){
                          return (
                            <div key={u.id} onClick={function(){ setTeamPicker(null); setTeamModal({promoteUserId:u.id, name:u.name, email:u.email, password:"", full:false, permissions:["users","devices"], scopeAll:true, studyScope:[], reportAccess:""}); }}
                              style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:"1px solid #f8fafc",cursor:"pointer"}}
                              onMouseEnter={function(e){ e.currentTarget.style.background = "#eff6ff"; }}
                              onMouseLeave={function(e){ e.currentTarget.style.background = "#fff"; }}>
                              <div style={{width:30,height:30,borderRadius:"50%",background:"#eef4fb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0,fontWeight:800,color:"#2563eb"}}>{(u.name || "?").charAt(0).toUpperCase()}</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:12,fontWeight:700,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</div>
                                <div style={{fontSize:10,color:"#64748b",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email}{_cById[u.clientId] ? " \u00B7 " + _cById[u.clientId] : ""}</div>
                              </div>
                              <span style={{fontSize:10,fontWeight:700,color:"#a16207",background:"#fefce8",border:"1px solid #fde68a",borderRadius:8,padding:"3px 10px",flexShrink:0}}>{"Choose \u2192"}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    <div style={{display:"flex",justifyContent:"flex-end",marginTop:12}}>
                      <button onClick={function(){ setTeamPicker(null); }} style={adminBtn("#f1f5f9","#475569","#e2e8f0")}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Team member modal ── */}
              {_isFullAdmin && teamModal && (
                <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,.5)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
                  <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:480,padding:"22px 26px",boxShadow:"0 20px 60px rgba(15,23,42,.3)",maxHeight:"90vh",overflowY:"auto"}}>
                    <div style={{fontSize:15,fontWeight:800,color:"#0f172a",marginBottom:16}}>{teamModal.promoteUserId ? "\uD83D\uDEE1\uFE0F Make User an Admin" : teamModal.adminId ? "\u270F\uFE0F Edit Team Member" : "\uD83D\uDEE1\uFE0F New Team Member"}</div>
                    <label style={{display:"block",fontSize:10.5,fontWeight:700,color:"#334155",marginBottom:4}}>Full Name</label>
                    <input style={{...adminInp,width:"100%",marginBottom:10,boxSizing:"border-box"}} value={teamModal.name}
                      onChange={function(e){ setTeamModal(Object.assign({},teamModal,{name:e.target.value})); }} />
                    <label style={{display:"block",fontSize:10.5,fontWeight:700,color:"#334155",marginBottom:4}}>Email</label>
                    <input style={{...adminInp,width:"100%",marginBottom:10,boxSizing:"border-box",background:(teamModal.adminId||teamModal.promoteUserId)?"#f8fafc":"#fff"}} value={teamModal.email} disabled={!!teamModal.adminId || !!teamModal.promoteUserId}
                      onChange={function(e){ setTeamModal(Object.assign({},teamModal,{email:e.target.value})); }} />
                    {teamModal.promoteUserId ? (
                      <div style={{padding:"9px 13px",background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,fontSize:10.5,color:"#1e40af",marginBottom:14,lineHeight:1.5}}>
                        {"\uD83D\uDD11 They keep their current password \u2014 from their next sign-in they land in the admin panel with the sections you grant below."}
                      </div>
                    ) : (
                      <div>
                        <label style={{display:"block",fontSize:10.5,fontWeight:700,color:"#334155",marginBottom:4}}>{teamModal.adminId ? "New Password (leave empty to keep current)" : "Password"}</label>
                        <input type="password" style={{...adminInp,width:"100%",marginBottom:14,boxSizing:"border-box"}} value={teamModal.password}
                          onChange={function(e){ setTeamModal(Object.assign({},teamModal,{password:e.target.value})); }} />
                      </div>
                    )}
                    <div style={{padding:"10px 14px",background:teamModal.full?"#fffbeb":"#f8fafc",border:"1px solid "+(teamModal.full?"#fde68a":"#e2e8f0"),borderRadius:9,marginBottom:12,cursor:teamModal.isSelf?"not-allowed":"pointer",opacity:teamModal.isSelf?0.6:1}}
                      onClick={function(){ if (teamModal.isSelf) return; setTeamModal(Object.assign({},teamModal,{full:!teamModal.full})); }}>
                      <span style={{fontSize:12,fontWeight:800,color:"#92400e"}}>{(teamModal.full?"\u2611":"\u2610") + " Full admin"}</span>
                      <span style={{fontSize:10,color:"#64748b",marginLeft:8}}>access to everything, including this Admin Team screen</span>
                    </div>
                    {!teamModal.full && partners.length > 0 && (
                      <div style={{marginBottom:14, padding:"8px 12px", background:"#f0fdfa", border:"1px solid " + (teamModal.partnerId ? "#5eead4" : "#e2e8f0"), borderRadius:8}}>
                        <div style={{fontSize:10.5,fontWeight:700,color:"#334155",marginBottom:4}}>{"\uD83E\uDD1D Partner admin of"}</div>
                        <select value={teamModal.partnerId || ""} onChange={function(e){ setTeamModal(Object.assign({}, teamModal, { partnerId: e.target.value })); }}
                          style={{width:"100%", padding:"7px 9px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11.5, fontFamily:"inherit", background:"#fff"}}>
                          <option value="">— not a partner admin (AMS Analytix team) —</option>
                          {partners.map(function(pr){ return <option key={pr.id} value={pr.id}>{pr.name + " (" + pr.code + ")"}</option>; })}
                        </select>
                        <div style={{fontSize:10, color:"#64748b", marginTop:4}}>A partner admin sees and manages only that partner's end customers (clients, users, devices, requests, usage) — within the study scope below.</div>
                      </div>
                    )}
                    {/* r99: PORTAL DATA ACCESS — the admin panel shows no study data by itself;
                        Market / Dashboards / Reports follow a portal account with the same e-mail */}
                    {!teamModal.full && !teamModal.promoteUserId && (function(){
                      var _pidM = teamModal.partnerId ? String(teamModal.partnerId) : "";
                      var _cl = (clients || []).filter(function(c){ if (c.status === "deleted") return false; if (c.ownerGroup) return false; return _pidM ? String(c.partnerId || "") === _pidM : true; });
                      _cl.sort(function(a, b){ return String(a.name || "").localeCompare(String(b.name || "")); });
                      var _has = !!teamModal.dataClientId;
                      /* the admin's CURRENT link may point at a client outside the partner (e.g. an old client
                         row created before the partner layer) — show it, flagged, instead of silently keeping it */
                      var _curC = _has ? ((clients || []).filter(function(c){ return String(c.id) === String(teamModal.dataClientId); })[0] || null) : null;
                      var _curOut = _has && !_cl.some(function(c){ return String(c.id) === String(teamModal.dataClientId); });
                      return (
                        <div style={{marginBottom:14, padding:"8px 12px", background:_has ? "#eef2ff" : "#fff7ed", border:"1px solid " + (_has ? "#c7d2fe" : "#fed7aa"), borderRadius:8}}>
                          <div style={{fontSize:10.5,fontWeight:700,color:"#334155",marginBottom:4}}>{"\uD83D\uDCCA Portal data access (Market \u00b7 Dashboards \u00b7 Reports)"}</div>
                          <select value={teamModal.dataClientId || ""} onChange={function(e){ setTeamModal(Object.assign({}, teamModal, { dataClientId: e.target.value })); }}
                            style={{width:"100%", padding:"7px 9px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11.5, fontFamily:"inherit", background:"#fff"}}>
                            <option value="">{"\u2014 none: admin panel only, no study data \u2014"}</option>
                            {_curOut ? <option value={teamModal.dataClientId}>{"\u26A0 " + (_curC ? _curC.name : (teamModal.dataClientName || ("client " + teamModal.dataClientId))) + " \u2014 current link, NOT under this partner"}</option> : null}
                            {_cl.map(function(c){ return <option key={c.id} value={c.id}>{c.name + (c.status !== "active" ? " (" + c.status + ")" : "")}</option>; })}
                          </select>
                          {_curOut ? <div style={{fontSize:10, color:"#b45309", fontWeight:700, marginTop:4, lineHeight:1.5}}>{"\u26A0 This admin is currently linked to \u201c" + (_curC ? _curC.name : (teamModal.dataClientName || "a client")) + "\u201d, which is not one of this partner's clients. Either open that client in Client Management and set its \u201c\uD83E\uDD1D Reseller partner\u201d to this partner (it then appears in this list), or pick another client here."}</div> : null}
                          <div style={{fontSize:10, color:"#64748b", marginTop:4, lineHeight:1.5}}>
                            {_has
                              ? "Signs in as a user of this client: same password, the client's studies, dashboards and reports. Without the \u201cFULL reports\u201d feature the reports open as SAMPLES (Full Year " + (new Date().getFullYear() - 2) + ")."
                              : (_pidM && !_cl.length
                                  ? "This partner has no client yet \u2014 create one in Clients (reseller partner = this partner) with the studies, dashboards and reports to show, then pick it here."
                                  : "Without a client the admin sees the admin panel only: Market, Dashboards and Reports stay empty. Pick the client whose studies, dashboards and (sample) reports this admin should open.")}
                          </div>
                        </div>
                      );
                    })()}
                    {/* r102: report access of a limited / partner admin — full decks, samples only, or whatever the linked client has */}
                    {!teamModal.full && (function(){
                      var _ra = teamModal.reportAccess || "";
                      var _opt = function(val, lbl, sub, col){
                        var on = _ra === val;
                        return (
                          <div key={val || "client"} onClick={function(){ setTeamModal(Object.assign({}, teamModal, { reportAccess: val })); }}
                            style={{display:"flex",alignItems:"flex-start",gap:8,padding:"6px 10px",borderRadius:8,border:"1px solid "+(on?col.b:"#f1f5f9"),background:on?col.bg:"#fff",marginBottom:4,cursor:"pointer"}}>
                            <span style={{fontSize:12,color:on?col.t:"#94a3b8",marginTop:1}}>{on?"\u25C9":"\u25CB"}</span>
                            <div><div style={{fontSize:11,fontWeight:on?700:500,color:on?col.t:"#475569"}}>{lbl}</div><div style={{fontSize:9.5,color:"#64748b"}}>{sub}</div></div>
                          </div>
                        );
                      };
                      return (
                        <div style={{marginBottom:14, padding:"8px 12px", background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8}}>
                          <div style={{fontSize:10.5,fontWeight:700,color:"#334155",marginBottom:6}}>{"\uD83D\uDCD1 Report access (in the portal)"}</div>
                          {_opt("", "Follow the linked client", "Full decks only if that client has the \u201cFULL reports\u201d feature; otherwise samples \u2014 like any user of the client.", {b:"#bfdbfe", bg:"#eff6ff", t:"#1d4ed8"})}
                          {_opt("full", "Full reports", "Opens and exports the complete report decks, whatever the client's feature says.", {b:"#a7f3d0", bg:"#ecfdf5", t:"#047857"})}
                          {_opt("sample", "Sample reports only", "Every report opens as a SAMPLE (Full Year " + (new Date().getFullYear() - 2) + ", quarterly allowance, \u201cOrder the full report\u201d), even if the client has the feature.", {b:"#9fd8e8", bg:"#eff9fb", t:"#0e7490"})}
                        </div>
                      );
                    })()}
                    {!teamModal.full && (
                      <div style={{marginBottom:14}}>
                        <div style={{fontSize:10.5,fontWeight:700,color:"#334155",marginBottom:6}}>Can manage these admin sections:</div>
                        {ADMIN_SECTIONS.map(function(s){
                          /* r45: "analytics" is on when the full section OR any analytics:<tab> is granted */
                          var _anKeys9 = teamModal.permissions.filter(function(k9){ return String(k9).indexOf("analytics:") === 0; });
                          var on = teamModal.permissions.indexOf(s.key) >= 0 || (s.key === "analytics" && _anKeys9.length > 0);
                          var _anAll9 = teamModal.permissions.indexOf("analytics") >= 0;
                          return (
                            <div key={s.key}>
                            <div onClick={function(){
                              var np = teamModal.permissions.slice();
                              if (s.key === "analytics") {
                                np = np.filter(function(k9){ return k9 !== "analytics" && String(k9).indexOf("analytics:") !== 0; });
                                if (!on) np.push("analytics");
                              } else if (on) np.splice(np.indexOf(s.key),1); else np.push(s.key);
                              setTeamModal(Object.assign({},teamModal,{permissions:np}));
                            }} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",borderRadius:8,border:"1px solid "+(on?"#bfdbfe":"#f1f5f9"),background:on?"#eff6ff":"#fff",marginBottom:5,cursor:"pointer"}}>
                              <span style={{fontSize:13,color:on?"#2563eb":"#94a3b8"}}>{on?"\u2611":"\u2610"}</span>
                              <span style={{fontSize:13}}>{s.icon}</span>
                              <span style={{fontSize:11.5,fontWeight:on?700:500,color:on?"#1d4ed8":"#475569"}}>{s.label}</span>
                              {s.key === "analytics" && on ? <span style={{marginLeft:"auto",fontSize:9,fontWeight:700,color:"#1d4ed8",background:"#dbeafe",borderRadius:8,padding:"1px 7px"}}>{_anAll9 ? "all tabs" : _anKeys9.length + " of " + ANALYTICS_TABS.length + " tabs"}</span> : null}
                            </div>
                            {s.key === "analytics" && on && (
                              <div style={{marginLeft:18, marginBottom:8}}>
                                <div onClick={function(){ var np = teamModal.permissions.filter(function(k9){ return String(k9).indexOf("analytics:") !== 0; }); if (np.indexOf("analytics") < 0) np.push("analytics"); setTeamModal(Object.assign({},teamModal,{permissions:np})); }}
                                  style={{display:"flex",alignItems:"center",gap:8,padding:"5px 12px",borderRadius:8,border:"1px solid "+(_anAll9?"#bfdbfe":"#f8fafc"),background:_anAll9?"#eff6ff":"#fff",marginBottom:4,cursor:"pointer"}}>
                                  <span style={{fontSize:12,color:_anAll9?"#2563eb":"#94a3b8"}}>{_anAll9?"\u25C9":"\u25CB"}</span>
                                  <span style={{fontSize:11,fontWeight:_anAll9?700:500,color:_anAll9?"#1d4ed8":"#475569"}}>All tabs</span>
                                </div>
                                <div onClick={function(){ if (!_anAll9) return; var np = teamModal.permissions.filter(function(k9){ return k9 !== "analytics"; }); np.push("analytics:users"); setTeamModal(Object.assign({},teamModal,{permissions:np})); }}
                                  style={{display:"flex",alignItems:"center",gap:8,padding:"5px 12px",borderRadius:8,border:"1px solid "+(!_anAll9?"#fde68a":"#f8fafc"),background:!_anAll9?"#fffbeb":"#fff",marginBottom:4,cursor:"pointer"}}>
                                  <span style={{fontSize:12,color:!_anAll9?"#d97706":"#94a3b8"}}>{!_anAll9?"\u25C9":"\u25CB"}</span>
                                  <span style={{fontSize:11,fontWeight:!_anAll9?700:500,color:!_anAll9?"#92400e":"#475569"}}>Only these tabs:</span>
                                </div>
                                {!_anAll9 && ANALYTICS_TABS.map(function(t9){
                                  var k9 = "analytics:" + t9.k, onT = teamModal.permissions.indexOf(k9) >= 0;
                                  return (
                                    <div key={t9.k} onClick={function(){
                                      var np = teamModal.permissions.slice();
                                      if (onT) { np.splice(np.indexOf(k9),1); } else np.push(k9);
                                      setTeamModal(Object.assign({},teamModal,{permissions:np}));
                                    }} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 12px",marginLeft:14,borderRadius:8,border:"1px solid "+(onT?"#fde68a":"#f8fafc"),background:onT?"#fffbeb":"#fff",marginBottom:3,cursor:"pointer"}}>
                                      <span style={{fontSize:12,color:onT?"#d97706":"#94a3b8"}}>{onT?"\u2611":"\u2610"}</span>
                                      <span style={{fontSize:11,fontWeight:onT?700:500,color:onT?"#92400e":"#475569"}}>{t9.l}</span>
                                    </div>
                                  );
                                })}
                                <div style={{fontSize:9,color:"#94a3b8",marginTop:3}}>{"Analytics data for a study-scoped admin is limited to their studies (below): activity, exports and fingerprint lists show only those studies' rows; the Audit Log shows only their own actions."}</div>
                              </div>
                            )}
                            </div>
                          );
                        })}

                        {/* ── Study scope ── */}
                        <div style={{fontSize:10.5,fontWeight:700,color:"#334155",margin:"12px 0 6px"}}>Study scope (applies to User Management):</div>
                        <div onClick={function(){ setTeamModal(Object.assign({},teamModal,{scopeAll:true})); }}
                          style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",borderRadius:8,border:"1px solid "+(teamModal.scopeAll?"#bfdbfe":"#f1f5f9"),background:teamModal.scopeAll?"#eff6ff":"#fff",marginBottom:5,cursor:"pointer"}}>
                          <span style={{fontSize:13,color:teamModal.scopeAll?"#2563eb":"#94a3b8"}}>{teamModal.scopeAll?"\u25C9":"\u25CB"}</span>
                          <span style={{fontSize:11.5,fontWeight:teamModal.scopeAll?700:500,color:teamModal.scopeAll?"#1d4ed8":"#475569"}}>All studies</span>
                        </div>
                        <div onClick={function(){ setTeamModal(Object.assign({},teamModal,{scopeAll:false})); }}
                          style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",borderRadius:8,border:"1px solid "+(!teamModal.scopeAll?"#fde68a":"#f1f5f9"),background:!teamModal.scopeAll?"#fffbeb":"#fff",marginBottom:5,cursor:"pointer"}}>
                          <span style={{fontSize:13,color:!teamModal.scopeAll?"#d97706":"#94a3b8"}}>{!teamModal.scopeAll?"\u25C9":"\u25CB"}</span>
                          <span style={{fontSize:11.5,fontWeight:!teamModal.scopeAll?700:500,color:!teamModal.scopeAll?"#92400e":"#475569"}}>Only these studies:</span>
                        </div>
                        {!teamModal.scopeAll && (
                          <div style={{marginLeft:18,marginBottom:6}}>
                            {studyTypes.map(function(st){
                              var onSc = teamModal.studyScope.indexOf(st.code) >= 0;
                              return (
                                <div key={st.code} onClick={function(){
                                  var ns = teamModal.studyScope.slice();
                                  if (onSc) ns.splice(ns.indexOf(st.code),1); else ns.push(st.code);
                                  setTeamModal(Object.assign({},teamModal,{studyScope:ns}));
                                }} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 12px",borderRadius:8,border:"1px solid "+(onSc?"#fde68a":"#f8fafc"),background:onSc?"#fffbeb":"#fff",marginBottom:4,cursor:"pointer"}}>
                                  <span style={{fontSize:12,color:onSc?"#d97706":"#94a3b8"}}>{onSc?"\u2611":"\u2610"}</span>
                                  <span style={{fontSize:11,fontWeight:onSc?700:500,color:onSc?"#92400e":"#475569"}}>{(st.category==="private"?"\uD83D\uDD12 ":"\uD83C\uDF10 ") + st.name + " (" + st.code + ")"}</span>
                                </div>
                              );
                            })}
                            <div style={{fontSize:9,color:"#94a3b8",marginTop:3}}>They only see and manage users of these studies; other studies on a shared user are locked for them.</div>
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                      <button onClick={function(){ setTeamModal(null); }} style={adminBtn("#f1f5f9","#475569","#e2e8f0")}>Cancel</button>
                      <button onClick={function(){
                        var tm = teamModal;
                        if (!tm.name.trim()) { alert("Name is required"); return; }
                        if (!tm.full && !tm.scopeAll && !tm.studyScope.length) { alert("Select at least one study for the scope (or choose All studies)"); return; }
                        var payload = { name: tm.name.trim(), full: !!tm.full, permissions: tm.permissions, studyScope: (tm.full || tm.scopeAll) ? [] : tm.studyScope, partnerId: tm.full ? null : (tm.partnerId || null) };   /* r97 */
                        if (!tm.full && !tm.promoteUserId) payload.dataClientId = tm.dataClientId ? (parseInt(tm.dataClientId, 10) || null) : null;   /* r99: portal data access */
                        payload.reportAccess = tm.full ? null : ((tm.reportAccess === "full" || tm.reportAccess === "sample") ? tm.reportAccess : null);   /* r102 */
                        if (tm.password) payload.password = tm.password;
                        var p9;
                        if (tm.promoteUserId) { p9 = api.promoteUserToAdmin({userId: tm.promoteUserId, full: !!tm.full, permissions: tm.permissions, studyScope: (tm.full || tm.scopeAll) ? [] : tm.studyScope, partnerId: tm.full ? null : (tm.partnerId || null), reportAccess: payload.reportAccess}); }
                        else if (tm.adminId) { p9 = api.updateTeamMember(tm.adminId, payload); }
                        else {
                          if (!tm.password) { alert("Password is required for a new account"); return; }
                          payload.email = tm.email.trim();
                          p9 = api.createTeamMember(payload);
                        }
                        p9.then(function(r9){
                          setTeamModal(null); reloadTeam();
                          if (r9 && r9.dataWarning) { alert(r9.dataWarning); return; }
                          if (tm.promoteUserId) alert(tm.name + " is now an admin. They sign in with their current password and will see only the sections you granted.");
                          else if (!tm.full && r9 && r9.dataLinked) alert(tm.name + " now opens the portal data of " + (r9.dataClientName || "the selected client") + " (Market, Dashboards, Reports). They must sign in again.");
                        }).catch(function(e){ alert("Error: " + e.message); });
                      }} style={adminBtn("#2563eb","#fff","#2563eb")}>{teamModal.adminId ? "Save Changes" : "Create Account"}</button>
                    </div>
                  </div>
                </div>
              )}

          {/* ═══ MASTER EXPORT ═══ */}
          {tab === "master_export" && _isFullAdmin && (
            <div style={{maxWidth:860}}>
              <div style={{fontSize:11.5,color:"#64748b",lineHeight:1.6,marginBottom:14}}>
                Generate a quarterly master data file for one user account, <b>directly on the server</b> — the data never passes through the portal.
                The file carries that user's fingerprint, and a second confidential file lists every mark so a leaked copy can be traced.
              </div>

              {/* Step 1: pick user */}
              <div style={{background:"#fff",borderRadius:12,border:"1.5px solid #e2e8f0",padding:"16px 20px",marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:800,color:"#0f172a",marginBottom:8}}>{"1\uFE0F\u20E3 User account"}</div>
                {meUser ? (
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:34,height:34,borderRadius:"50%",background:"#eef4fb",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,color:"#2563eb"}}>{(meUser.name||"?").charAt(0).toUpperCase()}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12.5,fontWeight:700,color:"#0f172a"}}>{meUser.name}</div>
                      <div style={{fontSize:10,color:"#64748b"}}>{meUser.email}{(function(){var c=_clientById(meUser.clientId);return c?" \u00B7 "+c.name:"";})()}</div>
                    </div>
                    <button onClick={function(){ setMeUser(null); setMeStudy(""); setMeStruct(null); setMeMsg(""); setMeFiles(null); }} style={adminBtn("#f1f5f9","#475569","#e2e8f0")}>Change</button>
                  </div>
                ) : (
                  <div>
                    <input style={{...adminInp,width:"100%",boxSizing:"border-box",marginBottom:8}} placeholder={"Search users by name, email or company…"} value={meUserQ}
                      onChange={function(e){ setMeUserQ(e.target.value); }} />
                    {meUserQ.trim() && (
                      <div style={{border:"1px solid #f1f5f9",borderRadius:9,maxHeight:220,overflowY:"auto"}}>
                        {(users||[]).filter(function(u){
                          if (u.status !== "active") return false;
                          var q = meUserQ.toLowerCase(); var c = _clientById(u.clientId);
                          return (u.name||"").toLowerCase().indexOf(q)>=0 || (u.email||"").toLowerCase().indexOf(q)>=0 || (c&&(c.name||"").toLowerCase().indexOf(q)>=0);
                        }).slice(0,12).map(function(u){
                          return (
                            <div key={u.id} onClick={function(){ setMeUser(u); setMeUserQ(""); setMeStruct(null); setMeMsg(""); var acc=userEffAccess(u); if(acc.length===1) setMeStudy(acc[0].code); }}
                              style={{display:"flex",alignItems:"center",gap:9,padding:"8px 13px",borderBottom:"1px solid #f8fafc",cursor:"pointer"}}>
                              <div style={{width:26,height:26,borderRadius:"50%",background:"#eef4fb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#2563eb"}}>{(u.name||"?").charAt(0).toUpperCase()}</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:11.5,fontWeight:700,color:"#0f172a"}}>{u.name}</div>
                                <div style={{fontSize:9.5,color:"#64748b"}}>{u.email}{(function(){var c=_clientById(u.clientId);return c?" \u00B7 "+c.name:"";})()}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Step 2: study + geography */}
              {meUser && (
                <div style={{background:"#fff",borderRadius:12,border:"1.5px solid #e2e8f0",padding:"16px 20px",marginBottom:12}}>
                  <div style={{fontSize:12,fontWeight:800,color:"#0f172a",marginBottom:8}}>{"2\uFE0F\u20E3 Study & geography"}</div>
                  <div style={{display:"flex",gap:14,flexWrap:"wrap",alignItems:"center"}}>
                    <select value={meStudy} onChange={function(e){ setMeStudy(e.target.value); setMeStruct(null); setMeMsg(""); }}
                      style={{...adminInp,minWidth:200}}>
                      <option value="">Select study…</option>
                      {userEffAccess(meUser).map(function(a){ return <option key={a.code} value={a.code}>{(a.country ? a.country + " — " : "") + a.code}</option>; })}
                    </select>
                    {[{v:"country",l:"Total country"},{v:"region",l:"With region"},{v:"governorate",l:"With region + governorate"}].map(function(g){
                      var on = meGeo === g.v;
                      return (
                        <label key={g.v} onClick={function(){ setMeGeo(g.v); setMeStruct(null); setMeMsg(""); }}
                          style={{display:"flex",alignItems:"center",gap:6,padding:"7px 13px",borderRadius:8,border:"1.5px solid "+(on?"#2563eb":"#e2e8f0"),background:on?"#eff6ff":"#fff",cursor:"pointer",fontSize:11.5,fontWeight:on?700:500,color:on?"#1d4ed8":"#475569"}}>
                          <span>{on?"\u25C9":"\u25CB"}</span>{g.l}
                        </label>
                      );
                    })}
                    <span style={{width:1,height:24,background:"#e2e8f0"}} />
                    {[{v:"wide",l:"Quarters as columns"},{v:"long",l:"Quarters as rows"}].map(function(g){
                      var on = meLayout === g.v;
                      return (
                        <label key={g.v} onClick={function(){ setMeLayout(g.v); setMeStruct(null); setMeMsg(""); }}
                          title={g.v === "wide" ? "One row per item — each quarter gets its own Units/Value columns (far fewer rows)" : "One row per item per quarter"}
                          style={{display:"flex",alignItems:"center",gap:6,padding:"7px 13px",borderRadius:8,border:"1.5px solid "+(on?"#0d9488":"#e2e8f0"),background:on?"#f0fdfa":"#fff",cursor:"pointer",fontSize:11.5,fontWeight:on?700:500,color:on?"#0f766e":"#475569"}}>
                          <span>{on?"\u25C9":"\u25CB"}</span>{g.l}
                        </label>
                      );
                    })}
                    <button disabled={!meStudy || meBusy} onClick={function(){
                      setMeStruct(null); setMeFiles(null); meFetchStruct(null);
                    }} style={{...adminBtn("#2563eb","#fff","#2563eb"),opacity:(!meStudy||meBusy)?0.5:1}}>{meBusy && !meStruct ? "Checking…" : "Preview structure"}</button>
                  </div>
                  {/* ── Period range (optional) ── */}
                  {/* ── Period type + range ── */}
                  {meStruct && (
                    <div style={{marginTop:12,paddingTop:12,borderTop:"1px dashed #e2e8f0"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                        <span style={{fontSize:10.5,fontWeight:700,color:"#64748b"}}>{"\uD83D\uDCC5 Period type:"}</span>
                        {[{v:"Yearly",l:"Yearly"},{v:"Quarterly",l:"Quarterly"},{v:"Monthly",l:"Monthly"},{v:"MAT",l:"MAT"},{v:"YTD",l:"YTD"}].map(function(pt){
                          var _avail = !meStruct.periodTypes || meStruct.periodTypes[pt.v];
                          var on = (mePeriod.ptype || meStruct.periodType || "Quarterly") === pt.v;
                          if (!_avail) return null;
                          var _isW = pt.v === "MAT" || pt.v === "YTD";
                          return (
                            <label key={pt.v} onClick={function(){ if (meBusy) return; setMeFiles(null); var np = Object.assign({}, mePeriod, {ptype:pt.v, fromY:"", fromS:"", toY:"", toS:"", sels:[]}); setMePeriod(np); meFetchStruct(meSelDims, np); }}
                              title={pt.v === "MAT" ? "Moving Annual Total — each column is the rolling last 4 quarters" : pt.v === "YTD" ? "Year-to-date — each column sums the quarters of the year up to that point" : undefined}
                              style={{display:"flex",alignItems:"center",gap:6,padding:"6px 13px",borderRadius:8,border:"1.5px solid "+(on?(_isW?"#d97706":"#7c3aed"):"#e2e8f0"),background:on?(_isW?"#fffbeb":"#f5f3ff"):"#fff",cursor:"pointer",fontSize:11,fontWeight:on?700:500,color:on?(_isW?"#92400e":"#6d28d9"):"#475569"}}>
                              <span>{on?"\u25C9":"\u25CB"}</span>{pt.l}
                            </label>
                          );
                        })}
                      </div>
                      {(meStruct.allPeriods || []).length > 0 && (function(){
                        /* MAT/YTD: no From/To range — pick the exact periods from a
                           dropdown; the file contains ONLY the picked windows. */
                        if (mePeriod.ptype === "MAT" || mePeriod.ptype === "YTD") {
                          var _pfx = mePeriod.ptype + " ";
                          var _qsW = (meStruct.periodType === "Monthly" || meStruct.periodType === "Yearly") ? [] : meStruct.allPeriods.slice().reverse();
                          var _selW = mePeriod.sels || [];
                          var _addOpts = _qsW.filter(function(qq){ return _selW.indexOf(qq.y + "-" + qq.s) < 0; });
                          var _selSorted = _selW.slice().sort(function(a,b){ var pa=a.split("-"),pb=b.split("-"); return (parseInt(pb[0],10)*10+parseInt(pb[1],10))-(parseInt(pa[0],10)*10+parseInt(pa[1],10)); });
                          var _updSels = function(ns){ setMeFiles(null); var np = Object.assign({}, mePeriod, {sels:ns}); setMePeriod(np); meFetchStruct(meSelDims, np); };
                          return (
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                              <span style={{fontSize:10.5,fontWeight:700,color:"#64748b"}}>{"Periods:"}</span>
                              <select value="" disabled={meBusy} onChange={function(e){ var v = e.target.value; if (!v) return; _updSels(_selW.concat([v])); }}
                                style={{padding:"6px 10px",border:"1.5px solid #d97706",borderRadius:8,background:"#fffbeb",color:"#92400e",fontSize:11,fontWeight:700,fontFamily:"inherit",outline:"none",cursor:"pointer"}}>
                                <option value="">{"+ Add " + mePeriod.ptype + " period…"}</option>
                                {_addOpts.map(function(qq){ return <option key={"w"+qq.y+"-"+qq.s} value={qq.y+"-"+qq.s}>{_pfx + "Q" + qq.s + " " + qq.y}</option>; })}
                              </select>
                              {_selSorted.map(function(v){
                                var pv = v.split("-");
                                return (
                                  <span key={"wc"+v} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:8,border:"1.5px solid #d97706",background:"#fffbeb",fontSize:10.5,fontWeight:700,color:"#92400e"}}>
                                    {_pfx + "Q" + pv[1] + " " + pv[0]}
                                    <span onClick={function(){ if (meBusy) return; _updSels(_selW.filter(function(x){ return x !== v; })); }}
                                      style={{cursor:"pointer",color:"#b45309",fontWeight:800}}>{"✕"}</span>
                                  </span>
                                );
                              })}
                              {_selSorted.length > 0 && (
                                <button disabled={meBusy} onClick={function(){ _updSels([]); }}
                                  style={{padding:"5px 10px",border:"1px solid #e2e8f0",borderRadius:7,background:"#f8fafc",color:"#475569",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{"✕ Clear"}</button>
                              )}
                              {!_selSorted.length && <span style={{fontSize:9.5,color:"#94a3b8"}}>{"no periods picked — ALL available " + mePeriod.ptype + " periods will be extracted"}</span>}
                            </div>
                          );
                        }
                        var _aq = meStruct.allPeriods;
                        var _isYr = meStruct.periodType === "Yearly"; var _per = meStruct.periodType === "Monthly" ? 100 : _isYr ? 1 : 10;
                        var _toVal = function(qq){ return meStruct.periodType === "Yearly" ? qq.y : (qq.y * _per + qq.s); };
                        var _fromV = mePeriod.fromY ? (meStruct.periodType === "Yearly" ? parseInt(mePeriod.fromY,10) : parseInt(mePeriod.fromY,10)*_per + (parseInt(mePeriod.fromS,10)||1)) : null;
                        var _toV = mePeriod.toY ? (meStruct.periodType === "Yearly" ? parseInt(mePeriod.toY,10) : parseInt(mePeriod.toY,10)*_per + (parseInt(mePeriod.toS,10)|| (_per===100?12:4))) : null;
                        var _fromOpts = _aq.filter(function(qq){ return _toV == null || _toVal(qq) <= _toV; });
                        var _toOpts = _aq.filter(function(qq){ return _fromV == null || _toVal(qq) >= _fromV; });
                        var _st = function(active){ return {padding:"6px 10px",border:"1.5px solid "+(active?"#2563eb":"#e2e8f0"),borderRadius:8,background:active?"#eff6ff":"#fff",color:active?"#1d4ed8":"#475569",fontSize:11,fontWeight:600,fontFamily:"inherit",outline:"none",cursor:"pointer"}; };
                        return (
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            <span style={{fontSize:10.5,fontWeight:700,color:"#64748b"}}>{"Range:"}</span>
                            <select value={mePeriod.fromY ? (mePeriod.fromY+"-"+mePeriod.fromS) : ""} onChange={function(e){
                              var v = e.target.value; setMeFiles(null);
                              if (!v) setMePeriod(Object.assign({}, mePeriod, {fromY:"", fromS:""}));
                              else { var p = v.split("-"); setMePeriod(Object.assign({}, mePeriod, {fromY:p[0], fromS:p[1]})); }
                            }} style={_st(!!mePeriod.fromY)}>
                              <option value="">From (earliest)</option>
                              {_fromOpts.map(function(qq){ return <option key={"f"+qq.label} value={qq.y+"-"+qq.s}>{qq.label}</option>; })}
                            </select>
                            <span style={{color:"#94a3b8"}}>{"\u2192"}</span>
                            <select value={mePeriod.toY ? (mePeriod.toY+"-"+mePeriod.toS) : ""} onChange={function(e){
                              var v = e.target.value; setMeFiles(null);
                              if (!v) setMePeriod(Object.assign({}, mePeriod, {toY:"", toS:""}));
                              else { var p = v.split("-"); setMePeriod(Object.assign({}, mePeriod, {toY:p[0], toS:p[1]})); }
                            }} style={_st(!!mePeriod.toY)}>
                              <option value="">To (latest)</option>
                              {_toOpts.map(function(qq){ return <option key={"t"+qq.label} value={qq.y+"-"+qq.s}>{qq.label}</option>; })}
                            </select>
                            {(mePeriod.fromY || mePeriod.toY) && (
                              <button onClick={function(){ setMeFiles(null); setMePeriod(Object.assign({}, mePeriod, {fromY:"",fromS:"",toY:"",toS:""})); }}
                                style={{padding:"5px 10px",border:"1px solid #e2e8f0",borderRadius:7,background:"#f8fafc",color:"#475569",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{"\u2715 Full history"}</button>
                            )}
                            <span style={{fontSize:9,color:"#94a3b8"}}>{"changing the range needs a new preview"}</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: approve structure & generate */}
              {meStruct && (
                <div style={{background:"#fff",borderRadius:12,border:"1.5px solid "+(meStruct.ok?"#6ee7b7":"#fecaca"),padding:"16px 20px",marginBottom:12}}>
                  <div style={{fontSize:12,fontWeight:800,color:"#0f172a",marginBottom:10}}>{"3\uFE0F\u20E3 Structure — approve before extraction"}</div>
                  <div style={{fontSize:10.5,color:"#334155",lineHeight:1.7}}>
                    <div>
                      <b>{"Columns (" + (((meSelDims || []).length) + (meStruct.fixed || []).length) + ") — click to select / deselect:"}</b>
                      <div style={{marginTop:4}}>
                        {(meStruct.dims || []).map(function(d){
                          var on = (meSelDims || []).indexOf(d.key) >= 0;
                          return (
                            <span key={d.key} onClick={function(){
                              if (d.required || meBusy) return;
                              var ns = (meSelDims || []).slice();
                              if (on) ns.splice(ns.indexOf(d.key), 1); else ns.push(d.key);
                              if (!ns.length) { setMeMsg("Error: keep at least one column selected"); return; }
                              meFetchStruct(ns);
                            }} title={d.required ? "Required — carries the user's fingerprint" : (on ? "Click to remove this column" : "Click to add this column back")}
                              style={{display:"inline-block",background:d.required?"#fffbeb":on?"#eff6ff":"#fff",border:"1.5px solid "+(d.required?"#fde68a":on?"#93c5fd":"#e2e8f0"),borderRadius:8,padding:"2px 9px",fontSize:9.5,fontWeight:700,color:d.required?"#92400e":on?"#1d4ed8":"#94a3b8",margin:"1px 3px 1px 0",cursor:d.required?"default":"pointer",textDecoration:on||d.required?"none":"line-through",opacity:meBusy?0.6:1}}>
                              {(d.required ? "\uD83D\uDD12 " : on ? "\u2611 " : "\u2610 ") + d.label}
                            </span>
                          );
                        })}
                        {(meStruct.fixed || []).map(function(c,i){ return <span key={"fx"+i} style={{display:"inline-block",background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:8,padding:"2px 9px",fontSize:9.5,fontWeight:700,color:"#475569",margin:"1px 3px 1px 0"}}>{c}</span>; })}
                      </div>
                      {(meStruct.missingDims || []).length > 0 && (
                        <div style={{marginTop:6,fontSize:9.5,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:7,padding:"4px 9px",fontWeight:600}}>
                          {"ⓘ Not available for this study: " + meStruct.missingDims.join(", ") + " — the " + (meStruct.table || "data") + " table has no such column. Once the column is added to the table, it will appear here automatically."}
                        </div>
                      )}
                      {meStruct.mexVersion === undefined && (
                        /* The structure response has no mexVersion → the SERVER is still
                           running a build older than the Form/Pack/Strength fallback.
                           Without this banner the old server just silently shows fewer
                           columns and looks like the fix "didn't work". */
                        <div style={{marginTop:6,fontSize:9.5,color:"#b91c1c",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:7,padding:"4px 9px",fontWeight:700}}>
                          {"⚠ The server is running an OLDER build — extra columns (Form Type, Pack Size, Strength) and their diagnostics are added by the updated server.js. Deploy the new server.js and restart pm2, then click Preview structure again."}
                        </div>
                      )}
                    </div>
                    <div style={{marginTop:6}}><b>Rows:</b> {meBusy ? "recounting\u2026" : meStruct.rowEstimate.toLocaleString()}
                      {(meStruct.parts || 1) > 1 && (meStruct.parts || 1) <= (meStruct.maxParts || 8) && <span style={{color:"#d97706",fontWeight:700}}>{" \u2014 will be split into " + meStruct.parts + " Excel files (max " + ((meStruct.partRows || 1000000)).toLocaleString() + " rows each)"}</span>}
                      {(meStruct.parts || 1) > (meStruct.maxParts || 8) && <span style={{color:"#dc2626",fontWeight:700}}>{" \u2014 too large even for " + (meStruct.maxParts || 8) + " files: deselect high-cardinality columns (Diagnosis, Specialty\u2026) or choose a coarser geography"}</span>}
                    </div>
                    <div style={{marginTop:2}}><b>Quarters:</b> {meStruct.quarters.length ? (meStruct.quarters[0] + " \u2192 " + meStruct.quarters[meStruct.quarters.length-1] + " (" + meStruct.quarters.length + " quarters, all data)") : "—"}</div>
                    <div style={{marginTop:2}}><b>Fingerprint:</b> {meStruct.fingerprint.enabled && meStruct.fingerprint.hasSeed
                      ? <span style={{color:"#059669",fontWeight:800}}>{"\u2713 ENABLED — file will carry " + meStruct.user.name + "'s fingerprint"}</span>
                      : <span style={{color:"#dc2626",fontWeight:800}}>{"\u2715 NOT ENABLED — enable fingerprint for this user first (User Analytics \u2192 Data Fingerprint)"}</span>}</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginTop:14}}>
                    <button disabled={!meStruct.ok || meBusy} onClick={function(){
                      if (!window.confirm("Generate the master file for " + meStruct.user.name + " (" + meStruct.rowEstimate.toLocaleString() + " rows)?\n\nTwo files download: the fingerprinted master data, and the confidential fingerprint map. Large extracts can take a few minutes.")) return;
                      setMeBusy(true); setMeFiles(null); setMeMsg("Starting generation on the server…");
                      api.generateMasterExport({userId: meUser.id, studyType: meStudy, geo: meGeo, dims: meSelDims, layout: meLayout, ptype: mePeriod.ptype, sels: (mePeriod.sels || []).join(","), fromY: mePeriod.fromY, fromS: mePeriod.fromS, toY: mePeriod.toY, toS: mePeriod.toS})
                        .then(function(r){
                          var _done = function(rr){
                            setMeBusy(false); setMeMsg("");
                            setMeFiles({ pairId: rr.pairId, rows: rr.rows, marks: rr.marks, files: (rr.files || []).map(function(f){ return { token: f.token, name: f.name, done: false }; }) });
                          };
                          if (r.files) { _done(r); return; } /* older sync server */
                          var _jt0 = Date.now();
                          var _poll = function(){
                            api.getMasterExportStatus(r.jobId).then(function(s){
                              if (s.status === "done" && s.result) { _done(s.result); }
                              else if (s.status === "error") { setMeBusy(false); setMeMsg("Error: " + (s.error || "generation failed — check pm2 logs")); }
                              else {
                                var _el = Math.round((Date.now() - _jt0) / 1000);
                                setMeMsg("Generating on the server… " + _el + "s elapsed — large extracts can take a few minutes. Keep this screen open.");
                                setTimeout(_poll, 4000);
                              }
                            }).catch(function(){ setTimeout(_poll, 6000); /* transient network blip — keep polling */ });
                          };
                          setTimeout(_poll, 3000);
                        })
                        .catch(function(e){ setMeBusy(false); setMeMsg("Error: " + e.message); });
                    }} style={{...adminBtn("#059669","#fff","#059669"),opacity:(!meStruct.ok||meBusy)?0.5:1}}>{meBusy ? "Working…" : "\u2705 Approve & extract to Excel"}</button>
                    <span style={{fontSize:10,color:"#64748b"}}>{"Downloads " + (((meStruct.parts || 1) > 1) ? meStruct.parts + " data files" : "the data file") + " + fingerprint map"}</span>
                  </div>
                </div>
              )}
              {meFiles && (
                <div style={{background:"#fff",borderRadius:12,border:"1.5px solid #6ee7b7",padding:"16px 20px",marginBottom:12}}>
                  <div style={{fontSize:12,fontWeight:800,color:"#065f46",marginBottom:4}}>{"\u2705 Generated — Pair ID " + meFiles.pairId}</div>
                  <div style={{fontSize:10.5,color:"#334155",marginBottom:12}}>{meFiles.rows.toLocaleString() + " data rows \u00B7 " + meFiles.marks + " fingerprint marks hidden in the Units figures. Download BOTH files — links work once and expire in 15 minutes."}</div>
                  {meFiles.files.map(function(f, fi){
                    var isFp = f.name.indexOf("_FINGERPRINT") >= 0;
                    return (
                      <div key={fi} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:isFp?"#fffbeb":"#f8fafc",border:"1px solid "+(isFp?"#fde68a":"#e2e8f0"),borderRadius:9,marginBottom:6}}>
                        <span style={{fontSize:16}}>{isFp ? "\uD83D\uDD75" : "\uD83D\uDCC4"}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:11,fontWeight:700,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
                          <div style={{fontSize:9.5,color:isFp?"#92400e":"#64748b"}}>{isFp ? "CONFIDENTIAL — the fingerprint map: every marked row with original vs delivered units. Keep it, never share it." : "The master data file — carries the user's invisible fingerprint."}</div>
                        </div>
                        <button disabled={f.done} onClick={function(){
                          var a9 = document.createElement("a");
                          a9.href = API_BASE + "/api/master-download/" + f.token;
                          a9.download = f.name;
                          document.body.appendChild(a9); a9.click();
                          setTimeout(function(){ try { document.body.removeChild(a9); } catch(e){} }, 2000);
                          var nf = { pairId: meFiles.pairId, rows: meFiles.rows, marks: meFiles.marks, files: meFiles.files.map(function(x, xi){ return xi === fi ? { token: x.token, name: x.name, done: true } : x; }) };
                          setMeFiles(nf);
                        }} style={{...adminBtn(f.done?"#f1f5f9":(isFp?"#d97706":"#2563eb"), f.done?"#94a3b8":"#fff", f.done?"#e2e8f0":(isFp?"#d97706":"#2563eb")),whiteSpace:"nowrap"}}>
                          {f.done ? "\u2713 Downloaded" : "\u2B07 Download"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {meMsg && <div style={{padding:"10px 14px",background:meMsg.indexOf("Error")===0?"#fef2f2":"#ecfdf5",border:"1px solid "+(meMsg.indexOf("Error")===0?"#fecaca":"#d1fae5"),borderRadius:9,fontSize:11,color:meMsg.indexOf("Error")===0?"#b91c1c":"#065f46",fontWeight:600}}>{meMsg}</div>}
            </div>
          )}

          {tab === "features" && (
            <div>
              {/* Study type selector + actions */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
                <select value={featStudy||""} onChange={function(e){setFeatStudy(e.target.value?Number(e.target.value):null);}}
                  style={{...adminInp, width:240, cursor:"pointer"}}>
                  <option value="">— Select Study Type —</option>
                  {studyTypes.filter(function(st){ return !_adminScope || _adminScope.indexOf(st.code) >= 0; }).map(function(st){ return <option key={st.id} value={st.id}>{st.category==="private"?"🔒":"🌐"} {st.name} ({st.code})</option>; })}
                </select>
                {featStudy && <button onClick={function(){setFeatModal("new");}} style={adminBtn("#2563eb","#fff","#2563eb")}>+ Add Feature</button>}
                {/* r113: data granularity of the study — decides what "latest complete period" means */}
                {featStudy && _isFullAdmin && (function(){
                  var st9 = null; for (var i9 = 0; i9 < studyTypes.length; i9++) if (studyTypes[i9].id === featStudy) st9 = studyTypes[i9];
                  var cur9 = (st9 && st9.periodGranularity) || "";
                  return (
                    <div style={{display:"flex", alignItems:"center", gap:6, marginLeft:12, padding:"4px 10px", background:cur9 === "quarterly" ? "#fff7ed" : "#f8fafc", border:"1px solid " + (cur9 === "quarterly" ? "#fed7aa" : "#e2e8f0"), borderRadius:8}}
                      title={"QUARTERLY: the data of this study is loaded by quarter — a quarter present in the data counts as complete even with one month inside (no Q* star, Quarterly permission shows it, reports' MAT ends at it). MONTHLY: the newest month decides whether the quarter is complete (Iraq style). Auto: monthly when the newest quarter carries more than one month, else quarterly."}>
                      <span style={{fontSize:10, color:"#64748b", fontWeight:700}}>{"\uD83D\uDCC5 Data granularity:"}</span>
                      <select value={cur9} onChange={function(e){
                          var v9 = e.target.value;
                          api.setStudyGranularity(featStudy, v9).then(function(r9){
                            setStudyTypes(studyTypes.map(function(x){ return x.id === featStudy ? Object.assign({}, x, { periodGranularity: v9 || null }) : x; }));
                            alert("Saved. " + (st9 ? st9.name : "Study") + " now runs as " + ((r9 && r9.effective) || v9 || "auto") + ". Users see the change within a minute.");
                          }).catch(function(e9){ alert("Error: " + e9.message); });
                        }} style={{...adminInp, width:250, fontSize:10.5, cursor:"pointer"}}>
                        <option value="">Auto (from the data)</option>
                        <option value="monthly">Monthly — newest month decides the open quarter</option>
                        <option value="quarterly">Quarterly only — a loaded quarter is complete</option>
                      </select>
                    </div>
                  );
                })()}
                {/* r197: maintenance switch — hide the study from ALL clients, re-open when done */}
                {featStudy && _isFullAdmin && (function(){
                  var stM = null; for (var iM = 0; iM < studyTypes.length; iM++) if (studyTypes[iM].id === featStudy) stM = studyTypes[iM];
                  if (!stM) return null;
                  var onM = !!stM.maintenance;
                  var sinceM = ""; try { sinceM = stM.maintenanceSince ? new Date(stM.maintenanceSince).toISOString().slice(0, 16).replace("T", " ") + " UTC" : ""; } catch (eS) {}
                  return (
                    <div style={{display:"flex", alignItems:"center", gap:8, marginLeft:12, padding:"4px 10px", background:onM ? "#fffbeb" : "#f8fafc", border:"1px solid " + (onM ? "#fcd34d" : "#e2e8f0"), borderRadius:8}}
                      title={onM ? "Closed for maintenance since " + sinceM + (stM.maintenanceNote ? " — " + stM.maintenanceNote : "") + ". Clients do not see the study; their open sessions get the maintenance notice. Admins keep full access." : "Close this study for maintenance: it disappears from every client's study list and every client request to it is refused with a maintenance notice until you re-open it. Admins keep full access."}>
                      <span style={{fontSize:10, color:onM ? "#92400e" : "#64748b", fontWeight:800}}>{onM ? "\uD83D\uDD27 UNDER MAINTENANCE" + (sinceM ? " since " + sinceM : "") : "\uD83D\uDD27 Maintenance:"}</span>
                      <button onClick={function(){
                          var noteM = "";
                          if (!onM) { noteM = window.prompt("Close " + stM.name + " for maintenance?\n\nAll clients lose access until you re-open it (admins keep working).\nOptional note shown to clients (e.g. \"data reload — back on Monday\"):", "") ; if (noteM === null) return; }
                          else if (!window.confirm("Re-open " + stM.name + " for all clients?")) return;
                          api.setStudyMaintenance(featStudy, !onM, noteM).then(function(r9){
                            setStudyTypes(studyTypes.map(function(x){ return x.id === featStudy ? Object.assign({}, x, { maintenance: !!(r9 && r9.maintenance), maintenanceNote: (r9 && r9.maintenanceNote) || "", maintenanceSince: (r9 && r9.maintenanceSince) || null }) : x; }));
                            alert(onM ? stM.name + " is open to clients again." : stM.name + " is now closed for maintenance — hidden from all clients.");
                          }).catch(function(e9){ alert("Error: " + e9.message); });
                        }} style={adminBtn(onM ? "#16a34a" : "#d97706", "#fff", onM ? "#16a34a" : "#d97706")}>
                        {onM ? "\u2705 Re-open study" : "Close for maintenance"}
                      </button>
                    </div>
                  );
                })()}
                {featStudy && (
                  <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:12}}>
                    <span style={{fontSize:10,color:"#64748b",fontWeight:600}}>Copy from:</span>
                    <select value={copyFrom} onChange={function(e){setCopyFrom(e.target.value);}}
                      style={{...adminInp, width:160, fontSize:10}}>
                      <option value="">—</option>
                      {studyTypes.filter(function(st){return st.id!==featStudy && (!_adminScope || _adminScope.indexOf(st.code) >= 0);}).map(function(st){ return <option key={st.id} value={st.id}>{st.name}</option>; })}
                    </select>
                    {copyFrom && <button onClick={function(){
                      if(!window.confirm("Copy missing features from selected study to current?")) return;
                      api.copyFeatures(Number(copyFrom), featStudy).then(function(r){
                        alert("Copied " + r.copied + " features");
                        reloadFeatures(); setCopyFrom("");
                      }).catch(function(e){alert("Error: " + e.message);});
                    }} style={adminBtn("#7c3aed","#fff","#7c3aed")}>📋 Copy</button>}
                  </div>
                )}
              </div>

              {!featStudy ? (
                <div style={{textAlign:"center",padding:"60px 20px",background:"#fff",borderRadius:14,border:"2px dashed #e2e8f0"}}>
                  <div style={{fontSize:40,marginBottom:10}}>⚙</div>
                  <div style={{fontSize:14,fontWeight:700,color:"#64748b"}}>Select a study type to manage its features</div>
                  <div style={{fontSize:11,color:"#94a3b8",marginTop:6}}>Features define which dimensions appear in the filter panel and pivot table</div>
                </div>
              ) : (() => {
                var feats = allFeatures.filter(function(f){return f.studyTypeId === featStudy;});
                var groups = {};
                feats.forEach(function(f){ var g=f.group||"Other"; if(!groups[g])groups[g]=[]; groups[g].push(f); });
                var stName = "";
                for(var si=0;si<studyTypes.length;si++){if(studyTypes[si].id===featStudy){stName=studyTypes[si].name;break;}}
                return (
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                      <span style={{fontSize:16,fontWeight:800,color:"#0f172a",letterSpacing:"-.02em"}}>{stName}</span>
                      <span style={{fontSize:10,background:"#dbeafe",color:"#1d4ed8",borderRadius:10,padding:"2px 10px",fontWeight:700}}>{feats.length} features</span>
                    </div>
                    {Object.keys(groups).map(function(gk){
                      var gFeats = groups[gk];
                      return (
                        <div key={gk} style={{background:"#fff",borderRadius:12,border:"1.5px solid #e2e8f0",marginBottom:12,overflow:"hidden"}}>
                          <div style={{padding:"10px 16px",background:"linear-gradient(90deg,#f8fafc,#eef2ff)",borderBottom:"1px solid #e2e8f0",display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:12,fontWeight:800,color:"#334155"}}>{gk}</span>
                            <span style={{fontSize:9,background:"#e0e7ff",color:"#4338ca",borderRadius:8,padding:"1px 7px",fontWeight:700}}>{gFeats.length}</span>
                          </div>
                          <table style={{width:"100%",borderCollapse:"collapse"}}>
                            <thead>
                              <tr style={{background:"#fafbfc"}}>
                                <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"left",borderBottom:"1px solid #f1f5f9"}}>Key</th>
                                <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"left",borderBottom:"1px solid #f1f5f9"}}>Label</th>
                                <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"left",borderBottom:"1px solid #f1f5f9"}}>DB Column</th>
                                <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"left",borderBottom:"1px solid #f1f5f9"}}>Group</th>
                                <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"center",borderBottom:"1px solid #f1f5f9",width:60}}>Sort</th>
                                <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"center",borderBottom:"1px solid #f1f5f9",width:90}}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {gFeats.sort(function(a,b){return a.sortOrder-b.sortOrder;}).map(function(feat){
                                var DEF_GRPS = ["Market Details","Therapeutic Class","Region / Governorate","Origin","Manufacturers Origin","ATC/Molecule"];
                                var inlineGrps = DEF_GRPS.slice();
                                allFeatures.forEach(function(ff){ if(ff.group && inlineGrps.indexOf(ff.group)<0) inlineGrps.push(ff.group); });
                                return (
                                  <tr key={feat.id} style={{borderBottom:"1px solid #f8fafc", background:(feat.enabled === false ? "#fff1f2" : undefined), opacity:(feat.enabled === false ? 0.75 : 1)}}>
                                    <td style={{padding:"7px 12px",fontSize:11,color:"#334155",fontFamily:"monospace",fontWeight:600}}>{feat.key}</td>
                                    <td style={{padding:"7px 12px",fontSize:11,color:"#0f172a"}}>
                                      {feat.label}
                                      {feat.enabled === false && <span style={{marginLeft:8,fontSize:8.5,fontWeight:800,background:"#fee2e2",color:"#b91c1c",borderRadius:8,padding:"1px 7px"}}>DISABLED FOR ALL USERS</span>}
                                    </td>
                                    <td style={{padding:"7px 12px",fontSize:10,color:"#64748b",fontFamily:"monospace"}}>{feat.dbColumn}</td>
                                    <td style={{padding:"5px 8px"}}>
                                      <select value={feat.group} onChange={function(e){
                                        api.updateStudyFeature(feat.id,{key:feat.key,label:feat.label,group:e.target.value,dbColumn:feat.dbColumn,sortOrder:feat.sortOrder})
                                          .then(reloadFeatures).catch(function(er){alert("Error: "+er.message);});
                                      }} style={{padding:"3px 6px",border:"1px solid #e2e8f0",borderRadius:4,fontSize:9,color:"#334155",background:"#fff",cursor:"pointer",width:"100%"}}>
                                        {inlineGrps.map(function(g){return <option key={g} value={g}>{g}</option>;})}
                                      </select>
                                    </td>
                                    <td style={{padding:"5px 8px",textAlign:"center"}}>
                                      <input type="number" min="0" value={feat.sortOrder} onChange={function(e){
                                        var newSort = Number(e.target.value);
                                        api.updateStudyFeature(feat.id,{key:feat.key,label:feat.label,group:feat.group,dbColumn:feat.dbColumn,sortOrder:newSort})
                                          .then(reloadFeatures).catch(function(er){alert("Error: "+er.message);});
                                      }} style={{width:44,padding:"3px 4px",border:"1px solid #e2e8f0",borderRadius:4,fontSize:10,textAlign:"center",color:"#334155"}} />
                                    </td>
                                    <td style={{padding:"7px 12px",textAlign:"center",whiteSpace:"nowrap"}}>
                                      <button onClick={function(){
                                        var msg = feat.enabled === false
                                          ? "Enable '" + feat.label + "' again for ALL users of this study?"
                                          : "Disable '" + feat.label + "' for ALL users of this study?\n\nIt disappears from the filter panel for every client and user with access to the study (admins included) until you enable it again.\nPer-client and per-user grants are kept, so re-enabling restores everything exactly.";
                                        if (!window.confirm(msg)) return;
                                        api.toggleStudyFeature(feat.id).then(reloadFeatures).catch(function(e){ alert("Error: " + e.message); });
                                      }} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,marginRight:4}}
                                        title={feat.enabled === false ? "Enable for all users of this study" : "Disable for all users of this study (dimension not ready)"}>
                                        {feat.enabled === false ? "🚫" : "🟢"}
                                      </button>
                                      <button onClick={function(){setFeatModal(feat);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,marginRight:4}} title="Edit">✏️</button>
                                      <button onClick={function(){
                                        if(!window.confirm("Delete feature '"+feat.label+"'?")) return;
                                        api.deleteStudyFeature(feat.id).then(reloadFeatures).catch(function(e){alert("Error: "+e.message);});
                                      }} style={{background:"none",border:"none",cursor:"pointer",fontSize:13}} title="Delete">🗑️</button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ═══ DASHBOARDS TAB ═══ */}
          {tab === "dashboards" && (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
                <select value={dashStudy||""} onChange={function(e){setDashStudy(e.target.value?Number(e.target.value):null);}}
                  style={{...adminInp, width:240, cursor:"pointer"}}>
                  <option value="">— Select Study Type —</option>
                  {studyTypes.filter(function(st){ return !_adminScope || _adminScope.indexOf(st.code) >= 0; }).map(function(st){ return <option key={st.id} value={st.id}>{st.category==="private"?"🔒":"🌐"} {st.name} ({st.code})</option>; })}
                </select>
                {dashStudy && <button onClick={function(){setDashModal("new");}} style={adminBtn("#2563eb","#fff","#2563eb")}>+ Add Dashboard</button>}
                <div style={{flex:1}} />
                {/* Refresh DashQ button — rebuilds the pre-aggregated summary tables that power dashboards.
                    Necessary after direct DB inserts that bypass the upload endpoint. */}
                <RefreshDashQButton />
              </div>
              {!dashStudy ? (
                <div style={{textAlign:"center",padding:"60px 20px",background:"#fff",borderRadius:14,border:"2px dashed #e2e8f0"}}>
                  <div style={{fontSize:40,marginBottom:10}}>📊</div>
                  <div style={{fontSize:14,fontWeight:700,color:"#64748b"}}>Select a study type to manage its dashboards</div>
                  <div style={{fontSize:11,color:"#94a3b8",marginTop:6}}>Dashboards define which analytics views are available for each study</div>
                </div>
              ) : (() => {
                var dashes = allDashboards.filter(function(d){return d.studyTypeId === dashStudy;}).sort(function(a,b){return a.sortOrder-b.sortOrder;});
                var stName = "";
                for(var si=0;si<studyTypes.length;si++){if(studyTypes[si].id===dashStudy){stName=studyTypes[si].name;break;}}
                var AVAIL_KEYS = ["market_overview","regional_leading","leading_manufacturers","manufacturer_overview","leading_products","product_overview","competitive_analysis","price_sku_analysis","new_launches","kimadia_leading_mfr","kimadia_product","kimadia_atc3","kimadia_foc","bi_designer"];
                return (
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                      <span style={{fontSize:16,fontWeight:800,color:"#0f172a",letterSpacing:"-.02em"}}>{stName}</span>
                      <span style={{fontSize:10,background:"#dbeafe",color:"#1d4ed8",borderRadius:10,padding:"2px 10px",fontWeight:700}}>{dashes.length} dashboards</span>
                    </div>
                    <div style={{background:"#fff",borderRadius:12,border:"1.5px solid #e2e8f0",overflow:"hidden"}}>
                      <table style={{width:"100%",borderCollapse:"collapse"}}>
                        <thead>
                          <tr style={{background:"#fafbfc"}}>
                            <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"center",width:40}}>Icon</th>
                            <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"left"}}>Label</th>
                            <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"left"}}>Key (Component)</th>
                            <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"left"}}>Description</th>
                            <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"center",width:60}}>Sort</th>
                            <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"center",width:60}}>Active</th>
                            <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#64748b",textAlign:"center",width:70}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashes.map(function(d){
                            var implemented = AVAIL_KEYS.indexOf(d.key) >= 0;
                            return (
                              <tr key={d.id} style={{borderBottom:"1px solid #f1f5f9",opacity:d.isActive?1:0.5}}>
                                <td style={{padding:"8px 12px",textAlign:"center",fontSize:18}}>{d.icon}</td>
                                <td style={{padding:"8px 12px",fontSize:12,fontWeight:700,color:"#0f172a"}}>{d.label}</td>
                                <td style={{padding:"8px 12px"}}>
                                  <span style={{fontSize:10,fontFamily:"monospace",color:implemented?"#15803d":"#dc2626",background:implemented?"#f0fdf4":"#fef2f2",padding:"2px 8px",borderRadius:4}}>{d.key}</span>
                                </td>
                                <td style={{padding:"8px 12px",fontSize:10,color:"#64748b",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.desc}</td>
                                <td style={{padding:"5px 8px",textAlign:"center"}}>
                                  <input type="number" min="0" value={d.sortOrder} onChange={function(e){
                                    api.updateStudyDashboard(d.id,{key:d.key,label:d.label,icon:d.icon,desc:d.desc,sortOrder:Number(e.target.value),isActive:d.isActive})
                                      .then(reloadDashboards).catch(function(er){window.alert("Error: "+er.message);});
                                  }} style={{width:44,padding:"3px 4px",border:"1px solid #e2e8f0",borderRadius:4,fontSize:10,textAlign:"center"}} />
                                </td>
                                <td style={{padding:"8px 12px",textAlign:"center"}}>
                                  <button onClick={function(){
                                    api.updateStudyDashboard(d.id,{key:d.key,label:d.label,icon:d.icon,desc:d.desc,sortOrder:d.sortOrder,isActive:!d.isActive})
                                      .then(reloadDashboards).catch(function(er){window.alert("Error: "+er.message);});
                                  }} style={{background:"none",border:"none",cursor:"pointer",fontSize:16}}>{d.isActive?"✅":"❌"}</button>
                                </td>
                                <td style={{padding:"8px 12px",textAlign:"center"}}>
                                  <button onClick={function(){setDashModal(d);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,marginRight:4}} title="Edit">✏️</button>
                                  <button onClick={function(){
                                    if(!window.confirm("Delete dashboard '"+d.label+"'?")) return;
                                    api.deleteStudyDashboard(d.id).then(reloadDashboards).catch(function(e){window.alert("Error: "+e.message);});
                                  }} style={{background:"none",border:"none",cursor:"pointer",fontSize:13}} title="Delete">🗑️</button>
                                </td>
                              </tr>
                            );
                          })}
                          {dashes.length === 0 && (
                            <tr><td colSpan={7} style={{padding:"30px",textAlign:"center",color:"#94a3b8",fontSize:12}}>No dashboards assigned. Click "+ Add Dashboard" to start.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div style={{marginTop:12,padding:"10px 14px",background:"#fffbeb",borderRadius:8,border:"1px solid #fde68a",fontSize:10,color:"#92400e"}}>
                      <strong>Available dashboard keys:</strong> {AVAIL_KEYS.join(", ")}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ═══ DEVICES TAB ═══ */}

          {/* ═══ BI DESIGNER TAB ═══ */}
          {tab === "bi_designer" && (
            <div style={{height:"calc(100vh - 170px)"}}>
              <BIDesignerAdmin />
            </div>
          )}

          {/* ═══ USER ANALYTICS TAB ═══ */}
          {tab === "analytics" && <UserAnalyticsPanel allowedTabs={_anTabs9} />}
          {tab === "contracts" && <ContractAnalysisPanel clients={clients} users={users} adminScope={_adminScope} />}

          {tab === "devices" && (() => {
            var pendingCount = devices.filter(function(d){return d.Status==="pending";}).length;
            var approvedCount = devices.filter(function(d){return d.Status==="approved";}).length;
            var revokedCount = devices.filter(function(d){return d.Status==="revoked";}).length;
            var filtered = devFilter === "all" ? devices : devices.filter(function(d){return d.Status===devFilter;});
            var searchLower = search.toLowerCase();
            if (search) {
              filtered = filtered.filter(function(d){
                return (d.UserName||"").toLowerCase().indexOf(searchLower)>=0 ||
                       (d.UserEmail||"").toLowerCase().indexOf(searchLower)>=0 ||
                       (d.DeviceName||"").toLowerCase().indexOf(searchLower)>=0 ||
                       (d.DeviceId||"").toLowerCase().indexOf(searchLower)>=0;
              });
            }
            var fmtDate = function(d){ if(!d) return "—"; var dt=new Date(d); return dt.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})+" "+dt.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}); };

            return (
              <div>
                {/* Pending notifications banner */}
                {deviceNotifs.length > 0 && (
                  <div style={{marginBottom:16,padding:"14px 18px",background:"linear-gradient(135deg,#fffbeb,#fef3c7)",borderRadius:12,border:"1.5px solid #fde68a",boxShadow:"0 2px 10px rgba(217,119,6,.1)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                      <span style={{fontSize:18}}>🔔</span>
                      <span style={{fontSize:13,fontWeight:800,color:"#92400e"}}>Pending Device Requests ({deviceNotifs.length})</span>
                    </div>
                    {deviceNotifs.map(function(n){
                      return (
                        <div key={n.NotificationId} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#fff",borderRadius:8,marginBottom:6,border:"1px solid #fde68a"}}>
                          <div style={{width:36,height:36,borderRadius:"50%",background:"#fef3c7",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>💻</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:700,color:"#0f172a"}}>{n.UserName || n.UserEmail}</div>
                            <div style={{fontSize:10,color:"#64748b"}}>{n.DeviceName} — {fmtDate(n.CreatedAt)}</div>
                          </div>
                          <button onClick={function(){api.approveDevice(n.DeviceRecordId,"admin").then(reloadDevices).catch(function(e){alert(e.message);});}}
                            style={{padding:"5px 14px",background:"#059669",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>Approve</button>
                          <button onClick={function(){api.revokeDevice(n.DeviceRecordId).then(reloadDevices).catch(function(e){alert(e.message);});}}
                            style={{padding:"5px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>Deny</button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Stats bar */}
                <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
                  {[{label:"Total",count:devices.length,bg:"#f0f4f8",color:"#334155",fk:"all"},
                    {label:"Pending",count:pendingCount,bg:"#fffbeb",color:"#d97706",fk:"pending"},
                    {label:"Approved",count:approvedCount,bg:"#ecfdf5",color:"#059669",fk:"approved"},
                    {label:"Revoked",count:revokedCount,bg:"#fef2f2",color:"#dc2626",fk:"revoked"}
                  ].map(function(s){
                    var active = devFilter === s.fk;
                    return (
                      <button key={s.fk} onClick={function(){setDevFilter(s.fk);}}
                        style={{padding:"10px 18px",borderRadius:10,border:active?"2px solid "+s.color:"2px solid transparent",background:s.bg,cursor:"pointer",textAlign:"center",minWidth:90,fontFamily:"inherit",transition:"all .15s"}}>
                        <div style={{fontSize:20,fontWeight:800,color:s.color}}>{s.count}</div>
                        <div style={{fontSize:10,fontWeight:600,color:s.color,opacity:.8}}>{s.label}</div>
                      </button>
                    );
                  })}
                  {/* Live presence cards — not filters; tooltip lists the users */}
                  <div title={devLive && devLive.activeUsers && devLive.activeUsers.length ? devLive.activeUsers.map(function(u2){ return u2.name + (u2.client ? " (" + u2.client + ")" : ""); }).join("\n") : "No users on the portal right now"}
                    style={{padding:"10px 18px",borderRadius:10,border:"2px solid #10b981",background:"#ecfdf5",textAlign:"center",minWidth:90,cursor:"default"}}>
                    <div style={{fontSize:20,fontWeight:800,color:"#059669",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      <span style={{width:8,height:8,borderRadius:"50%",background:"#10b981",display:"inline-block",boxShadow:"0 0 0 3px rgba(16,185,129,.25)"}} />
                      {devLive ? devLive.activeNow : "…"}
                    </div>
                    <div style={{fontSize:10,fontWeight:600,color:"#059669",opacity:.8}}>Active Now</div>
                  </div>
                  <div title={devLive && devLive.todayUsers && devLive.todayUsers.length ? devLive.todayUsers.map(function(u3){ return u3.name + (u3.client ? " (" + u3.client + ")" : ""); }).join("\n") : "No logins yet today"}
                    style={{padding:"10px 18px",borderRadius:10,border:"2px solid #93c5fd",background:"#eff6ff",textAlign:"center",minWidth:90,cursor:"default"}}>
                    <div style={{fontSize:20,fontWeight:800,color:"#2563eb"}}>{devLive ? devLive.today : "…"}</div>
                    <div style={{fontSize:10,fontWeight:600,color:"#2563eb",opacity:.8}}>Today</div>
                  </div>
                  <div style={{flex:1}} />
                  <input style={{...adminInp, width:220, alignSelf:"center"}} placeholder="Search devices…" value={search}
                    onChange={function(e){setSearch(e.target.value);}} />
                  <button onClick={reloadDevices} style={{...adminBtn("#f0f4f8","#334155","#e2e8f0"), alignSelf:"center"}}>⟳ Refresh</button>
                </div>

                {/* Devices table */}
                <div style={{background:"#fff",borderRadius:12,border:"1.5px solid #e2e8f0",overflow:"hidden",boxShadow:"0 1px 6px rgba(0,0,0,.04)"}}>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead>
                        <tr style={{background:"#fafbfc"}}>
                          <th style={thS}>User</th>
                          <th style={thS}>Device</th>
                          <th style={thS}>Device ID</th>
                          <th style={thS}>Status</th>
                          <th style={thS}>Auth Method</th>
                          <th style={thS}>Registered</th>
                          <th style={thS}>Last Used</th>
                          <th style={thS}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 && (
                          <tr><td colSpan={8} style={{padding:40,textAlign:"center",color:"#94a3b8",fontSize:12}}>
                            {devices.length === 0 ? "No devices registered yet. Devices appear here when users log in." : "No devices match the current filter."}
                          </td></tr>
                        )}
                        {filtered.map(function(d){
                          var stColor = d.Status==="approved"?"#059669":d.Status==="pending"?"#d97706":"#dc2626";
                          var stBg = d.Status==="approved"?"#ecfdf5":d.Status==="pending"?"#fffbeb":"#fef2f2";
                          return (
                            <tr key={d.DeviceRecordId} style={{borderTop:"1px solid #f1f5f9"}}>
                              <td style={{padding:"10px 12px"}}>
                                <div style={{fontSize:12,fontWeight:700,color:"#0f172a"}}>{d.UserName || "—"}</div>
                                <div style={{fontSize:10,color:"#64748b"}}>{d.UserEmail || ""}</div>
                                {d.ClientName && <div style={{fontSize:9,color:"#94a3b8"}}>{d.ClientName}</div>}
                              </td>
                              <td style={{padding:"10px 12px"}}>
                                <div style={{fontSize:11,color:"#334155"}}>{d.DeviceName || "Unknown"}</div>
                                {d.IsFirstDevice ? <span style={{fontSize:9,background:"#dbeafe",color:"#1d4ed8",borderRadius:4,padding:"1px 6px",fontWeight:600}}>First Device</span> : null}
                              </td>
                              <td style={{padding:"10px 12px"}}>
                                <span style={{fontFamily:"monospace",fontSize:10,color:"#64748b",background:"#f8fafc",padding:"2px 6px",borderRadius:4}}>{(d.DeviceId||"").substring(0,14)}…</span>
                              </td>
                              <td style={{padding:"10px 12px"}}>
                                <span style={{fontSize:10,fontWeight:700,color:stColor,background:stBg,padding:"3px 10px",borderRadius:10,textTransform:"capitalize"}}>{d.Status}</span>
                              </td>
                              <td style={{padding:"10px 12px"}}>
                                {(function(){
                                  /* Auth method badge — shows how the user logged in most recently:
                                     - token       : HIGH confidence, browser presented a valid token
                                     - fingerprint : MEDIUM confidence, fell back to hardware match
                                     - new         : Device was just registered, no successful auth yet
                                     - none        : Pre-token-system device with no recorded method (legacy) */
                                  var m = (d.LastAuthMethod || "").toLowerCase();
                                  var lbl, bg, fg, icon, tip;
                                  if (m === "token") {
                                    lbl = "Token"; bg = "#ecfdf5"; fg = "#047857"; icon = "🔐";
                                    tip = "HIGH confidence — browser presented a valid server-issued token";
                                  } else if (m === "fingerprint") {
                                    lbl = "Fingerprint"; bg = "#fffbeb"; fg = "#b45309"; icon = "🔍";
                                    tip = "MEDIUM confidence — token was missing/cleared, hardware fingerprint matched";
                                  } else if (m === "new") {
                                    lbl = "New"; bg = "#eff6ff"; fg = "#1d4ed8"; icon = "✨";
                                    tip = "Device just registered — no successful auth yet";
                                  } else {
                                    lbl = "Legacy"; bg = "#f1f5f9"; fg = "#64748b"; icon = "•";
                                    tip = "Pre-token device, no auth method recorded";
                                  }
                                  return (
                                    <span title={tip} style={{
                                      display:"inline-flex",alignItems:"center",gap:4,
                                      fontSize:10,fontWeight:700,color:fg,background:bg,
                                      padding:"3px 8px",borderRadius:10,
                                      border:"1px solid "+fg+"22"
                                    }}>
                                      <span style={{fontSize:9}}>{icon}</span>{lbl}
                                    </span>
                                  );
                                })()}
                                {d.HasToken === 1 && d.TokenLastUsedAt
                                  ? <div title={"Token last used: " + (d.TokenLastUsedAt || "")} style={{fontSize:8,color:"#64748b",marginTop:3}}>🔑 has token</div>
                                  : null}
                              </td>
                              <td style={{padding:"10px 12px",fontSize:10,color:"#64748b"}}>{fmtDate(d.CreatedAt)}</td>
                              <td style={{padding:"10px 12px",fontSize:10,color:"#64748b"}}>{fmtDate(d.LastUsedAt)}</td>
                              <td style={{padding:"10px 12px"}}>
                                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                                  {d.Status === "pending" && (
                                    <button onClick={function(){api.approveDevice(d.DeviceRecordId,"admin").then(reloadDevices).catch(function(e){alert(e.message);});}}
                                      style={{padding:"4px 10px",background:"#059669",color:"#fff",border:"none",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer"}}>Approve</button>
                                  )}
                                  {d.Status === "approved" && (
                                    <button onClick={function(){if(window.confirm("Revoke this device? The user won't be able to log in from it.")){api.revokeDevice(d.DeviceRecordId).then(reloadDevices).catch(function(e){alert(e.message);});} }}
                                      style={{padding:"4px 10px",background:"#dc2626",color:"#fff",border:"none",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer"}}>Revoke</button>
                                  )}
                                  {d.Status === "revoked" && (
                                    <button onClick={function(){api.approveDevice(d.DeviceRecordId,"admin").then(reloadDevices).catch(function(e){alert(e.message);});}}
                                      style={{padding:"4px 10px",background:"#2563eb",color:"#fff",border:"none",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer"}}>Re-approve</button>
                                  )}
                                  <button onClick={function(){if(window.confirm("Permanently delete this device record?")){api.deleteDevice(d.DeviceRecordId).then(reloadDevices).catch(function(e){alert(e.message);});} }}
                                    style={{padding:"4px 10px",background:"#fff",color:"#dc2626",border:"1px solid #fecaca",borderRadius:5,fontSize:10,fontWeight:600,cursor:"pointer"}}>Delete</button>
                                  <button onClick={function(){
                                    if (devAn && devAn.id === d.DeviceRecordId) { setDevAn(null); return; }
                                    setDevAn({id: d.DeviceRecordId, loading: true});
                                    api.getDeviceAnalysis(d.DeviceRecordId)
                                      .then(function(a){ setDevAn({id: d.DeviceRecordId, data: a}); })
                                      .catch(function(e){ setDevAn({id: d.DeviceRecordId, error: e.message}); });
                                  }} title="Login analysis — which logins came from this device"
                                    style={{padding:"4px 10px",background:(devAn&&devAn.id===d.DeviceRecordId)?"#eff6ff":"#fff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer"}}>
                                    {(devAn&&devAn.id===d.DeviceRecordId)?"▲ Analysis":"📊 Analysis"}</button>
                                </div>
                              </td>
                            </tr>
                          );
                        }).map(function(rowEl, ri){
                          /* Interleave the analysis panel row right under its device row */
                          var d = filtered[ri];
                          if (!devAn || devAn.id !== d.DeviceRecordId) return rowEl;
                          var _uaShort = function(ua){
                            ua = String(ua||"");
                            var b = ua.indexOf("Edg")>=0?"Edge":ua.indexOf("OPR")>=0?"Opera":ua.indexOf("Chrome")>=0?"Chrome":ua.indexOf("Firefox")>=0?"Firefox":ua.indexOf("Safari")>=0?"Safari":"Browser";
                            var o = ua.indexOf("Windows")>=0?"Windows":ua.indexOf("Mac")>=0?"macOS":ua.indexOf("Android")>=0?"Android":(ua.indexOf("iPhone")>=0||ua.indexOf("iPad")>=0)?"iOS":ua.indexOf("Linux")>=0?"Linux":"";
                            return b + (o?" on "+o:"");
                          };
                          var _chip = function(lbl, val){ return (
                            <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,padding:"8px 14px",minWidth:110}}>
                              <div style={{fontSize:9,color:"#94a3b8",fontWeight:700,textTransform:"uppercase"}}>{lbl}</div>
                              <div style={{fontSize:13,fontWeight:800,color:"#0f172a",marginTop:2}}>{val}</div>
                            </div>
                          ); };
                          var a = devAn, dt = a.data;
                          var panel;
                          if (a.loading) panel = <div style={{padding:18,fontSize:11,color:"#64748b"}}>Loading login analysis…</div>;
                          else if (a.error) panel = <div style={{padding:18,fontSize:11,color:"#dc2626"}}>{"Error: "+a.error}</div>;
                          else if (dt) {
                            var st9 = dt.stats||{};
                            var _devName9 = {}; (dt.userDevices||[]).forEach(function(u9){ _devName9[u9.DeviceId] = (u9.DeviceName||"Unknown") + (u9.IsFirstDevice?" · first device":""); });
                            panel = (
                              <div style={{padding:"14px 18px"}}>
                                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:10}}>
                                  <span style={{fontSize:12,fontWeight:800,color:"#0f172a"}}>{"🔎 Login analysis — " + (d.DeviceName||"Unknown device")}</span>
                                  <span style={{fontFamily:"monospace",fontSize:10,color:"#1d4ed8",background:"#eff6ff",border:"1px solid #bfdbfe",padding:"2px 8px",borderRadius:5}} title="Full device reference">{d.DeviceId}</span>
                                  {!dt.tracking && <span style={{fontSize:10,fontWeight:700,color:"#b45309",background:"#fffbeb",border:"1px solid #fde68a",padding:"2px 8px",borderRadius:5}}>login–device tracking activates after this server update — new logins will be attributed</span>}
                                </div>
                                <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:12}}>
                                  {_chip(dt.tracking?"Logins from this device":"User logins (all devices)", st9.total)}
                                  {_chip("First login", st9.first?fmtDate(st9.first):"—")}
                                  {_chip("Last login", st9.last?fmtDate(st9.last):"—")}
                                  {dt.tracking?_chip("Distinct IPs", st9.ips):null}
                                </div>
                                {(dt.logins||[]).length > 0 && (
                                  <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,overflow:"hidden",marginBottom:12}}>
                                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:10.5}}>
                                      <thead><tr style={{background:"#fafbfc"}}>
                                        <th style={{...thS,fontSize:9}}>Login time</th><th style={{...thS,fontSize:9}}>IP address</th><th style={{...thS,fontSize:9}}>Browser</th><th style={{...thS,fontSize:9}}>Device ref</th>
                                      </tr></thead>
                                      <tbody>
                                        {dt.logins.slice(0,15).map(function(lg,li){ return (
                                          <tr key={"lg"+li} style={{borderTop:"1px solid #f1f5f9"}}>
                                            <td style={{padding:"6px 12px",color:"#334155"}}>{fmtDate(lg.Timestamp)}</td>
                                            <td style={{padding:"6px 12px",fontFamily:"monospace",color:"#64748b"}}>{lg.IpAddress||"—"}</td>
                                            <td style={{padding:"6px 12px",color:"#64748b"}}>{_uaShort(lg.UserAgent)}</td>
                                            <td style={{padding:"6px 12px"}}><span style={{fontFamily:"monospace",fontSize:9,color:"#1d4ed8"}}>{(d.DeviceId||"").substring(0,18)+"…"}</span></td>
                                          </tr>
                                        );})}
                                      </tbody>
                                    </table>
                                    {dt.logins.length > 15 && <div style={{padding:"5px 12px",fontSize:9,color:"#94a3b8"}}>{"showing latest 15 of " + dt.logins.length}</div>}
                                  </div>
                                )}
                                {dt.tracking && (dt.logins||[]).length === 0 && (
                                  <div style={{padding:"8px 12px",background:"#f8fafc",border:"1px dashed #e2e8f0",borderRadius:8,fontSize:10.5,color:"#64748b",marginBottom:12}}>
                                    {"No logins recorded from this device yet" + (d.LastUsedAt ? "" : " — it was registered but never used to sign in") + "."}
                                  </div>
                                )}
                                {(dt.breakdown||[]).length > 0 && (
                                  <div>
                                    <div style={{fontSize:10,fontWeight:800,color:"#475569",marginBottom:5}}>{"This user's logins by device"}</div>
                                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                                      {dt.breakdown.map(function(b9,bi){
                                        var isThis = b9.DeviceId === d.DeviceId;
                                        var nm = b9.DeviceId === "" ? "earlier logins (before device tracking)" : (_devName9[b9.DeviceId] || ("unregistered " + b9.DeviceId.substring(0,12) + "…"));
                                        return (
                                          <div key={"bd"+bi} style={{background:isThis?"#eff6ff":"#fff",border:"1.5px solid "+(isThis?"#93c5fd":"#e2e8f0"),borderRadius:8,padding:"7px 12px"}}>
                                            <div style={{fontSize:10,fontWeight:isThis?800:600,color:isThis?"#1d4ed8":"#334155"}}>{nm + (isThis?" ← this device":"")}</div>
                                            {b9.DeviceId !== "" && <div style={{fontFamily:"monospace",fontSize:8.5,color:"#94a3b8"}}>{b9.DeviceId.substring(0,22)+"…"}</div>}
                                            <div style={{fontSize:9.5,color:"#64748b",marginTop:2}}>{b9.Logins + " login" + (b9.Logins===1?"":"s") + " · last " + fmtDate(b9.LastLogin)}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return [rowEl,
                            <tr key={"an"+d.DeviceRecordId}><td colSpan={8} style={{background:"#f8fafc",borderTop:"1px solid #e2e8f0",padding:0}}>{panel}</td></tr>
                          ];
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Info box */}
                <div style={{marginTop:14,padding:"12px 16px",background:"#f0f4f8",borderRadius:8,fontSize:10,color:"#64748b",lineHeight:1.7}}>
                  <strong>How device authorization works:</strong> Each user's first device is auto-approved. Additional devices require admin approval.
                  Users on unapproved devices see a waiting screen that auto-refreshes when approved. Revoking a device immediately blocks login from that browser.
                </div>
              </div>
            );
          })()}

        </div>
      </div>
      {dashModal && (() => {
        var isNew = dashModal === "new";
        var initD = isNew ? {key:"",label:"",icon:"📊",desc:"",sortOrder:99} : {key:dashModal.key,label:dashModal.label,icon:dashModal.icon,desc:dashModal.desc,sortOrder:dashModal.sortOrder};
        var AVAIL_KEYS2 = ["market_overview","regional_leading","leading_manufacturers","manufacturer_overview","leading_products","product_overview","competitive_analysis","price_sku_analysis","new_launches","kimadia_leading_mfr","kimadia_product","kimadia_atc3","kimadia_foc"];
        return <DashboardModal dashboard={initD} isNew={isNew} availableKeys={AVAIL_KEYS2}
          onSave={function(d){
            var p = isNew
              ? api.addStudyDashboard({studyTypeId:dashStudy,key:d.key,label:d.label,icon:d.icon,desc:d.desc,sortOrder:d.sortOrder})
              : api.updateStudyDashboard(dashModal.id,d);
            p.then(function(){reloadDashboards();setDashModal(null);}).catch(function(e){window.alert("Error: "+e.message);});
          }}
          onClose={function(){setDashModal(null);}} />;
      })()}

      {/* ── Feature Modal ── */}
      {featModal && (() => {
        var isNew = featModal === "new";
        var initF = isNew ? {key:"",label:"",group:"Market Details",dbColumn:"",sortOrder:99} : {key:featModal.key,label:featModal.label,group:featModal.group,dbColumn:featModal.dbColumn,sortOrder:featModal.sortOrder};
        var existGrps = []; var _gs = {}; allFeatures.forEach(function(ff){ if(ff.group && !_gs[ff.group]){_gs[ff.group]=1;existGrps.push(ff.group);} });
        return <FeatureModal feature={initF} isNew={isNew} featId={isNew?null:featModal.id} studyTypeId={featStudy}
          existingGroups={existGrps}
          onSave={function(f){
            var p = isNew
              ? api.addStudyFeature({studyTypeId:featStudy, key:f.key, label:f.label, group:f.group, dbColumn:f.dbColumn, sortOrder:f.sortOrder})
              : api.updateStudyFeature(featModal.id, f);
            p.then(function(){reloadFeatures();setFeatModal(null);}).catch(function(e){alert("Error: "+e.message);});
          }}
          onClose={function(){setFeatModal(null);}} />;
      })()}

      {/* ── Modals ── */}
      {clientModal && (
        <ClientModal partners={partners} client={clientModal === "new" ? null : clientModal} onSave={saveClient} onClose={function(){setClientModal(null);}} adminScope={_adminScope} canReports={_canRep} canOwner={_isFullAdmin} />
      )}
      {userModal && (
        <UserModal usr={userModal === "new" ? null : userModal} clients={clients} onSave={saveUser} onClose={function(){setUserModal(null);}} adminScope={_adminScope} canReports={_canRep} />
      )}
      {confirmDel && (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center"}}
          onClick={function(){setConfirmDel(null);}}>
          <div style={{background:"#fff", borderRadius:14, padding:"28px 30px", width:380, boxShadow:"0 20px 60px rgba(0,0,0,.15)"}}
            onClick={function(e){e.stopPropagation();}}>
            <div style={{fontSize:28, textAlign:"center", marginBottom:10}}>⚠️</div>
            <div style={{fontSize:14, fontWeight:800, color:"#0f172a", textAlign:"center", marginBottom:6}}>Confirm Delete</div>
            <div style={{fontSize:12, color:"#64748b", textAlign:"center", marginBottom:20}}>
              Are you sure you want to delete <strong>{confirmDel.name}</strong>?
              {confirmDel.type === "client" && <span><br/>All users under this client will be paused.</span>}
            </div>
            <div style={{display:"flex", justifyContent:"center", gap:10}}>
              <button onClick={function(){setConfirmDel(null);}} style={adminBtn("#fff","#64748b","#e2e8f0")}>Cancel</button>
              <button onClick={function(){ confirmDel.type==="client" ? deleteClient(confirmDel.id) : deleteUser(confirmDel.id); }}
                style={adminBtn("#dc2626","#fff","#dc2626")}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Password Reset Modal ── */}
      {pwResetId && (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center"}}
          onClick={function(){setPwResetId(null);}}>
          <div style={{background:"#fff", borderRadius:14, padding:"28px 30px", width:400, boxShadow:"0 20px 60px rgba(0,0,0,.15)"}}
            onClick={function(e){e.stopPropagation();}}>
            <div style={{fontSize:28, textAlign:"center", marginBottom:10}}>🔒</div>
            <div style={{fontSize:14, fontWeight:800, color:"#0f172a", textAlign:"center", marginBottom:6}}>Reset Password</div>
            <div style={{fontSize:12, color:"#64748b", textAlign:"center", marginBottom:14}}>
              Set new password for <strong>{(function(){ for(var i=0;i<users.length;i++){if(users[i].id===pwResetId)return users[i].name;} return "user"; })()}</strong>
            </div>
            <div style={{marginBottom:12}}>
              <input style={{...adminInp, textAlign:"center", fontSize:14, letterSpacing:1}} type="text" value={pwResetVal}
                onChange={function(e){setPwResetVal(e.target.value); setPwResetMsg("");}}
                placeholder="New password" />
            </div>
            {pwResetMsg && <div style={{fontSize:11, textAlign:"center", marginBottom:10, color:pwResetMsg.indexOf("success")>=0?"#15803d":"#dc2626", fontWeight:700}}>{pwResetMsg}</div>}
            <div style={{display:"flex", justifyContent:"center", gap:10}}>
              <button onClick={function(){setPwResetId(null);}} style={adminBtn("#fff","#64748b","#e2e8f0")}>Cancel</button>
              <button onClick={function(){resetPassword(pwResetId);}} style={adminBtn("#2563eb","#fff","#2563eb")}>Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   APP ROOT
════════════════════════════════════════════════════════════ */
/* ═══════════ PASSWORD CHANGE MODAL ═══════════
   Two modes:
   - "first"     → shown automatically after first login (MustChangePassword=1).
                   MANDATORY: no skip — the server blocks all data APIs until changed.
   - "voluntary" → opened from the 🔑 header button anytime. No keep button.
   On successful change the server revokes the user's OTHER sessions; this one
   stays alive so the user continues working uninterrupted. Admins never see
   this modal (their passwords are managed in the admin panel). */
/* ══ Custom Region Split builder — the user's own governorate→region grouping.
   Saved per user per study; when enabled, ALL regional analysis in this account
   (pivot, dashboards, drill-downs, rankings) is grouped by these regions
   server-side. Governorates left unassigned appear as the reserved 'Other'. ══ */
function RegionBuilderModal({ studyType, governorates, onClose, onSaved }) {
  var [enabled, setEnabled] = useState(false);
  var [groups, setGroups] = useState([]); /* [{name, govs:[]}] */
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);
  var [err, setErr] = useState("");
  useEffect(function() {
    var alive = true;
    api.getUserRegionMap(studyType).then(function(m) {
      if (!alive) return;
      setEnabled(!!(m && m.enabled));
      var g = [];
      if (m && m.regions) Object.keys(m.regions).forEach(function(n){ g.push({ name: n, govs: (m.regions[n] || []).slice() }); });
      setGroups(g.length ? g : [{ name: "", govs: [] }]);
      setLoading(false);
    }).catch(function(){ if (alive) { setGroups([{ name: "", govs: [] }]); setLoading(false); } });
    return function(){ alive = false; };
  }, [studyType]);
  var assigned = {};
  groups.forEach(function(g, gi){ g.govs.forEach(function(v){ if (assigned[v] == null) assigned[v] = gi; }); });
  var unassigned = governorates.filter(function(v){ return assigned[v] == null; });
  var toggleGov = function(gi, gov) {
    var owner = assigned[gov];
    if (owner != null && owner !== gi) return; /* owned by another region */
    setGroups(function(prev){
      var next = prev.map(function(g){ return { name: g.name, govs: g.govs.slice() }; });
      var idx = next[gi].govs.indexOf(gov);
      if (idx >= 0) next[gi].govs.splice(idx, 1); else next[gi].govs.push(gov);
      return next;
    });
  };
  var setName = function(gi, v) { setGroups(function(prev){ var next = prev.map(function(g){ return { name: g.name, govs: g.govs.slice() }; }); next[gi].name = v; return next; }); };
  var removeGroup = function(gi) { setGroups(function(prev){ return prev.filter(function(_, i){ return i !== gi; }); }); };
  var addGroup = function() { setGroups(function(prev){ return prev.concat([{ name: "", govs: [] }]); }); };
  var save = function() {
    setErr("");
    var out = {}; var names = {};
    for (var i = 0; i < groups.length; i++) {
      var nm = String(groups[i].name || "").trim();
      if (!nm && groups[i].govs.length === 0) continue; /* empty editor row */
      if (!nm) { setErr("Every region needs a name."); return; }
      if (nm.toLowerCase() === "other") { setErr("'Other' is reserved for unassigned governorates."); return; }
      if (names[nm.toLowerCase()]) { setErr("Region names must be unique: " + nm); return; }
      names[nm.toLowerCase()] = 1;
      if (!groups[i].govs.length) { setErr("Region '" + nm + "' has no governorates."); return; }
      out[nm] = groups[i].govs;
    }
    if (enabled && !Object.keys(out).length) { setErr("Define at least one region before enabling."); return; }
    setSaving(true);
    api.saveUserRegionMap(studyType, enabled, out).then(function() {
      /* NO page reload — sessions are memory-only, so a reload would log the
         user out. The parent refreshes all region-dependent state in place. */
      alert("Custom region split saved." + (enabled ? " All regional analysis in your account now uses your regions." : " (Currently disabled — standard regions apply.)"));
      if (onSaved) onSaved({ enabled: enabled, regions: out });
    }).catch(function(e){ setSaving(false); setErr(e.message || "Save failed"); });
  };
  return (
    <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.45)", zIndex:4000, display:"flex", alignItems:"center", justifyContent:"center", padding:20}} onClick={onClose}>
      <div style={{background:"#fff", borderRadius:14, width:"min(760px, 96vw)", maxHeight:"88vh", display:"flex", flexDirection:"column", boxShadow:"0 20px 60px rgba(0,0,0,.25)"}} onClick={function(e){ e.stopPropagation(); }}>
        <div style={{padding:"14px 18px", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:10}}>
          <span style={{fontSize:18}}>🗺️</span>
          <div style={{flex:1}}>
            <div style={{fontSize:14, fontWeight:800, color:"#0f172a"}}>Custom Region Split</div>
            <div style={{fontSize:10, color:"#64748b"}}>Group governorates into your own regions — when enabled, every regional chart, filter and ranking in your account uses them. Unassigned governorates appear as "Other".</div>
          </div>
          <button onClick={onClose} style={{border:"none", background:"transparent", fontSize:16, cursor:"pointer", color:"#94a3b8"}}>✕</button>
        </div>
        <div style={{padding:"14px 18px", overflowY:"auto", flex:1}}>
          {loading ? <div style={{textAlign:"center", padding:"30px 0", color:"#94a3b8", fontSize:12}}>Loading…</div> : (
            <div>
              <label style={{display:"flex", alignItems:"center", gap:8, marginBottom:12, cursor:"pointer", userSelect:"none", padding:"8px 12px", background:enabled ? "#eff6ff" : "#f8fafc", borderRadius:8, border:"1px solid " + (enabled ? "#bfdbfe" : "#e2e8f0")}}>
                <input type="checkbox" checked={enabled} onChange={function(e){ setEnabled(e.target.checked); }} />
                <span style={{fontSize:11, fontWeight:700, color:enabled ? "#1d4ed8" : "#334155"}}>{enabled ? "Enabled — my regions replace the standard split" : "Disabled — standard regions apply"}</span>
              </label>
              {groups.map(function(g, gi){
                return (
                  <div key={gi} style={{border:"1px solid #e2e8f0", borderRadius:10, padding:"10px 12px", marginBottom:10, background:"#fcfdfe"}}>
                    <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:8}}>
                      <input value={g.name} onChange={function(e){ setName(gi, e.target.value); }} placeholder={"Region name (e.g. My North)"}
                        style={{flex:1, padding:"5px 9px", border:"1.5px solid #cbd5e1", borderRadius:7, fontSize:11, fontWeight:700, fontFamily:"inherit", outline:"none"}} />
                      <span style={{fontSize:9, color:"#64748b", flexShrink:0}}>{g.govs.length} selected</span>
                      <button onClick={function(){ removeGroup(gi); }} title="Remove this region"
                        style={{border:"1px solid #fecaca", background:"#fef2f2", color:"#dc2626", borderRadius:6, fontSize:9, fontWeight:700, padding:"3px 8px", cursor:"pointer", fontFamily:"inherit", flexShrink:0}}>✕ Remove</button>
                    </div>
                    <div style={{display:"flex", flexWrap:"wrap", gap:5}}>
                      {governorates.map(function(gov){
                        var owner = assigned[gov];
                        var inThis = g.govs.indexOf(gov) >= 0;
                        var lockedElsewhere = owner != null && owner !== gi;
                        return (
                          <span key={gov} onClick={function(){ if (!lockedElsewhere) toggleGov(gi, gov); }}
                            title={lockedElsewhere ? ("Already in \"" + (groups[owner] && groups[owner].name ? groups[owner].name : "another region") + "\"") : ""}
                            style={{fontSize:9.5, fontWeight:600, padding:"3px 9px", borderRadius:14, cursor:lockedElsewhere ? "not-allowed" : "pointer", userSelect:"none",
                                    border:"1.5px solid " + (inThis ? "#2563eb" : lockedElsewhere ? "#f1f5f9" : "#e2e8f0"),
                                    background:inThis ? "#eff6ff" : lockedElsewhere ? "#f8fafc" : "#fff",
                                    color:inThis ? "#1d4ed8" : lockedElsewhere ? "#cbd5e1" : "#475569"}}>
                            {inThis ? "✓ " : ""}{gov}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <button onClick={addGroup} style={{border:"1.5px dashed #93c5fd", background:"#f8fbff", color:"#1d4ed8", borderRadius:8, fontSize:10.5, fontWeight:700, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit", width:"100%"}}>+ Add region</button>
              <div style={{marginTop:10, fontSize:9.5, color:"#64748b", padding:"7px 10px", background:"#f8fafc", borderRadius:7, border:"1px solid #eef2f6"}}>
                {unassigned.length > 0
                  ? (unassigned.length + " unassigned " + (unassigned.length === 1 ? "governorate" : "governorates") + " will appear as \"Other\": " + unassigned.slice(0, 8).join(", ") + (unassigned.length > 8 ? " +" + (unassigned.length - 8) + " more" : ""))
                  : "All governorates are assigned — no \"Other\" bucket will appear."}
              </div>
            </div>
          )}
        </div>
        <div style={{padding:"12px 18px", borderTop:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:10}}>
          {err && <span style={{fontSize:10, color:"#dc2626", fontWeight:700, flex:1}}>⚠ {err}</span>}
          {!err && <span style={{flex:1}} />}
          <button onClick={onClose} disabled={saving} style={{padding:"7px 16px", border:"1.5px solid #e2e8f0", borderRadius:8, background:"#fff", color:"#334155", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>Cancel</button>
          <button onClick={save} disabled={saving || loading} style={{padding:"7px 18px", border:"none", borderRadius:8, background:"#2563eb", color:"#fff", fontSize:11, fontWeight:700, cursor:saving ? "wait" : "pointer", fontFamily:"inherit", opacity:saving ? .7 : 1}}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function PasswordChangeModal({ mode, onClose }) {
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [cnfPw, setCnfPw] = useState("");
  const [err,   setErr]   = useState("");
  const [busy,  setBusy]  = useState(false);
  const [done,  setDone]  = useState(false);
  var isFirst = mode === "first";

  var submit = function() {
    setErr("");
    if (newPw.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (newPw !== cnfPw)  { setErr("New password and confirmation do not match."); return; }
    if (newPw === curPw)  { setErr("New password must be different from the current one."); return; }
    setBusy(true);
    api.changePassword(curPw, newPw)
      .then(function(){ setBusy(false); setDone(true); setTimeout(function(){ onClose(true); }, 1500); })
      .catch(function(e){ setBusy(false); setErr((e && e.message) || "Password change failed — please try again."); });
  };
  var onKey = function(ev) { if (ev.key === "Enter" && !busy && !done) submit(); };

  var inpStyle = {width:"100%", boxSizing:"border-box", padding:"8px 10px", fontSize:12, fontFamily:"inherit",
                  border:"1.5px solid #e2e8f0", borderRadius:7, outline:"none", marginBottom:10};
  var lblStyle = {fontSize:10.5, fontWeight:700, color:"#475569", marginBottom:4, display:"block"};

  return (
    <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.45)", zIndex:9000,
                 display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <div style={{width:380, maxWidth:"92vw", background:"#fff", borderRadius:14, border:"1.5px solid #e2e8f0",
                   boxShadow:"0 12px 48px rgba(0,0,0,.18)", padding:"26px 28px"}}>
        {done ? (
          <div style={{textAlign:"center", padding:"18px 0"}}>
            <div style={{fontSize:34, marginBottom:10}}>✅</div>
            <div style={{fontSize:14, fontWeight:800, color:"#0f172a", marginBottom:6}}>Password updated</div>
            <div style={{fontSize:11, color:"#64748b", lineHeight:1.5}}>
              Your other signed-in devices were signed out for security. This session stays active.
            </div>
          </div>
        ) : (
          <div>
            <div style={{fontSize:15, fontWeight:800, color:"#0f172a", marginBottom:4}}>
              {isFirst ? "Set a new password" : "Change password"}
            </div>
            <div style={{fontSize:11, color:"#64748b", lineHeight:1.55, marginBottom:16}}>
              {isFirst
                ? "This is your first sign-in. For security you must replace the password you were given with one only you know before continuing. This is required for every new account."
                : "Enter your current password, then choose a new one (minimum 8 characters)."}
            </div>
            <label style={lblStyle}>Current password</label>
            <input type="password" value={curPw} autoComplete="current-password"
              onChange={function(ev){ setCurPw(ev.target.value); }} onKeyDown={onKey} style={inpStyle} />
            <label style={lblStyle}>New password (min. 8 characters)</label>
            <input type="password" value={newPw} autoComplete="new-password"
              onChange={function(ev){ setNewPw(ev.target.value); }} onKeyDown={onKey} style={inpStyle} />
            <label style={lblStyle}>Confirm new password</label>
            <input type="password" value={cnfPw} autoComplete="new-password"
              onChange={function(ev){ setCnfPw(ev.target.value); }} onKeyDown={onKey} style={inpStyle} />
            {err && (
              <div style={{fontSize:10.5, fontWeight:600, color:"#b91c1c", background:"#fef2f2",
                           border:"1px solid #fecaca", borderRadius:7, padding:"7px 10px", marginBottom:10}}>
                {err}
              </div>
            )}
            <button onClick={submit} disabled={busy}
              style={{width:"100%", padding:"9px 0", fontSize:12, fontWeight:800, border:"none", borderRadius:8,
                      cursor: busy ? "default" : "pointer", fontFamily:"inherit",
                      background: busy ? "#93c5fd" : "#2563eb", color:"#fff", marginBottom:8}}>
              {busy ? "Saving…" : "Change password"}
            </button>
            {isFirst ? (
              <div style={{textAlign:"center", fontSize:10, color:"#94a3b8", padding:"6px 0"}}>
                🔒 A new password is required before you can use the portal.
              </div>
            ) : (
              <button onClick={function(){ onClose(false); }} disabled={busy}
                style={{width:"100%", padding:"8px 0", fontSize:11, fontWeight:700, borderRadius:8,
                        border:"1.5px solid #e2e8f0", background:"#fff", color:"#64748b",
                        cursor: busy ? "default" : "pointer", fontFamily:"inherit"}}>
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [user,    setUser]    = useState(null);
  const [ds,      setDs]      = useState(null);
  const [dimValues, setDimValues] = useState(null); /* dimension values for filter dropdowns */
  const [sel,     setSel]     = useState(EMPTY_SEL);
  const [pivot,   setPivot]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState([]);
  /* Lazy pivot loading: store context for on-demand child fetching */
  var pivotMetaRef = useRef({ allDims: [], filters: {}, grand: null, mapping: null, sel: null });
  var lazyChildrenRef = useRef({}); /* pathKey → children array */
  var lazyLevelLoadedRef = useRef({}); /* level → true once batch-loaded */
  var lazyLoadingRef = useRef({}); /* pathKey → true while loading */
  var runQueryRef = useRef(null); /* points at latest runQuery — used by reorderPivotDims to re-run after reorder */
  /* When applyTopNFromPivot triggers a re-run (after the user picks "Apply Top 100" in the
     expand-prompt modal), we DON'T want runQuery to clear the just-applied Top N. This ref
     is set to true right before the programmatic call and reset to false on the next runQuery
     entry, so MANUAL clicks of Run Query (FilterPanel button) always clear a prompt-applied
     Top N — giving the user a fresh modal next time. */
  var _skipPromptResetRef = useRef(false);
  const [mainTab, setMainTab] = useState("pivot"); /* "pivot" | "dashboards" */
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminDismissed, setAdminDismissed] = useState(false); /* admin explicitly went to portal */
  const [savedQueries, setSavedQueries] = useState([]);
  /* Password-change modal: null (hidden) | "first" (post-login prompt, skippable)
     | "voluntary" (opened from the 🔑 header button). Admins never get either. */
  const [pwPrompt, setPwPrompt] = useState(null);
  /* Portal launch announcement: true = show the one-time welcome modal */
  const [launchMsg, setLaunchMsg] = useState(false);

  /* ── Force fresh state on every app mount (prevents browser session restore from holding old queries) ── */
  useEffect(function() {
    setSel(EMPTY_SEL);
    setPivot(null);
    setDs(null);
    setDimValues(null);
    setMainTab("pivot");
    setFilterCollapsed(false);
    setErrors([]);
    setLoading(false);
  }, []); /* [] = run once on initial mount only */

  /* r197: a study closed for maintenance while the user is logged in — api.js fires this on
     the server's 423. Drop the study from the user's list, leave it, and show the notice. */
  var _mnS = useState(""), maintNotice = _mnS[0], setMaintNotice = _mnS[1];
  useEffect(function() {
    var onMaint = function(ev) {
      var d = (ev && ev.detail) || {}, code = String(d.study || "");
      setMaintNotice(d.message || "This study is temporarily unavailable for maintenance.");
      if (!code) return;
      setUser(function(prev){
        if (!prev || _isDataAdmin(prev)) return prev;
        var nx = Object.assign({}, prev);
        nx.studyTypes = (prev.studyTypes || []).filter(function(st){ return String(st.code || st) !== code; });
        if (Array.isArray(prev.studyAccess)) nx.studyAccess = prev.studyAccess.filter(function(sa){ return String(sa.studyTypeCode || "") !== code; });
        return nx;
      });
      setActiveStudyType(function(cur){
        if (cur !== code) return cur;
        setDs(null); setDimValues(null); setPivot(null); setSel(EMPTY_SEL); setStudyTypeConfig(null);
        var rest = ((session.user && session.user.studyTypes) || []).filter(function(st){ return String(st.code || st) !== code; });
        return rest.length ? String(rest[0].code || rest[0]) : null;
      });
    };
    window.addEventListener("amsx:study-maintenance", onMaint);
    return function() { window.removeEventListener("amsx:study-maintenance", onMaint); };
  }, []);

  /* ── Listen for "session expired" events fired by api.js when a data
        endpoint returns 401 (Layer 1 security gate rejection). On expiry,
        wipe all in-memory state and force the user back to the login screen.
        Without this, the user would see cryptic "Session expired" errors on
        every action while staying on a stale dashboard. ── */
  useEffect(function() {
    var onExpire = function() {
      try { session.user = null; } catch(e) {}
      setUser(null);
      setDs(null);
      setDimValues(null);
      setSel(EMPTY_SEL);
      setPivot(null);
      setClients([]);
      setAllUsers([]);
      setActiveStudyType(null);
      setStudyTypeConfig(null);
      setMainTab("pivot");
      setFilterCollapsed(false);
      setSavedQueries([]);
      setErrors([]);
      setLoading(false);
      try { setLoadedQueryId(null); setEditingQueryId(null); } catch(e) {}
      /* Inform the user — single alert so they understand why they were kicked out */
      alert("Your session expired. Please log in again.");
    };
    window.addEventListener("amsx:session-expired", onExpire);
    return function() { window.removeEventListener("amsx:session-expired", onExpire); };
  }, []);

  /* ── Load Google Fonts globally ── */
  useEffect(function() {
    if (!document.getElementById("amsx-gfonts")) {
      var link = document.createElement("link");
      link.id = "amsx-gfonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600;700&family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }
    /* Print styles */
    if (!document.getElementById("amsx-print-css")) {
      var style = document.createElement("style");
      style.id = "amsx-print-css";
      style.textContent = "@media print { " +
        "body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; } " +
        "* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } " +
        ".recharts-wrapper { break-inside: avoid !important; page-break-inside: avoid !important; } " +
        ".recharts-wrapper svg { max-width: 100% !important; height: auto !important; } " +
        "[style*='card'] { break-inside: avoid !important; page-break-inside: avoid !important; } " +
        "@page { size: A4 landscape; margin: 10mm; } " +
        "div[style*='boxShadow'] { box-shadow: none !important; border: 1px solid #e2e8f0 !important; break-inside: avoid !important; page-break-inside: avoid !important; } " +
        "div[style*='overflow'] { overflow: visible !important; } " +
        "div[style*='maxHeight'] { max-height: none !important; } " +
        "}";
      document.head.appendChild(style);
    }
  }, []);

  /* ── Study Type state ── */
  const [activeStudyType, setActiveStudyType] = useState(null);    /* selected study type code */
  const [studyTypeConfig, setStudyTypeConfig] = useState(null);    /* full config from server */

  /* ── Custom region split (per-user): the active study's enabled map. All data
     re-grouping happens SERVER-side; client-side this only swaps the Region
     filter list for the user's custom region names (+ reserved "Other"). ── */
  const [userRegionMap, setUserRegionMap] = useState(null);
  const [showRegionBuilder, setShowRegionBuilder] = useState(false);
  const [regionMapVersion, setRegionMapVersion] = useState(0); /* bumps on save → remounts dashboards tab */
  useEffect(function() {
    if (!user || !activeStudyType) { setUserRegionMap(null); return; }
    var alive = true;
    api.getUserRegionMap(activeStudyType).then(function(m) {
      if (!alive) return;
      setUserRegionMap(m && m.enabled && m.regions && Object.keys(m.regions).length ? m : null);
    }).catch(function() { if (alive) setUserRegionMap(null); });
    return function() { alive = false; };
  }, [user, activeStudyType]);
  /* ── Custom region split: filter-list overrides ──
     Region list = the user's custom names (+ reserved "Other"). Governorate list
     is LINKED to the selected custom regions straight from the map (instant,
     client-side) — the server cascade enforces the same link on the data side.
     NOTE: must stay AFTER the userRegionMap/sel declarations (TDZ). */
  const dimValuesEff = useMemo(function() {
    if (!dimValues || !userRegionMap) return dimValues;
    var c = Object.assign({}, dimValues);
    c.region = Object.keys(userRegionMap.regions).concat(["Other"]);
    var selRegs = (sel && sel.region) || [];
    if (selRegs.length) {
      var govs = []; var seen = {};
      selRegs.forEach(function(rn){
        var lst = userRegionMap.regions[rn];
        if (lst) lst.forEach(function(g){ if (!seen[g]) { seen[g] = 1; govs.push(g); } });
      });
      if (selRegs.indexOf("Other") >= 0) {
        var inMap = {}; Object.keys(userRegionMap.regions).forEach(function(rn){ userRegionMap.regions[rn].forEach(function(g){ inMap[g] = 1; }); });
        (dimValues.governorate || []).forEach(function(g){ if (!inMap[g] && !seen[g]) { seen[g] = 1; govs.push(g); } });
      }
      if (govs.length) c.governorate = govs;
    }
    return c;
  }, [dimValues, userRegionMap, sel]);

  /* ── Shared client/user state (loaded from database via API) ── */
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [dbLoading, setDbLoading] = useState(false);

  /* ── Load clients + users from database when user logs in ── */
  useEffect(function() {
    if (!user) return;
    setDbLoading(true);
    Promise.all([api.getClients(), api.getUsers()])
      .then(function(results) {
        setClients(results[0]);
        setAllUsers(results[1]);
      })
      .catch(function(e) { console.error("Failed to load from DB:", e); })
      .finally(function() { setDbLoading(false); });
  }, [user]);

  /* ── On login: load dimension values ── */
  /* Private studies → legacy pharma-data endpoints (proven, fast) */
  /* Public studies → study-type endpoints (dynamic, per-table) */
  useEffect(function() {
    if (!user) return;

    var userStudyTypes = user.studyTypes || [];

    /* If user has study types but hasn't picked one yet → wait for selector */
    if (userStudyTypes.length > 0 && !activeStudyType) return;

    /* Skip if already loaded for this study type */
    if (ds && ds.studyType === (activeStudyType || "legacy")) return;
    if (ds) return;

    setDbLoading(true);
    var t0 = performance.now();
    /* Admin users should not pass clientId (avoids StudyId filter) */
    var effClientId = _isDataAdmin(user) ? "" : (user.clientId || "");

    /* Find selected study type info */
    var stInfo = null;
    if (activeStudyType) {
      for (var i = 0; i < userStudyTypes.length; i++) {
        if (userStudyTypes[i].code === activeStudyType) { stInfo = userStudyTypes[i]; break; }
      }
    }
    /* Studies that are always public regardless of what category value reaches the client.
       Belt-and-suspenders: even if studyTypes.Category is missing/wrong/lost in serialization,
       these studies must always go through the public branch. */
    var isPublic = (stInfo && stInfo.category === "public") || _isPublicStudy(null, activeStudyType);

    if (isPublic) {
      /* ══ PUBLIC study (Kimadia, etc.) → study-type endpoints ══ */

      /* Hardcoded fallback mappings for known public studies */
      var PUBLIC_FALLBACK_MAPS = {
        kimadia: {
          manufacturer:"mf", medicine:"md", sku:"sku", molecule:"mol", agent:"ag",
          atc1:"a1", atc2:"a2", atc3:"a3", atc4:"a4",
          nfc:"nf", nfc2:"n2", nfc3:"n3",
          countryOfOrigin:"coo", manufacturerType:"mt", localImported:"li",
          continent:"ct", subContinent:"sc",
          tenderStatus:"ts", orderNo:"ono",
          country:"co", sector:"se",
          yearCol:"yr",
          _periodMode:"tender", _statusColumn:"ts",
          units:"u", focUnit:"fu", valLC:"l", valUSD:"d", priceUSD:"pu", priceLC:"pl"
        },
        /* GPD (Jordan tenders): initial mapping so the panel renders instantly;
           every query response replaces it with the server's real aliases. */
        gpd: {
          manufacturer:"mf", medicine:"md", sku:"sku", agent:"ag", molecule:"mol",
          atc1:"a1", atc2:"a2", atc3:"a3", atc4:"a4",
          nfc:"nf", nfc2:"n2", nfc3:"n3",
          orderNo:"ono", strength:"st", institution:"ins", tender:"tnd", tenderNo:"tno",
          bidder:"bd", registerStatus:"rst", reasonForAward:"rfa", shelfLife:"shl", tenderState:"tst",
          countryOfOrigin:"coo", manufacturerType:"mt", localImported:"li",
          continent:"ct", subContinent:"sc",
          yearCol:"yr",
          _periodMode:"standard",
          units:"u", focUnit:"fu", unitsIU:"iu", valLC:"l", valUSD:"d",
          priceUSD:"pu", priceLC:"plc", netPriceUSD:"npu", netPriceLC:"npl",
          priceUSDIU:"pui", priceLCIU:"pli"
        }
      };

      Promise.all([
        api.getStudyTypeConfig(activeStudyType).catch(function(){ return null; }),
        api.getStudyDimensions(activeStudyType, effClientId).catch(function(){ return {}; })
      ]).then(function(results) {
        var cfg = results[0];
        var dims = results[1];
        console.log("[perf] Public study " + activeStudyType + " loaded in " + Math.round(performance.now() - t0) + "ms");
        console.log("[loadPublic] cfg:", cfg ? "OK (" + (cfg.features||[]).length + " features)" : "NULL");
        console.log("[loadPublic] dims keys:", dims ? Object.keys(dims).join(", ") : "NULL");
        if (cfg) setStudyTypeConfig(cfg);
        if (dims && Object.keys(dims).length) setDimValues(dims);

        /* Use hardcoded fallback if available, else build from config */
        var mapping = PUBLIC_FALLBACK_MAPS[activeStudyType]
          ? Object.assign({}, PUBLIC_FALLBACK_MAPS[activeStudyType])
          : null;

        if (!mapping) {
          mapping = {};
          if (cfg && cfg.features) cfg.features.forEach(function(f) { mapping[f.key || f.FeatureKey] = f.key || f.FeatureKey; });
          var pCfg = (cfg && cfg.period) || {};
          var pMode = pCfg.PeriodMode || pCfg.mode || "standard";
          if (pMode === "standard" || pMode === "tender") {
            mapping.yearCol = "yearCol";
            if (pCfg.QuarterColumn || pCfg.quarterColumn) mapping.quarterCol = "quarterCol";
            if (pCfg.MonthColumn || pCfg.monthColumn) mapping.monthCol = "monthCol";
          }
          if (pCfg.StatusColumn || pCfg.statusColumn) {
            mapping.tenderStatus = "tenderStatus";
            mapping._statusColumn = pCfg.StatusColumn || pCfg.statusColumn;
          }
          if (cfg && cfg.measures) cfg.measures.forEach(function(m) { mapping[m.key || m.MeasureKey] = m.key || m.MeasureKey; });
        }

        console.log("[loadPublic] mapping keys:", Object.keys(mapping).join(", "));
        setDs({ rows: [], mapping: mapping, fileName: (cfg ? cfg.name : activeStudyType) + " — AMS_OnlinePortal", studyType: activeStudyType });
        setAdminDismissed(true);
      }).catch(function(e) {
        console.error("Failed to load public study:", e);
        /* Even on failure, use fallback mapping if available */
        var fbMap = PUBLIC_FALLBACK_MAPS[activeStudyType]
          ? Object.assign({}, PUBLIC_FALLBACK_MAPS[activeStudyType]) : {};
        console.log("[loadPublic] CATCH — using fallback mapping:", Object.keys(fbMap).join(", "));
        setDs({ rows: [], mapping: fbMap, fileName: activeStudyType + " — AMS_OnlinePortal (offline)", studyType: activeStudyType });
        setAdminDismissed(true);
      }).finally(function() { setDbLoading(false); });

    } else {
      /* ══ PRIVATE study (Iraq/Jordan/Libya) or legacy → pharma-data endpoints ══ */
      Promise.all([
        api.getStudyTypeConfig(activeStudyType).catch(function(){ return null; }),
        api.getDimensions(effClientId, activeStudyType || "")
      ]).then(function(results) {
        var cfg = results[0];
        var dims = results[1];
        console.log("[perf] Private study " + activeStudyType + " loaded in " + Math.round(performance.now() - t0) + "ms");
        if (cfg) {
          setStudyTypeConfig(cfg);
        } else {
          setStudyTypeConfig(null);
        }
        setDimValues(dims);
        var mapping;
        if (activeStudyType === "jordan_medical") {
          /* MedicalData only has: Manufacturer, Product, SKU, ATC1-3, Year, Quarter */
          mapping = {
            manufacturer:"mf", medicine:"md", sku:"sk",
            atc1:"a1", atc2:"a2", atc3:"a3",
            yearCol:"yr", quarterCol:"qr"
          };
        } else {
          mapping = {
            country:"co", sector:"se", manufacturer:"mf", medicine:"md", sku:"sk", agent:"ag",
            region:"rg", governorate:"gv",
            atc1:"a1", atc2:"a2", atc3:"a3",
            yearCol:"yr", quarterCol:"qr", monthCol:"mn"
          };
        }
        /* Add measures dynamically — check config measures or fallback to defaults */
        var cfgMeasures = cfg && cfg.measures ? cfg.measures : [];
        var hasCfgUnits = cfgMeasures.some(function(m){ return m.key === "units" || m.dbColumn === "Units"; });
        var hasCfgValLC = cfgMeasures.some(function(m){ return m.key === "valLC" || m.dbColumn === "ValueLC"; });
        var hasCfgValUSD = cfgMeasures.some(function(m){ return m.key === "valUSD" || m.dbColumn === "ValueUSD"; });
        if (cfgMeasures.length > 0) {
          if (hasCfgUnits || cfgMeasures.some(function(m){ return m.key === "product"; })) mapping.units = "u";
          if (hasCfgValLC) mapping.valLC = "l";
          if (hasCfgValUSD) mapping.valUSD = "d";
        } else {
          if (activeStudyType === "jordan_medical") {
            mapping.units = "u";
          } else {
            mapping.units = "u"; mapping.valLC = "l"; mapping.valUSD = "d";
          }
        }

        /* ── Jordan Medical + Medical & Pharma Intelligence: force ALL medical
           dimensions into base mapping (jordan_msa additionally keeps the pharma
           side — region/governorate/USD/LC — from the base mapping above) ── */
        if (activeStudyType === "jordan_medical" || activeStudyType === "jordan_msa") {
          /* Market — Agent column exists in MedicalData but base mapping didn't
             auto-set it (it wasn't in BASE_DIM_DEFS for the medical branch in some
             flow paths). Force it on so the Filter Panel renders the Agent row. */
          mapping.agent = "ag";
          /* Product Details */
          mapping.form = "fm";
          mapping.pack = "pk";
          mapping.strength = "st";
          mapping.aloneCombined = "ac";
          /* ATC / NFC / Molecule */
          mapping.atc4 = "a4";
          mapping.molecule = "mo";
          mapping.nfc = "nf";
          mapping.nfc2 = "n2";
          mapping.nfc3 = "n3";
          /* Doctor Details */
          mapping.docRegion = "dr";
          mapping.docSpec = "ds";
          mapping.docCity = "dc";
          /* Pharmacy Details */
          mapping.phRegion = "pr";
          mapping.phCity = "pc";
          /* Patient Details */
          mapping.patientAge = "pa";
          mapping.patientGender = "pg";
          /* Indication */
          mapping.diagnosis = "dg";
          /* Manufacturer Origin */
          mapping.continent = "ct";
          mapping.subContinent = "sc";
          mapping.countryOfOrigin = "coo";
          mapping.manufacturerType = "mt";
          mapping.localImported = "li";
          /* # Patients measure (COUNT DISTINCT PatientID) — medical-only */
          mapping.patients = "np";
          /* # Prescriptions measure (COUNT DISTINCT OrderNo) — medical-only.
             Server detects column existence; if absent the lookup will simply be empty. */
          mapping.prescriptions = "nrx";
          /* jordan_medical: no Region/Governorate in MedicalData — doctor/pharmacy
             regions serve this role. jordan_msa: the merged view carries BOTH the
             sales Region/Governorate and the Rx regions, plus the Source
             (Pharma/Rx) discriminator and the pharma value measures. */
          if (activeStudyType === "jordan_msa") {
            mapping.source = "src";
            mapping.valLC = "l";
            mapping.valUSD = "d";
            /* combined region dims (sales Region on Pharma rows, Doctor/Pharmacy
               region on Rx rows) — the study's ONLY region concepts in the panel */
            mapping.docSalesRegion = "dsr";
            mapping.phSalesRegion = "psr";
          }
        }

        /* Add optional columns for non-medical studies — check BOTH studyTypeConfig AND dimValues.
           IMPORTANT: this optMap controls what `mp.*` flags get set on the INITIAL dim-load
           response. The filter panel sidebar reads mp.* to decide which rows to render
           BEFORE any query runs. If a key is missing here, the row never shows up even
           when the column exists on the server. We include `agent`, `nfc`, `nfc2`, `nfc3`
           even though they're "core" optional columns server-side — they were previously
           omitted from this map, which caused them to appear only AFTER a query merged
           the server's mapping back in (line 7289). */
        var optMap = {atc4:"a4",molecule:"mo",continent:"ct",subContinent:"sc",countryOfOrigin:"coo",manufacturerType:"mt",localImported:"li",subManufacturer:"sm",subProduct:"sp",productSource:"ps",
          form:"fm",pack:"pk",strength:"st",aloneCombined:"ac",docRegion:"dr",docSpec:"ds",docCity:"dc",phRegion:"pr",phCity:"pc",patientAge:"pa",patientGender:"pg",diagnosis:"dg",
          /* Newly added — these are core dims sent by server but were missing from the
             initial mapping and so the filter panel never rendered them on first paint. */
          agent:"ag", nfc:"nf", nfc2:"n2", nfc3:"n3", LaunchDate:"ldt",
          /* Sales Tracker: mp.flow gates the Sell-In/Sell-Out period UI in
             FilterPanel — must be present on the INITIAL dim-load mapping,
             before any query merges server mapping. Same for pharmacyClass
             (panel class A-D filter row). */
          flow:"fl", pharmacyClass:"phc",
          /* Medical & Pharma Intelligence: the Pharma/Rx discriminator + combined regions */
          source:"src", docSalesRegion:"dsr", phSalesRegion:"psr"};
        Object.keys(optMap).forEach(function(k) {
          var inConfig = cfg && cfg.features && cfg.features.some(function(f){ return f.key === k; });
          var inDims = dims && dims[k] !== undefined;
          if (inConfig || inDims) {
            mapping[k] = optMap[k];
          }
        });
        /* Always include form/pack/strength — server data has these columns (fm/pk/st aliases) */
        if (!mapping.form) mapping.form = "fm";
        if (!mapping.pack) mapping.pack = "pk";
        if (!mapping.strength) mapping.strength = "st";
        /* Sales Tracker: # Pharmacies / Gross Selling / Stock checkboxes and the
           FOC % statistic toggle gate on mp.* keys — seed them ALL on the
           INITIAL mapping so the sidebar renders them before the first query.
           Server confirms via result mapping when the view carries the columns. */
        if (activeStudyType === "sales_tracker") { mapping.pharmacies = "nph"; mapping.grossUnit = "gu"; mapping.stock = "stk"; mapping.focUnit = "fu"; mapping.profitUSD = "pfd"; mapping.profitLC = "pfl"; mapping.sellInValUSD = "svd"; mapping.sellInValLC = "svl"; mapping.sellOutValUSD = "sod"; mapping.sellOutValLC = "sol"; }
        console.log("[loadStudy] cfg:", cfg ? cfg.features.length + " features" : "NULL");
        console.log("[loadStudy] dims keys:", dims ? Object.keys(dims).join(", ") : "NULL");
        console.log("[loadStudy] mapping keys:", Object.keys(mapping).join(", "));
        setDs({ rows: [], mapping: mapping, fileName: (stInfo ? stInfo.name : "Database") + " — AMS_OnlinePortal", studyType: activeStudyType || "legacy" });
        setAdminDismissed(true);
      })
      .catch(function(e) {
        console.error("Failed to load dimensions:", e);
        setDs({ rows: [], mapping: {}, fileName: "AMS_OnlinePortal (offline)" });
        setAdminDismissed(true);
      })
      .finally(function() { setDbLoading(false); });
    }
  }, [user, activeStudyType]);
  /* ── Global Definition Market → Market tab refresh ──
     When a group that scopes the MARKET tab is applied / removed / switched
     (event fired by the 🌐 header control), refetch the dimension lists so
     the filter dropdowns and their value bars reflect the group. The pivot
     grid itself picks the group up on the next "Run query". */
  useEffect(function() {
    var _gdmH = function() {
      var effCid = _isDataAdmin(user) ? "" : ((user && user.clientId) || "");
      api.getDimensions(effCid, activeStudyType || "").then(function(d) {
        if (d) setDimValues(d);
        /* the scoped lists are on screen → dismiss the "Applying market…" message */
        try { window.dispatchEvent(new CustomEvent("amsx-gdm-applied")); } catch (eAp) {}
      }).catch(function(){});
    };
    window.addEventListener("amsx-gdm-market", _gdmH);
    return function() { window.removeEventListener("amsx-gdm-market", _gdmH); };
  }, [user, activeStudyType]);

  /* ── Handle study type selection (from filter panel dropdown) ── */
  var selectStudyType = function(code) {
    if (code === activeStudyType) return;
    setActiveStudyType(code);
    setDs(null); /* trigger reload */
    setDimValues(null);
    setStudyTypeConfig(null);
    /* Sales Tracker: Flow (Sell-In / Sell-Out) lives in the PERIOD HEADER —
       every selected period (year / quarter / month / MAT / YTD) splits into
       one column band per selected flow (compound keys "2026-Q1~Sell-In").
       periodFlows defaults to both; the Period section's checkboxes narrow it.
       Flow is NOT a row dimension and NOT a sidebar filter row. */
    if (code === "sales_tracker") {
      var _stSel = Object.assign({}, EMPTY_SEL, {
        flow: [],
        periodFlows: ["Sell-In", "Sell-Out"],
        showCols: (EMPTY_SEL.showCols || []).filter(function(k){ return k !== "flow"; })
      });
      setSel(_stSel);
    } else if (code === "jordan_msa") {
      /* Merged Pharma+Rx market: default the Dataset (Source) dimension into the
         pivot so shared measures (Units) never silently sum sales units and
         prescribed units into one figure — the user can untick it deliberately. */
      var _msaSel = Object.assign({}, EMPTY_SEL, {
        showCols: ((EMPTY_SEL.showCols || []).indexOf("source") >= 0) ? EMPTY_SEL.showCols : ["source"].concat(EMPTY_SEL.showCols || [])
      });
      setSel(_msaSel);
    } else setSel(EMPTY_SEL);
    setPivot(null);
    setErrors([]);
    setLoadedQueryId(null); setEditingQueryId(null);  /* prior loaded query belongs to old study */
  };

  /* ── Compute client permissions for current user ── */
  const clientPerms = useMemo(function() {
    if (!user || !user.clientId) return null;
    var cid = String(user.clientId);
    for (var i = 0; i < clients.length; i++) {
      if (String(clients[i].id) === cid) return clients[i];
    }
    return null;
  }, [user, clients]);
  /* r138: when the current tab is hidden for this client + study, move to the first visible one */
  useEffect(function () {
    if (!user || !activeStudyType) return;
    var hid = _hiddenTabs9(user, clientPerms, activeStudyType);
    if (!hid[_TAB_KEY9[mainTab] || mainTab]) return;
    var order = ["pivot", "dashboards", "reports"];
    for (var i = 0; i < order.length; i++) { if (!hid[_TAB_KEY9[order[i]]]) { setMainTab(order[i]); return; } }
    setMainTab("pivot");
  }, [user, clientPerms, activeStudyType, mainTab]);

  /* ── Period access helper: parse "Q1 2023" → {year,q} ── */
  function parseQStr(s) {
    if (!s) return null;
    var m = /Q([1-4])\s*(\d{4})/i.exec(String(s).trim());
    return m ? {q:parseInt(m[1]), year:parseInt(m[2])} : null;
  }
  function qToNum(qObj) { return qObj ? (qObj.year * 10 + qObj.q) : 0; }

  /* ── Filter period options based on client's period access control ── */
  function filterPeriodsByAccess(pOpts, perms) {
    if (!perms || !perms.periodAccess || perms.periodAccess.mode === "none") return pOpts;
    var pa = perms.periodAccess;

    if (pa.mode === "quarter") {
      var fromQ = parseQStr(pa.fromQ);
      var toQ = parseQStr(pa.toQ);
      if (!fromQ && !toQ) return pOpts;
      var fNum = fromQ ? qToNum(fromQ) : 0;
      var tNum = toQ ? qToNum(toQ) : 99999;

      var filterQ = function(arr) {
        return arr.filter(function(item) {
          var iNum = item.year * 10 + (item.q || Math.ceil((item.m || 1) / 3));
          return iNum >= fNum && iNum <= tNum;
        });
      };
      var filterY = function(arr) {
        var minY = fromQ ? fromQ.year : 0;
        var maxY = toQ ? toQ.year : 9999;
        return arr.filter(function(item) { return item.year >= minY && item.year <= maxY; });
      };
      return {
        years: filterY(pOpts.years),
        quarters: filterQ(pOpts.quarters),
        months: filterQ(pOpts.months),
        mats: filterQ(pOpts.mats),
        ytds: filterY(pOpts.ytds),
        ytdMonthly: filterY(pOpts.ytdMonthly||[]),
        matMonths: filterQ(pOpts.matMonths||[]),
        openQuarter: pOpts.openQuarter || null,   /* r185: keep the open-quarter marker for the dashboard default period */
      };
    }

    if (pa.mode === "date") {
      var fd = pa.fromDate ? new Date(pa.fromDate) : null;
      var td = pa.toDate ? new Date(pa.toDate) : null;
      if (!fd && !td) return pOpts;
      var fY = fd ? fd.getFullYear() : 0;
      var tY = td ? td.getFullYear() : 9999;
      var fM = fd ? (fd.getMonth()+1) : 1;
      var tM = td ? (td.getMonth()+1) : 12;

      var filterByDate = function(arr) {
        return arr.filter(function(item) {
          var iY = item.year;
          var iM = item.m || ((item.q || 1) * 3);
          if (iY < fY || iY > tY) return false;
          if (iY === fY && iM < fM) return false;
          if (iY === tY && iM > tM) return false;
          return true;
        });
      };
      var filterYears = function(arr) { return arr.filter(function(i){ return i.year >= fY && i.year <= tY; }); };
      return {
        years: filterYears(pOpts.years),
        quarters: filterByDate(pOpts.quarters),
        months: filterByDate(pOpts.months),
        mats: filterByDate(pOpts.mats),
        ytds: filterYears(pOpts.ytds),
        ytdMonthly: filterYears(pOpts.ytdMonthly||[]),
        matMonths: filterByDate(pOpts.matMonths||[]),
        openQuarter: pOpts.openQuarter || null,   /* r185 */
      };
    }

    return pOpts;
  }

  /* ── Load saved queries from database — scoped to current study ── */
  const loadSavedQueries = useCallback(async function() {
    if (!user) return;
    try {
      /* Promoted (team-admin) users: user.userId is the AdminId; their saved
         queries live under the original portal id (user.dataUserId) — send that.
         The server matches BOTH session identities regardless. */
      var uid = (user.dataUserId != null ? user.dataUserId : user.userId) || "";
      var cid = user.clientId || "";
      /* Pass the active study so server filters out queries belonging to other studies */
      var all = await api.getSavedQueries(uid, cid, activeStudyType || "");
      setSavedQueries(all);
    } catch(e) { console.error("Load queries failed:", e); }
  }, [user, activeStudyType]);

  useEffect(function() {
    if (user) loadSavedQueries();
  }, [user, activeStudyType, loadSavedQueries]);

  /* ── Track which saved query is currently being EDITED in the filter panel.
     Set by the Edit button (which also loads the query's filters into the panel),
     cleared by Save (commit to server) or Cancel (discard exit). When non-null,
     the saved-query row shows amber styling + Save/Cancel buttons; otherwise
     the row shows Load/Edit/Share/✕. ── */
  const [editingQueryId, setEditingQueryId] = useState(null);
  /* ── Track which saved query's CONTENT is currently in the filter panel —
     i.e. the query the user last loaded (via Load or Edit). Used by the refresh
     button so a recipient who has a shared query loaded gets the owner's latest
     edits applied to their panel after clicking ↻. Cleared on logout / study
     switch / EMPTY_SEL reset elsewhere in the app (we just clear it when sel
     gets explicitly reset). ── */
  const [loadedQueryId, setLoadedQueryId] = useState(null);

  /* ── Save a query — auto-tags current study ── */
  const handleSaveQuery = useCallback(async function(name) {
    if (!user) return;
    var trimmedName = String(name || "").trim();
    if (!trimmedName) return;

    /* Saved queries are scoped to (UserId, ClientId). System admins (env-admin and
       SystemAdmins rows) have no clientId, so the server-side FK to Clients would
       fail. Short-circuit here with a friendly message instead of hitting the API. */
    if (!user.clientId) {
      alert("Saved queries are not available for system administrators. Log in as a client user to save queries.");
      return;
    }

    /* Save = always creates a NEW query. To UPDATE an existing query (preserving sharing),
       the user clicks "Save Changes" on that specific saved-query row in the side panel.
       That direct flow is unambiguous — no name retyping, no confirm dialog.
       The old "Smart Save by name match" behavior is kept as a safety net: if user types
       an exact existing name AND is not currently editing a query (so they don't already
       have a direct update path), we warn. Otherwise just create. */
    var existing = null;
    if (!editingQueryId) {
      var nameLower = trimmedName.toLowerCase();
      for (var _qi = 0; _qi < (savedQueries || []).length; _qi++) {
        var _sq = savedQueries[_qi];
        var ownedByMe = _sq.owner === user.name || _sq.ownerUserId === user.userId || (user.dataUserId != null && _sq.ownerUserId === user.dataUserId);
        var sameStudy = !_sq.studyType || _sq.studyType === activeStudyType;
        if (ownedByMe && sameStudy && (_sq.name || "").trim().toLowerCase() === nameLower) {
          existing = _sq;
          break;
        }
      }
    }

    if (existing) {
      /* Soft warning: same-name conflict without a loaded query. Ask once. */
      var shareDesc = "";
      if (existing.sharedScope === "team") shareDesc = " (shared with whole team — they will see the update)";
      else if (existing.sharedScope === "users") shareDesc = " (shared with " + (existing.sharedUserIds||[]).length + " user(s) — they will see the update)";
      var ok = window.confirm('A query named "' + trimmedName + '" already exists in this study. Update it with your current selection?' + shareDesc + '\n\nClick OK to update, or Cancel to create a new query with the same name.');
      if (ok) {
        try {
          await api.updateQuery(existing.id, {
            name: trimmedName,
            sel: JSON.parse(JSON.stringify(sel)),
            userId: (user.dataUserId != null ? user.dataUserId : user.userId) || 0,
            dims: (sel.showCols || []).length,
            measures: (sel.measures || []).length,
          });
          loadSavedQueries();
        } catch(e) {
          console.error("Update query failed:", e);
          alert("Update failed: " + e.message);
        }
        return;
      }
      /* User clicked Cancel → fall through to create a duplicate-named new row */
    }

    /* Create new */
    var query = {
      id: "sq_" + Date.now() + "_" + Math.random().toString(36).slice(2,7),
      name: trimmedName,
      sel: JSON.parse(JSON.stringify(sel)),
      owner: user.name,
      /* portal identity for promoted admins — the server enforces this too */
      userId: (user.dataUserId != null ? user.dataUserId : user.userId) || 0,
      clientId: user.clientId || 0,
      studyType: activeStudyType || "",
      sharedScope: null,
      sharedUserIds: [],
      shared: false,
      dims: (sel.showCols || []).length,
      measures: (sel.measures || []).length,
    };
    try {
      await api.saveQuery(query);
      loadSavedQueries();
    } catch(e) { console.error("Save query failed:", e); }
  }, [user, sel, activeStudyType, loadSavedQueries, savedQueries, editingQueryId]);

  /* ── Delete a query ── */
  const handleDeleteQuery = useCallback(async function(sq) {
    if (!user) return;
    try {
      await api.deleteQuery(sq.id);
      loadSavedQueries();
    } catch(e) { console.error("Delete query failed:", e); }
  }, [user, loadSavedQueries]);

  /* Bulk delete — deletes each query through the same scoped DELETE endpoint
     (so per-query ownership/scope enforcement still applies), then refreshes the
     list once at the end rather than after every delete. */
  const handleDeleteQueries = useCallback(async function(sqs) {
    if (!user || !sqs || !sqs.length) return;
    try {
      for (var i = 0; i < sqs.length; i++) {
        try { await api.deleteQuery(sqs[i].id); }
        catch(e) { console.error("Delete query failed for id=" + (sqs[i] && sqs[i].id) + ":", e); }
      }
    } finally { loadSavedQueries(); }
  }, [user, loadSavedQueries]);

  /* Shared loader — applies a saved query's sel to the filter panel.
     Three defenses (all were sources of "saved query not working"):
     1. MERGE over EMPTY_SEL: a query saved by an older app version (or another
        study's schema) may miss whole fields — applying it raw left sel without
        e.g. .manufacturer/.stats/.periodKeys and broke the panel. EMPTY_SEL
        supplies every current field; the saved values overwrite on top.
     2. Normalize container shapes (topN, stats, atc/nfc filter maps).
     3. SANITIZE against the user's CURRENT grants for the active study: a saved
        query may reference dimensions that were since revoked or disabled
        study-wide (e.g. subManufacturer switched off in Feature Management).
        Without this, Run is rejected server-side with "dimension not granted".
        Dropped items are surfaced as a notice instead of a failing query.
        Full admins skip sanitizing (they bypass server enforcement). */
  var _applySavedSel = useCallback(function(sq) {
    if (!sq || !sq.sel) return;
    var loaded = Object.assign({}, EMPTY_SEL, sq.sel);
    if (!loaded.topN) loaded.topN = {enabled:false, n:10, measure:"Unit", applyTo:"top", mode:"global", dims:[], showOthers:false};
    if (!loaded.topN.mode) loaded.topN.mode = "global";
    if (!loaded.topN.dims) loaded.topN.dims = [];
    if (loaded.topN.showOthers === undefined) loaded.topN.showOthers = false;
    if (!loaded.topN.dimN) loaded.topN.dimN = {}; /* per-dim N overrides */
    if (!loaded.topN.alwaysVals) loaded.topN.alwaysVals = []; /* "always include" pinned values */
    if (loaded.topN.alwaysDim === undefined) loaded.topN.alwaysDim = null;
    if (!loaded.country) loaded.country = [];
    if (!loaded.region) loaded.region = [];
    if (!loaded.governorate) loaded.governorate = [];
    if (!loaded.stats || typeof loaded.stats !== "object") loaded.stats = {indicators:[], growthMode:"prev"};
    if (!Array.isArray(loaded.stats.indicators)) loaded.stats = Object.assign({}, loaded.stats, {indicators:[]});
    if (!loaded.stats.growthMode) loaded.stats = Object.assign({}, loaded.stats, {growthMode:"prev"});
    if (!Array.isArray(loaded.periodKeys)) loaded.periodKeys = [];
    if (!Array.isArray(loaded.showCols)) loaded.showCols = [];
    if (!Array.isArray(loaded.measures)) loaded.measures = [];
    if (loaded.atcFilters == null || typeof loaded.atcFilters !== "object") loaded.atcFilters = {};
    if (loaded.nfcFilters == null || typeof loaded.nfcFilters !== "object") loaded.nfcFilters = {};

    /* ── Grant sanitizing (mirrors FilterPanel + server enforceDimAccess) ── */
    var _isFullAdmin = user && user.role === "admin" && !user.dataScoped;
    var _sqPerms = null;
    if (!_isFullAdmin && user && Array.isArray(user.studyAccess) && activeStudyType) {
      for (var _pi = 0; _pi < user.studyAccess.length; _pi++) {
        if (user.studyAccess[_pi].studyTypeCode === activeStudyType) { _sqPerms = user.studyAccess[_pi]; break; }
      }
    }
    if (_sqPerms && Array.isArray(_sqPerms.effectiveFeatures)) {
      var _okDim = {};
      _sqPerms.effectiveFeatures.forEach(function(k){ _okDim[String(k).toLowerCase()] = 1; });
      var _dropped = [];
      /* Pivot dimensions (GROUP BY) — exactly what the server refuses when ungranted */
      loaded.showCols = loaded.showCols.filter(function(k){
        if (_okDim[String(k).toLowerCase()]) return true;
        _dropped.push(FEAT_LBL[k] || k); return false;
      });
      /* Value filters on revoked dims — would silently distort results, clear them */
      ["manufacturer","subManufacturer","medicine","subProduct","productSource","sku","agent",
       "region","governorate","atc1","atc2","atc3","atc4","molecule","continent","subContinent",
       "countryOfOrigin","manufacturerType","localImported","nfc","nfc2","nfc3","form","pack",
       "strength","aloneCombined","docRegion","docSpec","docCity","phRegion","phCity",
       "patientAge","patientGender","diagnosis","flow","pharmacyClass","LaunchDate","source","docSalesRegion","phSalesRegion"].forEach(function(k){
        if (_okDim[k.toLowerCase()]) return;
        if (Array.isArray(loaded[k]) && loaded[k].length) { loaded[k] = []; if (_dropped.indexOf(FEAT_LBL[k] || k) < 0) _dropped.push(FEAT_LBL[k] || k); }
      });
      Object.keys(loaded.atcFilters).forEach(function(ak){
        if (!_okDim[String(ak).toLowerCase()] && Array.isArray(loaded.atcFilters[ak]) && loaded.atcFilters[ak].length) {
          loaded.atcFilters = Object.assign({}, loaded.atcFilters); delete loaded.atcFilters[ak];
          if (_dropped.indexOf(ak) < 0) _dropped.push(ak);
        }
      });
      Object.keys(loaded.nfcFilters).forEach(function(nk){
        if (!_okDim[String(nk).toLowerCase()] && Array.isArray(loaded.nfcFilters[nk]) && loaded.nfcFilters[nk].length) {
          loaded.nfcFilters = Object.assign({}, loaded.nfcFilters); delete loaded.nfcFilters[nk];
          if (_dropped.indexOf(nk) < 0) _dropped.push(nk);
        }
      });
      if (_dropped.length) {
        console.log("[saved-query] sanitized on load — no longer available in this study:", _dropped.join(", "));
        setErrors(["Saved query loaded — " + _dropped.join(", ") + " " + (_dropped.length === 1 ? "is" : "are") + " no longer available in this study and " + (_dropped.length === 1 ? "was" : "were") + " removed from the selection."]);
      } else {
        setErrors([]);
      }
    }
    setSel(loaded);
    setPivot(null);
  }, [user, activeStudyType]);

  /* ── Load a query (read-only) ──
     Pulls the saved selection into the filter panel. Does NOT enter edit mode —
     the row stays in its default styling. User can run the query, tweak filters
     freely, save with a new name, etc. without affecting the original. To modify
     the saved query in place, use Edit instead. */
  const handleLoadQuery = useCallback(function(sq) {
    if (!sq || !sq.id) return;
    _applySavedSel(sq);
    setLoadedQueryId(sq.id);   /* remember which query is in the panel — used by refresh */
    setEditingQueryId(null);   /* explicit: if we were editing another query, exit that edit mode */
  }, [_applySavedSel]);

  /* ── Edit a query ──
     Same load behavior PLUS enters edit mode: the row turns amber and shows
     Save / Cancel instead of the normal action buttons. Save commits the
     current filter state back to this query; Cancel exits edit mode without
     server changes (filter panel keeps current state). */
  const handleStartEdit = useCallback(function(sq) {
    if (!sq || !sq.id) return;
    _applySavedSel(sq);
    setLoadedQueryId(sq.id);
    setEditingQueryId(sq.id);
  }, [_applySavedSel]);

  /* ── Cancel edit ──
     Exits edit mode without server interaction. The user's current filter state
     stays (we don't revert to the saved query's original — letting them keep
     any work-in-progress). loadedQueryId is preserved so a subsequent ↻ refresh
     will still re-apply the latest sel to the panel. */
  const handleCancelEdit = useCallback(function() {
    setEditingQueryId(null);
  }, []);

  /* "Save" in edit mode — direct update of the query being edited. No name prompt,
     no confirm. The query keeps its sharing config; the Name + Selection get
     rewritten with what the user has in the panel. Recipients of a shared query
     get the new content on their next ↻ refresh. */
  const handleUpdateLoadedQuery = useCallback(async function(sq) {
    if (!user) return;
    if (!sq || !sq.id) return;
    try {
      await api.updateQuery(sq.id, {
        name: sq.name,
        sel: JSON.parse(JSON.stringify(sel)),
        userId: user.userId || 0,
        dims: (sel.showCols || []).length,
        measures: (sel.measures || []).length,
      });
      loadSavedQueries();
      /* Exit edit mode on success — row returns to default styling. loadedQueryId
         stays set so the user is still considered "viewing" this query. */
      setEditingQueryId(null);
    } catch(e) {
      console.error("Update query failed:", e);
      alert("Update failed: " + e.message);
    }
  }, [user, sel, loadSavedQueries]);

  /* ── Refresh saved queries (manual ↻ button) ──
     Re-fetches the saved-queries list AND, if a query is loaded in the panel and
     the user is NOT mid-edit, re-applies that query's freshly-fetched sel. This is
     how recipients of a shared query pick up the owner's latest edits: they click
     ↻ and their panel updates with the new content.

     We deliberately skip the re-apply when editingQueryId is set — wiping the
     user's in-progress edits on a refresh would be a hostile surprise. */
  const handleRefreshQueries = useCallback(async function() {
    if (!user) return;
    try {
      var uid = (user.dataUserId != null ? user.dataUserId : user.userId) || "";
      var cid = user.clientId || "";
      var all = await api.getSavedQueries(uid, cid, activeStudyType || "");
      setSavedQueries(all);
      if (loadedQueryId && !editingQueryId) {
        for (var i = 0; i < all.length; i++) {
          if (all[i].id === loadedQueryId) {
            _applySavedSel(all[i]);
            break;
          }
        }
      }
    } catch(e) { console.error("Refresh queries failed:", e); }
  }, [user, activeStudyType, loadedQueryId, editingQueryId, _applySavedSel]);

  /* ── Share query: opens a modal to pick scope (private/team/users). ──
     The legacy toggle endpoint flips IsShared as a fallback for older clients. */
  const [shareModalQuery, setShareModalQuery] = useState(null);
  const handleShareQuery = useCallback(function(sq) {
    if (!user) return;
    setShareModalQuery(sq);
  }, [user]);

  const handleSaveShareConfig = useCallback(async function(sq, scope, userIds) {
    if (!user) return;
    try {
      await api.shareQueryConfig(sq.id, scope, userIds || []);
      setShareModalQuery(null);
      loadSavedQueries();
    } catch(e) { console.error("Share config save failed:", e); alert("Share update failed: " + e.message); }
  }, [user, loadSavedQueries]);

  const pOptsRaw = useMemo(function() {
    /* Always prefer server-supplied dimensions for period dropdowns — they represent the full dataset */
    if (dimValues) {
      var po9 = buildPeriodOptsFromDims(dimValues);
      /* r108: star the open quarter in every list (quarters, MAT, YTD) and publish it */
      try {
        var oq = dimValues.openQuarter || null;
        window.__amsxOpenQ9 = oq;
        if (oq && po9) {
          var mark = function(arr){ return (arr || []).map(function(it){ return (it && _isOpenPk9(it.key) && String(it.label || "").indexOf("*") < 0) ? Object.assign({}, it, { label: it.label + "*" }) : it; }); };
          po9 = Object.assign({}, po9, { quarters: mark(po9.quarters), mats: mark(po9.mats), ytds: mark(po9.ytds), openQuarter: oq });
        }
      } catch (eOq) {}
      return po9;
    }
    /* Fallback: build from rows (only for initial upload/offline scenarios) */
    if (ds && ds.rows && ds.rows.length > 0) return buildPeriodOpts(ds.rows, ds.mapping);
    return {years:[],quarters:[],months:[],mats:[],ytds:[],matMonths:[]};
  }, [ds, dimValues]);
  const pOpts = useMemo(() => filterPeriodsByAccess(pOptsRaw, clientPerms), [pOptsRaw, clientPerms]);

  /* ── Effective Top-N ranking measure: never a measure the user hasn't
     selected. The Ranked-by pill in the filter panel auto-corrects its
     DISPLAY (availM[0]) when the stored measure is stale — e.g. the "Unit"
     default while only USD is selected — but the stored value used to reach
     the server untouched and silently rank the Top-N membership by Units.
     This helper is the single source of truth for the query AND the badges. */
  function tnEffMeasure(sel9) {
    var tn9 = sel9 && sel9.topN;
    if (!tn9) return "Unit";
    var m9 = tn9.measure || "Unit";
    var ms9 = (sel9 && sel9.measures) || [];
    var u9 = ms9.indexOf("Unit") >= 0, d9 = ms9.indexOf("USD") >= 0;
    var l9 = ms9.indexOf("LC") >= 0, p9 = ms9.indexOf("Price") >= 0;
    var ok9 = false;
    if ((m9 === "Unit" || m9 === "FOC Unit" || m9 === "Units IU") && u9) ok9 = true;
    else if (m9 === "USD" && d9) ok9 = true;
    else if ((m9 === "LC" || m9 === "IQD") && l9) ok9 = true;
    else if ((m9 === "Price USD" || m9 === "Net Price USD") && p9 && d9) ok9 = true;
    else if ((m9 === "Price LC" || m9 === "Price IQD" || m9 === "Net Price LC" || m9 === "Net Price IQD") && p9 && l9) ok9 = true;
    else if (m9 === "Price" && p9 && (d9 || l9)) ok9 = true;
    else if ((m9 === "Patients" || m9 === "Prescriptions" || m9 === "RxPerDoctor" || m9 === "Doctors" || m9 === "DoctorsTotal") && ms9.indexOf(m9) >= 0) ok9 = true;
    if (ok9) return m9;
    return u9 ? "Unit" : d9 ? "USD" : l9 ? "LC" : m9;
  }

  /* ── Top N filtering helper ── */
  function applyTopN(pivotResult, topNConfig) {
    if (!pivotResult || !topNConfig || !topNConfig.enabled) return pivotResult;
    var n = topNConfig.n || 10;
    var mKey = topNConfig.measure === "Unit" ? "u" : topNConfig.measure === "Units IU" ? "iu" : topNConfig.measure === "FOC Unit" ? "fu" : topNConfig.measure === "LC" ? "l" : topNConfig.measure === "IQD" ? "l" : topNConfig.measure === "USD" ? "d" : topNConfig.measure === "Price USD" ? "pd" : topNConfig.measure === "Price LC" ? "pl" : topNConfig.measure === "Price IQD" ? "pl" : topNConfig.measure === "Net Price USD" ? "npd" : topNConfig.measure === "Net Price LC" ? "npl" : topNConfig.measure === "Net Price IQD" ? "npl" : topNConfig.measure === "Price" ? (topNConfig._priceIsUSD ? "pd" : "pl") : "d";
    var isBottom = topNConfig.applyTo === "bottom";
    var periods = pivotResult.periods;
    var mode = topNConfig.mode || "global";
    var tnDims = topNConfig.dims || [];

    /* Deep-clone a node (and its children) to avoid mutating original data */
    function cloneNode(nd) {
      var d2 = {};
      if (nd.data) {
        for (var pk in nd.data) {
          if (nd.data.hasOwnProperty(pk)) {
            var s = nd.data[pk];
            d2[pk] = {u:s.u, fu:s.fu||0, gu:s.gu||0, l:s.l, d:s.d, pl:s.pl||0, pd:s.pd||0, npl:s.npl||0, npd:s.npd||0, np:s.np||0, nrx:s.nrx||0, rxpd:(s.rxpd == null ? null : s.rxpd), ndr:s.ndr||0, ndrt:(s.ndrt == null ? null : s.ndrt), iu:s.iu||0, pli:s.pli||0, pdi:s.pdi||0,
              us:s.us, fus:s.fus||"0.0", ls:s.ls, ds:s.ds, pls:s.pls||"0.0", pds:s.pds||"0.0", npls:s.npls||"0.0", npds:s.npds||"0.0", nps:s.nps||"0.0", nrxs:s.nrxs||"0.0", ius:s.ius||"0.0"};
          }
        }
      }
      var ch = nd.children ? nd.children.map(cloneNode) : [];
      return {name:nd.name, dim:nd.dim, depth:nd.depth, isATC:nd.isATC, data:d2, children:ch, _lazy:nd._lazy};
    }

    /* Helper: compute total measure across all periods for a node */
    function nodeTotal(nd) {
      var total = 0;
      for (var pi = 0; pi < periods.length; pi++) {
        var pk = periods[pi];
        if (nd.data && nd.data[pk]) total += (nd.data[pk][mKey] || 0);
      }
      return total;
    }

    var showOthers = topNConfig.showOthers || false;
    /* "Always include": pinned values of one feature stay in the result even
       outside the Top-N cut, and carry their ACTUAL rank (computed over the
       FULL sibling set, per period, per measure slot). */
    var alwDim = topNConfig.alwaysDim || null;
    var alwVals = {};
    (topNConfig.alwaysVals || []).forEach(function(v){ alwVals[String(v).trim().toUpperCase()] = 1; });
    var alwCnt = (topNConfig.alwaysVals || []).length;
    var RNK_SLOTS = ["u","fu","iu","l","d","pl","pd","npl","npd","np","nrx"];
    function stampActualRanks(nd, fullArr, pks9) {
      var all9 = fullArr.filter(function(x){ return !(x.name && x.name.indexOf("Others (") === 0); });
      var ov9 = {};
      for (var pi9 = 0; pi9 < pks9.length; pi9++) {
        var pk9 = pks9[pi9]; ov9[pk9] = {};
        for (var si9 = 0; si9 < RNK_SLOTS.length; si9++) {
          var sk9 = RNK_SLOTS[si9];
          var v9 = nd.data && nd.data[pk9] ? (nd.data[pk9][sk9] || 0) : 0;
          var r9 = 1;
          for (var ai9 = 0; ai9 < all9.length; ai9++) {
            var od9 = all9[ai9].data && all9[ai9].data[pk9];
            if (od9 && (od9[sk9] || 0) > v9) r9++;
          }
          ov9[pk9][sk9] = r9;
        }
      }
      nd._rnkOv = ov9; nd._pinned = true;
    }

    /* Helper: sort and slice a node array to top/bottom N */
    function sliceNodes(nodeArr, count, dimKey9) {
      if (!nodeArr || nodeArr.length <= count) return nodeArr;
      var withTotal = nodeArr.map(function(nd) { return {node:nd, total:nodeTotal(nd)}; });
      withTotal.sort(function(a,b) { return isBottom ? a.total - b.total : b.total - a.total; });
      var kept = withTotal.slice(0, count).map(function(item){ return item.node; });
      var restItems = withTotal.slice(count);
      if (alwCnt && alwDim && dimKey9 === alwDim) {
        var pinned9 = [];
        restItems = restItems.filter(function(item){
          var nm9 = String(item.node.name || "").trim().toUpperCase();
          if (alwVals[nm9]) { pinned9.push(item.node); return false; }
          return true;
        });
        pinned9.forEach(function(nd){ stampActualRanks(nd, nodeArr, periods); kept.push(nd); });
      }

      if (showOthers && restItems.length > 0) {
        var rest = restItems;
        /* If only 1 remaining item, show it directly (no "Others (1)" grouping) */
        if (rest.length === 1) {
          kept.push(rest[0].node);
        } else {
        /* Build "Others" node by summing all remaining items */
        var othersData = {};
        for (var opi = 0; opi < periods.length; opi++) {
          var opk = periods[opi];
          othersData[opk] = {u:0, fu:0, gu:0, l:0, d:0, pf:0, pfl:0, sv:0, svl:0, pl:0, pd:0, npl:0, npd:0, np:0, nrx:0, nph:0, pfs:"0.0", svs:"0.0", us:"0.0", fus:"0.0", gus:"0.0", ls:"0.0", ds:"0.0", pls:"0.0", pds:"0.0", npls:"0.0", npds:"0.0", nps:"0.0", nrxs:"0.0", nphs:"0.0",ius:"0.0"};
          var plCnt = 0; var nplCnt = 0;
          for (var ori = 0; ori < rest.length; ori++) {
            var ord = rest[ori].node.data && rest[ori].node.data[opk];
            if (ord) {
              othersData[opk].u += (ord.u || 0);
              othersData[opk].fu += (ord.fu || 0);
              othersData[opk].l += (ord.l || 0);
              othersData[opk].d += (ord.d || 0);
              othersData[opk].pf += (ord.pf || 0);
              othersData[opk].pfl += (ord.pfl || 0);
              othersData[opk].sv += (ord.sv || 0);
              othersData[opk].svl += (ord.svl || 0);
              if (ord.pl || ord.pd) { othersData[opk].pl += (ord.pl || 0); othersData[opk].pd += (ord.pd || 0); plCnt++; }
              if (ord.npl || ord.npd) { othersData[opk].npl += (ord.npl || 0); othersData[opk].npd += (ord.npd || 0); nplCnt++; }
            }
          }
          if (plCnt > 0) { othersData[opk].pl = othersData[opk].pl / plCnt; othersData[opk].pd = othersData[opk].pd / plCnt; }
          if (nplCnt > 0) { othersData[opk].npl = othersData[opk].npl / nplCnt; othersData[opk].npd = othersData[opk].npd / nplCnt; }
        }
        var refNode = kept[0] || nodeArr[0];
        var othersNode = {
          name: "Others (" + rest.length + ")",
          dim: refNode.dim,
          depth: refNode.depth,
          isATC: false,
          data: othersData,
          children: []
        };
        kept.push(othersNode);
        }
      }

      return kept;
    }

    /* Helper: recursively apply Top N at ALL dimension depths */
    function applyGlobal(nodeArr, depth) {
      if (!nodeArr || !nodeArr.length) return nodeArr;
      depth = depth || 0;
      /* When server pre-sliced level 0 (top + Others bucket), don't re-slice here — just descend. */
      var skipThisLevel = depth === 0 && topNConfig._skipLevel0;
      var result = skipThisLevel ? nodeArr.slice() : sliceNodes(nodeArr, n, (pivotResult.dims || [])[depth]);
      if (!result) return nodeArr;
      for (var i = 0; i < result.length; i++) {
        if (result[i].children && result[i].children.length) {
          result[i] = Object.assign({}, result[i], { children: applyGlobal(result[i].children, depth + 1) });
        }
      }
      return result;
    }

    /* Helper: recursively apply Top N at specific dimension depths.
       Per-dim N overrides: topNConfig.dimN[dimKey] takes precedence over the
       default n. So Manufacturer can be Top 10 while Product Name is Top 5,
       in the same query. Missing/null entries fall back to the default n. */
    var dimN = topNConfig.dimN || {};
    function applySelective(nodeArr, depth, dims) {
      if (!nodeArr || !nodeArr.length) return nodeArr;
      var currentDim = dims.length > depth ? dims[depth] : null;
      var shouldApplyHere = tnDims.indexOf(currentDim) >= 0;
      var nForLevel = (shouldApplyHere && dimN[currentDim] != null) ? dimN[currentDim] : n;
      var result = shouldApplyHere ? sliceNodes(nodeArr, nForLevel, currentDim) : nodeArr.slice();
      /* Recurse into children */
      for (var i = 0; i < result.length; i++) {
        if (result[i].children && result[i].children.length) {
          result[i] = Object.assign({}, result[i], { children: applySelective(result[i].children, depth + 1, dims) });
        }
      }
      return result;
    }

    /* Clone all nodes first to avoid mutating original pivot data */
    var clonedNodes = pivotResult.nodes.map(cloneNode);

    var filteredNodes;
    if (mode === "selective" && tnDims.length > 0) {
      filteredNodes = applySelective(clonedNodes, 0, pivotResult.dims);
    } else {
      /* Global mode: apply Top N at ALL levels of the tree */
      filteredNodes = applyGlobal(clonedNodes, 0);
      if (!filteredNodes) return pivotResult;
    }

    /* Recompute grand totals based on filtered top-level nodes */
    var newGrand = {};
    var origGrand = pivotResult.grand || {};
    for (var pi2 = 0; pi2 < periods.length; pi2++) {
      var pk2 = periods[pi2];
      newGrand[pk2] = {u:0, fu:0, l:0, d:0, pf:0, pfl:0, sv:0, svl:0, pl:0, pd:0, npl:0, npd:0, np:0, nrx:0, pfs:"100.0", svs:"100.0", us:"100.0", fus:"100.0", ls:"100.0", ds:"100.0", pls:"100.0", pds:"100.0", npls:"100.0", npds:"100.0", nps:"100.0", nrxs:"100.0"};
      /* Preserve true distinct counts from server (cannot be summed/recomputed from leaf nodes).
         Both # Patients (np) and # Prescriptions (nrx) are COUNT(DISTINCT) — they don't sum. */
      if (origGrand[pk2] && origGrand[pk2].np != null) newGrand[pk2].np = origGrand[pk2].np;
      if (origGrand[pk2] && origGrand[pk2].nph != null) newGrand[pk2].nph = origGrand[pk2].nph;
      if (origGrand[pk2] && origGrand[pk2].nrx != null) newGrand[pk2].nrx = origGrand[pk2].nrx;
      if (origGrand[pk2] && origGrand[pk2].rxpd != null) newGrand[pk2].rxpd = origGrand[pk2].rxpd;   /* r121: ratio, never recomputed from rows */
      if (origGrand[pk2] && origGrand[pk2].ndr != null) newGrand[pk2].ndr = origGrand[pk2].ndr;     /* r122: distinct doctors */
      if (origGrand[pk2] && origGrand[pk2].ndrt != null) newGrand[pk2].ndrt = origGrand[pk2].ndrt;
      var plCount = 0; var nplCount = 0;
      for (var ni = 0; ni < filteredNodes.length; ni++) {
        if (filteredNodes[ni].data && filteredNodes[ni].data[pk2]) {
          var _nd = filteredNodes[ni].data[pk2];
          newGrand[pk2].u += _nd.u || 0;
          newGrand[pk2].fu += _nd.fu || 0;
          newGrand[pk2].l += _nd.l || 0;
          newGrand[pk2].d += _nd.d || 0;
          newGrand[pk2].pf += _nd.pf || 0;
          newGrand[pk2].pfl += _nd.pfl || 0;
          newGrand[pk2].sv += _nd.sv || 0;
          newGrand[pk2].svl += _nd.svl || 0;
          var _npl = _nd.pl || 0;
          var _npd = _nd.pd || 0;
          if (_npl !== 0 || _npd !== 0) {
            newGrand[pk2].pl += _npl;
            newGrand[pk2].pd += _npd;
            plCount++;
          }
          var _nnpl = _nd.npl || 0;
          var _nnpd = _nd.npd || 0;
          if (_nnpl !== 0 || _nnpd !== 0) {
            newGrand[pk2].npl += _nnpl;
            newGrand[pk2].npd += _nnpd;
            nplCount++;
          }
        }
      }
      /* Price grand = average of node averages */
      if (plCount > 0) {
        newGrand[pk2].pl = newGrand[pk2].pl / plCount;
        newGrand[pk2].pd = newGrand[pk2].pd / plCount;
      }
      if (nplCount > 0) {
        newGrand[pk2].npl = newGrand[pk2].npl / nplCount;
        newGrand[pk2].npd = newGrand[pk2].npd / nplCount;
      }
    }
    /* Recompute share percentages for filtered nodes */
    var pct2 = function(a,b){ return b ? ((a/b)*100).toFixed(1) : "0.0"; };
    function recomputeShare(nodeList) {
      for (var i = 0; i < nodeList.length; i++) {
        var nd = nodeList[i];
        for (var pi3 = 0; pi3 < periods.length; pi3++) {
          var pk3 = periods[pi3];
          if (nd.data && nd.data[pk3]) {
            nd.data[pk3].us = pct2(nd.data[pk3].u, newGrand[pk3].u);
            nd.data[pk3].fus = pct2(nd.data[pk3].fu, newGrand[pk3].fu);
            nd.data[pk3].ls = pct2(nd.data[pk3].l, newGrand[pk3].l);
            nd.data[pk3].ds = pct2(nd.data[pk3].d, newGrand[pk3].d);
            nd.data[pk3].pls = pct2(nd.data[pk3].pl, newGrand[pk3].pl);
            nd.data[pk3].pds = pct2(nd.data[pk3].pd, newGrand[pk3].pd);
            nd.data[pk3].npls = pct2(nd.data[pk3].npl, newGrand[pk3].npl);
            nd.data[pk3].npds = pct2(nd.data[pk3].npd, newGrand[pk3].npd);
          }
        }
        if (nd.children && nd.children.length) recomputeShare(nd.children);
      }
    }
    recomputeShare(filteredNodes);
    return Object.assign({}, pivotResult, {nodes: filteredNodes, grand: newGrand});
  }

  /* Reorder dims directly from the pivot header. Updates sel.showCols (so the filter
     panel stays in sync) and re-runs the query so that lazy children are re-fetched
     under the new hierarchy. Uses functional setState so we can chain runQuery
     after the state has been committed. */
  const reorderPivotDims = useCallback(function(dragKey, targetKey) {
    setSel(function(p){
      var sc = (p.showCols || []).slice();
      var di = sc.indexOf(dragKey);
      var ti = sc.indexOf(targetKey);
      if (di < 0 || ti < 0 || di === ti) return p;
      sc.splice(di, 1);
      var insertAt = sc.indexOf(targetKey);
      /* Insert BEFORE the target — matches the visual cue of "drop here pushes target right". */
      sc.splice(insertAt, 0, dragKey);
      return Object.assign({}, p, {showCols: sc});
    });
    /* React batches the setSel above; defer the re-run by one tick so runQuery sees the new sel. */
    setTimeout(function(){
      try { runQueryRef.current && runQueryRef.current(); } catch(e){ console.error("[reorder] re-run failed:", e); }
    }, 0);
  }, []);

  /* Apply Top N from the pivot's expand-prompt modal. Sets sel.topN, preserves the
     existing sort measure/direction if available, and re-runs the query. The user's
     existing topN config (if any) is overwritten — they explicitly asked for "Top 100". */
  const applyTopNFromPivot = useCallback(function(cfg) {
    var n = (cfg && cfg.n) || 100;
    var showOthers = !!(cfg && cfg.showOthers);
    setSel(function(p){
      /* Pick a default measure for ranking: prefer USD/LC/Unit based on what's already selected. */
      var rankMeasure = "Unit";
      if (p.measures && p.measures.length) {
        if (p.measures.indexOf("USD") >= 0) rankMeasure = "USD";
        else if (p.measures.indexOf("LC") >= 0) rankMeasure = "LC";
        else if (p.measures.indexOf("Unit") >= 0) rankMeasure = "Unit";
        else rankMeasure = p.measures[0];
      }
      var newTopN = Object.assign({}, p.topN || {}, {
        enabled: true,
        n: n,
        showOthers: showOthers,
        dir: (p.topN && p.topN.dir) || "top",
        measure: (p.topN && p.topN.measure) || rankMeasure,
        /* Mark this Top N as applied via the expand prompt — so the next time the user
           clicks "Run Query" manually, we can reset it and let the heavy-expansion check
           run fresh against the new query. This makes the modal appear again when the
           user creates a new query rather than carrying over the temporary Top N. */
        _appliedViaPrompt: true
      });
      return Object.assign({}, p, {topN: newTopN});
    });
    /* Re-run after state commits. Open the Top N section in the filter panel so the user
       sees what was applied (purely cosmetic — modal already conveyed it).
       Set _skipPromptResetRef so this programmatic re-run keeps the Top N we just applied —
       only MANUAL clicks of Run Query should clear it. */
    _skipPromptResetRef.current = true;
    setTimeout(function(){
      try { runQueryRef.current && runQueryRef.current(); } catch(e){ console.error("[applyTopN] re-run failed:", e); }
    }, 0);
  }, []);

  const runQuery = () => {
    const e = [];
    if (!sel.showCols.length) e.push("Check at least one feature (✓) to define market rows.");
    if (!sel.measures.length) e.push("Select at least one measure.");
    setErrors(e);
    if (e.length) return;
    setLoading(true); setPivot(null);

    /* If the Top N was applied via the expand-prompt modal (i.e., user clicked "Apply Top 100"),
       clear it on the NEXT manual Run Query so the heavy-expansion check can fire fresh.
       Programmatic re-runs from applyTopNFromPivot set _skipPromptResetRef=true to preserve
       the just-applied Top N for that one re-run. */
    var promptResetApplied = false;
    if (sel.topN && sel.topN._appliedViaPrompt) {
      if (_skipPromptResetRef.current) {
        /* This is the re-run that comes from applyTopNFromPivot — keep Top N, just consume the flag */
        _skipPromptResetRef.current = false;
      } else {
        /* Manual click — clear the prompt-applied Top N before running */
        promptResetApplied = true;
        setSel(function(p){
          var newTopN = Object.assign({}, p.topN, {enabled: false, _appliedViaPrompt: false});
          return Object.assign({}, p, {topN: newTopN});
        });
      }
    }
    /* If we just queued a setSel above, defer the actual run by one tick so the new sel is read */
    if (promptResetApplied) {
      setLoading(false); /* will be re-set when the deferred runQuery fires */
      setTimeout(function(){ runQueryRef.current && runQueryRef.current(); }, 0);
      return;
    }

    /* ── Build permission-aware filters to send to server ── */
    var filters = {
      /* Sales Tracker: the Period section's flow checkboxes drive BOTH the row
         filter (only selected flows fetched) AND the server's flow-split grain
         (server adds Flow to SELECT/GROUP BY whenever a flow filter is present,
         so the pivot can split every period band into Sell-In / Sell-Out).
         Defaults to both flows for flow-capable studies. */
      flow: (ds && ds.mapping && ds.mapping.flow)
        ? (sel.periodFlows === undefined ? ["Sell-In", "Sell-Out"] : sel.periodFlows)
        : (sel.flow || []),
      country: sel.country || [],
      sector: sel.sector || [],
      manufacturer: sel.manufacturer || [],
      medicine: sel.medicine || [],
      sku: sel.sku || [],
      agent: sel.agent || [],
      region: sel.region || [],
      governorate: sel.governorate || [],
      continent: sel.continent || [],
      subContinent: sel.subContinent || [],
      countryOfOrigin: sel.countryOfOrigin || [],
      manufacturerType: sel.manufacturerType || [],
      localImported: sel.localImported || [],
      subManufacturer: sel.subManufacturer || [],
      subProduct: sel.subProduct || [],
      productSource: sel.productSource || [],
      LaunchDate: sel.LaunchDate || [],
      pharmacyClass: sel.pharmacyClass || [],
      /* Medical / doctor / pharmacy / patient dimensions — must be explicit,
         don't rely on studyTypeConfig.features loop alone */
      form: sel.form || [],
      pack: sel.pack || [],
      strength: sel.strength || [],
      aloneCombined: sel.aloneCombined || [],
      docRegion: sel.docRegion || [],
      docSpec: sel.docSpec || [],
      docCity: sel.docCity || [],
      phRegion: sel.phRegion || [],
      phCity: sel.phCity || [],
      patientAge: sel.patientAge || [],
      patientGender: sel.patientGender || [],
      diagnosis: sel.diagnosis || [],
      source: sel.source || [],
      docSalesRegion: sel.docSalesRegion || [],
      phSalesRegion: sel.phSalesRegion || [],
      atcFilters: sel.atcFilters || {},
      nfcFilters: sel.nfcFilters || {}
    };
    /* Include dynamic feature filters from study type config.
       We send the filter under BOTH the feature key AND the dbColumn name (lowercased),
       so the server matches regardless of how FeatureKey was named in StudyTypeFeatures. */
    if (studyTypeConfig && studyTypeConfig.features) {
      studyTypeConfig.features.forEach(function(f) {
        var vals = sel[f.key];
        if (vals && vals.length) {
          if (!filters[f.key]) filters[f.key] = vals;
          /* Also expose under the DB column name (lowercased) as a fallback key */
          if (f.dbColumn) {
            var dbKey = String(f.dbColumn).toLowerCase();
            if (!filters[dbKey] && dbKey !== String(f.key).toLowerCase()) filters[dbKey] = vals;
          }
        }
      });
    }
    /* Enforce client permission restrictions */
    if (clientPerms && clientPerms.allowedSectors && clientPerms.allowedSectors.length > 0) {
      var allSectorsAllowed = SECTOR_TYPES.every(function(st) { return clientPerms.allowedSectors.indexOf(st) >= 0; });
      if (!allSectorsAllowed && !filters.sector.length) filters.sector = clientPerms.allowedSectors;
    }

    /* ── Fetch aggregated data from server (GROUP BY in SQL) ── */
    var dims = sel.showCols.filter(function(k){ return k; }).slice(0, MAX_PIVOT_DIMS);
    if (clientPerms && clientPerms.allowedDimensions) {
      dims = dims.filter(function(k){ return clientPerms.allowedDimensions.indexOf(k) >= 0; });
    }

    /* Choose endpoint: public study → study-data, private/legacy → pharma-data */
    var dataPromise;
    var qClientId = _isDataAdmin(user) ? "" : (user.clientId || "");
    var topNActive = sel.topN && sel.topN.enabled;
    /* Check if any data filters are active (not measures/statistics/period) */
    var hasAnyFilter = false;
    var _fKeys = ["country","sector","manufacturer","medicine","sku","agent","region","governorate",
                  "continent","subContinent","countryOfOrigin","manufacturerType","localImported"];
    for (var _fki = 0; _fki < _fKeys.length; _fki++) {
      if (filters[_fKeys[_fki]] && filters[_fKeys[_fki]].length > 0) { hasAnyFilter = true; break; }
    }
    if (!hasAnyFilter) {
      var _atc = filters.atcFilters || {};
      var _atcKeys = Object.keys(_atc);
      for (var _aki = 0; _aki < _atcKeys.length; _aki++) {
        if (_atc[_atcKeys[_aki]] && _atc[_atcKeys[_aki]].length > 0) { hasAnyFilter = true; break; }
      }
    }
    /* Also check dynamic feature filters */
    if (!hasAnyFilter && studyTypeConfig && studyTypeConfig.features) {
      for (var _dfi = 0; _dfi < studyTypeConfig.features.length; _dfi++) {
        var _dfk = studyTypeConfig.features[_dfi].key;
        if (filters[_dfk] && filters[_dfk].length > 0) { hasAnyFilter = true; break; }
      }
    }
    /* ── Always full-fetch ──
       Previously we used progressive lazy loading: fetch L1 only, then load each
       parent's children on-demand. That caused random rendering order (parallel
       fetches racing each other) and hit rate limits on Expand All.

       New approach: always fetch ALL dimensions in ONE query. The server's
       GROUP BY aggregates the data; SQL Server can do (Manufacturer × Product × SKU)
       in 5-15 seconds with the existing covering indexes. The pivot builds the
       tree from the flat result — all children are present from the start, so
       Expand All is just CSS visibility toggling (no server roundtrips).

       Top N still uses level-based query when enabled (server-side TopN ranks at
       L1, then returns only top-N rows + their full descendants). */
    var initialLevel = null; /* fetch all levels in one shot */
    /* ── MEDICAL exception ──
       Rx-audit dimensions (diagnosis / doctor specialty / regions) explode leaf
       cardinality: a 6-dim full-fetch on MedicalData returns ~680K rows and takes
       ~60s end-to-end. For medical studies with 4+ dimensions, use PROGRESSIVE
       loading instead: fetch level 1 now (~hundreds of rows in <1s), children
       load on expand — the same lazy machinery the Top N path already uses.
       Retail studies keep the full-fetch behavior unchanged. */
    var _isMedicalStudy = String(activeStudyType || "").toLowerCase().indexOf("medical") >= 0;
    if (_isMedicalStudy && dims.length >= 4) initialLevel = 1;
    console.log("[runQuery] dims=" + dims.join(",") + " topN=" + topNActive + " filtered=" + hasAnyFilter + " level=" + (initialLevel != null ? initialLevel + " (progressive)" : "all (full-fetch)"));
    var qMeasures = sel.measures || [];
    /* Coverage % statistic derives from # Pharmacies distinct counts — request
       the Pharmacies measure server-side even when its checkbox is unticked
       (the column itself stays hidden; only the lookups are needed). */
    if (sel.stats && Array.isArray(sel.stats.indicators) && sel.stats.indicators.indexOf("Coverage %") >= 0 && qMeasures.indexOf("Pharmacies") < 0) {
      qMeasures = qMeasures.concat(["Pharmacies"]);
    }
    /* Profit STATISTIC sums the view's Profit + SellOutValue (retail) columns
       — the keywords ride in the measures list for the server. */
    if (sel.stats && Array.isArray(sel.stats.indicators) && sel.stats.indicators.indexOf("Profit") >= 0) {
      if (qMeasures.indexOf("Profit") < 0) qMeasures = qMeasures.concat(["Profit"]);
      if (qMeasures.indexOf("SellInValue") < 0) qMeasures = qMeasures.concat(["SellInValue"]);
      if (qMeasures.indexOf("SellOutValue") < 0) qMeasures = qMeasures.concat(["SellOutValue"]);
    }
    var qPeriodMode = sel.periodMode || "";
    var qPeriodKeys = sel.periodKeys || [];
    /* ── Effective period mode is derived from the KEYS, not the last-opened
       accordion section: keys are self-typed by format. One type → that type's
       mode (keeps mode/keys consistent even if the user browsed other type
       panels). Several types → "Multi" (server fetches the union of windows;
       the pivot attributes, bands and grows each type independently). ── */
    if (qPeriodMode !== "TenderStatus" && qPeriodKeys.length > 0) {
      var _qTypeSeen = {}; var _qTypeList = [];
      for (var _qti = 0; _qti < qPeriodKeys.length; _qti++) {
        var _qt = periodKeyType(qPeriodKeys[_qti]);
        if (_qt && !_qTypeSeen[_qt]) { _qTypeSeen[_qt] = 1; _qTypeList.push(_qt); }
      }
      if (_qTypeList.length === 1) qPeriodMode = _qTypeList[0];
      else if (_qTypeList.length > 1) qPeriodMode = "Multi";
    }

    /* Build server-side Top N options.
       Server-side Top-N only makes sense for GLOBAL mode (slice top dim, server returns top + Others bucket).
       For SELECTIVE mode (apply at specific dim levels only), server-side Top-N would mistakenly filter
       at the wrong level — let the client do all the slicing in applyTopN/applySelective. */
    var qTopNOpts = null;
    var _isPubQ = _isPublicStudy(studyTypeConfig, activeStudyType);
    /* PUBLIC studies (kimadia, gpd): NEVER request server-side Top-N. The
       study-data endpoint ignores the params, and a non-null qTopNOpts makes the
       client (a) skip slicing lazy children and (b) trust a server ranking that
       can be computed over a different window/measure than what is displayed —
       that is exactly the GPD "wrong Top-25 membership" bug (global mode picked
       a different manufacturer set than the on-screen USD/2026 ranking, while
       selective mode — always client-ranked — was right). Client-side applyTopN
       ranks by the SELECTED measure over the DISPLAYED periods at every level. */
    if (topNActive && (sel.topN.mode || "global") === "global" && !_isPubQ) {
      var _tnMsr = tnEffMeasure(sel);
      /* Resolve measure name for server */
      var _tnHasUSD = qMeasures.indexOf("USD") >= 0;
      var _tnHasLC = qMeasures.indexOf("LC") >= 0;
      if (_tnMsr === "Price" && _tnHasUSD) _tnMsr = "Price USD";
      else if (_tnMsr === "Price" && _tnHasLC) _tnMsr = "Price LC";
      qTopNOpts = {
        n: sel.topN.n || 10,
        measure: _tnMsr.replace(/\s+/g, ""),
        dir: sel.topN.applyTo === "bottom" ? "bottom" : "top",
        showOthers: sel.topN.showOthers || false,
        alwaysDim: (sel.topN.alwaysDim && (sel.topN.alwaysVals || []).length) ? sel.topN.alwaysDim : null,
        always: (sel.topN.alwaysVals || []).slice(0, 50)
      };
    }

    if (_isPublicStudy(studyTypeConfig, activeStudyType)) {
      dataPromise = api.getStudyDataAggregate(activeStudyType, qClientId, filters, dims, initialLevel, qMeasures, qPeriodMode, qPeriodKeys, qTopNOpts);
    } else {
      dataPromise = api.getFilteredData(qClientId, filters, dims, activeStudyType || "", initialLevel, qMeasures, qPeriodMode, qPeriodKeys, qTopNOpts);
    }
    var _qStart = Date.now();
    /* Clear lazy children cache for new query */
    lazyChildrenRef.current = {};
    lazyLevelLoadedRef.current = {};
    lazyLoadingRef.current = {};

    dataPromise
      .then(function(result) {
        /* Merge server mapping INTO original — keep all features for filter panel */
        var displayMapping = Object.assign({}, ds.mapping, result.mapping || {});
        /* Pivot mapping: use ONLY the server result mapping — this has only dims that were queried */
        var pivotMapping = result.mapping || {};
        var rows = result.rows || [];
        var serverHandledTopN = result.serverTopN || false;
        console.log("[runQuery] Server returned " + rows.length + " rows, level=" + (initialLevel != null ? initialLevel : "all") + " serverTopN=" + serverHandledTopN);

        /* ── LOG QUERY EXECUTION ──
           Build a COMPACT payload that mirrors what's actually in the pivot, not
           the raw filter skeleton. Drops empty arrays/maps so the timeline view
           shows only fields the user actually selected. Order chosen to read
           top-to-bottom: what they measured → grouped by → for which period →
           filtered by → ranked by → with stats. */
        try {
          /* Helper: only include filter keys whose value is non-empty.
             Handles flat arrays AND the nested atcFilters/nfcFilters maps. */
          var _compactFilters = (function(){
            var out = {};
            for (var k in filters) {
              if (!Object.prototype.hasOwnProperty.call(filters, k)) continue;
              var v = filters[k];
              if (Array.isArray(v)) {
                if (v.length > 0) out[k] = v;
              } else if (v && typeof v === "object") {
                /* atcFilters / nfcFilters — keep only non-empty sub-arrays */
                var sub = {};
                var hasAny = false;
                for (var sk in v) {
                  if (Array.isArray(v[sk]) && v[sk].length > 0) {
                    sub[sk] = v[sk]; hasAny = true;
                  }
                }
                if (hasAny) out[k] = sub;
              } else if (v != null && v !== "") {
                out[k] = v;
              }
            }
            return out;
          })();
          /* Compact Top N — include only when enabled. Strip showOthers when false
             and dims when empty (irrelevant in global mode). */
          var _compactTopN = null;
          if (sel.topN && sel.topN.enabled) {
            _compactTopN = { enabled: true, n: sel.topN.n, measure: tnEffMeasure(sel), mode: sel.topN.mode || "global" };
            if (sel.topN.mode === "selective" && Array.isArray(sel.topN.dims) && sel.topN.dims.length) {
              _compactTopN.dims = sel.topN.dims;
            }
            if (sel.topN.dimN && Object.keys(sel.topN.dimN).length) {
              _compactTopN.perDimN = sel.topN.dimN;
            }
            if (sel.topN.showOthers) _compactTopN.showOthers = true;
          }
          /* Compact Stats — only when at least one indicator is on. statMeasures
             is included as-is since it's the user's per-measure scope mapping. */
          var _compactStats = null;
          if (sel.stats && Array.isArray(sel.stats.indicators) && sel.stats.indicators.length) {
            _compactStats = { indicators: sel.stats.indicators };
            if (sel.stats.growthMode && sel.stats.growthMode !== "prev") {
              _compactStats.growthMode = sel.stats.growthMode;
            }
            if (sel.stats.statMeasures && Object.keys(sel.stats.statMeasures).length) {
              _compactStats.statMeasures = sel.stats.statMeasures;
            }
          }
          var _compactPayload = {
            measures: qMeasures,
            dims: dims,
            period: { mode: qPeriodMode, keys: qPeriodKeys }
          };
          if (Object.keys(_compactFilters).length) _compactPayload.filters = _compactFilters;
          if (_compactTopN)  _compactPayload.topN  = _compactTopN;
          if (_compactStats) _compactPayload.stats = _compactStats;

          logActivity("query_execute", {
            studyType: activeStudyType,
            dashboardName: "pivot",
            queryPayload: _compactPayload,
            rowsReturned: rows.length,
            durationMs: Date.now() - _qStart
          });
        } catch (e) { /* silent */ }

        /* Update ds with merged mapping (for filter panel) */
        setDs(function(prev) { return { rows: rows, mapping: displayMapping, fileName: prev ? prev.fileName : "Database", studyType: prev ? prev.studyType : "" }; });

        /* Build selection with permission-filtered dimensions */
        var permSel = Object.assign({}, sel);
        if (clientPerms && clientPerms.allowedDimensions) {
          var ad = clientPerms.allowedDimensions;
          permSel = Object.assign({}, permSel, {showCols: (permSel.showCols||[]).filter(function(k){ return ad.indexOf(k) >= 0; })});
        }

        var p = buildPivot(rows, pivotMapping, permSel, pOpts, { allDims: dims, patientsLookup: result.patientsLookup || null, pharmaciesLookup: result.pharmaciesLookup || null, stockLookup: result.stockLookup || null, rxLookup: result.rxLookup || null, doctorsLookup: result.doctorsLookup || null, doctorsDistinctLookup: result.doctorsDistinctLookup || null, coverageLookup: result.coverageLookup || null });

        /* Store metadata for lazy child loading */
        if (p) {
          pivotMetaRef.current = { allDims: dims, filters: filters, grand: p.grand, mapping: pivotMapping, sel: permSel, pOpts: pOpts, measures: qMeasures, periodMode: qPeriodMode, periodKeys: qPeriodKeys, topNOpts: qTopNOpts, patientsLookup: result.patientsLookup || null, pharmaciesLookup: result.pharmaciesLookup || null, stockLookup: result.stockLookup || null, rxLookup: result.rxLookup || null, doctorsLookup: result.doctorsLookup || null, doctorsDistinctLookup: result.doctorsDistinctLookup || null, coverageLookup: result.coverageLookup || null, _diag: result._diag || null, lazyMode: initialLevel === 1 };
          /* Debug hook: expose pivot state to window so devtools can inspect it. Safe to leave in. */
          try { if (typeof window !== "undefined") { window.__amsxPivotMeta = pivotMetaRef.current; window.__amsxPivot = p; } } catch(e){}
          /* Loud console diagnostic: shows server's view of what happened with prescriptions.
             Visible in browser DevTools without needing pm2 logs. */
          if (result._diag) {
            console.log("%c[AMSX DIAG] " + result._diag.reason, "background:#fef3c7;color:#92400e;padding:2px 6px;font-weight:bold;border-radius:3px");
            console.log("[AMSX DIAG] full:", result._diag);
          }
          console.log("[runQuery] Pivot built: L1 nodes=" + p.nodes.length + " allDims=" + dims.join(",") + " hasLazy=" + p.nodes.some(function(n){return n._lazy;}));
        }

        /* Apply Top N filtering.
           - Global mode + serverHandledTopN: server already sliced level 0 (top + Others).
             We still need client-side slicing for deeper levels (L2, L3, ...).
           - Selective mode: server-side Top-N is never used for selective; client applies all slicing.
           - Global mode without server: client slices every level. */
        if (p && sel.topN && sel.topN.enabled) {
          var topNCfg = Object.assign({}, sel.topN);
          topNCfg.measure = tnEffMeasure(sel); /* never rank by an unselected measure */
          var mp2 = displayMapping;
          var mOk = false;
          var _hasUSD = sel.measures.indexOf("USD") >= 0;
          var _hasLC = sel.measures.indexOf("LC") >= 0;
          var _hasPrice = sel.measures.indexOf("Price") >= 0;
          if (topNCfg.measure === "Unit"      && mp2.units    && sel.measures.indexOf("Unit") >= 0) mOk = true;
          if (topNCfg.measure === "USD"       && mp2.valUSD   && _hasUSD) mOk = true;
          if (topNCfg.measure === "LC"        && mp2.valLC    && _hasLC) mOk = true;
          if (topNCfg.measure === "Price USD" && mp2.priceUSD && _hasPrice && _hasUSD) mOk = true;
          if (topNCfg.measure === "Price LC"  && mp2.priceLC  && _hasPrice && _hasLC) mOk = true;
          /* Legacy: "Price" without USD/LC suffix */
          if (topNCfg.measure === "Price" && _hasPrice) {
            if (_hasUSD && mp2.priceUSD) { topNCfg.measure = "Price USD"; mOk = true; }
            else if (_hasLC && mp2.priceLC) { topNCfg.measure = "Price LC"; mOk = true; }
          }
          if (!mOk) {
            if (sel.measures.indexOf("Unit") >= 0 && mp2.units) topNCfg.measure = "Unit";
            else if (_hasUSD  && mp2.valUSD) topNCfg.measure = "USD";
            else if (_hasLC   && mp2.valLC)  topNCfg.measure = "LC";
            else if (_hasPrice && _hasUSD && mp2.priceUSD) topNCfg.measure = "Price USD";
            else if (_hasPrice && _hasLC  && mp2.priceLC)  topNCfg.measure = "Price LC";
          }
          topNCfg._priceIsUSD = _hasUSD;
          /* If server already handled level 0, tell applyTopN to skip slicing at depth 0 */
          topNCfg._skipLevel0 = !!serverHandledTopN;
          pivotMetaRef.current.topNCfg = topNCfg; /* store for lazy child loading */
          p = applyTopN(p, topNCfg);
          /* Server-side Top-N with pinned values: the server returns every
             row's ACTUAL rank (topNRanks) — stamp it on the pinned level-0
             rows that sit outside the Top-N cut. */
          if (serverHandledTopN && result.topNRanks && (topNCfg.alwaysVals || []).length) {
            var _rnkN9 = topNCfg.n || 10;
            var _rMap9 = {};
            Object.keys(result.topNRanks).forEach(function(k9){ _rMap9[String(k9).trim().toUpperCase()] = result.topNRanks[k9]; });
            (p.nodes || []).forEach(function(nd9){
              var rr9 = _rMap9[String(nd9.name || "").trim().toUpperCase()];
              if (rr9 && rr9 > _rnkN9) { nd9._rnkAll = rr9; nd9._pinned = true; }
            });
          }
        }
        setPivot(p); setLoading(false);
      })
      .catch(function(err) {
        console.error("runQuery error:", err);
        setErrors(["Failed to load data: " + err.message]);
        setLoading(false);
      });
  };
  /* Keep the ref pointing at the latest runQuery so reorderPivotDims can call it
     without React rebuilding callbacks every render. */
  runQueryRef.current = runQuery;

  /* ── Lazy pivot child loading ── */
  /* Helper: apply Top N to a flat array of child nodes */
  var _applyTopNToChildren = function(children, topNCfg) {
    if (!topNCfg || !topNCfg.enabled || !children || !children.length) return children;
    /* Per-dim N: in selective mode, children at a specific dim may have an override.
       Sibling children all share the same dim, so we read it from children[0]. */
    var defaultN = topNCfg.n || 10;
    var childDim = children[0] && children[0].dim;
    var dimNMap = topNCfg.dimN || {};
    var n = (childDim && dimNMap[childDim] != null) ? dimNMap[childDim] : defaultN;
    /* In selective mode, only apply at dims the user picked. Otherwise descend without slicing. */
    if ((topNCfg.mode||"global") === "selective") {
      var allowedDims = topNCfg.dims || [];
      if (childDim && allowedDims.indexOf(childDim) < 0) return children;
    }
    if (children.length <= n) return children;
    var alwDim9 = topNCfg.alwaysDim || null;
    var alwSet9 = {};
    (topNCfg.alwaysVals || []).forEach(function(v){ alwSet9[String(v).trim().toUpperCase()] = 1; });
    var mKey = topNCfg.measure === "Unit" ? "u" : topNCfg.measure === "Units IU" ? "iu" : topNCfg.measure === "FOC Unit" ? "fu" : topNCfg.measure === "LC" ? "l" : topNCfg.measure === "IQD" ? "l" : topNCfg.measure === "USD" ? "d" : topNCfg.measure === "Price USD" ? "pd" : topNCfg.measure === "Price LC" ? "pl" : topNCfg.measure === "Price" ? (topNCfg._priceIsUSD ? "pd" : "pl") : "d";
    var isBottom = topNCfg.applyTo === "bottom";
    var withTotal = children.map(function(nd) {
      var total = 0;
      if (nd.data) { for (var pk in nd.data) { if (nd.data.hasOwnProperty(pk)) total += (nd.data[pk][mKey] || 0); } }
      return {node: nd, total: total};
    });
    withTotal.sort(function(a, b) { return isBottom ? a.total - b.total : b.total - a.total; });
    var kept = withTotal.slice(0, n).map(function(item) { return item.node; });
    var restL9 = withTotal.slice(n);
    if (alwDim9 && childDim === alwDim9 && (topNCfg.alwaysVals || []).length) {
      var pin9 = [];
      restL9 = restL9.filter(function(item){
        var nm9 = String(item.node.name || "").trim().toUpperCase();
        if (alwSet9[nm9]) { pin9.push(item.node); return false; }
        return true;
      });
      if (pin9.length) {
        var slots9 = ["u","fu","iu","l","d","pl","pd","npl","npd","np","nrx"];
        var pkSet9 = {};
        children.forEach(function(c9){ if (c9.data) { for (var k9 in c9.data) { if (c9.data.hasOwnProperty(k9)) pkSet9[k9] = 1; } } });
        var pks9 = Object.keys(pkSet9);
        pin9.forEach(function(nd){
          var ov9 = {};
          for (var pi9 = 0; pi9 < pks9.length; pi9++) {
            var pk9 = pks9[pi9]; ov9[pk9] = {};
            for (var si9 = 0; si9 < slots9.length; si9++) {
              var sk9 = slots9[si9];
              var v9 = nd.data && nd.data[pk9] ? (nd.data[pk9][sk9] || 0) : 0;
              var r9 = 1;
              for (var ci9 = 0; ci9 < children.length; ci9++) {
                var cd9 = children[ci9].data && children[ci9].data[pk9];
                if (cd9 && (cd9[sk9] || 0) > v9) r9++;
              }
              ov9[pk9][sk9] = r9;
            }
          }
          nd._rnkOv = ov9; nd._pinned = true; kept.push(nd);
        });
      }
    }
    if (topNCfg.showOthers && restL9.length > 0) {
      var rest = restL9;
      /* If only 1 remaining item, show it directly (no "Others (1)" grouping) */
      if (rest.length === 1) {
        kept.push(rest[0].node);
      } else {
      var othersData = {};
      var periods = Object.keys(kept[0].data || {});
      for (var pi = 0; pi < periods.length; pi++) {
        var pk2 = periods[pi]; othersData[pk2] = {u:0,fu:0,gu:0,l:0,d:0,pf:0,pfl:0,sv:0,svl:0,pl:0,pd:0,npl:0,npd:0,np:0,nrx:0,nph:0,iu:0,pli:0,pdi:0,us:"0.0",fus:"0.0",gus:"0.0",ls:"0.0",ds:"0.0",pls:"0.0",pds:"0.0",npls:"0.0",npds:"0.0",nps:"0.0",nrxs:"0.0",nphs:"0.0",ius:"0.0"};
        for (var ri = 0; ri < rest.length; ri++) { var rd = rest[ri].node.data && rest[ri].node.data[pk2]; if (rd) { othersData[pk2].u += (rd.u||0); othersData[pk2].l += (rd.l||0); othersData[pk2].d += (rd.d||0); othersData[pk2].pf += (rd.pf||0); othersData[pk2].pfl += (rd.pfl||0); othersData[pk2].sv += (rd.sv||0); othersData[pk2].svl += (rd.svl||0); } }
      }
      kept.push({name:"Others ("+rest.length+")", dim:kept[0].dim, depth:kept[0].depth, isATC:false, data:othersData, children:[], _lazy:false});
      }
    }
    return kept;
  };

  /* Batch level loader — used by the level chips on PROGRESSIVE (medical) pivots.
     Instead of firing one child-query per parent node (747 molecules = 747 requests),
     fetch the ENTIRE next level in a single aggregate call and distribute the
     children to every parent via lazyChildrenRef. */
  var lazyLevelPromiseRef = useRef({}); /* r132: level → in-flight promise (export waits on it) */
  var loadPivotLevel = function(depth) {
    var meta = pivotMetaRef.current;
    if (!meta || !meta.lazyMode || !meta.allDims || !meta.allDims.length) return Promise.resolve(false);
    var nextLevel = depth + 1;
    var lk = "__level" + nextLevel;
    if (lazyLevelLoadedRef.current[nextLevel]) return Promise.resolve(true);
    if (lazyLoadingRef.current[lk]) return lazyLevelPromiseRef.current[nextLevel] || Promise.resolve(false);
    lazyLoadingRef.current[lk] = true;
    setPivot(function(prev) { return prev ? Object.assign({}, prev) : prev; });
    var qClientId = _isDataAdmin(user) ? "" : (user.clientId || "");
    var fetchFn;
    if (_isPublicStudy(studyTypeConfig, activeStudyType)) {
      fetchFn = api.getStudyDataAggregate(activeStudyType, qClientId, meta.filters, meta.allDims, nextLevel, meta.measures, meta.periodMode, meta.periodKeys, meta.topNOpts);
    } else {
      fetchFn = api.getFilteredData(qClientId, meta.filters, meta.allDims, activeStudyType || "", nextLevel, meta.measures, meta.periodMode, meta.periodKeys, meta.topNOpts);
    }
    var _lvP9 = fetchFn.then(function(result) {
      var childMapping = Object.assign({}, meta.mapping, result.mapping || {});
      var rows = result.rows || [];
      console.log("[lazyLevel] level=" + nextLevel + " rows=" + rows.length + " (single batch query)");
      var childPivot = buildPivot(rows, childMapping, Object.assign({}, meta.sel), meta.pOpts, {
        grandOverride: meta.grand, allDims: meta.allDims,
        patientsLookup: result.patientsLookup || null, pharmaciesLookup: result.pharmaciesLookup || null, stockLookup: result.stockLookup || null, rxLookup: result.rxLookup || null, doctorsLookup: result.doctorsLookup || null, doctorsDistinctLookup: result.doctorsDistinctLookup || null, coverageLookup: result.coverageLookup || null
      });
      /* Walk the fetched tree; every node at `depth` donates its children */
      var walk = function(nodes, parts) {
        for (var i = 0; i < nodes.length; i++) {
          var nd = nodes[i];
          var p2 = parts.concat([nd.name]);
          if (nd.depth === depth) {
            var key = p2.join("||");
            if (!lazyChildrenRef.current[key]) {
              var kids = nd.children || [];
              if (meta.topNCfg && meta.topNCfg.enabled && !meta.topNOpts && kids.length > 0) {
                kids = _applyTopNToChildren(kids, meta.topNCfg);
              }
              lazyChildrenRef.current[key] = kids;
            }
          } else if (nd.children && nd.children.length) {
            walk(nd.children, p2);
          }
        }
      };
      walk((childPivot && childPivot.nodes) || [], []);
      lazyLevelLoadedRef.current[nextLevel] = true;
      delete lazyLoadingRef.current[lk];
      setPivot(function(prev) { return prev ? Object.assign({}, prev) : prev; });
      return true;
    }).catch(function(e) {
      console.error("[lazyLevel] failed:", e);
      delete lazyLoadingRef.current[lk];
      setPivot(function(prev) { return prev ? Object.assign({}, prev) : prev; });
      return false;
    });
    lazyLevelPromiseRef.current[nextLevel] = _lvP9;
    return _lvP9;
  };
  /* r132: EXPORT COMPLETENESS. Medical studies with 4+ dimensions load the pivot
     progressively (levels 0-1 now, deeper levels on expand), so an Excel export
     taken before everything was expanded only carried the rows on screen. Before
     exporting, every remaining level is batch-loaded (one query per level, in
     order) so the export tree is complete; the on-screen pivot keeps its
     collapsed state. Resolves true when the tree is complete, false when a level
     failed (the export then says so). Non-lazy queries resolve immediately. */
  var loadAllPivotLevels = function() {
    var meta = pivotMetaRef.current;
    if (!meta || !meta.lazyMode || !meta.allDims || meta.allDims.length < 3) return Promise.resolve(true);
    var chain = Promise.resolve(true), okAll = true;
    var step = function(dd) { chain = chain.then(function(){ return loadPivotLevel(dd); }).then(function(r){ if (r === false) okAll = false; }); };
    for (var d = 1; d < meta.allDims.length - 1; d++) step(d);
    return chain.then(function(){ return okAll; });
  };
  /* r132: the Grid export projects RAW rows; in progressive mode the cached rows
     only cover levels 0-1, so it re-fetches the complete flat result (all
     dimensions, same filters / measures / periods / Top N) and the server's
     distinct-count lookups for it. Resolves null when the cached rows are
     already complete. */
  var fetchExportRows = function() {
    var meta = pivotMetaRef.current;
    if (!meta || !meta.lazyMode || !meta.allDims || !meta.allDims.length) return Promise.resolve(null);
    var qClientId = _isDataAdmin(user) ? "" : (user.clientId || "");
    var fetchFn = _isPublicStudy(studyTypeConfig, activeStudyType)
      ? api.getStudyDataAggregate(activeStudyType, qClientId, meta.filters, meta.allDims, null, meta.measures, meta.periodMode, meta.periodKeys, meta.topNOpts)
      : api.getFilteredData(qClientId, meta.filters, meta.allDims, activeStudyType || "", null, meta.measures, meta.periodMode, meta.periodKeys, meta.topNOpts);
    return fetchFn.then(function(result){
      console.log("[gridExport] full fetch rows=" + ((result && result.rows) || []).length);
      return { rows: (result && result.rows) || [], mapping: Object.assign({}, meta.mapping, (result && result.mapping) || {}),
        patientsLookup: result.patientsLookup || null, pharmaciesLookup: result.pharmaciesLookup || null, stockLookup: result.stockLookup || null, rxLookup: result.rxLookup || null, doctorsLookup: result.doctorsLookup || null, doctorsDistinctLookup: result.doctorsDistinctLookup || null };
    });
  };
  var loadPivotChildren = function(pathKey, parentNames, depth) {
    if (lazyChildrenRef.current[pathKey] || lazyLoadingRef.current[pathKey]) return; /* already loaded or loading */
    var meta = pivotMetaRef.current;
    if (!meta.allDims || !meta.allDims.length) return;
    var nextLevel = depth + 1;
    if (nextLevel >= meta.allDims.length) return;
    lazyLoadingRef.current[pathKey] = true;
    /* Force re-render to show loading indicator */
    setPivot(function(prev) { return prev ? Object.assign({}, prev) : prev; });
    /* Build parent filters: add parent dim values.
       CRITICAL: ATC and NFC dimensions are sent to the server through nested filter
       structures (filters.atcFilters.ATC3, filters.nfcFilters.NFC). All other dim
       keys are flat (filters.medicine, filters.region). When we add a parent value
       to childFilters, we must route it to the correct slot — otherwise the server
       receives no constraint on the parent and returns rows from the full dataset
       (e.g., expanding ATC3 "C10A" returns Molecules from EVERY ATC3). */
    var childFilters = Object.assign({}, meta.filters);
    /* Deep-clone the nested ATC/NFC filter objects so we don't mutate meta.filters */
    childFilters.atcFilters = Object.assign({}, meta.filters.atcFilters || {});
    childFilters.nfcFilters = Object.assign({}, meta.filters.nfcFilters || {});
    var _DIM_TO_FILTER_SLOT = {
      atc1: ["atcFilters", "ATC1"], atc2: ["atcFilters", "ATC2"],
      atc3: ["atcFilters", "ATC3"], atc4: ["atcFilters", "ATC4"],
      molecule: ["atcFilters", "MOLECULE"],
      nfc: ["nfcFilters", "NFC"], nfc2: ["nfcFilters", "NFC2"], nfc3: ["nfcFilters", "NFC3"]
    };
    for (var i = 0; i <= depth; i++) {
      var dimKey = meta.allDims[i];
      if (parentNames[i] == null) continue;
      var slot = _DIM_TO_FILTER_SLOT[dimKey];
      if (slot) {
        /* Nested ATC/NFC dim — clone existing array (avoid mutating parent state) and append */
        var existing = (childFilters[slot[0]][slot[1]] || []).slice();
        existing.push(parentNames[i]);
        childFilters[slot[0]][slot[1]] = existing;
      } else {
        /* Flat dim — same pattern but on the top-level key */
        var existingFlat = (childFilters[dimKey] || []).slice();
        existingFlat.push(parentNames[i]);
        childFilters[dimKey] = existingFlat;
      }
    }
    var qClientId = _isDataAdmin(user) ? "" : (user.clientId || "");
    var fetchFn;
    if (_isPublicStudy(studyTypeConfig, activeStudyType)) {
      fetchFn = api.getStudyDataAggregate(activeStudyType, qClientId, childFilters, meta.allDims, nextLevel, meta.measures, meta.periodMode, meta.periodKeys, meta.topNOpts);
    } else {
      fetchFn = api.getFilteredData(qClientId, childFilters, meta.allDims, activeStudyType || "", nextLevel, meta.measures, meta.periodMode, meta.periodKeys, meta.topNOpts);
    }
    fetchFn.then(function(result) {
      var childMapping = Object.assign({}, meta.mapping, result.mapping || {});
      var childRows = result.rows || [];
      console.log("[lazyLoad] pathKey=" + pathKey + " level=" + nextLevel + " rows=" + childRows.length);
      /* Build child nodes using buildPivot with grandOverride from L1 */
      var childSel = Object.assign({}, meta.sel);
      var childPivot = buildPivot(childRows, childMapping, childSel, meta.pOpts, {
        grandOverride: meta.grand, allDims: meta.allDims, patientsLookup: result.patientsLookup || null, pharmaciesLookup: result.pharmaciesLookup || null, stockLookup: result.stockLookup || null, rxLookup: result.rxLookup || null, doctorsLookup: result.doctorsLookup || null, doctorsDistinctLookup: result.doctorsDistinctLookup || null, coverageLookup: result.coverageLookup || null
      });
      if (childPivot && childPivot.nodes.length > 0) {
        /* Navigate to the correct parent node in the result tree */
        var targetNode = childPivot.nodes[0]; /* L1 node */
        for (var ni = 1; ni <= depth && targetNode; ni++) {
          var targetName = parentNames[ni];
          var found = null;
          if (targetNode.children) {
            for (var fi = 0; fi < targetNode.children.length; fi++) {
              if (targetNode.children[fi].name === targetName) { found = targetNode.children[fi]; break; }
            }
          }
          /* Also check lazy children from prior loads */
          if (!found) {
            var priorKey = parentNames.slice(0, ni + 1).join("||");
            var priorKids = lazyChildrenRef.current[priorKey];
            if (priorKids) {
              for (var pi2 = 0; pi2 < priorKids.length; pi2++) {
                if (priorKids[pi2].name === targetName) { found = priorKids[pi2]; break; }
              }
            }
          }
          targetNode = found;
        }
        var children = targetNode ? targetNode.children : [];
        /* Apply Top N filtering to lazy-loaded children — only if server didn't handle it */
        if (meta.topNCfg && meta.topNCfg.enabled && !meta.topNOpts && children.length > 0) {
          children = _applyTopNToChildren(children, meta.topNCfg);
        }
        lazyChildrenRef.current[pathKey] = children;
        console.log("[lazyLoad] stored " + children.length + " children for " + pathKey + (meta.topNOpts ? " (server TopN)" : meta.topNCfg ? " (client TopN)" : ""));
      } else {
        lazyChildrenRef.current[pathKey] = [];
      }
      delete lazyLoadingRef.current[pathKey];
      /* Force re-render */
      setPivot(function(prev) { return prev ? Object.assign({}, prev) : prev; });
    }).catch(function(err) {
      console.error("[lazyLoad] error:", err);
      delete lazyLoadingRef.current[pathKey];
      lazyChildrenRef.current[pathKey] = []; /* mark as loaded (empty) to prevent retry loop */
      setPivot(function(prev) { return prev ? Object.assign({}, prev) : prev; });
    });
  };

  if (!user) return <Login onLogin={function(u){ session.user = u; if (u && u.partner) { try { _trimPartner9(u.partner); } catch (eT) {} }   /* r131 */ logActivity("login", {studyType: (u && u.studyTypes && u.studyTypes[0]) ? (u.studyTypes[0].code || u.studyTypes[0]) : ""}); setUser(u); if (u && u.mustChangePassword && u.role !== "admin") setPwPrompt("first"); if (u && u.role !== "admin") { var _seen = null; try { _seen = localStorage.getItem(launchMsgKey(u)); } catch(e) {} if (!_seen) setLaunchMsg(true); } }} />;

  /* ── Password-change overlay (fixed-position, rendered inside every
        post-login screen so the first-login prompt is never missed) ── */
  var pwOverlay = pwPrompt ? (
    <PasswordChangeModal mode={pwPrompt} onClose={function(changed){
      setPwPrompt(null);
      /* Clear the flag locally so the prompt doesn't re-fire this session
         (server already cleared it — both on change and on "keep"). */
      setUser(function(prev){
        if (!prev || !prev.mustChangePassword) return prev;
        var nu = Object.assign({}, prev, { mustChangePassword: false });
        try { session.user = nu; } catch(e) {}
        return nu;
      });
    }} />
  ) : null;

  /* ── Portal launch announcement overlay (after the password modal, if any) ── */
  var launchOverlay = (launchMsg && !pwPrompt) ? (
    <LaunchAnnouncementModal user={user} onClose={function(){ setLaunchMsg(false); }} />
  ) : null;
  /* r197: maintenance notice — fixed banner, dismissable; rendered beside the other overlays */
  var maintOverlay = maintNotice ? (
    <div style={{position:"fixed", top:14, left:"50%", transform:"translateX(-50%)", zIndex:99999, maxWidth:720, width:"calc(100% - 32px)", background:"#fffbeb", border:"1.5px solid #f59e0b", borderRadius:12, boxShadow:"0 12px 32px rgba(15,23,42,.18)", padding:"12px 16px", display:"flex", alignItems:"flex-start", gap:10, fontFamily:"inherit"}}>
      <span style={{fontSize:18, flexShrink:0}}>{"\uD83D\uDD27"}</span>
      <div style={{flex:1, fontSize:12, color:"#92400e", fontWeight:700, lineHeight:1.5}}>{maintNotice}</div>
      <span onClick={function(){ setMaintNotice(""); }} style={{cursor:"pointer", fontSize:14, fontWeight:800, color:"#b45309", padding:"0 4px"}}>{"\u2715"}</span>
    </div>
  ) : null;

  /* ── Study Type Selector Screen ── */
  /* Show when user has study types assigned but hasn't picked one yet */
  var userStudyTypes = user.studyTypes || [];
  /* Auto-select first study type — skip selection screen */
  if (userStudyTypes.length > 0 && !activeStudyType) {
    setTimeout(function(){ setActiveStudyType(userStudyTypes[0].code); }, 0);
  }
  /* ── Admin routing ── */
  var isAdmin = user.role === "admin";
  if (isAdmin && (showAdmin || (!ds && !adminDismissed))) {
    return <AdminPage
      user={user}
      clients={clients} setClients={setClients}
      users={allUsers} setUsers={setAllUsers}
      onGoPortal={function(){
        setShowAdmin(false); setAdminDismissed(true);
        /* Study type selector will render automatically if user has studyTypes and none selected */
        /* The data-loading useEffect handles everything once a study type is chosen */
      }}
      onSignOut={function(){ logActivity("logout"); try{ api.logout(); }catch(e){} session.user = null; setUser(null); setDs(null); setDimValues(null); setClients([]); setAllUsers([]); setShowAdmin(false); setAdminDismissed(false); setActiveStudyType(null); setStudyTypeConfig(null); }} />;
  }

  /* Non-admin with NO study access: show a clear access screen instead of an
     infinite "Loading…" dead-end (security review: no silent fallbacks). */
  if (!isAdmin && (!userStudyTypes || userStudyTypes.length === 0)) {
    return (
      <div style={{minHeight:"100vh", background:"linear-gradient(135deg,#f0f5ff,#f8fafc 60%,#f0fdf4)", display:"flex", alignItems:"center", justifyContent:"center"}}>
        <div style={{textAlign:"center", maxWidth:420, padding:"40px 32px", background:"#fff", borderRadius:14, border:"1.5px solid #e2e8f0", boxShadow:"0 4px 24px rgba(0,0,0,.06)"}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:16}}><AmsxLogo height={40} /></div>
          <div style={{fontSize:15, fontWeight:800, color:"#0f172a", marginBottom:8}}>No study access assigned</div>
          <div style={{fontSize:11.5, color:"#64748b", lineHeight:1.6, marginBottom:18}}>
            Your account is active but has no studies assigned yet. Please contact your administrator to request access.
          </div>
          <button onClick={function(){ logActivity("logout"); try{ api.logout(); }catch(e){} session.user = null; setUser(null); setDs(null); setDimValues(null); setActiveStudyType(null); setStudyTypeConfig(null); }}
            style={{padding:"8px 22px", fontSize:11.5, fontWeight:700, border:"none", borderRadius:8, cursor:"pointer", fontFamily:"inherit", background:"#2563eb", color:"#fff"}}>
            Sign out
          </button>
        </div>
        {pwOverlay}{launchOverlay}{maintOverlay}
      </div>
    );
  }

  if (!ds) {
    return (
      <div style={{minHeight:"100vh", background:"linear-gradient(135deg,#f0f5ff,#f8fafc 60%,#f0fdf4)", display:"flex", alignItems:"center", justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:14}}><AmsxLogo height={44} /></div>
          <div style={{fontSize:14, fontWeight:700, color:"#165372", marginTop:12}}>
            {activeStudyType ? "Loading " + activeStudyType.toUpperCase() + "…" : "Loading…"}
          </div>
        </div>
        {pwOverlay}{launchOverlay}{maintOverlay}
      </div>
    );
  }

  const atcTagVals = Object.values(sel.atcFilters || {}).flat();
  const nfcTagValsApp = Object.values(sel.nfcFilters || {}).flat();
  const tnTagApp = (sel.topN && sel.topN.enabled) ? ["Top "+sel.topN.n+" by "+tnEffMeasure(sel)+(sel.topN.showOthers?" +Others":"")] : [];
  const activeTags = [...sel.country, ...sel.sector, ...sel.manufacturer, ...sel.medicine, ...sel.sku, ...sel.agent,
    ...atcTagVals, ...nfcTagValsApp, ...sel.region, ...sel.governorate,
    ...(sel.periodMode ? [sel.periodMode] : []), ...sel.periodKeys, ...sel.measures, ...tnTagApp];

  /* Sales Tracker shell — AMS navy top bar for the Sales Tracker study only.
     Every themed style below falls back to the exact pre-existing literal, so
     all other studies keep the current white header pixel-identical. */
  var _ptShell = activeStudyType === "sales_tracker";
  /* Sales Tracker header buttons: same actions, dark raised chips on the navy bar */
  var _hbtn = function (base) {
    if (!_ptShell) return base;
    return Object.assign({}, base, { background: "#123D55", border: "1.5px solid rgba(255,255,255,.22)", color: "#C7D9E8", borderRadius: 8 });
  };

  return (
    <div style={{display:"flex", flexDirection:"column", height:"100vh", overflow:"hidden",
                 fontFamily:"'Plus Jakarta Sans',system-ui,-apple-system,sans-serif", fontSize:13, color:"#1e293b", background:"#f0f4f8"}}>

      {/* TOP BAR */}
      <header style={{height:_ptShell?60:48, background:_ptShell?"#0A2F42":"#fff", borderBottom:"1.5px solid "+(_ptShell?"rgba(255,255,255,.14)":"#e2e8f0"),
                      display:"flex", alignItems:"center", padding:"0 12px", gap:8,
                      flexShrink:0, zIndex:20, boxShadow:_ptShell?"0 1px 6px rgba(0,0,0,.35)":"0 1px 4px rgba(0,0,0,.04)"}}>
        {(user && user.partner)
          ? <PartnerHeaderBrand partner={user.partner} dark={_ptShell} admin={!!(user.partnerId && user.role === "admin")} suffix={_ptShell ? "Sales Tracker" : null} />
          : _ptShell
          ? <div style={{display:"flex", alignItems:"center", gap:11, marginRight:4}}>
              <img src="/logowhite.png" alt="AMS Analytix" style={{height:36, width:"auto", display:"block"}} />
              <span style={{fontSize:16.5, fontWeight:800, color:"#F0F6FA", letterSpacing:"-.01em", whiteSpace:"nowrap"}}>Sales Tracker</span>
              <span style={{fontSize:11, color:"rgba(240,246,250,.55)", fontWeight:600, whiteSpace:"nowrap"}}>by AMS Analytix</span>
            </div>
          : <AmsxLogo height={28} />}

        {/* ── Main Tab Switcher ── */}
        <div style={{display:"flex", alignItems:"center", gap:2, background:_ptShell?"transparent":"#f1f5f9", borderRadius:6, padding:_ptShell?0:2, marginLeft:_ptShell?18:4, gap:_ptShell?4:2}}>
          {(function() {
            /* Compute permission gates for Dashboards / Reports tabs.
               Admin sees all. Non-admins are gated by effectiveDashboards/effectiveReports
               for the active study type. Empty array = explicit no-access; null/undefined = no record (default allow). */
            var _isAdminUser = _isDataAdmin(user);
            /* FAIL-CLOSED tab gating (security review): non-admins get Dashboards/Reports tabs
               ONLY from an explicit grant on the matched study entry. Missing entry, missing
               list, or empty list = NO tab ("Empty = no access"). Permission source: per-user
               resolved studyAccess first, client-level perms as fallback. A user with no
               permission records anywhere gets NO Dashboards/Reports tabs (no default-allow). */
            var _saList = null;
            if (!_isAdminUser) {
              if (user && Array.isArray(user.studyAccess) && user.studyAccess.length) _saList = user.studyAccess;
              else if (clientPerms && Array.isArray(clientPerms.studyAccess) && clientPerms.studyAccess.length) _saList = clientPerms.studyAccess;
            }
            var _saHere = null;
            if (_saList && activeStudyType) {
              for (var _msi = 0; _msi < _saList.length; _msi++) {
                if (_saList[_msi].studyTypeCode === activeStudyType) { _saHere = _saList[_msi]; break; }
              }
            }
            var _grantList = function(sa, effKey, rawKey) {
              if (!sa) return null;
              if (Array.isArray(sa[effKey])) return sa[effKey];
              if (Array.isArray(sa[rawKey])) return sa[rawKey];
              return null;
            };
            var _dashList = _grantList(_saHere, "effectiveDashboards", "dashboards");
            var _repList  = _grantList(_saHere, "effectiveReports", "reports");
            var _allowDash    = _isAdminUser || (!!_dashList && _dashList.length > 0);
            var _allowReports = _isAdminUser || (!!_repList && _repList.length > 0);
            /* r138: tabs hidden for this client + study (Admin → Client → study → Tabs) */
            var _hid9 = _hiddenTabs9(user, clientPerms, activeStudyType);
            var _tabs = [];
            if (!_hid9.market) _tabs.push({key:"pivot", label:"Market", icon:"📊"}); /* jordan_msa now has a Market tab too — the merged Pharma+Rx market */
            if (_allowDash && !_hid9.dashboards) _tabs.push({key:"dashboards", label:"Dashboards", icon:"📈"});
            if (_allowReports && !_hid9.reports) _tabs.push({key:"reports", label:"Reports", icon:"📑"});
            if (!_tabs.length) _tabs.push({key:"pivot", label:"Market", icon:"📊"}); /* never an empty portal: everything hidden → Market */
            /* Sales Tracker is now a STUDY (code "sales_tracker" — the pharmacy
               sell-in/sell-out panel), entered from the study selector with its own
               Market / Dashboards / Reports tabs. The old placeholder TAB is retired
               so the name doesn't exist twice with two meanings; its grant key and
               the /api/sales-tracker mount are kept for the study's endpoints.
               To restore the tab: re-add the _allowSales push that lived here. */
            return _tabs;
          })().map(function(t) {
            var on = (mainTab === t.key);
            return (
              <button key={t.key} onClick={function(){ setMainTab(t.key); }}
                style={{display:"flex", alignItems:"center", gap:_ptShell?7:3, padding:_ptShell?"8px 15px":"3px 10px", borderRadius:_ptShell?10:5,
                        border:"none", fontSize:_ptShell?12:10.5, fontWeight:on?(_ptShell?800:700):(_ptShell?600:500),
                        background:on?(_ptShell?"#1A4B66":"#fff"):"transparent", color:on?(_ptShell?"#F0F6FA":"#1d4ed8"):(_ptShell?"#C7D9E8":"#64748b"),
                        cursor:"pointer", fontFamily:"inherit", boxShadow:on?"0 1px 3px rgba(0,0,0,.06)":"none"}}>
                <span style={{fontSize:11}}>{t.icon}</span>
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Study Type Selector (in header) ── */}
        {userStudyTypes && userStudyTypes.length >= 1 && (
          <div style={{display:"flex",alignItems:"center",gap:5,marginLeft:4,padding:"2px 0"}}>
            {!_ptShell && <span style={{fontSize:8.5,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".04em"}}>STUDY:</span>}
            <select value={activeStudyType || ""} onChange={function(e){ if(e.target.value) selectStudyType(e.target.value); }}
              style={{padding:_ptShell?"7px 26px 7px 13px":"3px 24px 3px 8px",fontSize:_ptShell?11:10.5,fontWeight:_ptShell?800:700,border:"1.5px solid "+(_ptShell?"rgba(255,255,255,.3)":"#e2e8f0"),
                      borderRadius:_ptShell?10:6,background:_ptShell?"#123D55":"#f8fafc",color:_ptShell?"#F0F6FA":"#0f172a",cursor:"pointer",fontFamily:"inherit",
                      outline:"none",appearance:"auto",maxWidth:200,textTransform:"uppercase",letterSpacing:".03em"}}>
              {userStudyTypes.map(function(st) {
                return <option key={st.code} value={st.code} style={{textTransform:"uppercase"}}>{(st.name||"").toUpperCase() + (st.maintenance ? " \uD83D\uDD27 MAINTENANCE" : "")}</option>;
              })}
            </select>
            {(function(){
              /* Latest period available in the active study — quarter first (Q2 2026),
                 else month, else year. Uses the access-filtered pOpts so a user with
                 period restrictions sees the latest period THEY can query. */
              var _lp = null;
              if (pOpts) {
                if (pOpts.quarters && pOpts.quarters.length) _lp = pOpts.quarters[pOpts.quarters.length - 1].label;
                else if (pOpts.months && pOpts.months.length) _lp = pOpts.months[pOpts.months.length - 1].label;
                else if (pOpts.years && pOpts.years.length) _lp = pOpts.years[pOpts.years.length - 1].label;
              }
              return _lp ? (
                <span title={"Latest period available in this study: " + _lp}
                  style={{fontSize:9.5, fontWeight:800, color:_ptShell?"rgba(240,246,250,.6)":"#1d4ed8", background:_ptShell?"transparent":"#eff6ff",
                          border:_ptShell?"none":"1px solid #bfdbfe", borderRadius:5, padding:_ptShell?"2px 0":"2px 7px",
                          whiteSpace:"nowrap", letterSpacing:".03em"}}>
                  {_lp}
                </span>
              ) : null;
            })()}
          </div>
        )}

        <span style={{fontSize:9.5, color:_ptShell?"rgba(240,246,250,.6)":"#6b7280"}}>{ds.rows.length > 0 ? ds.rows.length.toLocaleString() + " rows" : (dimValues ? "✓ Ready" : "Loading…")}</span>
        <div style={{flex:1}} />
        <span style={{fontSize:10, color:_ptShell?"#C7D9E8":"#64748b"}}><strong style={{color:_ptShell?"#F0F6FA":"#0f172a"}}>{user.name}</strong></span>
        {_ptShell && (
          <div title={user.name}
            style={{width:32, height:32, borderRadius:"50%", background:"#1A4B66", border:"1px solid rgba(255,255,255,.25)",
                    display:"flex", alignItems:"center", justifyContent:"center", color:"#8FC9E8", fontWeight:800, fontSize:12.5, flexShrink:0}}>
            {String(user.name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        {isAdmin && (
          <button onClick={() => setShowAdmin(true)}
            style={_hbtn({padding:"3px 9px", border:"1.5px solid #6366f1", borderRadius:5, background:"#ede9fe",
                    color:"#4f46e5", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit"})}>
            ⚙ Admin
          </button>
        )}
        {mainTab === "pivot" && (
          <button
            onClick={() => {
              /* New Query — wipe ALL filters (sel → EMPTY_SEL) plus the pivot
                 output and loaded-query tracking so the user starts from a clean
                 slate. Unlike Reload this is instant and client-side: clearing the
                 filters makes the FilterPanel cascade restore the full dimension
                 lists on its own, so there's no DB round-trip. Unlike Reset, this
                 also clears the filters (Reset deliberately keeps them). */
              setSel(EMPTY_SEL);
              setPivot(null);
              setErrors([]);
              setLoadedQueryId(null);
              setEditingQueryId(null);
            }}
            title="Clear all filters and start a fresh query"
            style={_hbtn({padding:"3px 9px", border:"1.5px solid #2563eb", borderRadius:5, background:"#eff6ff",
                    color:"#2563eb", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit"})}>
            ✦ New Query
          </button>
        )}
        {mainTab === "pivot" && pivot && (
          <button
            onClick={() => {
              /* Clear only the pivot output and loaded-query tracking — filters in
                 sel stay intact, so the user can tweak the existing filter setup
                 (e.g. change measures, swap a dim) and re-run without re-picking
                 every filter from scratch. To wipe filters too, use Sign Out or
                 the Reload button in the header. */
              setPivot(null);
              setErrors([]);
              setLoadedQueryId(null);
              setEditingQueryId(null);
            }}
            title="Clear the pivot output (filters are preserved — tweak and re-run)"
            style={_hbtn({padding:"3px 9px", border:"1.5px solid #e2e8f0", borderRadius:5, background:"#fff",
                    color:"#334155", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit"})}>
            ↺ Reset
          </button>
        )}
        <button onClick={() => {
            fetch(API_BASE + "/api/cache/clear", {method:"POST"}).catch(function(){});
            var rClientId = _isDataAdmin(user) ? "" : (user.clientId || "");
            var dimPromise = (_isPublicStudy(studyTypeConfig, activeStudyType))
              ? api.getStudyDimensions(activeStudyType, rClientId)
              : api.getDimensions(rClientId, activeStudyType || "");
            dimPromise.then(function(dims) {
              setDimValues(dims);
              setDs(function(prev){ return { rows: [], mapping: prev ? prev.mapping : {}, fileName: prev ? prev.fileName : "Database — AMS_OnlinePortal", studyType: prev ? prev.studyType : "legacy" }; });
              setSel(EMPTY_SEL); setPivot(null); setErrors([]);
              setLoadedQueryId(null); setEditingQueryId(null);
            }).catch(function(e){ alert("DB reload failed: " + e.message); });
          }}
          style={_hbtn({padding:"3px 9px", border:"1.5px solid #15803d", borderRadius:5, background:"#f0fdf4",
                  color:"#15803d", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit"})}>
          🔄 Reload
        </button>

        {user.role !== "admin" && (
          <button onClick={() => setPwPrompt("voluntary")}
            title="Change your password"
            style={_hbtn({padding:"3px 9px", border:"1.5px solid #e2e8f0", borderRadius:5, background:"#fff",
                    color:"#334155", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit"})}>
            🔑 Password
          </button>
        )}

        {/* Custom Markets — global selector, exactly beside the Regions button.
            One pick here scopes the dashboards; self-hidden when the client's
            AllowCustomMarkets permission is off. */}
        <MarketHeaderControl studyType={activeStudyType || ""} clientId={user.clientId || ""} studyTypeConfig={studyTypeConfig} />

        {(user.allowCustomRegions === true && dimValues && dimValues.governorate && dimValues.governorate.length > 0) && (
          <button onClick={() => setShowRegionBuilder(true)}
            title={userRegionMap ? "Custom region split is ACTIVE — click to edit" : "Build your own regional split from governorates"}
            style={_hbtn({padding:"3px 9px", border:"1.5px solid " + (userRegionMap ? "#2563eb" : "#e2e8f0"), borderRadius:5,
                    background:userRegionMap ? "#eff6ff" : "#fff",
                    color:userRegionMap ? "#1d4ed8" : "#334155", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit"})}>
            🗺️ Regions{userRegionMap ? " ✓" : ""}
          </button>
        )}

        <button onClick={() => { try{logActivity("logout");}catch(e){} try{ api.logout(); }catch(e){} session.user = null; setUser(null); setDs(null); setDimValues(null); setSel(EMPTY_SEL); setPivot(null); setClients([]); setAllUsers([]); setActiveStudyType(null); setStudyTypeConfig(null); setMainTab("pivot"); setFilterCollapsed(false); setSavedQueries([]); setErrors([]); setLoading(false); setLoadedQueryId(null); setEditingQueryId(null); }}
          style={_hbtn({padding:"3px 9px", border:"1.5px solid #e2e8f0", borderRadius:5, background:"#fff",
                  color:"#334155", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit"})}>
          Sign Out
        </button>
      </header>
      {/* r99: a limited / partner admin with NO linked portal account sees why the portal is empty */}
      {(user && user.dataScoped && (!userStudyTypes || userStudyTypes.length === 0)) ? (
        <div style={{margin:"14px 18px 0", padding:"14px 18px", background:"#fff7ed", border:"1.5px solid #fed7aa", borderRadius:12, display:"flex", alignItems:"flex-start", gap:12}}>
          <span style={{fontSize:20, flexShrink:0}}>{"\uD83D\uDCCA"}</span>
          <div style={{flex:1, fontSize:11.5, color:"#7c2d12", lineHeight:1.6}}>
            <div style={{fontSize:13, fontWeight:800, color:"#9a3412", marginBottom:3}}>No portal data access on this admin account</div>
            {"Market, Dashboards and Reports follow a portal account with the same e-mail (" + (user.email || "") + ") under an active client \u2014 this account has none yet, so it opens the admin panel only. A full administrator links one in Admin \u2192 Admin Team \u2192 Edit \u2192 \u201cPortal data access\u201d (choose the client whose studies, dashboards and sample reports you should see); then sign in again."}
          </div>
          {isAdmin ? <button onClick={function(){ setShowAdmin(true); }} style={{padding:"7px 14px", border:"1.5px solid #6366f1", borderRadius:8, background:"#ede9fe", color:"#4f46e5", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", flexShrink:0}}>{"\u2699 Open admin panel"}</button> : null}
        </div>
      ) : null}
      {pwOverlay}{launchOverlay}{maintOverlay}
      {showRegionBuilder && <RegionBuilderModal studyType={activeStudyType || "iraq"} governorates={(dimValues && dimValues.governorate) || []}
        onClose={function(){ setShowRegionBuilder(false); }}
        onSaved={function(m){
          /* Apply the new split IN PLACE — no reload, no logout. */
          setShowRegionBuilder(false);
          setUserRegionMap(m && m.enabled && m.regions && Object.keys(m.regions).length ? { enabled: true, regions: m.regions } : null);
          setRegionMapVersion(function(v){ return v + 1; }); /* remounts the Dashboards tab → fresh fetches */
          setPivot(null); /* old pivot was grouped by the previous split */
          setSel(function(p){ return Object.assign({}, p, { region: [], governorate: [] }); }); /* old region selections no longer exist */
        }} />}

      {/* ══ MAIN CONTENT AREA ══ */}
      {mainTab === "pivot" ? (
        <div style={{display:"flex", flex:1, overflow:"hidden"}}>
          <FilterPanel rows={ds.rows} mp={ds.mapping} sel={sel} setSel={setSel} dimValues={dimValuesEff} setDimValues={setDimValues}
            onRun={runQuery} loading={loading} errors={errors} pOptsExt={pOpts}
            savedQueries={savedQueries} onSaveQuery={handleSaveQuery}
            onDeleteQuery={handleDeleteQuery} onDeleteQueries={handleDeleteQueries} onLoadQuery={handleLoadQuery}
            onShareQuery={handleShareQuery}
            editingQueryId={editingQueryId}
            onStartEdit={handleStartEdit} onCancelEdit={handleCancelEdit}
            onUpdateLoadedQuery={handleUpdateLoadedQuery}
            onRefreshQueries={handleRefreshQueries}
            user={user} clientPerms={clientPerms}
            userStudyTypes={user.studyTypes || []} activeStudyType={activeStudyType}
            onStudyTypeChange={selectStudyType} studyTypeConfig={studyTypeConfig}
            collapsed={filterCollapsed} onToggleCollapse={function(){ setFilterCollapsed(function(p){return !p;}); }} />

          <main style={{flex:1, overflowY:"auto", padding:"6px 10px", background:"#f0f4f8"}}>

            {loading && (
              <div style={{textAlign:"center", padding:"80px 0", color:"#94a3b8"}}>
                <div style={{fontSize:13, fontWeight:600}}>Processing…</div>
              </div>
            )}

            {pivot && !loading && (
              <div>
                {activeStudyType === "sales_tracker" && dimValues && dimValues.panelInfo && <PanelDisclosure info={dimValues.panelInfo} compact={true} />}
                {/* Pivot card */}
                <div style={{background:"#fff", borderRadius:10, border:"1.5px solid #e2e8f0",
                             overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,.04)"}}>
                  <div style={{padding:"5px 12px", borderBottom:"1px solid #f1f5f9", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
                    <span style={{fontWeight:800, fontSize:12, color:"#0f172a"}}>Market</span>
                    {pivot.tree && <span style={{fontSize:9, background:"#eff6ff", color:"#1d4ed8", borderRadius:5, padding:"1px 7px", border:"1px solid #bfdbfe"}}>{(pivot.allDims||pivot.dims).length}-Level: {(pivot.allDims||pivot.dims).map(function(d){return FEAT_LBL[d]||d;}).join(" \u2192 ")}</span>}
                    {sel.topN && sel.topN.enabled && <span style={{fontSize:9, background:"#fdf2f8", color:"#be185d", borderRadius:5, padding:"1px 7px", border:"1px solid #f9a8d4", fontWeight:700}}>{sel.topN.applyTo === "bottom" ? "Bottom" : "Top"} {sel.topN.n} by {tnEffMeasure(sel)}</span>}
                    <span style={{marginLeft:"auto", fontSize:9, color:"#94a3b8"}}>
                      {pivot.nodes.length} groups · {pivot.periods.length} periods · {pivot.dims.length} levels
                    </span>
                  </div>
                  <PivotTable data={pivot} stats={sel.stats} periodMode={sel.periodMode} user={user} allUsers={allUsers} setAllUsers={setAllUsers} mp={ds ? ds.mapping : {}} studyTypeConfig={studyTypeConfig} selection={sel}
                    onLoadChildren={loadPivotChildren} onLoadLevel={loadPivotLevel} onLoadAllLevels={loadAllPivotLevels} onFetchExportRows={fetchExportRows} lazyChildren={lazyChildrenRef.current} lazyLoading={lazyLoadingRef.current}
                    isLazyLoaded={function(k){ return lazyChildrenRef.current[k] !== undefined; }}
                    isLazyLoading={function(k){ return !!lazyLoadingRef.current[k]; }}
                    cachedRows={ds ? ds.rows : null} cachedMapping={ds ? ds.mapping : null}
                    gridDims={pivot ? pivot.allDims : null} gridMeasures={sel.measures}
                    onReorderDims={reorderPivotDims}
                    topNEnabled={!!(sel.topN && sel.topN.enabled)}
                    onApplyTopN={applyTopNFromPivot}
                    activeStudyType={activeStudyType} />
                </div>
              </div>
            )}

            {!pivot && !loading && (
              <div style={{textAlign:"center", padding:"70px 20px", background:"#fff", borderRadius:14, border:"1.5px dashed #e2e8f0"}}>
                <div style={{fontSize:40, marginBottom:14}}>📊</div>
                <div style={{fontSize:15, fontWeight:700, color:"#64748b", marginBottom:10}}>Ready to query</div>
                <div style={{fontSize:12, color:"#94a3b8", maxWidth:440, margin:"0 auto", lineHeight:1.8}}>
                  Check <strong>one feature</strong> for a flat list, or <strong>two to eight</strong> for a collapsible tree (L1 &rarr; L8).<br />
                  Features are ordered by selection &mdash; first checked = Level 1, last = deepest level.<br />
                  ATC levels show an inline drill-down badge. Multi-select values to filter &middot; Run Query
                </div>
                {activeStudyType === "sales_tracker" && dimValues && dimValues.panelInfo && <PanelDisclosure info={dimValues.panelInfo} />}
              </div>
            )}

          </main>
        </div>
      ) : mainTab === "sales_tracker" ? (
        /* ══ SALES TRACKER TAB ══ */
        <SalesTrackerPanel studyType={activeStudyType} studyTypeConfig={studyTypeConfig} clientPerms={clientPerms} user={user} clientId={(user && !_isDataAdmin(user)) ? (user.clientId || "") : ""} countryName={(function(){ var st=activeStudyType; if(st==="iraq")return "Iraq"; if(st==="jordan"||st==="jordan_medical"||st==="jordan_msa"||st==="sales_tracker")return "Jordan"; if(st==="libya")return "Libya"; if(st==="kimadia")return "Kimadia"; return st||""; })()} />
      ) : mainTab === "reports" ? (
        /* ══ REPORTS TAB ══ */
        <ReportsPanel rows={ds.rows} mp={ds.mapping} pOpts={pOpts} clientId={(user && !_isDataAdmin(user)) ? (user.clientId || "") : ""} studyType={activeStudyType} studyTypeConfig={studyTypeConfig} clientPerms={clientPerms} user={user} />
      ) : (
        /* ══ DASHBOARDS TAB ══ */
        <DashboardsPanel key={"dp" + regionMapVersion} rows={ds.rows} mp={ds.mapping} pOpts={pOpts} clientId={(user && !_isDataAdmin(user)) ? (user.clientId || "") : ""} studyType={activeStudyType} studyTypeConfig={studyTypeConfig} clientPerms={clientPerms} user={user} />
      )}

      {/* ── Share configuration modal ──
         Opens when user clicks the Share button on a saved query they own.
         Three scopes: Private (just me), Team (all users of my client),
         Specific Users (pick from teammates). */}
      {shareModalQuery && (
        <ShareQueryModal
          query={shareModalQuery}
          currentUser={user}
          allUsers={allUsers}
          onSave={handleSaveShareConfig}
          onClose={function(){ setShareModalQuery(null); }} />
      )}
    </div>
  );
}

/* ── Mount App to DOM ── */

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
